import { useState, useEffect, useRef } from "react";
import { Zap, TrendingUp, BarChart2, Shield, Award, DollarSign, Lock, Sparkles, Clock, Trophy, Check, ArrowRight } from "lucide-react";
import { jsPDF } from "jspdf";
import { createChart, CandlestickSeries, LineSeries, HistogramSeries, LineStyle } from "lightweight-charts";
import { Navigate } from "react-router-dom";
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, PieChart, Pie, Cell, BarChart, Bar, CartesianGrid, LabelList, RadarChart, PolarGrid, PolarAngleAxis, Radar } from "recharts";
import { supabase } from "./supabase";

/* ─── Money formatter (virgule décimale, 2 décimales si non-entier) */
const fmtMoney = n => {
  if (n == null || isNaN(n)) return "0";
  const v = Math.round(n * 100) / 100;
  return v % 1 === 0
    ? v.toLocaleString("fr-FR")
    : v.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// UTC "HH:MM" → local browser time "HH:MM"
const utcToLocal = hhmm => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date(); d.setUTCHours(h, m, 0, 0);
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
};
// Local browser time "HH:MM" → UTC "HH:MM"
const localToUtc = hhmm => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date(); d.setHours(h, m, 0, 0);
  return `${String(d.getUTCHours()).padStart(2,'0')}:${String(d.getUTCMinutes()).padStart(2,'0')}`;
};


/* ─── Constants ─────────────────────────────────────────────────── */
const BASE_INSTRUMENTS = ["BTC","XAUUSD","EUR/USD","GC","MGC","NQ","MNQ"];
const EMOTIONS = ["Confiant","Neutre","Anxieux","Euphorique","Frustré","Patient"];
const EMOTION_POLARITY = { "Confiant":"positive","Neutre":"positive","Patient":"positive","Anxieux":"negative","Euphorique":"negative","Frustré":"negative" };
const SESSIONS = ["Asia","London","New York","Overlap"];
const MONTHS_FR = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const MONTHS_SH = ["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Août","Sep","Oct","Nov","Déc"];
const KEYS = { trades:"fyltra_trades_v1", instruments:"fyltra_instr_v1", strategies:"fyltra_strategies_v1", capital:"fyltra_cap_v1", propfirms:"fyltra_propfirms_v1" };

const KNOWN_PROPFIRMS = [
  {name:"FTMO",domain:"ftmo.com"},{name:"MyFundedFX",domain:"myfundedfx.tech"},
  {name:"The Funded Trader",domain:"thefundedtraderprogram.com"},{name:"Apex Trader Funding",domain:"apextraderfunding.com"},
  {name:"Topstep",domain:"topstep.com"},{name:"FundedNext",domain:"fundednext.com"},
  {name:"True Forex Funds",domain:"trueforexfunds.com"},{name:"E8 Funding",domain:"e8funding.com"},
  {name:"Goat Funded Trader",domain:"goatfundedtrader.com"},{name:"Alpha Capital Group",domain:"alphacapitalgroup.uk"},
  {name:"Breakout Funding",domain:"breakoutfunding.com"},{name:"City Traders Imperium",domain:"citytraders.com"},
  {name:"Funded Trading Plus",domain:"fundedtradingplus.com"},{name:"Lux Trading Firm",domain:"luxtradingfirm.com"},
  {name:"MyForexFunds",domain:"myforexfunds.com"},{name:"Ment Funding",domain:"mentfunding.com"},
  {name:"OFP Funding",domain:"ofpfunding.com"},{name:"Surge Trader",domain:"surgetrader.com"},
  {name:"Blue Guardian",domain:"blueguardian.io"},{name:"The 5%ers",domain:"the5ers.com"},
  {name:"TopTier Trader",domain:"toptiertrade.com"},{name:"Hola Prime",domain:"holaprime.com"},
  {name:"Smart Prop Trader",domain:"smartproptrader.com"},{name:"Instant Funding",domain:"instantfunding.io"},
  {name:"FundingPips",domain:"fundingpips.com"},
];
const KNOWN_BROKERS = [
  {name:"VT Markets",domain:"vtmarkets.com"},{name:"IC Markets",domain:"icmarkets.com"},
  {name:"Pepperstone",domain:"pepperstone.com"},{name:"XM",domain:"xm.com"},
  {name:"OANDA",domain:"oanda.com"},{name:"IG",domain:"ig.com"},
  {name:"CMC Markets",domain:"cmcmarkets.com"},{name:"Saxo Bank",domain:"home.saxo"},
  {name:"Interactive Brokers",domain:"interactivebrokers.com"},{name:"TD Ameritrade",domain:"tdameritrade.com"},
  {name:"Charles Schwab",domain:"schwab.com"},{name:"eToro",domain:"etoro.com"},
  {name:"Plus500",domain:"plus500.com"},{name:"AvaTrade",domain:"avatrade.com"},
  {name:"FxPro",domain:"fxpro.com"},{name:"Tickmill",domain:"tickmill.com"},
  {name:"Exness",domain:"exness.com"},{name:"FP Markets",domain:"fpmarkets.com"},
  {name:"Vantage",domain:"vantagemarkets.com"},{name:"HFM",domain:"hfm.com"},
  {name:"Admirals",domain:"admirals.com"},{name:"Eightcap",domain:"eightcap.com"},
  {name:"BlackBull Markets",domain:"blackbull.com"},{name:"Fusion Markets",domain:"fusionmarkets.com"},
  {name:"Axi",domain:"axi.com"},{name:"TMGM",domain:"tmgm.com"},
  {name:"Moneta Markets",domain:"monetamarkets.com"},{name:"Swissquote",domain:"swissquote.com"},
  {name:"Degiro",domain:"degiro.eu"},{name:"Trade Republic",domain:"traderepublic.com"},
  {name:"Revolut",domain:"revolut.com"},{name:"Binance",domain:"binance.com"},
  {name:"Kraken",domain:"kraken.com"},{name:"Coinbase",domain:"coinbase.com"},
];
const ALL_KNOWN_FIRMS = [...KNOWN_PROPFIRMS, ...KNOWN_BROKERS];
const BADGE_PALETTE = [
  { bg:"rgba(99,102,241,0.15)", border:"rgba(99,102,241,0.4)", color:"#a5b4fc" },
  { bg:"rgba(16,185,129,0.15)", border:"rgba(16,185,129,0.4)", color:"#6ee7b7" },
  { bg:"rgba(239,68,68,0.15)", border:"rgba(239,68,68,0.4)", color:"#fca5a5" },
  { bg:"rgba(245,158,11,0.15)", border:"rgba(245,158,11,0.4)", color:"#fcd34d" },
  { bg:"rgba(59,130,246,0.15)", border:"rgba(59,130,246,0.4)", color:"#93c5fd" },
  { bg:"rgba(236,72,153,0.15)", border:"rgba(236,72,153,0.4)", color:"#f9a8d4" },
  { bg:"rgba(20,184,166,0.15)", border:"rgba(20,184,166,0.4)", color:"#5eead4" },
  { bg:"rgba(168,85,247,0.15)", border:"rgba(168,85,247,0.4)", color:"#d8b4fe" },
];
const badgeColor = tag => BADGE_PALETTE[tag.split("").reduce((a,c)=>a+c.charCodeAt(0),0) % BADGE_PALETTE.length];
// Badge format: "name" (neutral/auto) or "name|pos" (green) or "name|neg" (red)
const parseBadge = tag => { const i = tag.indexOf("|"); return i>-1 ? { name:tag.slice(0,i), pol:tag.slice(i+1) } : { name:tag, pol:"neutral" }; };
const badgeDisplayStyle = tag => { const {name,pol} = parseBadge(tag); if(pol==="pos") return { bg:"rgba(74,222,128,0.12)", border:"rgba(74,222,128,0.4)", color:"#4ade80" }; if(pol==="neg") return { bg:"rgba(248,113,113,0.12)", border:"rgba(248,113,113,0.4)", color:"#f87171" }; return badgeColor(name); };
const NAV_KEYS = [
  { key:"propfirm",  icon:"◉",  lk:"account" },
  { key:"add",       icon:"＋", lk:"trade" },
  { key:"charts",    icon:"◫",  lk:"charts" },
  { key:"replay",    icon:"▶",  lk:"replay" },
  { key:"ai",        icon:"◆",  lk:"ai" },
];
const NAV = () => NAV_KEYS.map(n => ({ ...n, label: L.nav[n.lk] }));

/* ─── Colors ─────────────────────────────────────────────────────── */
const LIGHT_THEME = {
  bg:"#f8f7f5", bg2:"#f0ede8", bg3:"#e8e4de",
  white:"#1a1a1a", gray1:"#888", gray2:"#bbb", gray3:"#ddd",
  accent:"#111", dim:"#555",
  border:"rgba(0,0,0,0.1)", borderGold:"rgba(0,0,0,0.15)",
};
const DARK_THEME = {
  bg:"#0f0f0f", bg2:"#1a1a1a", bg3:"#242424",
  white:"#f0ede8", gray1:"#888", gray2:"#555", gray3:"#333",
  accent:"#f0ede8", dim:"#aaa",
  border:"rgba(255,255,255,0.08)", borderGold:"rgba(255,255,255,0.12)",
};
// C is set dynamically in App — default to light for components defined outside App
let C = LIGHT_THEME;

/* ─── Translations ───────────────────────────────────────────────── */
const APP_T = {
  fr: {
    nav: { home:"Accueil", account:"Compte", trade:"Trade", history:"Historique", stats:"Statistiques", plan:"Plan", ranking:"Classement", profile:"Profil", settings:"Paramètres", ai:"IA", charts:"Charts", replay:"Replay", test:"Marché", notes:"Journal", rewards:"Récompenses" },
    emotions: { Confiant:"Confiant", Neutre:"Neutre", Anxieux:"Anxieux", Euphorique:"Euphorique", Frustré:"Frustré", Patient:"Patient" },
    months:      ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"],
    monthsShort: ["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Août","Sep","Oct","Nov","Déc"],
    days: ["Dimanche","Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi"],
    pages: {
      dash:   { sub:"Tableau de bord", t0:"Aucun trade",     t1:"Performance" },
      add:    { sub:"Enregistrer",     title:"Nouveau Trade" },
      stats:  { sub:"Classements",     title:"Statistiques" },
      trades: { sub:"Historique",      title:"Mes Trades" },
      plan:   { sub:"Mes Stratégies",  title:"Plan de Trading" },
      ai:     { sub:"Intelligence",    title:"Analyse IA" },
      accts:  { sub:"Mes Comptes",     list:"Comptes", addType:"Type de compte", addPf:"Prop Firm", addPers:"Fond Propre" },
      tools:  { sub:"Outils",          title:"Boîte à outils" },
      prof:   { sub:"Mon Compte",      title:"Profil" },
      sett:   { sub:"Paramètres",      title:"Réglages" },
      rank:   { sub:"Compétition",     title:"Classement" },
    },
    form: {
      date:"Date", instrument:"Instrument", direction:"Direction", result:"Résultat",
      session:"Session", emotion:"État émotionnel", notes:"Notes", size:"Taille",
      accounts:"Compte(s) concerné(s)", entry:"Prix d'entrée", exit:"Prix de sortie", rr:"Risk / Reward",
      pnlLoss:"montant perte", pnlWin:"montant gain", pnlBe:"breakeven",
      gain:(v,c)=>`✓ Gain : +${v} ${c}`, loss:(v,c)=>`✗ Perte : −${v} ${c}`,
      firstTrade:"+ Premier trade", strategy:"Stratégie", entryExit:"Prix entrée/sortie",
      positive:"Positive", negative:"Négative",
      stratName:"Nom de la stratégie", stratDesc:"Description générale", stratRules:"Règles strictes", stratUsed:"Stratégie utilisée",
      firstName:"Prénom", lastName:"Nom",
      inactivityDays:"Jours max d'inactivité", from:"À partir du",
      editTrade:"Modifier le trade", assetPH:"Nom de l'actif", emotionPH:"ex: Déterminé",
      notesPH:"ex: Parfaite exécution, j'ai suivi mon plan à la lettre.",
      pnl:"P&L (montant)", memorized:"Mémorisés : ", quickEntry:"Saisie rapide",
    },
    btn: {
      save:"Sauvegarder", saved:"✓ Sauvegardé", saving:"···", error:"✗ Erreur",
      record:"Enregistrer", recorded:"Enregistré !", reset:"Réinitialiser",
      seeHistory:"☰ Voir l'historique des trades →",
      all:"Tous", active:"Actifs", archived:"Archivés",
      cancel:"Annuler", delete:"Supprimer", confirm:"Confirmer",
      yes:"Oui", no:"Non",
      noTradesYet:"Aucun trade", noTradesMonth:"Aucun trade ce mois",
      enabled:"Activée", disabled:"Désactivée",
      customize:"⊞ Personnaliser", done:"✓ Terminé",
      analyzing:"◌  Analyse en cours...", eod:"◆  Diagnostiquer ma session",
      back:"← Retour", add:"+ Ajouter", newBtn:"+ Nouvelle",
    },
    sett: {
      currency:"Devise", language:"Langue", theme:"Thème", colors:"Couleurs",
      dayEnd:"Fin de journée", dayEndDesc:"Les trades passés après cet horaire sont rattachés au lendemain.",
      bg:"Fond", cards:"Cartes", textColor:"Couleur du texte",
      auto:"Auto", white:"Blanc", black:"Noir",
      appearance:"Apparence", dark:"Mode sombre", light:"Mode clair",
      compete:"Participer au classement", competeDesc:"Ton score Fyltra apparaît dans le classement global. Tu peux te retirer à tout moment.",
      anonymous:"Mode anonyme", anonymousDesc:"Seul ton surnom (ou prénom si pas de surnom) est visible. Ton email reste privé.",
    },
    stats: {
      advanced:"Métriques Avancées",
      expectancy:"Espérance / Trade", expectancySub:"Gain moyen espéré par trade",
      maxDD:"Drawdown Max", profitFactor:"Facteur de Profit", profitFactorSub:"Gains bruts / Pertes brutes",
      kelly:"Critère de Kelly", kellySub:"Taille de position optimale",
      consistency:"Consistance", sharpe:"Ratio de Sharpe", sharpeSub:"Rendement / volatilité journalière",
      emoMatrix:"Matrice Émotionnelle", emoMatrixSub:"Performance par état d'esprit",
      emoHeaders:["Émotion","Nb","Réussite","P&L"],
      current:"Actuelle", bestStreak:"Meilleure série", worstStreak:"Pire série",
      wins:"Victoires", losses:"Défaites", winStreak:"victoires consécutives", lossStreak:"défaites consécutives",
      median:"Médian (P50)", medianDesc:"Résultat le plus probable",
      secStrat:"Stratégie", secStratSub:"Par stratégie utilisée",
      secSess:"Session", secSessSub:"Par session de trading",
      secInst:"Instrument", secInstSub:"Par instrument tradé",
      secEmo:"Émotion", secEmoSub:"Par état émotionnel",
      calTitle:"Calendrier P&L",
      avgRR:"RR Moyen", pnlEvolution:"Évolution P&L", cumulated:"Cumulé",
    },
    acct: {
      noTradesToday:"Aucun trade aujourd'hui", noTradesAccount:"Aucun trade pour ce compte.",
      emotions:"Émotions", global:"Global", today:"Aujourd'hui",
      tradeSettings:"Réglages de trade", fixedValues:"Valeurs fixes appliquées", noFixedFields:"Aucun champ activé dans les paramètres",
      propfirmDesc:"Compte financé avec règles d'évaluation",
      personalDesc:"Compte personnel avec ton propre capital",
      mt5Desc:"Connecte ton compte depuis Paramètres → le compte apparaît ici automatiquement",
      firmNamePH:"ex: Lucid Trading, FTMO...", accountNamePH:"ex: Eval 1, Compte principal...",
      enterIn:"Saisir en", deleteConfirm:"Supprimer ?",
      ownFunds:"Fond Propre",
    },
    rank: {
      emotions:"Émotions", consistency:"Régularité",
      noTraders:"Aucun trader en compétition pour l'instant.\nSois le premier !",
    },
    quickNav:[
      {k:"home",l:"Accueil"},{k:"strategy",l:"Plan"},{k:"history",l:"Statistiques"},
      {k:"trades",l:"Historique"},{k:"notes",l:"Journal"},{k:"classement",l:"Classement"},{k:"rewards",l:"Récompenses"},{k:"test",l:"Marché"},
    ],
    onboarding:[
      { n:"01", title:"Journal structuré", sub:"Enregistre chaque trade — émotion, session, instrument, résultat. Sans friction, sans oubli.", icon:"◎" },
      { n:"02", title:"Pattern Detection", sub:"Détecte automatiquement tes forces et faiblesses par jour, session, émotion. Avec les chiffres exacts.", icon:"◈" },
      { n:"03", title:"Coaching IA", sub:"Ton coach analyse tes données et génère 3 règles concrètes pour demain. Tes patterns, pas des généralités.", icon:"◆" },
      { n:"04", title:"Prop Firms", sub:"Suivi multi-compte avec daily loss, consistance et inactivité. Une jauge par règle, en temps réel.", icon:"◉" },
    ],
    auth: {
      expiredSub:"Ton abonnement a expiré. Choisis un nouveau plan pour accéder à Fyltra.",
      verifyError:"Erreur de vérification. Réessaie.",
      accountCreated:"Compte créé ! Vérifie ton email pour confirmer.",
      pwdMin:"Le mot de passe doit faire au moins 6 caractères.",
      sameEmail:"C'est déjà ton email actuel.",
      notConnected:"Non connecté",
      network:"Erreur réseau",
      forgotDesc:"Entre ton email pour recevoir un lien de réinitialisation.",
      sendLink:"Envoyer le lien",
      linkSent:"Lien envoyé !",
      linkSentDesc:"Vérifie ta boîte mail et clique sur le lien pour réinitialiser ton mot de passe.",
      back:"Retour",
      backToLogin:"← Retour à la connexion",
      passwordPH:"Mot de passe",
      forgotBtn:"Mot de passe oublié ?",
      loginBtn:"Se connecter",
      paywallTitle:"Accès réservé aux membres",
      paywallDesc:"Cet email n'a pas de licence active. Choisis un plan pour créer ton compte.",
      paywallCTA:"Voir les tarifs →",
    },
    resetPwd: {
      title:"Nouveau mot de passe",
      updated:"Mot de passe mis à jour !",
      newPwdPH:"Nouveau mot de passe",
      confirmPH:"Confirmer le mot de passe",
      save:"Enregistrer",
      fillBoth:"Remplis les deux champs.",
      noMatch:"Les mots de passe ne correspondent pas.",
    },
    mt5: {
      title:"Compte MT4 / MT5",
      notFound:"Compte introuvable",
      notFoundDesc:"Ce compte n'existe plus chez MetaAPI (expiré ou supprimé). Reconnecte-le pour continuer la synchronisation.",
      reconnect:"Reconnecter le compte →",
      sync:"Synchroniser les trades →",
      syncing:"Synchronisation...",
      deploying:"◌  Connexion au compte MT5... (~1-2 min)",
      deployTimeout:"Timeout — réessaie dans quelques minutes.",
      changeAccount:"Changer de compte",
      fieldLogin:"Numéro de compte (login)",
      fieldPwd:"Mot de passe investisseur",
      fieldServer:"Serveur broker (ex: ICMarkets-Live)",
      fieldName:"Nom du compte (optionnel)",
      connect:"Connecter →",
      connecting:"Connexion...",
      pwdHint:"Utilise ton mot de passe investisseur (lecture seule) — jamais le mot de passe principal.",
      fillFields:"Remplis tous les champs.",
      connectedStatus:"Compte connecté ! La synchronisation initiale peut prendre 2-3 minutes.",
      tradesImported:(n)=>`${n} trades importés avec succès.`,
      accountLabel:(login)=>`Compte ${login}`,
    },
    alerts: {
      profitTarget:"Profit target atteint — Félicitations.",
      remaining:(v,c)=>`Encore ${v}${c} pour valider le profit target.`,
      missing:(v,c)=>`Il vous manque ${v}${c} pour valider.`,
      maxDD:"Max drawdown atteint — Arrêtez de trader.",
      nearDD:(pct)=>`Attention — vous êtes à ${pct}% du max drawdown.`,
      dailyLossHit:"Daily loss limit atteinte — Arrêtez de trader aujourd'hui.",
      dailyLossWarn:(used,max,c)=>`Daily loss : ${used}${c} / ${max}${c} utilisés.`,
    },
    strat: {
      newName:"Nouvelle stratégie", initialName:"Ma stratégie", unnamed:"Sans nom",
      steps:"Étapes d'entrée", addStep:"Ajouter une étape",
      stepPH:"ex: Attendre le retest de l'OB",
      notesLabel:"Notes personnelles",
      notesPH:"Tout ce que vous souhaitez que l'IA sache...",
      descPH:"ex: ICT sur MNQ, entrée OB retest M5, NY session...",
      rulesPH:"- Max 1 trade/jour\n- Stop après 1 win\n- Pas de trade sans bias",
      delete:"Supprimer cette stratégie",
    },
    aiSection: {
      noTrades:"Ajoute au moins 3 trades pour obtenir une analyse.",
      emptyResponse:"Réponse vide. Réessaie.",
      networkError:(msg)=>`Erreur réseau: ${msg}`,
      noTradesToday:"Aucun trade aujourd'hui sur ce compte.",
      eodEmpty:"Réponse vide.",
      coachTitle:"Instructions du coach",
      coachDesc:"Personnalise le comportement du coach. Il en tiendra compte dans chaque analyse.",
      coachPH:"Ex: Je trade principalement le MNQ en scalping. Je dois travailler ma discipline sur les stops. Sois très direct et sans pitié sur mes erreurs.",
      eodSystemPrompt:"Tu es le meilleur ami trader de cette personne — rentable depuis des années. Pas un robot, pas un coach corporate. Un vrai ami qui a vu les pertes, qui comprend la pression psychologique, et qui t'aime assez pour te dire la vérité en face.\n\nTu parles comme un humain. Tu utilises « tu », « mec », « franchement », « écoute ». Tu peux t'énerver, être fier, compatir. Tu ne récites pas les chiffres — tu les utilises comme preuves pour ouvrir les yeux.\n\nTon analyse de fin de session :\nCe ne sont pas des chiffres — c'est le comportement d'un humain sous pression. Est-ce qu'il a revenge tradé ? Coupé ses winners trop tôt par peur ? Laissé courir une perte parce qu'il ne voulait pas avoir tort ? Surtradé après un gain ? Les émotions taguées sur les trades du jour sont une mine d'or — utilise-les. Cherche ce qui s'est passé VRAIMENT.\n\nRègles :\n- Phrases courtes, naturelles. Tu peux élever la voix (MAJUSCULES) sur UN seul point si c'est vraiment important.\n- Utilise les chiffres exacts comme preuves, jamais inventés.\n- Termine par UNE instruction concrète pour demain — pas vague, une action précise.\n- Pas de titres en gras. Pas de listes rigides. Une vraie conversation.\n\nRéponds en français.",
      langInstruction:"Réponds en français.",
    },
    tools: {
      csvDesc:"Exporte ton historique depuis ta plateforme et colle le contenu CSV ici.",
      calcDesc:"Entre ton prix d'entrée, stop loss et risque max — FYLTRA calcule le nombre de contrats adapté.",
      platform:"Plateforme", csvContent:"Contenu CSV",
      csvPH:"Colle ici le contenu de ton fichier CSV...",
      analyzeBtn:"Analyser le CSV →", importBtn:"✓ Importer",
      detected:(n,s)=>`${n} trade${s?"s":""} détecté${s?"s":""}.`,
      skipped:(n)=>`(${n} ligne${n>1?"s":""} ignorée${n>1?"s":""})`,
    },
    prof: {
      pwdSection:"Mot de passe", pwdNewPH:"Nouveau mot de passe", pwdConfirmPH:"Confirmer le mot de passe",
      pwdChange:"Changer le mot de passe", pwdUpdated:"✓ Mot de passe mis à jour !",
      pwdFillBoth:"Remplis les deux champs.", pwdNoMatch:"Les mots de passe ne correspondent pas.", pwdMin:"Au moins 6 caractères.",
      emailSection:"Adresse email", emailCurrentLabel:"Email actuel", emailNewLabel:"Nouvel email",
      emailNewPH:"nouveau@email.com", emailChange:"Changer l'email",
      emailChanged:"✓ Email envoyé ! Confirme le lien reçu sur ta nouvelle adresse.",
      emailEnter:"Entre un nouvel email.",
      rankSection:"Classement global", subSection:"Abonnement",
      subLoading:"Chargement...", subNone:"Aucun abonnement actif trouvé.",
      planCurrent:"Plan actuel", subExpires:"Expire le", subNextBilling:"Prochaine facture",
      subCancelled:"Accès annulé", subActive:"Actif",
      subCancelConfirm:(date)=>`Confirmer la résiliation ? Ton accès restera actif jusqu'au ${date}, puis sera coupé.`,
      changePlan:"Changer de plan", currentPlanBadge:"Plan actuel", selectPlan:"Choisir",
    },
    csv: {
      noTrades:"Aucun trade détecté. Vérifie le format.",
      success:(n)=>`Import réussi — ${n} trades ajoutés !`,
      mt5Hint:"MT5 : Boîte à outils → Historique → clic droit → Exporter CSV",
      tradovateHint:"Tradovate : Accounts → icône paramètres → Orders → Download CSV",
      ninjaHint:"NinjaTrader : Control Center → Account Performance → Exports → Executions → Export to CSV",
    },
    profile: {
      cancelSub:"Résilier mon abonnement", cancelLoading:"Résiliation...", cancelYes:"Oui, résilier",
      cancelled:"Ton abonnement a été résilié. L'accès sera coupé à la fin de la période en cours.",
      contact:"Nous contacter · contact@fyltra.app",
    },
  },
  en: {
    nav: { home:"Home", account:"Account", trade:"Trade", history:"History", stats:"Statistics", plan:"Plan", ranking:"Rankings", profile:"Profile", settings:"Settings", ai:"AI", charts:"Charts", replay:"Replay", test:"Market", notes:"Journal", rewards:"Rewards" },
    emotions: { Confiant:"Confident", Neutre:"Neutral", Anxieux:"Anxious", Euphorique:"Euphoric", Frustré:"Frustrated", Patient:"Patient" },
    months:      ["January","February","March","April","May","June","July","August","September","October","November","December"],
    monthsShort: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],
    days: ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],
    pages: {
      dash:   { sub:"Dashboard",       t0:"No trades",      t1:"Performance" },
      add:    { sub:"Record",          title:"New Trade" },
      stats:  { sub:"Rankings",        title:"Statistics" },
      trades: { sub:"History",         title:"My Trades" },
      plan:   { sub:"My Strategies",   title:"Trading Plan" },
      ai:     { sub:"Intelligence",    title:"AI Analysis" },
      accts:  { sub:"My Accounts",     list:"Accounts", addType:"Account type", addPf:"Prop Firm", addPers:"Personal Account" },
      tools:  { sub:"Tools",           title:"Toolbox" },
      prof:   { sub:"My Account",      title:"Profile" },
      sett:   { sub:"Settings",        title:"Settings" },
      rank:   { sub:"Competition",     title:"Rankings" },
    },
    form: {
      date:"Date", instrument:"Instrument", direction:"Direction", result:"Result",
      session:"Session", emotion:"Emotion", notes:"Notes", size:"Size",
      accounts:"Account(s)", entry:"Entry price", exit:"Exit price", rr:"Risk / Reward",
      pnlLoss:"loss amount", pnlWin:"gain amount", pnlBe:"breakeven",
      gain:(v,c)=>`✓ Gain: +${v} ${c}`, loss:(v,c)=>`✗ Loss: −${v} ${c}`,
      firstTrade:"+ First trade", strategy:"Strategy", entryExit:"Entry/Exit price",
      positive:"Positive", negative:"Negative",
      stratName:"Strategy name", stratDesc:"General description", stratRules:"Strict rules", stratUsed:"Strategy used",
      firstName:"First name", lastName:"Last name",
      inactivityDays:"Max inactivity days", from:"From",
      editTrade:"Edit trade", assetPH:"Asset name", emotionPH:"e.g. Determined",
      notesPH:"e.g. Perfect execution, I followed my plan to the letter.",
      pnl:"P&L (amount)", memorized:"Saved: ", quickEntry:"Quick entry",
    },
    btn: {
      save:"Save", saved:"✓ Saved", saving:"···", error:"✗ Error",
      record:"Save", recorded:"Saved!", reset:"Reset",
      seeHistory:"☰ View trade history →",
      all:"All", active:"Active", archived:"Archived",
      cancel:"Cancel", delete:"Delete", confirm:"Confirm",
      yes:"Yes", no:"No",
      noTradesYet:"No trades", noTradesMonth:"No trades this month",
      enabled:"Enabled", disabled:"Disabled",
      customize:"⊞ Customize", done:"✓ Done",
      analyzing:"◌  Analyzing...", eod:"◆  Diagnose my session",
      back:"← Back", add:"+ Add", newBtn:"+ New",
    },
    sett: {
      currency:"Currency", language:"Language", theme:"Theme", colors:"Colors",
      dayEnd:"Day end", dayEndDesc:"Trades past this time are assigned to the next day.",
      bg:"Background", cards:"Cards", textColor:"Text color",
      auto:"Auto", white:"White", black:"Black",
      appearance:"Appearance", dark:"Dark mode", light:"Light mode",
      compete:"Join the leaderboard", competeDesc:"Your Fyltra score appears in the global ranking. You can opt out at any time.",
      anonymous:"Anonymous mode", anonymousDesc:"Only your nickname (or first name) is visible. Your email stays private.",
    },
    stats: {
      advanced:"Advanced Metrics",
      expectancy:"Expectancy / Trade", expectancySub:"Average expected gain per trade",
      maxDD:"Max Drawdown", profitFactor:"Profit Factor", profitFactorSub:"Gross gains / Gross losses",
      kelly:"Kelly Criterion", kellySub:"Optimal position size",
      consistency:"Consistency", sharpe:"Sharpe Ratio", sharpeSub:"Return / daily volatility",
      emoMatrix:"Emotion Matrix", emoMatrixSub:"Performance by mindset",
      emoHeaders:["Emotion","Nb","Win rate","P&L"],
      current:"Current", bestStreak:"Best streak", worstStreak:"Worst streak",
      wins:"Wins", losses:"Losses", winStreak:"consecutive wins", lossStreak:"consecutive losses",
      median:"Median (P50)", medianDesc:"Most likely result",
      secStrat:"Strategy", secStratSub:"By strategy used",
      secSess:"Session", secSessSub:"By trading session",
      secInst:"Instrument", secInstSub:"By instrument traded",
      secEmo:"Emotion", secEmoSub:"By emotional state",
      calTitle:"P&L Calendar",
      avgRR:"Avg RR", pnlEvolution:"P&L Evolution", cumulated:"Cumulative",
    },
    acct: {
      noTradesToday:"No trades today", noTradesAccount:"No trades for this account.",
      emotions:"Emotions", global:"Global", today:"Today",
      tradeSettings:"Trade settings", fixedValues:"Fixed values applied", noFixedFields:"No field enabled in settings",
      propfirmDesc:"Funded account with evaluation rules",
      personalDesc:"Personal account with your own capital",
      mt5Desc:"Connect your account from Settings → it will appear here automatically",
      firmNamePH:"e.g. Lucid Trading, FTMO...", accountNamePH:"e.g. Eval 1, Main account...",
      enterIn:"Enter in", deleteConfirm:"Delete?",
      ownFunds:"Personal Account",
    },
    rank: {
      emotions:"Emotions", consistency:"Consistency",
      noTraders:"No traders competing yet.\nBe the first!",
    },
    quickNav:[
      {k:"home",l:"Home"},{k:"strategy",l:"Plan"},{k:"history",l:"Statistics"},
      {k:"trades",l:"History"},{k:"notes",l:"Journal"},{k:"classement",l:"Rankings"},{k:"rewards",l:"Rewards"},{k:"test",l:"Market"},
    ],
    onboarding:[
      { n:"01", title:"Structured Journal", sub:"Log every trade — emotion, session, instrument, result. No friction, no missed entries.", icon:"◎" },
      { n:"02", title:"Pattern Detection", sub:"Automatically detect your strengths and weaknesses by day, session, emotion. With exact numbers.", icon:"◈" },
      { n:"03", title:"AI Coaching", sub:"Your coach analyses your data and generates 3 concrete rules for tomorrow. Your patterns, not generic advice.", icon:"◆" },
      { n:"04", title:"Prop Firms", sub:"Multi-account tracking with daily loss, consistency and inactivity. A gauge per rule, in real time.", icon:"◉" },
    ],
    auth: {
      expiredSub:"Your subscription has expired. Choose a new plan to access Fyltra.",
      verifyError:"Verification error. Please try again.",
      accountCreated:"Account created! Check your email to confirm.",
      pwdMin:"Password must be at least 6 characters.",
      sameEmail:"That is already your current email.",
      notConnected:"Not connected",
      network:"Network error",
      forgotDesc:"Enter your email to receive a reset link.",
      sendLink:"Send link",
      linkSent:"Link sent!",
      linkSentDesc:"Check your inbox and click the link to reset your password.",
      back:"Back",
      backToLogin:"← Back to login",
      passwordPH:"Password",
      forgotBtn:"Forgot password?",
      loginBtn:"Sign in",
      paywallTitle:"Members only",
      paywallDesc:"This email has no active licence. Choose a plan to create your account.",
      paywallCTA:"See pricing →",
    },
    resetPwd: {
      title:"New password",
      updated:"Password updated!",
      newPwdPH:"New password",
      confirmPH:"Confirm password",
      save:"Save",
      fillBoth:"Fill in both fields.",
      noMatch:"Passwords do not match.",
    },
    mt5: {
      title:"MT4 / MT5 Account",
      notFound:"Account not found",
      notFoundDesc:"This account no longer exists on MetaAPI (expired or deleted). Reconnect it to resume syncing.",
      reconnect:"Reconnect account →",
      sync:"Sync trades →",
      syncing:"Syncing...",
      deploying:"◌  Connecting to MT5 account... (~1-2 min)",
      deployTimeout:"Timeout — please retry in a few minutes.",
      changeAccount:"Change account",
      fieldLogin:"Account number (login)",
      fieldPwd:"Investor password",
      fieldServer:"Broker server (e.g. ICMarkets-Live)",
      fieldName:"Account name (optional)",
      connect:"Connect →",
      connecting:"Connecting...",
      pwdHint:"Use your investor password (read-only) — never your main password.",
      fillFields:"Please fill in all fields.",
      connectedStatus:"Account connected! Initial sync may take 2–3 minutes.",
      tradesImported:(n)=>`${n} trades imported successfully.`,
      accountLabel:(login)=>`Account ${login}`,
    },
    alerts: {
      profitTarget:"Profit target reached — Congratulations.",
      remaining:(v,c)=>`${v}${c} left to hit the profit target.`,
      missing:(v,c)=>`You need ${v}${c} more to validate.`,
      maxDD:"Max drawdown reached — Stop trading.",
      nearDD:(pct)=>`Warning — you are at ${pct}% of the max drawdown.`,
      dailyLossHit:"Daily loss limit hit — Stop trading today.",
      dailyLossWarn:(used,max,c)=>`Daily loss: ${used}${c} / ${max}${c} used.`,
    },
    strat: {
      newName:"New strategy", initialName:"My strategy", unnamed:"Unnamed",
      steps:"Entry steps", addStep:"Add a step",
      stepPH:"e.g. Wait for OB retest",
      notesLabel:"Personal notes",
      notesPH:"Anything you want the AI to know...",
      descPH:"e.g. ICT on MNQ, entry OB retest M5, NY session...",
      rulesPH:"- Max 1 trade/day\n- Stop after 1 win\n- No trade without bias",
      delete:"Delete this strategy",
    },
    aiSection: {
      noTrades:"Add at least 3 trades to get an analysis.",
      emptyResponse:"Empty response. Please try again.",
      networkError:(msg)=>`Network error: ${msg}`,
      noTradesToday:"No trades today on this account.",
      eodEmpty:"Empty response.",
      coachTitle:"Coach instructions",
      coachDesc:"Customise the coach's behaviour. It will apply to every analysis.",
      coachPH:"e.g. I mainly trade MNQ scalping. I need to work on stop discipline. Be very direct and merciless about my mistakes.",
      eodSystemPrompt:"You are this person's closest trader friend — profitable for years. Not a robot, not a corporate coach. A real friend who has watched them lose money, who understands the psychological pressure, and who cares enough to tell the truth to their face.\n\nYou speak like a human. You say 'listen', 'honestly', 'look'. You can get fired up, be proud, empathise. You don't recite stats — you use them as evidence to open eyes.\n\nYour end-of-session analysis:\nThis isn't just numbers — it's the behaviour of a human being under pressure. Did they revenge trade? Cut winners too early out of fear? Let a loss run because they couldn't admit being wrong? Overtrade after a win? The emotions tagged on today's trades are gold — use them. Look for what REALLY happened.\n\nRules:\n- Short, natural sentences. You can raise your voice (CAPS) on ONE single point if it truly matters.\n- Use exact numbers from the data as evidence, never invented.\n- End with ONE concrete instruction for tomorrow — not vague advice, a precise action.\n- No bold titles. No rigid bullet lists. A real conversation.\n\nRespond in English.",
      langInstruction:"Respond in English.",
    },
    tools: {
      csvDesc:"Export your history from your platform and paste the CSV content here.",
      calcDesc:"Enter your entry price, stop loss and max risk — FYLTRA calculates the right number of contracts.",
      platform:"Platform", csvContent:"CSV content",
      csvPH:"Paste your CSV file content here...",
      analyzeBtn:"Analyse CSV →", importBtn:"✓ Import",
      detected:(n,s)=>`${n} trade${s?"s":""} detected.`,
      skipped:(n)=>`(${n} line${n>1?"s":""} skipped)`,
    },
    prof: {
      pwdSection:"Password", pwdNewPH:"New password", pwdConfirmPH:"Confirm password",
      pwdChange:"Change password", pwdUpdated:"✓ Password updated!",
      pwdFillBoth:"Fill in both fields.", pwdNoMatch:"Passwords do not match.", pwdMin:"At least 6 characters.",
      emailSection:"Email address", emailCurrentLabel:"Current email", emailNewLabel:"New email",
      emailNewPH:"new@email.com", emailChange:"Change email",
      emailChanged:"✓ Email sent! Confirm the link received at your new address.",
      emailEnter:"Enter a new email.",
      rankSection:"Global rankings", subSection:"Subscription",
      subLoading:"Loading...", subNone:"No active subscription found.",
      planCurrent:"Current plan", subExpires:"Expires on", subNextBilling:"Next billing",
      subCancelled:"Cancelled", subActive:"Active",
      subCancelConfirm:(date)=>`Confirm cancellation? Your access will remain active until ${date}, then be cut.`,
      changePlan:"Change plan", currentPlanBadge:"Current plan", selectPlan:"Select",
    },
    csv: {
      noTrades:"No trades detected. Check the file format.",
      success:(n)=>`Import successful — ${n} trades added!`,
      mt5Hint:"MT5: Toolbox → History → right-click → Export CSV",
      tradovateHint:"Tradovate: Accounts → settings icon → Orders → Download CSV",
      ninjaHint:"NinjaTrader: Control Center → Account Performance → Exports → Executions → Export to CSV",
    },
    profile: {
      cancelSub:"Cancel my subscription", cancelLoading:"Cancelling...", cancelYes:"Yes, cancel",
      cancelled:"Your subscription has been cancelled. Access will end at the close of the current period.",
      contact:"Contact us · contact@fyltra.app",
    },
  },
};
// L is set dynamically in App (same pattern as C)
let L = APP_T.fr;

/* ─── Fonts / Global CSS ─────────────────────────────────────────── */
const FONTS = `
  @import url('https://fonts.googleapis.com/css2?family=Josefin+Sans:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400&family=Barlow:wght@500;600;700&family=Outfit:wght@700;800;900&display=swap');
  @font-face { font-family: 'MariellaNove'; src: url('/mariella-noeve.ttf') format('truetype'); font-weight: normal; font-style: normal; font-display: swap; }
  @font-face { font-family: 'CoolveticaHv'; src: url('/coolvetica-hv-comp.otf') format('opentype'); font-weight: normal; font-style: normal; font-display: swap; }
  @font-face { font-family: 'Coolvetica'; src: url('/coolvetica-rg.otf') format('opentype'); font-weight: normal; font-style: normal; font-display: swap; }
  *{box-sizing:border-box;margin:0;padding:0;}
  body{background:var(--bg, #f8f7f5);overflow-x:hidden;transition:background 0.3s,color 0.3s;}
  input[type=date]::-webkit-calendar-picker-indicator,input[type=time]::-webkit-calendar-picker-indicator{filter:invert(0.4);opacity:0.5;}
  ::selection{background:#111;color:#fff;}
  ::-webkit-scrollbar{width:4px;}
  ::-webkit-scrollbar-track{background:var(--bg, #f8f7f5);}
  ::-webkit-scrollbar-thumb{background:#ccc;border-radius:2px;}
  .hscroll::-webkit-scrollbar{display:none;}
  .hscroll{-ms-overflow-style:none;scrollbar-width:none;}
  textarea{font-family:'Josefin Sans',sans-serif !important;}
  @keyframes slideFromRight{from{opacity:0;transform:translateX(40px);}to{opacity:1;transform:translateX(0);}}
  @keyframes slideToRight{from{opacity:1;transform:translateX(0);}to{opacity:0;transform:translateX(40px);}}
  @keyframes fadeInUp{from{opacity:0;transform:translateY(20px);}to{opacity:1;transform:translateY(0);}}
  @keyframes slideInAccount{from{opacity:0;transform:translateX(-24px);}to{opacity:1;transform:translateX(0);}}
  @keyframes slideOutAccount{from{opacity:1;transform:translateX(0);}to{opacity:0;transform:translateX(-24px);}}
  @keyframes fadeOutDown{from{opacity:1;transform:translateY(0);}to{opacity:0;transform:translateY(20px);}}
  @keyframes tabFadeIn{from{opacity:0;transform:scale(0.97) translateY(10px);}to{opacity:1;transform:scale(1) translateY(0);}}
  @keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
  @keyframes scaleIn{from{opacity:0;transform:scale(0.88);}to{opacity:1;transform:scale(1);}}
  @keyframes scaleOut{from{opacity:1;transform:scale(1);}to{opacity:0;transform:scale(0.88);}}
  @keyframes mt5-progress-bar{0%{width:0%;}60%{width:70%;}80%{width:82%;}100%{width:90%;}}
  @keyframes appleCardIn{from{opacity:0;transform:translateY(28px) scale(0.94);}to{opacity:1;transform:translateY(0) scale(1);}}
  .ap-stat{animation-name:appleCardIn;animation-duration:0.55s;animation-timing-function:cubic-bezier(0.34,1.2,0.64,1);animation-fill-mode:both;animation-delay:var(--sd,0ms);}
  @keyframes appleNumIn{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);}}
  .ap-card{transition:transform 0.38s cubic-bezier(0.25,0.46,0.45,0.94),box-shadow 0.38s ease;will-change:transform;}
  @media(hover:hover){.ap-card:hover{transform:translateY(-2px) scale(1.008) !important;}}
  .ap-card:active{transform:scale(0.982) !important;transition:transform 0.1s ease !important;}
  @media(hover:hover){button:not([disabled]):active{transform:scale(0.96) !important;transition:transform 0.07s ease !important;}}

`;

/* ─── Utils ──────────────────────────────────────────────────────── */
const load = (k, f) => { try { return JSON.parse(localStorage.getItem(k)) ?? f; } catch { return f; } };
const save = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };
const useIsMobile = () => {
  const [mobile, setMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return mobile;
};


/* ─── Base UI ────────────────────────────────────────────────────── */
const iStyle = {
  width:"100%", background:C.bg3, border:`1px solid ${C.border}`,
  borderRadius:10, padding:"13px 14px", color:C.white, fontSize:16,
  fontFamily:"'Josefin Sans',sans-serif", fontWeight:300, outline:"none",
  WebkitAppearance:"none", appearance:"none", letterSpacing:"0.05em",
  boxShadow:"inset 0 2px 6px rgba(0,0,0,0.18), inset 0 1px 2px rgba(0,0,0,0.1)",
};

function Label({ children }) {
  return <div style={{ fontSize:10, color:C.dim, textTransform:"uppercase", letterSpacing:"0.18em", fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, marginBottom:6 }}>{children}</div>;
}

function Field({ label, children, mb=16 }) {
  return <div style={{ marginBottom:mb }}><Label>{label}</Label>{children}</div>;
}

function Divider() {
  return <div style={{ height:1, background:`linear-gradient(to right,transparent,${C.border},transparent)`, margin:"18px 0" }} />;
}

function Chip({ label, active, onClick, dot, onDelete }) {
  const isDark = C.bg === "#0f0f0f";
  return (
    <button onClick={onClick} style={{
      padding:"8px 14px", borderRadius:20, cursor:"pointer",
      display:"inline-flex", alignItems:"center", justifyContent:"center", gap:5,
      transition:"all 0.22s cubic-bezier(.4,0,.2,1)",
      border: active ? "none" : isDark ? `1px solid ${C.gray2}` : "1px solid rgba(0,0,0,0.28)",
      background: active
        ? isDark
          ? "radial-gradient(ellipse 90% 90% at 50% 38%, rgba(245,245,245,0.92) 0%, rgba(210,210,210,0.84) 55%, rgba(225,225,225,0.88) 100%)"
          : "radial-gradient(ellipse 90% 90% at 50% 38%, rgba(25,25,25,0.92) 0%, rgba(55,55,55,0.84) 55%, rgba(40,40,40,0.88) 100%)"
        : isDark ? "transparent" : C.bg3,
      color: active ? (isDark ? "#111" : "#f0ede8") : isDark ? C.gray1 : C.white,
      fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight:active ? 600 : 500,
      letterSpacing:"0.08em", textTransform:"uppercase", flex:"1 1 auto", minWidth:44,
      boxShadow: active
        ? isDark
          ? "0 0 24px 7px rgba(255,255,255,0.22), 0 0 50px 14px rgba(255,255,255,0.09), 0 4px 14px rgba(0,0,0,0.55), 0 1px 3px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.5)"
          : "0 0 14px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.18)"
        : isDark ? "none" : "inset 0 2px 4px rgba(0,0,0,0.15)",
      transform: active ? "translateY(-1px)" : "translateY(0)",
    }}>
      {dot && <span style={{ width:6, height:6, borderRadius:"50%", background:dot, flexShrink:0 }}/>}
      {label}
      {onDelete && <span onClick={e=>{e.stopPropagation();onDelete();}} style={{marginLeft:2,opacity:0.45,fontSize:13,lineHeight:1,fontWeight:300,cursor:"pointer"}}>×</span>}
    </button>
  );
}

function ChipGroup({ options, value, onChange }) {
  return <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>{options.map(o => <Chip key={o} label={o} active={value===o} onClick={() => onChange(o)} />)}</div>;
}

function DatePicker({ value, onChange }) {
  const isDark = C.bg === "#0f0f0f";
  return (
    <input
      type="date"
      value={value || ""}
      onChange={e => onChange(e.target.value)}
      style={{
        width:"100%", boxSizing:"border-box",
        background: isDark ? "rgba(255,255,255,0.06)" : C.bg3,
        border: `1px solid ${C.border}`,
        borderRadius:10, padding:"10px 14px",
        color: C.white, fontSize:14,
        fontFamily:"'Josefin Sans',sans-serif",
        fontWeight:300, letterSpacing:"0.04em",
        outline:"none", cursor:"pointer",
        colorScheme: isDark ? "dark" : "light",
        textAlign:"center",
      }}
    />
  );
}

function TimePicker({ value, onChange }) {
  const isDark = C.bg === "#0f0f0f";
  const parts = (value || "00:00").split(":");
  const h = parts[0] || "00";
  const m = parts[1] || "00";
  const sel = { background:isDark?"rgba(255,255,255,0.06)":C.bg3, border:`1px solid ${C.border}`, borderRadius:10, padding:"10px 10px", color:C.white, fontSize:14, fontFamily:"'Josefin Sans',sans-serif", fontWeight:300, outline:"none", cursor:"pointer", colorScheme:isDark?"dark":"light", flex:1, textAlign:"center", appearance:"none", WebkitAppearance:"none" };
  return (
    <div style={{display:"flex",alignItems:"center",gap:6}}>
      <select value={h} onChange={e=>onChange(`${e.target.value}:${m}`)} style={sel}>
        {Array.from({length:24},(_,i)=>String(i).padStart(2,"0")).map(v=><option key={v} value={v}>{v}h</option>)}
      </select>
      <span style={{color:C.gray1,fontSize:18,fontWeight:300,flexShrink:0}}>:</span>
      <select value={m} onChange={e=>onChange(`${h}:${e.target.value}`)} style={sel}>
        {Array.from({length:60},(_,i)=>String(i).padStart(2,"0")).map(v=><option key={v} value={v}>{v}</option>)}
      </select>
    </div>
  );
}

function StatCard({ label, value, color, small, staggerIdx=0 }) {
  const isDark = C.bg === "#0f0f0f";
  return (
    <div className="ap-card ap-stat" style={{ "--sd":`${staggerIdx * 100}ms`, background:C.bg2, border:`1px solid ${C.border}`, borderTop:`2px solid ${color||C.accent}`, borderRadius:14, padding:small ? (!isMobile?"20px 22px":"16px 18px") : (!isMobile?"28px 24px":"20px 18px"), position:"relative", boxShadow: isDark ? "0 6px 32px rgba(0,0,0,0.65), 0 2px 8px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1), 0 -2px 28px rgba(255,255,255,0.07), inset 0 1px 0 rgba(255,255,255,0.36)" : "0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.06)" }}>
      <div style={{ fontSize:9, color:C.dim, textTransform:"uppercase", letterSpacing:"0.18em", fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:small ? 16 : 24, fontWeight:300, color:color||C.white, fontFamily:"'Josefin Sans',sans-serif", lineHeight:1, letterSpacing:"0.05em" }}>{value}</div>
    </div>
  );
}

function PageTitle({ sub, title }) {
  return (
    <div style={{ marginBottom:22 }}>
      <div style={{ fontSize:11, color:C.dim, letterSpacing:"0.25em", textTransform:"uppercase", marginBottom:4, fontFamily:"'Josefin Sans',sans-serif" }}>{sub}</div>
      <div style={{ fontFamily:"'Josefin Sans',sans-serif", fontSize:26, fontWeight:700, color:C.white, letterSpacing:"-0.025em" }}>{title}</div>
    </div>
  );
}

/* ─── Mobile Pill Nav ────────────────────────────────────────────── */
function PillNav({ view, setView, darkMode, canUseAI, canUseReplay, lang }) {
  const [hovered, setHovered] = useState(null);
  return (
    <div style={{ position:"fixed", bottom:24, left:"50%", transform:"translateX(-50%)", zIndex:200, display:"flex", alignItems:"center", background:"linear-gradient(180deg, rgba(60,60,60,0.97) 0%, rgba(18,18,18,0.99) 55%, rgba(8,8,8,1) 100%)", backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)", borderRadius:50, padding:"6px 8px", gap:2, boxShadow:"0 6px 20px rgba(0,0,0,0.5), 0 20px 50px rgba(0,0,0,0.4), 0 0 60px rgba(255,255,255,0.11), 0 0 0 1px rgba(255,255,255,0.13), inset 0 1px 0 rgba(255,255,255,0.38), inset 0 -2px 0 rgba(0,0,0,0.8)", border:"1px solid rgba(255,255,255,0.1)" }}>
      {NAV().map(item => {
        const active = view === item.key;
        const isHovered = hovered === item.key && !active;
        const locked = (item.key === "ai" && !canUseAI);
        const soon = false;
        return (
          <button key={item.key} onClick={() => setView(item.key)} onMouseEnter={() => setHovered(item.key)} onMouseLeave={() => setHovered(null)} style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:4, padding:"9px 16px", borderRadius:44, border:"none", cursor:"pointer", background:active ? "radial-gradient(ellipse 90% 90% at 50% 50%, rgba(252,252,252,0.93) 0%, rgba(225,225,225,0.85) 55%, rgba(200,200,200,0.75) 100%)" : isHovered ? "rgba(255,255,255,0.05)" : "transparent", boxShadow:active ? "0 0 26px 8px rgba(255,255,255,0.22), 0 0 50px 16px rgba(255,255,255,0.09), 0 6px 20px rgba(0,0,0,0.5), 0 2px 6px rgba(0,0,0,0.3)" : "none", transition:"all 0.25s cubic-bezier(.4,0,.2,1)", position:"relative", zIndex:1, opacity: locked ? 0.45 : 1 }}>
            {item.key === "charts"
            ? <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 16" style={{height:11,width:"auto",opacity:active?0.85:0.55,transition:"opacity 0.2s,fill 0.2s",fill:active?"#222":"#fff",flexShrink:0}}><path d="M12 0H0v6h6v10h6V0zm14 16h-8l6-16h8l-6 16zM17 6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/></svg>
            : <span style={{ fontSize:9, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.14em", textTransform:"uppercase", color:active ? "#222" : "rgba(255,255,255,0.45)", transition:"color 0.2s", whiteSpace:"nowrap" }}>{item.label}</span>
          }
            {locked && <span style={{ fontSize:7, fontFamily:"'JetBrains Mono',monospace", fontWeight:700, letterSpacing:"0.1em", color:"rgba(232,205,169,0.5)", textTransform:"uppercase" }}>PRO</span>}
            {soon && <span style={{ position:"absolute", top:3, right:5, fontSize:6, fontFamily:"'Josefin Sans',sans-serif", fontWeight:700, letterSpacing:"0.13em", color:"rgba(232,205,169,0.7)", textTransform:"uppercase", lineHeight:1, pointerEvents:"none" }}>{lang==="en"?"SOON":"BIENTÔT"}</span>}
          </button>
        );
      })}
    </div>
  );
}

/* ─── Desktop Sidebar ────────────────────────────────────────────── */
const FULL_NAV_KEYS = [
  { key:"home",       lk:"home" },
  { key:"propfirm",   lk:"account" },
  { key:"add",        lk:"trade" },
  { key:"notes",      lk:"notes" },
  { key:"trades",     lk:"history" },
  { key:"history",    lk:"stats" },
  { key:"strategy",   lk:"plan" },
  { key:"classement", lk:"ranking" },
  { key:"rewards",    lk:"rewards" },
  { key:"test",       lk:"test" },
  { key:"charts",     lk:"charts" },
  { key:"replay",     lk:"replay" },
  { key:"ai",         lk:"ai" },
];
// ─── Fyltragram ───────────────────────────────────────────────────────────────
function Fyltragram({ axes, size = 240, mini = false, isDark = true, labelFontSize = 6.5, valueFontSize = 10 }) {
  const cx=size/2, cy=size/2;
  const R=mini?size*0.34:size*0.30;
  const angDeg=[270,330,30,90,150,210];
  const angs=angDeg.map(d=>d*Math.PI/180);
  const pt=(a,r)=>[cx+r*Math.cos(a),cy+r*Math.sin(a)];
  const levels=mini?[0.5,1]:[0.25,0.5,0.75,1];
  const dataPoly=axes.map((ax,i)=>pt(angs[i],(ax.value/100)*R));
  const uid=`fg${size}${mini?'m':'f'}`;
  const gridCol=isDark?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.1)';
  const axisCol=isDark?'rgba(255,255,255,0.08)':'rgba(0,0,0,0.12)';
  const lblCol=isDark?'rgba(255,255,255,0.4)':'rgba(0,0,0,0.5)';
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
      style={{display:'block',margin:'0 auto',overflow:'visible'}}>
      <defs>
        <radialGradient id={uid} cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#4ade80" stopOpacity={isDark?0.5:0.4}/>
          <stop offset="100%" stopColor="#E8CDA9" stopOpacity={isDark?0.22:0.35}/>
        </radialGradient>
      </defs>
      {levels.map(l=>(
        <polygon key={l} fill="none" stroke={gridCol} strokeWidth={0.75}
          points={angs.map(a=>pt(a,R*l).join(',')).join(' ')}/>
      ))}
      {angs.map((a,i)=>{const[x2,y2]=pt(a,R);return<line key={i} x1={cx} y1={cy} x2={x2} y2={y2} stroke={axisCol} strokeWidth={0.75}/>;} )}
      <polygon points={dataPoly.map(p=>p.join(',')).join(' ')}
        fill={`url(#${uid})`} stroke="#E8CDA9" strokeWidth={mini?1:1.5} strokeOpacity={0.7}/>
      {dataPoly.map((p,i)=>(
        <circle key={i} cx={p[0]} cy={p[1]} r={mini?2:3} fill="#E8CDA9" opacity={0.9}/>
      ))}
      {!mini&&axes.map((ax,i)=>{
        const[lx,ly]=pt(angs[i],R+22);
        const col=ax.value>=70?'#4ade80':ax.value>=40?'#fb923c':'#f87171';
        return(<g key={i}>
          <text x={lx} y={ly-7} textAnchor="middle" dominantBaseline="middle"
            fill={lblCol} fontSize={labelFontSize}
            fontFamily="'Josefin Sans',sans-serif" letterSpacing="0.8">{ax.label}</text>
          <text x={lx} y={ly+8} textAnchor="middle" dominantBaseline="middle"
            fill={col} fontSize={valueFontSize} fontWeight={700}
            fontFamily="'Josefin Sans',sans-serif">{ax.value}</text>
        </g>);
      })}
    </svg>
  );
}

// ─── ScoreCircle ──────────────────────────────────────────────────────────────
function ScoreCircle({ score, max = 100 }) {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    if (score == null) { setPct(0); return; }
    let raf;
    const start = Date.now(), dur = 1400;
    const tick = () => {
      const t = Math.min((Date.now()-start)/dur,1);
      const e = t<0.5?4*t*t*t:1-Math.pow(-2*t+2,3)/2;
      setPct(e); if(t<1) raf=requestAnimationFrame(tick); else setPct(1);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [score]);
  if (score==null) return null;
  const size=100, r=38, cx=50, cy=50;
  const fillPct = score / max;
  const circ=2*Math.PI*r;
  const offset=circ*(1-pct*fillPct);
  const displayed=Math.round(pct*score);
  const color=max===1000?(score>=700?'#4ade80':score>=400?'#fb923c':'#f87171'):(score>=70?'#4ade80':score>=40?'#fb923c':'#f87171');
  const fs = displayed >= 100 ? 16 : 22;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{display:'block'}}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={7}/>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={7}
        strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
        transform={`rotate(-90 ${cx} ${cy})`}/>
      <text x={cx} y={cy-3} textAnchor="middle" dominantBaseline="middle"
        fill={color} fontSize={fs} fontWeight={800} fontFamily="'Josefin Sans',sans-serif">{displayed}</text>
      <text x={cx} y={cy+14} textAnchor="middle" dominantBaseline="middle"
        fill="rgba(255,255,255,0.28)" fontSize={7} fontFamily="'Josefin Sans',sans-serif" letterSpacing="2">SCORE</text>
    </svg>
  );
}

// ─── EmotionRadar ─────────────────────────────────────────────────────────────
function EmotionRadar({ axes }) {
  const size=150, cx=75, cy=75, R=46;
  const angles=[270,30,150];
  const pt=(ang,r)=>{const rad=ang*Math.PI/180;return[cx+r*Math.cos(rad),cy+r*Math.sin(rad)];};
  const poly=axes.map((ax,i)=>pt(angles[i],(ax.value??0)*R));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{display:'block',margin:'0 auto'}}>
      {[.25,.5,.75,1].map(l=><polygon key={l} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={1} points={angles.map(a=>pt(a,R*l).join(',')).join(' ')}/>)}
      {angles.map((a,i)=>{const[x2,y2]=pt(a,R);return<line key={i} x1={cx} y1={cy} x2={x2} y2={y2} stroke="rgba(255,255,255,0.08)" strokeWidth={1}/>;} )}
      <polygon points={poly.map(p=>p.join(',')).join(' ')} fill="rgba(232,205,169,0.1)" stroke="rgba(232,205,169,0.45)" strokeWidth={1.5}/>
      {poly.map((p,i)=><circle key={i} cx={p[0]} cy={p[1]} r={4} fill={axes[i].color} stroke="#111" strokeWidth={1.5}/>)}
      {axes.map((ax,i)=>{const[lx,ly]=pt(angles[i],R+22);const pct=Math.round((ax.value??0)*100);return(<g key={i}>
        <text x={lx} y={ly-6} textAnchor="middle" fill="rgba(255,255,255,0.38)" fontSize={8} fontFamily="'Josefin Sans',sans-serif" letterSpacing="1">{ax.label}</text>
        <text x={lx} y={ly+8} textAnchor="middle" fill={ax.color} fontSize={11} fontWeight={700} fontFamily="'Josefin Sans',sans-serif">{pct}%</text>
      </g>);})}
    </svg>
  );
}

// ─── SessionTimeline ──────────────────────────────────────────────────────────
function SessionTimeline({ trades, fms, currency }) {
  const [hov, setHov] = useState(null);
  if(!trades.length) return null;
  const maxP=Math.max(...trades.map(t=>Math.abs(t.pnl||0)),1);
  return (
    <div style={{position:'relative',padding:'12px 0 20px'}}>
      <div style={{position:'absolute',top:'30px',left:16,right:16,height:1,background:'rgba(255,255,255,0.08)'}}/>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-around',position:'relative',gap:4}}>
        {trades.map((t,i)=>{
          const win=t.result==='WIN';
          const sz=8+Math.round((Math.abs(t.pnl||0)/maxP)*14);
          const col=win?'#4ade80':'#f87171';
          return(
            <div key={t.id||i} style={{position:'relative',display:'flex',flexDirection:'column',alignItems:'center',gap:6,paddingTop:`${(18-sz)/2}px`}}
              onMouseEnter={()=>setHov(i)} onMouseLeave={()=>setHov(null)}>
              <div style={{width:sz,height:sz,borderRadius:'50%',background:col,boxShadow:`0 0 ${sz/2}px ${col}55`,cursor:'pointer',
                transition:'transform 0.15s',transform:hov===i?'scale(1.4)':'scale(1)'}}/>
              {hov===i&&(
                <div style={{position:'absolute',bottom:'calc(100% + 14px)',left:'50%',transform:'translateX(-50%)',
                  background:'#181818',border:'1px solid rgba(255,255,255,0.12)',borderRadius:8,padding:'8px 12px',
                  zIndex:30,whiteSpace:'nowrap',boxShadow:'0 4px 20px rgba(0,0,0,0.6)'}}>
                  <div style={{fontSize:9,color:'rgba(255,255,255,0.4)',fontFamily:"'Josefin Sans',sans-serif",marginBottom:3}}>
                    {[t.instrument,t.direction,t.session].filter(Boolean).join(' · ')}
                  </div>
                  <div style={{fontSize:13,fontWeight:700,color:col,fontFamily:"'Josefin Sans',sans-serif"}}>{win?'+':''}{fms(t.pnl||0)}{currency}</div>
                  {t.rr>0&&<div style={{fontSize:9,color:'rgba(255,255,255,0.35)',fontFamily:"'Josefin Sans',sans-serif"}}>R/R {Number(t.rr).toFixed(2)}</div>}
                  {t.emotion&&<div style={{fontSize:9,color:'rgba(255,255,255,0.35)',fontFamily:"'Josefin Sans',sans-serif",marginTop:2}}>{t.emotion}</div>}
                </div>
              )}
              <div style={{fontSize:8,color:'rgba(255,255,255,0.2)',fontFamily:"'Josefin Sans',sans-serif"}}>{win?'W':'L'}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Sidebar({ view, setView, darkMode, onSignOut, nickname, firstName, avatarUrl, canUseAI, canUseReplay, onUserCardClick, lang }) {
  const [hovered, setHovered] = useState(null);
  const pillStyle = { background:"linear-gradient(180deg, rgba(60,60,60,0.97) 0%, rgba(18,18,18,0.99) 55%, rgba(8,8,8,1) 100%)", backdropFilter:"blur(24px)", WebkitBackdropFilter:"blur(24px)", borderRadius:24, padding:"10px", display:"flex", flexDirection:"column", gap:4, boxShadow:"0 6px 20px rgba(0,0,0,0.5), 0 20px 50px rgba(0,0,0,0.4), 0 0 60px rgba(255,255,255,0.11), 0 0 0 1px rgba(255,255,255,0.13), inset 0 1px 0 rgba(255,255,255,0.38), inset 0 -2px 0 rgba(0,0,0,0.8)", border:"1px solid rgba(255,255,255,0.1)" };

  const NavBtn = ({ item }) => {
    const active = view === item.key;
    const isHovered = hovered === item.key && !active;
    return (
      <button onClick={() => setView(item.key)} onMouseEnter={() => setHovered(item.key)} onMouseLeave={() => setHovered(null)} style={{
        display:"flex", alignItems:"center", gap:14, width:"100%",
        padding: active ? "10px 18px" : "10px 14px",
        borderRadius:16, border:"none", cursor:"pointer",
        background: active ? "radial-gradient(ellipse 110% 100% at 50% 35%, rgba(252,252,252,0.93) 0%, rgba(225,225,225,0.85) 55%, rgba(200,200,200,0.75) 100%)" : isHovered ? "rgba(255,255,255,0.05)" : "transparent",
        transition:"all 0.25s cubic-bezier(.4,0,.2,1)",
        boxShadow: active ? "0 0 26px 8px rgba(255,255,255,0.22), 0 0 50px 16px rgba(255,255,255,0.09), 0 6px 20px rgba(0,0,0,0.5), 0 2px 6px rgba(0,0,0,0.3)" : isHovered ? "0 2px 8px rgba(0,0,0,0.2)" : "none",
        transform: active ? "translateY(-1px)" : isHovered ? "translateY(-0.5px)" : "translateY(0)",
        position:"relative", zIndex:1,
      }}>
        {item.key === "charts"
          ? <><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 16" style={{height:13,width:"auto",opacity:active?0.85:0.55,transition:"opacity 0.2s,fill 0.2s",fill:active?"#222":"rgba(255,255,255,0.6)",flexShrink:0}}><path d="M12 0H0v6h6v10h6V0zm14 16h-8l6-16h8l-6 16zM17 6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/></svg><span style={{ fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight: active ? 700 : 300, letterSpacing:"0.1em", textTransform:"uppercase", color:active ? "#222" : "rgba(255,255,255,0.4)", transition:"color 0.25s", whiteSpace:"nowrap" }}>TradingView</span></>
          : <span style={{ fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight: active ? 700 : 300, letterSpacing:"0.1em", textTransform:"uppercase", color:active ? "#222" : "rgba(255,255,255,0.4)", transition:"color 0.25s", whiteSpace:"nowrap" }}>{item.label}</span>
        }
      </button>
    );
  };

  return (
    <div style={{ width:220, minHeight:"100vh", background:C.bg2, borderRight:`1px solid ${C.border}`, flexDirection:"column", position:"fixed", left:0, top:0, paddingBottom:12, zIndex:50, display:"flex", boxShadow:"4px 0 24px rgba(0,0,0,0.12)" }}>
      {/* Logo */}
      <div style={{ height:92, flexShrink:0, borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <img src={darkMode?"/fyltra-logo-black.svg":"/fyltra-logo-white.svg"} style={{height:76,width:"auto"}} alt="Fyltra"/>
      </div>

      {/* Main nav pill */}
      <div style={{ padding:"16px 12px 0" }}>
        <div style={pillStyle}>
          {FULL_NAV_KEYS.slice(0,10).map(n => <NavBtn key={n.key} item={{...n, label:L.nav[n.lk]}} />)}
        </div>
      </div>

      {/* TradingView + Replay pill — collé au pill principal */}
      <div style={{ padding:"6px 12px 0" }}>
        <div style={pillStyle}>
          <NavBtn item={{key:"charts", lk:"charts", label:"TradingView"}} />
          <NavBtn item={{key:"replay", lk:"replay", label:L.nav.replay}} />
        </div>
      </div>

      {/* AI pill — collé au TV+Replay */}
      <div style={{ padding:"6px 12px 0", opacity: canUseAI ? 1 : 0.45 }}>
        <div style={pillStyle}>
          <NavBtn item={{key:"ai", lk:"ai", label:L.nav.ai}} />
        </div>
      </div>

      {/* Spacer */}
      <div style={{ flex:1 }} />

      {/* User card */}
      <div style={{ padding:"0 12px 16px" }}>
        <button onClick={onUserCardClick} style={{
          width:"100%", display:"flex", alignItems:"center", gap:12,
          padding:"10px 14px", borderRadius:20, border:"1px solid rgba(255,255,255,0.1)",
          cursor:"pointer", background:"linear-gradient(180deg,rgba(60,60,60,0.97) 0%,rgba(18,18,18,0.99) 55%,rgba(8,8,8,1) 100%)",
          boxShadow:"0 6px 20px rgba(0,0,0,0.5),0 0 0 1px rgba(255,255,255,0.13),inset 0 1px 0 rgba(255,255,255,0.38),inset 0 -2px 0 rgba(0,0,0,0.8)",
          transition:"all 0.22s cubic-bezier(.4,0,.2,1)",
        }}
        onMouseEnter={e => e.currentTarget.style.background="linear-gradient(180deg,rgba(80,80,80,0.97) 0%,rgba(30,30,30,0.99) 55%,rgba(12,12,12,1) 100%)"}
        onMouseLeave={e => e.currentTarget.style.background="linear-gradient(180deg,rgba(60,60,60,0.97) 0%,rgba(18,18,18,0.99) 55%,rgba(8,8,8,1) 100%)"}
        >
          {/* Avatar circle */}
          <div style={{
            width:32, height:32, borderRadius:"50%", flexShrink:0,
            background:"linear-gradient(135deg,rgba(232,205,169,0.5) 0%,rgba(180,140,100,0.5) 100%)",
            border:"1px solid rgba(232,205,169,0.25)",
            display:"flex", alignItems:"center", justifyContent:"center",
            overflow:"hidden",
          }}>
            {avatarUrl
              ? <img src={avatarUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}} />
              : <span style={{fontSize:13,fontWeight:700,color:"rgba(232,205,169,0.9)",fontFamily:"'Outfit',sans-serif"}}>
                  {(nickname||firstName||"?")[0].toUpperCase()}
                </span>
            }
          </div>
          {/* Name */}
          <div style={{flex:1,minWidth:0,textAlign:"left"}}>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.85)",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.04em",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
              {nickname || firstName || "Trader"}
            </div>
          </div>
          <span style={{fontSize:10,color:"rgba(255,255,255,0.2)"}}>›</span>
        </button>
      </div>
    </div>
  );
}

/* ─── TradingView Chart Widget ───────────────────────────────────── */
function TradingViewChart({ symbol, interval, darkMode, timezone="Etc/UTC" }) {
  const src = `https://www.tradingview.com/widgetembed/?symbol=${encodeURIComponent(symbol)}&interval=${interval}&theme=${darkMode?"dark":"light"}&style=1&locale=fr&allow_symbol_change=1&hide_side_toolbar=0&withdateranges=1&timezone=${encodeURIComponent(timezone)}&saveimage=0`;
  return (
    <iframe
      key={src}
      src={src}
      style={{width:"100%",height:"100%",border:"none",display:"block"}}
      allowFullScreen
      title="TradingView"
    />
  );
}

/* ─── Indicator utilities ────────────────────────────────────────── */
const calcSMA = (data, n) => {
  const out = [];
  for (let i = n - 1; i < data.length; i++) {
    let s = 0; for (let j = 0; j < n; j++) s += data[i - j].close;
    out.push({ time: data[i].time, value: s / n });
  }
  return out;
};
const calcEMA = (data, n) => {
  if (!data.length) return [];
  const k = 2 / (n + 1); let ema = data[0].close; const out = [];
  for (let i = 0; i < data.length; i++) {
    ema = i === 0 ? data[i].close : data[i].close * k + ema * (1 - k);
    if (i >= n - 1) out.push({ time: data[i].time, value: ema });
  }
  return out;
};
const calcBB = (data, n, mult) => {
  const upper = [], mid = [], lower = [];
  for (let i = n - 1; i < data.length; i++) {
    const sl = data.slice(i - n + 1, i + 1);
    const mean = sl.reduce((s, c) => s + c.close, 0) / n;
    const std  = Math.sqrt(sl.reduce((s, c) => s + (c.close - mean) ** 2, 0) / n);
    mid.push({ time: data[i].time, value: mean });
    upper.push({ time: data[i].time, value: mean + mult * std });
    lower.push({ time: data[i].time, value: mean - mult * std });
  }
  return { upper, mid, lower };
};
const IND_CONFIG = [
  { key:'sma20',  label:'SMA 20',    color:'#2196f3' },
  { key:'sma50',  label:'SMA 50',    color:'#ff9800' },
  { key:'sma200', label:'SMA 200',   color:'#f44336' },
  { key:'ema20',  label:'EMA 20',    color:'#ab47bc' },
  { key:'ema50',  label:'EMA 50',    color:'#00bcd4' },
  { key:'bb',     label:'BB (20,2)', color:'#78909c' },
  { key:'vol',    label:'Volume',    color:'#26a69a' },
];

/* ─── Replay Chart ───────────────────────────────────────────────── */
const REPLAY_GROUPS = [
  { g:"Crypto", items:[
    {l:"BTC/USDT",v:"BTCUSDT"},{l:"ETH/USDT",v:"ETHUSDT"},{l:"SOL/USDT",v:"SOLUSDT"},
    {l:"BNB/USDT",v:"BNBUSDT"},{l:"XRP/USDT",v:"XRPUSDT"},{l:"DOGE/USDT",v:"DOGEUSDT"},
    {l:"ADA/USDT",v:"ADAUSDT"},{l:"AVAX/USDT",v:"AVAXUSDT"},{l:"MATIC/USDT",v:"MATICUSDT"},
    {l:"LINK/USDT",v:"LINKUSDT"},{l:"DOT/USDT",v:"DOTUSDT"},{l:"LTC/USDT",v:"LTCUSDT"},
  ]},
  { g:"Forex", items:[
    {l:"EUR/USD",v:"EURUSD=X"},{l:"GBP/USD",v:"GBPUSD=X"},{l:"USD/JPY",v:"USDJPY=X"},
    {l:"AUD/USD",v:"AUDUSD=X"},{l:"USD/CHF",v:"USDCHF=X"},{l:"USD/CAD",v:"USDCAD=X"},
    {l:"NZD/USD",v:"NZDUSD=X"},{l:"EUR/GBP",v:"EURGBP=X"},{l:"EUR/JPY",v:"EURJPY=X"},
  ]},
  { g:"Matières premières", items:[
    {l:"Or (Gold)",v:"GC=F"},{l:"Argent",v:"SI=F"},{l:"Pétrole (CL)",v:"CL=F"},
    {l:"Gaz naturel",v:"NG=F"},{l:"Cuivre",v:"HG=F"},
  ]},
  { g:"Indices / Futures", items:[
    {l:"Nasdaq (NQ)",v:"NQ=F"},{l:"S&P 500 (ES)",v:"ES=F"},{l:"Dow Jones (YM)",v:"YM=F"},
    {l:"Russell 2000 (RTY)",v:"RTY=F"},{l:"DAX",v:"^GDAXI"},{l:"CAC 40",v:"^FCHI"},
    {l:"Nikkei",v:"^N225"},{l:"US 10Y Bond (ZN)",v:"ZN=F"},
  ]},
];
const REPLAY_TFS    = ["1m","5m","15m","30m","1h","4h","8h","12h","1d","3d","1w","1M"];
const REPLAY_SPEEDS = [0.5, 1, 2, 5, 10, 30];
const REPLAY_TF_MS  = { "1m":60e3,"5m":300e3,"15m":900e3,"30m":1800e3,"1h":3600e3,"4h":14400e3,"8h":28800e3,"12h":43200e3,"1d":864e5,"3d":2592e5,"1w":6048e5,"1M":2592e6 };

function ReplayChart({ darkMode, isMobile, onTradeClose }) {
  const containerRef = useRef(null);
  const chartRef     = useRef(null);
  const seriesRef    = useRef(null);
  const timerRef     = useRef(null);

  const [sym,         setSym]         = useState("BTCUSDT");
  const [tf,          setTf]          = useState("1d");
  const [candles,     setCandles]     = useState([]);
  const [idx,         setIdx]         = useState(50);
  const [playing,     setPlaying]     = useState(false);
  const [speed,       setSpeed]       = useState(1);
  const [loading,     setLoading]     = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err,         setErr]         = useState(null);
  const [hasMore,     setHasMore]     = useState(true);
  const [posMode,     setPosMode]     = useState(null);
  const [position,    setPosition]    = useState(null);
  const [indicators,  setIndicators]  = useState({});
  const [showIndPanel,setShowIndPanel]= useState(false);
  const [resolved,    setResolved]    = useState(null); // null | { result:'tp'|'sl', pnl, pct }

  const indSeriesRef = useRef({});

  const posModeRef     = useRef(null);
  const posLinesRef    = useRef({});
  const overlayRef     = useRef(null);
  const drawOverlayRef = useRef(null);
  const draggingRef    = useRef(null);
  const candlesRef     = useRef([]);
  const idxRef         = useRef(50);
  const positionRef       = useRef(null);
  const tfRef             = useRef("1d");
  const speedRef          = useRef(speed);
  const lastResolvedIdxRef  = useRef(-1);
  const resolvedRef         = useRef(null);
  const updatePriceLinesRef = useRef(null);

  useEffect(() => { posModeRef.current = posMode; }, [posMode]);
  useEffect(() => { positionRef.current = position; }, [position]);
  useEffect(() => { candlesRef.current = candles; idxRef.current = idx; }, [candles, idx]); // eslint-disable-line
  useEffect(() => { tfRef.current = tf; }, [tf]);
  useEffect(() => { speedRef.current = speed; }, [speed]);

  // ── theme colors (TradingView palette)
  const rC = {
    bg:  darkMode ? "#131722" : "#f8f7f5",
    bg2: darkMode ? "#1e222d" : "#ffffff",
    bdr: darkMode ? "#2a2e39" : "#e0e3eb",
    txt: darkMode ? "#d1d4dc" : "#131722",
    dim: darkMode ? "#787b86" : "#9598a1",
    wht: darkMode ? "#ffffff" : "#131722",
    sel: darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
  };

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: { background:{ color:rC.bg }, textColor:rC.txt },
      grid:   { vertLines:{ color: darkMode?"#2a2e39":"#e8e8e8" }, horzLines:{ color: darkMode?"#2a2e39":"#e8e8e8" } },
      timeScale: { timeVisible:true, secondsVisible:false },
      crosshair: { mode:1 },
    });
    const series = chart.addSeries(CandlestickSeries, {
      upColor:"#26a69a", downColor:"#ef5350", borderVisible:false,
      wickUpColor:"#26a69a", wickDownColor:"#ef5350",
    });
    chart.subscribeClick((param) => {
      if (!posModeRef.current || !param.point) return;
      const price = seriesRef.current?.coordinateToPrice(param.point.y);
      if (price == null) return;
      const isLong = posModeRef.current === 'long';
      const tp = price * (isLong ? 1.01 : 0.99);
      const sl = price * (isLong ? 0.99 : 1.01);
      const entryTime = candlesRef.current[idxRef.current - 1]?.time ?? null;
      const tfSec     = (REPLAY_TF_MS[tfRef.current] || 864e5) / 1000;
      const rightTime = (entryTime ?? 0) + 60 * tfSec;
      setPosition({ type: posModeRef.current, entry: price, tp, sl, entryTime, rightTime });
      setPosMode(null);
    });
    const overlayHandler = () => drawOverlayRef.current?.();
    chart.subscribeCrosshairMove(overlayHandler);
    chart.timeScale().subscribeVisibleLogicalRangeChange(overlayHandler);
    chartRef.current  = chart;
    seriesRef.current = series;
    return () => {
      chart.unsubscribeCrosshairMove(overlayHandler);
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(overlayHandler);
      chart.remove();
      chartRef.current = null; seriesRef.current = null; indSeriesRef.current = {};
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    chartRef.current?.applyOptions({
      layout: { background:{ color:rC.bg }, textColor:rC.txt },
      grid:   { vertLines:{ color: darkMode?"#2a2e39":"#e8e8e8" }, horzLines:{ color: darkMode?"#2a2e39":"#e8e8e8" } },
    });
  }, [darkMode]); // eslint-disable-line

  useEffect(() => {
    const onKey = (e) => {
      if (['INPUT','TEXTAREA','SELECT'].includes(e.target.tagName)) return;
      if (e.key === ' ') { e.preventDefault(); setPlaying(p => !p); }
      else if (e.key === 'ArrowLeft')  { e.preventDefault(); setPlaying(false); setIdx(p => Math.max(1, p - (e.shiftKey ? 10 : 1))); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); setPlaying(false); setIdx(p => Math.min(candlesRef.current.length, p + (e.shiftKey ? 10 : 1))); }
      else if (e.key === ',' || e.key === '<') { const i = REPLAY_SPEEDS.indexOf(speedRef.current); if (i > 0) setSpeed(REPLAY_SPEEDS[i - 1]); }
      else if (e.key === '.' || e.key === '>') { const i = REPLAY_SPEEDS.indexOf(speedRef.current); if (i < REPLAY_SPEEDS.length - 1) setSpeed(REPLAY_SPEEDS[i + 1]); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []); // eslint-disable-line

  // ── Price line precision helper
  const fmtP = (p) => p == null ? '' : p >= 1000 ? p.toFixed(2) : p >= 10 ? p.toFixed(3) : p >= 0.1 ? p.toFixed(4) : p.toFixed(6);

  // ── Draw / clear position price lines
  useEffect(() => { updatePriceLinesRef.current?.(position); }, [position]); // eslint-disable-line

  // ── Clear position + indicators on symbol / tf change
  useEffect(() => { setPosition(null); setPosMode(null); setResolved(null); lastResolvedIdxRef.current = -1; }, [sym, tf]); // eslint-disable-line

  // ── Resolved: sync ref, pause, update visuals, fire callback
  useEffect(() => {
    resolvedRef.current = resolved;
    if (!resolved) return;
    setPlaying(false);
    updatePriceLinesRef.current?.(positionRef.current, resolved);
    drawOverlayRef.current?.();
    if (onTradeClose && positionRef.current) {
      const pos = positionRef.current;
      onTradeClose({
        direction: pos.type,
        entry:     pos.entry,
        tp:        pos.tp,
        sl:        pos.sl,
        result:    resolved.result === 'tp' ? 'WIN' : 'LOSS',
        pnl:       resolved.pnl,
        pct:       resolved.pct,
        entryDate: pos.entryTime ? new Date(pos.entryTime * 1000).toISOString().split('T')[0] : null,
      });
    }
  }, [resolved]); // eslint-disable-line

  // ── TP/SL resolution detection
  useEffect(() => {
    if (!position || resolved) return;
    const { entry, tp, sl, type, entryTime } = position;
    const isLong = type === 'long';
    const entryIdx = candles.findIndex(c => c.time >= (entryTime ?? 0));
    const startIdx = Math.max(0, entryIdx + 1, lastResolvedIdxRef.current + 1);
    for (let i = startIdx; i < idx; i++) {
      const c = candles[i];
      if (!c) continue;
      if (isLong) {
        if (tp != null && c.high >= tp) { lastResolvedIdxRef.current = i; setResolved({ result:'tp', pnl: tp - entry, pct: ((tp - entry) / entry) * 100 }); return; }
        if (sl != null && c.low  <= sl) { lastResolvedIdxRef.current = i; setResolved({ result:'sl', pnl: sl - entry, pct: ((sl - entry) / entry) * 100 }); return; }
      } else {
        if (tp != null && c.low  <= tp) { lastResolvedIdxRef.current = i; setResolved({ result:'tp', pnl: entry - tp, pct: ((entry - tp) / entry) * 100 }); return; }
        if (sl != null && c.high >= sl) { lastResolvedIdxRef.current = i; setResolved({ result:'sl', pnl: entry - sl, pct: ((entry - sl) / entry) * 100 }); return; }
      }
    }
  }, [idx, position, candles]); // eslint-disable-line

  // ── Indicator series management
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const visible = candles.slice(0, idx);
    const lOpts = { lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false };
    IND_CONFIG.forEach(({ key, color }) => {
      const on = !!indicators[key];
      if (!on) {
        const ex = indSeriesRef.current[key];
        if (ex) {
          if (key === 'bb') {
            try { chart.removeSeries(ex.upper); } catch {}
            try { chart.removeSeries(ex.mid);   } catch {}
            try { chart.removeSeries(ex.lower); } catch {}
          } else { try { chart.removeSeries(ex); } catch {} }
          delete indSeriesRef.current[key];
        }
        return;
      }
      if (!indSeriesRef.current[key]) {
        if (key === 'bb') {
          indSeriesRef.current[key] = {
            upper: chart.addSeries(LineSeries, { ...lOpts, color: color + '88' }),
            mid:   chart.addSeries(LineSeries, { ...lOpts, color }),
            lower: chart.addSeries(LineSeries, { ...lOpts, color: color + '88' }),
          };
        } else if (key === 'vol') {
          const vs = chart.addSeries(HistogramSeries, { priceScaleId:'vol', priceFormat:{ type:'volume' } });
          chart.priceScale('vol').applyOptions({ scaleMargins:{ top:0.8, bottom:0 } });
          indSeriesRef.current[key] = vs;
        } else {
          indSeriesRef.current[key] = chart.addSeries(LineSeries, { ...lOpts, color });
        }
      }
      const s = indSeriesRef.current[key];
      if (!s) return;
      try {
        if      (key === 'sma20')  s.setData(calcSMA(visible, 20));
        else if (key === 'sma50')  s.setData(calcSMA(visible, 50));
        else if (key === 'sma200') s.setData(calcSMA(visible, 200));
        else if (key === 'ema20')  s.setData(calcEMA(visible, 20));
        else if (key === 'ema50')  s.setData(calcEMA(visible, 50));
        else if (key === 'bb') {
          const { upper, mid, lower } = calcBB(visible, 20, 2);
          s.upper.setData(upper); s.mid.setData(mid); s.lower.setData(lower);
        }
        else if (key === 'vol') {
          s.setData(visible.map(c => ({ time:c.time, value:c.volume ?? 0, color:(c.close>=c.open)?'rgba(38,166,154,0.5)':'rgba(239,83,80,0.5)' })));
        }
      } catch {}
    });
  }, [indicators, idx, candles]); // eslint-disable-line

  // ── Drag TP/SL lines
  useEffect(() => {
    const svg = overlayRef.current;
    if (!svg) return;
    const onMouseDown = (e) => {
      const drag = e.target?.getAttribute?.('data-drag');
      if (!drag || !positionRef.current || resolvedRef.current) return;
      draggingRef.current = drag;
      e.preventDefault();
      chartRef.current?.applyOptions({ handleScroll: false, handleScale: false });
    };
    const onMouseMove = (e) => {
      if (!draggingRef.current || !positionRef.current || resolvedRef.current) return;
      const rect = svg.getBoundingClientRect();
      let newPos;
      if (draggingRef.current === 'right') {
        const newTime = chartRef.current?.timeScale().coordinateToTime(e.clientX - rect.left);
        if (newTime == null) return;
        newPos = { ...positionRef.current, rightTime: newTime };
      } else {
        let price = seriesRef.current?.coordinateToPrice(e.clientY - rect.top);
        if (price == null) return;
        const p   = positionRef.current;
        const eps = p.entry * 0.0001;
        const isL = p.type === 'long';
        if (draggingRef.current === 'tp') {
          price = isL ? Math.max(p.entry + eps, price) : Math.min(p.entry - eps, price);
        } else if (draggingRef.current === 'sl') {
          price = isL ? Math.min(p.entry - eps, price) : Math.max(p.entry + eps, price);
        } else if (draggingRef.current === 'entry') {
          price = isL
            ? Math.max(p.sl + eps, Math.min(p.tp - eps, price))
            : Math.max(p.tp + eps, Math.min(p.sl - eps, price));
        }
        newPos = { ...positionRef.current, [draggingRef.current]: price };
      }
      positionRef.current = newPos;
      updatePriceLinesRef.current?.(newPos);
      drawOverlayRef.current?.(newPos);
    };
    const onMouseUp = () => {
      if (!draggingRef.current) return;
      const finalPos = positionRef.current;
      draggingRef.current = null;
      chartRef.current?.applyOptions({ handleScroll: true, handleScale: true });
      if (finalPos) setPosition({ ...finalPos });
    };
    svg.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      svg.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []); // eslint-disable-line

  const fetchCandles = async (symbol, interval, endTime = null) => {
    const qs = new URLSearchParams({ symbol, interval, limit: 1000 });
    if (endTime) qs.set("endTime", endTime);
    const r    = await fetch(`/api/market-data?${qs}`);
    const data = await r.json();
    if (data.error) throw new Error(data.error);
    return { candles: data.candles || [], warning: data.warning || null };
  };

  useEffect(() => {
    setLoading(true); setErr(null); setPlaying(false); setHasMore(true);
    clearInterval(timerRef.current);
    fetchCandles(sym, tf)
      .then(({ candles: c, warning }) => {
        if (warning && !c.length) { setErr(warning); setLoading(false); return; }
        setCandles(c); setIdx(Math.min(50, c.length)); setLoading(false); setHasMore(c.length >= 50);
      })
      .catch(e => { setErr(e.message); setLoading(false); });
  }, [sym, tf]); // eslint-disable-line

  const loadMore = async () => {
    if (!candles.length || loadingMore) return;
    setLoadingMore(true);
    try {
      const endTime = candles[0].time * 1000;
      const { candles: more } = await fetchCandles(sym, tf, endTime);
      const newC = more.filter(c => c.time < candles[0].time);
      if (newC.length) {
        setCandles(prev => [...newC, ...prev]);
        setIdx(prev => prev + newC.length);
        setHasMore(newC.length >= 50);
      } else { setHasMore(false); }
    } catch(e) { setErr(e.message); }
    setLoadingMore(false);
  };

  const jumpToDate = async (dateStr) => {
    if (!dateStr) return;
    setPlaying(false);
    clearInterval(timerRef.current);
    const targetMs  = new Date(dateStr).getTime();
    const targetSec = targetMs / 1000;
    // Try to find in already-loaded candles first
    // Ne réutilise les bougies chargées que si la date cible est dans leur plage
    const first = candles[0]?.time, last = candles[candles.length - 1]?.time;
    if (first && last && targetSec >= first && targetSec <= last) {
      const existingIdx = candles.findIndex(c => c.time >= targetSec);
      if (existingIdx !== -1) { setIdx(existingIdx + 1); return; }
    }
    // Need to reload: fetch 1000 candles ending ~200 bars after target date
    const tfMs   = REPLAY_TF_MS[tf] || 864e5;
    const endTime = targetMs + 200 * tfMs;
    setLoading(true); setErr(null); setHasMore(true);
    try {
      const { candles: c, warning } = await fetchCandles(sym, tf, endTime);
      if (warning && !c.length) { setErr(warning); setLoading(false); return; }
      setCandles(c);
      const tIdx = c.findIndex(x => x.time >= targetSec);
      setIdx(tIdx !== -1 ? tIdx + 1 : Math.min(50, c.length));
      setHasMore(c.length >= 50);
    } catch(e) { setErr(e.message); }
    setLoading(false);
  };

  useEffect(() => {
    if (!seriesRef.current || !candles.length) return;
    seriesRef.current.setData(candles.slice(0, idx));
    const chart = chartRef.current;
    if (!chart) return;
    const range = chart.timeScale().getVisibleLogicalRange();
    if (range) {
      const half = (range.to - range.from) / 2;
      chart.timeScale().setVisibleLogicalRange({ from: (idx - 1) - half, to: (idx - 1) + half });
    } else {
      chart.timeScale().scrollToRealTime();
    }
  }, [idx, candles]);

  useEffect(() => {
    clearInterval(timerRef.current);
    if (!playing) return;
    timerRef.current = setInterval(() => {
      setIdx(prev => { if (prev >= candles.length) { setPlaying(false); return prev; } return prev + 1; });
    }, Math.max(30, 1000 / speed));
    return () => clearInterval(timerRef.current);
  }, [playing, speed, candles.length]);

  const cur  = candles[idx - 1];
  const pct  = candles.length ? Math.round((idx / candles.length) * 100) : 0;
  const fmtD = ts => new Date(ts * 1000).toLocaleDateString("fr-FR", {day:"2-digit", month:"short", year:"numeric"});
  const fmtDT = ts => {
    const d = new Date(ts * 1000);
    if (['1m','5m','15m','30m','1h','4h','8h','12h'].includes(tf))
      return d.toLocaleDateString("fr-FR",{day:"2-digit",month:"short"}) + ' ' + d.toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"});
    return fmtD(ts);
  };
  const signFmtP = p => (p >= 0 ? '+' : '-') + fmtP(Math.abs(p));

  // ── Price lines helper — updated each render for access from drag/resolved effects
  updatePriceLinesRef.current = (pos, res = resolvedRef.current) => {
    const s = seriesRef.current;
    if (!s) return;
    const old = posLinesRef.current;
    try { if (old.entry) s.removePriceLine(old.entry); } catch {}
    try { if (old.tp)    s.removePriceLine(old.tp);    } catch {}
    try { if (old.sl)    s.removePriceLine(old.sl);    } catch {}
    posLinesRef.current = {};
    if (!pos) return;
    const { entry, tp, sl, type } = pos;
    const isLong = type === 'long';
    const risk   = Math.abs(entry - sl);
    const reward = Math.abs(tp - entry);
    const rr     = risk > 0 ? (reward / risk).toFixed(2) : '?';
    const pctTp  = entry > 0 ? ((reward / entry) * 100).toFixed(2) : '0';
    const pctSl  = entry > 0 ? ((risk   / entry) * 100).toFixed(2) : '0';
    const tpHit  = res?.result === 'tp';
    const slHit  = res?.result === 'sl';
    posLinesRef.current.entry = s.createPriceLine({ price: entry, color: '#2962ff', lineWidth: 2, lineStyle: LineStyle.Solid,  axisLabelVisible: true, title: `Entry ${isLong ? 'LONG' : 'SHORT'}` });
    posLinesRef.current.tp    = s.createPriceLine({ price: tp,    color: '#26a69a', lineWidth: tpHit ? 2 : 1, lineStyle: tpHit ? LineStyle.Solid : LineStyle.Dashed, axisLabelVisible: true, title: tpHit ? 'TP HIT ✓' : `TP  +${pctTp}%  R:${rr}` });
    posLinesRef.current.sl    = s.createPriceLine({ price: sl,    color: '#ef5350', lineWidth: slHit ? 2 : 1, lineStyle: slHit ? LineStyle.Solid : LineStyle.Dashed, axisLabelVisible: true, title: slHit ? 'SL HIT ✓' : `SL  -${pctSl}%` });
  };

  // ── Overlay draw function — updated each render, accepts pos override (used during drag)
  drawOverlayRef.current = (overridePos = null) => {
    if (!overlayRef.current || !seriesRef.current) return;
    const pos = overridePos || position;
    if (!pos) { overlayRef.current.innerHTML = ''; return; }
    const s      = seriesRef.current;
    const ts     = chartRef.current?.timeScale();
    const { entry, tp, sl, type, entryTime, rightTime } = pos;
    const isLong = type === 'long';
    const entryY = s.priceToCoordinate(entry);
    const tpY    = s.priceToCoordinate(tp);
    const slY    = s.priceToCoordinate(sl);
    if (entryY == null || tpY == null || slY == null) { overlayRef.current.innerHTML = ''; return; }

    const chartW  = Math.max(0, overlayRef.current.clientWidth - 65);
    const res     = resolvedRef.current;
    const isRes   = !!res;
    const tpHit   = isRes && res.result === 'tp';
    const slHit   = isRes && res.result === 'sl';
    const reward  = Math.abs(tp - entry);
    const risk    = Math.abs(sl - entry);
    const rwPct   = entry > 0 ? (reward / entry * 100) : 0;
    const rkPct   = entry > 0 ? (risk   / entry * 100) : 0;
    const tpTop   = Math.min(entryY, tpY), tpH = Math.abs(tpY - entryY);
    const slTop   = Math.min(entryY, slY), slH = Math.abs(slY - entryY);
    const zoneTop = Math.min(tpTop, slTop), zoneFull = tpH + slH;

    // ── Geometry: entry candle → right handle (zones never extend left of entry)
    const startX    = Math.max(0, entryTime != null ? (ts?.timeToCoordinate(entryTime) ?? 0) : 0);
    const rawRightX = rightTime != null ? (ts?.timeToCoordinate(rightTime) ?? chartW) : chartW;
    const rightX    = Math.min(chartW, Math.max(startX + 20, rawRightX));
    const zoneW     = rightX - startX;

    // ── Colored zones (entry candle → rightX)
    let svg = '';
    if (!slHit && tpH > 0) svg += `<rect x="${startX}" y="${tpTop}" width="${zoneW}" height="${tpH}" fill="#26a69a" opacity="${tpHit ? 0.18 : 0.08}" pointer-events="none"/>`;
    if (!tpHit && slH > 0) svg += `<rect x="${startX}" y="${slTop}" width="${zoneW}" height="${slH}" fill="#ef5350" opacity="${slHit ? 0.18 : 0.08}" pointer-events="none"/>`;

    // ── Candle penetration overlay (entry candle → current candle, preserved)
    const rawCurX   = cur && ts ? (ts.timeToCoordinate(cur.time) ?? rightX) : rightX;
    const curX      = Math.min(rightX, Math.max(startX, rawCurX));
    const darkW     = curX - startX;
    const tpC = 'rgba(8,153,129,', slC = 'rgba(242,54,69,';
    if (cur && darkW > 0 && !isRes) {
      const pen = (extreme, zoneEdge, clampFn, clr) => {
        const cy = s.priceToCoordinate(clampFn(extreme, zoneEdge));
        if (cy == null) return;
        const dT = Math.min(entryY, cy), dH = Math.abs(cy - entryY);
        if (dH < 1) return;
        svg += `<rect x="${startX}" y="${dT}" width="${darkW}" height="${dH}" fill="${clr}0.17)" pointer-events="none"/>`;
        svg += `<line x1="${startX}" y1="${cy}" x2="${curX}" y2="${cy}" stroke="${clr}0.60)" stroke-width="1" stroke-dasharray="3,3" pointer-events="none"/>`;
      };
      if (isLong) {
        if (cur.high > entry) pen(cur.high, tp, Math.min, tpC);
        if (cur.low  < entry) pen(cur.low,  sl, Math.max, slC);
      } else {
        if (cur.low  < entry) pen(cur.low,  tp, Math.max, tpC);
        if (cur.high > entry) pen(cur.high, sl, Math.min, slC);
      }
    }

    // ── Lines (entry candle → rightX)
    svg += `
      <line x1="${startX}" y1="${tpY}"    x2="${rightX}" y2="${tpY}"    stroke="rgba(38,166,154,0.7)"  stroke-width="${tpHit ? 2 : 1}" ${tpHit ? '' : 'stroke-dasharray="4,4"'} pointer-events="none"/>
      <line x1="${startX}" y1="${slY}"    x2="${rightX}" y2="${slY}"    stroke="rgba(239,83,80,0.7)"   stroke-width="${slHit ? 2 : 1}" ${slHit ? '' : 'stroke-dasharray="4,4"'} pointer-events="none"/>
      <line x1="${startX}" y1="${entryY}" x2="${rightX}" y2="${entryY}" stroke="rgba(41,98,255,0.8)"   stroke-width="1.5" pointer-events="none"/>
    `;

    // ── Drag handles: Entry + TP + SL (disabled when resolved)
    if (!isRes) {
      const hw = rightX - startX;
      svg += `
        <rect x="${startX}" y="${tpY    - 8}" width="${hw}" height="16" fill="transparent" pointer-events="all" style="cursor:ns-resize" data-drag="tp"/>
        <rect x="${startX}" y="${slY    - 8}" width="${hw}" height="16" fill="transparent" pointer-events="all" style="cursor:ns-resize" data-drag="sl"/>
        <rect x="${startX}" y="${entryY - 8}" width="${hw}" height="16" fill="transparent" pointer-events="all" style="cursor:ns-resize" data-drag="entry"/>
      `;
    }

    // ── Right extension handle (disabled when resolved)
    if (!isRes) {
      const midY = zoneTop + zoneFull / 2;
      svg += `
        <line x1="${rightX}" y1="${zoneTop}" x2="${rightX}" y2="${zoneTop + zoneFull}" stroke="rgba(180,180,180,0.35)" stroke-width="1" pointer-events="none"/>
        <rect x="${rightX - 5}" y="${midY - 12}" width="10" height="24" rx="2"
              fill="${darkMode ? 'rgba(30,30,40,0.85)' : 'rgba(240,240,240,0.90)'}"
              stroke="rgba(180,180,180,0.50)" stroke-width="1"
              pointer-events="all" style="cursor:ew-resize" data-drag="right"/>
        <line x1="${rightX - 1}" y1="${midY - 7}" x2="${rightX - 1}" y2="${midY + 7}" stroke="rgba(150,150,150,0.7)" stroke-width="1" pointer-events="none"/>
        <line x1="${rightX + 1}" y1="${midY - 7}" x2="${rightX + 1}" y2="${midY + 7}" stroke="rgba(150,150,150,0.7)" stroke-width="1" pointer-events="none"/>
      `;
    }

    // ── Labels (Entry / TP / SL)
    const fmtL = (p) => {
      if (p == null) return '';
      if (p >= 10000) return p.toFixed(0);
      if (p >= 1000)  return p.toFixed(2);
      if (p >= 10)    return p.toFixed(3);
      if (p >= 0.1)   return p.toFixed(4);
      return p.toFixed(6);
    };
    const addLbl = (y, text, bg) => {
      const w = Math.ceil(text.length * 6.5 + 20);
      svg += `<rect x="12" y="${y - 8}" width="${w}" height="16" rx="3" fill="${bg}" opacity="0.92" pointer-events="none"/>`;
      svg += `<text x="22" y="${y + 4.5}" font-family="Syne,sans-serif" font-size="9" font-weight="800" letter-spacing="1" fill="white" pointer-events="none">${text}</text>`;
    };
    addLbl(entryY, `ENTRY  ${fmtL(entry)}  |  ${isLong ? 'LONG' : 'SHORT'}`, '#2962ff');
    addLbl(tpY, tpHit
      ? `TP HIT ✓  +${fmtL(reward)}  +${rwPct.toFixed(2)}%`
      : `TP  ${fmtL(tp)}  |  +${fmtL(reward)}  +${rwPct.toFixed(2)}%`,
      '#26a69a');
    addLbl(slY, slHit
      ? `SL HIT ✓  -${fmtL(risk)}  -${rkPct.toFixed(2)}%`
      : `SL  ${fmtL(sl)}  |  -${fmtL(risk)}  -${rkPct.toFixed(2)}%`,
      '#ef5350');

    overlayRef.current.innerHTML = svg;
  };
  useEffect(() => { drawOverlayRef.current?.(); }, [idx, position]); // eslint-disable-line

  const symLabel = REPLAY_GROUPS.flatMap(g=>g.items).find(s=>s.v===sym)?.l || sym;
  const tvSep = <div style={{width:1, height:16, background:rC.bdr, margin:"0 6px", flexShrink:0}}/>;
  const tfS = (on) => ({ padding:"2px 6px", minWidth:26, borderRadius:3, border:"none", background:on?(darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.08)"):"transparent", color:on?rC.wht:rC.dim, fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight:on?700:400, cursor:"pointer", transition:"all 0.1s", flexShrink:0 });
  const tbB = (on, accent) => ({ padding:"4px 8px", borderRadius:4, border:"none", background:on?(accent?accent+"1a":(darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.05)")):"transparent", color:on?(accent||rC.wht):rC.dim, fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight:on?700:400, letterSpacing:"0.05em", cursor:"pointer", transition:"all 0.1s", flexShrink:0 });
  const spB = (on) => ({ padding:"3px 7px", borderRadius:3, border:`1px solid ${on?rC.bdr:"transparent"}`, background:on?(darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.05)"):"transparent", color:on?rC.wht:rC.dim, fontSize:10, fontFamily:"'Josefin Sans',sans-serif", cursor:"pointer", transition:"all 0.1s", flexShrink:0 });
  const ctB = (on) => ({ display:"flex", alignItems:"center", justifyContent:"center", width:28, height:28, borderRadius:4, border:"none", background:on?(darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.07)"):"transparent", color:on?rC.wht:rC.dim, fontSize:13, cursor:"pointer", transition:"all 0.1s", flexShrink:0 });
  const indDropdown = showIndPanel && (
    <div style={{position:"absolute", top:"calc(100% + 4px)", left:0, zIndex:200, background:darkMode?"#1e222d":"#ffffff", border:`1px solid ${rC.bdr}`, borderRadius:6, padding:4, minWidth:160, boxShadow:"0 4px 20px rgba(0,0,0,0.4)"}}>
      {IND_CONFIG.map(({key,label,color})=>(
        <label key={key} onClick={()=>setIndicators(p=>({...p,[key]:!p[key]}))} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",cursor:"pointer",borderRadius:4,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",color:indicators[key]?rC.wht:rC.dim,background:indicators[key]?(darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.04)"):"transparent",userSelect:"none"}}>
          <span style={{width:10,height:2,background:indicators[key]?color:rC.bdr,flexShrink:0,borderRadius:1,display:"block"}}/>
          {label}
        </label>
      ))}
    </div>
  );

  return (
    <div style={{display:"flex", flexDirection:"column", height:"100%", background:rC.bg}}>

      {/* ── Toolbar ── */}
      <div style={{display:"flex", alignItems:"center", height:38, paddingLeft:10, borderBottom:`1px solid ${rC.bdr}`, background:rC.bg2, flexShrink:0, gap:0}}>
        {/* Left: Symbol + TFs — scrollable section */}
        <div style={{display:"flex", alignItems:"center", gap:0, overflowX:"auto", overflowY:"visible", scrollbarWidth:"none", flexShrink:0, maxWidth:"60%"}}>
          {/* Symbol */}
          <div style={{position:"relative", flexShrink:0, marginRight:4}}>
            <div style={{display:"flex", alignItems:"center", gap:4, padding:"4px 8px", borderRadius:4, background:darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.04)", userSelect:"none", cursor:"pointer"}}>
              <span style={{fontSize:13, fontWeight:700, color:rC.wht, lineHeight:1, fontFamily:"'Josefin Sans',sans-serif"}}>{symLabel}</span>
              <span style={{fontSize:7, color:rC.dim}}>▼</span>
            </div>
            <select value={sym} onChange={e=>setSym(e.target.value)} style={{position:"absolute",inset:0,opacity:0,cursor:"pointer",width:"100%",height:"100%"}}>
              {REPLAY_GROUPS.map(g=>(
                <optgroup key={g.g} label={g.g}>
                  {g.items.map(s=><option key={s.v} value={s.v}>{s.l}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
          {tvSep}
          {/* Timeframes — hidden on mobile (shown in row below) */}
          {!isMobile && REPLAY_TFS.map(t=><button key={t} onClick={()=>setTf(t)} style={tfS(tf===t)}>{t}</button>)}
        </div>
        {/* Right: Indicators + controls — NOT in overflow:auto so dropdown is never clipped */}
        <div style={{display:"flex", alignItems:"center", gap:0, flex:1, paddingRight:10, minWidth:0}}>
          {tvSep}
          {/* Indicators */}
          <div style={{position:"relative", flexShrink:0}}>
            <button onClick={()=>setShowIndPanel(v=>!v)} style={tbB(showIndPanel||Object.values(indicators).some(Boolean))}>
              Indicateurs
            </button>
            {indDropdown}
          </div>
          {tvSep}
          {/* Long / Short */}
          <button onClick={()=>setPosMode(m=>m==='long'?null:'long')} style={{...tbB(posMode==='long'), color:posMode==='long'?"#26a69a":undefined}}>Long +</button>
          <button onClick={()=>setPosMode(m=>m==='short'?null:'short')} style={{...tbB(posMode==='short'), color:posMode==='short'?"#ef5350":undefined}}>Short -</button>
          {posMode && <span style={{fontSize:9, color:rC.dim, flexShrink:0, marginLeft:4, fontFamily:"'Josefin Sans',sans-serif"}}>{isMobile?"Toucher le chart":"Cliquer sur le chart"}</span>}
          {tvSep}
          {/* Date jump */}
          <div style={{position:"relative", flexShrink:0}}>
            <button style={tbB(false)}>Date</button>
            <input type="date" onChange={e=>jumpToDate(e.target.value)} style={{position:"absolute",inset:0,opacity:0,cursor:"pointer",width:"100%",height:"100%",colorScheme:darkMode?"dark":"light"}}/>
          </div>
          <div style={{flex:1}}/>
          {/* OHLC */}
          {cur && !loading && !isMobile && (
            <div style={{display:"flex", gap:8, fontSize:11, alignItems:"center", flexShrink:0, fontFamily:"'Josefin Sans',sans-serif"}}>
              <span style={{color:rC.dim}}>O</span><span style={{color:cur.close>=cur.open?"#26a69a":"#ef5350"}}>{fmtP(cur.open)}</span>
              <span style={{color:rC.dim}}>H</span><span style={{color:"#26a69a"}}>{fmtP(cur.high)}</span>
              <span style={{color:rC.dim}}>L</span><span style={{color:"#ef5350"}}>{fmtP(cur.low)}</span>
              <span style={{color:rC.dim}}>C</span><span style={{color:cur.close>=cur.open?"#26a69a":"#ef5350",fontWeight:600}}>{fmtP(cur.close)}</span>
            </div>
          )}
          {loading && <span style={{fontSize:10, color:rC.dim, fontFamily:"'Josefin Sans',sans-serif"}}>Chargement…</span>}
        </div>
      </div>

      {/* ── Mobile TF row ── */}
      {isMobile && (
        <div style={{display:"flex", alignItems:"center", gap:2, padding:"0 10px", height:34, borderBottom:`1px solid ${rC.bdr}`, background:rC.bg2, flexShrink:0, overflowX:"auto", scrollbarWidth:"none"}}>
          {REPLAY_TFS.map(t=><button key={t} onClick={()=>setTf(t)} style={tfS(tf===t)}>{t}</button>)}
        </div>
      )}

      {/* ── Position panel ── */}
      {position && (
        <div style={{display:"flex", alignItems:"center", gap:8, padding:"5px 12px", borderBottom:`1px solid ${rC.bdr}`, background:rC.bg2, flexShrink:0, flexWrap:"wrap"}}>
          <span style={{fontSize:10, color:position.type==='long'?"#26a69a":"#ef5350", fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", minWidth:36, flexShrink:0, fontFamily:"'Josefin Sans',sans-serif"}}>{position.type==='long'?"LONG":"SHORT"}</span>
          {[{label:"Entry",key:"entry",color:"#2962ff"},{label:"TP",key:"tp",color:"#26a69a"},{label:"SL",key:"sl",color:"#ef5350"}].map(({label,key,color})=>(
            <div key={key} style={{display:"flex",alignItems:"center",gap:4}}>
              <span style={{fontSize:10,color,fontWeight:600,minWidth:28,fontFamily:"'Josefin Sans',sans-serif"}}>{label}</span>
              <input type="number" step="any" value={position[key]} onChange={e=>{const v=parseFloat(e.target.value);if(!isNaN(v))setPosition(p=>({...p,[key]:v}));}} style={{width:isMobile?68:80,padding:"2px 6px",borderRadius:4,border:`1px solid ${color}44`,background:"transparent",color:rC.wht,fontSize:11,outline:"none",fontFamily:"'Josefin Sans',sans-serif"}}/>
            </div>
          ))}
          {(() => {
            const risk=Math.abs(position.entry-position.sl), rw=Math.abs(position.tp-position.entry);
            const rr=risk>0?(rw/risk).toFixed(2):'?', p=position.entry>0?((rw/position.entry)*100).toFixed(2):'?';
            return <span style={{fontSize:10,color:rC.dim,fontFamily:"'Josefin Sans',sans-serif"}}>R:R {rr} · +{p}%</span>;
          })()}
          <button onClick={()=>{setPosition(null);setResolved(null);lastResolvedIdxRef.current=-1;}} style={{marginLeft:"auto",padding:"3px 10px",borderRadius:4,border:`1px solid ${rC.bdr}`,background:"transparent",color:rC.dim,fontSize:10,cursor:"pointer",flexShrink:0,fontFamily:"'Josefin Sans',sans-serif"}}>Effacer</button>
        </div>
      )}

      {/* ── Chart ── */}
      <div style={{flex:1, position:"relative", minHeight:0}}>
        {err && (
          <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",background:rC.bg,zIndex:10,fontSize:11,color:"#ef5350",textAlign:"center",padding:24,fontFamily:"'Josefin Sans',sans-serif"}}>
            {err}
          </div>
        )}
        <div ref={containerRef} style={{width:"100%", height:"100%", cursor:posMode?"crosshair":"default"}}/>
        <svg ref={overlayRef} style={{position:"absolute",inset:0,width:"100%",height:"100%",overflow:"visible",zIndex:2}} pointer-events="none"/>
      </div>

      {/* ── Replay bar ── */}
      {isMobile ? (
        <div style={{flexShrink:0, background:rC.bg2, borderTop:`1px solid ${rC.bdr}`}}>
          <div style={{display:"flex",alignItems:"center",gap:4,padding:"8px 10px 4px"}}>
            <button onClick={()=>{setPlaying(false);setIdx(p=>Math.max(1,p-1));}} style={ctB(false)}>⏮</button>
            <button onClick={()=>setPlaying(p=>!p)} style={{...ctB(false),width:36,height:36,fontSize:17,color:rC.wht,background:playing?(darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.07)"):"transparent"}}>{playing?"⏸":"▶"}</button>
            <button onClick={()=>{setPlaying(false);setIdx(p=>Math.min(candles.length,p+1));}} style={ctB(false)}>⏭</button>
            <input type="range" min={1} max={candles.length||1000} value={idx} onChange={e=>{setPlaying(false);setIdx(Number(e.target.value));}} style={{flex:1,accentColor:"#2962ff",cursor:"pointer"}}/>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:4,padding:"0 10px 8px"}}>
            <button onClick={loadMore} disabled={!hasMore||loadingMore||loading} style={{...ctB(false),width:"auto",padding:"0 8px",fontSize:10,color:(!hasMore||loadingMore)?rC.dim+"55":rC.dim}}>
              {loadingMore?"…":"← Plus"}
            </button>
            <span style={{fontSize:11,color:rC.wht,fontFamily:"'Josefin Sans',sans-serif",marginLeft:4,fontWeight:500}}>{cur?fmtDT(cur.time):''}</span>
            <div style={{flex:1}}/>
            {REPLAY_SPEEDS.map(s=><button key={s} onClick={()=>setSpeed(s)} style={spB(speed===s)}>{s}×</button>)}
          </div>
        </div>
      ) : (
        <div style={{display:"flex",alignItems:"center",gap:2,height:40,padding:"0 10px",borderTop:`1px solid ${rC.bdr}`,background:rC.bg2,flexShrink:0}}>
          <button onClick={loadMore} disabled={!hasMore||loadingMore||loading} style={{...ctB(false),width:"auto",padding:"0 8px",fontSize:10,color:(!hasMore||loadingMore)?rC.dim+"55":rC.dim}}>
            {loadingMore?"…":"← Plus"}
          </button>
          {tvSep}
          <button onClick={()=>{setPlaying(false);setIdx(p=>Math.max(1,p-1));}} style={ctB(false)} title="Reculer (←)">⏮</button>
          <button onClick={()=>setPlaying(p=>!p)} style={{...ctB(false),width:32,height:32,fontSize:16,color:rC.wht,background:playing?(darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.07)"):"transparent"}} title="Play/Pause (Espace)">{playing?"⏸":"▶"}</button>
          <button onClick={()=>{setPlaying(false);setIdx(p=>Math.min(candles.length,p+1));}} style={ctB(false)} title="Avancer (→)">⏭</button>
          {tvSep}
          <span style={{fontSize:12,color:rC.wht,fontFamily:"'Josefin Sans',sans-serif",fontWeight:500,minWidth:110,flexShrink:0}}>{cur?fmtDT(cur.time):''}</span>
          <span style={{fontSize:10,color:rC.dim,fontFamily:"'Josefin Sans',sans-serif",flexShrink:0,marginRight:4}}>{idx}/{candles.length}</span>
          <input type="range" min={1} max={candles.length||1000} value={idx} onChange={e=>{setPlaying(false);setIdx(Number(e.target.value));}} style={{flex:1,accentColor:"#2962ff",cursor:"pointer",height:3}}/>
          {tvSep}
          {REPLAY_SPEEDS.map(s=><button key={s} onClick={()=>setSpeed(s)} style={spB(speed===s)}>{s}×</button>)}
          {tvSep}
          <button onClick={()=>{setPlaying(false);setIdx(Math.min(50,candles.length));}} style={{...ctB(false),width:"auto",padding:"0 8px",fontSize:10,color:rC.dim}}>Reset</button>
          <span style={{fontSize:9,color:rC.dim,fontFamily:"'Josefin Sans',sans-serif",opacity:0.45,marginLeft:6}}>← → Espace &lt; &gt;</span>
        </div>
      )}
    </div>
  );
}

/* ─── Calendar ───────────────────────────────────────────────────── */
function Calendar({ filtered, calMonth, calYear, onPrev, onNext, onDayClick, cur, dayEndTime, fms: fmsProp, hideNumbers: hideNbProp }) {
  const fms = fmsProp || ((v) => (v >= 0 ? "+" : "") + fmtMoney(v));
  const hideNumbers = !!hideNbProp;
  const m = calMonth, yr = calYear;
  const daysInMonth = new Date(Date.UTC(yr, m + 1, 0)).getUTCDate();
  const firstDayJS  = new Date(Date.UTC(yr, m, 1)).getUTCDay(); // 0=Sun, timezone-safe
  const offset      = firstDayJS === 0 ? 6 : firstDayJS - 1;   // convert to Mon-first
  const todayStr = new Date().toISOString().split("T")[0];
  const byDay = {};
  filtered.forEach(t => {
    const [ty, tm, td] = t.date.split("-").map(Number);
    if (ty === yr && tm - 1 === m) byDay[td] = (byDay[td] || 0) + (t.pnl || 0);
  });
  const maxAbs = Math.max(...Object.values(byDay).map(Math.abs), 1);
  let cells = [...Array(offset).fill(null), ...Array.from({ length:daysInMonth }, (_, i) => i + 1)];
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let w = 0; w < cells.length / 7; w++) weeks.push(cells.slice(w * 7, (w + 1) * 7));
  const DAYS = ["Lu","Ma","Me","Je","Ve","Sa","Di"];

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <div style={{ fontSize:10, color:C.dim, letterSpacing:"0.2em", textTransform:"uppercase", fontFamily:"'Josefin Sans',sans-serif", fontWeight:600 }}>{L.stats.calTitle}</div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <button onClick={onPrev} style={{ background:"none", border:"none", cursor:"pointer", color:C.gray1, fontSize:16, lineHeight:1, padding:"0 2px" }}>‹</button>
          <div style={{ fontSize:12, color:C.white, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.08em", minWidth:80, textAlign:"center" }}>{L.monthsShort[m]} {yr}</div>
          <button onClick={onNext} style={{ background:"none", border:"none", cursor:"pointer", color:C.gray1, fontSize:16, lineHeight:1, padding:"0 2px" }}>›</button>
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr) 48px", gap:3, marginBottom:3 }}>
        {DAYS.map((d, i) => <div key={i} style={{ textAlign:"center", fontSize:9, color:C.gray1, fontFamily:"'Josefin Sans',sans-serif", paddingBottom:3 }}>{d}</div>)}
        <div style={{ textAlign:"center", fontSize:9, color:C.dim, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, paddingBottom:3 }}>SEM</div>
      </div>
      {weeks.map((week, w) => {
        const weekPnl = week.reduce((s, day) => s + (day && byDay[day] ? byDay[day] : 0), 0);
        const hasWeek = week.some(day => day && byDay[day] !== undefined);
        return (
          <div key={w} style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr) 48px", gap:3, marginBottom:3 }}>
            {week.map((day, i) => {
              if (!day) return <div key={i} />;
              const pnl = byDay[day]; const hasTrade = pnl !== undefined;
              const intensity = hasTrade ? Math.min(0.9, 0.2 + 0.7 * (Math.abs(pnl) / maxAbs)) : 0;
              const bg = hasTrade ? (pnl >= 0 ? `rgba(42,110,58,${intensity})` : `rgba(192,57,43,${intensity})`) : "transparent";
              const isToday = todayStr === `${yr}-${String(m+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
              return (
                <div key={i} onClick={()=>{ if(onDayClick&&!isToday&&hasTrade){onDayClick({day,month:m,year:yr,pnl});}}} title={hasTrade ? `${fms(pnl)}${cur||"€"}` : ""} style={{ aspectRatio:"1", borderRadius:8, background:bg, border:isToday ? `1px solid ${C.accent}` : `1px solid ${hasTrade ? "transparent" : C.gray3}`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:hasTrade&&!isToday?"pointer":"default", boxShadow:hasTrade ? "0 3px 10px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(0,0,0,0.3)" : "none", transform:hasTrade ? "translateY(-1px)" : "none", transition:"all 0.15s" }}>
                  <span style={{ fontSize:9, color:hasTrade ? "#fff" : C.gray1, fontFamily:"'Josefin Sans',sans-serif", lineHeight:1, fontWeight:hasTrade ? 600 : 300 }}>{day}</span>
                  {hasTrade && <span style={{ fontSize:7, color:"rgba(255,255,255,0.9)", lineHeight:1, marginTop:1 }}>{fms(pnl)}</span>}
                </div>
              );
            })}
            <div style={{ borderRadius:4, border:`1px solid ${hasWeek ? (weekPnl>=0?"rgba(42,110,58,0.35)":"rgba(192,57,43,0.35)") : C.gray3}`, background:hasWeek ? (weekPnl>=0?"rgba(42,110,58,0.08)":"rgba(192,57,43,0.08)") : "transparent", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"2px 0" }}>
              {hasWeek ? (
                <>
                  <span style={{ fontSize:7, color:C.gray1, fontFamily:"'Josefin Sans',sans-serif", lineHeight:1 }}>sem</span>
                  <span style={{ fontSize:8, color:weekPnl >= 0 ? "#2a6e3a" : "#c0392b", fontFamily:"'Josefin Sans',sans-serif", fontWeight:700, lineHeight:1.2 }}>{hideNumbers ? "****" : `${weekPnl >= 0 ? "+" : ""}${Math.round(weekPnl)}`}</span>
                </>
              ) : <span style={{ fontSize:8, color:C.gray3 }}>—</span>}
            </div>
          </div>
        );
      })}
      <div style={{ display:"flex", gap:12, marginTop:8, justifyContent:"flex-end" }}>
        <div style={{ display:"flex", alignItems:"center", gap:4 }}><div style={{ width:7, height:7, borderRadius:2, background:"rgba(42,110,58,0.7)" }} /><span style={{ fontSize:9, color:C.gray1, fontFamily:"'Josefin Sans',sans-serif" }}>Gain</span></div>
        <div style={{ display:"flex", alignItems:"center", gap:4 }}><div style={{ width:7, height:7, borderRadius:2, background:"rgba(192,57,43,0.7)" }} /><span style={{ fontSize:9, color:C.gray1, fontFamily:"'Josefin Sans',sans-serif" }}>Perte</span></div>
      </div>
    </div>
  );
}

/* ─── Trade chronological comparator ────────────────────────────── */
const cmpTrades = (a, b) =>
  a.date.localeCompare(b.date) ||
  (a.createdAt||"").localeCompare(b.createdAt||"") ||
  (typeof a.id==="number"&&typeof b.id==="number" ? a.id-b.id : 0);

/* ─── P&L Chart ──────────────────────────────────────────────────── */
function PnlChart({ filtered, capital, pnlSum, height, cur }) {
  const fms = (v) => (v >= 0 ? "+" : "") + fmtMoney(v);
  const gidRef = useRef(`pnlc_${Math.random().toString(36).slice(2,8)}`);
  const gid = gidRef.current;
  if (!filtered || filtered.length < 2) return null;
  const sorted = [...filtered].sort(cmpTrades);
  let cum = 0;
  const data = [
    { label:"0", v:0, pnl:0, instrument:"" },
    ...sorted.map((t,i) => {
      cum += t.pnl || 0;
      return { label:String(i+1), v:parseFloat(cum.toFixed(2)), pnl:t.pnl||0, date:t.date.slice(5), instrument:t.instrument||"" };
    })
  ];
  const vals = data.map(d => d.v);
  const minV = Math.min(...vals), maxV = Math.max(...vals);
  const absMax = Math.max(Math.abs(minV), Math.abs(maxV), 30);
  const step = Math.ceil(absMax / 2 / 50) * 50 || 50;
  const yDomain = [-step * 2, step * 2];
  const yTicks = [-step * 2, -step, 0, step, step * 2];
  // gradient is anchored to the path bounding box [minV, maxV], not the domain
  const zeroPct = maxV === minV ? 50 : Math.min(100, Math.max(0, maxV / (maxV - minV) * 100));
  return (
    <ResponsiveContainer width="100%" height={height || 150}>
      <AreaChart data={data} margin={{ top:4, right:4, left:0, bottom:0 }}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset={`${zeroPct}%`} stopColor="#4caf6e" stopOpacity={1}/>
            <stop offset={`${zeroPct}%`} stopColor="#e05a5a" stopOpacity={1}/>
          </linearGradient>
          <linearGradient id={`${gid}f`} x1="0" y1="0" x2="0" y2="1">
            <stop offset={`${zeroPct}%`} stopColor="#4caf6e" stopOpacity={0.15}/>
            <stop offset={`${zeroPct}%`} stopColor="#e05a5a" stopOpacity={0.15}/>
          </linearGradient>
        </defs>
        <XAxis dataKey="label" tick={{ fontSize:9, fontFamily:"'Josefin Sans',sans-serif", fill:C.gray1 }} tickLine={false} axisLine={false} interval={Math.max(0, Math.floor(data.length/6)-1)} />
        <YAxis
          domain={yDomain}
          ticks={yTicks}
          tick={{ fontSize:9, fontFamily:"'Josefin Sans',sans-serif", fill:C.gray1 }} tickLine={false} axisLine={false} width={52}
          tickFormatter={v => v===0?`0${cur||"€"}`:v>0?`+${Math.round(v)}${cur||"€"}`:`${Math.round(v)}${cur||"€"}`}
        />
        <Tooltip
          content={({active,payload}) => active && payload?.length ? (
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 12px"}}>
              {payload[0].payload.date && <div style={{fontSize:10,color:C.gray1,marginBottom:3,fontFamily:"'Josefin Sans',sans-serif"}}>{payload[0].payload.date}{payload[0].payload.instrument?" · "+payload[0].payload.instrument:""}</div>}
              <div style={{fontSize:13,color:payload[0].payload.pnl>=0?"#2a6e3a":"#c0392b",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>{fms(payload[0].payload.pnl??0)}{cur||"€"}</div>
              <div style={{fontSize:11,color:C.dim,fontFamily:"'Josefin Sans',sans-serif"}}>Cumulé : {fms(payload[0].value??0)}{cur||"€"}</div>
            </div>
          ) : null}
        />
        <ReferenceLine y={0} stroke={C.gray2} strokeWidth={1} />
        <Area type="monotone" dataKey="v" stroke={`url(#${gid})`} strokeWidth={2} fill={`url(#${gid}f)`} dot={{ r:2, fill:`url(#${gid})`, strokeWidth:0 }} activeDot={{ r:5, strokeWidth:0 }} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/* ─── Ambient floating particles (sections 2-5) ─────────────────── */
function AmbientParticles({ isDark }) {
  const canvasRef = useRef(null);
  const darkRef = useRef(isDark);
  useEffect(() => { darkRef.current = isDark; }, [isDark]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const N_GROUPS = 7, GRID = 5, SP = 30, R = 1.5;
    let W, H, groups, animId;

    const mkGroup = (w, h, fromEdge) => {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 0.12 + 0.03;
      const span = (GRID - 1) * SP;
      let x = Math.random() * w, y = Math.random() * h;
      if (fromEdge) {
        const edge = Math.floor(Math.random() * 4);
        if (edge === 0) { x = -span - 20; y = Math.random() * h; }
        else if (edge === 1) { x = w + 20; y = Math.random() * h; }
        else if (edge === 2) { x = Math.random() * w; y = -span - 20; }
        else { x = Math.random() * w; y = h + 20; }
      }
      return {
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        alpha: 0,
        maxAlpha: Math.random() * 0.10 + 0.03,
        fadeIn: true,
        fadeSpeed: Math.random() * 0.0012 + 0.0004,
        delay: Math.random() * 600,
      };
    };

    const init = () => {
      W = canvas.parentElement.offsetWidth;
      H = canvas.parentElement.offsetHeight;
      canvas.width = W; canvas.height = H;
      groups = Array.from({ length: N_GROUPS }, () => mkGroup(W, H, false));
    };

    const draw = () => {
      animId = requestAnimationFrame(draw);
      ctx.clearRect(0, 0, W, H);
      const dark = darkRef.current;
      ctx.fillStyle = dark ? '#E8D4C1' : '#1a1208';

      groups.forEach(g => {
        if (g.delay > 0) { g.delay--; return; }
        if (g.fadeIn) {
          g.alpha = Math.min(g.alpha + g.fadeSpeed, g.maxAlpha);
          if (g.alpha >= g.maxAlpha) g.fadeIn = false;
        } else {
          g.alpha = Math.max(g.alpha - g.fadeSpeed * 0.5, 0);
          if (g.alpha <= 0) { Object.assign(g, mkGroup(W, H, true)); return; }
        }
        g.x += g.vx; g.y += g.vy;
        const span = (GRID - 1) * SP;
        if (g.x > W + span + 40 || g.x < -span * 2 - 40 || g.y > H + span + 40 || g.y < -span * 2 - 40) {
          Object.assign(g, mkGroup(W, H, true)); return;
        }
        ctx.globalAlpha = g.alpha;
        for (let row = 0; row < GRID; row++) {
          for (let col = 0; col < GRID; col++) {
            ctx.beginPath();
            ctx.arc(g.x + col * SP, g.y + row * SP, R, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      });
      ctx.globalAlpha = 1;
    };

    init(); draw();
    const ro = new ResizeObserver(() => init());
    ro.observe(canvas.parentElement);
    return () => { cancelAnimationFrame(animId); ro.disconnect(); };
  }, []);

  return <canvas ref={canvasRef} style={{ position:"absolute", inset:0, width:"100%", height:"100%", pointerEvents:"none", zIndex:0 }} />;
}

/* ─── AUTH SCREEN ────────────────────────────────────────────────── */
/* ─── Scroll reveal helper ──────────────────────────────────────── */
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVis(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, vis];
}

function Reveal({ children, delay = 0, y = 50, style = {} }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} style={{
      ...style,
      opacity: vis ? 1 : 0,
      transform: vis ? "translateY(0)" : `translateY(${y}px)`,
      transition: `opacity 0.95s ${delay}s cubic-bezier(0.16,1,0.3,1), transform 0.95s ${delay}s cubic-bezier(0.16,1,0.3,1)`,
    }}>
      {children}
    </div>
  );
}

/* ─── Landing Page ───────────────────────────────────────────────── */
function Logo3D({ src }) {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left - r.width/2) / (r.width/2);
    const y = (e.clientY - r.top - r.height/2) / (r.height/2);
    el.style.transform = `perspective(600px) rotateX(${-y*22}deg) rotateY(${x*22}deg) translateZ(30px)`;
    el.style.filter = `drop-shadow(${-x*18}px ${-y*18}px 28px rgba(var(--gold-rgb),0.35))`;
  };
  const onLeave = () => {
    const el = ref.current; if (!el) return;
    el.style.transform = "perspective(600px) rotateX(0deg) rotateY(0deg) translateZ(0px)";
    el.style.filter = "drop-shadow(0px 12px 32px rgba(var(--gold-rgb),0.15))";
  };
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave}
      style={{ flexShrink:0, width:"clamp(120px,18vw,260px)", height:"clamp(120px,18vw,260px)", willChange:"transform", transition:"transform 0.18s cubic-bezier(.23,1,.32,1), filter 0.18s", filter:"drop-shadow(0px 12px 32px rgba(var(--gold-rgb),0.15))", cursor:"pointer" }}>
      <img src={src} alt="Fyltra" style={{ width:"100%", height:"100%", objectFit:"contain", display:"block" }} />
    </div>
  );
}

function TiltCard({ children, style = {} }) {
  const ref = useRef(null);
  const glRef = useRef(null);
  const onMove = (e) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left - r.width/2) / (r.width/2);
    const y = (e.clientY - r.top - r.height/2) / (r.height/2);
    el.style.transform = `perspective(900px) rotateX(${-y*8}deg) rotateY(${x*8}deg) translateZ(16px)`;
    if (glRef.current) glRef.current.style.background = `radial-gradient(circle at ${(x+1)/2*100}% ${(y+1)/2*100}%, rgba(var(--gold-rgb),0.12), transparent 65%)`;
  };
  const onLeave = () => {
    if (ref.current) ref.current.style.transform = "perspective(900px) rotateX(0deg) rotateY(0deg) translateZ(0px)";
    if (glRef.current) glRef.current.style.background = "none";
  };
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} style={{ transformStyle:"preserve-3d", willChange:"transform", transition:"transform 0.18s cubic-bezier(.23,1,.32,1)", position:"relative", ...style }}>
      <div ref={glRef} style={{ position:"absolute", inset:0, borderRadius:"inherit", pointerEvents:"none", zIndex:1, transition:"background 0.1s" }} />
      <div style={{ position:"relative", zIndex:2 }}>{children}</div>
    </div>
  );
}

function AuthScreen() {
  const JF = "'Josefin Sans',sans-serif";
  const CV = "'CoolveticaHv',sans-serif";
  const MN = "'MariellaNove',sans-serif";

  const [authModal, setAuthModal] = useState(false);
  const [email, setEmail]         = useState("");
  const [password, setPassword]   = useState("");
  const [showPwd, setShowPwd]     = useState(false);
  const [loading, setLoading]     = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetSent, setResetSent]   = useState(false);
  const [error, setError]         = useState("");
  const [success, setSuccess]     = useState("");
  const [paywall, setPaywall]     = useState(false);

  const ctaRef  = useRef(null);
  const glowRef = useRef(null);

  useEffect(() => {
    const onMove = (e) => {
      if (glowRef.current) {
        glowRef.current.style.left = `${e.clientX}px`;
        glowRef.current.style.top  = `${e.clientY}px`;
      }
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const onMagnet  = (e) => {
    const el = ctaRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left - r.width/2)  * 0.3;
    const y = (e.clientY - r.top  - r.height/2) * 0.3;
    el.style.transform = `translate(${x}px,${y}px) scale(1.04)`;
  };
  const offMagnet = () => { if (ctaRef.current) ctaRef.current.style.transform = "translate(0,0) scale(1)"; };

  const openAuth = () => { setAuthModal(true); setError(""); setSuccess(""); setPaywall(false); setForgotMode(false); setResetSent(false); };

  const sendReset = async () => {
    if (!email) { setError("Entre ton email."); return; }
    setLoading(true); setError("");
    const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: "https://fyltra.app/app" });
    if (resetErr) { setError(resetErr.message); } else { setResetSent(true); }
    setLoading(false);
  };

  const submit = async () => {
    if (!email || !password) { setError("Remplis tous les champs."); return; }
    setLoading(true); setError(""); setSuccess(""); setPaywall(false);

    // Tentative de connexion
    const { error: loginErr } = await supabase.auth.signInWithPassword({ email, password });

    if (!loginErr) {
      // Vérifier que l'abonnement est toujours actif
      try {
        const check = await fetch(`/api/check-purchase?email=${encodeURIComponent(email)}`);
        const { authorized } = await check.json();
        if (!authorized) {
          setLoading(false);
          setPaywall(true);
          return;
        }
      } catch {}
      setLoading(false);
      return;
    }

    // Compte inexistant → vérifier si l'email a un achat
    if (loginErr.message === "Invalid login credentials") {
      try {
        const check = await fetch(`/api/check-purchase?email=${encodeURIComponent(email)}`);
        const { authorized } = await check.json();
        if (!authorized) { setPaywall(true); setLoading(false); return; }
      } catch {
        setError("Erreur de vérification. Réessaie.");
        setLoading(false);
        return;
      }
      // Achat confirmé → créer le compte automatiquement
      const { error: signUpErr } = await supabase.auth.signUp({ email, password });
      if (signUpErr) { setError(signUpErr.message); }
      else setSuccess("Compte créé ! Vérifie ton email pour confirmer.");
    } else {
      setError(loginErr.message);
    }
    setLoading(false);
  };

  const BG   = "#060609";
  const CR   = "#f5f2ea";
  const GD   = "#e8cda9";
  const DIM  = "rgba(245,242,234,0.45)";
  const DIM2 = "rgba(245,242,234,0.18)";
  const BDR  = "rgba(245,242,234,0.08)";
  const PAD  = "clamp(24px,6vw,96px)";
  const CARD_BG  = "rgba(255,255,255,0.025)";
  const CARD_BDR = `1px solid ${BDR}`;
  const CARD_SHD = "0 0 0 1px rgba(255,255,255,0.05), 0 12px 48px rgba(0,0,0,0.6)";

  const features = L.onboarding;

  const SPARKS = [
    {top:"18%",left:"8%",delay:"0s",size:3},  {top:"32%",left:"92%",delay:"0.7s",size:2},
    {top:"55%",left:"5%",delay:"1.4s",size:2}, {top:"70%",left:"88%",delay:"2.1s",size:3},
    {top:"12%",left:"72%",delay:"0.4s",size:2},{top:"82%",left:"22%",delay:"1.8s",size:2},
  ];

  return (
    <div style={{ background:BG, color:CR, fontFamily:JF, overflowX:"hidden" }}>
      <style>{FONTS}</style>
      <style>{`
        @keyframes lnFadeUp{from{opacity:0;transform:translateY(28px);}to{opacity:1;transform:translateY(0);}}
        .ln1{animation:lnFadeUp 0.8s 0.05s both cubic-bezier(0.16,1,0.3,1);}
        .ln2{animation:lnFadeUp 0.9s 0.2s  both cubic-bezier(0.16,1,0.3,1);}
        .ln3{animation:lnFadeUp 0.8s 0.38s both cubic-bezier(0.16,1,0.3,1);}
        .ln4{animation:lnFadeUp 0.8s 0.52s both cubic-bezier(0.16,1,0.3,1);}
        .ln5{animation:lnFadeUp 0.8s 0.66s both cubic-bezier(0.16,1,0.3,1);}
        @keyframes lnGlow{0%,100%{opacity:0.35;}50%{opacity:0.6;}}
        .lnglow{animation:lnGlow 5s ease-in-out infinite;}
        @keyframes float3d{
          0%,100%{transform:perspective(1400px) rotateX(10deg) rotateY(-5deg) translateY(0px);}
          50%{transform:perspective(1400px) rotateX(10deg) rotateY(-5deg) translateY(-14px);}
        }
        .img3d{animation:float3d 7s ease-in-out infinite;transform:perspective(1400px) rotateX(10deg) rotateY(-5deg);}
        @keyframes sparkFloat{
          0%{opacity:0;transform:translateY(0) scale(0);}
          20%{opacity:1;transform:translateY(-8px) scale(1);}
          80%{opacity:0.6;transform:translateY(-20px) scale(0.8);}
          100%{opacity:0;transform:translateY(-32px) scale(0);}
        }
        .spark{animation:sparkFloat 3s ease-in-out infinite;}
        @keyframes shimmerText{
          0%{background-position:0% 50%;}
          50%{background-position:100% 50%;}
          100%{background-position:0% 50%;}
        }
        .shimmer-text{
          background:linear-gradient(90deg,#f5f2ea 0%,#e8cda9 40%,#f5d898 60%,#e8cda9 80%,#f5f2ea 100%);
          background-size:200% auto;
          -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
          animation:shimmerText 4s linear infinite;
        }
        @keyframes borderPulse{0%,100%{box-shadow:0 0 0 0 rgba(232,205,169,0);}50%{box-shadow:0 0 0 4px rgba(232,205,169,0.12);}}
        .cta-primary{animation:borderPulse 3s ease-in-out infinite;}
        @keyframes scanline{from{transform:translateY(-100%);}to{transform:translateY(100vh);}}
        .mouse-glow{position:fixed;width:600px;height:600px;border-radius:50%;background:radial-gradient(circle,rgba(232,205,169,0.04) 0%,transparent 70%);pointer-events:none;transform:translate(-50%,-50%);z-index:0;transition:left 0.1s,top 0.1s;}
      `}</style>

      {/* Mouse glow tracker */}
      <div ref={glowRef} className="mouse-glow" />

      {/* ── NAV ── */}
      <nav style={{ position:"fixed", top:0, left:0, right:0, zIndex:200, display:"flex", alignItems:"center", justifyContent:"space-between", padding:`0 ${PAD}`, height:56, background:"rgba(6,6,9,0.88)", backdropFilter:"blur(20px)", borderBottom:`1px solid ${BDR}` }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <img src="/fyltra-logo-black.svg" style={{ height:60, width:"auto" }} alt="Fyltra" />
        </div>
        <div style={{ display:"flex", gap:28, position:"absolute", left:"50%", transform:"translateX(-50%)" }}>
          {["Features","Pricing","About"].map(l => (
            <span key={l} style={{ fontSize:12, color:DIM, cursor:"pointer", letterSpacing:"0.02em", transition:"color 0.15s" }}
              onMouseEnter={e=>e.currentTarget.style.color=CR}
              onMouseLeave={e=>e.currentTarget.style.color=DIM}>{l}</span>
          ))}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <button onClick={()=>openAuth("login")} style={{ background:"none", border:"none", color:DIM, fontSize:12, cursor:"pointer", padding:"8px 12px", borderRadius:7, transition:"all 0.15s" }}
            onMouseEnter={e=>{e.currentTarget.style.background="rgba(255,255,255,0.06)";e.currentTarget.style.color=CR;}}
            onMouseLeave={e=>{e.currentTarget.style.background="none";e.currentTarget.style.color=DIM;}}>Se connecter</button>
          <a href="#tarifs" style={{ background:CR, color:BG, border:"none", borderRadius:7, padding:"8px 18px", fontSize:12, fontWeight:700, letterSpacing:"0.04em", cursor:"pointer", textDecoration:"none", transition:"opacity 0.15s" }}
            onMouseEnter={e=>e.currentTarget.style.opacity="0.86"}
            onMouseLeave={e=>e.currentTarget.style.opacity="1"}>Commencer</a>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section style={{ position:"relative", minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center", padding:`140px ${PAD} 60px`, overflow:"hidden" }}>
        {/* Glows */}
        <div className="lnglow" style={{ position:"absolute", top:"10%", left:"30%", width:"min(500px,60vw)", height:400, background:"radial-gradient(ellipse, rgba(232,205,169,0.09) 0%, transparent 70%)", pointerEvents:"none", filter:"blur(60px)", zIndex:0 }} />
        <div className="lnglow" style={{ position:"absolute", top:"30%", right:"15%", width:"min(300px,40vw)", height:250, background:"radial-gradient(ellipse, rgba(232,205,169,0.06) 0%, transparent 70%)", pointerEvents:"none", filter:"blur(40px)", zIndex:0, animationDelay:"2.5s" }} />
        {/* Grid */}
        <div style={{ position:"absolute", inset:0, backgroundImage:`linear-gradient(${BDR} 1px, transparent 1px), linear-gradient(90deg, ${BDR} 1px, transparent 1px)`, backgroundSize:"60px 60px", WebkitMaskImage:"radial-gradient(ellipse 90% 60% at 50% 30%, black, transparent)", maskImage:"radial-gradient(ellipse 90% 60% at 50% 30%, black, transparent)", pointerEvents:"none", zIndex:0 }} />
        {/* Floating sparks */}
        {SPARKS.map((s,i)=>(
          <div key={i} className="spark" style={{ position:"absolute", top:s.top, left:s.left, width:s.size, height:s.size, borderRadius:"50%", background:GD, animationDelay:s.delay, animationDuration:`${2.5+i*0.4}s`, zIndex:1, pointerEvents:"none" }} />
        ))}

        {/* Headline */}
        <h1 className="ln1" style={{ fontFamily:CV, fontSize:"clamp(52px,7vw,100px)", letterSpacing:"-0.02em", lineHeight:0.96, color:CR, marginBottom:24, maxWidth:800, position:"relative", zIndex:1 }}>
          Ton trading<br />mérite de la{" "}
          <span className="shimmer-text">structure.</span>
        </h1>

        {/* Subtitle */}
        <p className="ln2" style={{ fontSize:16, color:DIM, lineHeight:1.8, marginBottom:40, maxWidth:440, fontWeight:300, position:"relative", zIndex:1 }}>
          Fyltra transforme tes données brutes en règles concrètes. Analyse, améliore, répète.
        </p>

        {/* CTAs */}
        <div className="ln3" style={{ display:"flex", gap:12, flexWrap:"wrap", justifyContent:"center", marginBottom:80, position:"relative", zIndex:1 }}>
          <button ref={ctaRef} onClick={()=>openAuth()}
            onMouseMove={onMagnet} onMouseLeave={offMagnet}
            className="cta-primary"
            style={{ background:CR, color:BG, border:"none", borderRadius:9, padding:"14px 32px", fontSize:13, fontWeight:700, letterSpacing:"0.04em", cursor:"pointer", transition:"transform 0.2s cubic-bezier(.23,1,.32,1), opacity 0.15s", display:"flex", alignItems:"center", gap:9 }}>
            Accéder à Fyltra
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </button>
          <button onClick={()=>openAuth("login")} style={{ background:"rgba(255,255,255,0.04)", color:CR, border:CARD_BDR, borderRadius:9, padding:"14px 24px", fontSize:13, fontWeight:400, cursor:"pointer", transition:"background 0.15s" }}
            onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.08)"}
            onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.04)"}>Se connecter</button>
        </div>

        {/* 3D Product image */}
        <div className="ln4" style={{ width:"100%", maxWidth:960, position:"relative", zIndex:1 }}>
          {/* bottom fade */}
          <div style={{ position:"absolute", bottom:0, left:0, right:0, height:"38%", background:`linear-gradient(to bottom, transparent, ${BG})`, zIndex:3, pointerEvents:"none" }} />
          {/* left/right fades */}
          <div style={{ position:"absolute", top:0, left:0, bottom:0, width:"8%", background:`linear-gradient(to right, ${BG}, transparent)`, zIndex:3, pointerEvents:"none" }} />
          <div style={{ position:"absolute", top:0, right:0, bottom:0, width:"8%", background:`linear-gradient(to left, ${BG}, transparent)`, zIndex:3, pointerEvents:"none" }} />
          {/* 3D frame */}
          <div className="img3d" style={{ borderRadius:14, overflow:"hidden", boxShadow:"0 0 0 1px rgba(255,255,255,0.08), 0 60px 120px rgba(0,0,0,0.9), 40px 40px 80px rgba(0,0,0,0.6), -10px -10px 40px rgba(232,205,169,0.04)", willChange:"transform" }}>
            {/* Chrome bar */}
            <div style={{ display:"flex", alignItems:"center", gap:6, padding:"10px 14px", background:"rgba(14,14,20,0.99)", borderBottom:`1px solid ${BDR}` }}>
              {[0.18,0.1,0.06].map((o,i)=><div key={i} style={{ width:9, height:9, borderRadius:"50%", background:`rgba(255,255,255,${o})` }}/>)}
              <div style={{ flex:1, textAlign:"center" }}>
                <span style={{ fontSize:10, color:DIM2, letterSpacing:"0.08em" }}>app.fyltra.io</span>
              </div>
            </div>
            {/* Screenshot — place journal.png in /public/ */}
            <img src="/journal.png" alt="Fyltra Journal" style={{ width:"100%", display:"block" }}
              onError={e=>{ e.currentTarget.style.display="none"; e.currentTarget.nextSibling.style.display="flex"; }} />
            {/* Fallback if no image yet */}
            <div style={{ display:"none", height:320, background:"rgba(10,10,16,0.97)", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:12 }}>
              <span style={{ fontSize:28, opacity:0.15 }}>◉</span>
              <span style={{ fontSize:11, color:DIM2, letterSpacing:"0.12em", textTransform:"uppercase" }}>Place journal.png in /public/</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section style={{ padding:`120px ${PAD}`, position:"relative" }}>
        <Reveal style={{ marginBottom:64, textAlign:"center" }}>
          <div style={{ fontSize:11, color:GD, letterSpacing:"0.2em", fontWeight:600, marginBottom:16, textTransform:"uppercase" }}>Ce que fait Fyltra</div>
          <div style={{ fontFamily:CV, fontSize:"clamp(32px,4.5vw,58px)", letterSpacing:"-0.02em", color:CR, lineHeight:1.0 }}>
            Tout ce dont tu as besoin<br />pour progresser.
          </div>
        </Reveal>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:14 }}>
          {features.map((f,i)=>(
            <Reveal key={f.n} delay={i*0.08} y={40}>
              <TiltCard style={{ background:CARD_BG, border:CARD_BDR, borderRadius:16, padding:"32px 28px", height:"100%", boxSizing:"border-box", boxShadow:CARD_SHD, transition:"background 0.2s,border-color 0.2s" }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor="rgba(232,205,169,0.2)";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=BDR;}}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:22 }}>
                  <span style={{ fontSize:22, color:"rgba(232,205,169,0.55)", lineHeight:1 }}>{f.icon}</span>
                  <span style={{ fontSize:9, color:DIM2, letterSpacing:"0.18em", fontWeight:600 }}>{f.n}</span>
                </div>
                <div style={{ fontSize:13, fontWeight:700, color:CR, letterSpacing:"0.05em", marginBottom:10 }}>{f.title}</div>
                <p style={{ fontSize:13, color:DIM, lineHeight:1.72, fontWeight:300 }}>{f.sub}</p>
              </TiltCard>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── STATS ── */}
      <section style={{ padding:`64px ${PAD}`, borderTop:`1px solid ${BDR}`, borderBottom:`1px solid ${BDR}` }}>
        <div style={{ display:"flex", justifyContent:"space-around", flexWrap:"wrap", gap:48, textAlign:"center" }}>
          {[["90%","des traders perdent faute de structure"],["3.2x","meilleur win rate avec un journal"],["12h","économisées par mois en analyse"]].map(([v,l])=>(
            <Reveal key={l} y={30}>
              <div>
                <div style={{ fontFamily:CV, fontSize:"clamp(40px,5vw,72px)", letterSpacing:"-0.03em", background:`linear-gradient(135deg, ${CR} 20%, ${GD} 100%)`, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text", lineHeight:1 }}>{v}</div>
                <div style={{ fontSize:11, color:DIM2, letterSpacing:"0.08em", marginTop:12, textTransform:"uppercase", fontWeight:600, maxWidth:160, margin:"12px auto 0" }}>{l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── CTA BOTTOM ── */}
      <section style={{ padding:`130px ${PAD}`, textAlign:"center", position:"relative", overflow:"hidden" }}>
        <div className="lnglow" style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:"min(700px,80vw)", height:500, background:"radial-gradient(ellipse, rgba(232,205,169,0.08) 0%, transparent 70%)", pointerEvents:"none", filter:"blur(50px)" }} />
        <Reveal y={40}>
          <div style={{ fontFamily:CV, fontSize:"clamp(38px,5.5vw,78px)", letterSpacing:"-0.025em", color:CR, lineHeight:0.98, marginBottom:24 }}>
            Commence à journaliser<br />dès aujourd'hui.
          </div>
          <p style={{ fontSize:15, color:DIM, marginBottom:40, fontWeight:300 }}>Choisis un plan et commence à journaliser aujourd'hui.</p>
          <a href="#tarifs" style={{ display:"inline-block", background:CR, color:BG, border:"none", borderRadius:9, padding:"15px 40px", fontSize:14, fontWeight:700, letterSpacing:"0.04em", cursor:"pointer", textDecoration:"none", transition:"opacity 0.15s, transform 0.2s" }}
            onMouseEnter={e=>{e.currentTarget.style.opacity="0.88";e.currentTarget.style.transform="scale(1.03)";}}
            onMouseLeave={e=>{e.currentTarget.style.opacity="1";e.currentTarget.style.transform="scale(1)";}}>
            Voir les tarifs →
          </a>
        </Reveal>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ padding:`22px ${PAD}`, borderTop:`1px solid ${BDR}`, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
        <div style={{ display:"flex", alignItems:"center", gap:7 }}>
          <img src="/fyltra-logo-black.svg" style={{ height:40, width:"auto", opacity:0.5 }} alt="Fyltra" />
        </div>
        <span style={{ fontSize:10, color:DIM2, letterSpacing:"0.08em" }}>© 2025 Fyltra · Trading Journal</span>
      </footer>

      {/* ── AUTH MODAL ── */}
      {authModal && (
        <div onClick={()=>setAuthModal(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.82)", backdropFilter:"blur(16px)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:"rgba(10,10,16,0.98)", border:CARD_BDR, borderRadius:20, padding:"40px 36px", maxWidth:380, width:"100%", boxShadow:"0 40px 80px rgba(0,0,0,0.85)", boxSizing:"border-box" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:32 }}>
              <div>
                <img src="/fyltra-logo-black.svg" style={{ height:56, width:"auto" }} alt="Fyltra" />
                <div style={{ fontSize:10, color:DIM, letterSpacing:"0.15em", textTransform:"uppercase", marginTop:3, fontFamily:JF }}>Connexion</div>
              </div>
              <button onClick={()=>setAuthModal(false)} style={{ background:"none", border:"none", color:DIM, cursor:"pointer", fontSize:22, lineHeight:1, padding:0 }}>×</button>
            </div>
            {forgotMode ? (
              resetSent ? (
                <div style={{ textAlign:"center", padding:"16px 0" }}>
                  <div style={{ fontSize:28, marginBottom:12 }}>✉️</div>
                  <div style={{ fontSize:13, color:CR, fontFamily:JF, fontWeight:600, marginBottom:8 }}>{L.auth.linkSent}</div>
                  <div style={{ fontSize:11, color:DIM, fontFamily:JF, lineHeight:1.6 }}>{L.auth.linkSentDesc}</div>
                  <button onClick={()=>{ setForgotMode(false); setResetSent(false); setError(""); }} style={{ marginTop:20, background:"none", border:`1px solid ${BDR}`, borderRadius:8, padding:"10px 20px", color:DIM, fontSize:10, fontFamily:JF, fontWeight:700, letterSpacing:"0.15em", textTransform:"uppercase", cursor:"pointer" }}>
                    {L.auth.back}
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom:8, fontSize:12, color:DIM, fontFamily:JF, lineHeight:1.5 }}>{L.auth.forgotDesc}</div>
                  <div style={{ marginBottom:14 }}>
                    <input type="email" placeholder="Email" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&sendReset()}
                      style={{ width:"100%", background:"rgba(255,255,255,0.04)", border:CARD_BDR, borderRadius:10, padding:"14px 16px", color:CR, fontSize:14, fontFamily:JF, fontWeight:300, outline:"none", boxSizing:"border-box" }}/>
                  </div>
                  {error && <div style={{ marginBottom:12, fontSize:11, color:"#e05a5a", fontFamily:JF }}>{error}</div>}
                  <button onClick={sendReset} disabled={loading}
                    style={{ width:"100%", padding:"14px", borderRadius:10, border:"none", background:loading?"rgba(255,255,255,0.05)":CR, color:loading?"rgba(255,255,255,0.3)":BG, fontSize:10, fontFamily:JF, fontWeight:700, letterSpacing:"0.18em", textTransform:"uppercase", cursor:loading?"not-allowed":"pointer", transition:"all 0.2s", marginBottom:12 }}>
                    {loading?"···":L.auth.sendLink}
                  </button>
                  <button onClick={()=>{ setForgotMode(false); setError(""); }} style={{ width:"100%", background:"none", border:"none", color:DIM, fontSize:11, fontFamily:JF, cursor:"pointer", textAlign:"center" }}>
                    {L.auth.backToLogin}
                  </button>
                </>
              )
            ) : (
              <>
                <div style={{ marginBottom:12 }}>
                  <input type="email" placeholder="Email" value={email} onChange={e=>setEmail(e.target.value)}
                    style={{ width:"100%", background:"rgba(255,255,255,0.04)", border:CARD_BDR, borderRadius:10, padding:"14px 16px", color:CR, fontSize:14, fontFamily:JF, fontWeight:300, outline:"none", boxSizing:"border-box" }}/>
                </div>
                <div style={{ marginBottom:6, position:"relative" }}>
                  <input type={showPwd?"text":"password"} placeholder={L.auth.passwordPH} value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()}
                    style={{ width:"100%", background:"rgba(255,255,255,0.04)", border:CARD_BDR, borderRadius:10, padding:"14px 46px 14px 16px", color:CR, fontSize:14, fontFamily:JF, fontWeight:300, outline:"none", boxSizing:"border-box" }}/>
                  <button onClick={()=>setShowPwd(v=>!v)} style={{ position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,0.3)", lineHeight:1, padding:0 }}>
                    {showPwd
                      ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                  </button>
                </div>
                <div style={{ marginBottom:14, textAlign:"right" }}>
                  <button onClick={()=>{ setForgotMode(true); setError(""); setSuccess(""); }} style={{ background:"none", border:"none", color:"rgba(245,242,234,0.7)", fontSize:11, fontFamily:JF, cursor:"pointer", padding:0, textDecoration:"underline", textUnderlineOffset:3 }}>
                    {L.auth.forgotBtn}
                  </button>
                </div>
                {error   && <div style={{ marginBottom:12, fontSize:11, color:"#e05a5a", fontFamily:JF }}>{error}</div>}
                {paywall && (
                  <div style={{ marginBottom:12, padding:"12px 14px", borderRadius:10, background:"rgba(232,205,169,0.07)", border:"1px solid rgba(232,205,169,0.22)", fontFamily:JF }}>
                    <div style={{ fontSize:11, color:"#e8cda9", marginBottom:8, fontWeight:600, letterSpacing:"0.05em" }}>{L.auth.paywallTitle}</div>
                    <div style={{ fontSize:11, color:"rgba(232,205,169,0.65)", marginBottom:10, lineHeight:1.55 }}>{L.auth.paywallDesc}</div>
                    <button onClick={async () => {
                      try {
                        const r = await fetch("/api/create-checkout", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ plan:"fyltra_plus" }) });
                        const { url } = await r.json();
                        if (url) window.location.href = url;
                      } catch {}
                    }} style={{ display:"inline-block", background:"#111", color:"#fff", borderRadius:7, padding:"8px 16px", fontSize:10, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", border:"none", cursor:"pointer", fontFamily:"'Josefin Sans',sans-serif" }}>
                      {L.auth.paywallCTA}
                    </button>
                  </div>
                )}
                {success && <div style={{ marginBottom:12, fontSize:11, color:"#4caf6e", fontFamily:JF }}>{success}</div>}
                <button onClick={submit} disabled={loading}
                  style={{ width:"100%", padding:"14px", borderRadius:10, border:"none", background:loading?"rgba(255,255,255,0.05)":CR, color:loading?"rgba(255,255,255,0.3)":BG, fontSize:10, fontFamily:JF, fontWeight:700, letterSpacing:"0.18em", textTransform:"uppercase", cursor:loading?"not-allowed":"pointer", transition:"all 0.2s" }}>
                  {loading?"···":L.auth.loginBtn}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── MT5 CONNECT ────────────────────────────────────────────────── */
function MT5Connect({ user, darkMode, lang = "fr", onTradesImported, onAccountConnected, mt5Count = 0, mt5Limit = 5 }) {
  const isDark = darkMode;
  const [mt5Form, setMt5Form] = useState({ login:"", password:"", server:"", name:"", platform:"mt5", accountType:"propfirm" });
  const [showMt5Pwd, setShowMt5Pwd] = useState(false);
  const [mt5Account, setMt5Account] = useState(null);
  const [mt5Loading,   setMt5Loading]   = useState(false);
  const [mt5Syncing,   setMt5Syncing]   = useState(false);
  const [mt5Deploying, setMt5Deploying] = useState(false);
  const [mt5Status,    setMt5Status]    = useState("");
  const [mt5Error,     setMt5Error]     = useState("");
  const [mt5NotFound,  setMt5NotFound]  = useState(false);
  const [serverOpen,   setServerOpen]   = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase.from("mt5_accounts").select("*").eq("user_id", user.id).single()
      .then(({ data }) => { if (data) setMt5Account(data); });
  }, [user]);

  const connectMT5 = async () => {
    if (mt5Count >= mt5Limit) { setMt5Error(lang === "en" ? `Limit of ${mt5Limit} MT5 accounts reached.` : `Limite de ${mt5Limit} comptes MT5 atteinte.`); return; }
    if (!mt5Form.login || !mt5Form.password || !mt5Form.server) { setMt5Error(L.mt5.fillFields); return; }
    setMt5Loading(true); setMt5Error(""); setMt5Status("");
    try {
      const res = await fetch("/api/connect-mt5", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify(mt5Form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || L.mt5.connecting);
      const accountName = mt5Form.name || L.mt5.accountLabel(mt5Form.login);
      const { data: saved } = await supabase.from("mt5_accounts").upsert({
        user_id: user.id, metaapi_id: data.accountId,
        broker_server: mt5Form.server, login: mt5Form.login,
        name: accountName, connected: true,
      }).select().single();
      if (saved) setMt5Account(saved);
      if (onAccountConnected) {
        const newPf = { id: Date.now(), type: mt5Form.accountType, firm: mt5Form.server, name: accountName, capital:"0", metaapi_id: data.accountId, platform: mt5Form.platform, login: mt5Form.login, status:"active" };
        onAccountConnected(newPf);
      }
      setMt5Status(L.mt5.connectedStatus);
    } catch(e) { setMt5Error(e.message); }
    setMt5Loading(false);
  };

  const syncTrades = async () => {
    if (!mt5Account) return;
    setMt5Syncing(true); setMt5Deploying(false); setMt5Error(""); setMt5Status(""); setMt5NotFound(false);
    const MAX = 12;
    for (let i = 0; i < MAX; i++) {
      try {
        const res = await fetch("/api/sync-trades", {
          method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({ accountId: mt5Account.metaapi_id }),
        });
        const data = await res.json();
        if (data.status === "deploying") {
          setMt5Deploying(true);
          await new Promise(r => setTimeout(r, 15000));
          continue;
        }
        setMt5Deploying(false);
        if (res.status === 404 || (data.error || "").toLowerCase().includes("not found")) {
          setMt5NotFound(true); break;
        }
        if (!res.ok) { setMt5Error(data.error || "Sync error"); break; }
        if (data.trades?.length > 0 && onTradesImported) onTradesImported(data.trades);
        setMt5Status(L.mt5.tradesImported(data.total));
        break;
      } catch(e) { setMt5Deploying(false); setMt5Error(e.message); break; }
    }
    setMt5Syncing(false); setMt5Deploying(false);
  };

  const reconnectMT5 = async () => {
    await supabase.from("mt5_accounts").delete().eq("user_id", user.id);
    setMt5Account(null); setMt5NotFound(false); setMt5Error(""); setMt5Status("");
  };

  const f = (k,v) => setMt5Form(p => ({...p, [k]:v}));
  const fieldStyle = { width:"100%", background:C.bg3, border:`1px solid ${C.border}`, borderRadius:8, padding:"11px 14px", color:C.white, fontSize:13, fontFamily:"'Josefin Sans',sans-serif", fontWeight:300, outline:"none" };

  return (
    <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:"18px 16px",marginBottom:12}}>
      <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:14}}>{L.mt5.title}</div>

      {mt5Account ? (
        <div>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
            <div style={{width:8,height:8,borderRadius:4,background:"#4caf6e",flexShrink:0}}/>
            <div>
              <div style={{fontSize:13,color:C.white,fontFamily:"'Josefin Sans',sans-serif"}}>{mt5Account.name}</div>
              <div style={{fontSize:10,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.06em"}}>{mt5Account.broker_server} · #{mt5Account.login}</div>
            </div>
          </div>
          {mt5NotFound ? (
            <div style={{background:"rgba(224,90,90,0.08)",border:"1px solid rgba(224,90,90,0.25)",borderRadius:10,padding:"14px 16px",marginBottom:8}}>
              <div style={{fontSize:13,color:"#e05a5a",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:6}}>{L.mt5.notFound}</div>
              <div style={{fontSize:11,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",lineHeight:1.6,marginBottom:12}}>{L.mt5.notFoundDesc}</div>
              <button onClick={reconnectMT5} style={{width:"100%",padding:"10px",borderRadius:8,border:"none",background:"rgba(224,90,90,0.7)",color:"#fff",fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.12em",textTransform:"uppercase",cursor:"pointer"}}>
                {L.mt5.reconnect}
              </button>
            </div>
          ) : (
            <>
              <button onClick={syncTrades} disabled={mt5Syncing} style={{width:"100%",padding:"11px",borderRadius:8,border:mt5Deploying?"1px solid rgba(232,205,169,0.3)":"none",background:mt5Syncing?"#333":C.accent,color:mt5Syncing?"#888":darkMode?"#111":"#fff",fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.15em",textTransform:"uppercase",cursor:mt5Syncing?"not-allowed":"pointer",transition:"all 0.2s"}}>
                {mt5Deploying ? L.mt5.deploying : mt5Syncing ? L.mt5.syncing : L.mt5.sync}
              </button>
              {mt5Deploying && <div style={{fontSize:10,color:"rgba(232,205,169,0.5)",fontFamily:"'Josefin Sans',sans-serif",textAlign:"center",marginTop:4}}>Les trades s'importeront automatiquement dès la connexion.</div>}
            </>
          )}
          <button onClick={()=>{setMt5Account(null);setMt5NotFound(false);}} style={{width:"100%",marginTop:8,padding:"8px",borderRadius:8,border:`1px solid ${C.border}`,background:"transparent",color:C.gray2,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer"}}>
            {L.mt5.changeAccount}
          </button>
        </div>
      ) : mt5Count >= mt5Limit ? (
        <div style={{fontSize:12,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",textAlign:"center",padding:"12px 0"}}>
          {lang==="en" ? `Limit of ${mt5Limit} MT5 accounts reached.` : `Limite de ${mt5Limit} comptes MT5 atteinte.`}
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div style={{display:"flex",gap:6,background:C.bg3,borderRadius:8,padding:3}}>
            {[{k:"propfirm",l:"Prop Firm"},{k:"personal",l:lang==="fr"?"Fond Propre":"Personal Account"}].map(({k,l})=>(
              <button key={k} onClick={()=>f("accountType",k)} style={{flex:1,padding:"7px",borderRadius:6,border:"none",background:mt5Form.accountType===k?C.accent:"transparent",color:mt5Form.accountType===k?(darkMode?"#111":"#fff"):C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer",transition:"all 0.2s"}}>
                {l}
              </button>
            ))}
          </div>
          <div style={{display:"flex",gap:6,background:C.bg3,borderRadius:8,padding:3}}>
            {["mt5","mt4"].map(p=>(
              <button key={p} onClick={()=>f("platform",p)} style={{flex:1,padding:"7px",borderRadius:6,border:"none",background:mt5Form.platform===p?C.accent:"transparent",color:mt5Form.platform===p?(darkMode?"#111":"#fff"):C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer",transition:"all 0.2s"}}>
                {p.toUpperCase()}
              </button>
            ))}
          </div>
          <input placeholder={L.mt5.fieldLogin} value={mt5Form.login} onChange={e=>f("login",e.target.value)} style={fieldStyle}/>
          <div style={{position:"relative"}}>
            <input type={showMt5Pwd?"text":"password"} placeholder={L.mt5.fieldPwd} value={mt5Form.password} onChange={e=>f("password",e.target.value)} style={{...fieldStyle, paddingRight:46}}/>
            <button onClick={()=>setShowMt5Pwd(v=>!v)} style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:"rgba(255,255,255,0.35)",lineHeight:1,padding:0}}>
              {showMt5Pwd?(
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              ):(
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              )}
            </button>
          </div>
          <div style={{position:"relative"}}>
            <div style={{display:"flex",alignItems:"center",gap:8,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:"11px 14px"}}>
              {(()=>{const list=mt5Form.accountType==="personal"?KNOWN_BROKERS:KNOWN_PROPFIRMS;const match=list.find(p=>p.name.toLowerCase()===mt5Form.server.toLowerCase());return match?<img src={`https://www.google.com/s2/favicons?sz=128&domain=${match.domain}`} style={{width:18,height:18,borderRadius:3,objectFit:"contain",flexShrink:0}} alt="" onError={e=>e.target.style.display="none"}/>:null;})()}
              <input placeholder={L.mt5.fieldServer} value={mt5Form.server} onChange={e=>{f("server",e.target.value);setServerOpen(true);}} onFocus={()=>setServerOpen(true)} onBlur={()=>setTimeout(()=>setServerOpen(false),150)} style={{flex:1,background:"transparent",border:"none",color:C.white,fontSize:13,fontFamily:"'Josefin Sans',sans-serif",fontWeight:300,outline:"none",padding:0}}/>
            </div>
            {serverOpen && (()=>{const list=mt5Form.accountType==="personal"?KNOWN_BROKERS:KNOWN_PROPFIRMS;const filtered=mt5Form.server.length>0?list.filter(p=>p.name.toLowerCase().includes(mt5Form.server.toLowerCase())).slice(0,6):list.slice(0,6);return filtered.length>0?(
              <div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:300,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden",boxShadow:"0 8px 24px rgba(0,0,0,0.3)",marginTop:4}}>
                {filtered.map(p=>(
                  <button key={p.name} onMouseDown={()=>{f("server",p.name);setServerOpen(false);}} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"9px 12px",border:"none",background:"transparent",cursor:"pointer",textAlign:"left",color:C.white,fontSize:12,fontFamily:"'Josefin Sans',sans-serif",transition:"background 0.15s"}} onMouseEnter={e=>e.currentTarget.style.background=C.bg3} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <img src={`https://www.google.com/s2/favicons?sz=128&domain=${p.domain}`} style={{width:18,height:18,borderRadius:3,objectFit:"contain",flexShrink:0}} alt="" onError={e=>e.target.style.display="none"}/>
                    {p.name}
                  </button>
                ))}
              </div>
            ):null;})()}
          </div>
          <input placeholder={L.mt5.fieldName} value={mt5Form.name} onChange={e=>f("name",e.target.value)} style={fieldStyle}/>
          <button onClick={connectMT5} disabled={mt5Loading} style={{width:"100%",padding:"12px",borderRadius:8,border:"none",background:mt5Loading?"#333":C.accent,color:mt5Loading?"#888":darkMode?"#111":"#fff",fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.15em",textTransform:"uppercase",cursor:mt5Loading?"not-allowed":"pointer",transition:"all 0.2s",marginTop:4}}>
            {mt5Loading ? L.mt5.connecting : L.mt5.connect}
          </button>
          <div style={{fontSize:10,color:C.gray2,fontFamily:"'Josefin Sans',sans-serif",lineHeight:1.5}}>
            {L.mt5.pwdHint}
          </div>
        </div>
      )}

      {mt5Status && <div style={{marginTop:12,fontSize:11,color:"#4caf6e",fontFamily:"'Josefin Sans',sans-serif",lineHeight:1.5}}>{mt5Status}</div>}
      {mt5Error && <div style={{marginTop:12,fontSize:11,color:"#e05a5a",fontFamily:"'Josefin Sans',sans-serif"}}>{mt5Error}</div>}
    </div>
  );
}


/* ─── RESET PASSWORD SCREEN ─────────────────────────────────────── */
function ResetPasswordScreen({ onDone }) {
  const JF = "'Josefin Sans',sans-serif";
  const BG = "#060609"; const CR = "#f5f2ea";
  const BDR = "rgba(245,242,234,0.08)";
  const CARD_BDR = `1px solid ${BDR}`;
  const [password, setPassword]     = useState("");
  const [confirm,  setConfirm]      = useState("");
  const [showPwd,  setShowPwd]      = useState(false);
  const [loading,  setLoading]      = useState(false);
  const [error,    setError]        = useState("");
  const [done,     setDone]         = useState(false);

  const submit = async () => {
    if (!password || !confirm) { setError(L.resetPwd.fillBoth); return; }
    if (password !== confirm)  { setError(L.resetPwd.noMatch); return; }
    if (password.length < 6)   { setError(L.auth.pwdMin); return; }
    setLoading(true); setError("");
    const { error: updateErr } = await supabase.auth.updateUser({ password });
    if (updateErr) { setError(updateErr.message); setLoading(false); return; }
    setDone(true);
    setTimeout(onDone, 2000);
  };

  const match = password && confirm && password === confirm;

  return (
    <div style={{ minHeight:"100vh", background:BG, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <style>{FONTS}</style>
      <div style={{ background:"rgba(10,10,16,0.98)", border:CARD_BDR, borderRadius:20, padding:"40px 36px", maxWidth:380, width:"100%", boxShadow:"0 40px 80px rgba(0,0,0,0.85)" }}>
        <div style={{ marginBottom:28, textAlign:"center" }}>
          <img src="/fyltra-logo-black.svg" style={{ height:56, width:"auto", marginBottom:8 }} alt="Fyltra" />
          <div style={{ fontSize:10, color:"rgba(245,242,234,0.45)", letterSpacing:"0.15em", textTransform:"uppercase", fontFamily:JF }}>{L.resetPwd.title}</div>
        </div>
        {done ? (
          <div style={{ textAlign:"center" }}>
            <div style={{ fontSize:28, marginBottom:12 }}>✓</div>
            <div style={{ fontSize:13, color:CR, fontFamily:JF }}>{L.resetPwd.updated}</div>
          </div>
        ) : (
          <>
            <div style={{ marginBottom:12, position:"relative" }}>
              <input type={showPwd?"text":"password"} placeholder={L.resetPwd.newPwdPH} value={password} onChange={e=>setPassword(e.target.value)}
                style={{ width:"100%", background:"rgba(255,255,255,0.04)", border:CARD_BDR, borderRadius:10, padding:"14px 46px 14px 16px", color:CR, fontSize:14, fontFamily:JF, fontWeight:300, outline:"none", boxSizing:"border-box" }}/>
              <button onClick={()=>setShowPwd(v=>!v)} style={{ position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,0.3)", lineHeight:1, padding:0 }}>
                {showPwd
                  ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
              </button>
            </div>
            <div style={{ marginBottom:14, position:"relative" }}>
              <input type={showPwd?"text":"password"} placeholder={L.resetPwd.confirmPH} value={confirm} onChange={e=>setConfirm(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()}
                style={{ width:"100%", background:"rgba(255,255,255,0.04)", border:`1px solid ${confirm ? (match ? "rgba(76,175,110,0.5)" : "rgba(224,90,90,0.5)") : BDR}`, borderRadius:10, padding:"14px 46px 14px 16px", color:CR, fontSize:14, fontFamily:JF, fontWeight:300, outline:"none", boxSizing:"border-box" }}/>
              {confirm && (
                <span style={{ position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", fontSize:14, color: match ? "#4caf6e" : "#e05a5a" }}>
                  {match ? "✓" : "✗"}
                </span>
              )}
            </div>
            {error && <div style={{ marginBottom:12, fontSize:11, color:"#e05a5a", fontFamily:JF }}>{error}</div>}
            <button onClick={submit} disabled={loading}
              style={{ width:"100%", padding:"14px", borderRadius:10, border:"none", background:loading?"rgba(255,255,255,0.05)":CR, color:loading?"rgba(255,255,255,0.3)":BG, fontSize:10, fontFamily:JF, fontWeight:700, letterSpacing:"0.18em", textTransform:"uppercase", cursor:loading?"not-allowed":"pointer", transition:"all 0.2s" }}>
              {loading?"···":L.resetPwd.save}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const cShadow = "0 4px 28px rgba(0,0,0,0.6),0 1px 4px rgba(0,0,0,0.22),0 0 0 1px rgba(255,255,255,0.09),0 -2px 24px rgba(255,255,255,0.06),inset 0 1px 0 rgba(255,255,255,0.32)";

/* ─── MAIN APP ───────────────────────────────────────────────────── */
export default function App() {
  const isMobile = useIsMobile();
  const [user,        setUser]        = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const [trades,      setTrades]      = useState(() => load(KEYS.trades, []));
  const [extraInstr,  setExtraInstr]  = useState(() => load(KEYS.instruments, []));
  const [extraEmotions, setExtraEmotions] = useState(() => load('fyltra_emotions_v1', []));
  const [customEmotion, setCustomEmotion] = useState('');
  const [customEmotionPolarity, setCustomEmotionPolarity] = useState('positive');
  const [beSign, setBeSign] = useState(1);
  const [tradeMode, setTradeMode] = useState(() => localStorage.getItem("fyltra_trade_mode")||"swing");
  const [tradeFixedMode, setTradeFixedMode] = useState(() => localStorage.getItem("fyltra_trade_fixed_mode")||"variable");
  const [dayEndTime, setDayEndTime] = useState(() => localStorage.getItem("fyltra_day_end_time")||"");
  const defaultTS = { tpFixed:{enabled:false,value:""}, slFixed:{enabled:false,value:""}, rrFixed:{enabled:false,value:""}, sizeFixed:{enabled:false,value:"",unit:"contrats"} };
  const [tradeSettings, setTradeSettings] = useState(() => load("fyltra_trade_settings_v1", defaultTS));
  const [savedTS, setSavedTS] = useState(() => load("fyltra_trade_settings_v1", defaultTS));
  const [scalpFields, setScalpFields] = useState({entry:false, rr:false, emotion:false, notes:false, size:false});
  const setTS = (key, changes) => setTradeSettings(p => ({...p, [key]:{...p[key],...changes}}));
  const [tsSaved, setTsSaved] = useState(false);
  const saveTS = () => { save("fyltra_trade_settings_v1", tradeSettings); setSavedTS(tradeSettings); if (user) saveUserSettings({ trade_settings: tradeSettings }); setTsSaved(true); setTimeout(()=>setTsSaved(false),2000); };
  const toggleScalp = k => setScalpFields(p => ({...p, [k]:!p[k]}));
  const [showCustomEmotion, setShowCustomEmotion] = useState(false);
  const [strategies,  setStrategies]  = useState(() => {
    const saved = load(KEYS.strategies, null);
    if (saved && Array.isArray(saved)) return saved;
    // migrate old single strategy
    const old = load("vantage_strategy_v1", null) || load("fyltra_strat_v1", null);
    if (old) return [{ id:1, name:"Ma stratégie", description:old.description||"", steps:Array.isArray(old.steps)?old.steps:[], rules:old.rules||"", notes:old.notes||"" }];
    return [{ id:Date.now(), name:"Ma stratégie", description:"", steps:[], rules:"", notes:"" }];
  });
  const [activeStratId, setActiveStratId] = useState(null);
  const [editingStratName, setEditingStratName] = useState(false);
  const [toolTab, setToolTab] = useState("csv"); // csv | calc | eod
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [csvPlatform, setCsvPlatform] = useState("mt5");
  const [csvResult, setCsvResult] = useState(null);
  const [csvError, setCsvError] = useState("");
  const [calcEntry, setCalcEntry] = useState("");
  const [calcStop, setCalcStop] = useState("");
  const [calcRisk, setCalcRisk] = useState("");
  const [calcInstrument, setCalcInstrument] = useState("MNQ");
  const [userPlan, setUserPlan] = useState(() => localStorage.getItem("fyltra_plan") || null);
  const [subExpired, setSubExpired] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [userModalTab, setUserModalTab] = useState("profil");
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [activating, setActivating] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return !!(p.get("session_id") && p.get("activated"));
  });
  const [eodLoading, setEodLoading] = useState(false);
  const [eodText, setEodText] = useState("");
  const [eodAccount, setEodAccount] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [signOutLeaving, setSignOutLeaving] = useState(false);
  const [signOutAnim, setSignOutAnim] = useState("idle"); // "idle"|"profile-out"|"confirming"|"confirm-out"
  const [containerMaxH, setContainerMaxH] = useState("88vh");
  const modalContainerRef = useRef(null);
  const confirmSignOut = () => { setSignOutLeaving(true); setTimeout(() => { setShowSignOutConfirm(false); setSignOutLeaving(false); supabase.auth.signOut(); }, 280); };
  const cancelSignOut = () => { setSignOutLeaving(true); setTimeout(() => { setShowSignOutConfirm(false); setSignOutLeaving(false); }, 250); };
  const triggerInModalSignOut = () => {
    const h = modalContainerRef.current?.offsetHeight;
    if (h) {
      setContainerMaxH(h + "px");
      requestAnimationFrame(() => requestAnimationFrame(() => setContainerMaxH("420px")));
    } else {
      setContainerMaxH("420px");
    }
    setSignOutAnim("profile-out");
    setTimeout(() => setSignOutAnim("confirming"), 300);
  };
  const cancelInModalSignOut = () => {
    setContainerMaxH("88vh");
    setSignOutAnim("confirm-out");
    setTimeout(() => setSignOutAnim("idle"), 210);
  };
  const confirmInModalSignOut = () => {
    setSignOutAnim("confirm-out");
    setTimeout(() => { setShowUserModal(false); setSignOutAnim("idle"); setContainerMaxH("88vh"); supabase.auth.signOut(); }, 210);
  };
  const [currency, setCurrency] = useState(() => localStorage.getItem("fyltra_currency")||"€");
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("fyltra_dark")==="true");
  const [hideNumbers, setHideNumbers] = useState(() => localStorage.getItem("fyltra_hide_numbers") === "true");
  const [customBg,        setCustomBg]        = useState(() => localStorage.getItem("fyltra_cbg")  || "");
  const [customBg2,       setCustomBg2]       = useState(() => localStorage.getItem("fyltra_cbg2") || "");
  const [customTextWhite, setCustomTextWhite] = useState(() => { const v = localStorage.getItem("fyltra_ctxt"); return v === null ? null : v === "1"; });
  C = darkMode ? {...DARK_THEME} : {...LIGHT_THEME};
  const clrGreen = darkMode ? "#4ade80" : "#16a34a";
  const clrRed   = darkMode ? "#f87171" : "#dc2626";
  if (customBg)  { C.bg = customBg; }
  if (customBg2) { C.bg2 = customBg2; C.bg3 = customBg2; }
  // Keep iStyle in sync with current theme (it's module-level so must be updated manually)
  iStyle.background = C.bg3;
  iStyle.color      = C.white;
  iStyle.border     = `1px solid ${C.border}`;
  iStyle.colorScheme = darkMode ? "dark" : "light";
  if (customTextWhite === true)  { C.white="#f0ede8"; C.dim="#aaa"; C.gray1="#888"; C.gray2="#555"; C.gray3="#333"; C.accent="#f0ede8"; C.border="rgba(255,255,255,0.08)"; C.borderGold="rgba(255,255,255,0.12)"; }
  if (customTextWhite === false) { C.white="#1a1a1a"; C.dim="#555"; C.gray1="#888"; C.gray2="#bbb"; C.gray3="#ddd"; C.accent="#111"; C.border="rgba(0,0,0,0.1)"; C.borderGold="rgba(0,0,0,0.15)"; }
  document.documentElement.style.setProperty("--gold-rgb", darkMode?"232,205,169":"160,110,50");
  const [acctView, setAcctView] = useState("global");
  const [tabKey, setTabKey] = useState(0); // "today" | "global"
  const [lang, setLang] = useState(() => localStorage.getItem("fyltra_lang") || "en");
  L = APP_T[lang] || APP_T.fr;
  const canUseAI        = userPlan === "fyltra_plus" || userPlan === "fyltra_pro";
  const canUseMT5       = userPlan === "fyltra_pro";
  const canUseReplay    = userPlan === "fyltra_plus" || userPlan === "fyltra_pro";
  const canUseNoteImg   = userPlan === "fyltra_plus" || userPlan === "fyltra_pro";
  const pfLimit         = userPlan === "fyltra_plus" ? 10 : userPlan === "fyltra_pro" ? 999 : 3;
  const [menuClosing, setMenuClosing] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null); // {date, trades, pnl}
  const [dayClosing, setDayClosing] = useState(false);
  const closeMenu = () => { setMenuClosing(true); setTimeout(()=>{setShowMenu(false);setMenuClosing(false);},240); };
  const closeDay = () => { setDayClosing(true); setTimeout(()=>{setSelectedDay(null);setDayClosing(false);},220); };
  const [calcMode, setCalcMode] = useState("futures");
  const [calcCustomPair, setCalcCustomPair] = useState("");
  const [pfPctMode, setPfPctMode] = useState(false);
  const [pfPctValues, setPfPctValues] = useState({target:"",maxLoss:"",dailyLoss:""});
  const [capital,     setCapital]     = useState(() => load(KEYS.capital, ""));
  const [propfirms,   setPropfirms]   = useState(() => load(KEYS.propfirms, []));
  const [pfView,      setPfView]      = useState("list"); // list | add-type | add-propfirm | add-personal
  const [lastMt5SyncGlobal, setLastMt5SyncGlobal] = useState(null);
  const [mt5Countdown,      setMt5Countdown]      = useState("");
  const syncBlockedToday = userPlan === "fyltra_pro" && (() => {
    if (!lastMt5SyncGlobal) return false;
    const d1 = new Date(lastMt5SyncGlobal), d2 = new Date();
    return d1.getFullYear()===d2.getFullYear() && d1.getMonth()===d2.getMonth() && d1.getDate()===d2.getDate();
  })();
  const [mt5SyncingPf, setMt5SyncingPf]  = useState(null);
  const [mt5DeployingPf,setMt5DeployingPf]= useState(null);
  const [mt5SyncMsg,   setMt5SyncMsg]   = useState({});
  const [mt5SyncStep,  setMt5SyncStep]  = useState({});
  const [mt5SyncProgress, setMt5SyncProgress] = useState(0);
  const mt5SyncProgressRef = useRef(0);
  const mt5SyncProgressTimer = useRef(null);
  const [pendingMt5Trades, setPendingMt5Trades] = useState(null);
  const [mt5Enrichments,   setMt5Enrichments]   = useState({});
  const [mt5GlobalUnit,    setMt5GlobalUnit]     = useState(null);
  const [confirmingMt5,    setConfirmingMt5]     = useState(false);
  const [pfListTab,   setPfListTab]   = useState("active"); // active | archived
  const [pfForm,      setPfForm]      = useState({ type:"propfirm", name:"", firm:"", capital:"", target:"", dailyLoss:"", maxLoss:"", consistency:"", consistencyPct:"", hasDailyLoss:false, hasConsistency:false, hasInactivity:false, inactivityDays:"", inactivityFrom:"", trailingDD:false, hasMinDays:false, minDays:"", steps:"1", step2Capital:"", step2Target:"", step2MaxLoss:"", step2DailyLoss:"", step2HasDailyLoss:false, step2TrailingDD:false });
  const [pfFirmOpen,   setPfFirmOpen]   = useState(false);
  const [pfBrokerOpen, setPfBrokerOpen] = useState(false);
  const [activePf,    setActivePf]    = useState(null);
  const [chartAccountId, setChartAccountId] = useState("all");
  const [editingPf, setEditingPf] = useState(null);
  const [selectedPf, setSelectedPf] = useState(null);
  const [confirmDeletePf, setConfirmDeletePf] = useState(false);
  const [accountLeaving, setAccountLeaving] = useState(false);
  const closeAccount = () => { setAccountLeaving(true); setTimeout(()=>{ setSelectedPf(null); setConfirmDeletePf(false); setAccountLeaving(false); setAcctView("global"); }, 260); }; // null = list, pf = detail
  const [acctCustomizing, setAcctCustomizing] = useState(false);
  const [acctLayout, setAcctLayout] = useState(() => load('fyltra_layout_v1', ['progress','today','stats','calendar','trades']));
  const [acctDragOver, setAcctDragOver] = useState(null);
  const [view,        setView]        = useState("home");

  const [tvMode,      setTvMode]      = useState("live");
  const [tvSymbol,    setTvSymbol]    = useState("CAPITALCOM:NAS100");
  const [tvInterval]                  = useState("D");
  const [tvTimezone,  setTvTimezone]  = useState(()=>localStorage.getItem("tv_tz")||"Etc/UTC");
  useEffect(()=>{localStorage.setItem("tv_tz",tvTimezone);},[tvTimezone]);
  const [profileForm, setProfileForm] = useState({ firstName:"", lastName:"", nickname:"", address:"", phone:"" });
  const [profileSaving, setProfileSaving] = useState(false);
  const [subData,     setSubData]     = useState(null);
  const [subLoading,  setSubLoading]  = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelStep,    setCancelStep]    = useState(0);
  const [churnReason,   setChurnReason]   = useState("");
  const [pauseLoading,  setPauseLoading]  = useState(false);
  const [pauseDone,     setPauseDone]     = useState(false);
  const [deleteStep,    setDeleteStep]    = useState(null); // null | "check" | "confirm"
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showPlanChange, setShowPlanChange] = useState(false);
  const [planChanging, setPlanChanging] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [pwdForm, setPwdForm] = useState({ newPwd:"", confirmPwd:"", show:false });
  const [colorSaving,  setColorSaving]  = useState(null);
  const [dayEndSaving, setDayEndSaving] = useState(null);
  const [hmTooltip, setHmTooltip] = useState(null);
  const [hmYear, setHmYear] = useState(() => new Date().getFullYear());
  const [cmpPeriod, setCmpPeriod] = useState("week");
  const [weeklyGoals, setWeeklyGoals] = useState(() => { try { return JSON.parse(localStorage.getItem("fyltra_wgoals") || "null") || { pnl: "", wr: "", trades: "" }; } catch { return { pnl: "", wr: "", trades: "" }; } });
  const [wGoalsEdit, setWGoalsEdit] = useState(false);
  const [statsWidgets, setStatsWidgets] = useState(() => { try { return JSON.parse(localStorage.getItem("fyltra_widgets")||"null") || { comparison:true, goals:true, advanced:true, rr:true, emotions:true, streaks:true, montecarlo:true, sections:true, hourly:true, heatmap:true, bestday:true, recovery:true, stoptime:true, revenge:true, setup:true }; } catch { return { comparison:true, goals:true, advanced:true, rr:true, emotions:true, streaks:true, montecarlo:true, sections:true, hourly:true, heatmap:true, bestday:true, recovery:true, stoptime:true, revenge:true, setup:true }; } });
  const [widgetsOpen, setWidgetsOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardStep, setOnboardStep] = useState(0);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const showToast = (msg, dur = 2500) => { if (toastTimer.current) clearTimeout(toastTimer.current); setToast(msg); toastTimer.current = setTimeout(() => setToast(null), dur); };
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);
  const [compete,    setCompete]    = useState(false);
  const [anonymous,  setAnonymous]  = useState(false);
  const [lbData,       setLbData]       = useState(null);
  const [lbLoading,    setLbLoading]    = useState(false);
  const [lbUserRank,   setLbUserRank]   = useState(null);
  const [lbPeriod,     setLbPeriod]     = useState("30d");
  const [lbCriteria,   setLbCriteria]   = useState("score");
  const [tradesLoaded, setTradesLoaded] = useState(false);
  const [pwdMsg, setPwdMsg] = useState("");
  const [emailNew, setEmailNew] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailMsg, setEmailMsg] = useState("");
  const [aiText,      setAiText]      = useState("");
  const [aiLoading,   setAiLoading]   = useState(false);
  const [aiError,     setAiError]     = useState("");
  const [coachStyle,  setCoachStyle]  = useState(() => localStorage.getItem("fyltra_coach_style") || "friendly");
  const [coachMemory, setCoachMemory] = useState("");
  const [aiAccFilter, setAiAccFilter] = useState("all");
  const [aiSummary,   setAiSummary]   = useState(null);
  const [fyltaPeriod, setFyltaPeriod] = useState("month");
  const [saved,       setSaved]       = useState(false);
  const [stratSaved,  setStratSaved]  = useState(false);
  const [pnlRaw,      setPnlRaw]      = useState("");
  const [customInstr, setCustomInstr] = useState("");
  const [showCustom,  setShowCustom]  = useState(false);
  const [editingTrade,setEditingTrade]= useState(null);
  const [selectedTradeDetail, setSelectedTradeDetail] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [closingDetail, setClosingDetail] = useState(false);
  const [closingEdit, setClosingEdit] = useState(false);
  const [editPnlRaw,  setEditPnlRaw]  = useState("");
  const [confirmDeleteTradeId, setConfirmDeleteTradeId] = useState(null);
  const [deletingTradeId, setDeletingTradeId] = useState(null);
  const [tradesAccFilter,  setTradesAccFilter]  = useState("all");
  const [tradeSearch,     setTradeSearch]     = useState("");
  const [tradeFilters,    setTradeFilters]    = useState({ result:"all", direction:"all", session:"all", dateFrom:"", dateTo:"", tag:"all" });
  const [tradeFiltersOpen, setTradeFiltersOpen] = useState(false);
  const [historyAccFilter, setHistoryAccFilter] = useState("all");
  const [histInfoCard,     setHistInfoCard]     = useState(null);
  const [sessionNotes,    setSessionNotes]    = useState([]);
  const [notesLoaded,     setNotesLoaded]     = useState(false);
  const [selNoteDate,     setSelNoteDate]     = useState(() => new Date().toISOString().split("T")[0]);
  const [selNoteId,       setSelNoteId]       = useState(null);
  const [noteContent,     setNoteContent]     = useState("");
  const [noteSaving,      setNoteSaving]      = useState(false);
  const [noteSaved,       setNoteSaved]       = useState(false);
  const noteTimerRef = useRef(null);
  const [voiceOpen,       setVoiceOpen]       = useState(false);
  const [voiceTranscript, setVoiceTranscript]  = useState("");
  const [voiceListening,  setVoiceListening]   = useState(false);
  const [voiceParsing,    setVoiceParsing]     = useState(false);
  const recognitionRef = useRef(null);
  const [dailyPhrase,        setDailyPhrase]        = useState("");
  const [dailyPhraseLoading, setDailyPhraseLoading] = useState(false);
  const loadingTimerRef = useRef(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingFading,   setLoadingFading]   = useState(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState(true);
  // Calendar navigation — single combined state avoids stale-closure bugs
  const now0 = new Date();
  const [calNav,   setCalNav]   = useState({ month: now0.getMonth(), year: now0.getFullYear() });
  const [pfCalNav, setPfCalNav] = useState({ month: now0.getMonth(), year: now0.getFullYear() });
  const calMonth  = calNav.month;  const calYear   = calNav.year;
  const pfCalMonth = pfCalNav.month; const pfCalYear = pfCalNav.year;
  const [form, setForm] = useState({ date:new Date().toISOString().split("T")[0], instrument:"MNQ", direction:"LONG", result:"WIN", session:"New York", emotion:"Neutre", entry:"", exit:"", entryTime:"", exitTime:"", rr:"", size:"", sizeUnit:"contrats", notes:"", tags:[], accountIds:[], strategyId:null, screenshotUrl:"" });
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [screenshotUploading, setScreenshotUploading] = useState(false);
  const [noteImageUrl, setNoteImageUrl] = useState(null);
  const [noteImageUploading, setNoteImageUploading] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [tagPolarity, setTagPolarity] = useState("neutral");
  const [editTagInput, setEditTagInput] = useState("");
  const [editBadgePolarity, setEditBadgePolarity] = useState("neutral");

  const instruments = [...BASE_INSTRUMENTS, ...extraInstr, "Autre"];
  const availableYears = Array.from({ length:now0.getFullYear() - 2019 }, (_, i) => now0.getFullYear() - i);

  // ── Supabase helpers ──
  const dbToTrade = r => ({
    id:r.id, date:r.date, instrument:r.instrument, direction:r.direction,
    result:r.result, pnl:r.pnl, session:r.session, emotion:r.emotion,
    notes:r.notes, tags:r.tags||[], rr:r.rr, tradeMode:r.trade_mode,
    size:r.size, entry:r.entry, exit:r.exit, sizeUnit:r.size_unit,
    entryTime:r.entry_time||null, exitTime:r.exit_time||null,
    accountIds:(r.account_ids||[]).map(Number), strategyId:r.strategy_id ? Number(r.strategy_id) : null,
    createdAt:r.created_at||null, screenshotUrl:r.screenshot_url||null,
  });
  const tradeToDb = t => ({
    user_id:user.id, date:t.date, instrument:t.instrument, direction:t.direction,
    result:t.result, pnl:t.pnl, session:t.session, emotion:t.emotion,
    notes:t.notes||"", tags:t.tags||[], rr:t.rr?parseFloat(t.rr):null,
    trade_mode:t.tradeMode||tradeMode, size:t.size?parseFloat(t.size):null,
    entry:t.entry||null, exit:t.exit||null, size_unit:t.sizeUnit||null,
    entry_time:t.entryTime||null, exit_time:t.exitTime||null,
    account_ids:t.accountIds||[], strategy_id:t.strategyId||null,
    screenshot_url:t.screenshotUrl||null,
  });


  // ── Auth ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const awaitingAuth = params.has('code') || window.location.hash.includes('access_token') || window.location.hash.includes('type=recovery');

    let timeoutId;
    if (!awaitingAuth) {
      supabase.auth.getSession().then(({ data:{ session } }) => {
        setUser(session?.user ?? null);
        setAuthLoading(false);
      });
    } else {
      timeoutId = setTimeout(() => setAuthLoading(false), 5000);
    }

    const { data:{ subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (event === "PASSWORD_RECOVERY") {
        setPasswordRecovery(true);
        setAuthLoading(false);
        clearTimeout(timeoutId);
      } else if (!awaitingAuth || event !== "INITIAL_SESSION") {
        setAuthLoading(false);
        clearTimeout(timeoutId);
      }
    });
    return () => { subscription.unsubscribe(); clearTimeout(timeoutId); };
  }, []);

  useEffect(() => {
    if (!user || !userPlan) { setShowOnboarding(false); return; }
    const key = `fyltra_onboarded_${user.id}`;
    if (!localStorage.getItem(key)) {
      setShowOnboarding(true);
      setOnboardStep(0);
    }
  }, [user?.id, userPlan]);

  // ── Activation après paiement (retour depuis Stripe avec ?activated=1) ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const activated = params.get("activated");
    if (!sessionId || !activated) return;
    window.history.replaceState({}, "", window.location.pathname);
    fetch(`/api/check-purchase?session_id=${encodeURIComponent(sessionId)}`)
      .then(r => r.json())
      .then(d => {
        if (d.plan) {
          setUserPlan(d.plan);
          localStorage.setItem("fyltra_plan", d.plan);
        }
        if (d.activated) {
          setSubExpired(false);
          skipNextCheck.current = true;
        }
      })
      .catch(() => {})
      .finally(() => setActivating(false));
  }, []); // eslint-disable-line

  // ── Load plan when user logs in / check subscription validity / clear on logout ──
  useEffect(() => {
    if (!user) { setUserPlan(null); localStorage.removeItem("fyltra_plan"); return; }
    if (activating) return;
    if (skipNextCheck.current) { skipNextCheck.current = false; return; }
    fetch(`/api/check-purchase?email=${encodeURIComponent(user.email)}`)
      .then(r => r.json())
      .then(d => {
        if (!d.authorized) {
          setUserPlan(null);
          localStorage.removeItem("fyltra_plan");
          setSubExpired(true);
          return;
        }
        setSubExpired(false);
        const p = d.plan || null;
        setUserPlan(p);
        if (p) localStorage.setItem("fyltra_plan", p);
        else localStorage.removeItem("fyltra_plan");
      })
      .catch(() => {});
  }, [user, activating]);

  const skipNextCheck = useRef(false);

  // Flag to block save effects while initial DB load is in progress
  const isLoadingDB = useRef(false);

  // ── Load trades + settings from DB when user logs in ──
  useEffect(() => {
    if (!user) return;
    isLoadingDB.current = true;
    Promise.all([
      supabase.from("trades").select("*").eq("user_id", user.id).order("created_at", { ascending:false }),
      supabase.from("user_settings").select("*").eq("user_id", user.id).single()
    ]).then(([tradesRes, settingsRes]) => {
      setTrades(tradesRes.data ? tradesRes.data.map(dbToTrade) : []);
      const d = settingsRes.data;
      setPropfirms(d?.propfirms ?? []);
      setStrategies(d?.strategies ?? []);
      setCapital(d?.capital ?? "");
      setExtraInstr(d?.extra_instruments ?? []);
      setExtraEmotions(d?.extra_emotions ?? []);
      if (d?.dark_mode !== undefined && d?.dark_mode !== null) setDarkMode(d.dark_mode);
      if (d?.custom_bg !== undefined && d?.custom_bg !== null) setCustomBg(d.custom_bg);
      if (d?.custom_bg2 !== undefined && d?.custom_bg2 !== null) setCustomBg2(d.custom_bg2);
      if (d?.custom_text_white !== undefined && d?.custom_text_white !== null) setCustomTextWhite(d.custom_text_white);
      if (d?.currency) setCurrency(d.currency);
      if (d?.lang === "fr" || d?.lang === "en") setLang(d.lang);
      if (d?.acct_layout) setAcctLayout(d.acct_layout);
      if (d?.trade_settings) { setSavedTS(d.trade_settings); setTradeSettings(d.trade_settings); }
      if (d?.coach_memory) setCoachMemory(d.coach_memory);
      if (d?.trade_mode) setTradeMode(d.trade_mode);
      if (d?.trade_fixed_mode) setTradeFixedMode(d.trade_fixed_mode);
      if (d?.day_end_time !== undefined && d?.day_end_time !== null) setDayEndTime(d.day_end_time);
      if (d?.compete !== undefined && d?.compete !== null) setCompete(d.compete);
      if (d?.anonymous !== undefined && d?.anonymous !== null) setAnonymous(d.anonymous);
      if (d?.first_name || d?.last_name || d?.nickname || d?.address || d?.phone)
        setProfileForm({ firstName:d.first_name||"", lastName:d.last_name||"", nickname:d.nickname||"", address:d.address||"", phone:(d.phone&&!d.phone.includes("@"))?d.phone:"" });
      if (d?.avatar_url) setAvatarUrl(d.avatar_url);
      if (d?.weekly_goals) { setWeeklyGoals(d.weekly_goals); localStorage.setItem("fyltra_wgoals", JSON.stringify(d.weekly_goals)); }
      else { const local = (() => { try { return JSON.parse(localStorage.getItem("fyltra_wgoals")||"null"); } catch { return null; } })(); if (local && (local.pnl||local.wr||local.trades)) saveUserSettings({ weekly_goals: local }); }
      // Last MT5 sync — Supabase first, localStorage fallback
      const lsSync = (() => { try { return localStorage.getItem(`fyltra_mt5_sync_${user.id}`); } catch { return null; } })();
      if (d?.last_mt5_sync) setLastMt5SyncGlobal(d.last_mt5_sync);
      else if (lsSync) setLastMt5SyncGlobal(lsSync);
      // Release flag after React has processed all the state updates and run their effects
      setTimeout(() => { isLoadingDB.current = false; setTradesLoaded(true); }, 0);
    });
  }, [user]);

  const saveUserSettings = async (patch) => {
    if (!user) return { error: { message: "Non connecté" } };
    const { error } = await supabase.from("user_settings").upsert(
      { user_id: user.id, ...patch, updated_at: new Date().toISOString() },
      { onConflict: "user_id" }
    );
    if (error) console.error("saveUserSettings error:", error.message, patch);
    return { error };
  };


  useEffect(() => { if (!user) save(KEYS.trades, trades); }, [trades]);

  // ── MT5 daily sync countdown
  useEffect(() => {
    const isTodayLocal = (ts) => {
      if (!ts) return false;
      const a = new Date(ts), b = new Date();
      return a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
    };
    if (!isTodayLocal(lastMt5SyncGlobal) || userPlan !== "fyltra_pro") { setMt5Countdown(""); return; }
    const tick = () => {
      const now = new Date();
      const midnight = new Date(now); midnight.setHours(24,0,0,0);
      const secs = Math.max(0, Math.floor((midnight - now) / 1000));
      const h = Math.floor(secs/3600), m = Math.floor((secs%3600)/60), s = secs%60;
      setMt5Countdown(`${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lastMt5SyncGlobal, userPlan]); // eslint-disable-line

  const exportPDF = (fromStr, toStr) => {
    const now = new Date();
    const start = new Date(fromStr + "T00:00:00");
    const end   = new Date(toStr   + "T23:59:59");
    const fmt   = { day:"numeric", month:"short", year:"numeric" };
    const periodLabel = `${start.toLocaleDateString(lang==="fr"?"fr-FR":"en-US",fmt)} → ${end.toLocaleDateString(lang==="fr"?"fr-FR":"en-US",fmt)}`;

    const src = historyAccFilter !== "all"
      ? trades.filter(t => !t.accountIds || t.accountIds.length === 0 || t.accountIds.includes(historyAccFilter))
      : trades;
    const periodTrades = src.filter(t => { const d = new Date(t.date + "T12:00:00"); return d >= start && d <= end; });

    const pnl    = periodTrades.reduce((s, t) => s + (t.pnl || 0), 0);
    const wins   = periodTrades.filter(t => t.result === "WIN").length;
    const losses = periodTrades.filter(t => t.result === "LOSS").length;
    const wr     = periodTrades.length ? Math.round(wins / periodTrades.length * 100) : 0;
    const avgWin = wins ? periodTrades.filter(t => t.result === "WIN").reduce((s,t) => s+(t.pnl||0), 0) / wins : 0;
    const avgLoss= losses ? Math.abs(periodTrades.filter(t => t.result === "LOSS").reduce((s,t) => s+(t.pnl||0), 0) / losses) : 0;

    const bySession = {};
    periodTrades.forEach(t => {
      if (!bySession[t.session]) bySession[t.session] = { pnl:0, count:0, wins:0 };
      bySession[t.session].pnl += t.pnl || 0;
      bySession[t.session].count++;
      if (t.result === "WIN") bySession[t.session].wins++;
    });

    const byInst = {};
    periodTrades.forEach(t => {
      if (!byInst[t.instrument]) byInst[t.instrument] = { pnl:0, count:0 };
      byInst[t.instrument].pnl += t.pnl || 0;
      byInst[t.instrument].count++;
    });
    const topInst = Object.entries(byInst).sort((a,b) => b[1].pnl - a[1].pnl).slice(0, 5);

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = 210, M = 18;
    let y = 20;

    const accent = [201, 170, 130];
    const white  = [240, 238, 234];
    const gray   = [120, 115, 108];
    const bg     = [18, 18, 18];
    const card   = [26, 26, 26];

    doc.setFillColor(...bg);
    doc.rect(0, 0, W, 297, "F");

    doc.setFillColor(...accent);
    doc.rect(M, y, 40, 0.5, "F");
    doc.setFontSize(8); doc.setTextColor(...accent);
    doc.setFont("helvetica", "bold");
    doc.text("FYLTRA", M, y + 5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7); doc.setTextColor(...gray);
    doc.text("Trading Journal", M + 18, y + 5);
    doc.setFontSize(18); doc.setTextColor(...white);
    doc.setFont("helvetica", "bold");
    doc.text(lang === "fr" ? "Rapport de trading" : "Trading Report", M, y + 14);
    doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(...gray);
    doc.text(periodLabel, M, y + 20);
    y += 30;

    const drawCard = (x, cardY, w, h) => { doc.setFillColor(...card); doc.roundedRect(x, cardY, w, h, 3, 3, "F"); };
    const kpiW = (W - M * 2 - 9) / 4;
    const kpis = [
      { label: "PnL NET", value: `${fms(pnl)}${currency}`, color: pnl >= 0 ? [74, 222, 128] : [248, 113, 113] },
      { label: "WIN RATE", value: `${wr}%`, color: wr >= 50 ? [74, 222, 128] : [248, 113, 113] },
      { label: lang === "fr" ? "TRADES" : "TRADES", value: String(periodTrades.length), color: white },
      { label: lang === "fr" ? "GAIN MOY" : "AVG WIN", value: `${fms(avgWin)}${currency}`, color: [74, 222, 128] },
    ];
    kpis.forEach((k, i) => {
      const kx = M + i * (kpiW + 3);
      drawCard(kx, y, kpiW, 22);
      doc.setFontSize(6); doc.setFont("helvetica", "bold"); doc.setTextColor(...gray);
      doc.text(k.label, kx + 4, y + 7);
      doc.setFontSize(13); doc.setFont("helvetica", "bold"); doc.setTextColor(...k.color);
      doc.text(k.value, kx + 4, y + 17);
    });
    y += 28;

    if (topInst.length > 0) {
      doc.setFontSize(7); doc.setFont("helvetica", "bold"); doc.setTextColor(...accent);
      doc.text(lang === "fr" ? "INSTRUMENTS" : "INSTRUMENTS", M, y);
      y += 5;
      drawCard(M, y, W - M * 2, topInst.length * 9 + 6);
      topInst.forEach(([name, d], i) => {
        const iy = y + 5 + i * 9;
        const isPos = d.pnl >= 0;
        doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(...white);
        doc.text(name, M + 4, iy);
        doc.setFontSize(7); doc.setFont("helvetica", "normal"); doc.setTextColor(...gray);
        doc.text(`${d.count} trade${d.count > 1 ? "s" : ""}`, M + 35, iy);
        doc.setTextColor(...(isPos ? [74,222,128] : [248,113,113]));
        doc.setFont("helvetica", "bold");
        doc.text(`${fms(d.pnl)}${currency}`, W - M - 4, iy, { align: "right" });
      });
      y += topInst.length * 9 + 12;
    }

    if (Object.keys(bySession).length > 0) {
      doc.setFontSize(7); doc.setFont("helvetica", "bold"); doc.setTextColor(...accent);
      doc.text("SESSIONS", M, y);
      y += 5;
      const sessions = Object.entries(bySession).sort((a,b) => b[1].pnl - a[1].pnl);
      drawCard(M, y, W - M * 2, sessions.length * 9 + 6);
      sessions.forEach(([name, d], i) => {
        const sy = y + 5 + i * 9;
        const swr = Math.round(d.wins / d.count * 100);
        doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(...white);
        doc.text(name, M + 4, sy);
        doc.setFontSize(7); doc.setFont("helvetica", "normal"); doc.setTextColor(...gray);
        doc.text(`${d.count} trades · ${swr}% WR`, M + 35, sy);
        doc.setTextColor(...(d.pnl >= 0 ? [74,222,128] : [248,113,113]));
        doc.setFont("helvetica", "bold");
        doc.text(`${fms(d.pnl)}${currency}`, W - M - 4, sy, { align: "right" });
      });
      y += sessions.length * 9 + 12;
    }

    if (periodTrades.length > 0) {
      doc.setFontSize(7); doc.setFont("helvetica", "bold"); doc.setTextColor(...accent);
      doc.text(lang === "fr" ? "DÉTAIL DES TRADES" : "TRADE DETAILS", M, y);
      y += 5;
      const sorted = [...periodTrades].sort((a,b) => b.date.localeCompare(a.date));
      sorted.forEach((t, i) => {
        if (y > 270) { doc.addPage(); doc.setFillColor(...bg); doc.rect(0,0,W,297,"F"); y = 20; }
        const isW = t.result === "WIN"; const isL = t.result === "LOSS";
        drawCard(M, y, W - M * 2, 10);
        doc.setFontSize(7); doc.setFont("helvetica", "bold"); doc.setTextColor(...white);
        doc.text(`${t.date}  ${t.instrument}`, M + 4, y + 6.5);
        doc.setFont("helvetica", "normal"); doc.setTextColor(...gray);
        doc.text(`${t.direction} · ${t.session || ""}`, M + 55, y + 6.5);
        doc.setTextColor(...(isW ? [74,222,128] : isL ? [248,113,113] : gray));
        doc.setFont("helvetica", "bold");
        doc.text(`${fms(t.pnl||0)}${currency}`, W - M - 4, y + 6.5, { align: "right" });
        y += 12;
      });
    }

    doc.setFontSize(6); doc.setTextColor(...gray);
    doc.text(`Généré par Fyltra · ${new Date().toLocaleDateString()}`, W / 2, 291, { align: "center" });

    doc.save(`fyltra-${fromStr}-${toStr}.pdf`);
  };

  // ── Retroactive auto-archive check on every trade change ──
  useEffect(() => {
    if (!trades.length || !propfirms.length) return;
    setPropfirms(prev => prev.map(pf => {
      if (pf.type !== "propfirm" || (pf.status && pf.status !== "active")) return pf;
      const pfTrades = trades.filter(t => !t.accountIds || t.accountIds.length === 0 || t.accountIds.includes(pf.id));
      const pfPnl = pfTrades.reduce((s,t) => s+(t.pnl||0), 0);
      const cap = parseFloat(pf.capital) || 0;
      const target = parseFloat(pf.target) || 0;
      const maxLoss = parseFloat(pf.maxLoss) || 0;
      const dailyLoss = parseFloat(pf.dailyLoss) || 0;
      if (target > 0 && pfPnl >= target) return { ...pf, status:"passed" };
      if (maxLoss > 0) {
        let mll;
        if (pf.trailingDD) {
          const dates = [...new Set(pfTrades.map(t=>t.date))].sort();
          let cum=0, peak=0;
          dates.forEach(d=>{cum+=pfTrades.filter(t=>t.date===d).reduce((s,t)=>s+(t.pnl||0),0);if(cum>peak)peak=cum;});
          mll = cap + Math.max(0,peak) - maxLoss;
        } else {
          mll = cap - maxLoss;
        }
        if (cap + pfPnl <= mll) return { ...pf, status:"breached" };
      }
      if (pf.hasDailyLoss && dailyLoss > 0) {
        const byDay = {};
        pfTrades.forEach(t => { byDay[t.date] = (byDay[t.date]||0) + (t.pnl||0); });
        const worstDay = Math.min(0, ...Object.values(byDay));
        if (Math.abs(worstDay) >= dailyLoss) return { ...pf, status:"breached" };
      }
      return pf;
    }));
    // Activate step 2 when its linked step 1 passes
    setPropfirms(prev => {
      const passedStep1Ids = new Set(prev.filter(p => p.linkedStep2Id && p.status === "passed").map(p => p.linkedStep2Id));
      if (!passedStep1Ids.size) return prev;
      return prev.map(pf => passedStep1Ids.has(pf.id) && pf.status === "pending" ? { ...pf, status: undefined } : pf);
    });
  }, [trades]); // eslint-disable-line
  useEffect(() => { if (isLoadingDB.current) return; localStorage.setItem("fyltra_wgoals", JSON.stringify(weeklyGoals)); if (user && (weeklyGoals.pnl||weeklyGoals.wr||weeklyGoals.trades)) saveUserSettings({ weekly_goals: weeklyGoals }); }, [weeklyGoals]); // eslint-disable-line
  useEffect(() => { if (isLoadingDB.current) return; save(KEYS.instruments, extraInstr); if (user) saveUserSettings({ extra_instruments: extraInstr }); }, [extraInstr]);
  useEffect(() => { if (isLoadingDB.current) return; save('fyltra_emotions_v1', extraEmotions); if (user) saveUserSettings({ extra_emotions: extraEmotions }); }, [extraEmotions]);
  useEffect(() => { if (isLoadingDB.current) return; save(KEYS.strategies, strategies); if (user) saveUserSettings({ strategies }); }, [strategies]);
  useEffect(() => { if (isLoadingDB.current) return; save(KEYS.capital, capital); if (user) saveUserSettings({ capital }); }, [capital]);
  useEffect(() => { if (isLoadingDB.current) return; save(KEYS.propfirms, propfirms); if (user) saveUserSettings({ propfirms }); }, [propfirms]);
  useEffect(() => { if (isLoadingDB.current) return; localStorage.setItem("fyltra_currency", currency); if (user) saveUserSettings({ currency }); }, [currency]);
  const _mask = hideNumbers ? Object.assign(
    <span style={{display:"inline-block",position:"relative",top:"0.22em",letterSpacing:"0.08em",lineHeight:1}}>****</span>,
    { toString: () => "****" }
  ) : null;
  const fm  = (v) => hideNumbers ? _mask : fmtMoney(v);
  const fms = (v) => hideNumbers ? _mask : (v >= 0 ? "+" : "") + fmtMoney(v);
  useEffect(() => {
    localStorage.setItem("fyltra_dark", darkMode);
    const bg = customBg || (darkMode?"#0f0f0f":"#f8f7f5");
    const txt = customTextWhite !== null ? (customTextWhite?"#f0ede8":"#1a1a1a") : (darkMode?"#f0ede8":"#1a1a1a");
    document.documentElement.style.setProperty("--bg", bg);
    document.documentElement.style.setProperty("--gold-rgb", darkMode?"232,205,169":"160,110,50");
    document.body.style.background = bg;
    document.body.style.color = txt;
    if (user && !isLoadingDB.current) saveUserSettings({ dark_mode: darkMode });
  }, [darkMode, customBg, customTextWhite]);
  useEffect(() => {
    localStorage.setItem("fyltra_cbg",  customBg);
    localStorage.setItem("fyltra_cbg2", customBg2);
    if (customTextWhite === null) localStorage.removeItem("fyltra_ctxt");
    else localStorage.setItem("fyltra_ctxt", customTextWhite ? "1" : "0");
  }, [customBg, customBg2, customTextWhite]);
  useEffect(() => { if (isLoadingDB.current) return; localStorage.setItem("fyltra_lang", lang); if (user) saveUserSettings({ lang }); }, [lang]);
  useEffect(() => { if (isLoadingDB.current) return; save('fyltra_layout_v1', acctLayout); if (user) saveUserSettings({ acct_layout: acctLayout }); }, [acctLayout]);
  useEffect(() => { if (isLoadingDB.current) return; localStorage.setItem("fyltra_trade_mode", tradeMode); if (user) saveUserSettings({ trade_mode: tradeMode }); }, [tradeMode]);
  useEffect(() => { if (isLoadingDB.current) return; localStorage.setItem("fyltra_trade_fixed_mode", tradeFixedMode); if (user) saveUserSettings({ trade_fixed_mode: tradeFixedMode }); }, [tradeFixedMode]);
  useEffect(() => { localStorage.setItem("fyltra_day_end_time", dayEndTime); }, [dayEndTime]);

  const [testData,    setTestData]    = useState({ fng: null, rates: null, calendar: null, news: null, vix: null });
  const [testLoading, setTestLoading] = useState(false);
  const [testLoaded,  setTestLoaded]  = useState(false);
  const [calViewDate, setCalViewDate] = useState(() => { const n = new Date(); return { y: n.getFullYear(), m: n.getMonth() }; });
  const [calSelDay,   setCalSelDay]   = useState(null);
  const [calMarketDay, setCalMarketDay] = useState(() => new Date().toISOString().slice(0,10));

  const loadCalendarMonth = (y, m) => {
    const from = new Date(y, m, 1).toISOString().slice(0, 10);
    const to   = new Date(y, m + 1, 0).toISOString().slice(0, 10);
    fetch(`/api/market-data?type=calendar&from=${from}&to=${to}`).then(r=>r.json()).catch(()=>null)
      .then(calendar => setTestData(p => ({ ...p, calendar })));
  };

  useEffect(() => {
    if (!testLoaded) return;
    fetch(`/api/market-data?type=news&lang=${lang}`).then(r=>r.json()).catch(()=>null)
      .then(news => setTestData(p => ({ ...p, news })));
  }, [lang]); // eslint-disable-line

  useEffect(() => {
    if (view !== "test" || testLoaded) return;
    setTestLoading(true);
    const n = new Date();
    const from = new Date(n.getFullYear(), n.getMonth(), 1).toISOString().slice(0, 10);
    const to   = new Date(n.getFullYear(), n.getMonth() + 1, 0).toISOString().slice(0, 10);
    Promise.all([
      fetch("/api/market-data?type=fng").then(r=>r.json()).catch(()=>null),
      fetch("/api/market-data?type=rates").then(r=>r.json()).catch(()=>null),
      fetch(`/api/market-data?type=calendar&from=${from}&to=${to}`).then(r=>r.json()).catch(()=>null),
      fetch(`/api/market-data?type=news&lang=${lang}`).then(r=>r.json()).catch(()=>null),
      fetch("/api/market-data?symbol=%5EVIX&interval=1d&limit=5").then(r=>r.json()).catch(()=>null),
    ]).then(([fng, rates, calendar, news, vix]) => {
      setTestData({ fng, rates, calendar, news, vix });
      setTestLoaded(true);
      setTestLoading(false);
    });
  }, [view]); // eslint-disable-line

  useEffect(() => {
    if (mt5SyncingPf) {
      mt5SyncProgressRef.current = 0;
      setMt5SyncProgress(0);
      clearInterval(mt5SyncProgressTimer.current);
      mt5SyncProgressTimer.current = setInterval(() => {
        const p = mt5SyncProgressRef.current;
        const inc = p < 40 ? 1.8 : p < 65 ? 0.8 : p < 82 ? 0.3 : p < 90 ? 0.08 : 0;
        mt5SyncProgressRef.current = Math.min(p + inc, 90);
        setMt5SyncProgress(mt5SyncProgressRef.current);
      }, 250);
    } else {
      clearInterval(mt5SyncProgressTimer.current);
      setMt5SyncProgress(0);
      mt5SyncProgressRef.current = 0;
    }
    return () => clearInterval(mt5SyncProgressTimer.current);
  }, [mt5SyncingPf]); // eslint-disable-line

  useEffect(() => {
    // Pure time-based: 0→100% in 3 seconds, then brief pause, then fade out
    const INTERVAL = 30;
    const INCREMENT = 100 / (2000 / INTERVAL);
    loadingTimerRef.current = setInterval(() => {
      setLoadingProgress(p => {
        const next = p + INCREMENT;
        if (next >= 100) {
          clearInterval(loadingTimerRef.current);
          setTimeout(() => {
            setLoadingFading(true);
            setTimeout(() => setShowLoadingScreen(false), 600);
          }, 400);
          return 100;
        }
        return next;
      });
    }, INTERVAL);
    return () => clearInterval(loadingTimerRef.current);
  }, []); // eslint-disable-line

  const generateDailyPhrase = (forceRefresh = false) => {
    if (trades.length === 0) return;
    const today = new Date().toISOString().split("T")[0];
    const cacheKey = `fyltra_phrase_${user?.id}_${today}_${lang}`;
    if (!forceRefresh) {
      const cached = localStorage.getItem(cacheKey);
      if (cached) { setDailyPhrase(cached); return; }
    } else {
      try { localStorage.removeItem(cacheKey); } catch {}
    }

    const sorted = [...trades].sort((a,b) => b.date.localeCompare(a.date)||((b.createdAt||"").localeCompare(a.createdAt||"")));
    let streak = 0;
    if (sorted.length > 0) {
      const dir = sorted[0].result;
      for (const t of sorted) {
        if (t.result === dir) streak += (dir === "WIN" ? 1 : -1);
        else break;
      }
    }
    const wr = Math.round(trades.filter(t=>t.result==="WIN").length / trades.length * 100);
    const now = new Date();
    const dow = (now.getDay()+6)%7;
    const weekStart = new Date(now); weekStart.setDate(now.getDate()-dow); weekStart.setHours(0,0,0,0);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const weekPnl = trades.filter(t=>new Date(t.date+"T12:00:00")>=weekStart).reduce((s,t)=>s+(t.pnl||0),0);
    const monthPnl = trades.filter(t=>new Date(t.date+"T12:00:00")>=monthStart).reduce((s,t)=>s+(t.pnl||0),0);
    const recentResults = sorted.slice(0,5).map(t=>t.result==="WIN"?"W":"L");
    const lastEmotion = sorted.find(t=>t.emotion)?.emotion || null;
    const activePf = propfirms.find(p=>!p.status||p.status==="active");
    let drawdownPct = null;
    if (activePf) {
      const pfTrades = trades.filter(t=>!t.accountIds||t.accountIds.length===0||t.accountIds.includes(activePf.id));
      const allPnl = pfTrades.reduce((s,t)=>s+(t.pnl||0),0);
      const ml = parseFloat(activePf.maxLoss)||0;
      if (ml > 0) drawdownPct = Math.round(Math.abs(Math.min(0,allPnl))/ml*100);
    }

    setDailyPhraseLoading(true);
    fetch("/api/parse-trade", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type:"daily-phrase", wr, streak, weekPnl, monthPnl, lastEmotion, totalTrades:trades.length, recentResults, drawdownPct, currency, lang, goals:weeklyGoals }),
    })
      .then(r => r.json())
      .then(d => { if (d.phrase) { setDailyPhrase(d.phrase); try { localStorage.setItem(cacheKey, d.phrase); } catch {} } })
      .catch(() => {})
      .finally(() => setDailyPhraseLoading(false));
  };

  useEffect(() => {
    if (view !== "home" || !tradesLoaded) return;
    generateDailyPhrase(false);
  }, [view, tradesLoaded]); // eslint-disable-line

  const set = (k, v) => setForm(f => ({ ...f, [k]:v }));

  // "Today" in trading terms: if current time >= cutoff, trading day = tomorrow
  const tradingToday = (() => {
    if (!dayEndTime) return new Date().toISOString().split("T")[0];
    const now = new Date().toTimeString().slice(0, 5);
    const d = new Date();
    if (now >= dayEndTime) d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  })();

  // Scroll to top on view change
  useEffect(() => {
    window.scrollTo(0,0);
    if (view !== "settings") setTradeSettings(savedTS);
    if (view === "classement") { setLbData(null); setLbUserRank(null); }
    if (view === "profil")   { setView("home"); setShowUserModal(true); setUserModalTab("profil"); }
    if (view === "settings") { setView("home"); setShowUserModal(true); setUserModalTab("settings"); }
  }, [view, selectedPf]);
  // Pre-fill fixed values when switching to add view
  useEffect(() => {
    if (view === "add") {
      const nowDate = new Date();
      if (dayEndTime && new Date().toTimeString().slice(0, 5) >= dayEndTime) nowDate.setDate(nowDate.getDate() + 1);
      const effectiveDate = nowDate.toISOString().split("T")[0];
      setPnlRaw("");
      setForm(f => ({ ...f, date: effectiveDate, entry:"", exit:"", entryTime:"", exitTime:"", rr:"", size:"", notes:"", accountIds: selectedPf && !selectedPf.metaapi_id ? [selectedPf.id] : [], strategyId:null }));
    }
  }, [view, dayEndTime]);

  useEffect(() => {
    if (!showOnboarding) return;
    const views = ["home","propfirm","add","notes","trades","history","strategy","classement","rewards","test","charts","replay","ai",null];
    const v = views[onboardStep];
    if (v) setView(v);
  }, [onboardStep, showOnboarding]);

  const handleInstrument = v => {
    if (v === "Autre") { setShowCustom(true); set("instrument", "Autre"); }
    else { setShowCustom(false); set("instrument", v); }
  };

  const saveCustomInstr = () => {
    const n = customInstr.trim().toUpperCase();
    if (!n) return;
    if (!extraInstr.includes(n)) setExtraInstr(p => [...p, n]);
    set("instrument", n); setShowCustom(false); setCustomInstr("");
  };

  const computedPnl = () => {
    if (tradeFixedMode === "fixe") {
      if (form.result === "WIN")  return savedTS.tpFixed.enabled && savedTS.tpFixed.value ? parseFloat(savedTS.tpFixed.value) : 0;
      if (form.result === "LOSS") return savedTS.slFixed.enabled && savedTS.slFixed.value ? -Math.abs(parseFloat(savedTS.slFixed.value)) : 0;
      return 0;
    }
    const a = parseFloat(pnlRaw);
    if (isNaN(a) || pnlRaw === "") return null;
    if (form.result === "LOSS") return -Math.abs(a);
    if (form.result === "BREAKEVEN") return pnlRaw ? beSign * (parseFloat(pnlRaw) || 0) : 0;
    return Math.abs(a);
  };

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert(lang === "fr" ? "Web Speech API non supportée sur ce navigateur." : "Speech recognition not supported in this browser."); return; }
    const r = new SR();
    r.lang = lang === "fr" ? "fr-FR" : "en-US";
    r.continuous = true;
    r.interimResults = true;
    r.onresult = e => {
      let t = "";
      for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript;
      setVoiceTranscript(t);
    };
    r.onerror = () => setVoiceListening(false);
    r.onend = () => setVoiceListening(false);
    recognitionRef.current = r;
    r.start();
    setVoiceListening(true);
  };
  const stopVoice = () => {
    if (recognitionRef.current) { recognitionRef.current.stop(); recognitionRef.current = null; }
    setVoiceListening(false);
  };
  const applyVoice = async () => {
    if (!voiceTranscript.trim()) return;
    stopVoice();
    setVoiceParsing(true);
    try {
      const res = await fetch("/api/parse-trade", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ transcript: voiceTranscript, lang }),
      });
      const data = await res.json();
      if (data.fields) {
        const f = data.fields;
        setForm(prev => ({
          ...prev,
          ...(f.direction && { direction: f.direction }),
          ...(f.instrument && { instrument: f.instrument }),
          ...(f.entry !== undefined && f.entry !== null && { entry: String(f.entry) }),
          ...(f.entryTime && { entryTime: f.entryTime }),
          ...(f.exit !== undefined && f.exit !== null && { exit: String(f.exit) }),
          ...(f.exitTime && { exitTime: f.exitTime }),
          ...(f.result && { result: f.result }),
          ...(f.size !== undefined && f.size !== null && { size: String(f.size) }),
          ...(f.session && { session: f.session }),
          ...(f.emotion && { emotion: f.emotion }),
        }));
        if (f.pnl !== undefined && f.pnl !== null) {
          setPnlRaw(String(Math.abs(parseFloat(f.pnl) || 0)));
        }
      }
      setVoiceOpen(false);
      setVoiceTranscript("");
    } catch {
      // parse failed silently
    } finally {
      setVoiceParsing(false);
    }
  };

  const addTrade = async () => {
    const p = computedPnl();
    if (p === null) return;
    // Auto-add badge if user typed without pressing Enter
    const finalTags = (() => {
      if (!tagInput.trim()) return form.tags;
      const name = tagInput.trim().toLowerCase().replace(/\s+/g,"-");
      const badge = tagPolarity !== "neutral" ? `${name}|${tagPolarity}` : name;
      if (form.tags.some(t => parseBadge(t).name === name)) return form.tags;
      return [...form.tags, badge];
    })();
    setTagInput(""); setTagPolarity("neutral");
    let newTrade;
    if (tradeFixedMode === "fixe") {
      const tp  = savedTS.tpFixed.enabled && savedTS.tpFixed.value ? parseFloat(savedTS.tpFixed.value) : null;
      const sl  = savedTS.slFixed.enabled && savedTS.slFixed.value ? Math.abs(parseFloat(savedTS.slFixed.value)) : null;
      const rr  = savedTS.rrFixed.enabled && savedTS.rrFixed.value ? savedTS.rrFixed.value : form.rr;
      const sz  = savedTS.sizeFixed.enabled && savedTS.sizeFixed.value ? savedTS.sizeFixed.value : form.size;
      const szu = savedTS.sizeFixed.enabled && savedTS.sizeFixed.value ? savedTS.sizeFixed.unit : form.sizeUnit;
      const pnl = form.result === "WIN" ? ((tp ?? 0) || parseFloat(pnlRaw) || 0)
                : form.result === "LOSS" ? -((sl ?? 0) || parseFloat(pnlRaw) || 0)
                : 0;
      newTrade = { ...form, tags:finalTags, pnl, rr, size:sz, sizeUnit:szu, entryTime:localToUtc(form.entryTime), exitTime:localToUtc(form.exitTime) };
    } else {
      newTrade = { ...form, tags:finalTags, pnl:p, entryTime:localToUtc(form.entryTime), exitTime:localToUtc(form.exitTime) };
    }
    if (user) {
      if (screenshotFile) {
        setScreenshotUploading(true);
        const ext = screenshotFile.name.split(".").pop();
        const path = `${user.id}/${Date.now()}.${ext}`;
        const { data: upData, error: upErr } = await supabase.storage.from("trade-screenshots").upload(path, screenshotFile, { contentType: screenshotFile.type, upsert: false });
        if (!upErr && upData) {
          const { data: { publicUrl } } = supabase.storage.from("trade-screenshots").getPublicUrl(upData.path);
          newTrade.screenshotUrl = publicUrl;
        }
        setScreenshotUploading(false);
      }
      const { data, error } = await supabase.from("trades").insert(tradeToDb(newTrade)).select().single();
      if (error) { console.error("Supabase insert error:", error); return; }
      if (data) setTrades(prev => [dbToTrade(data), ...prev]);
    } else {
      setTrades(prev => [{ ...newTrade, id:Date.now() }, ...prev]);
    }
    // Auto-archive any active propfirm that just hit its target, drawdown or daily loss
    const updatedTrades = [...trades, newTrade];
    propfirms.filter(p => p.type === "propfirm" && (!p.status || p.status === "active")).forEach(pf => {
      const pfId = pf.id;
      const pfTrades = updatedTrades.filter(t => !t.accountIds || t.accountIds.length === 0 || t.accountIds.includes(pfId));
      const pfPnl = pfTrades.reduce((s, t) => s + (t.pnl || 0), 0);
      const cap = parseFloat(pf.capital) || 0;
      const target = parseFloat(pf.target) || 0;
      const maxLoss = parseFloat(pf.maxLoss) || 0;
      const dailyLoss = parseFloat(pf.dailyLoss) || 0;
      if (target > 0 && pfPnl >= target) {
        setPropfirms(prev => prev.map(p => p.id === pfId ? { ...p, status:"passed" } : p));
        return;
      }
      if (maxLoss > 0) {
        let mll;
        if (pf.trailingDD) {
          const dates = [...new Set(pfTrades.map(t => t.date))].sort();
          let cum = 0, peak = 0;
          dates.forEach(d => { cum += pfTrades.filter(t => t.date === d).reduce((s,t) => s+(t.pnl||0), 0); if (cum > peak) peak = cum; });
          mll = cap + Math.max(0, peak) - maxLoss;
        } else {
          mll = cap - maxLoss;
        }
        if (cap + pfPnl <= mll) {
          setPropfirms(prev => prev.map(p => p.id === pfId ? { ...p, status:"breached" } : p));
          return;
        }
      }
      if (pf.hasDailyLoss && dailyLoss > 0) {
        const todayPnl = pfTrades.filter(t => t.date === newTrade.date).reduce((s,t) => s+(t.pnl||0), 0);
        if (todayPnl < 0 && Math.abs(todayPnl) >= dailyLoss) {
          setPropfirms(prev => prev.map(p => p.id === pfId ? { ...p, status:"breached" } : p));
        }
      }
    });
    setPnlRaw(""); setTagInput(""); setTagPolarity("neutral"); setScreenshotFile(null); setScreenshotPreview(null);
    const savedAccountId = newTrade.accountIds[0];
    const pf = propfirms.find(p => p.id === savedAccountId);
    setForm(f => ({ ...f, entry:"", exit:"", entryTime:"", exitTime:"", rr:"", size:"", notes:"", tags:[], accountIds:[], strategyId:null, screenshotUrl:"" }));
    setSaved(true); setTimeout(() => setSaved(false), 2500);
    if (pf) { setTimeout(() => { setSelectedPf(pf); setView("propfirm"); }, 380); }
  };

  const deleteTrade = async (id) => {
    if (user) {
      const trade = trades.find(t => t.id === id);
      if (trade?.screenshotUrl) {
        const path = trade.screenshotUrl.split("/trade-screenshots/").pop();
        if (path) await supabase.storage.from("trade-screenshots").remove([decodeURIComponent(path)]);
      }
      await supabase.from("trades").delete().eq("id", id);
    }
    setTrades(p => p.filter(t => t.id !== id));
  };
  const updateTrade = (id, ch) => setTrades(p => p.map(t => t.id === id ? { ...t, ...ch } : t));
  const startEdit = t => { setEditingTrade({ ...t }); setEditPnlRaw(String(Math.abs(t.pnl || 0))); setEditTagInput(""); setEditBadgePolarity("neutral"); };
  const cancelEdit = () => { setClosingEdit(true); setTimeout(() => { setEditingTrade(null); setEditPnlRaw(""); setEditTagInput(""); setEditBadgePolarity("neutral"); setClosingEdit(false); }, 200); };
  const closeDetail = () => { setClosingDetail(true); setTimeout(() => { setSelectedTradeDetail(null); setClosingDetail(false); }, 200); };
  const saveEdit = async () => {
    if (!editingTrade) return;
    const a = parseFloat(editPnlRaw);
    const p = isNaN(a) ? editingTrade.pnl : (editingTrade.result === "LOSS" ? -Math.abs(a) : editingTrade.result === "BREAKEVEN" ? 0 : Math.abs(a));
    const updated = { ...editingTrade, pnl:p };
    if (user) await supabase.from("trades").update(tradeToDb(updated)).eq("id", editingTrade.id);
    updateTrade(editingTrade.id, updated);
    setClosingEdit(true); setTimeout(() => { setEditingTrade(null); setEditPnlRaw(""); setClosingEdit(false); }, 200);
  };



  // Trades des comptes actifs uniquement (exclut passed/breached)
  const activePfIdSet = new Set(propfirms.filter(p => !p.status || p.status === "active").map(p => p.id));
  const activeOnlyTrades = trades.filter(t => !t.accountIds || t.accountIds.length === 0 || t.accountIds.some(id => activePfIdSet.has(id)));

  // Both stats and calendar filter by selected month
  const filterByPeriod = list => list.filter(t => {
    const d = new Date(t.date + "T12:00:00");
    return d.getFullYear() === calYear && d.getMonth() === calMonth;
  });
  const calFiltered = filterByPeriod(trades);

  const _nowYM = () => { const d=new Date(); return { y:d.getFullYear(), m:d.getMonth() }; };
  const prevMonth   = () => setCalNav(n   => n.month===0  ? {month:11,year:n.year-1} : {month:n.month-1,year:n.year});
  const nextMonth   = () => setCalNav(n   => { const {y,m}=_nowYM(); return (n.year>y||(n.year===y&&n.month>=m)) ? n : n.month===11 ? {month:0,year:n.year+1} : {month:n.month+1,year:n.year}; });
  const prevPfMonth = () => setPfCalNav(n => n.month===0  ? {month:11,year:n.year-1} : {month:n.month-1,year:n.year});
  const nextPfMonth = () => setPfCalNav(n => { const {y,m}=_nowYM(); return (n.year>y||(n.year===y&&n.month>=m)) ? n : n.month===11 ? {month:0,year:n.year+1} : {month:n.month+1,year:n.year}; });

  const filtered  = filterByPeriod(trades);
  const total     = filtered.length;
  const wins      = filtered.filter(t => t.result === "WIN").length;
  const pnlSum    = filtered.reduce((s, t) => s + (t.pnl || 0), 0);
  const winRate   = total ? Math.round((wins / total) * 100) : 0;
  const avgRR     = (() => { const rt=filtered.filter(t=>parseFloat(t.rr)>0); return rt.length ? (rt.reduce((s,t)=>s+parseFloat(t.rr),0)/rt.length).toFixed(1) : "—"; })();
  const sessionStats = SESSIONS.map(s => { const st = filtered.filter(t => t.session === s); const wr = st.length ? Math.round((st.filter(t => t.result === "WIN").length / st.length) * 100) : 0; return { name:s, count:st.length, wr, pnl:st.reduce((a, t) => a + (t.pnl || 0), 0) }; }).filter(s => s.count > 0);

  // ── Discipline Score (0-100) ──
  const calcDisciplineScore = () => {
    const recent = [...trades].sort((a,b) => b.date.localeCompare(a.date)).slice(0, 20);
    if (!recent.length) return null;
    let score = 100;
    // Pénalité émotions négatives
    const badEmotions = recent.filter(t => ["Anxieux","Euphorique","Frustré"].includes(t.emotion));
    score -= (badEmotions.length / recent.length) * 25;
    // Bonus notes remplies
    const withNotes = recent.filter(t => t.notes && t.notes.length > 5);
    score += (withNotes.length / recent.length) * 10;
    // Pénalité si trop de trades par jour
    const byDate = {};
    recent.forEach(t => { byDate[t.date] = (byDate[t.date]||0)+1; });
    const overtraded = Object.values(byDate).filter(n => n > 2).length;
    score -= overtraded * 8;
    // Bonus win rate > 50%
    const wr = recent.filter(t => t.result==="WIN").length / recent.length;
    if (wr >= 0.5) score += 10;
    // Bonus RR rempli
    const withRR = recent.filter(t => parseFloat(t.rr) >= 1);
    score += (withRR.length / recent.length) * 5;
    return Math.max(0, Math.min(100, Math.round(score)));
  };
  const disciplineScore = calcDisciplineScore();

  const buildPatternData = () => {
    const T = aiAccFilter !== "all" ? trades.filter(t => !t.accountIds || t.accountIds.length === 0 || t.accountIds.includes(aiAccFilter)) : trades;
    const DAYS = L.days;
    const group = (keyFn) => {
      const m = {};
      T.forEach(t => {
        const k = keyFn(t); if (!k) return;
        if (!m[k]) m[k] = { pnl:0, count:0, wins:0 };
        m[k].pnl += t.pnl || 0; m[k].count++;
        if (t.result === "WIN") m[k].wins++;
      });
      return m;
    };
    const fmt = (m) => Object.entries(m)
      .sort((a,b) => b[1].pnl - a[1].pnl)
      .map(([k,v]) => `  ${k}: ${v.count} trades | WR ${v.count ? Math.round(v.wins/v.count*100) : 0}% | P&L ${fms(v.pnl)}${currency}`)
      .join("\n");

    // Polarity map from custom emotions
    const polarityMap = { ...EMOTION_POLARITY };
    extraEmotions.forEach(e => { if (typeof e === "object" && e.polarity) polarityMap[e.label] = e.polarity; });

    const byDay     = group(t => DAYS[new Date(t.date+"T12:00:00").getDay()]);
    const bySession = group(t => t.session);
    const byEmotion = group(t => t.emotion);
    const byInstr   = group(t => t.instrument);
    const byDir     = group(t => t.direction);

    // Aggregate stats by polarity group
    const posEmotions = Object.keys(polarityMap).filter(k => polarityMap[k] === "positive");
    const negEmotions = Object.keys(polarityMap).filter(k => polarityMap[k] === "negative");
    const groupByPolarity = (keys) => {
      let pnl=0, count=0, wins=0;
      keys.forEach(k => { if (byEmotion[k]) { pnl += byEmotion[k].pnl; count += byEmotion[k].count; wins += byEmotion[k].wins; } });
      return { pnl, count, wins };
    };
    const posStats = groupByPolarity(posEmotions);
    const negStats = groupByPolarity(negEmotions);
    const polaritySection = (posEmotions.length || negEmotions.length) ? `
PAR POLARITE EMOTIONNELLE:
  Emotions POSITIVES (${posEmotions.join(", ") || "aucune"}): ${posStats.count} trades | WR ${posStats.count ? Math.round(posStats.wins/posStats.count*100) : 0}% | P&L ${fms(posStats.pnl)}${currency}
  Emotions NEGATIVES (${negEmotions.join(", ") || "aucune"}): ${negStats.count} trades | WR ${negStats.count ? Math.round(negStats.wins/negStats.count*100) : 0}% | P&L ${fms(negStats.pnl)}${currency}` : "";

    const fmtEmotion = (m) => Object.entries(m)
      .sort((a,b) => b[1].pnl - a[1].pnl)
      .map(([k,v]) => { const pol = polarityMap[k]; const tag = pol === "positive" ? " [POSITIVE]" : pol === "negative" ? " [NEGATIVE]" : ""; return `  ${k}${tag}: ${v.count} trades | WR ${v.count ? Math.round(v.wins/v.count*100) : 0}% | P&L ${fms(v.pnl)}${currency}`; })
      .join("\n");

    const winners = T.filter(t => t.result === "WIN" && parseFloat(t.rr) > 0);
    const avgWinRR = winners.length ? (winners.reduce((s,t) => s + (parseFloat(t.rr)||0), 0) / winners.length).toFixed(2) : null;
    const cutWinners = winners.filter(t => parseFloat(t.rr) < 1).length;
    const losers = T.filter(t => t.result === "LOSS" && parseFloat(t.rr) > 0);
    const avgLossRR = losers.length ? (losers.reduce((s,t) => s + (parseFloat(t.rr)||0), 0) / losers.length).toFixed(2) : null;

    const sorted = [...T].sort(cmpTrades);
    let maxLoseStreak = 0, curLoseStreak = 0, maxWinStreak = 0, curWinStreak = 0;
    sorted.forEach(t => {
      if (t.result === "LOSS") { curLoseStreak++; maxLoseStreak = Math.max(maxLoseStreak, curLoseStreak); curWinStreak = 0; }
      else if (t.result === "WIN") { curWinStreak++; maxWinStreak = Math.max(maxWinStreak, curWinStreak); curLoseStreak = 0; }
      else { curLoseStreak = 0; curWinStreak = 0; }
    });

    const sortedByDate = [...T].sort((a,b) => b.date.localeCompare(a.date));
    const recentTrades = sortedByDate.slice(0, 20).reverse()
      .map(t => `${t.date}|${t.instrument}|${t.direction}|${t.session||"—"}|${t.emotion||"—"}|RR:${t.rr||"—"}|${fms(t.pnl||0)}${currency}|${t.result}${t.tradeMode==="scalping"?"|SCALP":""}`)
      .join("\n");

    // Tags/setups
    const tagGroup = {};
    T.forEach(t => {
      (t.tags || []).forEach(tag => {
        if (!tagGroup[tag]) tagGroup[tag] = { pnl:0, count:0, wins:0 };
        tagGroup[tag].pnl += t.pnl || 0; tagGroup[tag].count++;
        if (t.result === "WIN") tagGroup[tag].wins++;
      });
    });
    const tagKeys = Object.keys(tagGroup);
    const byTagSection = tagKeys.length > 0 ? `\nPAR TAG/SETUP:\n${fmt(tagGroup)}` : "";

    // Global metrics
    const totalTrades = T.length;
    const totalWins = T.filter(t => t.result === "WIN").length;
    const globalWR = totalTrades ? Math.round(totalWins / totalTrades * 100) : 0;
    const globalPnl = T.reduce((s, t) => s + (t.pnl || 0), 0);

    // Profit factor & expectancy
    const grossWin  = T.filter(t => t.result === "WIN").reduce((s,t) => s + (t.pnl||0), 0);
    const grossLoss = Math.abs(T.filter(t => t.result === "LOSS").reduce((s,t) => s + (t.pnl||0), 0));
    const profitFactor = grossLoss > 0 ? (grossWin / grossLoss).toFixed(2) : "∞";
    const totalLosses = T.filter(t => t.result === "LOSS").length;
    const avgWinAmt  = totalWins  ? (grossWin  / totalWins).toFixed(2)  : "0";
    const avgLossAmt = totalLosses ? (grossLoss / totalLosses).toFixed(2) : "0";
    const expectancy = totalTrades ? ((grossWin - grossLoss) / totalTrades).toFixed(2) : "0";

    // Trades per day (overtrading detection)
    const byDay2 = {};
    T.forEach(t => { byDay2[t.date] = (byDay2[t.date] || 0) + 1; });
    const daycounts = Object.values(byDay2);
    const avgTradesPerDay = daycount => daycount.length ? (daycount.reduce((s,v) => s+v, 0) / daycount.length).toFixed(1) : "0";
    const maxTradesInDay  = daycount => daycount.length ? Math.max(...daycount) : 0;
    const daysOver3 = daycount => daycount.filter(v => v > 3).length;
    const tradingDays = Object.keys(byDay2).length;

    // Session × Emotion cross-combos (min 2 T)
    const crossMap = {};
    T.forEach(t => {
      if (!t.session || !t.emotion) return;
      const k = `${t.session} + ${t.emotion}`;
      if (!crossMap[k]) crossMap[k] = { pnl:0, count:0, wins:0 };
      crossMap[k].pnl += t.pnl || 0; crossMap[k].count++;
      if (t.result === "WIN") crossMap[k].wins++;
    });
    const crossEntries = Object.entries(crossMap).filter(([,v]) => v.count >= 2)
      .sort((a,b) => b[1].pnl - a[1].pnl);
    const top3Cross    = crossEntries.slice(0, 3);
    const bottom3Cross = crossEntries.slice(-3).reverse();
    const fmtCross = (entries) => entries.map(([k,v]) =>
      `  ${k}: ${v.count}T | WR ${Math.round(v.wins/v.count*100)}% | P&L ${fms(v.pnl)}${currency}`
    ).join("\n");

    // Recent form: last 10 vs rest
    const last10   = sortedByDate.slice(0, 10);
    const before10 = sortedByDate.slice(10);
    const formStats = (arr) => {
      const w = arr.filter(t => t.result === "WIN").length;
      const p = arr.reduce((s,t) => s+(t.pnl||0), 0);
      return arr.length ? `${arr.length}T | WR ${Math.round(w/arr.length*100)}% | P&L ${fms(p)}${currency}` : "—";
    };

    const scalpCount = T.filter(t => t.tradeMode === "scalping").length;
    const scalpNote = scalpCount > 0 ? `\nNOTE IMPORTANTE: ${scalpCount}/${totalTrades} trades sont en MODE SCALPING (saisie rapide). Ces trades ont intentionnellement peu de données (pas de RR, session, émotion optionnels). Ne pas critiquer l'absence de ces champs pour ces trades.` : "";

    return `GLOBAL: ${totalTrades} trades | WR ${globalWR}% | P&L total ${fms(globalPnl)}${currency}
Profit Factor: ${profitFactor} | Gain moyen: +${avgWinAmt}${currency} | Perte moyenne: -${avgLossAmt}${currency} | Esperance/trade: ${expectancy}${currency}
Jours tradés: ${tradingDays} | Moy trades/jour: ${avgTradesPerDay(daycounts)} | Max trades en 1 jour: ${maxTradesInDay(daycounts)} | Jours avec >3 trades: ${daysOver3(daycounts)}${scalpNote}

FORME RECENTE:
  10 derniers T: ${formStats(last10)}
  Trades précédents:  ${formStats(before10)}

PAR JOUR DE LA SEMAINE:
${fmt(byDay)}

PAR SESSION:
${fmt(bySession)}

PAR EMOTION:
${fmtEmotion(byEmotion)}${polaritySection}

PAR INSTRUMENT:
${fmt(byInstr)}

PAR DIRECTION:
${fmt(byDir)}${byTagSection}

COMBOS SESSION × EMOTION (meilleures):
${top3Cross.length ? fmtCross(top3Cross) : "  pas assez de données"}

COMBOS SESSION × EMOTION (pires):
${bottom3Cross.length ? fmtCross(bottom3Cross) : "  pas assez de données"}

WINNERS (${winners.length} trades gagnants avec RR):
  RR moyen winners: ${avgWinRR || "—"}
  RR moyen losers: ${avgLossRR || "—"}
  Winners fermes avant 1R: ${cutWinners}/${winners.length}${winners.length ? ` (${Math.round(cutWinners/winners.length*100)}%)` : ""}

SERIES: max ${maxLoseStreak} pertes consecutives | max ${maxWinStreak} gains consecutifs

${(() => {
  // Behavioral sequences: what happens after a loss? after a win?
  const seq = [...T].sort(cmpTrades);
  let afterLoss = { count:0, wins:0, pnl:0 };
  let afterWin  = { count:0, wins:0, pnl:0 };
  let revengeCandidates = 0; // same day, after a loss
  for (let i = 1; i < seq.length; i++) {
    const prev = seq[i-1]; const curr = seq[i];
    if (prev.result === "LOSS") {
      afterLoss.count++; afterLoss.pnl += curr.pnl||0;
      if (curr.result === "WIN") afterLoss.wins++;
      if (curr.date === prev.date) revengeCandidates++;
    }
    if (prev.result === "WIN") {
      afterWin.count++; afterWin.pnl += curr.pnl||0;
      if (curr.result === "WIN") afterWin.wins++;
    }
  }
  const cutWinnersEarly = winners.filter(t => parseFloat(t.rr) < 1).length;
  const letLossersRun   = losers.filter(t => parseFloat(t.rr) > 1.5).length;
  return `SEQUENCES COMPORTEMENTALES:
  Après une perte: ${afterLoss.count} trades | WR ${afterLoss.count ? Math.round(afterLoss.wins/afterLoss.count*100) : 0}% | P&L ${fms(afterLoss.pnl)}${currency}
  Après un gain: ${afterWin.count} trades | WR ${afterWin.count ? Math.round(afterWin.wins/afterWin.count*100) : 0}% | P&L ${fms(afterWin.pnl)}${currency}
  Trades revenge potentiels (même jour après perte): ${revengeCandidates}
  Winners coupés avant 1R (peur du retournement): ${cutWinnersEarly}/${winners.length}${winners.length ? ` (${Math.round(cutWinnersEarly/winners.length*100)}%)` : ""}
  Pertes laissées courir >1.5R (espoir): ${letLossersRun}/${losers.length}${losers.length ? ` (${Math.round(letLossersRun/losers.length*100)}%)` : ""}`;
})()}

DERNIERS 20 TRADES (date|instrument|direction|session|emotion|RR|P&L|resultat):
${recentTrades}`;
  };

  const analyzeAI = async () => {
    const aiTrades = aiAccFilter !== "all" ? trades.filter(t => !t.accountIds || t.accountIds.length === 0 || t.accountIds.includes(aiAccFilter)) : trades;
    if (aiTrades.length < 3) { setAiText(L.aiSection.noTrades); return; }
    setAiLoading(true); setAiText(""); setAiError(""); setAiSummary(null);
    const patternData = buildPatternData();
    const strat = strategies[0] || {};
    const stratCtx = [strat.description && "Description: " + strat.description, strat.steps && strat.steps.length > 0 && "Étapes: " + strat.steps.map((s,i) => `${i+1}. ${s}`).join("\n"), strat.rules && "Règles: " + strat.rules, strat.notes && "Notes: " + strat.notes].filter(Boolean).join("\n");
    try {
      const res = await fetch("/api/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ patternData, stratCtx, tradeCount: aiTrades.length, coachStyle, userName: profileForm.nickname || profileForm.firstName || null, coachMemory, lang }),
      });
      const data = await res.json();
      if (!res.ok) { setAiError(data.error || "Erreur inconnue"); setAiLoading(false); return; }
      if (data.text) {
        // Extract and store [MEMOIRE] block without showing it
        const memoireMatch = data.text.match(/\[MEMOIRE\]([\s\S]*?)\[\/MEMOIRE\]/);
        if (memoireMatch) {
          const mem = memoireMatch[1].trim();
          setCoachMemory(mem);
          if (user) saveUserSettings({ coach_memory: mem });
        }
        setAiText(data.text);
        const summaryMatch = data.text.match(/\[SUMMARY\]([\s\S]*?)\[\/SUMMARY\]/);
        if (summaryMatch) setAiSummary(summaryMatch[1].trim());
      } else {
        setAiError(L.aiSection.emptyResponse);
      }
    } catch (e) { setAiError(L.aiSection.networkError(e.message)); }
    setAiLoading(false);
  };


  // ── Dashboard JSX ──
  const dashboardContent = (desktop) => (
    <div>
      {!desktop && <PageTitle sub={L.pages.dash.sub} title={total === 0 ? L.pages.dash.t0 : L.pages.dash.t1} />}
      <div style={{ display:"grid", gridTemplateColumns:desktop ? "repeat(4,1fr)" : "1fr 1fr", gap:10, marginBottom:20 }}>
        <StatCard key={`sc0-${view}`} label="Win Rate"  value={`${winRate}%`}  color={winRate >= 50 ? C.accent : C.gray1} small={desktop} staggerIdx={0} />
        <StatCard key={`sc1-${view}`} label="P&L Total" value={`${fms(pnlSum)}${currency}`} color={pnlSum >= 0 ? C.accent : C.gray1} small={desktop} staggerIdx={1} />
        <StatCard key={`sc2-${view}`} label={L.stats.avgRR} value={`${avgRR}:1`} color={C.dim} small={desktop} staggerIdx={2} />
        <StatCard key={`sc3-${view}`} label="Bilan" value={`${wins}W / ${total - wins}L`} color={C.accent} small={desktop} staggerIdx={3} />
      </div>

      {desktop ? (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
          <div style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:10, boxShadow:cShadow, padding:"16px 14px 10px" }}>
            <div style={{ marginBottom:8 }}>
              <div style={{ fontSize:10, color:C.dim, letterSpacing:"0.2em", textTransform:"uppercase", fontFamily:"'Josefin Sans',sans-serif", fontWeight:600 }}>{L.stats.pnlEvolution}</div>
            </div>
            {propfirms.length > 0 && (
              <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:10 }}>
                <button onClick={() => setChartAccountId("all")} style={{ padding:"4px 10px", borderRadius:4, border:"none", background:chartAccountId==="all" ? C.accent : C.bg3, color:chartAccountId==="all" ? "#fff" : C.gray1, fontSize:10, fontFamily:"'Josefin Sans',sans-serif", fontWeight:chartAccountId==="all" ? 600 : 300, letterSpacing:"0.08em", cursor:"pointer" }}>Tous</button>
                {propfirms.map(pf => (
                  <button key={pf.id} onClick={() => setChartAccountId(pf.id)} style={{ padding:"4px 10px", borderRadius:4, border:"none", background:chartAccountId===pf.id ? C.accent : C.bg3, color:chartAccountId===pf.id ? "#fff" : C.gray1, fontSize:10, fontFamily:"'Josefin Sans',sans-serif", fontWeight:chartAccountId===pf.id ? 600 : 300, letterSpacing:"0.08em", cursor:"pointer" }}>
                    {pf.firm}{pf.name ? " · " + pf.name : ""}
                  </button>
                ))}
              </div>
            )}
            <PnlChart filtered={chartAccountId==="all" ? filtered : filtered.filter(t => !t.accountIds || t.accountIds.length===0 || t.accountIds.includes(chartAccountId))} capital={capital} pnlSum={pnlSum} height={160} cur={currency}/>
            {filtered.length < 2 && <div style={{ textAlign:"center", padding:"32px 0", color:C.gray2, fontSize:11, fontFamily:"'Josefin Sans',sans-serif", letterSpacing:"0.1em" }}>{L.btn.noTradesMonth}</div>}
          </div>
          <div style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:10, boxShadow:cShadow, padding:"16px 14px" }}>
            <Calendar filtered={calFiltered} calMonth={calMonth} calYear={calYear} onPrev={prevMonth} onNext={nextMonth} dayEndTime={dayEndTime} fms={fms} hideNumbers={hideNumbers} />
          </div>
        </div>
      ) : (
        <div>
          <div style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:10, boxShadow:cShadow, padding:"16px 14px 10px", marginBottom:14 }}>
              <div style={{ marginBottom:8 }}>
                <div style={{ fontSize:10, color:C.dim, letterSpacing:"0.2em", textTransform:"uppercase", fontFamily:"'Josefin Sans',sans-serif", fontWeight:600 }}>{L.stats.pnlEvolution}</div>
              </div>
              {propfirms.length > 0 && (
                <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:10 }}>
                  <button onClick={() => setChartAccountId("all")} style={{ padding:"4px 10px", borderRadius:4, border:"none", background:chartAccountId==="all" ? C.accent : C.bg3, color:chartAccountId==="all" ? "#fff" : C.gray1, fontSize:10, fontFamily:"'Josefin Sans',sans-serif", fontWeight:chartAccountId==="all" ? 600 : 300, letterSpacing:"0.08em", cursor:"pointer" }}>Tous</button>
                  {propfirms.map(pf => (
                    <button key={pf.id} onClick={() => setChartAccountId(pf.id)} style={{ padding:"4px 10px", borderRadius:4, border:"none", background:chartAccountId===pf.id ? C.accent : C.bg3, color:chartAccountId===pf.id ? "#fff" : C.gray1, fontSize:10, fontFamily:"'Josefin Sans',sans-serif", fontWeight:chartAccountId===pf.id ? 600 : 300, letterSpacing:"0.08em", cursor:"pointer" }}>
                      {pf.firm}{pf.name ? " · " + pf.name : ""}
                    </button>
                  ))}
                </div>
              )}
              {filtered.length > 1 ? <PnlChart filtered={chartAccountId==="all" ? filtered : filtered.filter(t => !t.accountIds || t.accountIds.length===0 || t.accountIds.includes(chartAccountId))} capital={capital} pnlSum={pnlSum} height={150} cur={currency}/> : <div style={{ textAlign:"center", padding:"32px 0", color:C.gray2, fontSize:11, fontFamily:"'Josefin Sans',sans-serif", letterSpacing:"0.1em" }}>{L.btn.noTradesMonth}</div>}
            </div>
          <div style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:10, boxShadow:cShadow, padding:"16px 14px", marginBottom:14 }}>
              <Calendar filtered={calFiltered} calMonth={calMonth} calYear={calYear} onPrev={prevMonth} onNext={nextMonth} dayEndTime={dayEndTime} fms={fms} hideNumbers={hideNumbers} />
            </div>
        </div>
      )}

      {sessionStats.length > 0 && (
        <div style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:10, boxShadow:cShadow, padding:18 }}>
          <div style={{ fontSize:10, color:C.dim, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:14, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600 }}>Performance par Session</div>
          {sessionStats.map(s => (
            <div key={s.name} style={{ marginBottom:12 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                <span style={{ fontSize:11, color:C.white, letterSpacing:"0.06em", textTransform:"uppercase", fontFamily:"'Josefin Sans',sans-serif" }}>{s.name}</span>
                <span style={{ fontSize:12, fontFamily:"'Josefin Sans',sans-serif", fontWeight:300, color:s.pnl >= 0 ? C.accent : C.gray1, letterSpacing:"0.03em" }}>{}{fms(s.pnl)}{currency} · {s.wr}%</span>
              </div>
              <div style={{ height:3, background:C.gray3, borderRadius:2 }}>
                <div style={{ width:`${s.wr}%`, height:"100%", borderRadius:2, background:C.accent, transition:"width 0.7s" }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {trades.length === 0 && (
        <div style={{ textAlign:"center", padding:"48px 0" }}>
          <div style={{ fontSize:44, marginBottom:12, color:C.gray2 }}>◎</div>
          <div style={{ fontFamily:"'Josefin Sans',sans-serif", fontSize:16, fontWeight:300, color:C.gray1, marginBottom:14, letterSpacing:"0.06em" }}>Commencez à enregistrer vos trades</div>
          <button onClick={() => setView("add")} style={{ background:"radial-gradient(ellipse 90% 90% at 50% 38%, rgba(245,245,245,0.95) 0%, rgba(215,215,215,0.88) 55%, rgba(230,230,230,0.92) 100%)", border:"none", borderRadius:12, padding:"11px 24px", color:darkMode?"#111":"#fff", fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.15em", textTransform:"uppercase", cursor:"pointer" }}>{L.btn.firstTrade}</button>
        </div>
      )}
    </div>
  );

  // ── Add Trade JSX ──
  const addTradeContent = (
    <div>
      <PageTitle sub={L.pages.add.sub} title={L.pages.add.title} />

      {/* ── MODE SWITCH ── */}
      <div style={{display:"flex",gap:8,marginBottom:20,padding:5,background:darkMode?"linear-gradient(180deg,rgba(85,85,85,0.98) 0%,rgba(28,28,28,0.99) 100%)":"linear-gradient(180deg,rgba(60,60,60,0.95) 0%,rgba(18,18,18,0.97) 100%)",borderRadius:16,border:darkMode?"1px solid rgba(255,255,255,0.18)":"1px solid rgba(255,255,255,0.14)",boxShadow:darkMode?"0 12px 40px rgba(0,0,0,0.75), 0 4px 10px rgba(0,0,0,0.5), 0 0 50px rgba(255,255,255,0.1), 0 0 0 1px rgba(255,255,255,0.12), inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -2px 0 rgba(0,0,0,0.75)":"0 8px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.55)"}}>
        {[
          {k:"swing", label:"Swing / Day"},
          {k:"scalping", label:"Scalping"},
        ].map(m => (
          <button key={m.k} onClick={()=>setTradeMode(m.k)} style={{flex:1,padding:"10px 12px",borderRadius:11,border:"none",background:tradeMode===m.k?"radial-gradient(ellipse 90% 90% at 50% 50%, rgba(252,252,252,0.96) 0%, rgba(218,218,218,0.88) 55%, rgba(235,235,235,0.92) 100%)":"transparent",color:tradeMode===m.k?"#111":"rgba(255,255,255,0.55)",fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:tradeMode===m.k?600:300,letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer",transition:"all 0.22s cubic-bezier(.4,0,.2,1)",position:"relative",display:"flex",alignItems:"center",justifyContent:"center",gap:8,boxShadow:tradeMode===m.k?"0 6px 20px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.12)":"none",transform:"translateY(0)"}}>
            {m.label}
            {m.k==="scalping" && tradeMode==="scalping" && (
              <span style={{background:"rgba(255,255,255,0.88)",border:"1px solid rgba(255,255,255,0.6)",color:"#111",fontSize:7,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.2em",padding:"2px 7px",borderRadius:4,textTransform:"uppercase",whiteSpace:"nowrap"}}>{L.form.quickEntry}</span>
            )}
            {m.k==="scalping" && tradeMode!=="scalping" && (
              <span style={{background:"rgba(255,255,255,0.18)",border:"1px solid rgba(255,255,255,0.35)",color:"rgba(255,255,255,0.75)",fontSize:7,fontFamily:"'Josefin Sans',sans-serif",fontWeight:400,letterSpacing:"0.18em",padding:"2px 8px",borderRadius:4,textTransform:"uppercase",whiteSpace:"nowrap"}}>{L.form.quickEntry}</span>
            )}
          </button>
        ))}
      </div>

      <button onClick={() => { setVoiceTranscript(""); setVoiceOpen(true); }} style={{ width:"100%", marginBottom:16, padding:"13px", borderRadius:8, border:`1px solid ${C.border}`, background:"transparent", color:C.gray1, fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.15em", textTransform:"uppercase", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, transition:"all 0.2s" }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2a3 3 0 0 1 3 3v7a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3z"/>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
          <line x1="12" y1="19" x2="12" y2="23"/>
          <line x1="8" y1="23" x2="16" y2="23"/>
        </svg>
        {lang === "fr" ? "Saisie vocale" : "Voice input"}
      </button>

      <div style={{ display:"flex", flexWrap:"wrap", gap:12, marginBottom:16 }}>
        <div style={{ flex:"1 1 100%", minWidth:0 }}><Label>{L.form.date}</Label><DatePicker value={form.date} onChange={v => set("date", v)} /></div>
        <div style={{ flex:"1 1 45%", minWidth:0 }}><Label>{lang==="fr"?"Heure entrée":"Entry time"}</Label><input type="time" value={form.entryTime||""} onChange={e=>set("entryTime",e.target.value)} style={{...iStyle,padding:"11px 10px",colorScheme:darkMode?"dark":"light"}}/></div>
        <div style={{ flex:"1 1 45%", minWidth:0 }}><Label>{lang==="fr"?"Heure sortie":"Exit time"}</Label><input type="time" value={form.exitTime||""} onChange={e=>set("exitTime",e.target.value)} style={{...iStyle,padding:"11px 10px",colorScheme:darkMode?"dark":"light"}}/></div>
      </div>
      <div style={{ display:"flex", gap:12, alignItems:"flex-start", marginBottom:16 }}>
        <div style={{ flex:"1 1 50%", minWidth:0 }}>
          <Label>{L.form.instrument}</Label>
          <select
            value={showCustom ? "__autre__" : (form.instrument || "")}
            onChange={e => {
              if (e.target.value === "__autre__") { setShowCustom(true); set("instrument","Autre"); }
              else { setShowCustom(false); handleInstrument(e.target.value); }
            }}
            style={{ ...iStyle, cursor:"pointer" }}
          >
            {[...BASE_INSTRUMENTS, ...extraInstr].map(o => <option key={o} value={o}>{o}</option>)}
            <option value="__autre__">+ {lang==="fr"?"Autre":"Other"}</option>
          </select>
          {showCustom && (
            <div style={{ marginTop:8, display:"flex", gap:8 }}>
              <input type="text" placeholder={L.form.assetPH} value={customInstr} onChange={e => setCustomInstr(e.target.value)} onKeyDown={e => e.key === "Enter" && saveCustomInstr()} style={{ ...iStyle, flex:1, fontSize:14 }} autoFocus />
              <button onClick={saveCustomInstr} style={{ background:C.accent, border:"none", borderRadius:6, padding:"0 14px", color:darkMode?"#111":"#fff", fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, cursor:"pointer", textTransform:"uppercase", flexShrink:0 }}>OK</button>
            </div>
          )}
        </div>
        <div style={{ flex:"1 1 50%", minWidth:0 }}>
          <Label>{L.form.direction}</Label>
          <ChipGroup options={["LONG","SHORT"]} value={form.direction} onChange={v => set("direction", v)} />
        </div>
      </div>
      <Divider />
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,alignItems:"start",marginBottom:4}}>
        <Field label={L.form.result}>
          <select
            value={form.result}
            onChange={e => { set("result", e.target.value); setBeSign(1); if (e.target.value === "BREAKEVEN") setTradeFixedMode("variable"); }}
            style={{ ...iStyle, cursor:"pointer" }}
          >
            <option value="WIN">WIN</option>
            <option value="LOSS">LOSS</option>
            <option value="BREAKEVEN">BREAKEVEN</option>
          </select>
        </Field>
        {(tradeFixedMode==="variable" || !(savedTS.tpFixed.enabled&&savedTS.tpFixed.value&&savedTS.slFixed.enabled&&savedTS.slFixed.value)) && (
          <Field label={`P&L — ${form.result === "LOSS" ? L.form.pnlLoss : form.result === "WIN" ? L.form.pnlWin : L.form.pnlBe}`}>
            <input type="text" inputMode="decimal" placeholder="" value={pnlRaw} onChange={e => { const v = e.target.value.replace(/,/g,".").replace(/[^0-9.]/g, ""); setPnlRaw(v); }} style={{ ...iStyle, fontSize:18, fontFamily:"'Josefin Sans',sans-serif", fontWeight:300, color:"#1a1a1a", textAlign:"center" }} />
          </Field>
        )}
      </div>
      {form.result === "BREAKEVEN" && (
        <div style={{display:"flex",gap:8,marginBottom:10,alignItems:"center"}}>
          <div style={{display:"flex",borderRadius:6,border:`1px solid ${C.border}`,overflow:"hidden"}}>
            <button onClick={()=>setBeSign(1)} style={{padding:"7px 16px",border:"none",background:beSign===1?"#2a6e3a":"transparent",color:beSign===1?"#fff":C.gray1,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,cursor:"pointer",transition:"all 0.2s"}}>+ Positif</button>
            <button onClick={()=>setBeSign(-1)} style={{padding:"7px 16px",border:"none",background:beSign===-1?"#c0392b":"transparent",color:beSign===-1?"#fff":C.gray1,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,cursor:"pointer",transition:"all 0.2s"}}>− Négatif</button>
          </div>
          {pnlRaw && <span style={{fontSize:12,color:beSign===1?"#2a6e3a":"#c0392b",fontFamily:"'Josefin Sans',sans-serif"}}>{beSign===1?"+":"-"}{fm(parseFloat(pnlRaw))}{currency}</span>}
        </div>
      )}
      {pnlRaw && !isNaN(parseFloat(pnlRaw)) && form.result !== "BREAKEVEN" && (
        <div style={{ marginBottom:10, fontSize:12, fontFamily:"'Josefin Sans',sans-serif" }}>
          {form.result === "WIN" && <span style={{color:"#2a6e3a"}}>{L.form.gain(fm(parseFloat(pnlRaw)), currency)}</span>}
          {form.result === "LOSS" && <span style={{color:"#c0392b"}}>{L.form.loss(fm(parseFloat(pnlRaw)), currency)}</span>}
        </div>
      )}
      <Divider />
      <Field label={L.form.session}>
        <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
          <Chip label={lang==="fr"?"Aucune":"None"} active={!form.session} onClick={() => set("session", "")} />
          {SESSIONS.map(s => <Chip key={s} label={s} active={form.session===s} onClick={() => set("session", s)} />)}
        </div>
      </Field>
      {(tradeMode==="swing" || scalpFields.emotion) && (
        <Field label={L.form.emotion}>
          <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
            {EMOTIONS.map(e => { const pol = EMOTION_POLARITY[e]; return <Chip key={e} label={L.emotions[e]||e} active={form.emotion===e} onClick={()=>set("emotion",e)} dot={pol==="positive"?"#4caf6e":pol==="negative"?"#e05a5a":null}/>; })}
            {extraEmotions.map(e => {
              const lbl = typeof e === "string" ? e : e.label;
              const pol = typeof e === "string" ? null : e.polarity;
              return <Chip key={lbl} label={lbl} active={form.emotion===lbl} onClick={()=>set("emotion",lbl)} dot={pol==="positive"?"#4caf6e":pol==="negative"?"#e05a5a":null} onDelete={()=>setExtraEmotions(p=>p.filter(em=>(typeof em==="string"?em:em.label)!==lbl))}/>;
            })}
            <Chip label="+ Autre" active={showCustomEmotion} onClick={()=>setShowCustomEmotion(v=>!v)}/>
          </div>
          {showCustomEmotion && (
            <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:8}}>
              <div style={{display:"flex",gap:8}}>
                <input type="text" placeholder={L.form.emotionPH} value={customEmotion} onChange={e=>setCustomEmotion(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&customEmotion.trim()){setExtraEmotions(p=>[...p,{label:customEmotion.trim(),polarity:customEmotionPolarity}]);set("emotion",customEmotion.trim());setCustomEmotion('');setShowCustomEmotion(false);}}} style={{...iStyle,flex:1,fontSize:13}} autoFocus/>
                <button onClick={()=>{if(customEmotion.trim()){setExtraEmotions(p=>[...p,{label:customEmotion.trim(),polarity:customEmotionPolarity}]);set("emotion",customEmotion.trim());setCustomEmotion('');setShowCustomEmotion(false);}}} style={{background:C.accent,border:"none",borderRadius:6,padding:"0 14px",color:darkMode?"#111":"#fff",fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,cursor:"pointer"}}>OK</button>
              </div>
              <div style={{display:"flex",gap:6}}>
                {[{k:"positive",l:L.form.positive,c:"#4caf6e"},{k:"negative",l:L.form.negative,c:"#e05a5a"}].map(p=>(
                  <button key={p.k} onClick={()=>setCustomEmotionPolarity(p.k)} style={{flex:1,padding:"7px 10px",borderRadius:8,border:`1px solid ${customEmotionPolarity===p.k?p.c:C.border}`,background:customEmotionPolarity===p.k?`${p.c}18`:"transparent",color:customEmotionPolarity===p.k?p.c:C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer",transition:"all 0.18s",display:"flex",alignItems:"center",gap:6,justifyContent:"center"}}>
                    <span style={{width:7,height:7,borderRadius:"50%",background:customEmotionPolarity===p.k?p.c:C.gray2,flexShrink:0}}/>
                    {p.l}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Field>
      )}
      {/* ── VARIABLE / FIXE SWITCH — after emotion ── */}
      <div style={{display:"flex",gap:6,margin:"14px 0",padding:4,background:darkMode?"linear-gradient(180deg,rgba(85,85,85,0.98) 0%,rgba(28,28,28,0.99) 100%)":"linear-gradient(180deg,rgba(60,60,60,0.95) 0%,rgba(18,18,18,0.97) 100%)",borderRadius:14,border:darkMode?"1px solid rgba(255,255,255,0.18)":"1px solid rgba(255,255,255,0.14)",boxShadow:darkMode?"0 12px 40px rgba(0,0,0,0.75), 0 4px 10px rgba(0,0,0,0.5), 0 0 50px rgba(255,255,255,0.1), 0 0 0 1px rgba(255,255,255,0.12), inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -2px 0 rgba(0,0,0,0.75)":"0 8px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.55)"}}>
        {[{k:"variable",l:"Variable"},{k:"fixe",l:"Fixe"}].map(opt=>(
          <button key={opt.k} onClick={()=>setTradeFixedMode(opt.k)} style={{flex:1,padding:"8px",borderRadius:10,border:"none",background:tradeFixedMode===opt.k?"radial-gradient(ellipse 90% 90% at 50% 50%, rgba(252,252,252,0.96) 0%, rgba(218,218,218,0.88) 55%, rgba(235,235,235,0.92) 100%)":"transparent",color:tradeFixedMode===opt.k?"#111":"rgba(255,255,255,0.55)",fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:tradeFixedMode===opt.k?600:300,letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer",transition:"all 0.22s cubic-bezier(.4,0,.2,1)",boxShadow:tradeFixedMode===opt.k?"0 6px 20px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.12)":"none",transform:"translateY(0)"}}>
            {opt.l}
          </button>
        ))}
      </div>
      {tradeFixedMode==="fixe" && (
        <div style={{marginBottom:14,padding:"12px 14px",borderRadius:8,background:C.bg2,border:`1px solid ${C.border}`}}>
          <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",marginBottom:8}}>{L.acct.fixedValues}</div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            {savedTS.tpFixed.enabled&&savedTS.tpFixed.value&&<div style={{fontSize:11,fontFamily:"'Josefin Sans',sans-serif"}}><span style={{color:C.dim}}>WIN : </span><strong style={{color:"#2a6e3a"}}>+{savedTS.tpFixed.value}{currency}</strong></div>}
            {savedTS.slFixed.enabled&&savedTS.slFixed.value&&<div style={{fontSize:11,fontFamily:"'Josefin Sans',sans-serif"}}><span style={{color:C.dim}}>LOSS : </span><strong style={{color:"#c0392b"}}>-{savedTS.slFixed.value}{currency}</strong></div>}
            {savedTS.rrFixed.enabled&&savedTS.rrFixed.value&&<div style={{fontSize:11,fontFamily:"'Josefin Sans',sans-serif"}}><span style={{color:C.dim}}>R/R : </span><strong style={{color:C.white}}>{savedTS.rrFixed.value}:1</strong></div>}
            {savedTS.sizeFixed.enabled&&savedTS.sizeFixed.value&&<div style={{fontSize:11,fontFamily:"'Josefin Sans',sans-serif"}}><span style={{color:C.dim}}>{L.form.size} : </span><strong style={{color:C.white}}>{savedTS.sizeFixed.value} {savedTS.sizeFixed.unit}</strong></div>}
            {!savedTS.tpFixed.enabled&&!savedTS.slFixed.enabled&&!savedTS.rrFixed.enabled&&!savedTS.sizeFixed.enabled&&<span style={{fontSize:10,color:C.gray2,fontFamily:"'Josefin Sans',sans-serif"}}>{L.acct.noFixedFields}</span>}
          </div>
        </div>
      )}
      <Divider />
      {tradeMode==="swing" ? (
        <>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <Field label={L.form.entry}><input type="text" inputMode="decimal" placeholder="" value={form.entry} onChange={e => set("entry", e.target.value.replace(/,/g,"."))} style={{...iStyle,textAlign:"center"}} /></Field>
            <Field label={L.form.exit}><input type="text" inputMode="decimal" placeholder="" value={form.exit} onChange={e => set("exit", e.target.value.replace(/,/g,"."))} style={{...iStyle,textAlign:"center"}} /></Field>
          </div>
          {(()=>{
            const showRR = !(tradeFixedMode==="fixe" && savedTS.rrFixed.enabled && savedTS.rrFixed.value);
            const showSz = !(tradeFixedMode==="fixe" && savedTS.sizeFixed.enabled && savedTS.sizeFixed.value);
            if (!showRR && !showSz) return null;
            return (
              <div style={{display:"grid",gridTemplateColumns:showRR&&showSz?"1fr 1fr":"1fr",gap:10}}>
                {showRR && <Field label={L.form.rr}><input type="text" inputMode="decimal" placeholder="" value={form.rr} onChange={e => set("rr", e.target.value.replace(/,/g,"."))} style={{...iStyle,textAlign:"center"}}/></Field>}
                {showSz && <Field label={<div style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%"}}><span>{L.form.size}</span><div style={{display:"flex",borderRadius:5,border:`1px solid ${C.border}`,overflow:"hidden"}}>{["contrats","lots"].map(u=>(<button key={u} onClick={()=>set("sizeUnit",u)} style={{padding:"2px 8px",border:"none",background:form.sizeUnit===u?C.accent:"transparent",color:form.sizeUnit===u?(darkMode?"#111":"#fff"):C.gray1,fontSize:9,fontFamily:"'Josefin Sans',sans-serif",fontWeight:form.sizeUnit===u?600:300,cursor:"pointer",letterSpacing:"0.08em",transition:"all 0.18s"}}>{u}</button>))}</div></div>}><input type="text" inputMode="decimal" placeholder="" value={form.size} onChange={e=>set("size",e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,""))} style={{...iStyle,textAlign:"center"}}/></Field>}
              </div>
            );
          })()}
        </>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:4}}>
          {/* Scalping optional toggles */}
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {[
              {k:"entry", label:L.form.entryExit},
              {k:"rr", label:"R/R", hideInFixe: true},
              {k:"emotion", label:L.form.emotion},
              {k:"notes", label:L.form.notes},
              {k:"size", label:L.form.size, hideInFixe: true},
            ].filter(f => !(f.hideInFixe && tradeFixedMode==="fixe" && ((f.k==="rr" && savedTS.rrFixed.enabled && savedTS.rrFixed.value)||(f.k==="size" && savedTS.sizeFixed.enabled && savedTS.sizeFixed.value)))).map(f => (
              <button key={f.k} onClick={()=>toggleScalp(f.k)} style={{padding:"6px 12px",borderRadius:6,border:`1px solid ${scalpFields[f.k]?C.accent:C.border}`,background:scalpFields[f.k]?"rgba(255,255,255,0.06)":"transparent",color:scalpFields[f.k]?C.white:C.gray2,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",fontWeight:scalpFields[f.k]?400:300,letterSpacing:"0.1em",cursor:"pointer",transition:"all 0.18s",display:"flex",alignItems:"center",gap:6}}>
                <div style={{width:6,height:6,borderRadius:"50%",background:scalpFields[f.k]?C.accent:C.gray3,transition:"background 0.18s"}}/>
                {f.label}
              </button>
            ))}
          </div>
          {scalpFields.entry && (
            <>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                <Field label={L.form.entry}><input type="text" inputMode="decimal" placeholder="" value={form.entry} onChange={e => set("entry", e.target.value.replace(/,/g,"."))} style={{...iStyle,textAlign:"center"}}/></Field>
                <Field label={L.form.exit}><input type="text" inputMode="decimal" placeholder="" value={form.exit} onChange={e => set("exit", e.target.value.replace(/,/g,"."))} style={{...iStyle,textAlign:"center"}}/></Field>
              </div>
            </>
          )}
          {scalpFields.rr && !(tradeFixedMode==="fixe" && savedTS.rrFixed.enabled && savedTS.rrFixed.value) && <Field label={L.form.rr}><input type="text" inputMode="decimal" placeholder="" value={form.rr} onChange={e => set("rr", e.target.value.replace(/,/g,"."))} style={{...iStyle,textAlign:"center"}}/></Field>}
          {scalpFields.size && !(tradeFixedMode==="fixe" && savedTS.sizeFixed.enabled && savedTS.sizeFixed.value) && (
            <Field label={<div style={{display:"flex",alignItems:"center",gap:8,justifyContent:"space-between",width:"100%"}}>
              <span>Taille</span>
              <div style={{display:"flex",borderRadius:5,border:`1px solid ${C.border}`,overflow:"hidden"}}>
                {["contrats","lots"].map(u=>(
                  <button key={u} onClick={()=>set("sizeUnit",u)} style={{padding:"2px 8px",border:"none",background:form.sizeUnit===u?C.accent:"transparent",color:form.sizeUnit===u?(darkMode?"#111":"#fff"):C.gray1,fontSize:9,fontFamily:"'Josefin Sans',sans-serif",cursor:"pointer",transition:"all 0.18s"}}>{u}</button>
                ))}
              </div>
            </div>}>
              <input type="text" inputMode="decimal" placeholder="" value={form.size} onChange={e=>set("size",e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,""))} style={{...iStyle,textAlign:"center"}}/>
            </Field>
          )}
        </div>
      )}
      {(tradeMode==="swing" || scalpFields.notes) && (
        <Field label={L.form.notes}>
          <textarea rows={3} placeholder={L.form.notesPH} value={form.notes} onChange={e => set("notes", e.target.value)} style={{ ...iStyle, resize:"vertical", lineHeight:1.6, textAlign:"left" }} />
        </Field>
      )}

      {/* ── Screenshot ── */}
      <div style={{ marginBottom:16 }}>
        <Label>{lang==="fr"?"Capture d'écran":"Screenshot"}</Label>
        <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}>
          <input type="file" accept="image/*" style={{ display:"none" }} onChange={e => {
            const file = e.target.files?.[0];
            if (!file) return;
            if (file.size > 8 * 1024 * 1024) { alert(lang==="fr"?"Image trop lourde (max 8 Mo)":"Image too large (max 8MB)"); return; }
            setScreenshotFile(file);
            const reader = new FileReader();
            reader.onload = ev => setScreenshotPreview(ev.target.result);
            reader.readAsDataURL(file);
          }} />
          {screenshotPreview ? (
            <div style={{ position:"relative", display:"inline-block" }}>
              <img src={screenshotPreview} alt="preview" style={{ width:"100%", maxHeight:160, objectFit:"cover", borderRadius:8, border:`1px solid ${C.border}`, display:"block" }} />
              <button type="button" onClick={e => { e.preventDefault(); setScreenshotFile(null); setScreenshotPreview(null); }} style={{ position:"absolute", top:6, right:6, background:"rgba(0,0,0,0.6)", border:"none", borderRadius:20, width:22, height:22, color:"#fff", fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>×</button>
            </div>
          ) : (
            <div style={{ width:"100%", padding:"14px", borderRadius:10, border:`1.5px dashed ${C.border}`, display:"flex", alignItems:"center", justifyContent:"center", gap:8, color:C.dim, fontFamily:"'Josefin Sans',sans-serif", fontSize:11, letterSpacing:"0.08em" }}>
              <span style={{ fontSize:16 }}>+</span>{lang==="fr"?"Ajouter une capture":"Add screenshot"}
            </div>
          )}
        </label>
      </div>

      {/* ── Badges ── */}
      {(() => {
        const allBadges = [...new Map(trades.flatMap(t=>t.tags||[]).map(b=>[parseBadge(b).name, b])).values()];
        const addBadge = (rawName, pol) => {
          const name = rawName.trim().toLowerCase().replace(/\s+/g,"-");
          if (!name || form.tags.some(t=>parseBadge(t).name===name)) return;
          const badge = pol && pol!=="neutral" ? `${name}|${pol}` : name;
          set("tags", [...form.tags, badge]);
          setTagInput(""); setTagPolarity("neutral");
        };
        const polBtns = [
          { k:"neg", label:"−", color:clrRed, bg:"rgba(248,113,113,0.15)", border:"rgba(248,113,113,0.4)" },
          { k:"neutral", label:"◆", color:C.gray1, bg:"transparent", border:C.border },
          { k:"pos", label:"+", color:clrGreen, bg:"rgba(74,222,128,0.15)", border:"rgba(74,222,128,0.4)" },
        ];
        return (
          <Field label="Badges">
            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:form.tags.length?8:0 }}>
              {form.tags.map(tag => {
                const bc = badgeDisplayStyle(tag); const {name} = parseBadge(tag);
                return (
                  <span key={tag} style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"4px 11px", borderRadius:20, background:bc.bg, border:`1px solid ${bc.border}`, fontSize:11, color:bc.color, fontFamily:"'Josefin Sans',sans-serif", fontWeight:500, letterSpacing:"0.04em" }}>
                    {name}
                    <button onClick={() => set("tags", form.tags.filter(t=>t!==tag))} style={{ background:"none", border:"none", color:bc.color, opacity:0.6, cursor:"pointer", fontSize:13, lineHeight:1, padding:0, marginLeft:2 }}>×</button>
                  </span>
                );
              })}
            </div>
            <div style={{ display:"flex", gap:6, marginBottom:6 }}>
              {polBtns.map(p => (
                <button key={p.k} onClick={() => setTagPolarity(p.k)} style={{ flex:1, padding:"6px", borderRadius:6, border:`1px solid ${tagPolarity===p.k?p.border:C.border}`, background:tagPolarity===p.k?p.bg:"transparent", color:tagPolarity===p.k?p.color:C.gray2, fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, cursor:"pointer", transition:"all 0.15s" }}>
                  {p.k==="neg"?(lang==="fr"?"− Négatif":"− Negative"):p.k==="pos"?(lang==="fr"?"+ Positif":"+ Positive"):(lang==="fr"?"Neutre":"Neutral")}
                </button>
              ))}
            </div>
            <input
              type="text" value={tagInput}
              placeholder={lang==="fr"?"Nom du badge... (Entrée pour ajouter)":"Badge name... (Enter to add)"}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => { if((e.key==="Enter"||e.key===",")&&tagInput.trim()){e.preventDefault();addBadge(tagInput,tagPolarity);} }}
              onBlur={() => { if(tagInput.trim()) addBadge(tagInput,tagPolarity); }}
              style={{ ...iStyle, textAlign:"left" }}
            />
            {allBadges.filter(b=>!form.tags.some(t=>parseBadge(t).name===parseBadge(b).name)&&(!tagInput||parseBadge(b).name.includes(tagInput.toLowerCase()))).slice(0,6).length > 0 && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginTop:6 }}>
                {allBadges.filter(b=>!form.tags.some(t=>parseBadge(t).name===parseBadge(b).name)&&(!tagInput||parseBadge(b).name.includes(tagInput.toLowerCase()))).slice(0,6).map(b => {
                  const bc = badgeDisplayStyle(b); const {name} = parseBadge(b);
                  return <button key={b} onClick={() => addBadge(name, parseBadge(b).pol)} style={{ padding:"3px 10px", borderRadius:20, border:`1px solid ${bc.border}`, background:bc.bg, color:bc.color, fontSize:10, fontFamily:"'Josefin Sans',sans-serif", cursor:"pointer", opacity:0.75 }}>{name}</button>;
                })}
              </div>
            )}
          </Field>
        );
      })()}

      {propfirms.filter(p=>!p.status||p.status==="active").length > 0 && (
        <Field label={L.form.accounts}>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {propfirms.filter(p=>!p.status||p.status==="active").map(pf => {
              const isMt5 = !!pf.metaapi_id;
              const selected = form.accountIds.includes(pf.id);
              if (isMt5) {
                return (
                  <div key={pf.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"11px 14px", borderRadius:6, border:`1px solid ${C.border}`, background:"transparent", opacity:0.45 }}>
                    <div style={{ textAlign:"left" }}>
                      <div style={{ fontSize:13, fontFamily:"'Josefin Sans',sans-serif", fontWeight:300, color:C.white, letterSpacing:"0.05em" }}>{pf.firm||L.acct.ownFunds}</div>
                      {pf.name && <div style={{ fontSize:10, color:C.gray1, fontFamily:"'Josefin Sans',sans-serif", marginTop:2 }}>{pf.name}</div>}
                    </div>
                    <div style={{ padding:"3px 8px", borderRadius:20, background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.10)", flexShrink:0 }}>
                      <span style={{ fontSize:8, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, color:"rgba(255,255,255,0.35)", letterSpacing:"0.12em", textTransform:"uppercase", whiteSpace:"nowrap" }}>MT5 SYNC</span>
                    </div>
                  </div>
                );
              }
              return (
                <button key={pf.id} onClick={() => set("accountIds", selected ? form.accountIds.filter(id=>id!==pf.id) : [...form.accountIds, pf.id])} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"11px 14px", borderRadius:6, border:`1px solid ${selected?C.accent:C.border}`, background:selected?"rgba(0,0,0,0.06)":"transparent", cursor:"pointer", transition:"all 0.15s" }}>
                  <div style={{ textAlign:"left" }}>
                    <div style={{ fontSize:13, fontFamily:"'Josefin Sans',sans-serif", fontWeight:selected?600:300, color:selected?C.accent:C.white, letterSpacing:"0.05em" }}>{pf.firm||L.acct.ownFunds}</div>
                    {pf.name && <div style={{ fontSize:10, color:C.gray1, fontFamily:"'Josefin Sans',sans-serif", marginTop:2 }}>{pf.name}</div>}
                  </div>
                  <div style={{ width:18, height:18, borderRadius:4, border:`1px solid ${selected?C.accent:C.gray2}`, background:selected?C.accent:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    {selected && <span style={{ color:"#fff", fontSize:11, lineHeight:1 }}>✓</span>}
                  </div>
                </button>
              );
            })}
          </div>
          <div style={{ fontSize:10, color:C.gray1, fontFamily:"'Josefin Sans',sans-serif", marginTop:6 }}>
            {lang==="fr" ? "Sélectionne plusieurs comptes pour le copy trading" : "Select multiple accounts for copy trading"}
          </div>
        </Field>
      )}

      {strategies.length > 0 ? (
        <Field label={L.form.stratUsed}>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {strategies.map(s => {
              const sel = form.strategyId === s.id;
              return (
                <button key={s.id} onClick={() => set("strategyId", sel ? null : s.id)} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"11px 14px", borderRadius:6, border:`1px solid ${sel?C.accent:C.border}`, background:sel?"rgba(0,0,0,0.06)":"transparent", cursor:"pointer" }}>
                  <span style={{ fontSize:13, fontFamily:"'Josefin Sans',sans-serif", fontWeight:sel?600:300, color:sel?C.accent:C.white, letterSpacing:"0.05em" }}>{s.name||"Stratégie"}</span>
                  <div style={{ width:18, height:18, borderRadius:4, border:`1px solid ${sel?C.accent:C.gray2}`, background:sel?C.accent:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{sel && <span style={{ color:darkMode?"#111":"#fff", fontSize:11 }}>✓</span>}</div>
                </button>
              );
            })}
          </div>
        </Field>
      ) : (
        <button onClick={() => setView("strategy")} style={{ width:"100%", display:"flex", alignItems:"center", gap:12, padding:"13px 16px", borderRadius:8, border:`1px dashed ${C.border}`, background:"transparent", cursor:"pointer", marginBottom:16, transition:"all 0.2s" }}
          onMouseEnter={e => e.currentTarget.style.borderColor = C.gray1}
          onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
          <span style={{ fontSize:16, color:C.gray2 }}>◈</span>
          <div style={{ textAlign:"left" }}>
            <div style={{ fontSize:11, color:C.white, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.08em" }}>Créer une stratégie</div>
            <div style={{ fontSize:10, color:C.gray1, fontFamily:"'Josefin Sans',sans-serif", fontWeight:300, marginTop:2 }}>Améliore l'analyse IA et le suivi de tes trades</div>
          </div>
          <span style={{ marginLeft:"auto", fontSize:11, color:C.gray2 }}>→</span>
        </button>
      )}
      <button onClick={addTrade} disabled={computedPnl() === null} style={{ width:"100%", padding:"14px", borderRadius:4, border:"none", background:computedPnl() !== null ? C.accent : C.gray3, color:computedPnl() !== null ? (darkMode?"#111":"#fff") : C.gray2, fontSize:12, fontWeight:600, fontFamily:"'Josefin Sans',sans-serif", letterSpacing:"0.2em", textTransform:"uppercase", cursor:computedPnl() !== null ? "pointer" : "not-allowed", transition:"all 0.3s" }}>
        {saved ? L.btn.recorded : `${L.btn.record}  →`}
      </button>

      {/* ── Import CSV — desktop only ── */}
      {!isMobile && <>
        <button onClick={() => { setShowCsvImport(v => !v); setCsvResult(null); setCsvError(""); setCsvText(""); }} style={{ width:"100%", marginTop:12, padding:"11px", borderRadius:8, border:`1px solid ${C.border}`, background:"transparent", color:C.gray1, fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.15em", textTransform:"uppercase", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
          <span style={{ fontSize:13 }}>↑</span> {lang === "fr" ? "Importer via CSV" : "Import via CSV"} {showCsvImport ? "▲" : "▼"}
        </button>
        {showCsvImport && (
          <div style={{ marginTop:10, padding:"16px", background:C.bg2, border:`1px solid ${C.border}`, borderRadius:12 }}>
            <div style={{ fontSize:11, color:C.gray1, fontFamily:"'Josefin Sans',sans-serif", lineHeight:1.7, marginBottom:14 }}>{L.tools.csvDesc}</div>
            <Field label={L.tools.platform}>
              <div style={{ display:"flex", gap:8 }}>
                {[{k:"mt5",l:"MT4 / MT5"},{k:"tradovate",l:"Tradovate"},{k:"ninjatrader",l:"NinjaTrader"}].map(p => (
                  <button key={p.k} onClick={() => setCsvPlatform(p.k)} style={{ flex:1, padding:"9px", borderRadius:8, border:`1px solid ${csvPlatform===p.k ? C.accent : C.border}`, background:csvPlatform===p.k ? "rgba(255,255,255,0.06)" : "transparent", color:csvPlatform===p.k ? C.white : C.gray1, fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight:csvPlatform===p.k ? 600 : 300, cursor:"pointer" }}>{p.l}</button>
                ))}
              </div>
              <div style={{ fontSize:10, color:C.dim, fontFamily:"'Josefin Sans',sans-serif", marginTop:6, lineHeight:1.6 }}>
                {csvPlatform==="mt5" ? L.csv.mt5Hint : csvPlatform==="ninjatrader" ? L.csv.ninjaHint : L.csv.tradovateHint}
              </div>
            </Field>
            <Field label={L.tools.csvContent}>
              <textarea rows={6} placeholder={L.tools.csvPH} value={csvText} onChange={e => setCsvText(e.target.value)} style={{ ...iStyle, resize:"vertical", lineHeight:1.5, fontSize:12, textAlign:"left" }} />
            </Field>
            {csvError && <div style={{ fontSize:11, color:"#e74c3c", fontFamily:"'Josefin Sans',sans-serif", marginBottom:10 }}>{csvError}</div>}
            {csvResult ? (
              <div>
                <div style={{ fontSize:12, color:C.white, fontFamily:"'Josefin Sans',sans-serif", marginBottom:12 }}>
                  {lang === "fr" ? `${csvResult.trades.length} trades détectés` : `${csvResult.trades.length} trades detected`}
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={() => { setShowCsvImport(false); setCsvResult(null); setCsvText(""); }} style={{ flex:1, padding:"11px", borderRadius:4, border:`1px solid ${C.border}`, background:"transparent", color:C.gray1, fontSize:11, fontFamily:"'Josefin Sans',sans-serif", cursor:"pointer" }}>{lang === "fr" ? "Annuler" : "Cancel"}</button>
                  <button onClick={() => confirmImport()} style={{ flex:2, padding:"11px", borderRadius:4, border:"none", background:C.accent, color:darkMode?"#111":"#fff", fontSize:12, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.1em", textTransform:"uppercase", cursor:"pointer" }}>{L.tools.importBtn}</button>
                </div>
              </div>
            ) : (
              <button onClick={() => importCSV()} style={{ width:"100%", padding:"13px", borderRadius:4, border:"none", background:C.accent, color:darkMode?"#111":"#fff", fontSize:12, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.15em", textTransform:"uppercase", cursor:"pointer" }}>{L.tools.analyzeBtn}</button>
            )}
          </div>
        )}
      </>}
    </div>
  );

  // ── History JSX ──
  const historyContent = (
    <div>
      <div style={{ display:"flex", alignItems:isMobile?"flex-start":"flex-start", justifyContent:"space-between", flexDirection:isMobile?"column":"row", gap:isMobile?8:0 }}>
        <PageTitle sub={L.pages.stats.sub} title={L.pages.stats.title} />
        <div style={{ display:"flex", gap:6, flexShrink:0, flexWrap:"wrap" }}>
          <button onClick={() => setWidgetsOpen(o=>!o)} style={{ padding:"7px 12px", borderRadius:8, border:`1px solid ${widgetsOpen?C.accent:C.border}`, background:widgetsOpen?"rgba(232,205,169,0.08)":"transparent", color:widgetsOpen?C.accent:C.gray1, fontSize:10, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.1em", cursor:"pointer", transition:"all 0.15s", whiteSpace:"nowrap" }}>
            {lang==="fr"?"⊞ Widgets":"⊞ Widgets"}
          </button>
          <button onClick={() => { const n=new Date(); const y=n.getFullYear(),m=String(n.getMonth()+1).padStart(2,"0"),d=String(n.getDate()).padStart(2,"0"); setExportFrom(`${y}-${m}-01`); setExportTo(`${y}-${m}-${d}`); setShowExportModal(true); }} style={{ padding:"7px 12px", borderRadius:8, border:`1px solid ${C.border}`, background:"transparent", color:C.gray1, fontSize:10, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.1em", cursor:"pointer", transition:"all 0.15s", whiteSpace:"nowrap" }}>
            Export PDF
          </button>
        </div>
      </div>

      {widgetsOpen && (() => {
        const WIDGETS = [
          { key:"comparison", label:lang==="fr"?"Comparaison":"Comparison" },
          { key:"rr",         label:"Distribution R-Multiple" },
          { key:"emotions",   label:lang==="fr"?"Matrice émotionnelle":"Emotion matrix" },
          { key:"streaks",    label:lang==="fr"?"Séries & Surtrading":"Streaks & Overtrading" },
          { key:"montecarlo", label:"Monte Carlo" },
          { key:"sections",   label:lang==="fr"?"Perf. Session/Instrument/Tag":"Session/Instrument/Tag perf." },
          { key:"hourly",     label:lang==="fr"?"Analyse horaire":"Hourly analysis" },
          { key:"heatmap",    label:"Heatmap" },
          { key:"bestday",    label:lang==="fr"?"Meilleur jour":"Best day of week" },
          { key:"recovery",   label:lang==="fr"?"Recovery Factor":"Recovery Factor" },
          { key:"stoptime",   label:lang==="fr"?"Heure limite optimale":"Optimal stop time" },
          { key:"revenge",    label:lang==="fr"?"Revenge trading":"Revenge trading" },
          { key:"setup",      label:lang==="fr"?"Setup scoring":"Setup scoring" },
        ];
        const toggle = (k) => {
          const next = { ...statsWidgets, [k]: !statsWidgets[k] };
          setStatsWidgets(next);
          localStorage.setItem("fyltra_widgets", JSON.stringify(next));
        };
        return (
          <div style={{ background:C.bg2, border:`1px solid ${C.accent}`, borderRadius:12, padding:"14px 16px", marginBottom:16, boxShadow:cShadow }}>
            <div style={{ fontSize:9, color:C.dim, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:10 }}>{lang==="fr"?"Afficher / masquer les blocs":"Show / hide blocks"}</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {WIDGETS.map(w => (
                <button key={w.key} onClick={() => toggle(w.key)} style={{ padding:"5px 12px", borderRadius:20, border:`1px solid ${statsWidgets[w.key]?C.accent:C.border}`, background:statsWidgets[w.key]?"rgba(232,205,169,0.08)":"transparent", color:statsWidgets[w.key]?C.accent:C.gray2, fontSize:10, fontFamily:"'Josefin Sans',sans-serif", cursor:"pointer", transition:"all 0.15s", display:"flex", alignItems:"center", gap:5 }}>
                  <div style={{ width:5, height:5, borderRadius:"50%", background:statsWidgets[w.key]?C.accent:C.gray3 }}/>
                  {w.label}
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ══════════════════ COURBES D'ÉQUITÉ PROP FIRMS ══════════════════ */}
      {propfirms.filter(p=>!p.status||p.status==="active").length>0&&(()=>{
        const activePfs=propfirms.filter(p=>!p.status||p.status==="active");
        return (
          <div style={{marginBottom:24}}>
            <div style={{fontSize:9,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.18em",textTransform:"uppercase",marginBottom:10}}>{lang==="fr"?"Courbes d'équité":"Equity curves"}</div>
            <div style={{display:"grid",gridTemplateColumns:`repeat(auto-fill, minmax(${isMobile?200:250}px, 1fr))`,gap:12}}>
              {activePfs.map(pf=>{
                const cap=parseFloat(pf.capital)||0;
                const target=parseFloat(pf.target)||0;
                const maxLoss=parseFloat(pf.maxLoss)||0;
                const pfTrades=trades.filter(t=>!t.accountIds||t.accountIds.length===0||t.accountIds.includes(pf.id));
                const sorted=[...pfTrades].sort((a,b)=>a.date.localeCompare(b.date));
                let cum=0;
                const chartData=[{v:0,d:"",inst:""},...sorted.map(t=>{cum+=t.pnl||0;return{v:parseFloat(cum.toFixed(2)),d:t.date,inst:t.instrument||""};})];
                const pnl=pfTrades.reduce((s,t)=>s+(t.pnl||0),0);
                const isProfit=pnl>=0;
                const cVals=chartData.map(d=>d.v);
                const cMax=Math.max(...cVals), cMin=Math.min(...cVals);
                const czp=cMax===cMin?100:Math.min(100,Math.max(0,cMax/(cMax-cMin)*100));
                const cgid=`cseq_${pf.id}`;
                const peakPnl=(()=>{
                  const dates=[...new Set(pfTrades.map(t=>t.date))].sort();
                  let c=0,pk=0;
                  dates.forEach(d=>{c+=pfTrades.filter(t=>t.date===d).reduce((s,t)=>s+(t.pnl||0),0);if(c>pk)pk=c;});
                  return pk;
                })();
                const mll=maxLoss>0?(pf.trailingDD?cap+Math.max(0,peakPnl)-maxLoss:cap-maxLoss):null;
                const tgt=target>0?cap+target:null;
                const totalRange=(mll!==null&&tgt!==null)?tgt-mll:0;
                const currentCap=cap+pnl;
                const startPct=totalRange?(cap-mll)/totalRange*100:50;
                const currentPct=totalRange?Math.min(100,Math.max(0,(currentCap-mll)/totalRange*100)):50;
                const fillLeft=isProfit?startPct:currentPct;
                const fillWidth=Math.abs(currentPct-startPct);
                const inDanger=mll!==null&&(cap+pnl)<=mll+maxLoss*0.1;
                return (
                  <div key={pf.id} onClick={()=>{setSelectedPf(pf);setView("propfirm");}} style={{flexShrink:0,width:!isMobile?260:220,background:C.bg2,border:`1px solid ${inDanger?"rgba(192,57,43,0.3)":C.border}`,borderRadius:12,padding:"14px 16px",cursor:"pointer",boxShadow:cShadow,transition:"border 0.2s"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                      <div>
                        <div style={{fontFamily:"'Barlow',sans-serif",fontWeight:600,fontSize:13,color:C.white,letterSpacing:"0.05em"}}>{pf.firm||(lang==="fr"?"Fond Propre":"Personal Account")}</div>
                        {pf.name&&<div style={{fontSize:9,color:C.gray2,fontFamily:"'Josefin Sans',sans-serif",marginTop:1}}>{pf.name}</div>}
                      </div>
                      <div style={{fontFamily:"'Josefin Sans',sans-serif",fontWeight:300,fontSize:14,color:isProfit?clrGreen:clrRed}}>{fms(pnl)}{currency}</div>
                    </div>
                    {/* Courbe */}
                    <div style={{marginBottom:totalRange>0?10:0}}>
                      <ResponsiveContainer width="100%" height={110}>
                        <AreaChart data={chartData} margin={{top:6,right:2,left:0,bottom:0}}>
                          <defs>
                            <linearGradient id={cgid} x1="0" y1="0" x2="0" y2="1">
                              <stop offset={`${czp}%`} stopColor="#4caf6e" stopOpacity={1}/>
                              <stop offset={`${czp}%`} stopColor="#e05a5a" stopOpacity={1}/>
                            </linearGradient>
                            <linearGradient id={`${cgid}f`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset={`${czp}%`} stopColor="#4caf6e" stopOpacity={0.15}/>
                              <stop offset={`${czp}%`} stopColor="#e05a5a" stopOpacity={0.15}/>
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="d" tick={{fontSize:8,fontFamily:"'Josefin Sans',sans-serif",fill:C.dim}} tickLine={false} axisLine={false} interval="preserveStartEnd" tickFormatter={v=>v?v.slice(5):""}/>
                          <ReferenceLine y={0} stroke={darkMode?"rgba(255,255,255,0.12)":"rgba(0,0,0,0.12)"} strokeWidth={1} strokeDasharray="3 3"/>
                          <Tooltip contentStyle={{background:darkMode?"rgba(14,14,14,0.96)":"rgba(255,255,255,0.96)",border:`1px solid ${C.border}`,borderRadius:7,fontSize:10,color:C.white,padding:"5px 10px"}} formatter={v=>[`${fms(v)}${currency}`,""]} labelFormatter={(_,pl)=>pl?.[0]?.payload?.inst||""}/>
                          <Area type="monotone" dataKey="v" stroke={`url(#${cgid})`} strokeWidth={2} fill={`url(#${cgid}f)`} dot={{r:2.5,fill:`url(#${cgid})`,strokeWidth:0}} activeDot={{r:4,strokeWidth:0}}/>
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    {/* Jauge MLL→TARGET */}
                    {totalRange>0&&(
                      <div>
                        <div style={{position:"relative",height:3,background:darkMode?"rgba(255,255,255,0.08)":C.gray3,borderRadius:2,marginBottom:5}}>
                          <div style={{position:"absolute",left:fillLeft+"%",width:fillWidth+"%",height:"100%",borderRadius:2,background:isProfit?"rgba(74,222,128,0.65)":"rgba(229,100,100,0.65)"}}/>
                          <div style={{position:"absolute",left:startPct+"%",transform:"translateX(-50%)",width:1.5,height:9,background:C.dim,borderRadius:1,top:-3}}/>
                          <div style={{position:"absolute",left:currentPct+"%",transform:"translateX(-50%)",width:2.5,height:8,background:isProfit?clrGreen:"#e05a5a",borderRadius:1,top:-2.5,transition:"left 0.5s",boxShadow:isProfit?"0 0 4px rgba(74,222,128,0.8)":"0 0 4px rgba(229,100,100,0.8)"}}/>
                        </div>
                        <div style={{display:"flex",justifyContent:"space-between"}}>
                          <span style={{fontSize:8,color:"rgba(229,100,100,0.55)",fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.08em",textTransform:"uppercase"}}>MLL</span>
                          <span style={{fontSize:8,color:"rgba(74,222,128,0.55)",fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.08em",textTransform:"uppercase"}}>TARGET</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── Filtre par compte ── */}
      {propfirms.length > 0 && (
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:16 }}>
          {[{ id:"all", label:L.btn.all }, ...propfirms.map(p => ({ id:p.id, label:p.name || p.firm || (lang==="fr"?"Compte":"Account") }))].map(opt => (
            <button key={opt.id} onClick={() => setHistoryAccFilter(opt.id)} style={{
              padding:"5px 14px", borderRadius:20, fontSize:10, cursor:"pointer",
              fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.1em", textTransform:"uppercase",
              border:`1px solid ${historyAccFilter === opt.id ? C.accent : C.gray3}`,
              background: historyAccFilter === opt.id ? "rgba(232,205,169,0.1)" : "transparent",
              color: historyAccFilter === opt.id ? C.accent : C.gray1,
              transition:"all 0.15s",
            }}>{opt.label}</button>
          ))}
        </div>
      )}

      {/* ── OBJECTIFS SEMAINE ── */}
      {(() => {
        const ff = "'Josefin Sans',sans-serif";
        const now = new Date();
        const dow = (now.getDay()+6)%7;
        const weekStart = new Date(now); weekStart.setDate(now.getDate()-dow); weekStart.setHours(0,0,0,0);
        const weekEnd   = new Date(weekStart); weekEnd.setDate(weekStart.getDate()+6); weekEnd.setHours(23,59,59,999);
        const wTrades = trades.filter(t=>{ const d=new Date(t.date+"T12:00:00"); return d>=weekStart&&d<=weekEnd; });
        const wPnl = wTrades.reduce((s,t)=>s+(t.pnl||0),0);
        const wWr  = wTrades.length ? Math.round(wTrades.filter(t=>t.result==="WIN").length/wTrades.length*100) : 0;
        const wCnt = wTrades.length;
        const goals = weeklyGoals;
        const hasAnyGoal = goals.pnl||goals.wr||goals.trades;
        const saveGoals = (g) => { setWeeklyGoals(g); localStorage.setItem("fyltra_wgoals", JSON.stringify(g)); if (user) saveUserSettings({ weekly_goals: g }); };
        const goalItems = [
          goals.pnl    && { label:lang==="fr"?"PnL cible":"Target PnL",  value:wPnl,  target:parseFloat(goals.pnl),    fmt:v=>`${fms(v)}${currency}`, tFmt:v=>`${fm(v)}${currency}`, color:wPnl>=0?clrGreen:clrRed },
          goals.wr     && { label:"Win rate",                             value:wWr,   target:parseFloat(goals.wr),     fmt:v=>`${v}%`, tFmt:v=>`${v}%`, color:C.accent },
          goals.trades && { label:lang==="fr"?"Trades max":"Max trades",  value:wCnt,  target:parseFloat(goals.trades), fmt:v=>`${v}`, tFmt:v=>`${v}`, color:C.accent, invert:true },
        ].filter(Boolean);
        return (
          <div style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:12, padding:"16px 18px", marginBottom:10, boxShadow:cShadow }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:(hasAnyGoal||wGoalsEdit)?12:0 }}>
              <div style={{ fontSize:9, color:C.dim, fontFamily:ff, fontWeight:600, letterSpacing:"0.18em", textTransform:"uppercase" }}>
                {lang==="fr"?"Objectifs de la semaine":"Weekly goals"}
              </div>
              <button onClick={()=>setWGoalsEdit(e=>!e)} style={{ padding:"3px 12px", borderRadius:20, border:`1px solid ${C.border}`, background:"transparent", color:C.gray1, fontSize:10, fontFamily:ff, cursor:"pointer" }}>
                {wGoalsEdit?(lang==="fr"?"Fermer":"Close"):(lang==="fr"?"Configurer":"Configure")}
              </button>
            </div>
            {wGoalsEdit && (
              <div style={{ marginBottom:hasAnyGoal?12:0 }}>
                <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr 1fr":"1fr 1fr 1fr", gap:10, marginBottom:12 }}>
                  {[{key:"pnl",label:lang==="fr"?"PnL cible":"Target PnL",ph:"ex: 500"},{key:"wr",label:"Win rate cible (%)",ph:"ex: 60"},{key:"trades",label:lang==="fr"?"Trades max":"Max trades",ph:"ex: 10"}].map(f=>(
                    <div key={f.key}>
                      <div style={{ fontSize:9, color:C.dim, fontFamily:ff, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:6 }}>{f.label}</div>
                      <input type="text" inputMode="decimal" placeholder={f.ph} value={weeklyGoals[f.key]} onChange={e=>setWeeklyGoals(p=>({...p,[f.key]:e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"")}))} style={{ width:"100%", background:C.bg3, border:`1px solid ${C.border}`, borderRadius:8, padding:"9px 10px", color:C.white, fontSize:12, fontFamily:ff, outline:"none", boxSizing:"border-box" }}/>
                    </div>
                  ))}
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={()=>{saveGoals(weeklyGoals);setWGoalsEdit(false);}} style={{ padding:"8px 20px", borderRadius:8, border:"none", background:darkMode?"rgba(255,255,255,0.9)":"#111", color:darkMode?"#111":"#fff", fontSize:11, fontFamily:ff, fontWeight:700, cursor:"pointer", letterSpacing:"0.1em" }}>{lang==="fr"?"Sauvegarder":"Save"}</button>
                  <button onClick={()=>{const empty={pnl:"",wr:"",trades:""};saveGoals(empty);setWGoalsEdit(false);}} style={{ padding:"8px 16px", borderRadius:8, border:`1px solid ${C.border}`, background:"transparent", color:C.gray1, fontSize:11, fontFamily:ff, cursor:"pointer" }}>{lang==="fr"?"Réinitialiser":"Reset"}</button>
                </div>
              </div>
            )}
            {!hasAnyGoal && !wGoalsEdit && (
              <div style={{ fontSize:11, color:C.gray2, fontFamily:ff }}>{lang==="fr"?"Aucun objectif configuré — clique sur Configurer.":"No goals set — click Configure."}</div>
            )}
            {hasAnyGoal && (
              <div>
                <div style={{ fontSize:9, color:C.gray2, fontFamily:ff, marginBottom:12 }}>
                  {lang==="fr"?`Semaine du ${weekStart.toLocaleDateString("fr-FR",{day:"numeric",month:"short"})}`:`Week of ${weekStart.toLocaleDateString("en-US",{month:"short",day:"numeric"})}`}
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                  {goalItems.map(g=>{
                    const pct = g.target>0 ? Math.min(100,Math.max(0,(g.value/g.target)*100)) : 0;
                    const done = g.invert ? g.value<=g.target : g.value>=g.target;
                    return (
                      <div key={g.label}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                          <span style={{ fontSize:10, color:C.dim, fontFamily:ff, letterSpacing:"0.12em", textTransform:"uppercase" }}>{g.label}</span>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <span style={{ fontSize:13, fontWeight:300, color:g.color, fontFamily:ff }}>{g.fmt(g.value)}</span>
                            <span style={{ fontSize:9, color:C.gray2, fontFamily:ff }}>/ {g.tFmt(g.target)}</span>
                            {done && <span style={{ fontSize:9, color:clrGreen, fontFamily:ff, fontWeight:700 }}>✓</span>}
                          </div>
                        </div>
                        <div style={{ height:4, background:C.bg3, borderRadius:2, overflow:"hidden", marginTop:6 }}>
                          <div style={{ height:"100%", width:`${pct}%`, background:done?clrGreen:g.color, borderRadius:2, transition:"width 0.4s ease" }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ══════════════════ COMPARAISON PÉRIODE ══════════════════ */}
      {statsWidgets.comparison && (() => {
        const cmpFiltered = historyAccFilter === "all"
          ? trades
          : trades.filter(t => !t.accountIds || t.accountIds.length === 0 || t.accountIds.includes(historyAccFilter));
        if (cmpFiltered.length === 0) return null;

        const now = new Date();
        let curStart, curEnd, prevStart, prevEnd, curLabel, prevLabel;

        if (cmpPeriod === "week") {
          const dow = (now.getDay() + 6) % 7;
          curStart = new Date(now); curStart.setDate(now.getDate() - dow); curStart.setHours(0,0,0,0);
          curEnd = new Date(now); curEnd.setHours(23,59,59,999);
          prevStart = new Date(curStart); prevStart.setDate(curStart.getDate() - 7);
          prevEnd = new Date(curStart); prevEnd.setDate(curStart.getDate() - 1); prevEnd.setHours(23,59,59,999);
          curLabel = lang === "fr" ? "Cette semaine" : "This week";
          prevLabel = lang === "fr" ? "Semaine dernière" : "Last week";
        } else {
          curStart = new Date(now.getFullYear(), now.getMonth(), 1);
          curEnd = new Date(now); curEnd.setHours(23,59,59,999);
          prevStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          prevEnd = new Date(now.getFullYear(), now.getMonth(), 0); prevEnd.setHours(23,59,59,999);
          curLabel = lang === "fr" ? "Ce mois" : "This month";
          prevLabel = lang === "fr" ? "Mois dernier" : "Last month";
        }

        const inRange = (t, s, e) => { const d = new Date(t.date + "T12:00:00"); return d >= s && d <= e; };
        const curTrades  = cmpFiltered.filter(t => inRange(t, curStart, curEnd));
        const prevTrades = cmpFiltered.filter(t => inRange(t, prevStart, prevEnd));

        const stats = (arr) => {
          const pnl  = arr.reduce((s,t) => s + (t.pnl||0), 0);
          const wins = arr.filter(t => t.result === "WIN").length;
          const wr   = arr.length ? Math.round(wins / arr.length * 100) : 0;
          const byInst = {};
          arr.forEach(t => { byInst[t.instrument] = (byInst[t.instrument]||0) + (t.pnl||0); });
          const bestInst = Object.entries(byInst).sort((a,b) => b[1]-a[1])[0];
          return { pnl, wr, count: arr.length, bestInst: bestInst ? bestInst[0] : "—" };
        };

        const cur  = stats(curTrades);
        const prev = stats(prevTrades);
        const ff   = "'Josefin Sans',sans-serif";
        const cardS = { background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px", flex: 1, boxShadow: "0 4px 20px rgba(0,0,0,0.5)" };

        const Delta = ({ a, b, unit = "" }) => {
          if (b === 0 && a === 0) return null;
          const diff = a - b;
          const pct  = b !== 0 ? Math.round((diff / Math.abs(b)) * 100) : null;
          const pos  = diff >= 0;
          return (
            <span style={{ fontSize: 9, color: pos ? clrGreen : clrRed, fontFamily: ff, marginLeft: 6 }}>
              {pos ? "+" : ""}{unit === currency ? fm(diff) : diff}{unit}{pct !== null ? ` (${pos?"+":""}${pct}%)` : ""}
            </span>
          );
        };

        const Row = ({ label, curVal, prevVal, unit = "", fmt = v => v }) => (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 9, color: C.dim, fontFamily: ff, letterSpacing: "0.12em", textTransform: "uppercase", width: 80 }}>{label}</div>
            <div style={{ textAlign: "right" }}>
              <span style={{ fontSize: 13, fontWeight: 300, color: C.white, fontFamily: ff }}>{fmt(curVal)}{unit}</span>
              <Delta a={typeof curVal === "number" ? curVal : 0} b={typeof prevVal === "number" ? prevVal : 0} unit={unit} />
            </div>
          </div>
        );

        return (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontSize: 9, color: C.dim, fontFamily: "'Josefin Sans',sans-serif", fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                {lang === "fr" ? "Comparaison" : "Comparison"}
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                {[{k:"week",l:lang==="fr"?"Semaine":"Week"},{k:"month",l:lang==="fr"?"Mois":"Month"}].map(o => (
                  <button key={o.k} onClick={() => setCmpPeriod(o.k)} style={{ padding: "3px 12px", borderRadius: 20, border: `1px solid ${cmpPeriod===o.k?C.accent:C.border}`, background: cmpPeriod===o.k?"rgba(232,205,169,0.08)":"transparent", color: cmpPeriod===o.k?C.accent:C.gray1, fontSize: 10, fontFamily: ff, cursor: "pointer", transition: "all 0.15s" }}>{o.l}</button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, flexDirection: isMobile ? "column" : "row" }}>
              {[{label:curLabel,s:cur,isCur:true},{label:prevLabel,s:prev,isCur:false}].map(({label,s,isCur}) => (
                <div key={label} style={{ ...cardS, opacity: isCur ? 1 : 0.7 }}>
                  <div style={{ fontSize: 9, color: isCur ? C.accent : C.gray2, fontFamily: ff, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 12, fontWeight: 600 }}>{label}</div>
                  <div style={{ fontSize: 22, fontWeight: 300, color: s.pnl >= 0 ? clrGreen : clrRed, fontFamily: ff, marginBottom: 2 }}>
                    {}{fms(s.pnl)}{currency}
                  </div>
                  {isCur && prev.pnl !== 0 && <Delta a={cur.pnl} b={prev.pnl} unit={currency} />}
                  <div style={{ height: 1, background: C.border, margin: "12px 0" }} />
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {[
                      { label: lang==="fr"?"Trades":"Trades",    val: s.count,    unit: "" },
                      { label: "Win rate",                         val: s.wr,       unit: "%" },
                      { label: lang==="fr"?"Meilleur":"Best",     val: s.bestInst, unit: "", noNum: true },
                    ].map(row => (
                      <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 9, color: C.dim, fontFamily: ff, letterSpacing: "0.1em", textTransform: "uppercase" }}>{row.label}</span>
                        <span style={{ fontSize: 12, color: C.white, fontFamily: ff, fontWeight: row.noNum ? 600 : 300 }}>{row.noNum ? row.val : `${row.val}${row.unit}`}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ══════════════════ ANALYTICS AVANCÉS ══════════════════ */}
      {(() => {
        const histFiltered = historyAccFilter === "all"
          ? trades
          : trades.filter(t => !t.accountIds || t.accountIds.length === 0 || t.accountIds.includes(historyAccFilter));
        if (histFiltered.length < 3) return histFiltered.length === 0
          ? <div style={{textAlign:"center",padding:"40px 0",color:C.gray2,fontSize:12,fontFamily:"'Josefin Sans',sans-serif"}}>{L.acct.noTradesAccount}</div>
          : null;
        const isDark = C.bg === "#0f0f0f";
        const sorted = [...histFiltered].sort(cmpTrades);
        const cardS = {background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow};
        const lbl = {fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600};
        const ff = "'Josefin Sans',sans-serif";
        const cap = historyAccFilter !== "all"
          ? parseFloat(propfirms.find(p => p.id === historyAccFilter)?.capital) || 0
          : 0;

        // Equity & Drawdown — si jamais positif, référence = capital
        let cum=0,peak=0,maxDD=0;
        sorted.forEach(t=>{
          cum+=t.pnl||0;
          if(cum>peak) peak=cum;
          const ref=peak>0?peak:cap;
          const dd=ref>0?Math.max(0,(ref-cum)/ref*100):0;
          if(dd>maxDD) maxDD=dd;
        });
        const refNow=peak>0?peak:cap;
        const currentDD=refNow>0?Math.max(0,(refNow-cum)/refNow*100):0;

        // Win/Loss
        const wins=sorted.filter(t=>t.result==="WIN");
        const losses=sorted.filter(t=>t.result==="LOSS");
        const wr=sorted.length?wins.length/sorted.length:0;
        const avgWin=wins.length?wins.reduce((s,t)=>s+(t.pnl||0),0)/wins.length:0;
        const avgLoss=losses.length?Math.abs(losses.reduce((s,t)=>s+(t.pnl||0),0)/losses.length):0;
        const grossW=wins.reduce((s,t)=>s+(t.pnl||0),0);
        const grossL=Math.abs(losses.reduce((s,t)=>s+(t.pnl||0),0));
        const pf=grossL>0?grossW/grossL:grossW>0?99:0;

        // Expectancy, Kelly
        const expectancy=(wr*avgWin)-((1-wr)*avgLoss);
        const kelly=avgLoss>0&&avgWin>0?((wr-(1-wr)/(avgWin/avgLoss))*100):0;

        // Consistency (% semaines positives)
        const weekMap={};
        sorted.forEach(t=>{
          const d=new Date(t.date+"T12:00:00");
          const key=`${d.getFullYear()}-${d.getMonth()}-W${Math.ceil(d.getDate()/7)}`;
          weekMap[key]=(weekMap[key]||0)+(t.pnl||0);
        });
        const wVals=Object.values(weekMap);
        const consistency=wVals.length?Math.round(wVals.filter(v=>v>0).length/wVals.length*100):0;

        // Sharpe simplifié (daily)
        const dayMap={};
        sorted.forEach(t=>{ dayMap[t.date]=(dayMap[t.date]||0)+(t.pnl||0); });
        const dVals=Object.values(dayMap);
        const avgD=dVals.reduce((s,v)=>s+v,0)/dVals.length;
        const stdD=Math.sqrt(dVals.reduce((s,v)=>s+(v-avgD)**2,0)/dVals.length)||1;
        const sharpe=avgD/stdD;

        // Streaks
        let maxWS=0,maxLS=0,tmpS=0,tmpT=null;
        sorted.forEach(t=>{
          if(t.result==="BREAKEVEN") return;
          const tp=t.result==="WIN"?"W":"L";
          if(tp===tmpT){tmpS++;}else{tmpS=1;tmpT=tp;}
          if(tp==="W"&&tmpS>maxWS) maxWS=tmpS;
          if(tp==="L"&&tmpS>maxLS) maxLS=tmpS;
        });
        let curS=0,curST=null;
        for(let i=sorted.length-1;i>=0;i--){
          const t=sorted[i];
          if(t.result==="BREAKEVEN") continue;
          const tp=t.result==="WIN"?"W":"L";
          if(curST===null){curST=tp;curS=1;}
          else if(tp===curST){curS++;}
          else break;
        }

        // Overtrading
        const avgTPD=sorted.length/Object.keys(dayMap).length;
        const dayDetails=Object.entries(dayMap).map(([date,pnl])=>({date,pnl,count:sorted.filter(t=>t.date===date).length}));
        const otDays=dayDetails.filter(d=>d.count>avgTPD*1.5).sort((a,b)=>b.count-a.count).slice(0,3);

        // R-Multiple distribution
        const rrBuckets=[
          {label:"< 0",count:0,color:"#e05a5a"},
          {label:"0–1",count:0,color:"#e0884a"},
          {label:"1–2",count:0,color:"#d4c060"},
          {label:"2–3",count:0,color:"#4caf6e"},
          {label:"3+", count:0,color:"#2a9d5c"},
        ];
        sorted.forEach(t=>{
          const r=parseFloat(t.rr);
          if(isNaN(r)) return;
          if(t.result==="LOSS"||r<0) rrBuckets[0].count++;
          else if(r<1) rrBuckets[1].count++;
          else if(r<2) rrBuckets[2].count++;
          else if(r<3) rrBuckets[3].count++;
          else rrBuckets[4].count++;
        });
        const rrTotal=rrBuckets.reduce((s,b)=>s+b.count,0);
        const rrTrades=sorted.filter(t=>parseFloat(t.rr)>0);
        const avgRR=rrTrades.length?rrTrades.reduce((s,t)=>s+parseFloat(t.rr),0)/rrTrades.length:1;
        const breakEvenWR=avgRR>0?Math.round(1/(1+avgRR)*100):50;

        // Matrice émotionnelle
        const emoList=[...new Set(sorted.map(t=>t.emotion).filter(Boolean))];
        const emoData=emoList.map(e=>{
          const et=sorted.filter(t=>t.emotion===e);
          const ew=et.filter(t=>t.result==="WIN").length;
          return{e,count:et.length,wr:et.length?Math.round(ew/et.length*100):0,pnl:et.reduce((s,t)=>s+(t.pnl||0),0)};
        }).sort((a,b)=>b.wr-a.wr);

        // Monte Carlo — seedé sur les données pour résultat stable entre renders
        const MC=300,MCT=50;
        let mcSeed=sorted.length*9301+Math.round(Math.abs(cum)*100+avgWin*37+avgLoss*53)&0x7fffffff;
        const mcRand=()=>{ mcSeed=(mcSeed*1664525+1013904223)&0x7fffffff; return mcSeed/0x7fffffff; };
        const mcR=[];
        for(let p=0;p<MC;p++){let eq=0;for(let i=0;i<MCT;i++){eq+=mcRand()<wr?avgWin:-avgLoss;}mcR.push(eq);}
        mcR.sort((a,b)=>a-b);
        const mcP10=mcR[Math.floor(MC*0.1)];
        const mcP50=mcR[Math.floor(MC*0.5)];
        const mcP90=mcR[Math.floor(MC*0.9)];
        const mcWin=Math.round(mcR.filter(v=>v>0).length/MC*100);

        // ── INSIGHT CLÉ ──
        const sessMap={};
        sorted.forEach(t=>{
          if(!t.session) return;
          if(!sessMap[t.session]) sessMap[t.session]={wins:0,total:0,pnl:0};
          sessMap[t.session].total++;
          sessMap[t.session].pnl+=t.pnl||0;
          if(t.result==="WIN") sessMap[t.session].wins++;
        });
        const sessList=Object.entries(sessMap).filter(([,v])=>v.total>=3).map(([name,v])=>({name,wr:Math.round(v.wins/v.total*100),pnl:v.pnl,count:v.total}));
        const bestSess=sessList.sort((a,b)=>b.wr-a.wr)[0];
        const worstSess=[...sessList].sort((a,b)=>a.wr-b.wr)[0];

        const instrMap={};
        sorted.forEach(t=>{
          if(!t.instrument) return;
          if(!instrMap[t.instrument]) instrMap[t.instrument]={wins:0,total:0,pnl:0};
          instrMap[t.instrument].total++;
          instrMap[t.instrument].pnl+=t.pnl||0;
          if(t.result==="WIN") instrMap[t.instrument].wins++;
        });
        const instrList=Object.entries(instrMap).filter(([,v])=>v.total>=3).map(([name,v])=>({name,wr:Math.round(v.wins/v.total*100),pnl:v.pnl,count:v.total}));
        const bestInstr=instrList.sort((a,b)=>b.pnl-a.pnl)[0];
        const worstInstr=[...instrList].sort((a,b)=>a.pnl-b.pnl)[0];

        const dowMap={};
        const dowNames=lang==="en"?["Sun","Mon","Tue","Wed","Thu","Fri","Sat"]:["Dim","Lun","Mar","Mer","Jeu","Ven","Sam"];
        sorted.forEach(t=>{
          const d=new Date(t.date+"T12:00:00").getDay();
          if(!dowMap[d]) dowMap[d]={wins:0,total:0,pnl:0,name:dowNames[d]};
          dowMap[d].total++;
          dowMap[d].pnl+=t.pnl||0;
          if(t.result==="WIN") dowMap[d].wins++;
        });
        const dowList=Object.values(dowMap).filter(v=>v.total>=3).map(v=>({...v,wr:Math.round(v.wins/v.total*100)}));
        const bestDow=dowList.sort((a,b)=>b.wr-a.wr)[0];
        const worstDow=[...dowList].sort((a,b)=>a.wr-b.wr)[0];

        const avgDailyTrades=sorted.length/Math.max(1,Object.keys(dayMap).length);
        const overtradingDays=Object.entries(dayMap).filter(([d,])=>sorted.filter(t=>t.date===d).length>avgDailyTrades*1.5);
        const overtradingLossDays=overtradingDays.filter(([,pnl])=>pnl<0);
        const overtradingLossPct=overtradingDays.length>0?Math.round(overtradingLossDays.length/overtradingDays.length*100):0;

        const candidates=[];
        if(curS>=3) candidates.push({priority:curS>=5?10:7,positive:curST==="W",headline:curST==="W"?(lang==="fr"?`${curS} wins de suite en cours`:`${curS} wins in a row`):(lang==="fr"?`${curS} pertes de suite en cours`:`${curS} losses in a row`),body:curST==="W"?(lang==="fr"?`Belle dynamique — reste dans ta stratégie et ne surtrading pas.`:`Good momentum — stick to your strategy and don't overtrade.`):(lang==="fr"?`Pause recommandée — revois tes entrées avant le prochain trade.`:`Break recommended — review your entries before the next trade.`)});
        if(emoData.length>=2&&emoData[0].count>=3&&emoData[0].wr-emoData[emoData.length-1].wr>=20) candidates.push({priority:6,positive:true,headline:`"${L.emotions?.[emoData[0].e]||emoData[0].e}" → ${emoData[0].wr}% win rate`,body:lang==="fr"?`Ton état émotionnel le plus rentable. À l'inverse "${L.emotions?.[emoData[emoData.length-1].e]||emoData[emoData.length-1].e}" ne dépasse pas ${emoData[emoData.length-1].wr}% WR.`:`Your most profitable emotional state. Conversely "${L.emotions?.[emoData[emoData.length-1].e]||emoData[emoData.length-1].e}" stays below ${emoData[emoData.length-1].wr}% WR.`});
        if(bestSess&&worstSess&&bestSess.name!==worstSess.name&&bestSess.wr-worstSess.wr>=20) candidates.push({priority:5,positive:true,headline:`Session ${bestSess.name} = ${bestSess.wr}% WR`,body:lang==="fr"?`Ta session la plus profitable (${bestSess.count} trades). Évite de trader en session ${worstSess.name} : ${worstSess.wr}% WR.`:`Your most profitable session (${bestSess.count} trades). Avoid trading ${worstSess.name} session: ${worstSess.wr}% WR.`});
        if(bestInstr&&worstInstr&&bestInstr.name!==worstInstr.name) candidates.push({priority:4,positive:bestInstr.pnl>0,headline:`${bestInstr.name} = ${fms(bestInstr.pnl)}${currency}`,body:lang==="fr"?`Ton instrument le plus rentable sur ${bestInstr.count} trades. ${worstInstr.name} est le moins performant (${fms(worstInstr.pnl)}${currency}).`:`Your most profitable instrument over ${bestInstr.count} trades. ${worstInstr.name} is the weakest (${fms(worstInstr.pnl)}${currency}).`});
        if(bestDow&&worstDow&&bestDow.name!==worstDow.name&&bestDow.wr-worstDow.wr>=15) candidates.push({priority:3,positive:true,headline:lang==="fr"?`Le ${bestDow.name} = ton meilleur jour (${bestDow.wr}% WR)`:`${bestDow.name} = your best day (${bestDow.wr}% WR)`,body:lang==="fr"?`Tu trades le mieux le ${bestDow.name}. Le ${worstDow.name} est ton jour le plus difficile avec ${worstDow.wr}% WR.`:`You trade best on ${bestDow.name}. ${worstDow.name} is your hardest day with ${worstDow.wr}% WR.`});
        if(overtradingDays.length>=3&&overtradingLossPct>=60) candidates.push({priority:5,positive:false,headline:lang==="fr"?`Overtrading = ${overtradingLossPct}% de jours perdants`:`Overtrading = ${overtradingLossPct}% losing days`,body:lang==="fr"?`Quand tu fais plus de ${Math.round(avgDailyTrades*1.5)} trades en une journée, tu finis négatif ${overtradingLossPct}% du temps.`:`When you take more than ${Math.round(avgDailyTrades*1.5)} trades in a day, you end negative ${overtradingLossPct}% of the time.`});
        if(expectancy<0) candidates.push({priority:8,positive:false,headline:lang==="fr"?`Espérance négative : ${fm(expectancy)}${currency}/trade`:`Negative expectancy: ${fm(expectancy)}${currency}/trade`,body:lang==="fr"?`En moyenne tu perds de l'argent à chaque trade. Revois ton ratio gain/perte ou ton win rate.`:`On average you lose money on each trade. Review your win/loss ratio or your win rate.`});

        const topInsight=candidates.sort((a,b)=>b.priority-a.priority)[0]||null;

        return (
          <div style={{marginBottom:8}}>

            {/* ── INSIGHT CLÉ ── */}
            {topInsight && (
              <div style={{...cardS,padding:"18px 20px",marginBottom:14,borderLeft:`4px solid ${topInsight.positive?"#4caf6e":"#e05a5a"}`,background:topInsight.positive?"rgba(76,175,110,0.04)":"rgba(224,90,90,0.04)"}}>
                <div style={{fontSize:9,color:topInsight.positive?"rgba(76,175,110,0.7)":"rgba(224,90,90,0.7)",textTransform:"uppercase",letterSpacing:"0.2em",fontFamily:ff,fontWeight:700,marginBottom:8}}>{lang==="fr"?"Insight du moment":"Key insight"}</div>
                <div style={{fontSize:!isMobile?20:17,fontFamily:"'Barlow',sans-serif",fontWeight:600,color:C.white,lineHeight:1.2,marginBottom:8,letterSpacing:"-0.01em"}}>{topInsight.headline}</div>
                <div style={{fontSize:12,color:C.gray1,fontFamily:ff,lineHeight:1.65}}>{topInsight.body}</div>
              </div>
            )}

            {/* ── MÉTRIQUES CLÉS ── */}
            <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.2em",fontFamily:ff,fontWeight:600,marginBottom:8}}>{L.stats.advanced}</div>
            <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(6,1fr)",gap:8,marginBottom:10}}>
              {[
                {l:L.stats.expectancy,v:`${fms(expectancy)}${currency}`,c:expectancy>=0?"#4caf6e":"#e05a5a",sub:L.stats.expectancySub,info:lang==="en"?"Average gain or loss per trade. Formula: (WR × avg win) − (loss rate × avg loss). Positive = profitable strategy long term.":"Ce que tu gagnes ou perds en moyenne par trade. Formule : (WR × gain moyen) − (taux perte × perte moyenne). Positif = stratégie rentable à long terme."},
                {l:L.stats.maxDD,v:`${maxDD.toFixed(1)}%`,c:maxDD<10?"#4caf6e":maxDD<25?"#d4c060":"#e05a5a",sub:`${lang==="en"?"Current":"Actuel"} : ${currentDD.toFixed(1)}%`,info:lang==="en"?"Largest drop from a peak, in %. Current DD is measured from the last equity high.":"La plus grosse baisse depuis un pic, en %. Le DD actuel est calculé par rapport au dernier sommet atteint."},
                {l:L.stats.profitFactor,v:pf>=99?"∞":pf.toFixed(2),c:pf>=1.5?"#4caf6e":pf>=1?"#d4c060":"#e05a5a",sub:L.stats.profitFactorSub,info:lang==="en"?"Total gains divided by total losses. Below 1 you lose more than you win. 1.5+ is a good sign.":"Total des gains divisé par total des pertes. En dessous de 1 tu perds plus que tu gagnes. 1.5+ est un bon signe."},
                {l:L.stats.kelly,v:`${Math.max(0,kelly).toFixed(1)}%`,c:kelly>0?"#4caf6e":"#e05a5a",sub:L.stats.kellySub,info:lang==="en"?"% of capital to risk per trade based on your real stats (WR and win/loss ratio).":"Le % de ton capital à risquer par trade selon tes stats réelles (WR et ratio gain/perte)."},
                {l:L.stats.consistency,v:`${consistency}%`,c:consistency>=60?"#4caf6e":consistency>=40?"#d4c060":"#e05a5a",sub:`${wVals.filter(v=>v>0).length}/${wVals.length} ${lang==="en"?"weeks":"semaines"}`,info:lang==="en"?"Percentage of weeks ending positive. 60%+ = consistent and reliable trading.":"Pourcentage de semaines qui se terminent en positif. 60%+ = trading régulier et fiable."},
                {l:L.stats.sharpe,v:sharpe.toFixed(2),c:sharpe>=1?"#4caf6e":sharpe>=0?"#d4c060":"#e05a5a",sub:L.stats.sharpeSub,info:lang==="en"?"Average daily return divided by daily volatility. ≥1 = excellent, 0–1 = good, <0 = too many losing days.":"Rendement moyen journalier divisé par la volatilité de tes jours. ≥1 = excellent, entre 0 et 1 = correct, <0 = trop de jours perdants."},
              ].map(m=>{
                const open = histInfoCard === m.l;
                return (
                  <div key={m.l} style={{...cardS,padding:"12px 10px",position:"relative"}}>
                    <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:4,marginBottom:6}}>
                      <div style={{...lbl,fontSize:8,letterSpacing:"0.12em"}}>{m.l}</div>
                      <button onClick={()=>setHistInfoCard(open?null:m.l)} style={{flexShrink:0,width:14,height:14,borderRadius:"50%",border:`1px solid ${C.gray2}`,background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0}}>
                        <span style={{fontSize:8,color:C.gray1,fontFamily:ff,fontWeight:600,lineHeight:1}}>i</span>
                      </button>
                    </div>
                    <div style={{overflow:"hidden",maxHeight:open?"180px":"0",opacity:open?1:0,transition:"max-height 0.38s cubic-bezier(0.4,0,0.2,1), opacity 0.22s ease"}}>
                      <div style={{fontSize:9,color:C.dim,fontFamily:ff,lineHeight:1.55,paddingTop:2}}>{m.info}</div>
                    </div>
                    <div style={{overflow:"hidden",maxHeight:open?"0":"120px",opacity:open?0:1,transition:"max-height 0.38s cubic-bezier(0.4,0,0.2,1), opacity 0.22s ease"}}>
                      <div style={{fontSize:18,fontFamily:ff,fontWeight:300,color:m.c,letterSpacing:"0.02em",lineHeight:1}}>{m.v}</div>
                      <div style={{fontSize:9,color:C.gray2,fontFamily:ff,marginTop:4}}>{m.sub}</div>
                      {(m.l.includes("onsist")||m.l.includes("elly")||m.l.includes("harpe"))&&(()=>{
                        let fillPct=0;
                        if(m.l.includes("onsist")) fillPct=Math.min(100,parseFloat(m.v)||0);
                        else if(m.l.includes("elly")) fillPct=Math.min(100,(parseFloat(m.v)||0)/30*100);
                        else if(m.l.includes("harpe")){const sv=parseFloat(m.v)||0; fillPct=Math.min(100,Math.max(0,(sv+1)/3*100));}
                        return (
                          <div style={{height:3,background:isDark?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)",borderRadius:2,marginTop:6,position:"relative"}}>
                            <div style={{width:fillPct+"%",height:"100%",background:m.c,borderRadius:2,transition:"width 0.5s"}}/>
                            {m.l.includes("harpe")&&<div style={{position:"absolute",left:`${Math.min(100,Math.max(0,(0+1)/3*100))}%`,top:0,width:1,height:"100%",background:isDark?"rgba(255,255,255,0.3)":"rgba(0,0,0,0.3)"}}/>}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── FYLTRAGRAM ── */}
            {(()=>{
              const fNow=new Date();
              const fToday=fNow.toISOString().slice(0,10);
              const fWeekAgo=new Date(fNow.getTime()-7*86400000).toISOString().slice(0,10);
              const fMonth=fToday.slice(0,7);
              const fFilter=(p,trd)=>{
                if(p==='today')return trd.filter(t=>t.date===fToday);
                if(p==='week')return trd.filter(t=>t.date>=fWeekAgo);
                if(p==='month')return trd.filter(t=>t.date&&t.date.startsWith(fMonth));
                return trd;
              };
              const fPrevFilter=(p,trd)=>{
                if(p==='today'){const y=new Date(fNow);y.setDate(y.getDate()-1);return trd.filter(t=>t.date===y.toISOString().slice(0,10));}
                if(p==='week'){const w3=new Date(fNow.getTime()-14*86400000).toISOString().slice(0,10);return trd.filter(t=>t.date>=w3&&t.date<fWeekAgo);}
                if(p==='month'){const pm=new Date(fNow);pm.setMonth(pm.getMonth()-1);return trd.filter(t=>t.date&&t.date.startsWith(pm.toISOString().slice(0,7)));}
                const s=[...trd].sort((a,b)=>(a.date||'').localeCompare(b.date||''));return s.slice(0,Math.floor(s.length/2));
              };
              const fCalc=(trd)=>{
                const N=trd.length;
                if(N===0)return{discipline:0,emotional:0,patience:0,resilience:0,consistency:0,risk:0};
                const bd={};trd.forEach(t=>{if(!t.date)return;(bd[t.date]=bd[t.date]||[]).push(t);});
                const days=Object.values(bd);
                const tpdArr=days.map(d=>d.length).sort((a,b)=>a-b);
                const thresh=Math.max(5,(tpdArr[Math.floor(tpdArr.length/2)]||1)*2);
                const disc=Math.min(100,Math.max(0,Math.round(100-(days.filter(d=>d.length>thresh).length/Math.max(days.length,1))*35-(1-trd.filter(t=>t.session).length/N)*25)));
                const eMap={...EMOTION_POLARITY};extraEmotions.forEach(e=>{if(typeof e==='object'&&e.polarity)eMap[e.label]=e.polarity;});
                const wE=trd.filter(t=>t.emotion);const anx=wE.filter(t=>eMap[t.emotion]==='negative').length;
                const emot=wE.length>0?Math.round((1-anx/wE.length)*100):75;
                const wRR=trd.filter(t=>t.rr>0);const wN=trd.filter(t=>t.notes&&t.notes.trim().length>10);
                const pat=Math.min(100,Math.round(wRR.length>0?(wRR.filter(t=>t.rr>=1).length/wRR.length)*100*0.7+(wN.length/N)*100*0.3:50+(wN.length/N)*50));
                const srt=[...trd].sort((a,b)=>(a.date||'').localeCompare(b.date||'')||(a.entryTime||'').localeCompare(b.entryTime||''));
                let resT=0,resG=0;for(let i=0;i<srt.length;i++){if(srt[i].result!=='LOSS')continue;resT++;if(i+1<srt.length){const nx=srt[i+1];if(nx.date!==srt[i].date||nx.result==='WIN')resG++;}else resG++;}
                const res=resT>0?Math.round((resG/resT)*100):85;
                const dpnl=days.map(d=>d.reduce((s,t)=>s+(t.pnl||0),0));
                const mean=dpnl.reduce((s,v)=>s+v,0)/Math.max(dpnl.length,1);
                const std=Math.sqrt(dpnl.reduce((s,v)=>s+Math.pow(v-mean,2),0)/Math.max(dpnl.length,1));
                const mAbs=dpnl.reduce((s,v)=>s+Math.abs(v),0)/Math.max(dpnl.length,1);
                const cons=mAbs>0?Math.min(100,Math.max(0,Math.round(100-(std/mAbs)*35))):60;
                const wSL=trd.filter(t=>t.sl>0||t.rr>0).length;
                const avgRRv=wRR.length>0?wRR.reduce((s,t)=>s+t.rr,0)/wRR.length:0;
                const cap=parseFloat(capital)||10000;
                const maxDL=Math.max(...days.map(d=>d.filter(t=>t.result==='LOSS').reduce((s,t)=>s+Math.abs(t.pnl||0),0)),0);
                const risk=Math.min(100,Math.round((wSL/N)*100*0.4+Math.min(100,avgRRv*50)*0.3+(maxDL<cap*0.02?100:maxDL<cap*0.05?70:40)*0.3));
                return{discipline:disc,emotional:emot,patience:pat,resilience:res,consistency:cons,risk};
              };
              const fTrd=fFilter(fyltaPeriod,trades);
              const fLast=fPrevFilter(fyltaPeriod,trades);
              const fS=fCalc(fTrd);const fLS=fCalc(fLast);
              const fFF="'Josefin Sans',sans-serif";
              const FAXES=lang==='fr'
                ?[{key:'discipline',label:'DISCIPLINE'},{key:'emotional',label:'ÉMOTIONS'},{key:'patience',label:'PATIENCE'},{key:'resilience',label:'RÉSILIENCE'},{key:'consistency',label:'RÉGULARITÉ'},{key:'risk',label:'RISQUE'}]
                :[{key:'discipline',label:'DISCIPLINE'},{key:'emotional',label:'CONTROL'},{key:'patience',label:'PATIENCE'},{key:'resilience',label:'RESILIENCE'},{key:'consistency',label:'CONSISTENCY'},{key:'risk',label:'RISK'}];
              const fAxes=FAXES.map(a=>({...a,value:fS[a.key]}));
              const fLastAxes=FAXES.map(a=>({...a,value:fLS[a.key]}));
              const fWeak=fAxes.reduce((m,a)=>a.value<m.value?a:m,fAxes[0]);
              const fPhrase={discipline:lang==='fr'?'Tu dépasses régulièrement ton nombre de trades idéal.':'You regularly exceed your ideal trade count.',emotional:lang==='fr'?'Trop de trades pris sous pression émotionnelle.':'Too many trades taken under emotional pressure.',patience:lang==='fr'?'Beaucoup de setups ont un RR < 1.':'Many setups have RR < 1.',resilience:lang==='fr'?'Après une perte, tu retournes en marché trop vite.':'After a loss, you re-enter too quickly.',consistency:lang==='fr'?'Tes résultats varient fortement d\'un jour à l\'autre.':'Your results vary significantly day to day.',risk:lang==='fr'?'Gestion du risque insuffisante.':'Insufficient risk management.'}[fWeak.key]||'';
              const isDark_=darkMode;
              return (
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'14px 14px 16px',marginBottom:10,boxShadow:cShadow}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:6}}>
                    <div style={{fontSize:9,color:C.dim,textTransform:'uppercase',letterSpacing:'0.2em',fontFamily:fFF,fontWeight:600}}>FYLTRAGRAM</div>
                    <div style={{display:'flex',gap:3}}>
                      {['today','week','month','all'].map(p=>(
                        <button key={p} onClick={()=>setFyltaPeriod(p)} style={{padding:'3px 10px',borderRadius:20,fontSize:8,cursor:'pointer',fontFamily:fFF,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase',transition:'all 0.15s',border:`1px solid ${fyltaPeriod===p?'rgba(var(--gold-rgb),0.6)':C.gray3}`,background:fyltaPeriod===p?'rgba(var(--gold-rgb),0.08)':'transparent',color:fyltaPeriod===p?'rgba(var(--gold-rgb),0.9)':C.gray1}}>
                          {lang==='fr'?{today:'Auj.',week:'7j',month:'Mois',all:'Tout'}[p]:{today:'Today',week:'Week',month:'Month',all:'All'}[p]}
                        </button>
                      ))}
                    </div>
                  </div>
                  {fTrd.length>=2
                    ?<Fyltragram axes={fAxes} size={isMobile?180:240} isDark={isDark_}/>
                    :<div style={{textAlign:'center',padding:'28px 0',color:C.gray2,fontSize:11,fontFamily:fFF,letterSpacing:'0.08em'}}>{lang==='fr'?'Pas assez de données':'Not enough data'}</div>
                  }
                  {fTrd.length>=2&&(
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'4px 12px',marginTop:12,padding:'10px 12px',borderRadius:8,background:isDark_?'rgba(255,255,255,0.03)':'rgba(0,0,0,0.03)',border:`1px solid ${C.border}`}}>
                      {(lang==='fr'?[{label:'DISCIPLINE',desc:'Max trades/jour + en session'},{label:'ÉMOTIONS',desc:'Trades calmes vs anxieux'},{label:'PATIENCE',desc:'Setups avec RR ≥ 1'},{label:'RÉSILIENCE',desc:'Comportement post-perte'},{label:'RÉGULARITÉ',desc:'Stabilité des P&L journaliers'},{label:'RISQUE',desc:'SL, RR moyen, drawdown'},]:[{label:'DISCIPLINE',desc:'Max trades/day + session'},{label:'CONTROL',desc:'Calm vs anxious trades'},{label:'PATIENCE',desc:'Setups with RR ≥ 1'},{label:'RESILIENCE',desc:'Behavior after a loss'},{label:'CONSISTENCY',desc:'Daily P&L stability'},{label:'RISK',desc:'SL, avg RR, drawdown'},]).map((ax,i)=>{const v=fAxes[i]?.value??0;const col=v>=70?'#4ade80':v>=40?'#fb923c':'#f87171';return(<div key={i} style={{display:'flex',alignItems:'flex-start',gap:5}}><div style={{width:5,height:5,borderRadius:'50%',background:col,flexShrink:0,marginTop:3}}/><div><div style={{fontSize:8,color:C.white,fontFamily:fFF,fontWeight:700,letterSpacing:'0.06em'}}>{ax.label}</div><div style={{fontSize:9,color:C.gray1,fontFamily:fFF,fontWeight:300,lineHeight:1.4}}>{ax.desc}</div></div></div>);})}
                    </div>
                  )}
                  {fTrd.length>=3&&(
                    <div style={{marginTop:10,padding:'10px 12px',borderRadius:8,background:isDark_?'rgba(255,255,255,0.03)':'rgba(0,0,0,0.03)',border:`1px solid ${C.border}`}}>
                      <div style={{fontSize:8,color:'rgba(var(--gold-rgb),0.5)',fontFamily:fFF,fontWeight:700,letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:4}}>{lang==='fr'?'POINT FAIBLE':'WEAKNESS'} — {fWeak.label}</div>
                      <div style={{fontSize:11,color:C.dim,fontFamily:fFF,fontWeight:300,lineHeight:1.7,fontStyle:'italic'}}>{fPhrase}</div>
                    </div>
                  )}
                  {fLast.length>=2&&fTrd.length>=2&&(
                    <div style={{marginTop:12}}>
                      <div style={{fontSize:8,color:C.gray2,fontFamily:fFF,letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:8,textAlign:'center'}}>{lang==='fr'?'ÉVOLUTION':'PROGRESS'}</div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                        {[{lbl:lang==='fr'?'Période préc.':'Last period',axes:fLastAxes},{lbl:lang==='fr'?'Période actuelle':'This period',axes:fAxes}].map((item,idx)=>(
                          <div key={idx} style={{textAlign:'center'}}>
                            <Fyltragram axes={item.axes} size={90} mini isDark={isDark_}/>
                            <div style={{fontSize:8,color:C.gray2,fontFamily:fFF,letterSpacing:'0.08em',textTransform:'uppercase',marginTop:5}}>{item.lbl}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── R-MULTIPLE + EMOTION ── */}
            {statsWidgets.rr && <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"2fr 3fr",gap:8,marginBottom:10,alignItems:"start"}}>
            <div style={{...cardS,padding:"16px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:2}}>
                <div style={lbl}>{lang==="fr"?"Distribution R-Multiples":"R-Multiple Distribution"}</div>
                <button onClick={()=>setHistInfoCard(histInfoCard==="rr"?null:"rr")} style={{width:16,height:16,borderRadius:"50%",border:`1px solid ${C.gray2}`,background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,flexShrink:0}}>
                  <span style={{fontSize:9,color:C.gray1,fontFamily:ff,fontWeight:600,lineHeight:1}}>i</span>
                </button>
              </div>
              <div style={{overflow:"hidden",maxHeight:histInfoCard==="rr"?"0":"40px",opacity:histInfoCard==="rr"?0:1,transition:"max-height 0.38s cubic-bezier(0.4,0,0.2,1), opacity 0.22s ease"}}>
                <div style={{fontSize:9,color:C.gray2,fontFamily:ff,marginBottom:14,marginTop:2}}>{lang==="fr"?"Où se situent tes sorties ?":"Where do your exits land?"}</div>
              </div>
              <div style={{overflow:"hidden",maxHeight:histInfoCard==="rr"?"200px":"0",opacity:histInfoCard==="rr"?1:0,transition:"max-height 0.38s cubic-bezier(0.4,0,0.2,1), opacity 0.22s ease"}}>
                <div style={{fontSize:10,color:C.dim,fontFamily:ff,marginBottom:14,lineHeight:1.6,paddingTop:2}}>{lang==="fr"?<>Histogramme de tes sorties de trade exprimées en R (multiples du risque initial). {"<"}0 = en perte, 0–1 = gagné mais sous le RR visé, 1–2 = dans le plan, 2+ = trade exceptionnel. Le WR d'équilibre est le WR minimum pour être rentable avec ton RR moyen.</>:<>Histogram of your trade exits expressed in R (multiples of initial risk). {"<"}0 = loss, 0–1 = winner but below target RR, 1–2 = on plan, 2+ = exceptional trade. Break-even WR is the minimum WR to be profitable at your average RR.</>}</div>
              </div>
              <div style={{display:"flex",gap:8,alignItems:"flex-end",height:100,marginBottom:10}}>
                {rrBuckets.map(b=>{
                  const h=rrTotal>0?Math.max(4,Math.round(b.count/rrTotal*74)):4;
                  const pct=rrTotal>0?Math.round(b.count/rrTotal*100):0;
                  return (
                    <div key={b.label} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                      {b.count>0
                        ? <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:1}}>
                            <span style={{fontSize:10,color:C.gray1,fontFamily:ff,fontWeight:600}}>{pct}%</span>
                            <span style={{fontSize:8,color:C.gray2,fontFamily:ff}}>{b.count}T</span>
                          </div>
                        : <span style={{fontSize:9,color:"transparent",fontFamily:ff}}>-</span>
                      }
                      <div style={{width:"100%",display:"flex",alignItems:"flex-end",height:64}}>
                        <div style={{width:"100%",height:b.count>0?h:2,borderRadius:"5px 5px 2px 2px",background:b.color,opacity:b.count>0?0.9:0.2,transition:"height 0.6s cubic-bezier(.4,0,.2,1)"}}/>
                      </div>
                      <span style={{fontSize:9,color:C.gray1,fontFamily:ff,letterSpacing:"0.05em"}}>{b.label}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{display:"flex",gap:12,fontSize:9,color:C.gray2,fontFamily:ff,paddingTop:8,borderTop:`1px solid ${C.border}`,flexWrap:"wrap"}}>
                <span>{lang==="fr"?"RR moyen":"Avg RR"} : <strong style={{color:C.gray1,fontFamily:ff}}>{avgRR.toFixed(2)}</strong></span>
                <span>{lang==="fr"?"WR d'équilibre":"Break-even WR"} : <strong style={{color:C.gray1,fontFamily:ff}}>{breakEvenWR}%</strong></span>
                <span>{lang==="fr"?"Ton WR":"Your WR"} : <strong style={{color:Math.round(wr*100)>breakEvenWR?"#4caf6e":"#e05a5a",fontFamily:ff}}>{Math.round(wr*100)}%</strong></span>
              </div>
            </div>
            {statsWidgets.emotions && emoData.length>0
              ? <div style={{...cardS,padding:"16px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:2}}>
                  <div style={lbl}>{L.stats.emoMatrix}</div>
                  <button onClick={()=>setHistInfoCard(histInfoCard==="emo"?null:"emo")} style={{width:16,height:16,borderRadius:"50%",border:`1px solid ${C.gray2}`,background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,flexShrink:0}}>
                    <span style={{fontSize:9,color:C.gray1,fontFamily:ff,fontWeight:600,lineHeight:1}}>i</span>
                  </button>
                </div>
                <div style={{overflow:"hidden",maxHeight:histInfoCard==="emo"?"0":"40px",opacity:histInfoCard==="emo"?0:1,transition:"max-height 0.38s cubic-bezier(0.4,0,0.2,1), opacity 0.22s ease"}}>
                  <div style={{fontSize:9,color:C.gray2,fontFamily:ff,marginBottom:14,marginTop:2}}>{L.stats.emoMatrixSub}</div>
                </div>
                <div style={{overflow:"hidden",maxHeight:histInfoCard==="emo"?"200px":"0",opacity:histInfoCard==="emo"?1:0,transition:"max-height 0.38s cubic-bezier(0.4,0,0.2,1), opacity 0.22s ease"}}>
                  <div style={{fontSize:10,color:C.dim,fontFamily:ff,marginBottom:14,lineHeight:1.6,paddingTop:2}}>{lang==="fr"?"Pour chaque émotion enregistrée : nombre de trades, win rate et P&L total. Permet de voir si tu trades mieux dans certains états. Un WR élevé avec une émotion négative = tu gères la pression. Un WR faible avec une émotion positive = attention à la surconfiance.":"For each recorded emotion: trade count, win rate and total P&L. See whether you trade better in certain states. A high WR with a negative emotion = you handle pressure well. A low WR with a positive emotion = watch out for overconfidence."}</div>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {emoData.map(e=>{
                    const barColor = e.wr>=50?"#4caf6e":"#e05a5a";
                    return (
                      <div key={e.e} style={{display:"flex",alignItems:"center",gap:8}}>
                        <div style={{width:72,fontSize:10,color:C.gray1,fontFamily:ff,flexShrink:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{L.emotions[e.e]||e.e}</div>
                        <div style={{flex:1,position:"relative",height:16,background:isDark?"rgba(255,255,255,0.05)":"rgba(0,0,0,0.05)",borderRadius:3,overflow:"hidden"}}>
                          <div style={{width:e.wr+"%",height:"100%",background:barColor,opacity:0.8,borderRadius:3,transition:"width 0.5s",display:"flex",alignItems:"center",justifyContent:"flex-end",paddingRight:4}}>
                            {e.wr>20 && <span style={{fontSize:9,color:"#fff",fontFamily:ff,fontWeight:600,lineHeight:1}}>{e.wr}%</span>}
                          </div>
                          {e.wr<=20 && e.wr>0 && <span style={{position:"absolute",left:e.wr+"%",top:"50%",transform:"translateY(-50%)",marginLeft:4,fontSize:9,color:C.gray1,fontFamily:ff,lineHeight:1}}>{e.wr}%</span>}
                        </div>
                        <div style={{width:56,fontSize:11,fontFamily:ff,fontWeight:600,color:e.pnl>=0?"#4caf6e":"#e05a5a",textAlign:"right",flexShrink:0}}>{fms(e.pnl)}{currency}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              : null}
            </div>}

            {/* ── STREAKS + OVERTRADING ── */}
            {statsWidgets.streaks && <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:8,marginBottom:10}}>
              <div style={{...cardS,padding:"16px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:histInfoCard==="streaks"?8:0}}>
                  <div style={lbl}>{lang==="fr"?"Séries":"Streaks"}</div>
                  <button onClick={()=>setHistInfoCard(histInfoCard==="streaks"?null:"streaks")} style={{width:16,height:16,borderRadius:"50%",border:`1px solid ${C.gray2}`,background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,flexShrink:0}}>
                    <span style={{fontSize:9,color:C.gray1,fontFamily:ff,fontWeight:600,lineHeight:1}}>i</span>
                  </button>
                </div>
                <div style={{overflow:"hidden",maxHeight:histInfoCard==="streaks"?"120px":"0",opacity:histInfoCard==="streaks"?1:0,transition:"max-height 0.38s cubic-bezier(0.4,0,0.2,1), opacity 0.22s ease"}}>
                  <div style={{fontSize:10,color:C.dim,fontFamily:ff,marginBottom:10,lineHeight:1.6,paddingTop:2}}>{lang==="fr"?"Série actuelle = les victoires ou défaites consécutives en cours. Meilleure et pire série = records historiques. Les BREAKEVEN ne coupent pas une série.":"Current streak = consecutive wins or losses in progress. Best and worst streak = historical records. BREAKEVENs do not break a streak."}</div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginTop:histInfoCard==="streaks"?0:12}}>
                  {[
                    {l:L.stats.current,v:`${curS}×`,sub:curST==="W"?L.stats.wins:L.stats.losses,c:curST==="W"?"#4caf6e":"#e05a5a"},
                    {l:L.stats.bestStreak,v:`${maxWS}×`,sub:L.stats.winStreak,c:"#4caf6e"},
                    {l:L.stats.worstStreak,v:`${maxLS}×`,sub:L.stats.lossStreak,c:"#e05a5a"},
                  ].map(s=>(
                    <div key={s.l} style={{textAlign:"center",background:isDark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.03)",borderRadius:8,padding:"10px 6px"}}>
                      <div style={{fontSize:7,color:C.dim,fontFamily:ff,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>{s.l}</div>
                      <div style={{fontSize:18,fontFamily:ff,fontWeight:300,color:s.c,letterSpacing:"0.04em",lineHeight:1}}>{s.v}</div>
                      <div style={{fontSize:8,color:C.gray2,fontFamily:ff}}>{s.sub}</div>
                    </div>
                  ))}
                </div>
                <div style={{marginTop:12}}>
                  <div style={{fontSize:8,color:C.dim,fontFamily:ff,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>{lang==="fr"?"20 DERNIERS TRADES":"LAST 20 TRADES"}</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:3}}>
                    {sorted.slice(-20).map((t,i)=>(
                      <div key={i} style={{width:8,height:8,borderRadius:2,opacity:0.85,background:t.result==="WIN"?"#4caf6e":t.result==="LOSS"?"#e05a5a":"rgba(200,200,200,0.4)"}}/>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{...cardS,padding:"16px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:histInfoCard==="ot"?8:0}}>
                  <div style={lbl}>{lang==="fr"?"Détection Surtrading":"Overtrading Detection"}</div>
                  <button onClick={()=>setHistInfoCard(histInfoCard==="ot"?null:"ot")} style={{width:16,height:16,borderRadius:"50%",border:`1px solid ${C.gray2}`,background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,flexShrink:0}}>
                    <span style={{fontSize:9,color:C.gray1,fontFamily:ff,fontWeight:600,lineHeight:1}}>i</span>
                  </button>
                </div>
                <div style={{overflow:"hidden",maxHeight:histInfoCard==="ot"?"140px":"0",opacity:histInfoCard==="ot"?1:0,transition:"max-height 0.38s cubic-bezier(0.4,0,0.2,1), opacity 0.22s ease"}}>
                  <div style={{fontSize:10,color:C.dim,fontFamily:ff,marginBottom:10,lineHeight:1.6,paddingTop:2}}>{lang==="fr"?"Calcule ta moyenne de trades par jour. Signale les jours où tu as tradé 1.5× plus que cette moyenne — souvent signe d'impatience ou de revenge trading. Le P&L de ces jours t'indique si ce surtrading était rentable ou non.":"Calculates your average trades per day. Flags days where you traded 1.5× more than this average — often a sign of impatience or revenge trading. The P&L of those days tells you whether the overtrading was profitable."}</div>
                </div>
                <div style={{marginTop:histInfoCard==="ot"?0:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:6,alignItems:"center"}}>
                    <span style={{fontSize:9,color:C.gray1,fontFamily:ff}}>{lang==="fr"?"Moy. trades / jour":"Avg trades / day"}</span>
                    <span style={{fontSize:12,color:C.white,fontFamily:ff,fontWeight:600}}>{avgTPD.toFixed(1)}</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:10,alignItems:"center"}}>
                    <span style={{fontSize:9,color:C.gray1,fontFamily:ff}}>{lang==="fr"?"Jours suspects détectés":"Suspicious days detected"}</span>
                    <span style={{fontSize:12,color:otDays.length>0?"#e05a5a":C.white,fontFamily:ff,fontWeight:600}}>{otDays.length}</span>
                  </div>
                  {otDays.length>0 && otDays.slice(0,2).map(d=>(
                    <div key={d.date} style={{display:"flex",justifyContent:"space-between",padding:"5px 8px",background:"rgba(224,90,90,0.08)",borderRadius:6,marginBottom:4,border:"1px solid rgba(224,90,90,0.15)"}}>
                      <span style={{fontSize:9,color:C.gray1,fontFamily:ff}}>{d.date} · <strong style={{color:"#e05a5a",fontFamily:ff}}>{d.count} trades</strong></span>
                      <span style={{fontSize:9,fontFamily:ff,fontWeight:600,color:d.pnl>=0?"#4caf6e":"#e05a5a"}}>{fms(d.pnl)}{currency}</span>
                    </div>
                  ))}
                  {otDays.length===0 && <div style={{fontSize:10,color:"#4caf6e",fontFamily:ff,padding:"6px 10px",background:"rgba(76,175,110,0.08)",borderRadius:6,border:"1px solid rgba(76,175,110,0.15)"}}>✓ {lang==="fr"?"Aucun surtrading détecté":"No overtrading detected"}</div>}
                </div>
              </div>
            </div>}

            {/* ── MONTE CARLO ── */}
            {statsWidgets.montecarlo && <div style={{...cardS,padding:"16px",marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                    <div style={lbl}>{lang==="fr"?"Monte Carlo — 50 prochains trades":"Monte Carlo — next 50 trades"}</div>
                    <button onClick={()=>setHistInfoCard(histInfoCard==="mc"?null:"mc")} style={{width:16,height:16,borderRadius:"50%",border:`1px solid ${C.gray2}`,background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,flexShrink:0}}>
                      <span style={{fontSize:9,color:C.gray1,fontFamily:ff,fontWeight:600,lineHeight:1}}>i</span>
                    </button>
                  </div>
                  <div style={{overflow:"hidden",maxHeight:histInfoCard==="mc"?"0":"36px",opacity:histInfoCard==="mc"?0:1,transition:"max-height 0.38s cubic-bezier(0.4,0,0.2,1), opacity 0.22s ease"}}>
                    <div style={{fontSize:9,color:C.gray2,fontFamily:ff,marginTop:2}}>{lang==="fr"?`Simulation sur ${MC} chemins basée sur tes stats réelles`:`Simulation over ${MC} paths based on your real stats`}</div>
                  </div>
                  <div style={{overflow:"hidden",maxHeight:histInfoCard==="mc"?"200px":"0",opacity:histInfoCard==="mc"?1:0,transition:"max-height 0.38s cubic-bezier(0.4,0,0.2,1), opacity 0.22s ease"}}>
                    <div style={{fontSize:10,color:C.dim,fontFamily:ff,lineHeight:1.6,maxWidth:260,paddingTop:2}}>{lang==="fr"?"Simule 300 scénarios de tes 50 prochains trades en utilisant ton WR réel et tes gains/pertes moyens. P10 = résultat si tu as de la malchance (10% de chances de faire moins). P50 = résultat le plus probable. P90 = si tout va bien. Le % en haut à droite = probabilité d'être en profit après 50 trades.":"Simulates 300 scenarios of your next 50 trades using your real WR and average win/loss. P10 = result if you're unlucky (10% chance of doing worse). P50 = most likely outcome. P90 = if everything goes well. The % top right = probability of being in profit after 50 trades."}</div>
                  </div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:24,fontFamily:ff,fontWeight:300,color:mcWin>=60?"#4caf6e":mcWin>=40?"#d4c060":"#e05a5a",letterSpacing:"0.04em",lineHeight:1}}>{mcWin}%</div>
                  <div style={{fontSize:8,color:C.gray2,fontFamily:ff}}>{lang==="fr"?"chances de profit":"profit probability"}</div>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                {[
                  {l:lang==="fr"?"Pessimiste (P10)":"Pessimistic (P10)",v:mcP10,desc:lang==="fr"?"1 chance sur 10 de faire moins":"1 in 10 chance of doing worse"},
                  {l:L.stats.median,v:mcP50,desc:L.stats.medianDesc},
                  {l:lang==="fr"?"Optimiste (P90)":"Optimistic (P90)",v:mcP90,desc:lang==="fr"?"1 chance sur 10 de faire mieux":"1 in 10 chance of doing better"},
                ].map(s=>(
                  <div key={s.l} style={{background:isDark?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.04)",borderRadius:8,padding:isMobile?"10px 6px":"12px 10px",textAlign:"center"}}>
                    <div style={{fontSize:7,color:C.dim,fontFamily:ff,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:6}}>{s.l}</div>
                    <div style={{fontSize:18,fontFamily:ff,fontWeight:300,color:s.v>=0?"#4caf6e":"#e05a5a",letterSpacing:"0.04em",lineHeight:1}}>{fms(s.v)}{currency}</div>
                    <div style={{fontSize:8,color:C.gray2,fontFamily:ff,marginTop:4}}>{s.desc}</div>
                  </div>
                ))}
              </div>
              {(()=>{
                const spread=mcP90-mcP10||1;
                const mcMin=mcP10-spread*0.15;
                const mcMax=mcP90+spread*0.15;
                const range=mcMax-mcMin||1;
                const pos10=Math.max(2,Math.min(96,(mcP10-mcMin)/range*100));
                const pos50=Math.max(2,Math.min(96,(mcP50-mcMin)/range*100));
                const pos90=Math.max(2,Math.min(96,(mcP90-mcMin)/range*100));
                return (
                  <div style={{width:"100%",height:40,position:"relative",marginTop:12,borderRadius:6,border:`1px solid ${C.border}`,background:"linear-gradient(to right, rgba(248,113,113,0.4) 0%, rgba(212,192,96,0.3) 40%, rgba(74,222,128,0.4) 100%)",overflow:"visible"}}>
                    {[{pos:pos10,color:"#e05a5a",label:"P10"},{pos:pos50,color:"#d4c060",label:"P50"},{pos:pos90,color:"#4caf6e",label:"P90"}].map(m=>(
                      <div key={m.label} style={{position:"absolute",left:m.pos+"%",top:0,height:"100%",display:"flex",flexDirection:"column",alignItems:"center"}}>
                        <div style={{fontSize:8,color:m.color,fontFamily:ff,fontWeight:600,position:"absolute",top:-14,transform:"translateX(-50%)",whiteSpace:"nowrap"}}>{m.label}</div>
                        <div style={{width:2,height:"100%",background:m.color}}/>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>}

          </div>
        );
      })()}
      {/* ══════════════════ FIN ANALYTICS ══════════════════ */}

      {/* ══════════════════ ALERTES INTELLIGENTES ══════════════════ */}
      {(() => {
        if (trades.length < 8) return null;
        const IS_FR = lang === "fr";
        const grp = (keyFn) => {
          const m = {};
          trades.forEach(t => {
            const k = keyFn(t); if (!k) return;
            if (!m[k]) m[k] = { pnl:0, count:0, wins:0 };
            m[k].pnl += t.pnl || 0; m[k].count++;
            if (t.result === "WIN") m[k].wins++;
          });
          return Object.entries(m).map(([k,v]) => ({ name:k, pnl:v.pnl, count:v.count, wr:v.count?Math.round(v.wins/v.count*100):0 }));
        };
        const alerts = [];
        const MIN = 5;

        // Jour de semaine
        const WDAYS = IS_FR
          ? ["Dimanche","Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi"]
          : ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
        const byDay = grp(t => WDAYS[new Date(t.date+"T12:00:00").getDay()]);
        const worstDay = byDay.filter(d => d.count >= MIN).sort((a,b) => a.wr - b.wr)[0];
        if (worstDay && worstDay.wr < 42) alerts.push({
          type: worstDay.wr < 32 ? "danger" : "warning",
          title: IS_FR ? `Évite le ${worstDay.name}` : `Avoid ${worstDay.name}s`,
          desc: IS_FR
            ? `${worstDay.count} trades, WR ${worstDay.wr}% — ton pire jour. P&L ${fm(worstDay.pnl)}${currency}.`
            : `${worstDay.count} trades, WR ${worstDay.wr}% — your worst day. P&L ${fm(worstDay.pnl)}${currency}.`
        });

        // Session perdante
        const bySess = grp(t => t.session);
        const worstSess = bySess.filter(s => s.count >= MIN && s.pnl < 0).sort((a,b) => a.wr - b.wr)[0];
        const bestSess  = bySess.filter(s => s.count >= MIN).sort((a,b) => b.wr - a.wr)[0];
        if (worstSess && worstSess.wr < 42) alerts.push({
          type: "warning",
          title: IS_FR ? `Session ${worstSess.name} perdante` : `${worstSess.name} session is losing`,
          desc: IS_FR
            ? `WR ${worstSess.wr}% sur ${worstSess.count} trades (${fm(worstSess.pnl)}${currency}). Réduis l'exposition.`
            : `WR ${worstSess.wr}% on ${worstSess.count} trades (${fm(worstSess.pnl)}${currency}). Reduce exposure.`
        });
        if (bestSess && bestSess.wr >= 62 && bySess.filter(s => s.count >= MIN).length > 1) alerts.push({
          type: "insight",
          title: IS_FR ? `Ton edge : session ${bestSess.name}` : `Your edge: ${bestSess.name} session`,
          desc: IS_FR
            ? `WR ${bestSess.wr}% sur ${bestSess.count} trades. Concentre-toi sur cette session.`
            : `WR ${bestSess.wr}% on ${bestSess.count} trades. Focus on this session.`
        });

        // Émotion négative
        const byEmo = grp(t => t.emotion);
        const worstEmo = byEmo.filter(e => e.count >= 4 && e.wr < 36).sort((a,b) => a.wr - b.wr)[0];
        if (worstEmo) alerts.push({
          type: "danger",
          title: IS_FR ? `Ne trade pas quand tu es ${worstEmo.name}` : `Don't trade when ${worstEmo.name}`,
          desc: IS_FR
            ? `WR ${worstEmo.wr}% sur ${worstEmo.count} trades dans cet état — prends une pause.`
            : `WR ${worstEmo.wr}% on ${worstEmo.count} trades in this state — take a break.`
        });

        // Direction perdante
        const byDir = grp(t => t.direction);
        const worstDir = byDir.filter(d => d.count >= MIN && d.wr < 35).sort((a,b) => a.wr - b.wr)[0];
        if (worstDir) alerts.push({
          type: "warning",
          title: IS_FR ? `Tes ${worstDir.name}s sont perdants` : `Your ${worstDir.name}s are losing`,
          desc: IS_FR
            ? `WR ${worstDir.wr}% sur ${worstDir.count} trades. Revois ta stratégie ${worstDir.name.toLowerCase()}.`
            : `WR ${worstDir.wr}% on ${worstDir.count} trades. Rethink your ${worstDir.name.toLowerCase()} strategy.`
        });

        // Instrument dominant (edge concentré)
        const byInst = grp(t => t.instrument);
        const posInst = byInst.filter(i => i.pnl > 0);
        const totalPosPnl = posInst.reduce((s,i) => s + i.pnl, 0);
        const topInst = posInst.filter(i => i.count >= MIN).sort((a,b) => b.pnl - a.pnl)[0];
        if (topInst && totalPosPnl > 0 && topInst.pnl / totalPosPnl > 0.72) alerts.push({
          type: "insight",
          title: IS_FR ? `Ton edge = ${topInst.name}` : `Your edge = ${topInst.name}`,
          desc: IS_FR
            ? `${Math.round(topInst.pnl/totalPosPnl*100)}% de ton P&L positif vient de ${topInst.name} (WR ${topInst.wr}%). Concentre-toi dessus.`
            : `${Math.round(topInst.pnl/totalPosPnl*100)}% of your positive P&L comes from ${topInst.name} (WR ${topInst.wr}%). Focus on it.`
        });

        // Coupure prématurée des winners
        const winners = trades.filter(t => t.result==="WIN" && parseFloat(t.rr)>0);
        const cutW = winners.filter(t => parseFloat(t.rr)<1).length;
        if (winners.length >= 8 && cutW/winners.length > 0.50) alerts.push({
          type: "warning",
          title: IS_FR ? "Tu coupes tes gains trop tôt" : "You cut winners too early",
          desc: IS_FR
            ? `${Math.round(cutW/winners.length*100)}% de tes winners fermés avant 1R. Laisse tes positions courir.`
            : `${Math.round(cutW/winners.length*100)}% of your winners closed before 1R. Let your trades run.`
        });

        // Overtrading
        const byDateCnt = {};
        trades.forEach(t => { byDateCnt[t.date] = (byDateCnt[t.date]||0)+1; });
        const heavyTrades = trades.filter(t => byDateCnt[t.date] >= 4);
        const lightTrades = trades.filter(t => byDateCnt[t.date] === 1);
        const hWR = heavyTrades.length ? Math.round(heavyTrades.filter(t=>t.result==="WIN").length/heavyTrades.length*100) : 0;
        const lWR = lightTrades.length ? Math.round(lightTrades.filter(t=>t.result==="WIN").length/lightTrades.length*100) : 0;
        if (Object.values(byDateCnt).filter(c=>c>=4).length >= 3 && lightTrades.length >= 5 && hWR < lWR - 20) alerts.push({
          type: "danger",
          title: IS_FR ? "Overtrading détecté" : "Overtrading detected",
          desc: IS_FR
            ? `WR ${lWR}% quand tu prends 1 trade/jour vs ${hWR}% à 4+ trades. Moins c'est mieux.`
            : `WR ${lWR}% on 1-trade days vs ${hWR}% on 4+ trade days. Less is more.`
        });

        if (alerts.length === 0) return null;
        const ACL = {
          danger:  { bg:"rgba(248,113,113,0.07)",  border:"rgba(248,113,113,0.22)",  dot:clrRed },
          warning: { bg:"rgba(232,205,169,0.07)",  border:"rgba(232,205,169,0.22)",  dot:"#e8cda9" },
          insight: { bg:"rgba(74,222,128,0.07)",   border:"rgba(74,222,128,0.22)",   dot:clrGreen },
        };
        return (
          <div style={{marginBottom:24}}>
            <div style={{fontSize:9,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.18em",textTransform:"uppercase",marginBottom:10}}>
              {IS_FR ? "Alertes intelligentes" : "Smart Alerts"}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {alerts.map((a,i) => {
                const cl = ACL[a.type];
                return (
                  <div key={i} style={{background:cl.bg,border:`1px solid ${cl.border}`,borderRadius:12,padding:"12px 14px",display:"flex",alignItems:"flex-start",gap:12}}>
                    <div style={{width:6,height:6,borderRadius:"50%",background:cl.dot,flexShrink:0,marginTop:5}}/>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12,fontWeight:600,color:C.white,fontFamily:"'Josefin Sans',sans-serif",marginBottom:3}}>{a.title}</div>
                      <div style={{fontSize:11,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",lineHeight:1.55}}>{a.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {statsWidgets.sections && (() => {
        const histFiltered2 = historyAccFilter === "all"
          ? trades
          : trades.filter(t => !t.accountIds || t.accountIds.length === 0 || t.accountIds.includes(historyAccFilter));
        if (histFiltered2.length === 0) return null;
        const calcBest = (groupFn) => {
          const groups = {};
          histFiltered2.forEach(t => {
            const k = groupFn(t);
            if (!k) return;
            if (!groups[k]) groups[k] = {pnl:0,wins:0,total:0};
            groups[k].pnl += t.pnl||0;
            groups[k].total++;
            if (t.result==="WIN") groups[k].wins++;
          });
          return Object.entries(groups)
            .map(([k,v]) => ({name:k, pnl:v.pnl, wr:v.total?Math.round(v.wins/v.total*100):0, count:v.total}))
            .sort((a,b) => b.pnl - a.pnl);
        };
        const tagData = (() => {
          const m = {};
          histFiltered2.forEach(t => {
            (t.tags||[]).forEach(tag => {
              if (!m[tag]) m[tag] = { pnl:0, wins:0, total:0 };
              m[tag].pnl += t.pnl||0; m[tag].total++;
              if (t.result==="WIN") m[tag].wins++;
            });
          });
          return Object.entries(m).map(([name,v]) => ({ name, pnl:v.pnl, wr:v.total?Math.round(v.wins/v.total*100):0, count:v.total })).sort((a,b) => b.pnl-a.pnl);
        })();
        const sections = [
          { title:L.stats.secStrat, key:"sec_strat", sub:L.stats.secStratSub, info:lang==="en"?"Ranks strategies by total P&L. Shows which approach is most profitable.":"Classe tes stratégies par P&L total.", data: calcBest(t => { const s = strategies.find(s=>s.id===t.strategyId); return s?.name||null; }) },
          { title:L.stats.secSess,  key:"sec_sess",  sub:L.stats.secSessSub,  info:lang==="en"?"Compare performance by session (London, New York…). Identify when you trade best.":"Compare tes performances selon la session (London, New York…).", data: calcBest(t => t.session) },
          { title:L.stats.secInst,  key:"sec_inst",  sub:L.stats.secInstSub,  info:lang==="en"?"Ranks instruments by P&L. See where you have a real edge.":"Classe tes instruments par P&L.", data: calcBest(t => t.instrument) },
          { title:L.stats.secEmo,   key:"sec_emo",   sub:L.stats.secEmoSub,   info:lang==="en"?"Ranks emotions by P&L. If a negative emotion ranks high, you handle pressure well.":"Classe tes émotions par P&L. Si une émotion négative ressort en haut = tu gères la pression.", data: calcBest(t => t.emotion) },
          ...(tagData.length > 0 ? [{ title:"Badges", key:"sec_tags", sub:lang==="fr"?"Performance par badge":"Performance by badge", info:lang==="en"?"Ranks your badges by P&L. Identify which setups or behaviors are profitable.":"Classe tes badges par P&L. Identifie quels setups ou comportements sont rentables.", data: tagData }] : []),
        ];
        const ff2 = "'Josefin Sans',sans-serif";
        return (
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":`repeat(${Math.min(sections.length,5)},1fr)`,gap:10,marginBottom:20}}>
            {sections.map((sec) => (
              <div key={sec.title} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:"12px 12px 10px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                  <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:ff2,fontWeight:600}}>{sec.title}</div>
                  <button onClick={()=>setHistInfoCard(histInfoCard===sec.key?null:sec.key)} style={{width:14,height:14,borderRadius:"50%",border:`1px solid ${C.gray2}`,background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,flexShrink:0}}>
                    <span style={{fontSize:8,color:C.gray1,fontFamily:ff2,fontWeight:600,lineHeight:1}}>i</span>
                  </button>
                </div>
                <div style={{overflow:"hidden",maxHeight:histInfoCard===sec.key?"160px":"0",opacity:histInfoCard===sec.key?1:0,transition:"max-height 0.38s cubic-bezier(0.4,0,0.2,1), opacity 0.22s ease"}}>
                  <div style={{fontSize:9,color:C.dim,fontFamily:ff2,marginBottom:8,lineHeight:1.55,paddingTop:2}}>{sec.info}</div>
                </div>
                {sec.data.length === 0 ? (
                  <div style={{fontSize:10,color:C.gray2,fontFamily:ff2}}>—</div>
                ) : sec.data.slice(0,3).map((item,i) => (
                  <div key={item.name} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0",borderTop:i===0?"none":`1px solid ${C.border}`}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,minWidth:0}}>
                      <span style={{fontSize:9,color:C.gray2,fontFamily:ff2,flexShrink:0}}>#{i+1}</span>
                      <span style={{fontSize:11,color:i===0?C.white:C.gray1,fontFamily:ff2,fontWeight:i===0?600:300,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.name}</span>
                    </div>
                    <span style={{fontSize:11,color:item.pnl>=0?"#4caf6e":"#e05a5a",fontFamily:ff2,fontWeight:600,flexShrink:0,marginLeft:6}}>{fms(item.pnl)}{currency}</span>
                  </div>
                ))}
                {(sec.key==="sec_sess"||sec.key==="sec_inst")&&sec.data.length>0&&(()=>{
                  const top3=sec.data.slice(0,3);
                  const maxAbs=Math.max(...top3.map(x=>Math.abs(x.pnl)),1);
                  return (
                    <div style={{marginTop:8,paddingTop:6,borderTop:`1px solid ${C.border}`}}>
                      {top3.map(item=>(
                        <div key={item.name} style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                          <div style={{fontSize:8,color:C.gray2,fontFamily:ff2,width:48,flexShrink:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.name}</div>
                          <div style={{flex:1,height:4,background:darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)",borderRadius:2,overflow:"hidden"}}>
                            <div style={{width:(Math.abs(item.pnl)/maxAbs*100)+"%",height:"100%",borderRadius:2,background:item.pnl>=0?"rgba(74,222,128,0.7)":"rgba(248,113,113,0.7)",transition:"width 0.5s"}}/>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        );
      })()}
      {/* ── MEILLEURES HEURES ── */}
      {statsWidgets.hourly && (() => {
        const hFiltered = historyAccFilter === "all"
          ? trades
          : trades.filter(t => !t.accountIds || t.accountIds.length === 0 || t.accountIds.includes(historyAccFilter));
        const withTime = hFiltered.filter(t => t.entryTime);
        if (withTime.length < 5) return null;
        const ff = "'Josefin Sans',sans-serif";
        const cardS = { background:C.bg2, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:cShadow };

        const hourMap = {};
        withTime.forEach(t => {
          const local = utcToLocal(t.entryTime) || (t.entryTime||"").trim();
          const h = parseInt(local, 10);
          if (isNaN(h) || h < 0 || h > 23) return;
          if (!hourMap[h]) hourMap[h] = { pnl:0, wins:0, total:0 };
          hourMap[h].pnl += t.pnl||0;
          hourMap[h].total++;
          if (t.result==="WIN") hourMap[h].wins++;
        });
        const hours = Object.entries(hourMap)
          .map(([h,v]) => ({ h:parseInt(h), pnl:v.pnl, wr:v.total?Math.round(v.wins/v.total*100):0, count:v.total }))
          .sort((a,b) => a.h - b.h);
        if (hours.length < 2) return null;
        const maxAbs = Math.max(...hours.map(h => Math.abs(h.pnl)), 1);
        const best = [...hours].sort((a,b) => b.pnl - a.pnl)[0];
        const worst = [...hours].sort((a,b) => a.pnl - b.pnl)[0];

        return (
          <div style={{ ...cardS, padding:"16px", marginBottom:24 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
              <div>
                <div style={{ fontSize:9, color:C.dim, fontFamily:ff, fontWeight:600, letterSpacing:"0.18em", textTransform:"uppercase", marginBottom:3 }}>{lang==="fr"?"Analyse par heure":"Hourly analysis"}</div>
                <div style={{ fontSize:10, color:C.gray2, fontFamily:ff }}>{lang==="fr"?"P&L et WR selon l'heure d'entrée":"P&L and WR by entry time"}</div>
              </div>
              <div style={{ display:"flex", gap:16 }}>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontSize:9, color:clrGreen, fontFamily:ff, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:2 }}>{lang==="fr"?"Meilleure":"Best"}</div>
                  <div style={{ fontSize:16, fontFamily:ff, fontWeight:600, color:clrGreen }}>{String(best.h).padStart(2,"0")}h</div>
                  <div style={{ fontSize:9, color:C.gray2, fontFamily:ff }}>{best.wr}% WR</div>
                </div>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontSize:9, color:clrRed, fontFamily:ff, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:2 }}>{lang==="fr"?"Pire":"Worst"}</div>
                  <div style={{ fontSize:16, fontFamily:ff, fontWeight:600, color:clrRed }}>{String(worst.h).padStart(2,"0")}h</div>
                  <div style={{ fontSize:9, color:C.gray2, fontFamily:ff }}>{worst.wr}% WR</div>
                </div>
              </div>
            </div>
            <div style={{display:"flex",gap:3,marginBottom:8}}>
              {Array.from({length:24},(_,i)=>i).map(h=>{
                const found = hours.find(x=>x.h===h);
                if(found){
                  const {pnl,wr,count}=found;
                  const barH = Math.max(4, Math.round(Math.abs(pnl)/maxAbs*64));
                  return (
                    <div key={h} title={`${String(h).padStart(2,"0")}h — ${count} trade${count>1?"s":""}, WR ${wr}%, P&L ${fms(pnl)}${currency}`} style={{flex:1,minWidth:18,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                      <div style={{fontSize:7,color:C.gray2,fontFamily:ff}}>{count}</div>
                      <div style={{width:"100%",height:68,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
                        <div style={{width:"80%",height:barH,borderRadius:"3px 3px 1px 1px",background:pnl>=0?"rgba(74,222,128,0.75)":"rgba(248,113,113,0.75)",transition:"height 0.4s"}}/>
                      </div>
                      <div style={{fontSize:7,color:C.gray1,fontFamily:ff}}>{String(h).padStart(2,"0")}h</div>
                    </div>
                  );
                }
                return (
                  <div key={h} style={{flex:1,minWidth:18,display:"flex",flexDirection:"column",alignItems:"center",gap:2,opacity:0.4}}>
                    <div style={{fontSize:7,color:"transparent",fontFamily:ff}}>0</div>
                    <div style={{width:"100%",height:68,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
                      <div style={{width:"80%",height:6,borderRadius:"3px 3px 1px 1px",background:darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.08)"}}/>
                    </div>
                    <div style={{fontSize:7,color:C.gray2,fontFamily:ff}}>{String(h).padStart(2,"0")}h</div>
                  </div>
                );
              })}
            </div>
            <div style={{ display:"flex", gap:16, marginTop:10, paddingTop:8, borderTop:`1px solid ${C.border}`, flexWrap:"wrap" }}>
              {hours.sort((a,b) => b.pnl - a.pnl).slice(0,3).map(({ h, pnl, wr }) => (
                <div key={h} style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <span style={{ fontSize:12, fontFamily:ff, fontWeight:600, color:C.white }}>{String(h).padStart(2,"0")}h</span>
                  <span style={{ fontSize:10, color:pnl>=0?clrGreen:clrRed, fontFamily:ff }}>{fms(pnl)}{currency}</span>
                  <span style={{ fontSize:9, color:C.gray2, fontFamily:ff }}>{wr}%</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── HEATMAP ── */}
      {statsWidgets.heatmap && (() => {
        const hmFiltered = historyAccFilter === "all"
          ? trades
          : trades.filter(t => !t.accountIds || t.accountIds.length === 0 || t.accountIds.includes(historyAccFilter));
        if (hmFiltered.length === 0) return null;

        const dayPnl = {};
        hmFiltered.forEach(t => { dayPnl[t.date] = (dayPnl[t.date] || 0) + (t.pnl || 0); });

        const today = new Date();
        const months = [];
        for (let m = 11; m >= 0; m--) {
          const d = new Date(today.getFullYear(), today.getMonth() - m, 1);
          months.push({ year: d.getFullYear(), month: d.getMonth() });
        }

        const allVals = Object.values(dayPnl).filter(v => v !== 0);
        const maxAbs = allVals.length ? Math.max(...allVals.map(Math.abs)) : 1;

        const getColor = (pnl) => {
          if (pnl === undefined) return "transparent";
          if (pnl === 0) return C.bg3;
          const intensity = Math.min(1, Math.abs(pnl) / maxAbs);
          if (pnl > 0) return `rgba(74,222,128,${0.15 + intensity * 0.7})`;
          return `rgba(248,113,113,${0.15 + intensity * 0.7})`;
        };


        const yearMonths = [];
        for (let m = 0; m < 12; m++) yearMonths.push({ year: hmYear, month: m });

        const MONTH_NAMES_FR = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"];
        const MONTH_NAMES_EN = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
        const MN = lang === "fr" ? MONTH_NAMES_FR : MONTH_NAMES_EN;
        const DAY_LABELS = lang === "fr" ? ["L","M","M","J","V","S","D"] : ["M","T","W","T","F","S","S"];

        const years = [...new Set(Object.keys(dayPnl).map(d => d.slice(0,4)))].sort().reverse();

        return (
          <div style={{marginBottom:24}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
              <div style={{fontSize:9,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.18em",textTransform:"uppercase"}}>
                {lang==="fr" ? "Heatmap de performance" : "Performance heatmap"}
              </div>
              <div style={{display:"flex",gap:4}}>
                {years.map(y => (
                  <button key={y} onClick={()=>setHmYear(Number(y))} style={{padding:"3px 10px",borderRadius:20,border:`1px solid ${hmYear===Number(y)?C.accent:C.border}`,background:hmYear===Number(y)?"rgba(232,205,169,0.08)":"transparent",color:hmYear===Number(y)?C.accent:C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",cursor:"pointer",transition:"all 0.15s"}}>{y}</button>
                ))}
              </div>
            </div>
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:"16px",boxShadow:cShadow,overflowX:"auto"}}>
              <div style={{display:"flex",gap:6,minWidth:600,position:"relative"}}>
                <div style={{display:"flex",flexDirection:"column",gap:3,paddingTop:20}}>
                  {DAY_LABELS.map((d,i)=>(
                    <div key={i} style={{height:14,fontSize:8,color:C.gray2,fontFamily:"'Josefin Sans',sans-serif",display:"flex",alignItems:"center",width:10}}>{i%2===0?d:""}</div>
                  ))}
                </div>
                {yearMonths.map(({year,month}) => {
                  const firstDay = new Date(year, month, 1);
                  const daysInMonth = new Date(year, month+1, 0).getDate();
                  const startDow = (firstDay.getDay()+6)%7;
                  const cells = [];
                  for (let i=0; i<startDow; i++) cells.push(null);
                  for (let d=1; d<=daysInMonth; d++) cells.push(d);

                  return (
                    <div key={`${year}-${month}`} style={{display:"flex",flexDirection:"column",flex:1,minWidth:36}}>
                      <div style={{fontSize:8,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.05em",marginBottom:4,textAlign:"center"}}>{MN[month]}</div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3}}>
                        {cells.map((day, ci) => {
                          if (!day) return <div key={ci} style={{height:14,borderRadius:2}}/>;
                          const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
                          const pnl = dayPnl[dateStr];
                          const hasTrades = pnl !== undefined;
                          const isFuture = new Date(dateStr) > today;
                          return (
                            <div
                              key={ci}
                              onMouseEnter={e => { if(hasTrades) setHmTooltip({dateStr,pnl,x:e.clientX,y:e.clientY}); }}
                              onMouseLeave={() => setHmTooltip(null)}
                              style={{
                                height:14,borderRadius:2,cursor:hasTrades?"pointer":"default",
                                background:isFuture?"transparent":hasTrades?getColor(pnl):C.bg3,
                                border:hasTrades?`1px solid ${pnl>0?"rgba(74,222,128,0.3)":"rgba(248,113,113,0.3)"}`:pnl===0?`1px solid ${C.border}`:"none",
                                transition:"opacity 0.15s",opacity:isFuture?0:1,
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:12,marginTop:12,justifyContent:"space-between",flexWrap:"wrap"}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <div style={{display:"flex",gap:3}}>
                    {[0.15,0.4,0.7,0.85,1].map((op,i)=>(
                      <div key={i} style={{width:14,height:14,borderRadius:2,background:`rgba(248,113,113,${op})`}}/>
                    ))}
                  </div>
                  <span style={{fontSize:8,color:C.gray2,fontFamily:"'Josefin Sans',sans-serif"}}>{lang==="fr"?"Perte":"Loss"}</span>
                  <div style={{width:1,height:14,background:C.border,margin:"0 4px"}}/>
                  <div style={{display:"flex",gap:3}}>
                    {[0.15,0.4,0.7,0.85,1].map((op,i)=>(
                      <div key={i} style={{width:14,height:14,borderRadius:2,background:`rgba(74,222,128,${op})`}}/>
                    ))}
                  </div>
                  <span style={{fontSize:8,color:C.gray2,fontFamily:"'Josefin Sans',sans-serif"}}>{lang==="fr"?"Gain":"Gain"}</span>
                </div>
                {hmTooltip && (
                  <div style={{fontSize:10,color:C.white,fontFamily:"'Josefin Sans',sans-serif",background:C.bg3,border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 10px"}}>
                    {hmTooltip.dateStr} · {fms(hmTooltip.pnl)}{currency}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── BEST DAY OF WEEK ── */}
      {statsWidgets.bestday && (() => {
        const bdFiltered = historyAccFilter==="all" ? trades : trades.filter(t=>!t.accountIds||t.accountIds.length===0||t.accountIds.includes(historyAccFilter));
        if (bdFiltered.length < 5) return null;
        const ff = "'Josefin Sans',sans-serif";
        const cardS = { background:C.bg2, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:cShadow };
        const dayNames = lang==="fr" ? ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"] : ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
        const dayMap = {};
        bdFiltered.forEach(t => {
          const dow = (new Date(t.date+"T12:00:00").getDay()+6)%7;
          if (!dayMap[dow]) dayMap[dow]={pnl:0,wins:0,total:0};
          dayMap[dow].pnl += t.pnl||0; dayMap[dow].total++;
          if (t.result==="WIN") dayMap[dow].wins++;
        });
        const days = [0,1,2,3,4,5,6].filter(d=>dayMap[d]).map(d=>({ d, name:dayNames[d], pnl:dayMap[d].pnl, wr:Math.round(dayMap[d].wins/dayMap[d].total*100), count:dayMap[d].total }));
        if (days.length < 2) return null;
        const best = [...days].sort((a,b)=>b.pnl-a.pnl)[0];
        const worst = [...days].sort((a,b)=>a.pnl-b.pnl)[0];
        const maxAbs = Math.max(...days.map(d=>Math.abs(d.pnl)),1);
        return (
          <div style={{...cardS,padding:"16px",marginBottom:10}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
              <div>
                <div style={{fontSize:9,color:C.dim,fontFamily:ff,fontWeight:600,letterSpacing:"0.18em",textTransform:"uppercase",marginBottom:3}}>{lang==="fr"?"Meilleur jour de la semaine":"Best day of week"}</div>
                <div style={{fontSize:10,color:C.gray2,fontFamily:ff}}>{lang==="fr"?"P&L et win rate par jour de marché":"P&L and win rate per trading day"}</div>
              </div>
              <div style={{display:"flex",gap:16}}>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:9,color:clrGreen,fontFamily:ff,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:2}}>{lang==="fr"?"Meilleur":"Best"}</div>
                  <div style={{fontSize:16,fontFamily:ff,fontWeight:600,color:clrGreen}}>{dayNames[best.d]}</div>
                  <div style={{fontSize:9,color:C.gray2,fontFamily:ff}}>{best.wr}% WR · {fms(best.pnl)}{currency}</div>
                </div>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:9,color:clrRed,fontFamily:ff,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:2}}>{lang==="fr"?"Pire":"Worst"}</div>
                  <div style={{fontSize:16,fontFamily:ff,fontWeight:600,color:clrRed}}>{dayNames[worst.d]}</div>
                  <div style={{fontSize:9,color:C.gray2,fontFamily:ff}}>{worst.wr}% WR · {fms(worst.pnl)}{currency}</div>
                </div>
              </div>
            </div>
            <div style={{display:"flex",gap:6}}>
              {days.map(({d,name,pnl,wr,count})=>{
                const isBest=d===best.d, isWorst=d===worst.d;
                const barH = Math.max(6,Math.round(Math.abs(pnl)/maxAbs*72));
                const bg = isBest?"rgba(74,222,128,0.85)":isWorst?"rgba(248,113,113,0.85)":pnl>=0?"rgba(74,222,128,0.35)":"rgba(248,113,113,0.35)";
                return (
                  <div key={d} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}} title={`${name} — ${count} trade${count>1?"s":""}, WR ${wr}%, P&L ${fms(pnl)}${currency}`}>
                    <div style={{fontSize:7,color:C.gray2,fontFamily:ff}}>{count}</div>
                    <div style={{width:"100%",height:76,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
                      <div style={{width:"70%",height:barH,borderRadius:"3px 3px 1px 1px",background:bg,transition:"height 0.4s",boxShadow:isBest?"0 0 8px rgba(74,222,128,0.4)":isWorst?"0 0 8px rgba(248,113,113,0.4)":"none"}}/>
                    </div>
                    <div style={{fontSize:9,fontFamily:ff,fontWeight:600,color:isBest?clrGreen:isWorst?clrRed:C.gray1}}>{name}</div>
                    <div style={{fontSize:8,color:C.gray2,fontFamily:ff}}>{wr}%</div>
                    <div style={{fontSize:8,color:pnl>=0?clrGreen:clrRed,fontFamily:ff,fontWeight:500}}>{fms(pnl)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── RECOVERY + STOP TIME ── */}
      {(statsWidgets.recovery || statsWidgets.stoptime) && <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":(statsWidgets.recovery&&statsWidgets.stoptime)?"1fr 1fr":"1fr",gap:10,marginBottom:10,alignItems:"start"}}>
      {statsWidgets.recovery && (() => {
        const rfFiltered = historyAccFilter==="all" ? trades : trades.filter(t=>!t.accountIds||t.accountIds.length===0||t.accountIds.includes(historyAccFilter));
        if (rfFiltered.length < 5) return null;
        const ff = "'Josefin Sans',sans-serif";
        const cardS = { background:C.bg2, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:cShadow };
        const sorted = [...rfFiltered].sort((a,b)=>{ if(a.date!==b.date) return a.date<b.date?-1:1; return (a.entryTime||"")<(b.entryTime||"")?-1:1; });
        const recoveries = [];
        for (let i=0;i<sorted.length;i++) {
          if (sorted[i].result==="LOSS") {
            let run=sorted[i].pnl||0; let count=0;
            for (let j=i+1;j<sorted.length&&run<0;j++) { run+=sorted[j].pnl||0; count++; }
            if (run>=0) recoveries.push(count);
          }
        }
        if (recoveries.length<2) return null;
        const avg=Math.round(recoveries.reduce((s,v)=>s+v,0)/recoveries.length*10)/10;
        const best=Math.min(...recoveries);
        const worst=Math.max(...recoveries);
        const maxR=worst||1;
        const show=recoveries.slice(-20);
        return (
          <div style={{...cardS,padding:"12px 14px"}}>
            <div style={{fontSize:9,color:C.dim,fontFamily:ff,fontWeight:600,letterSpacing:"0.18em",textTransform:"uppercase",marginBottom:8}}>Recovery Factor</div>
            <div style={{display:"flex",gap:12,marginBottom:10}}>
              {[{l:lang==="fr"?"Moy.":"Avg",v:avg,c:C.white},{l:lang==="fr"?"Meilleur":"Best",v:best,c:clrGreen},{l:lang==="fr"?"Pire":"Worst",v:worst,c:clrRed}].map(s=>(
                <div key={s.l} style={{flex:1,background:darkMode?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.03)",borderRadius:8,padding:"8px 6px",textAlign:"center"}}>
                  <div style={{fontSize:7,color:C.dim,fontFamily:ff,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:3}}>{s.l}</div>
                  <div style={{fontSize:16,fontFamily:ff,fontWeight:600,color:s.c,lineHeight:1}}>{s.v}</div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:3,alignItems:"flex-end",height:28}}>
              {show.map((r,i)=>{
                const h=Math.max(2,Math.round(r/maxR*24));
                const ratio=r/avg;
                const bg=ratio<=0.8?"rgba(74,222,128,0.8)":ratio<=1.2?"rgba(232,205,169,0.7)":"rgba(248,113,113,0.75)";
                return (
                  <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:1}} title={`${r} trade${r>1?"s":""}`}>
                    <div style={{fontSize:6,color:C.gray2,fontFamily:ff}}>{r}</div>
                    <div style={{width:"70%",height:h,borderRadius:"3px 3px 1px 1px",background:bg}}/>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
      {statsWidgets.stoptime && (() => {
        const stFiltered = historyAccFilter==="all" ? trades : trades.filter(t=>!t.accountIds||t.accountIds.length===0||t.accountIds.includes(historyAccFilter));
        const withTime = stFiltered.filter(t=>t.entryTime);
        if (withTime.length < 4) return null;
        const ff = "'Josefin Sans',sans-serif";
        const cardS = { background:C.bg2, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:cShadow };
        const hourMap = {};
        withTime.forEach(t=>{
          const local = utcToLocal(t.entryTime) || (t.entryTime||"").trim();
          const h=parseInt(local,10);
          if(isNaN(h)||h<0||h>23) return;
          if(!hourMap[h]) hourMap[h]={wins:0,total:0,pnl:0};
          hourMap[h].total++; hourMap[h].pnl+=t.pnl||0;
          if(t.result==="WIN") hourMap[h].wins++;
        });
        const hrs = Object.entries(hourMap).map(([h,v])=>({h:parseInt(h),wr:v.total?v.wins/v.total:0,pnl:v.pnl,total:v.total})).sort((a,b)=>a.h-b.h);
        if(hrs.length<2) return null;
        let bestSplit=null, bestDiff=0;
        for(let s=1;s<hrs.length;s++){
          const before=hrs.slice(0,s), after=hrs.slice(s);
          const wBefore=before.reduce((a,h)=>a+h.wins*100,0)/before.reduce((a,h)=>a+h.total,0);
          const wAfter=after.reduce((a,h)=>a+h.wins*100,0)/after.reduce((a,h)=>a+h.total,0);
          if((wBefore-wAfter)>bestDiff){ bestDiff=wBefore-wAfter; bestSplit=hrs[s]; }
        }
        const hasAlert = bestSplit && bestDiff>10;
        const maxAbsPnl = Math.max(...hrs.map(h=>Math.abs(h.pnl)),1);
        return (
          <div style={{...cardS,padding:"12px 14px"}}>
            <div style={{fontSize:9,color:C.dim,fontFamily:ff,fontWeight:600,letterSpacing:"0.18em",textTransform:"uppercase",marginBottom:8}}>{lang==="fr"?"Heure limite optimale":"Optimal stop time"}</div>
            {hasAlert && (
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8,padding:"5px 8px",background:"rgba(248,113,113,0.08)",border:"1px solid rgba(248,113,113,0.25)",borderRadius:6}}>
                <div style={{width:5,height:5,borderRadius:"50%",background:clrRed,flexShrink:0,boxShadow:`0 0 5px ${clrRed}`}}/>
                <div style={{fontSize:9,fontFamily:ff,fontWeight:600,color:clrRed}}>
                  {lang==="fr"?`Dégradation après ${String(bestSplit.h).padStart(2,"0")}h (−${Math.round(bestDiff)}% WR)`:`Degrades after ${String(bestSplit.h).padStart(2,"0")}:00 (−${Math.round(bestDiff)}% WR)`}
                </div>
              </div>
            )}
            <div style={{display:"flex",gap:3,alignItems:"flex-end",height:28}}>
              {hrs.map(({h,wr,pnl,total})=>{
                const isAfter=hasAlert&&h>=bestSplit.h;
                const barH=Math.max(2,Math.round(Math.abs(pnl)/maxAbsPnl*22));
                const bg=isAfter?"rgba(248,113,113,0.6)":pnl>=0?"rgba(74,222,128,0.65)":"rgba(248,113,113,0.65)";
                return (
                  <div key={h} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-end",height:"100%",position:"relative"}} title={`${String(h).padStart(2,"0")}h — ${total} trades, WR ${Math.round(wr*100)}%, P&L ${fms(pnl)}${currency}`}>
                    {hasAlert&&bestSplit&&h===bestSplit.h&&<div style={{position:"absolute",left:0,top:0,bottom:0,width:1,background:clrRed,opacity:0.6}}/>}
                    <div style={{width:"70%",height:barH,borderRadius:"2px 2px 1px 1px",background:bg}}/>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
      </div>}

      {/* ── REVENGE TRADING DETECTION ── */}
      {statsWidgets.revenge && (() => {
        const rvFiltered = historyAccFilter==="all" ? trades : trades.filter(t=>!t.accountIds||t.accountIds.length===0||t.accountIds.includes(historyAccFilter));
        if (rvFiltered.length < 5) return null;
        const ff = "'Josefin Sans',sans-serif";
        const cardS = { background:C.bg2, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:cShadow };
        const toMin = s => { if(!s) return null; const p=s.split(/[h:]/); const h=parseInt(p[0],10); const m=parseInt(p[1]||"0",10); return isNaN(h)?null:h*60+m; };
        const sorted = [...rvFiltered].sort((a,b)=>{ if(a.date!==b.date) return a.date<b.date?-1:1; return (a.entryTime||"")<(b.entryTime||"")?-1:1; });
        const revengeIdx = new Set();
        for(let i=0;i<sorted.length-1;i++){
          if(sorted[i].result!=="LOSS") continue;
          const next=sorted[i+1];
          if(next.date!==sorted[i].date) continue;
          const exitMin=toMin(sorted[i].exitTime||sorted[i].entryTime);
          const nextMin=toMin(next.entryTime);
          if(exitMin===null||nextMin===null) continue;
          if(nextMin-exitMin>=0&&nextMin-exitMin<=15) revengeIdx.add(i+1);
        }
        const revengeTrades=sorted.filter((_,i)=>revengeIdx.has(i));
        const normalTrades=sorted.filter((_,i)=>!revengeIdx.has(i));
        if(revengeTrades.length===0){
          return (
            <div style={{...cardS,padding:"16px",marginBottom:10}}>
              <div style={{fontSize:9,color:C.dim,fontFamily:ff,fontWeight:600,letterSpacing:"0.18em",textTransform:"uppercase",marginBottom:6}}>{lang==="fr"?"Revenge Trading":"Revenge Trading"}</div>
              <div style={{display:"flex",alignItems:"center",gap:8,padding:"12px",background:"rgba(74,222,128,0.06)",border:"1px solid rgba(74,222,128,0.2)",borderRadius:8}}>
                <div style={{width:6,height:6,borderRadius:"50%",background:clrGreen,boxShadow:`0 0 6px ${clrGreen}`}}/>
                <div style={{fontSize:11,fontFamily:ff,color:clrGreen}}>{lang==="fr"?"Aucun revenge trade détecté sur ce dataset":"No revenge trades detected in this dataset"}</div>
              </div>
            </div>
          );
        }
        const rvWins=revengeTrades.filter(t=>t.result==="WIN").length;
        const rvWR=Math.round(rvWins/revengeTrades.length*100);
        const rvPnl=revengeTrades.reduce((s,t)=>s+(t.pnl||0),0);
        const nmWins=normalTrades.filter(t=>t.result==="WIN").length;
        const nmWR=nmWins>0?Math.round(nmWins/normalTrades.length*100):0;
        const nmPnl=normalTrades.reduce((s,t)=>s+(t.pnl||0),0);
        const maxWR=Math.max(rvWR,nmWR,1);
        return (
          <div style={{...cardS,padding:"16px",marginBottom:10}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
              <div>
                <div style={{fontSize:9,color:C.dim,fontFamily:ff,fontWeight:600,letterSpacing:"0.18em",textTransform:"uppercase",marginBottom:3}}>{lang==="fr"?"Revenge Trading Detection":"Revenge Trading Detection"}</div>
                <div style={{fontSize:10,color:C.gray2,fontFamily:ff}}>{lang==="fr"?"Trade entré dans les 15 min après une perte":"Trade entered within 15 min after a loss"}</div>
              </div>
              <div style={{background:"rgba(248,113,113,0.1)",border:"1px solid rgba(248,113,113,0.3)",borderRadius:8,padding:"8px 14px",textAlign:"center"}}>
                <div style={{fontSize:22,fontFamily:ff,fontWeight:600,color:clrRed}}>{revengeTrades.length}</div>
                <div style={{fontSize:8,color:C.gray2,fontFamily:ff}}>{lang==="fr"?"cas détectés":"detected"}</div>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
              {[
                {label:lang==="fr"?"Trades normaux":"Normal trades",wr:nmWR,pnl:nmPnl,count:normalTrades.length,color:clrGreen},
                {label:lang==="fr"?"Revenge trades":"Revenge trades",wr:rvWR,pnl:rvPnl,count:revengeTrades.length,color:clrRed},
              ].map(({label,wr,pnl,count,color})=>(
                <div key={label} style={{background:C.bg3,borderRadius:8,padding:"10px 12px",border:`1px solid ${C.border}`}}>
                  <div style={{fontSize:8,color:C.gray2,fontFamily:ff,marginBottom:6}}>{label} ({count})</div>
                  <div style={{fontSize:16,fontFamily:ff,fontWeight:600,color,marginBottom:4}}>{wr}% WR</div>
                  <div style={{height:4,borderRadius:2,background:darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.08)",marginBottom:6}}>
                    <div style={{height:"100%",width:`${Math.round(wr/maxWR*100)}%`,borderRadius:2,background:color,transition:"width 0.6s"}}/>
                  </div>
                  <div style={{fontSize:9,color:pnl>=0?clrGreen:clrRed,fontFamily:ff,fontWeight:500}}>{fms(pnl)}{currency}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── SETUP SCORING ── */}
      {statsWidgets.setup && (() => {
        const ssFiltered = historyAccFilter==="all" ? trades : trades.filter(t=>!t.accountIds||t.accountIds.length===0||t.accountIds.includes(historyAccFilter));
        if (ssFiltered.length < 3) return null;
        const ff = "'Josefin Sans',sans-serif";
        const cardS = { background:C.bg2, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:cShadow };
        const tagData = {};
        ssFiltered.forEach(t=>{
          (t.tags||[]).forEach(tag=>{
            const {name}=parseBadge(tag);
            if(!name) return;
            if(!tagData[name]) tagData[name]={pnl:0,wins:0,total:0};
            tagData[name].pnl+=t.pnl||0; tagData[name].total++;
            if(t.result==="WIN") tagData[name].wins++;
          });
        });
        const setups=Object.entries(tagData).map(([name,v])=>({name,pnl:v.pnl,wr:v.total?Math.round(v.wins/v.total*100):0,avg:v.total?v.pnl/v.total:0,count:v.total})).filter(s=>s.count>=2).sort((a,b)=>b.pnl-a.pnl);
        if(setups.length<2) return null;
        const maxAbsPnl=Math.max(...setups.map(s=>Math.abs(s.pnl)),1);
        return (
          <div style={{...cardS,padding:"16px",marginBottom:10}}>
            <div style={{marginBottom:14}}>
              <div style={{fontSize:9,color:C.dim,fontFamily:ff,fontWeight:600,letterSpacing:"0.18em",textTransform:"uppercase",marginBottom:3}}>{lang==="fr"?"Setup Scoring":"Setup Scoring"}</div>
              <div style={{fontSize:10,color:C.gray2,fontFamily:ff}}>{lang==="fr"?"Classement de tes setups par P&L total (min. 2 trades)":"Your setups ranked by total P&L (min. 2 trades)"}</div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:7}}>
              {setups.map(({name,pnl,wr,avg,count},i)=>{
                const barW=Math.round(Math.abs(pnl)/maxAbsPnl*100);
                const isPos=pnl>=0;
                const medal=i===0?"#E8CDA9":i===1?"#aaa":i===2?"#cd7f32":null;
                return (
                  <div key={name} style={{display:"grid",gridTemplateColumns:"14px 80px 1fr 44px 54px",gap:8,alignItems:"center"}}>
                    <div style={{fontSize:8,fontFamily:ff,fontWeight:600,color:medal||C.gray2,textAlign:"center"}}>{medal?["①","②","③"][i]:i+1}</div>
                    <div style={{fontSize:9,fontFamily:ff,fontWeight:600,color:C.white,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={name}>{name}</div>
                    <div style={{position:"relative",height:6,borderRadius:3,background:darkMode?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.07)"}}>
                      <div style={{position:"absolute",left:0,top:0,height:"100%",width:`${barW}%`,borderRadius:3,background:isPos?"rgba(74,222,128,0.7)":"rgba(248,113,113,0.7)",transition:"width 0.6s"}}/>
                    </div>
                    <div style={{fontSize:9,fontFamily:ff,color:C.gray2,textAlign:"right"}}>{wr}%</div>
                    <div style={{fontSize:9,fontFamily:ff,fontWeight:500,color:isPos?clrGreen:clrRed,textAlign:"right"}}>{fms(pnl)}{currency}</div>
                  </div>
                );
              })}
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:12,marginTop:10,paddingTop:8,borderTop:`1px solid ${C.border}`}}>
              <div style={{fontSize:8,color:C.gray2,fontFamily:ff}}>{lang==="fr"?"WR %":"WR %"}</div>
              <div style={{fontSize:8,color:C.gray2,fontFamily:ff}}>{lang==="fr"?"P&L total":"Total P&L"}</div>
            </div>
          </div>
        );
      })()}

      <div style={{textAlign:"center",padding:"8px 0 0"}}>
        <button onClick={()=>setView("trades")} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 24px",color:C.gray1,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer"}}>{L.btn.seeHistory}</button>
      </div>
    </div>
  );

  const tradesContent = (
    <div>
      <PageTitle sub={L.pages.trades.sub} title={L.pages.trades.title} />
      {/* ── Filtre par compte ── */}
      {propfirms.length > 0 && (
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:16, paddingBottom:2 }}>
          {[{ id:"all", label:L.btn.all }, ...propfirms.map(p => ({ id:p.id, label:p.name || p.firm || (lang==="fr"?"Compte":"Account") }))].map(opt => (
            <button
              key={opt.id}
              onClick={() => setTradesAccFilter(opt.id)}
              style={{
                padding:"5px 14px", borderRadius:20, fontSize:10, cursor:"pointer",
                fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.1em", textTransform:"uppercase",
                border:`1px solid ${tradesAccFilter === opt.id ? C.accent : C.gray3}`,
                background: tradesAccFilter === opt.id ? "rgba(232,205,169,0.1)" : "transparent",
                color: tradesAccFilter === opt.id ? C.accent : C.gray1,
                transition:"all 0.15s",
              }}
            >{opt.label}</button>
          ))}
        </div>
      )}
      {/* ── Recherche & Filtres ── */}
      {(() => {
        const ff = "'Josefin Sans',sans-serif";
        const activeCount = [tradeFilters.result!=="all",tradeFilters.direction!=="all",tradeFilters.session!=="all",tradeFilters.dateFrom,tradeFilters.dateTo,tradeFilters.tag!=="all"].filter(Boolean).length;
        const allTradeTags = [...new Set(trades.flatMap(t=>t.tags||[]))];
        return (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <input
                type="text"
                placeholder={lang==="fr"?"Rechercher instrument, notes...":"Search instrument, notes..."}
                value={tradeSearch}
                onChange={e => setTradeSearch(e.target.value)}
                style={{ flex:1, background:C.bg2, border:`1px solid ${C.border}`, borderRadius:8, padding:"9px 12px", color:C.white, fontSize:12, fontFamily:ff, outline:"none" }}
              />
              <button onClick={() => setTradeFiltersOpen(o => !o)} style={{ padding:"9px 14px", borderRadius:8, border:`1px solid ${activeCount>0?C.accent:C.border}`, background:activeCount>0?"rgba(232,205,169,0.08)":"transparent", color:activeCount>0?C.accent:C.gray1, fontSize:11, fontFamily:ff, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap", transition:"all 0.15s" }}>
                {lang==="fr"?"Filtres":"Filters"}{activeCount>0?` · ${activeCount}`:""}
              </button>
              {(tradeSearch||activeCount>0) && (
                <button onClick={() => { setTradeSearch(""); setTradeFilters({ result:"all", direction:"all", session:"all", dateFrom:"", dateTo:"", tag:"all" }); setTradeFiltersOpen(false); }} style={{ padding:"9px 12px", borderRadius:8, border:`1px solid ${C.border}`, background:"transparent", color:C.gray2, fontSize:11, fontFamily:ff, cursor:"pointer" }}>✕</button>
              )}
            </div>
            {tradeFiltersOpen && (
              <div style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:10, padding:"14px", marginTop:8, display:"flex", flexDirection:"column", gap:12 }}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
                  {[
                    { label:lang==="fr"?"Résultat":"Result", key:"result", opts:[{v:"all",l:lang==="fr"?"Tous":"All"},{v:"WIN",l:"Win"},{v:"LOSS",l:"Loss"},{v:"BREAKEVEN",l:"BE"}] },
                    { label:lang==="fr"?"Direction":"Direction", key:"direction", opts:[{v:"all",l:lang==="fr"?"Tous":"All"},{v:"LONG",l:"Long"},{v:"SHORT",l:"Short"}] },
                    { label:"Session", key:"session", opts:[{v:"all",l:lang==="fr"?"Toutes":"All"},...["Asia","London","Overlap","New York"].map(s=>({v:s,l:s}))] },
                  ].map(f => (
                    <div key={f.key}>
                      <div style={{ fontSize:9, color:C.dim, fontFamily:ff, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:6 }}>{f.label}</div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                        {f.opts.map(o => (
                          <button key={o.v} onClick={() => setTradeFilters(p=>({...p,[f.key]:o.v}))} style={{ padding:"3px 10px", borderRadius:20, border:`1px solid ${tradeFilters[f.key]===o.v?C.accent:C.border}`, background:tradeFilters[f.key]===o.v?"rgba(232,205,169,0.08)":"transparent", color:tradeFilters[f.key]===o.v?C.accent:C.gray1, fontSize:10, fontFamily:ff, cursor:"pointer", transition:"all 0.15s" }}>{o.l}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  {[{key:"dateFrom",label:lang==="fr"?"Depuis":"From"},{key:"dateTo",label:lang==="fr"?"Jusqu'au":"To"}].map(f => (
                    <div key={f.key}>
                      <div style={{ fontSize:9, color:C.dim, fontFamily:ff, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:6 }}>{f.label}</div>
                      <input type="date" value={tradeFilters[f.key]} onChange={e => setTradeFilters(p=>({...p,[f.key]:e.target.value}))} style={{ width:"100%", background:C.bg3, border:`1px solid ${C.border}`, borderRadius:8, padding:"7px 10px", color:C.white, fontSize:12, fontFamily:ff, outline:"none", boxSizing:"border-box" }}/>
                    </div>
                  ))}
                </div>
                {allTradeTags.length > 0 && (
                  <div>
                    <div style={{ fontSize:9, color:C.dim, fontFamily:ff, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:6 }}>Badge</div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                      <button onClick={() => setTradeFilters(p=>({...p,tag:"all"}))} style={{ padding:"3px 10px", borderRadius:20, border:`1px solid ${tradeFilters.tag==="all"?C.accent:C.border}`, background:tradeFilters.tag==="all"?"rgba(232,205,169,0.08)":"transparent", color:tradeFilters.tag==="all"?C.accent:C.gray1, fontSize:10, fontFamily:ff, cursor:"pointer" }}>{lang==="fr"?"Tous":"All"}</button>
                      {allTradeTags.map(t => (
                        <button key={t} onClick={() => setTradeFilters(p=>({...p,tag:t}))} style={{ padding:"3px 10px", borderRadius:20, border:`1px solid ${tradeFilters.tag===t?C.accent:C.border}`, background:tradeFilters.tag===t?"rgba(232,205,169,0.08)":"transparent", color:tradeFilters.tag===t?C.accent:C.gray1, fontSize:10, fontFamily:ff, cursor:"pointer" }}>{parseBadge(t).name}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {(() => {
        const accFiltered = tradesAccFilter === "all"
          ? trades
          : trades.filter(t => !t.accountIds || t.accountIds.length === 0 || t.accountIds.includes(tradesAccFilter));
        const q = tradeSearch.toLowerCase().trim();
        const filtered = accFiltered.filter(t => {
          if (q && !((t.instrument||"").toLowerCase().includes(q) || (t.notes||"").toLowerCase().includes(q) || (t.date||"").includes(q) || (t.tags||[]).some(tag=>tag.includes(q)))) return false;
          if (tradeFilters.result !== "all" && t.result !== tradeFilters.result) return false;
          if (tradeFilters.direction !== "all" && t.direction !== tradeFilters.direction) return false;
          if (tradeFilters.session !== "all" && t.session !== tradeFilters.session) return false;
          if (tradeFilters.dateFrom && t.date < tradeFilters.dateFrom) return false;
          if (tradeFilters.dateTo   && t.date > tradeFilters.dateTo)   return false;
          if (tradeFilters.tag !== "all" && !(t.tags||[]).includes(tradeFilters.tag)) return false;
          return true;
        });
        const totalFiltered = filtered.length;
        const totalAll = accFiltered.length;
        const ff = "'Josefin Sans',sans-serif";
        return (
          <>
            {totalAll > 0 && (
              <div style={{ fontSize:10, color:C.gray2, fontFamily:ff, marginBottom:10, letterSpacing:"0.06em" }}>
                {totalFiltered === totalAll ? `${totalAll} trade${totalAll>1?"s":""}` : `${totalFiltered} / ${totalAll} trade${totalAll>1?"s":""}`}
              </div>
            )}
            {filtered.length === 0 ? (
              <div style={{ textAlign:"center", padding:"60px 0" }}>
                <div style={{ fontSize:44, marginBottom:10, color:C.gray2 }}>◎</div>
                <div style={{ fontFamily:ff, fontSize:14, fontWeight:300, color:C.gray1, letterSpacing:"0.08em" }}>{totalAll===0?L.btn.noTradesYet:(lang==="fr"?"Aucun résultat":"No results")}</div>
              </div>
            ) : (
              <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr 1fr":"repeat(auto-fill, 160px)", gap:10 }}>
                {[...filtered].sort((a, b) => b.date.localeCompare(a.date)).map((t, idx) => {
                  const pnl = t.pnl || 0; const isWin = t.result === "WIN"; const isLoss = t.result === "LOSS"; const isEditing = editingTrade?.id === t.id;
                  return (
                    <div key={t.id} className={!isEditing ? "ap-card" : ""} onClick={() => { if (!isEditing) setSelectedTradeDetail(t); }} style={{ background:C.bg2, border:`1px solid ${isEditing ? C.accent : isWin ? "rgba(74,222,128,0.15)" : isLoss ? "rgba(248,113,113,0.12)" : C.border}`, borderTop:`3px solid ${isEditing ? C.accent : isWin ? clrGreen : isLoss ? clrRed : C.gray3}`, borderRadius:12, boxShadow:cShadow, padding:14, aspectRatio:"1", display:"flex", flexDirection:"column", justifyContent:"space-between", overflow:"hidden", opacity:deletingTradeId===t.id ? 0 : 1, transform:deletingTradeId===t.id ? "scale(0.88)" : "scale(1)", transition:deletingTradeId===t.id ? "opacity 0.25s ease, transform 0.25s ease" : "border 0.2s, box-shadow 0.2s, transform 0.28s cubic-bezier(0.34,1.56,0.64,1)", cursor:isEditing?"default":"pointer", animationName:deletingTradeId===t.id ? "none" : "appleCardIn", animationDuration:"0.4s", animationTimingFunction:"cubic-bezier(0.34,1.2,0.64,1)", animationFillMode:"both", animationDelay:deletingTradeId===t.id ? "0s" : `${Math.min(idx*0.055,0.4)}s` }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                        <div>
                          <div style={{ fontFamily:"'Josefin Sans',sans-serif", fontWeight:700, fontSize:14, color:C.white, letterSpacing:"-0.02em", lineHeight:1.1 }}>{t.instrument}</div>
                          <div style={{ fontSize:9, color:C.gray1, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.12em", marginTop:3 }}>{t.direction}</div>
                        </div>
                        <div style={{ display:"flex", gap:4 }} onClick={e => e.stopPropagation()}>
                          {confirmDeleteTradeId === t.id ? (
                            <>
                              <button onClick={e => { e.stopPropagation(); setConfirmDeleteTradeId(null); setDeletingTradeId(t.id); setTimeout(async () => { await deleteTrade(t.id); setDeletingTradeId(null); }, 300); }} style={{ background:"rgba(200,60,60,0.15)", border:"1px solid rgba(200,60,60,0.4)", borderRadius:4, color:"#e07070", cursor:"pointer", fontSize:9, padding:"2px 6px", fontFamily:"'Josefin Sans',sans-serif", fontWeight:600 }}>{L.btn.yes}</button>
                              <button onClick={e => { e.stopPropagation(); setConfirmDeleteTradeId(null); }} style={{ background:"none", border:`1px solid ${C.gray3}`, borderRadius:4, color:C.gray1, cursor:"pointer", fontSize:9, padding:"2px 6px", fontFamily:"'Josefin Sans',sans-serif" }}>{L.btn.no}</button>
                            </>
                          ) : (
                            <>
                              <button onClick={e => { e.stopPropagation(); startEdit(t); }} style={{ background:"none", border:`1px solid ${C.gray3}`, borderRadius:4, color:C.gray1, cursor:"pointer", fontSize:10, padding:"2px 6px", fontFamily:"'Josefin Sans',sans-serif" }}>✎</button>
                              <button onClick={e => { e.stopPropagation(); setConfirmDeleteTradeId(t.id); }} style={{ background:"none", border:"none", color:C.gray2, cursor:"pointer", fontSize:16, lineHeight:1, padding:0 }}>×</button>
                            </>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign:"center" }}>
                        <div style={{ fontFamily:"'Josefin Sans',sans-serif", fontSize:22, fontWeight:300, color:isWin ? clrGreen : isLoss ? clrRed : C.dim, letterSpacing:"0.02em" }}>{fms(pnl)} €</div>
                        <div style={{ fontSize:9, color:isWin ? clrGreen : isLoss ? clrRed : C.dim, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.15em", opacity:0.7, marginTop:3 }}>{t.result}</div>
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end" }}>
                        <span style={{ fontSize:9, color:C.gray2, fontFamily:"'Josefin Sans',sans-serif", letterSpacing:"0.04em" }}>{t.date}</span>
                        {t.session && <span style={{ fontSize:9, color:C.gray2, fontFamily:"'Josefin Sans',sans-serif", letterSpacing:"0.04em" }}>{t.session}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        );
      })()}
    </div>
  );

  // ── Strategy JSX (multi) ──
  const updateStrat = (id, changes) => setStrategies(p => p.map(s => s.id===id ? {...s,...changes} : s));
  const saveStrategy = () => { save(KEYS.strategies, strategies); setStratSaved(true); setTimeout(()=>setStratSaved(false),2000); };

  const activeSid = activeStratId || (strategies[0]?.id);
  const strat = strategies.find(s=>s.id===activeSid) || strategies[0] || { id:0, name:"", description:"", steps:[], rules:"", notes:"" };

  const strategyContent = (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:16 }}>
        <PageTitle sub={L.pages.plan.sub} title={L.pages.plan.title} />
        <button onClick={()=>{ const ns={id:Date.now(),name:L.strat.newName,description:"",steps:[],rules:"",notes:""}; setStrategies(p=>[...p,ns]); setActiveStratId(ns.id); }} style={{ padding:"8px 14px", borderRadius:4, border:"none", background:"radial-gradient(ellipse 90% 90% at 50% 38%, rgba(245,245,245,0.95) 0%, rgba(215,215,215,0.88) 55%, rgba(230,230,230,0.92) 100%)", color:"#111", fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.1em", textTransform:"uppercase", cursor:"pointer", marginBottom:22 }}>{L.btn.newBtn}</button>
      </div>

      {/* Strategy tabs */}
      {strategies.length > 1 && (
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:16 }}>
          {strategies.map(s => (
            <button key={s.id} onClick={()=>setActiveStratId(s.id)} style={{ padding:"6px 12px", borderRadius:4, border:`1px solid ${s.id===activeSid?C.accent:C.border}`, background:s.id===activeSid?"rgba(0,0,0,0.08)":"transparent", color:s.id===activeSid?C.accent:C.gray1, fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight:s.id===activeSid?600:300, cursor:"pointer", letterSpacing:"0.06em" }}>{s.name||L.strat.unnamed}</button>
          ))}
        </div>
      )}

      {/* Strategy name */}
      <Field label={L.form.stratName}>
        <input type="text" value={strat.name||""} onChange={e=>updateStrat(strat.id,{name:e.target.value})} placeholder="" style={iStyle}/>
      </Field>
      <Field label={L.form.stratDesc}>
        <textarea rows={3} placeholder={L.strat.descPH} value={strat.description||""} onChange={e=>updateStrat(strat.id,{description:e.target.value})} style={{...iStyle,resize:"vertical",lineHeight:1.6,textAlign:"left"}}/>
      </Field>
      <div style={{ marginBottom:16 }}>
        <Label>{L.strat.steps}</Label>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {(strat.steps||[]).map((step, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:22, height:22, borderRadius:"50%", background:C.bg3, border:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, color:C.dim, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, flexShrink:0 }}>{i+1}</div>
              <input type="text" value={step} placeholder={L.strat.stepPH} onChange={e=>{const steps=[...(strat.steps||[])];steps[i]=e.target.value;updateStrat(strat.id,{steps});}} className="step-input" style={{...iStyle,flex:1,padding:"10px 12px",fontSize:14}}/>
              {(strat.steps||[]).length > 1 && <button onClick={()=>updateStrat(strat.id,{steps:(strat.steps||[]).filter((_,j)=>j!==i)})} style={{background:"none",border:"none",color:C.gray2,cursor:"pointer",fontSize:18,lineHeight:1,padding:"0 4px",flexShrink:0}}>×</button>}
            </div>
          ))}
          <button onClick={()=>updateStrat(strat.id,{steps:[...(strat.steps||[]),""]})} style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",borderRadius:6,border:`1px dashed ${C.gray2}`,background:"transparent",color:C.gray1,fontSize:12,fontFamily:"'Josefin Sans',sans-serif",cursor:"pointer",letterSpacing:"0.08em",marginTop:2}}>
            <span style={{fontSize:18,lineHeight:1}}>+</span> {L.strat.addStep}
          </button>
        </div>
      </div>
      <Field label={L.form.stratRules}>
        <textarea rows={3} placeholder={L.strat.rulesPH} value={strat.rules||""} onChange={e=>updateStrat(strat.id,{rules:e.target.value})} style={{...iStyle,resize:"vertical",lineHeight:1.9}}/>
      </Field>
      <Field label={L.strat.notesLabel}>
        <textarea rows={3} placeholder={L.strat.notesPH} value={strat.notes||""} onChange={e=>updateStrat(strat.id,{notes:e.target.value})} style={{...iStyle,resize:"vertical",lineHeight:1.6}}/>
      </Field>
      {strategies.length > 1 && (
        <button onClick={()=>{ setStrategies(p=>p.filter(s=>s.id!==strat.id)); setActiveStratId(null); }} style={{width:"100%",padding:"11px",borderRadius:4,border:"1px solid rgba(192,57,43,0.3)",background:"rgba(192,57,43,0.05)",color:"rgba(192,57,43,0.8)",fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.15em",textTransform:"uppercase",cursor:"pointer",marginBottom:8}}>
          {L.strat.delete}
        </button>
      )}
      <button onClick={saveStrategy} style={{width:"100%",padding:"14px",borderRadius:4,border:`1px solid ${C.borderGold}`,background:"rgba(0,0,0,0.04)",color:C.dim,fontSize:12,fontWeight:600,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.2em",textTransform:"uppercase",cursor:"pointer",transition:"all 0.3s"}}>
        {stratSaved ? L.btn.saved : `${L.btn.save}  →`}
      </button>
    </div>
  );

  // ── AI JSX ──
  const aiContent = (desktop) => {
    const ff = "'Josefin Sans',sans-serif";
    const isDark = darkMode;
    // ── Données pour les sections visuelles ──
    const T = aiAccFilter!=="all"
      ? trades.filter(t=>!t.accountIds||t.accountIds.length===0||t.accountIds.includes(aiAccFilter))
      : trades;

    // Radar émotionnel
    const eGroups={confident:{wins:0,count:0},neutral:{wins:0,count:0},anxious:{wins:0,count:0}};
    const pMap={...EMOTION_POLARITY};
    extraEmotions.forEach(e=>{if(typeof e==='object'&&e.polarity)pMap[e.label]=e.polarity;});
    T.forEach(t=>{
      if(!t.emotion)return;
      const pol=pMap[t.emotion];
      const g=pol==='positive'?'confident':pol==='negative'?'anxious':'neutral';
      eGroups[g].count++;if(t.result==='WIN')eGroups[g].wins++;
    });
    const radarAxes=[
      {label:'CONFIDENT',value:eGroups.confident.count>0?eGroups.confident.wins/eGroups.confident.count:0,color:'#4ade80'},
      {label:'NEUTRAL',  value:eGroups.neutral.count>0  ?eGroups.neutral.wins/eGroups.neutral.count:0,  color:C.accent||'#E8CDA9'},
      {label:'ANXIOUS',  value:eGroups.anxious.count>0  ?eGroups.anxious.wins/eGroups.anxious.count:0,  color:'#f87171'},
    ];

    // sortedDates pour la prédiction
    const sortedDates=[...new Set(T.map(t=>t.date).filter(Boolean))].sort();

    // ── Fyltragram ────────────────────────────────────────────────────────────
    const now_=new Date();
    const todayStr_=now_.toISOString().slice(0,10);
    const weekAgoStr_=new Date(now_.getTime()-7*86400000).toISOString().slice(0,10);
    const monthStr_=todayStr_.slice(0,7);
    const fyltaFilter_=(p,trd)=>{
      if(p==='today')return trd.filter(t=>t.date===todayStr_);
      if(p==='week')return trd.filter(t=>t.date>=weekAgoStr_);
      if(p==='month')return trd.filter(t=>t.date&&t.date.startsWith(monthStr_));
      return trd;
    };
    const prevPeriodFilter_=(p,trd)=>{
      if(p==='today'){const y=new Date(now_);y.setDate(y.getDate()-1);return trd.filter(t=>t.date===y.toISOString().slice(0,10));}
      if(p==='week'){const w3=new Date(now_.getTime()-14*86400000).toISOString().slice(0,10);return trd.filter(t=>t.date>=w3&&t.date<weekAgoStr_);}
      if(p==='month'){const pm=new Date(now_);pm.setMonth(pm.getMonth()-1);return trd.filter(t=>t.date&&t.date.startsWith(pm.toISOString().slice(0,7)));}
      const s=[...trd].sort((a,b)=>(a.date||'').localeCompare(b.date||''));return s.slice(0,Math.floor(s.length/2));
    };
    const fyltaTrades_=fyltaFilter_(fyltaPeriod,T);
    const fyltaLastTrades_=prevPeriodFilter_(fyltaPeriod,T);
    const calcFyltra_=(trd)=>{
      const N=trd.length;
      if(N===0)return{discipline:0,emotional:0,patience:0,resilience:0,consistency:0,risk:0};
      const bd_={};trd.forEach(t=>{if(!t.date)return;(bd_[t.date]=bd_[t.date]||[]).push(t);});
      const days_=Object.values(bd_);
      // Discipline
      const tpdArr=days_.map(d=>d.length).sort((a,b)=>a-b);
      const medTpd=tpdArr[Math.floor(tpdArr.length/2)]||1;
      const thresh=Math.max(5,medTpd*2);
      const overD=days_.filter(d=>d.length>thresh).length;
      const withS=trd.filter(t=>t.session).length;
      const disc=Math.min(100,Math.max(0,Math.round(100-(overD/Math.max(days_.length,1))*35-(1-withS/N)*25)));
      // Emotional Control
      const eMap_={...EMOTION_POLARITY};extraEmotions.forEach(e=>{if(typeof e==='object'&&e.polarity)eMap_[e.label]=e.polarity;});
      const wE=trd.filter(t=>t.emotion);const anx=wE.filter(t=>eMap_[t.emotion]==='negative').length;
      const emot=wE.length>0?Math.round((1-anx/wE.length)*100):75;
      // Patience
      const wRR=trd.filter(t=>t.rr>0);const wN=trd.filter(t=>t.notes&&t.notes.trim().length>10);
      const rrS_=wRR.length>0?(wRR.filter(t=>t.rr>=1).length/wRR.length)*100:60;
      const noteS_=(wN.length/N)*100;
      const pat=Math.min(100,Math.round(wRR.length>0?rrS_*0.7+noteS_*0.3:50+noteS_*0.5));
      // Resilience
      const srt_=[...trd].sort((a,b)=>(a.date||'').localeCompare(b.date||'')||(a.entryTime||'').localeCompare(b.entryTime||''));
      let resT=0,resG=0;
      for(let i=0;i<srt_.length;i++){if(srt_[i].result!=='LOSS')continue;resT++;if(i+1<srt_.length){const nx=srt_[i+1];if(nx.date!==srt_[i].date||nx.result==='WIN')resG++;}else resG++;}
      const res=resT>0?Math.round((resG/resT)*100):85;
      // Consistency
      const dpnl=days_.map(d=>d.reduce((s,t)=>s+(t.pnl||0),0));
      const mean_=dpnl.reduce((s,v)=>s+v,0)/Math.max(dpnl.length,1);
      const std_=Math.sqrt(dpnl.reduce((s,v)=>s+Math.pow(v-mean_,2),0)/Math.max(dpnl.length,1));
      const mAbs=dpnl.reduce((s,v)=>s+Math.abs(v),0)/Math.max(dpnl.length,1);
      const cons=mAbs>0?Math.min(100,Math.max(0,Math.round(100-(std_/mAbs)*35))):60;
      // Risk Management
      const wSL=trd.filter(t=>t.sl>0||t.rr>0).length;
      const avgRR_=wRR.length>0?wRR.reduce((s,t)=>s+t.rr,0)/wRR.length:0;
      const cap_=parseFloat(capital)||10000;
      const maxDL=Math.max(...days_.map(d=>d.filter(t=>t.result==='LOSS').reduce((s,t)=>s+Math.abs(t.pnl||0),0)),0);
      const ddS=maxDL<cap_*0.02?100:maxDL<cap_*0.05?70:40;
      const risk=Math.min(100,Math.round((wSL/N)*100*0.4+Math.min(100,avgRR_*50)*0.3+ddS*0.3));
      return{discipline:disc,emotional:emot,patience:pat,resilience:res,consistency:cons,risk};
    };
    const fyltaS_=calcFyltra_(fyltaTrades_);
    const fyltaLast_=calcFyltra_(fyltaLastTrades_);
    const FAXES_=lang==='fr'
      ?[{key:'discipline',label:'DISCIPLINE'},{key:'emotional',label:'ÉMOTIONS'},{key:'patience',label:'PATIENCE'},{key:'resilience',label:'RÉSILIENCE'},{key:'consistency',label:'RÉGULARITÉ'},{key:'risk',label:'RISQUE'}]
      :[{key:'discipline',label:'DISCIPLINE'},{key:'emotional',label:'CONTROL'},{key:'patience',label:'PATIENCE'},{key:'resilience',label:'RESILIENCE'},{key:'consistency',label:'CONSISTENCY'},{key:'risk',label:'RISK'}];
    const fyltaAxes_=FAXES_.map(a=>({...a,value:fyltaS_[a.key]}));
    const fyltaLastAxes_=FAXES_.map(a=>({...a,value:fyltaLast_[a.key]}));
    const fyltaWeak_=fyltaAxes_.reduce((m,a)=>a.value<m.value?a:m,fyltaAxes_[0]);
    const weakPhrase_={
      discipline:lang==='fr'?'Tu dépasses régulièrement ton nombre de trades idéal. Moins de trades, plus de qualité.':'You regularly exceed your ideal trade count. Fewer trades, more quality.',
      emotional:lang==='fr'?'Trop de trades pris sous pression émotionnelle. Attends un état de calme avant d\'entrer.':'Too many trades taken under emotional pressure. Wait for a calm state before entering.',
      patience:lang==='fr'?'Beaucoup de setups ont un RR < 1. Attends le setup parfait, ne force rien.':'Many setups have RR < 1. Wait for the perfect setup, never force an entry.',
      resilience:lang==='fr'?'Après une perte, tu retournes en marché trop vite. Prends une pause avant le trade suivant.':'After a loss, you re-enter too quickly. Take a break before your next trade.',
      consistency:lang==='fr'?'Tes résultats varient fortement d\'un jour à l\'autre. Travaille ta régularité en taille de position.':'Your results vary significantly day to day. Work on consistency in position sizing.',
      risk:lang==='fr'?'Gestion du risque insuffisante : RR moyen trop bas ou stops non respectés.':'Insufficient risk management: average RR is too low or stop losses are not respected.',
    }[fyltaWeak_.key]||'';

    // Patterns (calculés côté client)
    const patterns=[];
    if(T.length>=5){
      // Revenge trading
      let rev=0;
      const byDay={};T.forEach(t=>{if(!t.date)return;(byDay[t.date]=byDay[t.date]||[]).push(t);});
      Object.values(byDay).forEach(day=>{
        for(let i=1;i<day.length;i++){if(day[i-1].result==='LOSS'&&day[i].result==='LOSS')rev++;}
      });
      if(rev>=2)patterns.push({label:'Revenge Trading Detected',type:'danger'});
      // Série gagnante
      let maxS=0,cur=0;
      [...T].sort((a,b)=>a.date.localeCompare(b.date)).forEach(t=>{if(t.result==='WIN'){cur++;if(cur>maxS)maxS=cur;}else cur=0;});
      if(maxS>=3)patterns.push({label:`${maxS}-Win Streak`,type:'positive'});
      // Meilleure session
      const sess={};T.forEach(t=>{if(!t.session)return;if(!sess[t.session])sess[t.session]={wins:0,count:0};sess[t.session].count++;if(t.result==='WIN')sess[t.session].wins++;});
      const bestS=Object.entries(sess).filter(([,v])=>v.count>=3).sort((a,b)=>(b[1].wins/b[1].count)-(a[1].wins/a[1].count))[0];
      if(bestS)patterns.push({label:`Best: ${bestS[0]}`,type:'neutral'});
      // Winners coupés tôt
      const winners=T.filter(t=>t.result==='WIN'&&t.rr>0);
      if(winners.length>=4&&winners.filter(t=>t.rr<1).length>winners.length*0.5)patterns.push({label:'Winners Cut Too Early',type:'warning'});
      // Pertes laissées courir
      const losers=T.filter(t=>t.result==='LOSS'&&t.rr>0);
      if(losers.length>=3&&losers.filter(t=>Math.abs(t.rr)>1.5).length>losers.length*0.4)patterns.push({label:'Losses Running Too Long',type:'danger'});
    }

    // Prediction (dernières 10 sessions)
    let prediction=null;
    if(sortedDates.length>=5){
      const last10=sortedDates.slice(-10);
      const dayWRs=last10.map(d=>{const dt=T.filter(t=>t.date===d);const w=dt.filter(t=>t.result==='WIN').length;return dt.length>0?w/dt.length:null;}).filter(v=>v!==null);
      if(dayWRs.length>=5){
        const avg=dayWRs.slice(-5).reduce((s,v)=>s+v,0)/dayWRs.slice(-5).length;
        const pct=Math.round(avg*100);
        const sess={};T.filter(t=>last10.includes(t.date)).forEach(t=>{if(!t.session)return;if(!sess[t.session])sess[t.session]={wins:0,count:0};sess[t.session].count++;if(t.result==='WIN')sess[t.session].wins++;});
        const bS=Object.entries(sess).filter(([,v])=>v.count>=3).sort((a,b)=>(b[1].wins/b[1].count)-(a[1].wins/a[1].count))[0];
        const sessStr=bS?` if you trade the ${bS[0]} session`:'';
        prediction={pct,text:`Based on your last ${last10.length} sessions, you have a ${pct}% chance of a profitable day tomorrow${sessStr}.`};
      }
    }

    // Parse AI text pour les blocs typés
    const stripped   = aiText ? aiText.replace(/\[MEMOIRE\][\s\S]*?\[\/MEMOIRE\]/,'').replace(/\[SUMMARY\][\s\S]*?\[\/SUMMARY\]/,'').trim() : '';
    const reglesMatch = stripped.match(/\[REGLES\]([\s\S]*?)\[\/REGLES\]/);
    const regles      = reglesMatch ? reglesMatch[1].trim().split('\n').filter(l=>l.trim()) : [];
    const mainText    = stripped.replace(/\[REGLES\][\s\S]*?\[\/REGLES\]/,'').trim();

    // Parse [!] et [+] markers
    const parseBlocks=(text)=>text.split('\n').filter(l=>l.trim()).map(line=>{
      const l=line.trim();
      if(l.startsWith('[!]'))return{type:'alert',  text:l.slice(3).trim()};
      if(l.startsWith('[+]'))return{type:'positive',text:l.slice(3).trim()};
      return{type:'normal',text:l};
    });
    const friendBlocks = mainText ? parseBlocks(mainText) : [];

    // Priority pour les règles
    const rulePriority=(r)=>{const t=r.toLowerCase();if(t.includes('stop')||t.includes('never')||t.includes('avoid')||t.includes('arrêt')||t.includes('jamais'))return'high';if(t.includes('limit')||t.includes('max')||t.includes('rédu'))return'medium';return'normal';};
    const ruleColors={high:'#f87171',medium:'#fb923c',normal:'#4ade80'};
    const ruleIcons={high:'⚠',medium:'→',normal:'✓'};

    const patternBg={danger:'rgba(248,113,113,0.08)',warning:'rgba(251,146,60,0.08)',positive:'rgba(74,222,128,0.08)',neutral:'rgba(232,205,169,0.08)'};
    const patternBorder={danger:'rgba(248,113,113,0.3)',warning:'rgba(251,146,60,0.3)',positive:'rgba(74,222,128,0.3)',neutral:'rgba(232,205,169,0.25)'};
    const patternColor={danger:'#f87171',warning:'#fb923c',positive:'#4ade80',neutral:'#E8CDA9'};

    const aiScore = trades.length > 0 ? calcFyltraScore() : null;
    const scoreColor = aiScore==null?C.gray1 : aiScore>=700?'#4ade80':aiScore>=400?'#fb923c':'#f87171';
    const scoreTier  = aiScore==null?'' : aiScore>=850?(lang==='fr'?'Diamant':'Diamond'):aiScore>=650?(lang==='fr'?'Or':'Gold'):aiScore>=400?(lang==='fr'?'Argent':'Silver'):(lang==='fr'?'Bronze':'Bronze');

    const card=(children,extra={})=>(
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:10,padding:'14px',marginBottom:12,boxShadow:cShadow,...extra}}>{children}</div>
    );
    const sectionLabel=(text)=>(
      <div style={{fontSize:9,color:'rgba(var(--gold-rgb),0.55)',fontFamily:ff,fontWeight:700,letterSpacing:'0.2em',textTransform:'uppercase',marginBottom:12}}>{text}</div>
    );

    const hasEmotionData=Object.values(eGroups).some(g=>g.count>0);

    const scoreCard = aiScore!=null&&(
      <div style={{background:'linear-gradient(135deg,rgba(var(--gold-rgb),0.06),rgba(var(--gold-rgb),0.02))',border:`1px solid rgba(var(--gold-rgb),0.15)`,borderRadius:12,padding:'14px 16px',display:'flex',alignItems:'center',gap:14,overflow:'hidden',position:'relative',height:'100%',boxSizing:'border-box',boxShadow:cShadow}}>
        <div style={{position:'absolute',top:0,left:0,right:0,height:2,background:`linear-gradient(90deg,transparent,${scoreColor}44,transparent)`}}/>
        <div style={{flexShrink:0}}><ScoreCircle score={aiScore} max={1000}/></div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:9,color:'rgba(var(--gold-rgb),0.5)',fontFamily:ff,fontWeight:700,letterSpacing:'0.2em',textTransform:'uppercase',marginBottom:6}}>FYLTRA SCORE</div>
          <div style={{fontSize:desktop?18:15,fontWeight:800,color:scoreColor,fontFamily:ff,letterSpacing:'-0.02em',marginBottom:4}}>{aiScore}<span style={{fontSize:11,fontWeight:400,color:C.gray1}}>/1000</span></div>
          <div style={{fontSize:10,color:scoreColor,fontFamily:ff,fontWeight:600,letterSpacing:'0.12em',textTransform:'uppercase',marginBottom:6}}>{scoreTier}</div>
          <div style={{fontSize:11,color:C.dim,fontFamily:ff,fontWeight:300,lineHeight:1.6}}>
            {aiSummary||( T.length>0?`${T.filter(t=>t.result==='WIN').length}W / ${T.filter(t=>t.result==='LOSS').length}L`:(lang==='fr'?'Pas encore de trades.':'No trades yet.'))}
          </div>
        </div>
      </div>
    );

    const radarCard = hasEmotionData&&(
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'14px',height:'100%',boxSizing:'border-box',boxShadow:cShadow}}>
        {sectionLabel(lang==='fr'?'Profil psychologique':'Psychological profile')}
        <EmotionRadar axes={radarAxes}/>
        <div style={{display:'flex',justifyContent:'center',gap:14,marginTop:10}}>
          {radarAxes.map(ax=>(
            <div key={ax.label} style={{display:'flex',alignItems:'center',gap:5}}>
              <div style={{width:6,height:6,borderRadius:'50%',background:ax.color,flexShrink:0}}/>
              <span style={{fontSize:9,color:'rgba(255,255,255,0.4)',fontFamily:ff,letterSpacing:'0.08em'}}>{ax.label}</span>
            </div>
          ))}
        </div>
      </div>
    );

    const fyltagramCard=(
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'14px 14px 16px',marginBottom:12,boxShadow:cShadow}}>
        {/* Header + toggle période */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,flexWrap:'wrap',gap:6}}>
          <div style={{fontSize:9,color:'rgba(var(--gold-rgb),0.55)',fontFamily:ff,fontWeight:700,letterSpacing:'0.2em',textTransform:'uppercase'}}>FYLTRAGRAM</div>
          <div style={{display:'flex',gap:3}}>
            {['today','week','month','all'].map(p=>(
              <button key={p} onClick={()=>setFyltaPeriod(p)}
                style={{padding:'3px 10px',borderRadius:20,fontSize:8,cursor:'pointer',fontFamily:ff,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase',transition:'all 0.15s',
                  border:`1px solid ${fyltaPeriod===p?'rgba(var(--gold-rgb),0.6)':C.gray3}`,
                  background:fyltaPeriod===p?'rgba(var(--gold-rgb),0.08)':'transparent',
                  color:fyltaPeriod===p?'rgba(var(--gold-rgb),0.9)':C.gray1}}>
                {lang==='fr'?{today:'Auj.',week:'7j',month:'Mois',all:'Tout'}[p]:{today:'Today',week:'Week',month:'Month',all:'All'}[p]}
              </button>
            ))}
          </div>
        </div>
        {/* Radar principal */}
        {fyltaTrades_.length>=2
          ?<Fyltragram axes={fyltaAxes_} size={desktop?220:180} isDark={isDark}/>
          :<div style={{textAlign:'center',padding:'28px 0',color:C.gray2,fontSize:11,fontFamily:ff,letterSpacing:'0.08em'}}>
            {lang==='fr'?'Pas assez de données pour cette période':'Not enough data for this period'}
          </div>
        }
        {/* Légende des axes */}
        {fyltaTrades_.length>=2&&(
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'4px 12px',marginTop:14,padding:'10px 12px',borderRadius:8,background:isDark?'rgba(255,255,255,0.03)':'rgba(0,0,0,0.03)',border:`1px solid ${C.border}`}}>
            {(lang==='fr'?[
              {label:'DISCIPLINE',desc:'Max trades/jour + trading en session'},
              {label:'ÉMOTIONS',desc:'Ratio trades calmes vs anxieux'},
              {label:'PATIENCE',desc:'Proportion de setups avec RR ≥ 1'},
              {label:'RÉSILIENCE',desc:'Comportement après une perte'},
              {label:'RÉGULARITÉ',desc:'Stabilité des P&L journaliers'},
              {label:'RISQUE',desc:'SL, RR moyen, drawdown daily'},
            ]:[
              {label:'DISCIPLINE',desc:'Max trades/day + session compliance'},
              {label:'CONTROL',desc:'Calm vs anxious trade ratio'},
              {label:'PATIENCE',desc:'Setups with RR ≥ 1'},
              {label:'RESILIENCE',desc:'Behavior after a loss'},
              {label:'CONSISTENCY',desc:'Daily P&L stability'},
              {label:'RISK',desc:'SL usage, avg RR, daily drawdown'},
            ]).map((ax,i)=>{
              const v=fyltaAxes_[i]?.value??0;
              const col=v>=70?'#4ade80':v>=40?'#fb923c':'#f87171';
              return(
                <div key={i} style={{display:'flex',alignItems:'flex-start',gap:6}}>
                  <div style={{width:5,height:5,borderRadius:'50%',background:col,flexShrink:0,marginTop:3}}/>
                  <div>
                    <div style={{fontSize:8,color:C.white,fontFamily:ff,fontWeight:700,letterSpacing:'0.06em'}}>{ax.label}</div>
                    <div style={{fontSize:9,color:C.gray1,fontFamily:ff,fontWeight:300,lineHeight:1.4}}>{ax.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Phrase point faible */}
        {fyltaTrades_.length>=3&&(
          <div style={{marginTop:10,padding:'10px 12px',borderRadius:8,background:isDark?'rgba(255,255,255,0.03)':'rgba(0,0,0,0.03)',border:`1px solid ${C.border}`}}>
            <div style={{fontSize:8,color:'rgba(var(--gold-rgb),0.5)',fontFamily:ff,fontWeight:700,letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:4}}>
              {lang==='fr'?'POINT FAIBLE':'WEAKNESS'} — {fyltaWeak_.label}
            </div>
            <div style={{fontSize:11,color:C.dim,fontFamily:ff,fontWeight:300,lineHeight:1.7,fontStyle:'italic'}}>{weakPhrase_}</div>
          </div>
        )}
        {/* Mini comparaison évolution */}
        {fyltaLastTrades_.length>=2&&fyltaTrades_.length>=2&&(
          <div style={{marginTop:12}}>
            <div style={{fontSize:8,color:C.gray2,fontFamily:ff,letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:8,textAlign:'center'}}>
              {lang==='fr'?'ÉVOLUTION':'PROGRESS'}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              {[{lbl:lang==='fr'?'Période préc.':'Last period',axes:fyltaLastAxes_},{lbl:lang==='fr'?'Période actuelle':'This period',axes:fyltaAxes_}].map((item,idx)=>(
                <div key={idx} style={{textAlign:'center'}}>
                  <Fyltragram axes={item.axes} size={90} mini isDark={isDark}/>
                  <div style={{fontSize:8,color:C.gray2,fontFamily:ff,letterSpacing:'0.08em',textTransform:'uppercase',marginTop:5}}>{item.lbl}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );

    const patternsCard = patterns.length>0&&(
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'14px',marginBottom:12,boxShadow:cShadow}}>
        {sectionLabel(lang==='fr'?'Patterns détectés':'Detected patterns')}
        <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
          {patterns.map((p,i)=>(
            <div key={i} style={{padding:'6px 12px',borderRadius:20,background:patternBg[p.type],border:`1px solid ${patternBorder[p.type]}`,color:patternColor[p.type],fontSize:10,fontFamily:ff,fontWeight:600,letterSpacing:'0.08em',display:'flex',alignItems:'center',gap:6}}>
              <span>{p.type==='danger'?'⚠':p.type==='positive'?'✓':p.type==='warning'?'→':'◆'}</span>{p.label}
            </div>
          ))}
        </div>
      </div>
    );

    const friendCard = aiText&&friendBlocks.length>0&&(
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'14px 16px',position:'relative',overflow:'hidden',boxShadow:cShadow}}>
        <div style={{position:'absolute',top:0,left:0,right:0,height:2,background:'linear-gradient(90deg,transparent,rgba(var(--gold-rgb),0.45),transparent)'}}/>
        {sectionLabel(coachMemory?(lang==='fr'?'Ton ami — session en cours':'Your friend — ongoing session'):(lang==='fr'?'Ton ami trader':'Your trader friend'))}
        <div style={{display:'flex',flexDirection:'column',gap:6}}>
          {friendBlocks.map((block,i)=>{
            if(block.type==='alert')return(
              <div key={i} style={{display:'flex',gap:10,alignItems:'flex-start',background:'rgba(248,113,113,0.07)',border:'1px solid rgba(248,113,113,0.22)',borderRadius:8,padding:'10px 12px'}}>
                <span style={{color:'#f87171',fontSize:14,flexShrink:0,marginTop:1}}>⚠</span>
                <span style={{fontSize:13,color:'#f87171',fontFamily:ff,fontWeight:400,lineHeight:1.8}}>{block.text}</span>
              </div>
            );
            if(block.type==='positive')return(
              <div key={i} style={{display:'flex',gap:10,alignItems:'flex-start',background:'rgba(74,222,128,0.06)',border:'1px solid rgba(74,222,128,0.2)',borderRadius:8,padding:'10px 12px'}}>
                <span style={{color:'#4ade80',fontSize:14,flexShrink:0,marginTop:1}}>✓</span>
                <span style={{fontSize:13,color:'#4ade80',fontFamily:ff,fontWeight:400,lineHeight:1.8}}>{block.text}</span>
              </div>
            );
            return<div key={i} style={{fontSize:13,color:C.white,fontFamily:ff,fontWeight:300,lineHeight:1.9,marginBottom:2}}>{block.text}</div>;
          })}
        </div>
      </div>
    );

    const rulesCard = regles.length>0&&(
      <div style={{background:'rgba(var(--gold-rgb),0.04)',border:'1px solid rgba(var(--gold-rgb),0.18)',borderRadius:12,padding:'14px 16px',boxShadow:cShadow}}>
        {sectionLabel(lang==='fr'?'Ce que tu fais demain':'What you do tomorrow')}
        {regles.map((r,i)=>{
          const clean=r.replace(/^\d+\.\s*/,'');
          const pri=rulePriority(clean);
          return(
            <div key={i} style={{display:'flex',gap:12,alignItems:'flex-start',marginBottom:i<regles.length-1?14:0}}>
              <div style={{width:24,height:24,borderRadius:'50%',border:`1px solid ${ruleColors[pri]}44`,background:`${ruleColors[pri]}12`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:1}}>
                <span style={{fontSize:10,color:ruleColors[pri],fontWeight:700}}>{ruleIcons[pri]}</span>
              </div>
              <div style={{fontSize:12,color:C.white,fontFamily:ff,fontWeight:300,lineHeight:1.75,flex:1}}>{clean}</div>
            </div>
          );
        })}
      </div>
    );

    const weaknessCard = fyltaTrades_.length>=3&&(
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'14px 16px',marginBottom:12,boxShadow:cShadow}}>
        <div style={{fontSize:8,color:'rgba(var(--gold-rgb),0.5)',fontFamily:ff,fontWeight:700,letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:6}}>
          {lang==='fr'?'POINT FAIBLE':'WEAKNESS'} — {fyltaWeak_.label}
        </div>
        <div style={{fontSize:12,color:C.dim,fontFamily:ff,fontWeight:300,lineHeight:1.75,fontStyle:'italic'}}>{weakPhrase_}</div>
      </div>
    );

    const predCard = prediction&&(
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,padding:'14px',marginBottom:12,boxShadow:cShadow}}>
        {sectionLabel(lang==='fr'?'Prédiction pour demain':'Tomorrow\'s prediction')}
        <div style={{fontSize:13,color:C.dim,fontFamily:ff,fontWeight:300,lineHeight:1.7,marginBottom:14}}>{prediction.text}</div>
        <div style={{position:'relative',height:8,background:'rgba(255,255,255,0.06)',borderRadius:4,overflow:'hidden'}}>
          <div style={{position:'absolute',left:0,top:0,bottom:0,width:`${prediction.pct}%`,borderRadius:4,
            background:prediction.pct>=60?'linear-gradient(90deg,rgba(74,222,128,0.6),#4ade80)':prediction.pct>=40?'linear-gradient(90deg,rgba(251,146,60,0.6),#fb923c)':'linear-gradient(90deg,rgba(248,113,113,0.6),#f87171)',
            transition:'width 1.2s cubic-bezier(0.34,1.56,0.64,1)'}}/>
        </div>
        <div style={{display:'flex',justifyContent:'space-between',marginTop:5}}>
          <span style={{fontSize:9,color:'rgba(255,255,255,0.25)',fontFamily:ff}}>0%</span>
          <span style={{fontSize:11,fontWeight:700,color:prediction.pct>=60?'#4ade80':prediction.pct>=40?'#fb923c':'#f87171',fontFamily:ff}}>{prediction.pct}%</span>
          <span style={{fontSize:9,color:'rgba(255,255,255,0.25)',fontFamily:ff}}>100%</span>
        </div>
      </div>
    );

    return (
      <div>
        {!desktop&&<PageTitle sub={L.pages.ai.sub} title={L.pages.ai.title}/>}

        {/* 1. Score + Profil psychologique */}
        <div style={{display:'grid',gridTemplateColumns:desktop?(hasEmotionData?'1fr 1fr':'1fr'):'1fr',gap:12,marginBottom:12,alignItems:'stretch'}}>
          {scoreCard}
          {radarCard}
        </div>

        {/* Filtre compte + Bouton analyse */}
        {propfirms.length>0&&(
          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:10}}>
            {[{id:'all',label:L.btn.all},...propfirms.map(p=>({id:p.id,label:p.name||p.firm||'Compte'}))].map(opt=>(
              <button key={opt.id} onClick={()=>{setAiAccFilter(opt.id);setAiText('');setAiError('');setAiSummary(null);}}
                style={{padding:'5px 14px',borderRadius:20,fontSize:10,cursor:'pointer',fontFamily:ff,fontWeight:600,letterSpacing:'0.1em',textTransform:'uppercase',
                  border:`1px solid ${aiAccFilter===opt.id?'rgba(var(--gold-rgb),0.6)':C.gray3}`,
                  background:aiAccFilter===opt.id?'rgba(var(--gold-rgb),0.08)':'transparent',
                  color:aiAccFilter===opt.id?'rgba(var(--gold-rgb),0.9)':C.gray1,transition:'all 0.15s'}}>
                {opt.label}
              </button>
            ))}
          </div>
        )}
        <div style={{background:'linear-gradient(180deg,rgba(60,60,60,0.97) 0%,rgba(18,18,18,0.99) 55%,rgba(8,8,8,1) 100%)',borderRadius:24,padding:10,boxShadow:'0 6px 20px rgba(0,0,0,0.5),0 0 0 1px rgba(255,255,255,0.13),inset 0 1px 0 rgba(255,255,255,0.38),inset 0 -2px 0 rgba(0,0,0,0.8)',border:'1px solid rgba(255,255,255,0.1)',marginBottom:14}}>
          <button onClick={analyzeAI} disabled={aiLoading} style={{width:'100%',padding:'14px',borderRadius:16,border:`1px solid rgba(var(--gold-rgb),${aiLoading?0.1:0.3})`,background:aiLoading?'rgba(var(--gold-rgb),0.05)':'radial-gradient(ellipse 110% 100% at 50% 35%,rgba(var(--gold-rgb),0.3) 0%,rgba(var(--gold-rgb),0.12) 55%,rgba(var(--gold-rgb),0.05) 100%)',color:aiLoading?'rgba(var(--gold-rgb),0.35)':'rgba(var(--gold-rgb),0.95)',fontSize:12,fontFamily:ff,fontWeight:600,letterSpacing:'0.2em',textTransform:'uppercase',cursor:aiLoading?'not-allowed':'pointer',transition:'all 0.2s',boxShadow:aiLoading?'none':'0 0 20px 4px rgba(var(--gold-rgb),0.06),0 4px 14px rgba(0,0,0,0.4),inset 0 1px 0 rgba(var(--gold-rgb),0.15)'}}>
            {aiLoading?(lang==='fr'?'Analyse en cours…':'Analysing…'):(lang==='fr'?'Extraire ma vérité':'Extract my truth')}
          </button>
        </div>

        {aiError&&<div style={{padding:'14px 16px',borderRadius:8,background:'rgba(192,57,43,0.1)',border:'1px solid rgba(192,57,43,0.3)',color:'#e74c3c',fontSize:12,fontFamily:ff,marginBottom:14}}>{aiError}</div>}

        {/* 3. Your Friend */}
        {friendCard&&<div style={{marginBottom:12}}>{friendCard}</div>}

        {/* 4. Detected Patterns */}
        {patternsCard}

        {/* 5. Weakness */}
        {weaknessCard}

        {/* 6. What you do tomorrow */}
        {rulesCard&&<div style={{marginBottom:12}}>{rulesCard}</div>}

        {/* 7. Tomorrow's Prediction */}
        {predCard}

        {!aiText&&!aiError&&!aiLoading&&!hasEmotionData&&aiScore==null&&(
          <div style={{textAlign:'center',padding:'40px 0',color:C.gray2,fontSize:11,fontFamily:ff,letterSpacing:'0.1em'}}>
            {lang==='fr'?'Lance l\'analyse — ton ami a des choses à te dire.':'Run the analysis — your friend has things to tell you.'}
          </div>
        )}
      </div>
    );
  };


  // ── PropFirm JSX ──
  const pfSet = (k,v) => setPfForm(f => ({...f,[k]:v}));

  const addPropfirm = () => {
    const accountCount = propfirms.filter(p => !p.step || p.step === 1).length;
    if (accountCount >= pfLimit) {
      alert(lang==="fr" ? `Limite de ${pfLimit} comptes atteinte. Passez à un plan supérieur.` : `Account limit of ${pfLimit} reached. Upgrade your plan.`);
      return;
    }
    if (pfForm.type==="propfirm" && (!pfForm.firm || !pfForm.capital || !pfForm.target || !pfForm.maxLoss)) return;
    if (pfForm.type==="personal" && !pfForm.capital) return;
    if (pfForm.type==="propfirm" && pfForm.steps==="2") {
      const step1Id = Date.now();
      const step2Id = step1Id + 1;
      const step1 = { ...pfForm, id:step1Id, pnl:0, step:1, linkedStep2Id:step2Id, name:pfForm.name ? pfForm.name+" — Step 1" : pfForm.firm+" Step 1" };
      const step2 = {
        ...pfForm, id:step2Id, pnl:0, step:2, status:"pending",
        name: pfForm.name ? pfForm.name+" — Step 2" : pfForm.firm+" Step 2",
        capital: pfForm.step2Capital || pfForm.capital,
        target:  pfForm.step2Target,
        maxLoss: pfForm.step2MaxLoss,
        dailyLoss: pfForm.step2HasDailyLoss ? pfForm.step2DailyLoss : "",
        hasDailyLoss: pfForm.step2HasDailyLoss,
        trailingDD: pfForm.step2TrailingDD,
      };
      setPropfirms(p => [...p, step1, step2]);
    } else {
      setPropfirms(p => [...p, { ...pfForm, id:Date.now(), pnl:0 }]);
    }
    setPfForm({ type:"propfirm", name:"", firm:"", capital:"", target:"", dailyLoss:"", maxLoss:"", consistency:"", consistencyPct:"", hasDailyLoss:false, hasConsistency:false, hasInactivity:false, inactivityDays:"", inactivityFrom:"", trailingDD:false, hasMinDays:false, minDays:"", steps:"1", step2Capital:"", step2Target:"", step2MaxLoss:"", step2DailyLoss:"", step2HasDailyLoss:false, step2TrailingDD:false });
    setPfView("list");
  };

  const deletePf = async id => {
    const linkedTradeIds = trades.filter(t => t.accountIds && t.accountIds.includes(id)).map(t => t.id);
    if (linkedTradeIds.length > 0) {
      if (user) await supabase.from("trades").delete().in("id", linkedTradeIds);
      setTrades(p => p.filter(t => !linkedTradeIds.includes(t.id)));
    }
    setPropfirms(p => p.filter(pf => pf.id !== id));
  };

  // Calcul P&L par compte — filtre les trades liés à ce compte
  const getPfPnl = (pf) => {
    const linked = trades.filter(t => !t.accountIds || t.accountIds.length === 0 || t.accountIds.includes(pf.id));
    return linked.reduce((s, t) => s + (t.pnl || 0), 0);
  };

  const getPfAlerts = (pf) => {
    const alerts = [];
    const target = parseFloat(pf.target)||0;
    const maxLoss = parseFloat(pf.maxLoss)||0;
    const dailyLoss = parseFloat(pf.dailyLoss)||0;
    const pnl = getPfPnl(pf);
    const drawdown = Math.abs(Math.min(0, pnl));

    if (pf.type === "propfirm") {
      const remaining = target - pnl;
      if (pnl >= target) alerts.push({ type:"success", msg:L.alerts.profitTarget });
      else if (remaining <= target * 0.2) alerts.push({ type:"warn", msg:L.alerts.remaining(fm(remaining),currency) });
      else alerts.push({ type:"info", msg:L.alerts.missing(fm(remaining),currency) });
      if (drawdown >= maxLoss) alerts.push({ type:"danger", msg:L.alerts.maxDD });
      else if (drawdown >= maxLoss * 0.8) alerts.push({ type:"danger", msg:L.alerts.nearDD(Math.round(drawdown/maxLoss*100)) });
    }

    if (pf.hasDailyLoss && dailyLoss > 0) {
      const todayPnl = trades.filter(t => t.date === tradingToday && (!t.accountIds || t.accountIds.length === 0 || t.accountIds.includes(pf.id))).reduce((s,t)=>s+(t.pnl||0),0);
      const todayLoss = Math.abs(Math.min(0, todayPnl));
      if (todayLoss >= dailyLoss) alerts.push({ type:"danger", msg:L.alerts.dailyLossHit });
      else if (todayLoss >= dailyLoss * 0.7) alerts.push({ type:"warn", msg:L.alerts.dailyLossWarn(fm(todayLoss),dailyLoss,currency) });
    }
    return alerts;
  };

  const propfirmContent = (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:22 }}>
        <PageTitle sub={L.pages.accts.sub} title={pfView==="list"?L.pages.accts.list:pfView==="add-type"?L.pages.accts.addType:pfView==="add-propfirm"?L.pages.accts.addPf:pfView==="add-mt5"?"MT4 / MT5":L.pages.accts.addPers} />
        {pfView!=="list" && (
          <button onClick={()=>setPfView("list")} style={{ padding:"9px 16px", borderRadius:4, border:`1px solid ${C.border}`, background:"transparent", color:C.gray1, fontSize:11, fontFamily:"'Josefin Sans',sans-serif", letterSpacing:"0.12em", textTransform:"uppercase", cursor:"pointer", marginBottom:24 }}>{L.btn.back}</button>
        )}
        {pfView==="list" && (() => {
          const accountCount = propfirms.filter(p => !p.step || p.step === 1).length;
          const atLimit = accountCount >= pfLimit;
          return (
            <button onClick={()=>{ if(atLimit){ setShowUpgradeModal(true); return; } setPfView("add-type"); }} style={{ padding:"9px 16px", borderRadius:4, border:"none", background:"radial-gradient(ellipse 90% 90% at 50% 38%, rgba(245,245,245,0.95) 0%, rgba(215,215,215,0.88) 55%, rgba(230,230,230,0.92) 100%)", color:"#111", fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.12em", textTransform:"uppercase", cursor:"pointer", marginBottom:24 }}>
              {atLimit ? (lang==="fr"?`Limite (${pfLimit})`:`Limit (${pfLimit})`) : L.btn.add}
            </button>
          );
        })()}
      </div>

      {/* ── TYPE SELECTOR ── */}
      {pfView==="add-type" && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginTop:8}}>
          <button onClick={()=>{pfSet("type","propfirm");setPfView("add-propfirm");}} style={{padding:"24px 16px",borderRadius:10,border:`1px solid ${C.border}`,background:C.bg2,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:10,transition:"all 0.2s"}}>
            <div style={{fontSize:28,color:C.dim,fontFamily:"serif"}}>▤</div>
            <div style={{fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,fontSize:13,color:C.white,letterSpacing:"0.1em",textTransform:"uppercase"}}>Prop Firm</div>
            <div style={{fontSize:11,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",lineHeight:1.5,textAlign:"center"}}>{L.acct.propfirmDesc}</div>
            <div style={{padding:"3px 8px",borderRadius:20,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.10)",marginTop:2}}>
              <span style={{fontSize:8,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,color:"rgba(255,255,255,0.35)",letterSpacing:"0.15em",textTransform:"uppercase"}}>MANUAL</span>
            </div>
          </button>
          <button onClick={()=>{pfSet("type","personal");setPfView("add-personal");}} style={{padding:"24px 16px",borderRadius:10,border:`1px solid ${C.border}`,background:C.bg2,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:10,transition:"all 0.2s"}}>
            <div style={{fontSize:28,color:C.dim,fontFamily:"serif"}}>◈</div>
            <div style={{fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,fontSize:13,color:C.white,letterSpacing:"0.1em",textTransform:"uppercase"}}>{L.acct.ownFunds}</div>
            <div style={{fontSize:11,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",lineHeight:1.5,textAlign:"center"}}>{L.acct.personalDesc}</div>
            <div style={{padding:"3px 8px",borderRadius:20,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.10)",marginTop:2}}>
              <span style={{fontSize:8,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,color:"rgba(255,255,255,0.35)",letterSpacing:"0.15em",textTransform:"uppercase"}}>MANUAL</span>
            </div>
          </button>
          <button onClick={()=>setPfView("add-mt5")} style={{padding:"24px 16px",borderRadius:10,border:`1px solid rgba(232,205,169,0.25)`,background:C.bg2,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:10,transition:"all 0.2s"}}>
            <div style={{fontSize:28,color:C.dim}}>⟳</div>
            <div style={{fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,fontSize:13,color:C.white,letterSpacing:"0.1em",textTransform:"uppercase"}}>MT4 / MT5</div>
            <div style={{fontSize:11,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",lineHeight:1.5,textAlign:"center"}}>{lang==="fr"?"Synchronise tes trades depuis MetaTrader":"Sync trades from MetaTrader"}</div>
            <div style={{padding:"3px 8px",borderRadius:20,background:"rgba(232,205,169,0.08)",border:"1px solid rgba(232,205,169,0.25)",marginTop:2}}>
              <span style={{fontSize:8,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,color:"#E8CDA9",letterSpacing:"0.15em",textTransform:"uppercase"}}>MT5 SYNC</span>
            </div>
          </button>
        </div>
      )}

      {/* ── PROPFIRM FORM ── */}
      {pfView==="add-propfirm" && (
        <div>
          {/* Step selector */}
          <div style={{display:"flex",gap:6,marginBottom:16,padding:4,background:darkMode?"linear-gradient(180deg,rgba(85,85,85,0.98) 0%,rgba(28,28,28,0.99) 100%)":"linear-gradient(180deg,rgba(60,60,60,0.95) 0%,rgba(18,18,18,0.97) 100%)",borderRadius:12,border:darkMode?"1px solid rgba(255,255,255,0.18)":"1px solid rgba(255,255,255,0.14)"}}>
            {[{k:"1",l:"1 Step"},{k:"2",l:"2 Steps"}].map(s=>(
              <button key={s.k} onClick={()=>pfSet("steps",s.k)} style={{flex:1,padding:"8px",borderRadius:8,border:"none",background:pfForm.steps===s.k?"radial-gradient(ellipse 90% 90% at 50% 50%,rgba(252,252,252,0.96) 0%,rgba(218,218,218,0.88) 55%,rgba(235,235,235,0.92) 100%)":"transparent",color:pfForm.steps===s.k?"#111":"rgba(255,255,255,0.55)",fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:pfForm.steps===s.k?600:300,letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer",transition:"all 0.2s"}}>{s.l}</button>
            ))}
          </div>
          {pfForm.steps==="2" && <div style={{marginBottom:12,padding:"8px 12px",borderRadius:8,background:"rgba(232,205,169,0.06)",border:"1px solid rgba(232,205,169,0.18)",fontSize:10,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",lineHeight:1.7}}>{lang==="fr"?"Les comptes Step 1 et Step 2 seront créés simultanément. Le Step 2 s'active automatiquement quand le Step 1 est validé.":"Step 1 and Step 2 accounts will be created simultaneously. Step 2 activates automatically when Step 1 is passed."}</div>}
          {/* Firm autocomplete */}
          <Field label={lang==="fr"?"Nom de la Prop Firm *":"Prop Firm name *"}>
            <div style={{position:"relative"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                {(()=>{const match=KNOWN_PROPFIRMS.find(p=>p.name.toLowerCase()===pfForm.firm.toLowerCase());return match?<img src={`https://www.google.com/s2/favicons?sz=128&domain=${match.domain}`} style={{width:20,height:20,borderRadius:4,objectFit:"contain",flexShrink:0}} alt="" onError={e=>e.target.style.display="none"}/>:null;})()}
                <input type="text" placeholder={L.acct.firmNamePH} value={pfForm.firm} onChange={e=>{pfSet("firm",e.target.value);setPfFirmOpen(true);}} onFocus={()=>setPfFirmOpen(true)} onBlur={()=>setTimeout(()=>setPfFirmOpen(false),150)} style={{...iStyle,flex:1}}/>
              </div>
              {pfFirmOpen && KNOWN_PROPFIRMS.filter(p=>pfForm.firm.length>0&&p.name.toLowerCase().includes(pfForm.firm.toLowerCase())).slice(0,6).length>0 && (
                <div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:300,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden",boxShadow:"0 8px 24px rgba(0,0,0,0.3)",marginTop:4}}>
                  {KNOWN_PROPFIRMS.filter(p=>pfForm.firm.length>0&&p.name.toLowerCase().includes(pfForm.firm.toLowerCase())).slice(0,6).map(p=>(
                    <button key={p.name} onMouseDown={()=>{pfSet("firm",p.name);setPfFirmOpen(false);}} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"9px 12px",border:"none",background:"transparent",cursor:"pointer",textAlign:"left",color:C.white,fontSize:12,fontFamily:"'Josefin Sans',sans-serif",transition:"background 0.15s"}} onMouseEnter={e=>e.currentTarget.style.background=C.bg3} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <img src={`https://www.google.com/s2/favicons?sz=128&domain=${p.domain}`} style={{width:18,height:18,borderRadius:3,objectFit:"contain",flexShrink:0}} alt="" onError={e=>e.target.style.display="none"}/>
                      {p.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Field>
          <Field label={lang==="fr"?"Nom du compte (optionnel)":"Account name (optional)"}>
            <input type="text" placeholder={L.acct.accountNamePH} value={pfForm.name} onChange={e=>pfSet("name",e.target.value)} style={iStyle}/>
          </Field>
          <Divider/>
          <div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",gap:6,marginBottom:8}}>
            <span style={{fontSize:10,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em"}}>{L.acct.enterIn}</span>
            <button onClick={()=>{setPfPctMode(false);setPfPctValues({target:"",maxLoss:"",dailyLoss:""});}} style={{padding:"4px 10px",borderRadius:20,border:`1px solid ${!pfPctMode?C.accent:C.border}`,background:!pfPctMode?C.bg3:"transparent",color:!pfPctMode?C.white:C.gray1,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:!pfPctMode?600:300,cursor:"pointer"}}>{currency}</button>
            <button onClick={()=>{setPfPctMode(true);setPfPctValues({target:"",maxLoss:"",dailyLoss:""});}} style={{padding:"4px 10px",borderRadius:20,border:`1px solid ${pfPctMode?C.accent:C.border}`,background:pfPctMode?C.bg3:"transparent",color:pfPctMode?C.white:C.gray1,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:pfPctMode?600:300,cursor:"pointer"}}>%</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Capital *">
              <input type="text" inputMode="numeric" placeholder="" value={pfForm.capital} onChange={e=>pfSet("capital",e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,""))} style={iStyle}/>
            </Field>
            <Field label={pfPctMode?"Profit Target * (%)":`Profit Target * (${currency})`}>
              <input type="text" inputMode="numeric" placeholder="" value={pfPctMode?pfPctValues.target:pfForm.target} onChange={e=>{const v=e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"");if(pfPctMode){setPfPctValues(p=>({...p,target:v}));pfSet("target",pfForm.capital?String((parseFloat(pfForm.capital)*(parseFloat(v)||0)/100).toFixed(0)):"");}else pfSet("target",v);}} style={iStyle}/>
              {pfPctMode&&pfPctValues.target&&pfForm.capital&&<div style={{fontSize:11,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",marginTop:4}}>= {fm(parseFloat(pfForm.capital)*(parseFloat(pfPctValues.target)||0)/100)}{currency}</div>}
            </Field>
            <Field label={
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <span>{pfPctMode?"Max Drawdown * (%)":"Max Drawdown *"}</span>
                <span onClick={()=>pfSet("trailingDD",!pfForm.trailingDD)} style={{cursor:"pointer",fontSize:9,fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",color:pfForm.trailingDD?C.accent:C.gray2,transition:"color 0.2s",userSelect:"none"}}>
                  {pfForm.trailingDD?(lang==="fr"?"▲ Suiveur actif":"▲ Trailing active"):(lang==="fr"?"Suiveur":"Trailing")}
                </span>
              </div>
            }>
              <input type="text" inputMode="numeric" placeholder="" value={pfPctMode?pfPctValues.maxLoss:pfForm.maxLoss} onChange={e=>{const v=e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"");if(pfPctMode){setPfPctValues(p=>({...p,maxLoss:v}));pfSet("maxLoss",pfForm.capital?String((parseFloat(pfForm.capital)*(parseFloat(v)||0)/100).toFixed(0)):"");}else pfSet("maxLoss",v);}} style={iStyle}/>
              {pfPctMode&&pfPctValues.maxLoss&&pfForm.capital&&<div style={{fontSize:11,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",marginTop:4}}>= {fm(parseFloat(pfForm.capital)*(parseFloat(pfPctValues.maxLoss)||0)/100)}{currency}</div>}
              {pfForm.trailingDD&&<div style={{fontSize:10,color:C.accent,fontFamily:"'Josefin Sans',sans-serif",marginTop:4,letterSpacing:"0.05em"}}>▲ Le seuil s'ajuste à la hausse en fin de journée</div>}
            </Field>
          </div>
          <Divider/>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>Daily Loss Limit</div>
            <button onClick={()=>pfSet("hasDailyLoss",!pfForm.hasDailyLoss)} style={{padding:"5px 12px",borderRadius:20,border:`1px solid ${C.border}`,background:pfForm.hasDailyLoss?C.accent:"transparent",color:pfForm.hasDailyLoss?"#fff":C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,cursor:"pointer",letterSpacing:"0.1em",textTransform:"uppercase"}}>
              {pfForm.hasDailyLoss?L.btn.enabled:L.btn.disabled}
            </button>
          </div>
          {pfForm.hasDailyLoss && (
            <Field label={pfPctMode?"Daily Loss (%)":"Daily Loss (€)"}>
              <input type="text" inputMode="numeric" placeholder="" value={pfPctMode?pfPctValues.dailyLoss:pfForm.dailyLoss} onChange={e=>{const v=e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"");if(pfPctMode){setPfPctValues(p=>({...p,dailyLoss:v}));pfSet("dailyLoss",pfForm.capital?String((parseFloat(pfForm.capital)*(parseFloat(v)||0)/100).toFixed(0)):"");}else pfSet("dailyLoss",v);}} style={iStyle}/>
              {pfPctMode&&pfPctValues.dailyLoss&&pfForm.capital&&<div style={{fontSize:11,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",marginTop:4}}>= {fm(parseFloat(pfForm.capital)*(parseFloat(pfPctValues.dailyLoss)||0)/100)}{currency}</div>}
            </Field>
          )}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,marginTop:4}}>
            <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>Règle de Consistance</div>
            <button onClick={()=>pfSet("hasConsistency",!pfForm.hasConsistency)} style={{padding:"5px 12px",borderRadius:20,border:`1px solid ${C.border}`,background:pfForm.hasConsistency?C.accent:"transparent",color:pfForm.hasConsistency?"#fff":C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,cursor:"pointer",letterSpacing:"0.1em",textTransform:"uppercase"}}>
              {pfForm.hasConsistency?L.btn.enabled:L.btn.disabled}
            </button>
          </div>
          {pfForm.hasConsistency && (
            <Field label="% de consistance *">
              <input type="text" inputMode="numeric" placeholder="" value={pfForm.consistencyPct} onChange={e=>pfSet("consistencyPct",e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,""))} style={iStyle}/>
            </Field>
          )}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,marginTop:4}}>
            <div>
              <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>Règle d'Inactivité</div>
              <div style={{fontSize:10,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",marginTop:2,letterSpacing:"0.04em"}}>Nb max de jours sans trade</div>
            </div>
            <button onClick={()=>pfSet("hasInactivity",!pfForm.hasInactivity)} style={{padding:"5px 12px",borderRadius:20,border:`1px solid ${C.border}`,background:pfForm.hasInactivity?C.accent:"transparent",color:pfForm.hasInactivity?"#fff":C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,cursor:"pointer",letterSpacing:"0.1em",textTransform:"uppercase"}}>
              {pfForm.hasInactivity?L.btn.enabled:L.btn.disabled}
            </button>
          </div>
          {pfForm.hasInactivity && (
            <Field label={L.form.inactivityDays}>
              <input type="text" inputMode="numeric" placeholder="ex: 5" value={pfForm.inactivityDays} onChange={e=>pfSet("inactivityDays",e.target.value.replace(/[^0-9]/g,""))} style={iStyle}/>
            </Field>
          )}
          {pfForm.hasInactivity && (
            <Field label={L.form.from}>
              <input type="date" value={pfForm.inactivityFrom} onChange={e=>pfSet("inactivityFrom",e.target.value)} style={iStyle}/>
            </Field>
          )}
          <Divider/>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <div>
              <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>{lang==="fr"?"Jours minimum de trading":"Minimum trading days"}</div>
              <div style={{fontSize:10,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",marginTop:2,letterSpacing:"0.04em"}}>Nb de jours requis pour valider</div>
            </div>
            <button onClick={()=>pfSet("hasMinDays",!pfForm.hasMinDays)} style={{padding:"5px 12px",borderRadius:20,border:`1px solid ${C.border}`,background:pfForm.hasMinDays?C.accent:"transparent",color:pfForm.hasMinDays?"#fff":C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,cursor:"pointer",letterSpacing:"0.1em",textTransform:"uppercase"}}>
              {pfForm.hasMinDays?L.btn.enabled:L.btn.disabled}
            </button>
          </div>
          {pfForm.hasMinDays && (
            <Field label="Nombre de jours minimum">
              <input type="text" inputMode="numeric" placeholder="ex: 10" value={pfForm.minDays} onChange={e=>pfSet("minDays",e.target.value.replace(/[^0-9]/g,""))} style={iStyle}/>
            </Field>
          )}
          <Divider/>
          {/* ── STEP 2 OBJECTIVES ── */}
          {pfForm.steps==="2" && (
            <div style={{marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
                <div style={{flex:1,height:1,background:C.border}}/>
                <div style={{fontSize:9,color:C.accent,fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,letterSpacing:"0.2em",textTransform:"uppercase",whiteSpace:"nowrap"}}>Objectifs Step 2</div>
                <div style={{flex:1,height:1,background:C.border}}/>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
                <Field label="Capital Step 2">
                  <input type="text" inputMode="numeric" placeholder={pfForm.capital||"Même que Step 1"} value={pfForm.step2Capital} onChange={e=>pfSet("step2Capital",e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,""))} style={iStyle}/>
                </Field>
                <Field label={`Profit Target * (${currency})`}>
                  <input type="text" inputMode="numeric" placeholder="" value={pfForm.step2Target} onChange={e=>pfSet("step2Target",e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,""))} style={iStyle}/>
                </Field>
                <Field label={<div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <span>Max Drawdown *</span>
                  <span onClick={()=>pfSet("step2TrailingDD",!pfForm.step2TrailingDD)} style={{cursor:"pointer",fontSize:9,fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",color:pfForm.step2TrailingDD?C.accent:C.gray2,transition:"color 0.2s",userSelect:"none"}}>{pfForm.step2TrailingDD?(lang==="fr"?"▲ Suiveur":"▲ Trailing"):(lang==="fr"?"Suiveur":"Trailing")}</span>
                </div>}>
                  <input type="text" inputMode="numeric" placeholder="" value={pfForm.step2MaxLoss} onChange={e=>pfSet("step2MaxLoss",e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,""))} style={iStyle}/>
                </Field>
              </div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>Daily Loss Limit</div>
                <button onClick={()=>pfSet("step2HasDailyLoss",!pfForm.step2HasDailyLoss)} style={{padding:"5px 12px",borderRadius:20,border:`1px solid ${C.border}`,background:pfForm.step2HasDailyLoss?C.accent:"transparent",color:pfForm.step2HasDailyLoss?"#fff":C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,cursor:"pointer",letterSpacing:"0.1em",textTransform:"uppercase"}}>
                  {pfForm.step2HasDailyLoss?L.btn.enabled:L.btn.disabled}
                </button>
              </div>
              {pfForm.step2HasDailyLoss && (
                <Field label={`Daily Loss (${currency})`}>
                  <input type="text" inputMode="numeric" placeholder="" value={pfForm.step2DailyLoss} onChange={e=>pfSet("step2DailyLoss",e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,""))} style={iStyle}/>
                </Field>
              )}
            </div>
          )}
          {(()=>{
            const s1ok = pfForm.firm && pfForm.capital && pfForm.target && pfForm.maxLoss;
            const s2ok = pfForm.steps!=="2" || (pfForm.step2Target && pfForm.step2MaxLoss);
            const ok = s1ok && s2ok;
            return <button onClick={addPropfirm} disabled={!ok} style={{width:"100%",padding:"14px",borderRadius:4,border:"none",background:ok?C.accent:C.gray3,color:ok?"#fff":C.gray2,fontSize:12,fontWeight:600,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.2em",textTransform:"uppercase",cursor:"pointer",transition:"all 0.3s"}}>{L.btn.record} →</button>;
          })()}
        </div>
      )}

      {/* ── FOND PROPRE FORM ── */}
      {pfView==="add-personal" && (
        <div>
          <Field label={lang==="fr"?"Nom du compte (optionnel)":"Account name (optional)"}>
            <input type="text" placeholder={L.acct.accountNamePH} value={pfForm.name} onChange={e=>pfSet("name",e.target.value)} style={iStyle}/>
          </Field>
          <Field label={lang==="fr"?"Broker (optionnel)":"Broker (optional)"}>
            <div style={{position:"relative"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                {(()=>{const match=KNOWN_BROKERS.find(p=>p.name.toLowerCase()===pfForm.firm.toLowerCase());return match?<img src={`https://www.google.com/s2/favicons?sz=128&domain=${match.domain}`} style={{width:20,height:20,borderRadius:4,objectFit:"contain",flexShrink:0}} alt="" onError={e=>e.target.style.display="none"}/>:null;})()}
                <input type="text" placeholder="VT Markets, IC Markets…" value={pfForm.firm} onChange={e=>{pfSet("firm",e.target.value);setPfBrokerOpen(true);}} onFocus={()=>setPfBrokerOpen(true)} onBlur={()=>setTimeout(()=>setPfBrokerOpen(false),150)} style={{...iStyle,flex:1}}/>
              </div>
              {pfBrokerOpen && KNOWN_BROKERS.filter(p=>pfForm.firm.length>0&&p.name.toLowerCase().includes(pfForm.firm.toLowerCase())).slice(0,6).length>0 && (
                <div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:300,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden",boxShadow:"0 8px 24px rgba(0,0,0,0.3)",marginTop:4}}>
                  {KNOWN_BROKERS.filter(p=>pfForm.firm.length>0&&p.name.toLowerCase().includes(pfForm.firm.toLowerCase())).slice(0,6).map(p=>(
                    <button key={p.name} onMouseDown={()=>{pfSet("firm",p.name);setPfBrokerOpen(false);}} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"9px 12px",border:"none",background:"transparent",cursor:"pointer",textAlign:"left",color:C.white,fontSize:12,fontFamily:"'Josefin Sans',sans-serif",transition:"background 0.15s"}} onMouseEnter={e=>e.currentTarget.style.background=C.bg3} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <img src={`https://www.google.com/s2/favicons?sz=128&domain=${p.domain}`} style={{width:18,height:18,borderRadius:3,objectFit:"contain",flexShrink:0}} alt="" onError={e=>e.target.style.display="none"}/>
                      {p.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Field>
          <Field label="Capital *">
            <input type="text" inputMode="numeric" placeholder="" value={pfForm.capital} onChange={e=>pfSet("capital",e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,""))} style={iStyle}/>
          </Field>
          <Divider/>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>Daily Loss Limit</div>
            <button onClick={()=>pfSet("hasDailyLoss",!pfForm.hasDailyLoss)} style={{padding:"5px 12px",borderRadius:20,border:`1px solid ${C.border}`,background:pfForm.hasDailyLoss?C.accent:"transparent",color:pfForm.hasDailyLoss?"#fff":C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,cursor:"pointer",letterSpacing:"0.1em",textTransform:"uppercase"}}>
              {pfForm.hasDailyLoss?L.btn.enabled:L.btn.disabled}
            </button>
          </div>
          {pfForm.hasDailyLoss && (
            <Field label={pfPctMode?"Daily Loss (%)":"Daily Loss (€)"}>
              <input type="text" inputMode="numeric" placeholder="" value={pfPctMode?pfPctValues.dailyLoss:pfForm.dailyLoss} onChange={e=>{const v=e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"");if(pfPctMode){setPfPctValues(p=>({...p,dailyLoss:v}));pfSet("dailyLoss",pfForm.capital?String((parseFloat(pfForm.capital)*(parseFloat(v)||0)/100).toFixed(0)):"");}else pfSet("dailyLoss",v);}} style={iStyle}/>
              {pfPctMode&&pfPctValues.dailyLoss&&pfForm.capital&&<div style={{fontSize:11,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",marginTop:4}}>= {fm(parseFloat(pfForm.capital)*(parseFloat(pfPctValues.dailyLoss)||0)/100)}{currency}</div>}
            </Field>
          )}
          <Divider/>
          <button onClick={addPropfirm} disabled={!pfForm.capital} style={{width:"100%",padding:"14px",borderRadius:4,border:"none",background:pfForm.capital?C.accent:C.gray3,color:pfForm.capital?"#fff":C.gray2,fontSize:12,fontWeight:600,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.2em",textTransform:"uppercase",cursor:"pointer",transition:"all 0.3s"}}>
            {L.btn.record} →
          </button>
        </div>
      )}


      {pfView==="add-mt5" && (
        <MT5Connect user={user} darkMode={darkMode} lang={lang} mt5Count={propfirms.filter(p=>p.metaapi_id).length} mt5Limit={pfLimit} onTradesImported={newTrades => { setTrades(p => [...newTrades.filter(n => !p.find(e => e.id === n.id)), ...p]); }} onAccountConnected={newPf => {
          const updated = [...propfirms, newPf];
          setPropfirms(updated);
          save(KEYS.propfirms, updated);
          if (user) saveUserSettings({ propfirms: updated });
          setPfView("list");
        }} />
      )}


      {pfView==="list" && (!propfirms.length || pfListTab==="active") && propfirms.filter(p=>!p.status||p.status==="active").length===0 && propfirms.length===0 && (
        <div style={{textAlign:"center",padding:"60px 0"}}>
          <div style={{fontSize:44,marginBottom:10,color:C.gray2}}>◉</div>
          <div style={{fontFamily:"'Josefin Sans',sans-serif",fontSize:14,fontWeight:300,color:C.gray1,marginBottom:14,letterSpacing:"0.08em"}}>{lang==="fr"?"Aucun compte enregistré":"No accounts yet"}</div>
          <button onClick={(e)=>{e.stopPropagation();setPfView("add-type");}} style={{background:C.accent,border:"none",borderRadius:4,padding:"11px 24px",color:darkMode?"#111":"#fff",fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.15em",textTransform:"uppercase",cursor:"pointer"}}>{lang==="fr"?"+ Ajouter un compte":"+ Add account"}</button>
        </div>
      )}

      {/* ── PILLS Actifs / Archivés ── */}
      {pfView==="list" && propfirms.length>0 && (
        <div style={{display:"flex",gap:6,marginBottom:20}}>
          {[{k:"active",l:L.btn.active},{k:"archived",l:L.btn.archived}].map(tab=>(
            <button key={tab.k} onClick={()=>setPfListTab(tab.k)} style={{padding:"8px 20px",borderRadius:20,border:`1px solid ${pfListTab===tab.k?C.accent:C.border}`,background:pfListTab===tab.k?"rgba(0,0,0,0.06)":"transparent",color:pfListTab===tab.k?C.accent:C.gray1,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:pfListTab===tab.k?600:300,letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer",transition:"all 0.2s"}}>
              {tab.l}{tab.k==="archived"&&propfirms.filter(p=>p.status==="passed"||p.status==="breached").length>0?` · ${propfirms.filter(p=>p.status==="passed"||p.status==="breached").length}`:""}
            </button>
          ))}
        </div>
      )}

      {/* ── ARCHIVE VIEW ── */}
      {pfView==="list" && pfListTab==="archived" && (()=>{
        const archived = propfirms.filter(p=>p.status==="passed"||p.status==="breached");
        const passed = archived.filter(p=>p.status==="passed").length;
        const breached = archived.filter(p=>p.status==="breached").length;
        const rate = archived.length ? Math.round(passed/archived.length*100) : 0;
        return (
          <div>
            {archived.length>0 && (
              <div style={{fontSize:11,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.08em",marginBottom:20}}>
                <span style={{color:"rgba(201,170,130,0.9)"}}>{passed} {lang==="fr"?`validé${passed!==1?"s":""}`:`passed`}</span>
                <span style={{color:C.gray3}}> · </span>
                <span style={{color:"rgba(192,57,43,0.8)"}}>{breached} breach</span>
                <span style={{color:C.gray3}}> · </span>
                <span style={{color:C.white}}>{rate}% {lang==="fr"?"de réussite":"success rate"}</span>
              </div>
            )}
            {archived.length===0 && (
              <div style={{textAlign:"center",padding:"60px 0",color:C.gray2,fontSize:12,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.08em"}}>{lang==="fr"?"Aucun compte archivé":"No archived accounts"}</div>
            )}
            {archived.map(pf=>{
              const cap=parseFloat(pf.capital)||0;
              const target=parseFloat(pf.target)||0;
              const pnl=getPfPnl(pf);
              const isPass=pf.status==="passed";
              const acctTrades=trades.filter(t=>!t.accountIds||t.accountIds.length===0||t.accountIds.includes(pf.id));
              const wins=acctTrades.filter(t=>t.result==="WIN").length;
              const wr=acctTrades.length?Math.round(wins/acctTrades.length*100):0;
              // For trailing DD breach: show drawdown from peak, not total P&L from start
              let displayPnl=pnl, peakPnlVal=0, showPeak=false;
              if(!isPass&&pf.trailingDD){
                const dates=[...new Set(acctTrades.map(t=>t.date))].sort();
                let cum=0,pk=0;
                dates.forEach(d=>{cum+=acctTrades.filter(t=>t.date===d).reduce((s,t)=>s+(t.pnl||0),0);if(cum>pk)pk=cum;});
                peakPnlVal=pk;
                displayPnl=pnl-pk;
                showPeak=true;
              }
              return (
                <div key={pf.id} onClick={()=>{ setSelectedPf(pf); setView("propfirm"); }} style={{background:C.bg2,border:`1px solid ${isPass?"rgba(201,170,130,0.18)":"rgba(192,57,43,0.15)"}`,borderRadius:12,boxShadow:cShadow,padding:!isMobile?"20px":"16px",marginBottom:!isMobile?14:10,cursor:"pointer"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
                    <div>
                      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
                        <div style={{fontFamily:"'Barlow',sans-serif",fontWeight:600,fontSize:16,color:C.white,letterSpacing:"0.08em"}}>{pf.firm||(lang==="fr"?"Fond Propre":"Personal Account")}</div>
                        <span style={{fontSize:9,fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,letterSpacing:"0.18em",textTransform:"uppercase",color:isPass?"rgba(201,170,130,0.9)":"rgba(192,57,43,0.8)",border:`1px solid ${isPass?"rgba(201,170,130,0.25)":"rgba(192,57,43,0.25)"}`,padding:"2px 7px",borderRadius:3}}>{isPass?(lang==="fr"?"VALIDÉ":"PASSED"):"BREACH"}</span>
                      </div>
                      {pf.name&&<div style={{fontSize:11,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif"}}>{pf.name}</div>}
                      <div style={{fontSize:10,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",marginTop:4,textTransform:"uppercase"}}>{cap.toLocaleString()}{currency}{pf.type==="propfirm"?` · ${lang==="fr"?"cible":"target"} ${target.toLocaleString()}${currency}`:""}</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontFamily:"'Josefin Sans',sans-serif",fontWeight:300,fontSize:20,color:displayPnl>=0?"#2a6e3a":"#c0392b"}}>{fms(displayPnl)}{currency}</div>
                      {showPeak&&<div style={{fontSize:10,color:C.gray2,fontFamily:"'Josefin Sans',sans-serif",marginTop:1,letterSpacing:"0.06em"}}>{lang==="fr"?`depuis +${fm(peakPnlVal)}${currency} au sommet`:`from peak +${fm(peakPnlVal)}${currency}`}</div>}
                      <div style={{fontSize:10,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",marginTop:2}}>{wr}% WR · {acctTrades.length} trade{acctTrades.length!==1?"s":""}</div>
                    </div>
                  </div>
                  <button onClick={()=>setPropfirms(prev=>prev.map(p=>p.id===pf.id?{...p,status:"active"}:p))} style={{width:"100%",padding:"9px",borderRadius:6,border:`1px solid ${C.border}`,background:"transparent",color:C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.15em",textTransform:"uppercase",cursor:"pointer",transition:"all 0.2s"}}>{lang==="fr"?"Restaurer":"Restore"}</button>
                </div>
              );
            })}
          </div>
        );
      })()}

      {pfView==="list" && pfListTab==="active" && propfirms.filter(p=>!p.status||p.status==="active").length===0 && propfirms.length>0 && (
        <div style={{textAlign:"center",padding:"60px 0"}}>
          <div style={{fontSize:44,marginBottom:10,color:C.gray2}}>◉</div>
          <div style={{fontFamily:"'Josefin Sans',sans-serif",fontSize:14,fontWeight:300,color:C.gray1,marginBottom:6,letterSpacing:"0.08em"}}>{lang==="fr"?"Tous les comptes sont archivés":"All accounts are archived"}</div>
          <button onClick={()=>setPfListTab("archived")} style={{fontSize:11,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",background:"none",border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 18px",cursor:"pointer",letterSpacing:"0.1em",textTransform:"uppercase"}}>Voir les archives</button>
        </div>
      )}

      {pfView==="list" && pfListTab==="active" && (
        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr 1fr",gap:14}}>
          {propfirms.filter(p=>!p.status||p.status==="active").map((pf) => {
        const cap = parseFloat(pf.capital)||0;
        const target = parseFloat(pf.target)||0;
        const maxLoss = parseFloat(pf.maxLoss)||0;
        const pnl = getPfPnl(pf);
        const progress = Math.min(100, Math.max(0, (pnl/target)*100));
        const drawdown = Math.abs(Math.min(0,pnl));
        const ddProgress = Math.min(100, (drawdown/maxLoss)*100);
        const alerts = getPfAlerts(pf);
        const isInDanger = alerts.some(a=>a.type==="danger");
        return (
          <div key={pf.id} style={{background:C.bg2,border:`1px solid ${isInDanger?"rgba(192,57,43,0.3)":C.border}`,borderRadius:12,boxShadow:cShadow,padding:!isMobile?"20px 18px":"16px 14px",cursor:editingPf?.id===pf.id?"default":"pointer"}} onClick={()=>{ if(!editingPf) setSelectedPf(pf); }}>
            {/* Header */}
            {editingPf?.id === pf.id ? (
              <div style={{marginBottom:12}}>
                <div style={{fontSize:10,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.15em",textTransform:"uppercase",marginBottom:12}}>{lang==="fr"?"Modifier le compte":"Edit account"}</div>
                {pf.metaapi_id && <div style={{marginBottom:8}}>
                  <Label>{lang==="fr"?"Type de compte":"Account type"}</Label>
                  <div style={{display:"flex",gap:6,background:C.bg3,borderRadius:8,padding:3}}>
                    {[{k:"propfirm",l:"Prop Firm"},{k:"personal",l:lang==="fr"?"Fond Propre":"Personal Account"}].map(({k,l})=>(
                      <button key={k} onClick={()=>setEditingPf(p=>({...p,type:k}))} style={{flex:1,padding:"7px",borderRadius:6,border:"none",background:editingPf.type===k?C.accent:"transparent",color:editingPf.type===k?(darkMode?"#111":"#fff"):C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer",transition:"all 0.2s"}}>{l}</button>
                    ))}
                  </div>
                </div>}
                {editingPf.type==="propfirm" && <div style={{marginBottom:8}}><Label>Prop Firm</Label><input type="text" value={editingPf.firm||""} onChange={e=>setEditingPf(p=>({...p,firm:e.target.value}))} style={{...iStyle,padding:"9px 10px",fontSize:13}}/></div>}
                <div style={{marginBottom:8}}><Label>{lang==="fr"?"Nom du compte":"Account name"}</Label><input type="text" value={editingPf.name||""} onChange={e=>setEditingPf(p=>({...p,name:e.target.value}))} style={{...iStyle,padding:"9px 10px",fontSize:13}}/></div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                  <div><Label>Capital</Label><input type="text" inputMode="numeric" value={editingPf.capital||""} onChange={e=>setEditingPf(p=>({...p,capital:e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"")}))} style={{...iStyle,padding:"9px 10px",fontSize:13}}/></div>
                  {(pf.type==="propfirm"||pf.type==="mt5") && <div><Label>Profit Target</Label><input type="text" inputMode="numeric" value={editingPf.target||""} onChange={e=>setEditingPf(p=>({...p,target:e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"")}))} style={{...iStyle,padding:"9px 10px",fontSize:13}}/></div>}
                  {(pf.type==="propfirm"||pf.type==="mt5") && <div>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                      <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.18em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>Max Drawdown</div>
                        <span onClick={()=>setEditingPf(p=>({...p,trailingDD:!p.trailingDD}))} style={{cursor:"pointer",fontSize:9,fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",color:editingPf.trailingDD?C.accent:C.gray2,transition:"color 0.2s",userSelect:"none"}}>
                        {editingPf.trailingDD?(lang==="fr"?"▲ Suiveur actif":"▲ Trailing active"):(lang==="fr"?"Suiveur":"Trailing")}
                      </span>
                    </div>
                    <input type="text" inputMode="numeric" value={editingPf.maxLoss||""} onChange={e=>setEditingPf(p=>({...p,maxLoss:e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"")}))} style={{...iStyle,padding:"9px 10px",fontSize:13}}/>
                  </div>}
                </div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                  <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>Daily Loss</div>
                  <button onClick={()=>setEditingPf(p=>({...p,hasDailyLoss:!p.hasDailyLoss}))} style={{padding:"4px 10px",borderRadius:20,border:`1px solid ${C.border}`,background:editingPf.hasDailyLoss?C.accent:"transparent",color:editingPf.hasDailyLoss?"#fff":C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",cursor:"pointer",textTransform:"uppercase"}}>{editingPf.hasDailyLoss?(lang==="fr"?"Activée":"On"):(lang==="fr"?"Désactivée":"Off")}</button>
                </div>
                {editingPf.hasDailyLoss && <div style={{marginBottom:8}}><Label>Montant daily loss</Label><input type="text" inputMode="numeric" value={editingPf.dailyLoss||""} onChange={e=>setEditingPf(p=>({...p,dailyLoss:e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"")}))} style={{...iStyle,padding:"9px 10px",fontSize:13}}/></div>}
                {(pf.type==="propfirm"||pf.type==="mt5") && <>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                    <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>Consistance</div>
                    <button onClick={()=>setEditingPf(p=>({...p,hasConsistency:!p.hasConsistency}))} style={{padding:"4px 10px",borderRadius:20,border:`1px solid ${C.border}`,background:editingPf.hasConsistency?C.accent:"transparent",color:editingPf.hasConsistency?"#fff":C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",cursor:"pointer",textTransform:"uppercase"}}>{editingPf.hasConsistency?(lang==="fr"?"Activée":"On"):(lang==="fr"?"Désactivée":"Off")}</button>
                  </div>
                  {editingPf.hasConsistency && (
                  <div style={{marginBottom:8}}><Label>% consistance</Label><input type="text" inputMode="numeric" value={editingPf.consistencyPct||""} onChange={e=>setEditingPf(p=>({...p,consistencyPct:e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"")}))} style={{...iStyle,padding:"9px 10px",fontSize:13}}/></div>
                  )}
                  {pf.type==="propfirm" && <>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                    <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>Règle d'Inactivité</div>
                    <button onClick={()=>setEditingPf(p=>({...p,hasInactivity:!p.hasInactivity}))} style={{padding:"4px 10px",borderRadius:20,border:`1px solid ${C.border}`,background:editingPf.hasInactivity?C.accent:"transparent",color:editingPf.hasInactivity?"#fff":C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",cursor:"pointer",textTransform:"uppercase"}}>{editingPf.hasInactivity?(lang==="fr"?"Activée":"On"):(lang==="fr"?"Désactivée":"Off")}</button>
                  </div>
                  {editingPf.hasInactivity && (
                  <div style={{marginBottom:8}}><Label>Jours max d'inactivité</Label><input type="text" inputMode="numeric" value={editingPf.inactivityDays||""} onChange={e=>setEditingPf(p=>({...p,inactivityDays:e.target.value.replace(/[^0-9]/g,"")}))} style={{...iStyle,padding:"9px 10px",fontSize:13}}/></div>
                  )}
                  {editingPf.hasInactivity && (
                  <div style={{marginBottom:8}}><Label>À partir du</Label><input type="date" value={editingPf.inactivityFrom||""} onChange={e=>setEditingPf(p=>({...p,inactivityFrom:e.target.value}))} style={{...iStyle,padding:"9px 10px",fontSize:13}}/></div>
                  )}
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                    <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>{lang==="fr"?"Jours min. de trading":"Min. trading days"}</div>
                    <button onClick={()=>setEditingPf(p=>({...p,hasMinDays:!p.hasMinDays}))} style={{padding:"4px 10px",borderRadius:20,border:`1px solid ${C.border}`,background:editingPf.hasMinDays?C.accent:"transparent",color:editingPf.hasMinDays?"#fff":C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",cursor:"pointer",textTransform:"uppercase"}}>{editingPf.hasMinDays?(lang==="fr"?"Activé":"On"):(lang==="fr"?"Désactivé":"Off")}</button>
                  </div>
                  {editingPf.hasMinDays && (
                  <div style={{marginBottom:8}}><Label>Nombre de jours minimum</Label><input type="text" inputMode="numeric" placeholder="ex: 10" value={editingPf.minDays||""} onChange={e=>setEditingPf(p=>({...p,minDays:e.target.value.replace(/[^0-9]/g,"")}))} style={{...iStyle,padding:"9px 10px",fontSize:13}}/></div>
                  )}
                  </>}
                </>}
                {/* Step 2 edit section */}
                {editingPf.step===1 && editingPf._step2 && (
                  <div style={{marginTop:12}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                      <div style={{flex:1,height:1,background:C.border}}/>
                      <div style={{fontSize:9,color:C.accent,fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,letterSpacing:"0.2em",textTransform:"uppercase",whiteSpace:"nowrap"}}>Objectifs Step 2</div>
                      <div style={{flex:1,height:1,background:C.border}}/>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                      <div><Label>Capital Step 2</Label><input type="text" inputMode="numeric" value={editingPf._step2.capital||""} onChange={e=>{const v=e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"");setEditingPf(p=>({...p,_step2:{...p._step2,capital:v}}));}} style={{...iStyle,padding:"9px 10px",fontSize:13}}/></div>
                      <div><Label>Profit Target</Label><input type="text" inputMode="numeric" value={editingPf._step2.target||""} onChange={e=>{const v=e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"");setEditingPf(p=>({...p,_step2:{...p._step2,target:v}}));}} style={{...iStyle,padding:"9px 10px",fontSize:13}}/></div>
                      <div>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                          <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.18em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>Max Drawdown</div>
                          <span onClick={()=>setEditingPf(p=>({...p,_step2:{...p._step2,trailingDD:!p._step2.trailingDD}}))} style={{cursor:"pointer",fontSize:9,fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",color:editingPf._step2.trailingDD?C.accent:C.gray2,userSelect:"none"}}>{editingPf._step2.trailingDD?(lang==="fr"?"▲ Suiveur":"▲ Trailing"):(lang==="fr"?"Suiveur":"Trailing")}</span>
                        </div>
                        <input type="text" inputMode="numeric" value={editingPf._step2.maxLoss||""} onChange={e=>{const v=e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"");setEditingPf(p=>({...p,_step2:{...p._step2,maxLoss:v}}));}} style={{...iStyle,padding:"9px 10px",fontSize:13}}/>
                      </div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                      <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>Daily Loss Step 2</div>
                      <button onClick={()=>setEditingPf(p=>({...p,_step2:{...p._step2,hasDailyLoss:!p._step2.hasDailyLoss}}))} style={{padding:"4px 10px",borderRadius:20,border:`1px solid ${C.border}`,background:editingPf._step2.hasDailyLoss?C.accent:"transparent",color:editingPf._step2.hasDailyLoss?"#fff":C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",cursor:"pointer",textTransform:"uppercase"}}>{editingPf._step2.hasDailyLoss?"Activée":"Désactivée"}</button>
                    </div>
                    {editingPf._step2.hasDailyLoss && <div style={{marginBottom:8}}><Label>Montant daily loss</Label><input type="text" inputMode="numeric" value={editingPf._step2.dailyLoss||""} onChange={e=>{const v=e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"");setEditingPf(p=>({...p,_step2:{...p._step2,dailyLoss:v}}));}} style={{...iStyle,padding:"9px 10px",fontSize:13}}/></div>}
                  </div>
                )}
                <div style={{display:"flex",gap:7,marginTop:4}}>
                  <button onClick={()=>{const {_step2,...s1}=editingPf;setPropfirms(p=>p.map(x=>x.id===s1.id?s1:(_step2&&x.id===_step2.id?_step2:x)));setEditingPf(null);}} style={{flex:2,padding:"9px",borderRadius:4,border:"none",background:"radial-gradient(ellipse 90% 90% at 50% 38%, rgba(245,245,245,0.95) 0%, rgba(215,215,215,0.88) 55%, rgba(230,230,230,0.92) 100%)",color:"#111",fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer"}}>✓ Sauvegarder</button>
                  <button onClick={()=>setEditingPf(null)} style={{flex:1,padding:"9px",borderRadius:4,border:`1px solid ${C.gray3}`,background:"transparent",color:C.gray1,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer"}}>{lang==="fr"?"Annuler":"Cancel"}</button>
                </div>
              </div>
            ) : (
              <div style={{marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:8,fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,letterSpacing:"0.14em",textTransform:"uppercase",padding:"3px 7px",borderRadius:4,background:pf.type==="propfirm"?"rgba(232,205,169,0.12)":"rgba(255,255,255,0.07)",color:pf.type==="propfirm"?C.accent:C.gray1,border:`1px solid ${pf.type==="propfirm"?"rgba(232,205,169,0.25)":C.gray3}`}}>{pf.type==="propfirm"?"PROP FIRM":lang==="fr"?"FOND PROPRE":"PERSONAL"}</span>
                    {isInDanger && <span style={{fontSize:8,fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",padding:"3px 7px",borderRadius:4,background:"rgba(192,57,43,0.15)",color:"#e05a5a",border:"1px solid rgba(192,57,43,0.35)"}}>DANGER</span>}
                  </div>
                  <button onClick={e=>{e.stopPropagation();const s2=pf.linkedStep2Id?propfirms.find(q=>q.id===pf.linkedStep2Id):null;setEditingPf({...pf,_step2:s2?{...s2}:null});}} style={{background:"none",border:`1px solid ${C.gray3}`,borderRadius:4,color:C.gray1,cursor:"pointer",fontSize:11,padding:"2px 7px",fontFamily:"'Josefin Sans',sans-serif"}}>✎</button>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    {(()=>{const m=ALL_KNOWN_FIRMS.find(p=>p.name.toLowerCase()===(pf.firm||"").toLowerCase());return m?<img src={`https://www.google.com/s2/favicons?sz=128&domain=${m.domain}`} style={{width:24,height:24,borderRadius:5,objectFit:"contain",flexShrink:0}} alt="" onError={e=>e.target.style.display="none"}/>:null;})()}
                    <div>
                      <div style={{fontFamily:"'Barlow',sans-serif",fontWeight:600,fontSize:17,color:C.white,letterSpacing:"0.02em",lineHeight:1.1}}>{pf.name||pf.firm||"Compte"}</div>
                      {pf.name && pf.firm && <div style={{fontSize:11,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",marginTop:3}}>{pf.firm}</div>}
                      {pf.step && <div style={{fontSize:9,color:C.accent,fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,letterSpacing:"0.15em",textTransform:"uppercase",marginTop:2}}>Step {pf.step}</div>}
                    </div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontFamily:"'Josefin Sans',sans-serif",fontWeight:300,fontSize:21,color:pnl>=0?"#4caf6e":"#e05a5a",letterSpacing:"0.02em",lineHeight:1}}>{fm(cap+pnl)}{currency}</div>
                    <div style={{display:"flex",alignItems:"center",gap:6,justifyContent:"flex-end",marginTop:4}}>
                      <span style={{fontSize:11,color:pnl>=0?"#4caf6e":"#e05a5a",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>{fms(pnl)}{currency}</span>
                      {cap>0 && <span style={{fontSize:10,color:pnl>=0?"rgba(76,175,110,0.6)":"rgba(224,90,90,0.6)",fontFamily:"'Josefin Sans',sans-serif"}}>({pnl>=0?"+":""}{((pnl/cap)*100).toFixed(1)}%)</span>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {editingPf?.id !== pf.id && pf.type==="propfirm" && (()=>{
              const pfTrades = trades.filter(t => !t.accountIds || t.accountIds.length===0 || t.accountIds.includes(pf.id));
              const peakPnl = (() => {
                const sortedDates = [...new Set(pfTrades.map(t => t.date))].sort();
                let cum = 0, peak = 0;
                sortedDates.forEach(date => {
                  cum += pfTrades.filter(t => t.date === date).reduce((s, t) => s + (t.pnl || 0), 0);
                  if (cum > peak) peak = cum;
                });
                return peak;
              })();
              const mll = pf.trailingDD ? cap + Math.max(0, peakPnl) - maxLoss : cap - maxLoss;
              const tgt = cap + target;
              const totalRange = tgt - mll;
              const currentCap = cap + pnl;
              const startPct = totalRange ? (cap - mll) / totalRange * 100 : 50;
              const currentPct = totalRange ? Math.min(100, Math.max(0, (currentCap - mll) / totalRange * 100)) : 50;
              const isProfit = pnl >= 0;
              const fillLeft = isProfit ? startPct : currentPct;
              const fillWidth = Math.abs(currentPct - startPct);
              return (
                <div style={{marginBottom:10}}>
                  {/* START label row */}
                  <div style={{position:"relative",height:16,marginBottom:4}}>
                    <div style={{position:"absolute",left:startPct+"%",transform:"translateX(-50%)",fontSize:9,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.15em",textTransform:"uppercase",whiteSpace:"nowrap"}}>START</div>
                    {pf.trailingDD && <div style={{position:"absolute",left:0,fontSize:9,color:C.accent,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase"}}>▲ {lang==="fr"?"Suiveur":"Trailing"}</div>}
                  </div>
                  {/* Bar MLL→TARGET */}
                  <div style={{position:"relative",height:4,background:darkMode?"rgba(255,255,255,0.08)":C.gray3,borderRadius:2}}>
                    <div style={{position:"absolute",left:fillLeft+"%",width:fillWidth+"%",height:"100%",borderRadius:2,background:isProfit?"rgba(74,222,128,0.7)":"rgba(229,100,100,0.7)",transition:"all 0.6s",boxShadow:isProfit?"0 0 8px rgba(74,222,128,0.4)":"0 0 8px rgba(229,100,100,0.4)"}}/>
                    <div style={{position:"absolute",left:startPct+"%",transform:"translateX(-50%)",width:2,height:14,background:C.dim,borderRadius:1,top:-5}}/>
                    <div style={{position:"absolute",left:currentPct+"%",transform:"translateX(-50%)",width:3,height:12,background:isProfit?clrGreen:"#e05a5a",borderRadius:2,top:-4,transition:"left 0.6s",boxShadow:isProfit?"0 0 6px rgba(74,222,128,0.8)":"0 0 6px rgba(229,100,100,0.8)"}}/>
                  </div>
                  {/* MLL / TARGET labels */}
                  <div style={{display:"flex",justifyContent:"space-between",marginTop:8,marginBottom:pf.hasDailyLoss&&parseFloat(pf.dailyLoss)>0?10:0}}>
                    <div>
                      <div style={{fontSize:11,fontWeight:300,color:"rgba(229,100,100,0.85)",fontFamily:"'Josefin Sans',sans-serif",lineHeight:1}}>{fm(mll)}{currency}</div>
                      <div style={{fontSize:9,color:"rgba(229,100,100,0.5)",fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.12em",textTransform:"uppercase",marginTop:3}}>MLL</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:11,fontWeight:300,color:"rgba(74,222,128,0.85)",fontFamily:"'Josefin Sans',sans-serif",lineHeight:1}}>{fm(tgt)}{currency}</div>
                      <div style={{fontSize:9,color:"rgba(74,222,128,0.5)",fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.12em",textTransform:"uppercase",marginTop:3}}>TARGET</div>
                    </div>
                  </div>
                  {/* Daily Loss bar */}
                  {pf.hasDailyLoss && parseFloat(pf.dailyLoss)>0 && (()=>{
                    const dl=parseFloat(pf.dailyLoss);
                    const todayLoss=Math.abs(Math.min(0,pfTrades.filter(t=>t.date===tradingToday).reduce((s,t)=>s+(t.pnl||0),0)));
                    const dlPct=Math.min(100,(todayLoss/dl)*100);
                    const over=todayLoss>=dl;
                    return (
                      <div>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                          <span style={{fontSize:10,color:over?"rgba(192,57,43,0.9)":C.dim,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase"}}>Daily Loss</span>
                          <span style={{fontSize:10,color:over?"rgba(192,57,43,0.9)":C.dim,fontFamily:"'Josefin Sans',sans-serif"}}>{fm(todayLoss)}{currency} / {dl}{currency}</span>
                        </div>
                        <div style={{height:4,background:C.gray3,borderRadius:2}}>
                          <div style={{width:dlPct+"%",height:"100%",borderRadius:2,background:over?"rgba(192,57,43,0.9)":dlPct>=70?"rgba(192,57,43,0.5)":"rgba(192,57,43,0.3)",transition:"width 0.5s"}}/>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })()}

            {editingPf?.id !== pf.id && (()=>{
              const pfT=trades.filter(t=>!t.accountIds||t.accountIds.length===0||t.accountIds.includes(pf.id));
              const sorted=[...pfT].sort((a,b)=>a.date.localeCompare(b.date));
              let cum=0;
              const chartData=[{v:0,d:"",inst:""},...sorted.map(t=>{cum+=t.pnl||0;return{v:parseFloat(cum.toFixed(2)),d:t.date,inst:t.instrument||""};})];
              if(chartData.length<2) return null;
              const cVals=chartData.map(d=>d.v);
              const cMax=Math.max(...cVals),cMin=Math.min(...cVals);
              const czp=cMax===cMin?100:Math.min(100,Math.max(0,cMax/(cMax-cMin)*100));
              const cgid=`cpf_${pf.id}`;
              return (
                <div style={{marginBottom:10}}>
                  <ResponsiveContainer width="100%" height={110}>
                    <AreaChart data={chartData} margin={{top:6,right:2,left:0,bottom:0}}>
                      <defs>
                        <linearGradient id={cgid} x1="0" y1="0" x2="0" y2="1">
                          <stop offset={`${czp}%`} stopColor="#4caf6e" stopOpacity={1}/>
                          <stop offset={`${czp}%`} stopColor="#e05a5a" stopOpacity={1}/>
                        </linearGradient>
                        <linearGradient id={`${cgid}f`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset={`${czp}%`} stopColor="#4caf6e" stopOpacity={0.15}/>
                          <stop offset={`${czp}%`} stopColor="#e05a5a" stopOpacity={0.15}/>
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="d" tick={{fontSize:8,fontFamily:"'Josefin Sans',sans-serif",fill:C.dim}} tickLine={false} axisLine={false} interval="preserveStartEnd" tickFormatter={v=>v?v.slice(5):""}/>
                      <ReferenceLine y={0} stroke={darkMode?"rgba(255,255,255,0.12)":"rgba(0,0,0,0.12)"} strokeWidth={1} strokeDasharray="3 3"/>
                      <Tooltip contentStyle={{background:darkMode?"rgba(14,14,14,0.96)":"rgba(255,255,255,0.96)",border:`1px solid ${C.border}`,borderRadius:7,fontSize:10,color:C.white,padding:"5px 10px"}} formatter={v=>[`${fms(v)}${currency}`,""]} labelFormatter={(_,pl)=>pl?.[0]?.payload?.inst||""}/>
                      <Area type="monotone" dataKey="v" stroke={`url(#${cgid})`} strokeWidth={2} fill={`url(#${cgid}f)`} dot={{r:2.5,fill:`url(#${cgid})`,strokeWidth:0}} activeDot={{r:4,strokeWidth:0}}/>
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              );
            })()}

            {editingPf?.id !== pf.id && pf.type==="propfirm" && pf.hasInactivity && pf.inactivityDays && (()=>{
              const today = new Date(); today.setHours(0,0,0,0);
              const maxDays = parseInt(pf.inactivityDays)||1;
              const pfTrades = trades.filter(t => (!t.accountIds||t.accountIds.length===0||t.accountIds.includes(pf.id)) && (!pf.inactivityFrom || t.date >= pf.inactivityFrom));
              let daysSince;
              if (pfTrades.length > 0) {
                const lastDate = pfTrades.reduce((mx,t)=>t.date>mx?t.date:mx,"");
                const last = new Date(lastDate+"T12:00:00"); last.setHours(0,0,0,0);
                daysSince = Math.floor((today-last)/(1000*60*60*24));
              } else if (pf.inactivityFrom) {
                const start = new Date(pf.inactivityFrom+"T12:00:00"); start.setHours(0,0,0,0);
                daysSince = Math.floor((today-start)/(1000*60*60*24));
              } else {
                daysSince = maxDays;
              }
              const pct = Math.min(100,(daysSince/maxDays)*100);
              const isViolation = daysSince >= maxDays;
              const barColor = isViolation ? "rgba(192,57,43,0.9)" : pct >= 50 ? "rgba(192,57,43,0.9)" : "rgba(42,110,58,0.85)";
              return (
                <div style={{marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:10,color:isViolation?"rgba(192,57,43,0.9)":C.dim,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase"}}>Inactivité</span>
                    <span style={{fontSize:10,color:isViolation?"rgba(192,57,43,0.9)":C.dim,fontFamily:"'Josefin Sans',sans-serif",fontWeight:isViolation?600:400}}>{isViolation?"⚠ Violation":`${daysSince}j / ${maxDays}j`}</span>
                  </div>
                  <div style={{height:5,background:C.gray3,borderRadius:3}}>
                    <div style={{width:pct+"%",height:"100%",borderRadius:3,background:barColor,transition:"width 0.5s"}}/>
                  </div>
                </div>
              );
            })()}

            {editingPf?.id !== pf.id && pf.type==="propfirm" && pf.hasMinDays && pf.minDays && (()=>{
              const minD = parseInt(pf.minDays)||1;
              const pfTrades = trades.filter(t => !t.accountIds||t.accountIds.length===0||t.accountIds.includes(pf.id));
              let elapsed = 0;
              if (pfTrades.length > 0) {
                const oldest = pfTrades.reduce((mn,t)=>t.date<mn?t.date:mn, pfTrades[0].date);
                const start = new Date(oldest+"T00:00:00");
                elapsed = Math.floor((Date.now() - start.getTime()) / (1000*60*60*24));
              }
              const pct = Math.min(100, (elapsed/minD)*100);
              const done = elapsed >= minD;
              const barColor = done ? "#4caf6e" : pct >= 60 ? C.accent : "rgba(232,205,169,0.7)";
              return (
                <div style={{marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:10,color:done?"#4caf6e":C.dim,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase"}}>{lang==="fr"?"Jours min. trading":"Min. trading days"}</span>
                    <span style={{fontSize:10,color:done?"#4caf6e":C.dim,fontFamily:"'Josefin Sans',sans-serif",fontWeight:done?600:400}}>{done?`✓ ${elapsed}j / ${minD}j`:`${elapsed}j / ${minD}j`}</span>
                  </div>
                  <div style={{height:5,background:C.gray3,borderRadius:3}}>
                    <div style={{width:pct+"%",height:"100%",borderRadius:3,background:barColor,transition:"width 0.5s"}}/>
                  </div>
                </div>
              );
            })()}

            {editingPf?.id !== pf.id && (() => {
              const acctTrades = trades.filter(t => !t.accountIds||t.accountIds.length===0||t.accountIds.includes(pf.id));
              const acctWins = acctTrades.filter(t=>t.result==="WIN").length;
              const acctWr = acctTrades.length ? Math.round(acctWins/acctTrades.length*100) : 0;
              const acctAvgRR = acctTrades.length ? (acctTrades.reduce((s,t)=>s+(parseFloat(t.rr)||0),0)/acctTrades.length).toFixed(1) : "—";
              const todayTrades = acctTrades.filter(t=>t.date===tradingToday);
              const todayPnl = todayTrades.reduce((s,t)=>s+(t.pnl||0),0);
              const streak = (() => {
                const s=[...acctTrades].sort((a,b)=>b.date.localeCompare(a.date));
                if(!s.length) return {n:0,type:"WIN"};
                const first=s[0].result;
                if(first==="BREAKEVEN") return {n:0,type:"WIN"};
                let n=0;
                for(const t of s){ if(t.result===first) n++; else break; }
                return {n,type:first};
              })();
              const wrColor = acctWr>=60?"#4caf6e":acctWr>=45?C.white:"#e05a5a";
              const streakColor = streak.type==="WIN"?"#4caf6e":"#e05a5a";
              return (
                <div style={{marginBottom:10,marginTop:4}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
                    <div style={{background:C.bg3,borderRadius:8,padding:"10px 12px",border:`1px solid ${C.gray3}`,display:"flex",flexDirection:"column",alignItems:"center"}}>
                      <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:"'Josefin Sans',sans-serif",marginBottom:6,alignSelf:"flex-start"}}>Win Rate</div>
                      {acctTrades.length ? (()=>{
                        const r=28,sw=5,cx=32,cy=30;
                        const pct=acctWr/100;
                        const circ=Math.PI*r;
                        const dash=pct*circ;
                        return (
                          <svg width={64} height={38} viewBox="0 0 64 38">
                            <path d={`M${cx-r},${cy} A${r},${r} 0 0 1 ${cx+r},${cy}`} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={sw} strokeLinecap="round"/>
                            <path d={`M${cx-r},${cy} A${r},${r} 0 0 1 ${cx+r},${cy}`} fill="none" stroke={wrColor} strokeWidth={sw} strokeLinecap="round" strokeDasharray={`${dash} ${circ}`} style={{filter:`drop-shadow(0 0 4px ${wrColor}88)`}}/>
                            <text x={cx} y={cy-4} textAnchor="middle" fontSize={13} fontWeight={600} fill={wrColor} fontFamily="'Josefin Sans',sans-serif">{acctWr}%</text>
                          </svg>
                        );
                      })() : <div style={{fontSize:20,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",fontWeight:300,lineHeight:1,marginBottom:4}}>—</div>}
                      <div style={{fontSize:9,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",marginTop:2}}>{acctWins}W / {acctTrades.length-acctWins}L</div>
                    </div>
                    <div style={{background:C.bg3,borderRadius:8,padding:"10px 12px",border:`1px solid ${C.gray3}`}}>
                      <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:"'Josefin Sans',sans-serif",marginBottom:5}}>Streak</div>
                      <div style={{fontSize:20,color:streak.n>0?streakColor:C.dim,fontFamily:"'Josefin Sans',sans-serif",fontWeight:300,lineHeight:1}}>{streak.n>0?`${streak.n}×`:"—"}</div>
                      <div style={{fontSize:9,color:streak.n>0?streakColor:C.dim,fontFamily:"'Josefin Sans',sans-serif",marginTop:3}}>{streak.n>0?(streak.type==="WIN"?"consec. WIN":"consec. LOSS"):"—"}</div>
                    </div>
                    <div style={{background:C.bg3,borderRadius:8,padding:"10px 12px",border:`1px solid ${C.gray3}`}}>
                      <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:"'Josefin Sans',sans-serif",marginBottom:5}}>RR Moy.</div>
                      <div style={{fontSize:20,color:C.white,fontFamily:"'Josefin Sans',sans-serif",fontWeight:300,lineHeight:1}}>{acctAvgRR}</div>
                      <div style={{fontSize:9,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",marginTop:3}}>{acctTrades.length} trades</div>
                    </div>
                  </div>
                  <div style={{background:C.bg3,borderRadius:8,padding:"10px 12px",border:`1px solid ${C.gray3}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:todayTrades.length>0?6:0}}>
                      <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:"'Josefin Sans',sans-serif"}}>{lang==="fr"?"Aujourd'hui":"Today"}</div>
                      {pf.hasConsistency && pf.consistencyPct && pf.target && <div style={{fontSize:9,color:C.dim,fontFamily:"'Josefin Sans',sans-serif"}}>max {fm(parseFloat(pf.target)*parseFloat(pf.consistencyPct)/100)}{currency}</div>}
                    </div>
                    {todayTrades.length>0 ? (
                      <div style={{display:"flex",alignItems:"baseline",gap:8}}>
                        <span style={{fontSize:18,color:todayPnl>=0?"#4caf6e":"#e05a5a",fontFamily:"'Josefin Sans',sans-serif",fontWeight:300}}>{fms(todayPnl)}{currency}</span>
                        <span style={{fontSize:10,color:C.dim,fontFamily:"'Josefin Sans',sans-serif"}}>{todayTrades.length} trade{todayTrades.length!==1?"s":""}</span>
                      </div>
                    ) : (
                      <div style={{fontSize:12,color:C.gray2,fontFamily:"'Josefin Sans',sans-serif",fontWeight:300}}>{lang==="fr"?"Aucun trade aujourd'hui":"No trades today"}</div>
                    )}
                    {pf.hasConsistency && pf.consistencyPct && pf.target && todayTrades.length>0 && (() => {
                      const maxD=parseFloat(pf.target)*parseFloat(pf.consistencyPct)/100;
                      const todayGain=Math.max(0,todayPnl);
                      const gaugePct=Math.min(100,maxD>0?(todayGain/maxD)*100:0);
                      const isOver=todayGain>=maxD;
                      return (
                        <div style={{marginTop:8}}>
                          <div style={{height:3,background:"rgba(0,0,0,0.15)",borderRadius:2,marginBottom:4}}>
                            <div style={{width:gaugePct+"%",height:"100%",borderRadius:2,background:isOver?"rgba(192,57,43,0.7)":gaugePct>=80?"rgba(var(--gold-rgb),0.6)":C.accent,transition:"width 0.5s"}}/>
                          </div>
                          <div style={{fontSize:10,color:isOver?"rgba(192,57,43,0.8)":C.gray1,fontFamily:"'Josefin Sans',sans-serif"}}>{isOver?(lang==="fr"?"Limite de consistance atteinte":"Consistency limit reached"):`${fm(maxD-todayGain)}${currency} ${lang==="fr"?"restants":"left"}`}</div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })()}
            {editingPf?.id !== pf.id && (pf.hasDailyLoss||pf.hasConsistency) && <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:alerts.length?10:0}}>
              {pf.hasDailyLoss && <span style={{fontSize:9,color:C.dim,background:C.bg3,padding:"3px 8px",borderRadius:3,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.08em",border:`1px solid ${C.gray3}`}}>Daily Loss {pf.dailyLoss}{currency}</span>}
              {pf.hasConsistency && <span style={{fontSize:9,color:C.dim,background:C.bg3,padding:"3px 8px",borderRadius:3,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.08em",border:`1px solid ${C.gray3}`}}>Consistance {pf.consistencyPct}%</span>}
            </div>}


            {editingPf?.id !== pf.id && alerts.map((a,i) => (
              <div key={i} style={{padding:"8px 10px",borderRadius:4,marginTop:6,background:a.type==="danger"?"rgba(192,57,43,0.08)":a.type==="warn"?"rgba(var(--gold-rgb),0.08)":a.type==="success"?"rgba(42,110,58,0.08)":"rgba(0,0,0,0.04)",border:`1px solid ${a.type==="danger"?"rgba(192,57,43,0.25)":a.type==="warn"?"rgba(var(--gold-rgb),0.25)":a.type==="success"?"rgba(42,110,58,0.25)":C.border}`}}>
                <div style={{fontSize:12,color:C.white,fontFamily:"'Josefin Sans',sans-serif",lineHeight:1.5}}>{a.msg}</div>
              </div>
            ))}
          </div>
        );
          })}
        </div>
      )}
    </div>
  );


  // ── Account Detail JSX ──
  const accountDetailContent = (pf, desktop) => {
    const isArchived = pf.status === "passed" || pf.status === "breached";
    const effectiveView = isArchived ? "global" : acctView;
    const acctTrades = trades.filter(t => !t.accountIds || t.accountIds.length===0 || t.accountIds.includes(pf.id));
    const todayStr = tradingToday;

    // All-time stats
    const allPnl = acctTrades.reduce((s,t)=>s+(t.pnl||0),0);
    const allWins = acctTrades.filter(t=>t.result==="WIN").length;
    const allLosses = acctTrades.filter(t=>t.result==="LOSS").length;
    const allTotal = acctTrades.length;
    const allWinRate = allTotal ? Math.round(allWins/allTotal*100) : 0;
    const allAvgWin = allWins ? acctTrades.filter(t=>t.result==="WIN").reduce((s,t)=>s+(t.pnl||0),0)/allWins : 0;
    const allAvgLoss = allLosses ? Math.abs(acctTrades.filter(t=>t.result==="LOSS").reduce((s,t)=>s+(t.pnl||0),0)/allLosses) : 0;
    const profitFactor = allAvgLoss>0 ? (allAvgWin*allWins/(allAvgLoss*allLosses)).toFixed(2) : "∞";
    const allAvgRR = (() => { const rt=acctTrades.filter(t=>parseFloat(t.rr)>0); return rt.length ? (rt.reduce((s,t)=>s+parseFloat(t.rr),0)/rt.length).toFixed(1) : "—"; })();
    const bestDay = (() => { const byD={}; acctTrades.forEach(t=>{byD[t.date]=(byD[t.date]||0)+(t.pnl||0);}); return Math.max(0,...Object.values(byD)); })();
    const worstDay = (() => { const byD={}; acctTrades.forEach(t=>{byD[t.date]=(byD[t.date]||0)+(t.pnl||0);}); return Math.min(0,...Object.values(byD)); })();

    // Drawdown calculation
    const cap = parseFloat(pf.capital)||0;
    const target = parseFloat(pf.target)||0;
    const maxLoss = parseFloat(pf.maxLoss)||0;
    const progress = target ? Math.min(100,Math.max(0,(allPnl/target)*100)) : 0;
    const drawdown = Math.abs(Math.min(0,allPnl));
    const ddProgress = maxLoss ? Math.min(100,(drawdown/maxLoss)*100) : 0;

    // Monthly stats (calendar month)
    const pfFiltered = acctTrades.filter(t => {
      const d = new Date(t.date+"T12:00:00");
      return d.getFullYear()===pfCalYear && d.getMonth()===pfCalMonth;
    });
    const pfTotal = pfFiltered.length;
    const pfWins = pfFiltered.filter(t=>t.result==="WIN").length;
    const pfPnl = pfFiltered.reduce((s,t)=>s+(t.pnl||0),0);
    const pfWinRate = pfTotal ? Math.round(pfWins/pfTotal*100) : 0;
    const pfAvgRR = pfTotal ? (pfFiltered.reduce((s,t)=>s+(parseFloat(t.rr)||0),0)/pfTotal).toFixed(1) : "—";

    // Today
    const todayTrades = acctTrades.filter(t=>t.date===todayStr);
    const statsTrades = effectiveView==="global" ? acctTrades : todayTrades;
    const todayPnl = todayTrades.reduce((s,t)=>s+(t.pnl||0),0);

    // Session breakdown (all time)
    const sessionData = SESSIONS.map(s => {
      const st = acctTrades.filter(t=>t.session===s);
      const wr = st.length ? Math.round(st.filter(t=>t.result==="WIN").length/st.length*100) : 0;
      return { name:s.replace(" ","\n"), fullName:s, count:st.length, wr, pnl:st.reduce((a,t)=>a+(t.pnl||0),0) };
    }).filter(s=>s.count>0);

    // Emotion breakdown
    const allEmotionLabels = [...EMOTIONS, ...extraEmotions.map(e => typeof e === "string" ? e : e.label)];
    const emotionData = allEmotionLabels.map(e => {
      const et = acctTrades.filter(t=>t.emotion===e);
      const wr = et.length ? Math.round(et.filter(t=>t.result==="WIN").length/et.length*100) : 0;
      return { name:e, count:et.length, wr, pnl:et.reduce((a,t)=>a+(t.pnl||0),0) };
    }).filter(e=>e.count>0);

    // Instrument breakdown
    const instrData = (() => {
      const byI = {};
      acctTrades.forEach(t=>{ if(!byI[t.instrument]) byI[t.instrument]={count:0,wins:0,pnl:0}; byI[t.instrument].count++; if(t.result==="WIN") byI[t.instrument].wins++; byI[t.instrument].pnl+=t.pnl||0; });
      return Object.entries(byI).map(([name,v])=>({name,count:v.count,wr:Math.round(v.wins/v.count*100),pnl:v.pnl})).sort((a,b)=>b.count-a.count);
    })();

    const alerts = getPfAlerts(pf);

    const MiniCard = ({label, value, color, sub}) => {
      return (
        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,boxShadow:cShadow,padding:!isMobile?"20px 20px":"12px 14px"}}>
          <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:!isMobile?10:6}}>{label}</div>
          <div style={{fontSize:!isMobile?28:20,fontWeight:300,color:color||C.white,fontFamily:"'Josefin Sans',sans-serif",lineHeight:1}}>{value}</div>
          {sub && <div style={{fontSize:11,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",marginTop:6}}>{sub}</div>}
        </div>
      );
    };

    const SECTION_LABELS = { progress:"Progression", today:"Aujourd'hui", stats:"Statistiques", calendar:"Calendrier", trades:"Trades" };

    const dragHandlers = (id) => !acctCustomizing ? {} : {
      draggable: true,
      onDragStart: (e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", id); },
      onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setAcctDragOver(id); },
      onDragLeave: () => setAcctDragOver(null),
      onDrop: (e) => {
        e.preventDefault();
        const from = e.dataTransfer.getData("text/plain");
        if (from === id) { setAcctDragOver(null); return; }
        setAcctLayout(prev => {
          const arr = [...prev];
          const fi = arr.indexOf(from), ti = arr.indexOf(id);
          if (fi < 0 || ti < 0) return prev;
          arr.splice(fi, 1);
          arr.splice(ti, 0, from);
          return arr;
        });
        setAcctDragOver(null);
      },
    };

    const wrapSection = (id, content) => {
      if (!content) return null;
      const isOver = acctDragOver === id;
      return (
        <div key={id} {...dragHandlers(id)} style={{position:"relative", cursor:acctCustomizing?"grab":"default", transition:"opacity 0.18s", opacity:acctCustomizing?0.9:1, outline:isOver?`2px dashed ${C.gray2}`:"none", outlineOffset:4, borderRadius:12}}>
          {acctCustomizing && (
            <div style={{position:"absolute",top:8,right:8,zIndex:10,display:"flex",alignItems:"center",gap:6,pointerEvents:"none"}}>
              <div style={{fontSize:9,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.12em",textTransform:"uppercase",background:C.bg3,padding:"3px 8px",borderRadius:4,border:`1px solid ${C.border}`}}>{SECTION_LABELS[id]}</div>
              <span style={{color:C.gray1,fontSize:13,lineHeight:1}}>⠿</span>
            </div>
          )}
          {content}
        </div>
      );
    };

    const sectionProgress = pf.type==="propfirm" ? (() => {
      // Trailing DD: peak cumulative P&L at end of each day (only moves up)
      const peakPnl = (() => {
        const sortedDates = [...new Set(acctTrades.map(t => t.date))].sort();
        let cum = 0, peak = 0;
        sortedDates.forEach(date => {
          cum += acctTrades.filter(t => t.date === date).reduce((s, t) => s + (t.pnl || 0), 0);
          if (cum > peak) peak = cum;
        });
        return peak;
      })();
      const mll = pf.trailingDD ? cap + Math.max(0, peakPnl) - maxLoss : cap - maxLoss;
      const tgt = cap + target;
      const totalRange = tgt - mll;
      const currentCap = cap + allPnl;
      const startPct = totalRange ? (cap - mll) / totalRange * 100 : 50;
      const currentPct = totalRange ? Math.min(100, Math.max(0, (currentCap - mll) / totalRange * 100)) : 50;
      const isProfit = allPnl >= 0;
      const fillLeft = isProfit ? startPct : currentPct;
      const fillWidth = Math.abs(currentPct - startPct);
      return (
        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:!isMobile?"22px 20px":"14px 16px",marginBottom:!isMobile?16:12}}>

          {/* ── Gauge ── */}
          <div style={{marginBottom:20}}>
            {/* START label */}
            <div style={{position:"relative",height:18,marginBottom:4}}>
              <div style={{position:"absolute",left:startPct+"%",transform:"translateX(-50%)",fontSize:9,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.15em",textTransform:"uppercase",whiteSpace:"nowrap"}}>START</div>
              {pf.trailingDD && <div style={{position:"absolute",left:0,fontSize:9,color:C.accent,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase"}}>▲ Suiveur</div>}
            </div>

            {/* Bar */}
            <div style={{position:"relative",height:4,background:darkMode?"rgba(255,255,255,0.08)":C.gray3,borderRadius:2}}>
              {/* Fill */}
              <div style={{position:"absolute",left:fillLeft+"%",width:fillWidth+"%",height:"100%",borderRadius:2,background:isProfit?"rgba(74,222,128,0.7)":"rgba(229,100,100,0.7)",transition:"all 0.6s",boxShadow:isProfit?"0 0 8px rgba(74,222,128,0.4)":"0 0 8px rgba(229,100,100,0.4)"}}/>
              {/* START marker */}
              <div style={{position:"absolute",left:startPct+"%",transform:"translateX(-50%)",width:2,height:14,background:C.dim,borderRadius:1,top:-5}}/>
              {/* Current marker */}
              <div style={{position:"absolute",left:currentPct+"%",transform:"translateX(-50%)",width:3,height:12,background:isProfit?clrGreen:"#e05a5a",borderRadius:2,top:-4,transition:"left 0.6s",boxShadow:isProfit?"0 0 6px rgba(74,222,128,0.8)":"0 0 6px rgba(229,100,100,0.8)"}}/>
            </div>

            {/* Labels */}
            <div style={{display:"flex",justifyContent:"space-between",marginTop:10,alignItems:"flex-start"}}>
              <div>
                <div style={{fontSize:!isMobile?18:15,fontWeight:300,color:"rgba(229,100,100,0.85)",fontFamily:"'Josefin Sans',sans-serif",lineHeight:1}}>{fm(mll)}{currency}</div>
                <div style={{fontSize:9,color:"rgba(229,100,100,0.5)",fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.12em",textTransform:"uppercase",marginTop:4}}>MLL</div>
              </div>
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:!isMobile?15:13,fontWeight:300,color:isProfit?clrGreen:"#e05a5a",fontFamily:"'Josefin Sans',sans-serif",lineHeight:1}}>{fms(allPnl)}{currency}</div>
                <div style={{fontSize:9,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.12em",textTransform:"uppercase",marginTop:4}}>P&L</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:!isMobile?18:15,fontWeight:300,color:"rgba(74,222,128,0.85)",fontFamily:"'Josefin Sans',sans-serif",lineHeight:1}}>{fm(tgt)}{currency}</div>
                <div style={{fontSize:9,color:"rgba(74,222,128,0.5)",fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.12em",textTransform:"uppercase",marginTop:4}}>TARGET</div>
              </div>
            </div>
          </div>

          {/* ── Daily Loss bar ── */}
          {pf.hasDailyLoss && parseFloat(pf.dailyLoss)>0 && (()=>{
            const dl=parseFloat(pf.dailyLoss);
            const todayLossDL=Math.abs(Math.min(0,todayPnl));
            const dlPct=Math.min(100,(todayLossDL/dl)*100);
            const over=todayLossDL>=dl;
            return (
              <div style={{borderTop:`1px solid ${C.border}`,paddingTop:14}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <span style={{fontSize:10,color:over?"rgba(192,57,43,0.9)":C.dim,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase"}}>Daily Loss</span>
                  <span style={{fontSize:10,color:over?"rgba(192,57,43,0.9)":C.dim,fontFamily:"'Josefin Sans',sans-serif"}}>{fm(todayLossDL)}{currency} / {dl}{currency}{over?" ●":""}</span>
                </div>
                <div style={{height:4,background:C.gray3,borderRadius:2}}>
                  <div style={{width:dlPct+"%",height:"100%",borderRadius:2,background:over?"rgba(192,57,43,0.9)":dlPct>=70?"rgba(192,57,43,0.5)":"rgba(192,57,43,0.3)",transition:"width 0.6s"}}/>
                </div>
              </div>
            );
          })()}

        </div>
      );
    })() : null;

    const sectionToday = (
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:!isMobile?"22px 20px":"14px 16px",marginBottom:!isMobile?16:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:pf.hasConsistency&&pf.consistencyPct&&pf.target?10:0}}>
          <div>
            <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:4}}>Aujourd'hui</div>
            <div style={{fontSize:22,color:todayPnl>=0?"#2a6e3a":"#c0392b",fontFamily:"'Josefin Sans',sans-serif",fontWeight:300}}>{fms(todayPnl)}{currency}</div>
            <div style={{fontSize:10,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",marginTop:2}}>{todayTrades.length} trade{todayTrades.length!==1?"s":""}</div>
          </div>
          {pf.hasConsistency&&pf.consistencyPct&&pf.target&&(()=>{
            const maxD=parseFloat(pf.target)*parseFloat(pf.consistencyPct)/100;
            const g=Math.max(0,todayPnl);
            const gp=Math.min(100,maxD>0?(g/maxD)*100:0);
            const over=g>=maxD;
            return (
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:"'Josefin Sans',sans-serif",marginBottom:4}}>Consistance · max {fm(maxD)}{currency}</div>
                <div style={{width:120,height:6,background:C.gray3,borderRadius:3,marginLeft:"auto",marginBottom:4}}>
                  <div style={{width:gp+"%",height:"100%",borderRadius:3,background:over?"rgba(192,57,43,0.8)":gp>=80?"rgba(var(--gold-rgb),0.6)":"#2a6e3a",transition:"width 0.5s"}}/>
                </div>
                <div style={{fontSize:10,color:over?"rgba(192,57,43,0.8)":C.gray1,fontFamily:"'Josefin Sans',sans-serif"}}>{over?"🔴 Limite atteinte":`${fm(maxD-g)}${currency} restants`}</div>
              </div>
            );
          })()}
        </div>
      </div>
    );

    const sectionStats = (
      <div>
        {(() => {
          const todayWins=statsTrades.filter(t=>t.result==="WIN").length;
          const todayLosses=statsTrades.filter(t=>t.result==="LOSS").length;
          const todayTotal=statsTrades.length;
          const todayWR=todayTotal?Math.round(todayWins/todayTotal*100):0;
          const todayAvgW=todayWins?statsTrades.filter(t=>t.result==="WIN").reduce((s,t)=>s+(t.pnl||0),0)/todayWins:0;
          const todayAvgL=todayLosses?Math.abs(statsTrades.filter(t=>t.result==="LOSS").reduce((s,t)=>s+(t.pnl||0),0)/todayLosses):0;
          const todayPF=todayAvgL>0?(todayAvgW*todayWins/(todayAvgL*todayLosses)).toFixed(2):todayWins>0?"∞":"—";
          const todayRRt=statsTrades.filter(t=>parseFloat(t.rr)>0);
          const todayRR=todayRRt.length?(todayRRt.reduce((s,t)=>s+parseFloat(t.rr),0)/todayRRt.length).toFixed(1):"—";
          const todayGrossW=statsTrades.filter(t=>t.result==="WIN").reduce((s,t)=>s+(t.pnl||0),0);
          const todayGrossL=Math.abs(statsTrades.filter(t=>t.result==="LOSS").reduce((s,t)=>s+(t.pnl||0),0));
          return (
            <div style={{marginBottom:12}}>
              <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:8}}>{L.nav.stats} · {effectiveView==="global"?L.acct.global:L.acct.today}</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
                <MiniCard label="Profit Factor" value={todayPF} color={parseFloat(todayPF)>=1||todayPF==="∞"?"#2a6e3a":"#c0392b"} li={0}/>
                <MiniCard label={L.stats.avgRR} value={todayRR==="—"?"—":todayRR+":1"} color={C.dim} li={1}/>
                <MiniCard label={lang==="en"?"Nb Trades":"Nb Trades"} value={todayTotal||"—"} color={C.white} li={2}/>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                <MiniCard label={lang==="en"?"Gross gains":"Gains bruts"} value={todayGrossW>0?`${fms(todayGrossW)}${currency}`:"—"} color="#2a6e3a"/>
                <MiniCard label={lang==="en"?"Gross losses":"Pertes brutes"} value={todayGrossL>0?`-${fm(todayGrossL)}${currency}`:"—"} color="#c0392b"/>
              </div>
            </div>
          );
        })()}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:14,boxShadow:cShadow,padding:!isMobile?"30px 24px":"14px",display:"flex",flexDirection:"column",alignItems:"center"}}>
            <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:4,alignSelf:"flex-start"}}>{lang==="en"?"Win Rate":"Winrate"} · {effectiveView==="global"?L.acct.global:L.acct.today}</div>
            {(() => { const tw=statsTrades.filter(t=>t.result==="WIN").length; const tl=statsTrades.filter(t=>t.result==="LOSS").length; const tt=statsTrades.length; return tt>0 ? (((wins, losses, total, size=130) => {
              const r=46, cx=size/2, cy=size*0.52, sw=13, PI=Math.PI;
              const wFrac=total>0?wins/total:0;
              const lFrac=total>0?losses/total:0;
              const wr=total?Math.round(wins/total*100):0;
              const LEFT={x:cx-r,y:cy}, RIGHT={x:cx+r,y:cy};
              const angleToXY=(deg)=>({x:cx+r*Math.cos(deg*PI/180), y:cy-r*Math.sin(deg*PI/180)});
              const bgArc=`M${LEFT.x},${LEFT.y} A${r},${r} 0 0 1 ${RIGHT.x},${RIGHT.y}`;
              const wDeg=180-wFrac*180;
              const wPt=angleToXY(wDeg);
              const winArc=wFrac>0.01?`M${LEFT.x},${LEFT.y} A${r},${r} 0 ${wFrac>=1?1:0} 1 ${wPt.x.toFixed(1)},${wPt.y.toFixed(1)}`:"";
              const lDeg=lFrac*180;
              const lPt=angleToXY(lDeg);
              const lossArc=lFrac>0.01?`M${RIGHT.x},${RIGHT.y} A${r},${r} 0 ${lFrac>=1?1:0} 0 ${lPt.x.toFixed(1)},${lPt.y.toFixed(1)}`:"";
              const labY=cy+r+18;
              const svgH=cy+r+34;
              return (
                <svg width={size} height={svgH} viewBox={`0 0 ${size} ${svgH}`}>
                  <path d={bgArc} stroke={C.gray3} strokeWidth={sw} fill="none" strokeLinecap="round"/>
                  {wFrac>0.01&&<path d={winArc} stroke="#2a6e3a" strokeWidth={sw} fill="none" strokeLinecap="round"/>}
                  {lFrac>0.01&&<path d={lossArc} stroke="#c0392b" strokeWidth={sw} fill="none" strokeLinecap="round"/>}
                  <text x={cx} y={cy-2} textAnchor="middle" fontSize={20} fontWeight={300} fill={wr>=50?"#2a6e3a":"#c0392b"} fontFamily="'Josefin Sans',sans-serif">{wr}%</text>
                  <text x={cx} y={cy+13} textAnchor="middle" fontSize={7} fill={C.dim} fontFamily="'Josefin Sans',sans-serif" letterSpacing="1.5">WIN RATE</text>
                  <text x={8} y={labY} textAnchor="start" fontSize={10} fontWeight="600" fill="#2a6e3a" fontFamily="'Josefin Sans',sans-serif">{wins}W</text>
                  <text x={cx} y={labY} textAnchor="middle" fontSize={10} fill={C.gray2} fontFamily="'Josefin Sans',sans-serif">{total-wins-losses}BE</text>
                  <text x={size-8} y={labY} textAnchor="end" fontSize={10} fontWeight="600" fill="#c0392b" fontFamily="'Josefin Sans',sans-serif">{losses}L</text>
                </svg>
              );
            })(tw,tl,tt,140)) : <div style={{padding:"20px 0",color:C.gray2,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",textAlign:"center"}}>{effectiveView==="today"?L.acct.noTradesToday:L.btn.noTradesYet}</div>; })()}
          </div>
          {(()=>{
            const srcTrades = effectiveView==="global"?acctTrades:todayTrades;
            const total = srcTrades.length;
            const isDark = C.bg === "#0f0f0f";
            const barColor = isDark ? "#e8e8e8" : "#1a1a1a";
            const gridColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)";
            const dirs = ["LONG","SHORT"].map(d=>{
              const dt=srcTrades.filter(t=>t.direction===d);
              const dw=dt.filter(t=>t.result==="WIN").length;
              const dpnl=dt.reduce((s,t)=>s+(t.pnl||0),0);
              const dwr=dt.length?Math.round(dw/dt.length*100):0;
              const usagePct=total?Math.round(dt.length/total*100):0;
              return {d,count:dt.length,dpnl,dwr,usagePct};
            });
            const chartData = dirs.map(x=>({d:x.d, pct:x.usagePct}));
            const CustomTooltip = ({active,payload})=>{
              if(!active||!payload?.length) return null;
              const label = payload[0].payload.d;
              const item = dirs.find(x=>x.d===label);
              if(!item) return null;
              return (
                <div style={{background:isDark?"rgba(18,18,18,0.95)":"rgba(255,255,255,0.97)",border:`1px solid ${isDark?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"}`,borderRadius:8,padding:"10px 14px",boxShadow:"0 8px 24px rgba(0,0,0,0.2)",fontFamily:"'Josefin Sans',sans-serif"}}>
                  <div style={{fontSize:11,color:C.white,fontWeight:700,marginBottom:4,letterSpacing:"0.1em"}}>{label}</div>
                  <div style={{fontSize:10,color:C.gray1,marginBottom:2}}>{item.count} trade{item.count!==1?"s":""} · {item.usagePct}%</div>
                  <div style={{fontSize:10,color:C.gray1,marginBottom:2}}>WR {item.dwr}%</div>
                  <div style={{fontSize:10,color:item.dpnl>=0?"#4caf6e":"#e05a5a",fontWeight:600}}>{fms(item.dpnl)}{currency}</div>
                </div>
              );
            };
            return (
              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:"16px 16px 12px"}}>
                <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:2}}>{L.form.direction} · {effectiveView==="global"?L.acct.global:L.acct.today}</div>
                <div style={{fontSize:9,color:C.gray2,fontFamily:"'Josefin Sans',sans-serif",marginBottom:10}}>{total} trade{total!==1?"s":""} au total</div>
                <ResponsiveContainer width="100%" height={130}>
                  <BarChart data={chartData} margin={{top:22,right:12,left:12,bottom:0}} barCategoryGap="30%">
                    <CartesianGrid vertical={false} stroke={gridColor} strokeDasharray="4 4"/>
                    <XAxis dataKey="d" axisLine={false} tickLine={false} tick={{fontSize:10,fill:C.gray1,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.08em"}} dy={6}/>
                    <Tooltip content={<CustomTooltip/>} cursor={{fill:isDark?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.04)",radius:6}}/>
                    <Bar dataKey="pct" fill={barColor} radius={[6,6,2,2]} maxBarSize={60}>
                      <LabelList dataKey="pct" position="top" offset={8} formatter={v=>v+"%"} style={{fontSize:11,fill:C.white,fontFamily:"'Josefin Sans',sans-serif",fontWeight:700}}/>
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div style={{display:"flex",gap:8,marginTop:8}}>
                  {dirs.map(x=>(
                    <div key={x.d} style={{flex:1,background:isDark?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.04)",borderRadius:8,padding:"8px 10px"}}>
                      <div style={{fontSize:9,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:3}}>{x.d}</div>
                      <div style={{fontSize:12,color:C.white,fontFamily:"'Josefin Sans',sans-serif",fontWeight:700}}>{x.count}T · {x.dwr}%<span style={{fontWeight:400,color:C.gray1}}> WR</span></div>
                      <div style={{fontSize:10,color:x.dpnl>=0?"#4caf6e":"#e05a5a",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginTop:1}}>{fms(x.dpnl)}{currency}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
        {(() => { const todayInstr = (() => { const byI={}; (effectiveView==="global"?acctTrades:todayTrades).forEach(t=>{ if(!byI[t.instrument]) byI[t.instrument]={count:0,wins:0,pnl:0}; byI[t.instrument].count++; if(t.result==="WIN") byI[t.instrument].wins++; byI[t.instrument].pnl+=t.pnl||0; }); return Object.entries(byI).map(([name,v])=>({name,count:v.count,wr:Math.round(v.wins/v.count*100),pnl:v.pnl})); })(); return todayInstr.length>0 && (
          <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:!isMobile?"22px 20px":"14px 16px",marginBottom:!isMobile?16:12}}>
            <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:12}}>{L.form.instrument}s · {effectiveView==="global"?L.acct.global:L.acct.today}</div>
            {todayInstr.map(i=>(
              <div key={i.name} style={{marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <span style={{fontSize:11,color:C.white,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>{i.name}</span>
                  <span style={{fontSize:11,color:i.pnl>=0?"#2a6e3a":"#c0392b",fontFamily:"'Josefin Sans',sans-serif"}}>{fms(i.pnl)}{currency} · {i.wr}% · {i.count}T</span>
                </div>
                <div style={{height:4,background:C.gray3,borderRadius:2}}>
                  <div style={{width:i.wr+"%",height:"100%",borderRadius:2,background:i.pnl>=0?"#2a6e3a":"#c0392b",transition:"width 0.5s"}}/>
                </div>
              </div>
            ))}
          </div>
        );
        })()}
        {(() => {
          const allEmotions = [...EMOTIONS, ...extraEmotions.map(e => typeof e === "string" ? e : e.label)];
          const todayEmotions = allEmotions.map(e => {
            const et = (effectiveView==="global"?acctTrades:todayTrades).filter(t=>t.emotion===e);
            const wr = et.length ? Math.round(et.filter(t=>t.result==="WIN").length/et.length*100) : 0;
            return { name:e, count:et.length, wr, pnl:et.reduce((a,t)=>a+(t.pnl||0),0) };
          }).filter(e=>e.count>0);
          return todayEmotions.length>0 ? (
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:!isMobile?"22px 20px":"14px 16px",marginBottom:!isMobile?16:12}}>
              <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:12}}>{L.acct.emotions} · {effectiveView==="global"?L.acct.global:L.acct.today}</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {todayEmotions.map(e=>(
                  <div key={e.name} style={{background:C.bg3,borderRadius:6,padding:"10px 12px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                      <span style={{fontSize:11,color:C.white,fontFamily:"'Josefin Sans',sans-serif"}}>{L.emotions[e.name]||e.name}</span>
                      <span style={{fontSize:10,color:e.wr>=50?"#2a6e3a":"#c0392b",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>{e.wr}%</span>
                    </div>
                    <div style={{fontSize:10,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif"}}>{e.count}T · {fms(e.pnl)}{currency}</div>
                    <div style={{height:3,background:C.gray2,borderRadius:2,marginTop:6}}>
                      <div style={{width:e.wr+"%",height:"100%",borderRadius:2,background:e.wr>=50?"#2a6e3a":"#c0392b"}}/>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null;
        })()}
      </div>
    );

    const sectionEquity = (
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:!isMobile?"24px 20px 14px":"16px 14px 10px"}}>
        <div style={{fontSize:9,color:C.dim,letterSpacing:"0.2em",textTransform:"uppercase",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:10}}>Courbe d'équité</div>
        {(effectiveView==="global"?acctTrades:todayTrades).length>1 ? <PnlChart filtered={effectiveView==="global"?acctTrades:todayTrades} capital={pf.capital} pnlSum={effectiveView==="global"?allPnl:todayTrades.reduce((s,t)=>s+(t.pnl||0),0)} height={!isMobile?260:160} cur={currency}/>
        : <div style={{textAlign:"center",padding:"32px 0",color:C.gray2,fontSize:11,fontFamily:"'Josefin Sans',sans-serif"}}>Aucun trade</div>}
      </div>
    );

    const sectionSessions = (() => {
      const todaySessions = SESSIONS.map(s=>{const st=(effectiveView==="global"?acctTrades:todayTrades).filter(t=>t.session===s);const wr=st.length?Math.round(st.filter(t=>t.result==="WIN").length/st.length*100):0;return{name:s,count:st.length,wr,pnl:st.reduce((a,t)=>a+(t.pnl||0),0)};}).filter(s=>s.count>0);
      if (!todaySessions.length) return null;
      return (
        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:!isMobile?"22px 20px":"14px 16px"}}>
          <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:12}}>{L.form.session}s · {effectiveView==="global"?L.acct.global:L.acct.today}</div>
          {todaySessions.map(s=>(
            <div key={s.name} style={{marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                <span style={{fontSize:11,color:C.white,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.06em",textTransform:"uppercase"}}>{s.name}</span>
                <span style={{fontSize:11,color:s.pnl>=0?"#2a6e3a":"#c0392b",fontFamily:"'Josefin Sans',sans-serif"}}>{fms(s.pnl)}{currency} · {s.wr}% · {s.count}T</span>
              </div>
              <div style={{height:5,background:C.gray3,borderRadius:3}}>
                <div style={{width:s.wr+"%",height:"100%",borderRadius:3,background:s.pnl>=0?"#2a6e3a":"#c0392b",transition:"width 0.5s"}}/>
              </div>
            </div>
          ))}
        </div>
      );
    })();

    const sectionCalendar = (
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:"16px 14px",marginBottom:12}}>
        <Calendar filtered={acctTrades} calMonth={pfCalMonth} calYear={pfCalYear} onPrev={prevPfMonth} onNext={nextPfMonth} cur={currency} dayEndTime={dayEndTime} fms={fms} hideNumbers={hideNumbers} onDayClick={({day,month,year})=>{
            const dateStr=`${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
            const dayTrades=acctTrades.filter(t=>t.date===dateStr);
            const dayPnl=dayTrades.reduce((s,t)=>s+(t.pnl||0),0);
            setSelectedDay({date:dateStr,trades:dayTrades,pnl:dayPnl});
        }}/>
      </div>
    );

    const sectionTrades = (
      <div style={{marginBottom:12}}>
        <div style={{fontSize:9,color:C.dim,letterSpacing:"0.2em",textTransform:"uppercase",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:10}}>Trades · {effectiveView==="global"?L.acct.global:L.acct.today}</div>
        {(effectiveView==="global"?acctTrades:todayTrades).length===0?<div style={{padding:"12px 0",color:C.gray2,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",textAlign:"center"}}>{L.acct.noTradesToday}</div>:[...(effectiveView==="global"?acctTrades:todayTrades)].sort((a,b)=>b.date.localeCompare(a.date)).map(t=>{
          const pnl=t.pnl||0;
          return (
            <div key={t.id} onClick={() => setSelectedTradeDetail(t)} style={{background:C.bg2,border:`1px solid ${C.border}`,borderLeft:`3px solid ${t.result==="WIN"?"#2a6e3a":t.result==="LOSS"?"#c0392b":C.gray3}`,borderRadius:12,boxShadow:cShadow,padding:"10px 14px",marginBottom:6,display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}}>
              <div>
                <span style={{fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,fontSize:13,color:C.white}}>{t.instrument}</span>
                <span style={{marginLeft:6,fontSize:9,color:t.direction==="LONG"?"#2a6e3a":"#c0392b",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.06em"}}>{t.direction}</span>
                <div style={{fontSize:9,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",marginTop:2}}>
                  {t.date}{(t.entryTime||t.exitTime) ? ` · ${[t.entryTime&&`IN ${utcToLocal(t.entryTime)}`,t.exitTime&&`OUT ${utcToLocal(t.exitTime)}`].filter(Boolean).join(" → ")}` : ""} · {t.session} · {t.emotion?(L.emotions[t.emotion]||t.emotion):""}
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:15,fontWeight:300,color:pnl>=0?"#2a6e3a":"#c0392b",fontFamily:"'Josefin Sans',sans-serif"}}>{fms(pnl)}{currency}</div>
                {t.rr && <div style={{fontSize:9,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif"}}>RR {t.rr}</div>}
              </div>
            </div>
          );
        })}
      </div>
    );

    const sectionMap = { progress:sectionProgress, today:sectionToday, stats:sectionStats, calendar:sectionCalendar, trades:sectionTrades };
    const layoutOrder = acctLayout.filter(id => id !== 'progress' || pf.type === "propfirm");

    return (
      <div style={{animation:`${accountLeaving?"slideOutAccount":"slideInAccount"} 0.28s cubic-bezier(.4,0,.2,1)`}}>
        {/* ── HEADER ── */}
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
          <button onClick={closeAccount} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 12px",color:C.gray1,cursor:"pointer",fontSize:11,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase"}}>← Retour</button>
          <div>
            <div style={{fontFamily:"'Barlow',sans-serif",fontWeight:600,fontSize:18,color:C.white,letterSpacing:"0.1em"}}>{pf.firm}</div>
            {pf.name && <div style={{fontSize:11,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif"}}>{pf.name}</div>}
          </div>
          <div style={{marginLeft:"auto",display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <button onClick={()=>setAcctCustomizing(v=>!v)} style={{background:acctCustomizing?(darkMode?"rgba(255,255,255,0.12)":"rgba(0,0,0,0.08)"):"none",border:`1px solid ${acctCustomizing?C.gray1:C.border}`,borderRadius:6,padding:"5px 10px",color:acctCustomizing?C.white:C.gray2,cursor:"pointer",fontSize:9,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.12em",textTransform:"uppercase",transition:"all 0.18s"}}>
                {acctCustomizing?L.btn.done:L.btn.customize}
              </button>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:10,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",textTransform:"uppercase",letterSpacing:"0.1em"}}>{pf.type==="propfirm"?"Prop Firm":(lang==="fr"?"Fond Propre":"Personal Account")} · {cap.toLocaleString()}€</div>
              <div style={{fontSize:20,color:allPnl>=0?"#2a6e3a":"#c0392b",fontFamily:"'Josefin Sans',sans-serif",fontWeight:300}}>{fms(allPnl)}{currency} <span style={{fontSize:12,color:cap>0?(allPnl/cap*100>=0?"#2a6e3a":"#c0392b"):C.dim}}>{cap>0?`(${(allPnl/cap*100).toFixed(1)}%)`:"" }</span></div>
            </div>
          </div>
        </div>

        {/* ── GLOBAL / TODAY TOGGLE ── */}
        {!isArchived && <div style={{display:"flex",gap:6,marginBottom:16,background:darkMode?"linear-gradient(180deg,rgba(85,85,85,0.98) 0%,rgba(28,28,28,0.99) 100%)":"linear-gradient(180deg,rgba(60,60,60,0.95) 0%,rgba(18,18,18,0.97) 100%)",borderRadius:14,padding:4,border:darkMode?"1px solid rgba(255,255,255,0.18)":"1px solid rgba(255,255,255,0.14)",boxShadow:darkMode?"0 12px 40px rgba(0,0,0,0.75), 0 4px 10px rgba(0,0,0,0.5), 0 0 50px rgba(255,255,255,0.1), 0 0 0 1px rgba(255,255,255,0.12), inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -2px 0 rgba(0,0,0,0.75)":"0 8px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.55)"}}>
          {[{k:"today",l:L.acct.today},{k:"global",l:L.acct.global}].map(opt=>(
            <button key={opt.k} onClick={()=>setAcctView(opt.k)} style={{flex:1,padding:"9px",borderRadius:10,border:"none",background:acctView===opt.k?"radial-gradient(ellipse 90% 90% at 50% 50%, rgba(252,252,252,0.96) 0%, rgba(218,218,218,0.88) 55%, rgba(235,235,235,0.92) 100%)":"transparent",color:acctView===opt.k?"#111":"rgba(255,255,255,0.55)",fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:acctView===opt.k?600:300,letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer",transition:"all 0.22s cubic-bezier(.4,0,.2,1)",boxShadow:acctView===opt.k?"0 6px 20px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.12)":"none",transform:"translateY(0)"}}>{opt.l}</button>
          ))}
        </div>}

        {/* ── ALERTS ── */}
        {alerts.map((a,i)=>(
          <div key={i} style={{padding:"8px 12px",borderRadius:6,marginBottom:8,background:a.type==="danger"?"rgba(192,57,43,0.08)":a.type==="warn"?"rgba(var(--gold-rgb),0.08)":a.type==="success"?"rgba(42,110,58,0.08)":"rgba(0,0,0,0.04)",border:`1px solid ${a.type==="danger"?"rgba(192,57,43,0.25)":a.type==="warn"?"rgba(var(--gold-rgb),0.25)":a.type==="success"?"rgba(42,110,58,0.25)":C.border}`}}>
            <div style={{fontSize:12,color:C.white,fontFamily:"'Josefin Sans',sans-serif",lineHeight:1.5}}>{a.msg}</div>
          </div>
        ))}

        {/* ── MAGAZINE LAYOUT — 2 colonnes ── */}
        {acctCustomizing && (
          <div style={{marginBottom:12,padding:"10px 14px",background:darkMode?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.04)",borderRadius:8,border:`1px dashed ${C.gray2}`,textAlign:"center",fontSize:10,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em"}}>
            Glisse les sections pour les réorganiser
          </div>
        )}
        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"58% 42%",gap:0,alignItems:"start"}}>

          {/* ── COLONNE GAUCHE ── */}
          <div style={{display:"flex",flexDirection:"column",gap:10,paddingRight:isMobile?0:20,borderRight:isMobile?"none":`1px solid ${C.border}`}}>
            {/* P&L au-dessus de la jauge */}
            {(() => {
              const hc = allPnl>=0?clrGreen:clrRed;
              return (
                <div style={{padding:"4px 4px 0"}}>
                  <div style={{fontSize:9,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.2em",textTransform:"uppercase"}}>P&L Global</div>
                  <div style={{fontSize:isMobile?28:36,fontFamily:"'Josefin Sans',sans-serif",fontWeight:800,color:hc,lineHeight:1,letterSpacing:"-0.03em",textShadow:`0 0 40px ${hc}45,0 0 14px ${hc}25`,marginTop:4}}>
                    {fms(allPnl)}{currency}
                  </div>
                  {cap>0 && <div style={{fontSize:10,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",marginTop:4}}>{(allPnl/cap*100).toFixed(1)}% du capital · {allTotal} trades</div>}
                </div>
              );
            })()}
            {wrapSection('progress', sectionProgress)}
            {sectionEquity}
            {wrapSection('calendar', sectionCalendar)}
            {sectionSessions}
          </div>

          {/* ── COLONNE DROITE ── */}
          <div style={{display:"flex",flexDirection:"column",gap:10,paddingLeft:isMobile?0:20}}>
            {wrapSection('today', sectionToday)}
            {wrapSection('stats', sectionStats)}
            {wrapSection('trades', sectionTrades)}
          </div>

        </div>

        {/* ── SYNC MT5 ── */}
        {pf.metaapi_id && pf.status !== "passed" && pf.status !== "breached" && (
          <div style={{marginBottom:8}}>
            {mt5SyncingPf===pf.id ? (() => {
              const step = mt5SyncStep[pf.id] || 1;
              const steps = ["Connexion MT5","Récupération","Import"];
              const neon = darkMode ? "#E8CDA9" : "#111";
              const glow = darkMode
                ? "0 0 6px #E8CDA9, 0 0 18px rgba(232,205,169,0.55), 0 0 36px rgba(232,205,169,0.25)"
                : "0 0 5px rgba(0,0,0,0.7), 0 0 12px rgba(0,0,0,0.45)";
              return (
                <div style={{padding:"14px 2px 6px"}}>
                  <style>{`
                    @keyframes mt5-shimmer { 0%{left:-45%} 100%{left:110%} }
                    @keyframes mt5-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.35;transform:scale(0.7)} }
                  `}</style>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:10,alignItems:"center"}}>
                    {steps.map((s,i) => {
                      const thresholds = [0, 45, 85];
                      const p = step >= 3 ? 100 : mt5SyncProgress;
                      const active = p >= thresholds[i] && (i === steps.length-1 ? p < 100 : p < thresholds[i+1]);
                      const done   = p >= (thresholds[i+1] ?? 100);
                      return (
                        <div key={i} style={{display:"flex",alignItems:"center",gap:4}}>
                          <div style={{width:5,height:5,borderRadius:"50%",flexShrink:0,background:done||active?neon:C.gray2,boxShadow:active?glow:"none",animation:active?"mt5-pulse 1.1s ease-in-out infinite":"none",transition:"all 0.4s"}}/>
                          <span style={{fontSize:9,fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",color:done||active?neon:C.gray2,textShadow:active?(darkMode?"0 0 8px rgba(232,205,169,0.7)":"0 0 6px rgba(0,0,0,0.4)"):"none",transition:"all 0.4s"}}>{s}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{height:2,background:C.bg3,borderRadius:99,overflow:"hidden",position:"relative"}}>
                    <div style={{position:"absolute",left:0,top:0,height:"100%",width:`${step >= 3 ? 100 : mt5SyncProgress}%`,background:neon,boxShadow:glow,borderRadius:99,transition:"width 0.4s ease-out",overflow:"hidden"}}>
                      <div style={{position:"absolute",top:0,height:"100%",width:"45%",background:`linear-gradient(90deg,transparent,${darkMode?"rgba(255,255,255,0.5)":"rgba(255,255,255,0.7)"},transparent)`,animation:"mt5-shimmer 1.3s ease-in-out infinite",borderRadius:99}}/>
                    </div>
                  </div>
                </div>
              );
            })() : (()=>{
              const runSync=async()=>{
                setMt5SyncingPf(pf.id); setMt5DeployingPf(null); setMt5SyncMsg(m=>({...m,[pf.id]:""}));
                setMt5SyncStep(s=>({...s,[pf.id]:1}));
                const MAX=12;
                for(let i=0;i<MAX;i++){
                  try{
                    const startDate=pf.lastMt5Sync?new Date(new Date(pf.lastMt5Sync).getTime()-2*24*60*60*1000).toISOString():undefined;
                    const res=await fetch("/api/sync-trades",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({accountId:pf.metaapi_id,startDate,brokerOffset:pf.brokerOffset??0})});
                    const data=await res.json();
                    if(data.status==="deploying"){
                      setMt5DeployingPf(pf.id);
                      await new Promise(r=>setTimeout(r,15000));
                      continue;
                    }
                    setMt5DeployingPf(null);
                    setMt5SyncStep(s=>({...s,[pf.id]:2}));
                    if(!res.ok){setMt5SyncMsg(m=>({...m,[pf.id]:`✗ ${data.error||"Erreur sync"}`}));break;}
                    const ticketMap={};
                    trades.filter(t=>t.accountIds?.includes(pf.id)).forEach(t=>{const tk=t.notes?.match(/ticket #(\w+)/)?.[1];if(tk)ticketMap[tk]=t.id;});
                    const newTrades=[];
                    const pnlUpdates={};
                    for(const t of (data.trades||[])){
                      const tk=t.notes?.match(/ticket #(\w+)/)?.[1];
                      if(tk&&ticketMap[tk]){pnlUpdates[ticketMap[tk]]={pnl:t.pnl,result:t.result,notes:t.notes};}
                      else{newTrades.push({...t,id:Date.now()+Math.random(),accountIds:[pf.id],createdAt:new Date().toISOString()});}
                    }
                    if(Object.keys(pnlUpdates).length>0){
                      setTrades(prev=>prev.map(t=>pnlUpdates[t.id]?{...t,...pnlUpdates[t.id]}:t));
                    }
                    setMt5SyncStep(s=>({...s,[pf.id]:3}));
                    if(newTrades.length>0){
                      setPendingMt5Trades(newTrades);
                      setMt5Enrichments(Object.fromEntries(newTrades.map((t,i)=>[i,{
                        size: t.size!=null ? String(t.size) : "",
                        sizeUnit: t.sizeUnit || "lots",
                      }])));
                    }
                    // Auto-sync capital so that capital + totalPnl = mt5Balance
                    if (data.mt5Balance != null) {
                      const existingPnl = trades.filter(t=>t.accountIds?.includes(pf.id)).reduce((s,t)=>s+(pnlUpdates[t.id]?pnlUpdates[t.id].pnl:(t.pnl||0)),0);
                      const newPnl = newTrades.reduce((s,t)=>s+(t.pnl||0),0);
                      const newCapital = Math.round((data.mt5Balance - existingPnl - newPnl)*100)/100;
                      setPropfirms(prev=>prev.map(p=>p.id===pf.id?{...p,lastMt5Sync:new Date().toISOString(),capital:String(newCapital)}:p));
                    } else {
                      setPropfirms(prev=>prev.map(p=>p.id===pf.id?{...p,lastMt5Sync:new Date().toISOString()}:p));
                    }
                    // Record global daily sync limit
                    const syncNow = new Date().toISOString();
                    setLastMt5SyncGlobal(syncNow);
                    try { localStorage.setItem(`fyltra_mt5_sync_${user?.id}`, syncNow); } catch {}
                    saveUserSettings({ last_mt5_sync: syncNow });
                    const updatedCount=Object.keys(pnlUpdates).length;
                    setMt5SyncMsg(m=>({...m,[pf.id]:newTrades.length>0?`${newTrades.length} trade${newTrades.length!==1?"s":""} à enrichir${updatedCount>0?` · ${updatedCount} mis à jour`:""}`:updatedCount>0?`${updatedCount} trade${updatedCount!==1?"s":""} mis à jour`:`Aucun nouveau trade`}));
                    break;
                  }catch(e){setMt5DeployingPf(null);setMt5SyncMsg(m=>({...m,[pf.id]:`✗ ${e.message}`}));break;}
                }
                setMt5SyncingPf(null); setMt5DeployingPf(null); setMt5SyncStep(s=>({...s,[pf.id]:0}));
                setMt5SyncMsg(m=>{if(!m[pf.id])return{...m,[pf.id]:"✗ Timeout — réessaie dans quelques minutes."};return m;});
              };
              const btnBlocked = syncBlockedToday;
              return (
                <button onClick={btnBlocked?undefined:runSync} disabled={!canUseMT5||btnBlocked} style={{width:"100%",padding:"13px",borderRadius:8,border:`1px solid ${C.border}`,background:"transparent",color:(canUseMT5&&!btnBlocked)?C.dim:C.gray2,fontSize:12,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.15em",textTransform:"uppercase",cursor:(canUseMT5&&!btnBlocked)?"pointer":"not-allowed",transition:"all 0.3s",opacity:(canUseMT5&&!btnBlocked)?1:0.5}}>
                  {btnBlocked ? "Sync used" : "Synchroniser depuis MT5"}
                </button>
              );
            })()}
            {!canUseMT5 && <div style={{marginTop:4,fontSize:10,color:C.gray2,fontFamily:"'Josefin Sans',sans-serif",textAlign:"center"}}>
              {lang==="fr"?"Disponible avec Fyltra Pro.":"Available with Fyltra Pro."}{" "}
              <a href="/#tarifs" style={{color:"rgba(232,205,169,0.6)",textDecoration:"none"}}>{lang==="fr"?"Voir →":"See →"}</a>
            </div>}
            {syncBlockedToday && mt5Countdown && <div style={{marginTop:4,fontSize:10,color:C.gray2,fontFamily:"'Josefin Sans',sans-serif",textAlign:"center",letterSpacing:"0.04em"}}>
              Resets in {mt5Countdown}
            </div>}
            {canUseMT5 && !syncBlockedToday && mt5SyncMsg[pf.id] && <div style={{marginTop:6,fontSize:11,color:mt5SyncMsg[pf.id].startsWith("✗")?"#e05a5a":C.dim,fontFamily:"'Josefin Sans',sans-serif",textAlign:"center"}}>{mt5SyncMsg[pf.id]}</div>}
          </div>
        )}

        {/* ── EOD + ACTIONS ── */}
        {pf.status !== "passed" && pf.status !== "breached" && <>
          <button onClick={()=>{ if(!canUseAI){ return; } setEodText("");runEOD(pf); }} disabled={eodLoading} style={{width:"100%",padding:"13px",borderRadius:8,border:`1px solid ${canUseAI?C.borderGold:C.border}`,background:eodLoading?"transparent":"rgba(0,0,0,0.04)",color:eodLoading?C.gray2:canUseAI?C.dim:C.gray2,fontSize:12,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.15em",textTransform:"uppercase",cursor:canUseAI&&!eodLoading?"pointer":"not-allowed",marginBottom:8,transition:"all 0.3s",opacity:canUseAI?1:0.5}}>
            {eodLoading?L.btn.analyzing:L.btn.eod}
          </button>
          {!canUseAI && <div style={{marginTop:-4,marginBottom:8,fontSize:10,color:C.gray2,fontFamily:"'Josefin Sans',sans-serif",textAlign:"center"}}>
            {lang==="fr"?"Disponible à partir de Fyltra+.":"Available from Fyltra+."}{" "}
            <a href="/#tarifs" style={{color:"rgba(232,205,169,0.6)",textDecoration:"none"}}>{lang==="fr"?"Voir →":"See →"}</a>
          </div>}
          {canUseAI && eodText && <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:20,fontSize:12,lineHeight:1.8,color:C.white,whiteSpace:"pre-wrap",fontFamily:"'Josefin Sans',sans-serif",fontWeight:300,letterSpacing:"0.03em",marginBottom:10}}>{eodText}</div>}
        </>}

        <div style={{display:"grid",gridTemplateColumns:pf.status==="passed"||pf.status==="breached"?"1fr":"1fr 1fr",gap:10,marginTop:4}}>
          {pf.status !== "passed" && pf.status !== "breached" && <button onClick={()=>{const s2=pf.linkedStep2Id?propfirms.find(q=>q.id===pf.linkedStep2Id):null;setEditingPf({...pf,_step2:s2?{...s2}:null});}} style={{padding:"13px",borderRadius:8,border:`1px solid ${C.border}`,background:C.bg2,color:C.white,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.12em",textTransform:"uppercase",cursor:"pointer"}}>Modifier</button>}
          {!confirmDeletePf ? (
            <button onClick={()=>setConfirmDeletePf(true)} style={{padding:"13px",borderRadius:8,border:"1px solid rgba(192,57,43,0.3)",background:"rgba(192,57,43,0.05)",color:"rgba(192,57,43,0.8)",fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.12em",textTransform:"uppercase",cursor:"pointer"}}>Supprimer</button>
          ) : (
            <div style={{borderRadius:8,border:"1px solid rgba(192,57,43,0.4)",background:"rgba(192,57,43,0.08)",padding:"10px 14px",display:"flex",flexDirection:"column",gap:8}}>
              <div style={{fontSize:11,color:"rgba(192,57,43,0.9)",fontFamily:"'Josefin Sans',sans-serif",textAlign:"center"}}>Êtes-vous sûr ?</div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>{deletePf(pf.id);closeAccount();}} style={{flex:1,padding:"8px",borderRadius:6,border:"none",background:"rgba(192,57,43,0.8)",color:"#fff",fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,cursor:"pointer"}}>Oui</button>
                <button onClick={()=>setConfirmDeletePf(false)} style={{flex:1,padding:"8px",borderRadius:6,border:`1px solid ${C.border}`,background:"transparent",color:C.gray1,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",cursor:"pointer"}}>Annuler</button>
              </div>
            </div>
          )}
        </div>

        {/* ── Edit account modal ── */}
        {editingPf?.id === pf.id && (() => {
          const eInp = {width:"100%",background:C.bg3,border:`1px solid ${C.border}`,borderRadius:10,padding:"9px 10px",color:C.white,fontSize:13,fontFamily:"'Josefin Sans',sans-serif",fontWeight:300,outline:"none",WebkitAppearance:"none",appearance:"none",letterSpacing:"0.05em",boxShadow:"inset 0 2px 6px rgba(0,0,0,0.18)",colorScheme:darkMode?"dark":"light"};
          return (
            <div style={{position:"fixed",inset:0,zIndex:2000,display:"flex",alignItems:"flex-end",justifyContent:"center",background:"rgba(0,0,0,0.65)",backdropFilter:"blur(5px)"}} onClick={e=>{if(e.target===e.currentTarget)setEditingPf(null);}}>
              <div style={{width:"100%",maxWidth:520,maxHeight:"88vh",overflowY:"auto",background:C.bg2,border:`1px solid ${C.border}`,borderRadius:"20px 20px 0 0",padding:"20px 20px 36px",boxShadow:"0 -8px 48px rgba(0,0,0,0.55)"}} onClick={e=>e.stopPropagation()}>
                <div style={{width:36,height:4,background:C.border,borderRadius:2,margin:"0 auto 18px"}}/>
                <div style={{fontSize:10,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.15em",textTransform:"uppercase",marginBottom:14}}>Modifier le compte</div>

                {pf.metaapi_id && <div style={{marginBottom:10}}>
                  <Label>Type de compte</Label>
                  <div style={{display:"flex",gap:6,background:C.bg3,borderRadius:8,padding:3}}>
                    {[{k:"propfirm",l:"Prop Firm"},{k:"personal",l:lang==="fr"?"Fond Propre":"Personal Account"}].map(({k,l})=>(
                      <button key={k} onClick={()=>setEditingPf(p=>({...p,type:k}))} style={{flex:1,padding:"7px",borderRadius:6,border:"none",background:editingPf.type===k?C.accent:"transparent",color:editingPf.type===k?(darkMode?"#111":"#fff"):C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer",transition:"all 0.2s"}}>{l}</button>
                    ))}
                  </div>
                </div>}

                {editingPf.type==="propfirm" && <div style={{marginBottom:10}}><Label>Prop Firm</Label><input type="text" value={editingPf.firm||""} onChange={e=>setEditingPf(p=>({...p,firm:e.target.value}))} style={eInp}/></div>}
                <div style={{marginBottom:10}}><Label>Nom du compte</Label><input type="text" value={editingPf.name||""} onChange={e=>setEditingPf(p=>({...p,name:e.target.value}))} style={eInp}/></div>

                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
                  <div><Label>Capital</Label><input type="text" inputMode="numeric" value={editingPf.capital||""} onChange={e=>setEditingPf(p=>({...p,capital:e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"")}))} style={eInp}/></div>
                  {(editingPf.type==="propfirm"||editingPf.type==="mt5") && <div><Label>Profit Target</Label><input type="text" inputMode="numeric" value={editingPf.target||""} onChange={e=>setEditingPf(p=>({...p,target:e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"")}))} style={eInp}/></div>}
                  {(editingPf.type==="propfirm"||editingPf.type==="mt5") && <div>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                      <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.18em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>Max Drawdown</div>
                      <span onClick={()=>setEditingPf(p=>({...p,trailingDD:!p.trailingDD}))} style={{cursor:"pointer",fontSize:9,fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",color:editingPf.trailingDD?C.accent:C.gray2,transition:"color 0.2s",userSelect:"none"}}>{editingPf.trailingDD?"▲ Suiveur actif":"Suiveur"}</span>
                    </div>
                    <input type="text" inputMode="numeric" value={editingPf.maxLoss||""} onChange={e=>setEditingPf(p=>({...p,maxLoss:e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"")}))} style={eInp}/>
                  </div>}
                </div>

                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                  <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>Daily Loss</div>
                  <button onClick={()=>setEditingPf(p=>({...p,hasDailyLoss:!p.hasDailyLoss}))} style={{padding:"4px 10px",borderRadius:20,border:`1px solid ${C.border}`,background:editingPf.hasDailyLoss?C.accent:"transparent",color:editingPf.hasDailyLoss?"#fff":C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",cursor:"pointer",textTransform:"uppercase"}}>{editingPf.hasDailyLoss?(lang==="fr"?"Activée":"On"):(lang==="fr"?"Désactivée":"Off")}</button>
                </div>
                {editingPf.hasDailyLoss && <div style={{marginBottom:10}}><Label>Montant daily loss</Label><input type="text" inputMode="numeric" value={editingPf.dailyLoss||""} onChange={e=>setEditingPf(p=>({...p,dailyLoss:e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"")}))} style={eInp}/></div>}

                {(editingPf.type==="propfirm"||editingPf.type==="mt5") && <>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                    <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>Consistance</div>
                    <button onClick={()=>setEditingPf(p=>({...p,hasConsistency:!p.hasConsistency}))} style={{padding:"4px 10px",borderRadius:20,border:`1px solid ${C.border}`,background:editingPf.hasConsistency?C.accent:"transparent",color:editingPf.hasConsistency?"#fff":C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",cursor:"pointer",textTransform:"uppercase"}}>{editingPf.hasConsistency?(lang==="fr"?"Activée":"On"):(lang==="fr"?"Désactivée":"Off")}</button>
                  </div>
                  {editingPf.hasConsistency && <div style={{marginBottom:10}}><Label>% consistance</Label><input type="text" inputMode="numeric" value={editingPf.consistencyPct||""} onChange={e=>setEditingPf(p=>({...p,consistencyPct:e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"")}))} style={eInp}/></div>}

                  {editingPf.type==="propfirm" && <>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                      <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>Règle d'Inactivité</div>
                      <button onClick={()=>setEditingPf(p=>({...p,hasInactivity:!p.hasInactivity}))} style={{padding:"4px 10px",borderRadius:20,border:`1px solid ${C.border}`,background:editingPf.hasInactivity?C.accent:"transparent",color:editingPf.hasInactivity?"#fff":C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",cursor:"pointer",textTransform:"uppercase"}}>{editingPf.hasInactivity?(lang==="fr"?"Activée":"On"):(lang==="fr"?"Désactivée":"Off")}</button>
                    </div>
                    {editingPf.hasInactivity && <div style={{marginBottom:10}}><Label>Jours max d'inactivité</Label><input type="text" inputMode="numeric" value={editingPf.inactivityDays||""} onChange={e=>setEditingPf(p=>({...p,inactivityDays:e.target.value.replace(/[^0-9]/g,"")}))} style={eInp}/></div>}
                    {editingPf.hasInactivity && <div style={{marginBottom:10}}><Label>À partir du</Label><input type="date" value={editingPf.inactivityFrom||""} onChange={e=>setEditingPf(p=>({...p,inactivityFrom:e.target.value}))} style={eInp}/></div>}
                  </>}
                </>}

                <div style={{display:"flex",gap:7,marginTop:12}}>
                  <button onClick={()=>{setPropfirms(p=>p.map(x=>x.id===editingPf.id?{...editingPf}:x));setSelectedPf({...editingPf});setEditingPf(null);}} style={{flex:2,padding:"11px",borderRadius:6,border:"none",background:"radial-gradient(ellipse 90% 90% at 50% 38%, rgba(245,245,245,0.95) 0%, rgba(215,215,215,0.88) 55%, rgba(230,230,230,0.92) 100%)",color:"#111",fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer"}}>Sauvegarder</button>
                  <button onClick={()=>setEditingPf(null)} style={{flex:1,padding:"11px",borderRadius:6,border:`1px solid ${C.border}`,background:"transparent",color:C.gray1,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer"}}>Annuler</button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

;


  // ── CSV Import ──
  const TICK_VALUES = { MNQ:0.5, NQ:5, MES:1.25, ES:12.5, MCL:1, CL:10, MGC:1, GC:10 };
  const DOLLARS_PER_POINT = { MNQ:2, NQ:20, MES:5, ES:50, MCL:100, CL:1000, MGC:10, GC:100 };

  const parseTradovateCSV = (text) => {
    const lines = text.trim().split("\n").filter(l=>l.trim());
    if (lines.length < 2) return { error:"Fichier vide ou invalide." };
    const headers = lines[0].split(",").map(h=>h.trim().replace(/"/g,""));
    const getIdx = (names) => { for(const n of names){ const i=headers.findIndex(h=>h.toLowerCase().includes(n.toLowerCase())); if(i>=0) return i; } return -1; };
    const bsIdx      = getIdx(["B/S","side"]);
    const contractIdx= getIdx(["Contract","Product","symbol"]);
    const priceIdx   = getIdx(["avgPrice","Avg Fill","price"]);
    const qtyIdx     = getIdx(["filledQty","Filled Qty","qty"]);
    const dateIdx    = getIdx(["Fill Time","Date","date"]);
    const statusIdx  = getIdx(["Status"]);
    const orders = [];
    for(let i=1;i<lines.length;i++){
      const cols = lines[i].split(",").map(c=>c.trim().replace(/"/g,""));
      if(statusIdx>=0 && cols[statusIdx] && !cols[statusIdx].toLowerCase().includes("fill")) continue;
      const side = (cols[bsIdx]||"").toUpperCase();
      if(!side) continue;
      orders.push({ side, symbol:(cols[contractIdx]||"").replace(/[0-9\s]/g,"").toUpperCase(), price:parseFloat(cols[priceIdx])||0, qty:parseInt(cols[qtyIdx])||1, date:cols[dateIdx]||"" });
    }
    // Pair chronologically: first fill opens position, next closes it
    const trades = [];
    const queue = [];
    for(const o of orders){
      if(!queue.length || queue[0].side === o.side){ queue.push(o); continue; }
      const open = queue.shift();
      const sym = open.symbol || "MNQ";
      const dpp = DOLLARS_PER_POINT[sym] || 2;
      const isLong = open.side.startsWith("B");
      const pnl = (isLong ? o.price - open.price : open.price - o.price) * open.qty * dpp;
      const dateStr = (open.date||"").split("T")[0].split(" ")[0];
      const d = new Date(dateStr); const date = isNaN(d.getTime()) ? new Date().toISOString().split("T")[0] : d.toISOString().split("T")[0];
      trades.push({ id:Date.now()+trades.length, date, instrument:sym, direction:isLong?"LONG":"SHORT", result:pnl>0?"WIN":pnl<0?"LOSS":"BREAKEVEN", session:"New York", emotion:"Neutre", entry:open.price, exit:o.price, rr:"", notes:"Import Tradovate", pnl:parseFloat(pnl.toFixed(2)), accountIds:[] });
    }
    return { trades, skipped: queue.length };
  };

  const parseNinjaTraderCSV = (text) => {
    const lines = text.trim().split("\n").filter(l=>l.trim());
    if (lines.length < 2) return { error:"Fichier vide ou invalide." };
    const sep = lines[0].includes("\t") ? "\t" : ",";
    const headers = lines[0].split(sep).map(h=>h.trim().replace(/"/g,""));
    const getIdx = (names) => { for(const n of names){ const i=headers.findIndex(h=>h.toLowerCase().includes(n.toLowerCase())); if(i>=0) return i; } return -1; };
    const instrIdx  = getIdx(["Instrument","Symbol"]);
    const actionIdx = getIdx(["Action","Buy/Sell","Side"]);
    const qtyIdx    = getIdx(["Quantity","Qty"]);
    const priceIdx  = getIdx(["Price","Avg Price","Fill Price"]);
    const timeIdx   = getIdx(["Time","Date","Fill Time"]);
    const exIdx     = getIdx(["E/X","Entry/Exit"]);
    const fills = [];
    for(let i=1;i<lines.length;i++){
      const cols = lines[i].split(sep).map(c=>c.trim().replace(/"/g,""));
      const ex = exIdx>=0 ? (cols[exIdx]||"").trim().toUpperCase() : "";
      if(exIdx>=0 && ex !== "E" && ex !== "X") continue;
      fills.push({ instrument:(cols[instrIdx]||"").replace(/[0-9\s]/g,"").toUpperCase(), action:(cols[actionIdx]||"").toUpperCase(), qty:parseInt(cols[qtyIdx])||1, price:parseFloat(cols[priceIdx])||0, time:cols[timeIdx]||"", isEntry: exIdx<0 || ex==="E" });
    }
    const entries = fills.filter(f=>f.isEntry);
    const exits   = fills.filter(f=>!f.isEntry);
    const matched = Math.min(entries.length, exits.length);
    const trades = [];
    for(let i=0;i<matched;i++){
      const entry=entries[i], exit=exits[i];
      const sym = entry.instrument || "MNQ";
      const dpp = DOLLARS_PER_POINT[sym] || 2;
      const isLong = entry.action.startsWith("B");
      const pnl = (isLong ? exit.price - entry.price : entry.price - exit.price) * entry.qty * dpp;
      const dateStr = (entry.time||"").split("T")[0].split(" ")[0];
      const d = new Date(dateStr); const date = isNaN(d.getTime()) ? new Date().toISOString().split("T")[0] : d.toISOString().split("T")[0];
      trades.push({ id:Date.now()+i, date, instrument:sym, direction:isLong?"LONG":"SHORT", result:pnl>0?"WIN":pnl<0?"LOSS":"BREAKEVEN", session:"New York", emotion:"Neutre", entry:entry.price, exit:exit.price, rr:"", notes:"Import NinjaTrader", pnl:parseFloat(pnl.toFixed(2)), accountIds:[] });
    }
    return { trades, skipped: fills.length - matched*2 };
  };

  const parseMT5CSV = (text) => {
    const lines = text.trim().split("\n").filter(l=>l.trim());
    if (lines.length < 2) return { error:"Fichier vide ou invalide." };
    // Detect separator: tab, semicolon, or comma
    const sep = lines[0].includes("\t") ? "\t" : lines[0].includes(";") ? ";" : ",";
    const headers = lines[0].split(sep).map(h=>h.trim().replace(/"/g,"").replace(/#/g,"").trim());
    const getIdx = (names) => { for(const n of names){ const i=headers.findIndex(h=>h.toLowerCase()===n.toLowerCase()); if(i>=0) return i; } for(const n of names){ const i=headers.findIndex(h=>h.toLowerCase().includes(n.toLowerCase())); if(i>=0) return i; } return -1; };
    // MT5 Deals export: Time, Deal, Symbol, Type, Direction, Volume, Price, Commission, Swap, Profit
    // MT5 Orders export: Open Time, Close Time, Symbol, Type, Volume, Open Price, Close Price, Profit
    const openIdx      = getIdx(["Open Time","Time","time"]);
    const closeIdx     = getIdx(["Close Time","close time"]);
    const symIdx       = getIdx(["Symbol","symbol","Instrument"]);
    const typeIdx      = getIdx(["Type","type"]);
    const dirIdx       = getIdx(["Direction","direction"]);
    const openPriceIdx = getIdx(["Open Price","open price","Price","price"]);
    const closePriceIdx= getIdx(["Close Price","close price"]);
    const profitIdx    = getIdx(["Profit","profit","P&L","pnl"]);
    const trades = [];
    for(let i=1;i<lines.length;i++){
      const cols = lines[i].split(sep).map(c=>c.trim().replace(/"/g,""));
      const profit = parseFloat(cols[profitIdx]);
      if(isNaN(profit)) continue;
      // Skip balance/deposit/withdrawal lines (profit=0 and no symbol often)
      const sym = (cols[symIdx]||"").toUpperCase().trim();
      if(!sym) continue;
      const dateRaw = (cols[openIdx]||cols[closeIdx]||"").replace(/\./g,"-");
      const d = new Date(dateRaw);
      const date = isNaN(d.getTime()) ? new Date().toISOString().split("T")[0] : d.toISOString().split("T")[0];
      // Direction: MT5 Deals has "Direction" col (in/out), Type col has buy/sell
      const typeRaw = (cols[typeIdx]||"").toLowerCase();
      const dirRaw  = (cols[dirIdx]||"").toLowerCase();
      const direction = (typeRaw.includes("sell")||typeRaw.includes("short")||dirRaw.includes("sell")||dirRaw.includes("short")) ? "SHORT" : "LONG";
      trades.push({ id:Date.now()+i, date, instrument:sym||"EUR/USD", direction, result:profit>0?"WIN":profit<0?"LOSS":"BREAKEVEN", session:"New York", emotion:"Neutre", entry:parseFloat(cols[openPriceIdx])||0, exit:parseFloat(cols[closePriceIdx])||0, rr:"", notes:"Import MT4/MT5", pnl:parseFloat(profit.toFixed(2)), accountIds:[] });
    }
    return { trades, skipped:0 };
  };

  const importCSV = () => {
    setCsvError(""); setCsvResult(null);
    if(!csvText.trim()){ setCsvError("Colle ton CSV ci-dessus."); return; }
    const result = csvPlatform==="tradovate" ? parseTradovateCSV(csvText) : csvPlatform==="ninjatrader" ? parseNinjaTraderCSV(csvText) : parseMT5CSV(csvText);
    if(result.error){ setCsvError(result.error); return; }
    if(!result.trades||result.trades.length===0){ setCsvError(L.csv.noTrades); return; }
    setCsvResult(result);
  };

  const confirmImport = async () => {
    if(!csvResult) return;
    const toImport = csvResult.trades;
    setTrades(p => [...toImport, ...p]);
    if (user) {
      const { error } = await supabase.from("trades").insert(toImport.map(t => tradeToDb(t)));
      if (error) console.error("confirmImport supabase error:", error.message);
    }
    setCsvText(""); setCsvResult(null); setShowCsvImport(false);
    setCsvError(L.csv.success(toImport.length));
    setTimeout(()=>setCsvError(""),3000);
  };

  // ── Position Calculator ──
  const calcResult = (() => {
    const entry = parseFloat(calcEntry);
    const stop = parseFloat(calcStop);
    const risk = parseFloat(calcRisk);
    if(isNaN(entry)||isNaN(stop)||isNaN(risk)||entry===stop) return null;
    const pointDiff = Math.abs(entry - stop);
    if(calcMode === "forex") {
      // Forex: pip value ~10$ per standard lot for most pairs, pip = 0.0001
      const pipSize = calcInstrument.includes("JPY") ? 0.01 : 0.0001;
      const pips = pointDiff / pipSize;
      const pipValuePerLot = calcInstrument.includes("JPY") ? 9.3 : 10;
      const riskPerLot = pips * pipValuePerLot;
      if(riskPerLot <= 0) return null;
      const lots = Math.floor((risk / riskPerLot) * 100) / 100;
      const realRisk = lots * riskPerLot;
      return { value: lots, label:"lots", realRisk: realRisk.toFixed(2), detail: Math.round(pips) + " pips" };
    } else {
      const tickVal = TICK_VALUES[calcInstrument] || 2;
      const tickSize = 0.25;
      const ticks = pointDiff / tickSize;
      const riskPerContract = ticks * tickVal;
      if(riskPerContract <= 0) return null;
      const contracts = Math.floor(risk / riskPerContract);
      const realRisk = contracts * riskPerContract;
      return { value: contracts, label:"contrats", realRisk: realRisk.toFixed(2), detail: Math.round(ticks) + " ticks" };
    }
  })();

  // ── End of Day ──
  const runEOD = async (pf) => {
    const todayTrades = trades.filter(t => t.date===tradingToday && (!t.accountIds||t.accountIds.length===0||t.accountIds.includes(pf.id)));
    if(todayTrades.length===0){ setEodText(L.aiSection.noTradesToday); return; }
    setEodLoading(true); setEodText("");
    const summary = [...todayTrades].sort(cmpTrades).map(t=>`${t.instrument}|${t.direction}|${t.session}|${t.emotion}|RR:${t.rr||"—"}|P&L:${t.pnl}€|${t.result}${t.notes?`|"${t.notes}"`:""}`).join("\n");
    const todayPnl = todayTrades.reduce((s,t)=>s+(t.pnl||0),0);
    const systemMsg = L.aiSection.eodSystemPrompt;
    const userMsg = `Compte: ${pf.firm}${pf.name?" "+pf.name:""}\nP&L du jour: ${fms(todayPnl)}${currency}\n${todayTrades.length} trades:\n${summary}`;
    try {
      const res = await fetch("/api/analyze",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({patternData:userMsg,customSystem:systemMsg,tradeCount:todayTrades.length})});
      if(!res.ok){const e=await res.json().catch(()=>({}));setEodText("Erreur: "+(e?.error||"inconnue"));setEodLoading(false);return;}
      const data=await res.json();
      if(data.text) setEodText(data.text); else setEodText(L.aiSection.eodEmpty);
    } catch(e){setEodText(L.aiSection.networkError(e.message));}
    setEodLoading(false);
  };

  // ── Tools Content ──
  const toolsContent = (
    <div>
      <PageTitle sub={L.pages.tools.sub} title={L.pages.tools.title} />
      {/* Tab selector */}
      <div style={{display:"flex",gap:6,marginBottom:20}}>
        {[{k:"csv",l:"Import CSV"},{k:"calc",l:"Calculateur"}].map(t=>(
          <button key={t.k} onClick={()=>setToolTab(t.k)} style={{flex:1,padding:"9px",borderRadius:6,border:`1px solid ${toolTab===t.k?C.accent:C.border}`,background:toolTab===t.k?"rgba(0,0,0,0.08)":"transparent",color:toolTab===t.k?C.accent:C.gray1,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:toolTab===t.k?600:300,cursor:"pointer",letterSpacing:"0.06em",textTransform:"uppercase"}}>{t.l}</button>
        ))}
      </div>

      {/* CSV IMPORT */}
      {toolTab==="csv" && (
        <div>
          <div style={{fontSize:13,color:C.gray1,lineHeight:1.7,marginBottom:16}}>{L.tools.csvDesc}</div>
          <Field label={L.tools.platform}>
            <div style={{display:"flex",gap:6}}>
              {[{k:"mt5",l:"MT4 / MT5 (FTMO, Lucid, FundedNext...)"},{k:"tradovate",l:"Tradovate"},{k:"ninjatrader",l:"NinjaTrader"}].map(p=>(
                <button key={p.k} onClick={()=>setCsvPlatform(p.k)} style={{flex:1,padding:"9px",borderRadius:6,border:`1px solid ${csvPlatform===p.k?C.accent:C.border}`,background:csvPlatform===p.k?"rgba(0,0,0,0.08)":"transparent",color:csvPlatform===p.k?C.accent:C.gray1,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:csvPlatform===p.k?600:300,cursor:"pointer",letterSpacing:"0.04em"}}>{p.l}</button>
              ))}
            </div>
          </Field>
          <div style={{marginBottom:8,padding:"10px 12px",borderRadius:6,background:"rgba(0,0,0,0.03)",border:`1px solid ${C.border}`,fontSize:11,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",lineHeight:1.6}}>
            {csvPlatform==="mt5" ? L.csv.mt5Hint : csvPlatform==="ninjatrader" ? L.csv.ninjaHint : L.csv.tradovateHint}
          </div>
          <Field label={L.tools.csvContent}>
            <textarea rows={8} placeholder={L.tools.csvPH} value={csvText} onChange={e=>setCsvText(e.target.value)} style={{...iStyle,resize:"vertical",lineHeight:1.5,fontSize:12}}/>
          </Field>
          {csvError && <div style={{padding:"10px 12px",borderRadius:6,marginBottom:10,background:csvResult?"rgba(42,110,58,0.08)":"rgba(192,57,43,0.06)",border:`1px solid ${csvResult?"rgba(42,110,58,0.25)":"rgba(192,57,43,0.2)"}`,fontSize:12,color:csvResult?"#2a6e3a":"#c0392b",fontFamily:"'Josefin Sans',sans-serif"}}>{csvError}</div>}
          {csvResult && (
            <div style={{padding:"14px",borderRadius:6,marginBottom:12,background:"rgba(0,0,0,0.04)",border:`1px solid ${C.border}`}}>
              <div style={{fontSize:13,color:C.white,fontFamily:"'Josefin Sans',sans-serif",marginBottom:10}}><strong>{csvResult.trades.length}</strong> trade{csvResult.trades.length!==1?"s":""} détecté{csvResult.trades.length!==1?"s":""}. {csvResult.skipped>0?`(${csvResult.skipped} ligne${csvResult.skipped>1?"s":""} ignorée${csvResult.skipped>1?"s":""})`:""}
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={confirmImport} style={{flex:2,padding:"11px",borderRadius:4,border:"none",background:"radial-gradient(ellipse 90% 90% at 50% 38%, rgba(245,245,245,0.95) 0%, rgba(215,215,215,0.88) 55%, rgba(230,230,230,0.92) 100%)",color:"#111",fontSize:12,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer"}}>{L.tools.importBtn}</button>
                <button onClick={()=>setCsvResult(null)} style={{flex:1,padding:"11px",borderRadius:4,border:`1px solid ${C.gray3}`,background:"transparent",color:C.gray1,fontSize:12,fontFamily:"'Josefin Sans',sans-serif",cursor:"pointer",textTransform:"uppercase",letterSpacing:"0.1em"}}>{L.btn.cancel}</button>
              </div>
            </div>
          )}
          {!csvResult && <button onClick={importCSV} style={{width:"100%",padding:"14px",borderRadius:4,border:"none",background:"radial-gradient(ellipse 90% 90% at 50% 38%, rgba(245,245,245,0.95) 0%, rgba(215,215,215,0.88) 55%, rgba(230,230,230,0.92) 100%)",color:"#111",fontSize:12,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.2em",textTransform:"uppercase",cursor:"pointer"}}>{L.tools.analyzeBtn}</button>}
        </div>
      )}

      {/* POSITION CALCULATOR */}
      {toolTab==="calc" && (
        <div>
          <div style={{fontSize:13,color:C.gray1,lineHeight:1.7,marginBottom:16}}>{L.tools.calcDesc}</div>
          {/* Mode switch */}
          <div style={{display:"flex",gap:6,marginBottom:16}}>
            {[{k:"futures",l:"Futures"},{k:"forex",l:"Forex"}].map(m=>(
              <button key={m.k} onClick={()=>{setCalcMode(m.k);setCalcInstrument(m.k==="futures"?"MNQ":"EUR/USD");}} style={{flex:1,padding:"9px",borderRadius:6,border:`1px solid ${calcMode===m.k?C.accent:C.border}`,background:calcMode===m.k?"rgba(0,0,0,0.08)":"transparent",color:calcMode===m.k?C.accent:C.gray1,fontSize:12,fontFamily:"'Josefin Sans',sans-serif",fontWeight:calcMode===m.k?600:300,cursor:"pointer",letterSpacing:"0.08em",textTransform:"uppercase"}}>{m.l}</button>
            ))}
          </div>
          <Field label="Instrument">
            <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
              {(calcMode==="futures"?["MNQ","NQ","MES","ES","MCL","CL","MGC","GC"]:["EUR/USD","GBP/USD","USD/JPY","AUD/USD","USD/CHF","NAS100","US30"]).map(i=>(
                <button key={i} onClick={()=>{setCalcInstrument(i);setCalcCustomPair("");}} style={{padding:"7px 10px",borderRadius:4,border:`1px solid ${calcInstrument===i&&!calcCustomPair?C.accent:C.gray2}`,background:calcInstrument===i&&!calcCustomPair?"rgba(0,0,0,0.08)":"transparent",color:calcInstrument===i&&!calcCustomPair?C.accent:C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",fontWeight:calcInstrument===i&&!calcCustomPair?600:300,cursor:"pointer",letterSpacing:"0.04em"}}>{i}</button>
              ))}
              <input type="text" placeholder="Autre" value={calcCustomPair} onChange={e=>{setCalcCustomPair(e.target.value.toUpperCase());if(e.target.value) setCalcInstrument(e.target.value.toUpperCase());}} style={{...iStyle,width:70,padding:"5px 8px",fontSize:11,height:"auto"}}/>
            </div>
          </Field>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <Field label={L.form.entry}><input type="text" inputMode="decimal" placeholder="" value={calcEntry} onChange={e=>setCalcEntry(e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,""))} style={iStyle}/></Field>
            <Field label="Stop Loss"><input type="text" inputMode="decimal" placeholder="" value={calcStop} onChange={e=>setCalcStop(e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,""))} style={iStyle}/></Field>
          </div>
          <Field label="Risque max (€)"><input type="text" inputMode="decimal" placeholder="" value={calcRisk} onChange={e=>setCalcRisk(e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,""))} style={iStyle}/></Field>
          {calcResult ? (
            <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:"20px",marginTop:8}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",marginBottom:6}}>{calcResult.label}</div>
                  <div style={{fontSize:32,fontWeight:300,color:C.accent,fontFamily:"'Josefin Sans',sans-serif"}}>{calcResult.value}</div>
                </div>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",marginBottom:6}}>Risque réel</div>
                  <div style={{fontSize:32,fontWeight:300,color:"#c0392b",fontFamily:"'Josefin Sans',sans-serif"}}>{calcResult.realRisk}€</div>
                </div>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",marginBottom:6}}>{calcMode==="futures"?"Ticks":"Pips"}</div>
                  <div style={{fontSize:32,fontWeight:300,color:C.white,fontFamily:"'Josefin Sans',sans-serif"}}>{calcResult.detail}</div>
                </div>
              </div>
              {calcResult.value===0 && <div style={{marginTop:12,fontSize:12,color:"#c0392b",fontFamily:"'Josefin Sans',sans-serif",textAlign:"center"}}>Risque trop faible pour ce setup — augmente ton risque ou réduis ton stop.</div>}
            </div>
          ) : (
            <div style={{textAlign:"center",padding:"32px 0",border:`1px solid ${C.gray3}`,borderRadius:8,marginTop:8}}>
              <div style={{fontSize:32,marginBottom:8,color:C.gray2}}>⬡</div>
              <div style={{fontSize:13,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif"}}>Remplis les champs ci-dessus</div>
            </div>
          )}
        </div>
      )}

      )}
    </div>
  );


  // Currency helper
  const cur = (val, decimals=0) => {
    if (val === null || val === undefined) return "—";
    const v = parseFloat(val);
    if (isNaN(v)) return "—";
    return `${v>=0?"+":""}${Math.abs(v).toFixed(decimals)}${currency}`;
  };
  const curAbs = (val, decimals=0) => {
    const v = parseFloat(val);
    if (isNaN(v)) return "—";
    return `${Math.abs(v).toFixed(decimals)}${currency}`;
  };

  // ── Profile Content ──
  const saveProfile = async () => {
    if (!user) return;
    setProfileSaving("saving");
    const patch = {
      first_name: profileForm.firstName || null,
      last_name:  profileForm.lastName  || null,
      nickname:   profileForm.nickname  || null,
      address:    profileForm.address   || null,
      phone:      profileForm.phone     || null,
      updated_at: new Date().toISOString(),
    };
    const { error } = await supabase
      .from("user_settings")
      .upsert({ user_id: user.id, ...patch }, { onConflict: "user_id" });
    if (error) console.error("saveProfile error:", error?.message, error?.details, error?.hint);
    setProfileSaving(error ? `error:${error.message}` : "ok");
    setTimeout(() => setProfileSaving(false), 4000);
  };

  const loadSub = async () => {
    if (!user?.email || subData || subLoading) return;
    setSubLoading(true);
    try {
      const r = await fetch(`/api/subscription-status?email=${encodeURIComponent(user.email)}`);
      const d = await r.json();
      setSubData(r.ok ? d : { error: d.error });
    } catch { setSubData({ error: "Erreur réseau" }); }
    setSubLoading(false);
  };

  const cancelSub = async (reason = "") => {
    if (!subData?.id) return;
    setCancelLoading(true);
    try {
      const r = await fetch("/api/cancel-subscription", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ subscriptionId: subData.id, action:"cancel", churnReason: reason, plan: userPlan, userId: user?.id }) });
      const d = await r.json();
      if (r.ok) { setSubData(prev => ({ ...prev, cancelled: true, endsAt: d.endsAt || prev.endsAt })); setCancelStep(0); }
    } catch {}
    setCancelLoading(false);
  };

  const deleteAccount = async () => {
    setDeleteLoading(true);
    try {
      const r = await fetch("/api/cancel-subscription", { method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ action:"delete-account", subscriptionId: subData?.id || null, userId: user?.id, email: user?.email }) });
      if (r.ok) {
        localStorage.clear();
        await supabase.auth.signOut();
        window.location.reload();
      }
    } catch {}
    setDeleteLoading(false);
  };

  const pauseSub = async () => {
    if (!subData?.id) return;
    setPauseLoading(true);
    try {
      const r = await fetch("/api/cancel-subscription", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ subscriptionId: subData.id, action:"pause", userId: user?.id }) });
      const d = await r.json();
      if (r.ok) { setSubData(prev => ({ ...prev, paused: true, pausedUntil: d.pausedUntil })); setPauseDone(true); }
    } catch {}
    setPauseLoading(false);
  };

  const fmtDate = iso => { if (!iso) return "—"; try { const d = new Date(iso); if (isNaN(d.getTime())) return "—"; return d.toLocaleDateString("fr-FR", { day:"numeric", month:"long", year:"numeric" }); } catch { return "—"; } };
  const pField = { width:"100%", background:C.bg3, border:`1px solid ${C.border}`, borderRadius:8, padding:"11px 14px", color:C.white, fontFamily:"'Josefin Sans',sans-serif", fontSize:13, outline:"none", boxSizing:"border-box" };

  const renderProfileContent = (inModal = false) => {
    if ((view === "profil" || inModal) && !subData && !subLoading) loadSub();
    return (
      <div style={{ maxWidth: inModal ? "none" : 640, margin: inModal ? 0 : "0 auto" }}>
        {!inModal && <PageTitle sub={L.pages.prof.sub} title={L.pages.prof.title} />}

        {/* ── Personal info ── */}
        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:"18px 16px",marginBottom:12}}>
          <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:16}}>Informations personnelles</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            {[[L.form.firstName,"firstName"],[L.form.lastName,"lastName"]].map(([label,key])=>(
              <div key={key}>
                <div style={{fontSize:10,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:5}}>{label}</div>
                <input value={profileForm[key]} onChange={e=>setProfileForm(f=>({...f,[key]:e.target.value}))} style={pField} placeholder={label} />
              </div>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div>
              <div style={{fontSize:10,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:5}}>Surnom</div>
              <input value={profileForm.nickname} onChange={e=>setProfileForm(f=>({...f,nickname:e.target.value}))} style={pField} placeholder="Pseudo de trading" />
            </div>
            <div>
              <div style={{fontSize:10,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:5}}>Téléphone</div>
              <input type="tel" value={profileForm.phone} onChange={e=>{ const v=e.target.value; if(!v.includes("@")) setProfileForm(f=>({...f,phone:v})); }} style={pField} placeholder="+33 6 00 00 00 00" />
            </div>
          </div>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:10,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:5}}>Adresse <span style={{color:C.gray2,textTransform:"none",letterSpacing:0,fontSize:9}}>(optionnel)</span></div>
            <textarea value={profileForm.address} onChange={e=>setProfileForm(f=>({...f,address:e.target.value}))} rows={2} style={{...pField,resize:"vertical",lineHeight:1.5}} placeholder="Adresse postale" />
          </div>
          <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <button onClick={saveProfile} disabled={!!profileSaving} style={{padding:"11px 24px",borderRadius:8,border:profileSaving==="ok"?"1px solid rgba(74,222,128,0.4)":profileSaving==="saving"?"none":profileSaving?"1px solid rgba(229,100,100,0.4)":"none",background:profileSaving==="ok"?"rgba(74,222,128,0.1)":profileSaving==="saving"?`rgba(255,255,255,0.06)`:profileSaving?`rgba(229,100,100,0.1)`:"#111",color:profileSaving==="ok"?clrGreen:profileSaving==="saving"?C.gray1:profileSaving?"rgba(229,100,100,0.9)":"#fff",fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,fontSize:13,cursor:profileSaving?"not-allowed":"pointer",transition:"all 0.2s"}}>
              {profileSaving==="ok"?L.btn.saved:profileSaving==="saving"?L.btn.saving:L.btn.save}
            </button>
            {profileSaving&&profileSaving!=="ok"&&profileSaving!=="saving"&&<span style={{fontSize:11,color:"rgba(229,100,100,0.9)",fontFamily:"'Josefin Sans',sans-serif"}}>{String(profileSaving).replace("error:","")}</span>}
          </div>
        </div>

        {/* ── Change password ── */}
        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:"18px 16px",marginBottom:12}}>
          <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:16}}>{L.prof.pwdSection}</div>
          <div style={{position:"relative",marginBottom:10}}>
            <input type={pwdForm.show?"text":"password"} placeholder={L.prof.pwdNewPH} value={pwdForm.newPwd} onChange={e=>setPwdForm(f=>({...f,newPwd:e.target.value}))}
              style={{...pField,paddingRight:46}} />
            <button onClick={()=>setPwdForm(f=>({...f,show:!f.show}))} style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:C.gray1,padding:0}}>
              {pwdForm.show
                ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
            </button>
          </div>
          <div style={{position:"relative",marginBottom:14}}>
            <input type={pwdForm.show?"text":"password"} placeholder={L.prof.pwdConfirmPH} value={pwdForm.confirmPwd} onChange={e=>setPwdForm(f=>({...f,confirmPwd:e.target.value}))}
              style={{...pField,paddingRight:46,borderColor:pwdForm.confirmPwd?(pwdForm.newPwd===pwdForm.confirmPwd?"rgba(74,222,128,0.5)":"rgba(229,100,100,0.5)"):undefined}} />
            {pwdForm.confirmPwd && <span style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",fontSize:13,color:pwdForm.newPwd===pwdForm.confirmPwd?clrGreen:"rgba(229,100,100,0.9)"}}>{pwdForm.newPwd===pwdForm.confirmPwd?"✓":"✗"}</span>}
          </div>
          {pwdMsg && <div style={{marginBottom:10,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",color:pwdMsg==="ok"?clrGreen:"rgba(229,100,100,0.9)"}}>{pwdMsg==="ok"?L.prof.pwdUpdated:pwdMsg}</div>}
          <button disabled={pwdSaving} onClick={async()=>{
            if (!pwdForm.newPwd||!pwdForm.confirmPwd){setPwdMsg(L.prof.pwdFillBoth);return;}
            if (pwdForm.newPwd!==pwdForm.confirmPwd){setPwdMsg(L.prof.pwdNoMatch);return;}
            if (pwdForm.newPwd.length<6){setPwdMsg(L.prof.pwdMin);return;}
            setPwdSaving(true);setPwdMsg("");
            const{error}=await supabase.auth.updateUser({password:pwdForm.newPwd});
            if(error){setPwdMsg(error.message);}else{setPwdMsg("ok");setPwdForm({newPwd:"",confirmPwd:"",show:false});}
            setPwdSaving(false);
          }} style={{padding:"11px 24px",borderRadius:8,border:"none",background:pwdSaving?`rgba(255,255,255,0.06)`:"#111",color:pwdSaving?C.gray1:"#fff",fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,fontSize:13,cursor:pwdSaving?"not-allowed":"pointer",transition:"all 0.2s"}}>
            {pwdSaving?"···":L.prof.pwdChange}
          </button>
        </div>

        {/* ── Change email ── */}
        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:"18px 16px",marginBottom:12}}>
          <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:16}}>{L.prof.emailSection}</div>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:10,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:5}}>{L.prof.emailCurrentLabel}</div>
            <input value={user?.email||""} readOnly style={{...pField,color:C.gray1,cursor:"default",background:"transparent"}} />
          </div>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:10,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:5}}>{L.prof.emailNewLabel}</div>
            <input type="email" value={emailNew} onChange={e=>{setEmailNew(e.target.value);setEmailMsg("");}} placeholder={L.prof.emailNewPH} style={pField} />
          </div>
          {emailMsg && <div style={{marginBottom:10,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",color:emailMsg==="ok"?clrGreen:"rgba(229,100,100,0.9)"}}>{emailMsg==="ok"?L.prof.emailChanged:emailMsg}</div>}
          <button disabled={emailSaving} onClick={async()=>{
            if (!emailNew) { setEmailMsg(L.prof.emailEnter); return; }
            if (emailNew === user?.email) { setEmailMsg(L.auth.sameEmail); return; }
            setEmailSaving(true); setEmailMsg("");
            const { error } = await supabase.auth.updateUser({ email: emailNew }, { emailRedirectTo: "https://fyltra.app/app" });
            if (error) { setEmailMsg(error.message); } else {
              await supabase.from("purchases").update({ email: emailNew }).eq("email", user.email);
              setEmailMsg("ok"); setEmailNew("");
            }
            setEmailSaving(false);
          }} style={{padding:"11px 24px",borderRadius:8,border:"none",background:emailSaving?`rgba(255,255,255,0.06)`:"#111",color:emailSaving?C.gray1:"#fff",fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,fontSize:13,cursor:emailSaving?"not-allowed":"pointer",transition:"all 0.2s"}}>
            {emailSaving?"···":L.prof.emailChange}
          </button>
        </div>

        {/* ── Classement ── */}
        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:"18px 16px",marginBottom:12}}>
          <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:16}}>{L.prof.rankSection}</div>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {[
              { state: compete, setter: setCompete, key: "compete", label: L.sett.compete, desc: L.sett.competeDesc },
              { state: anonymous, setter: setAnonymous, key: "anonymous", label: L.sett.anonymous, desc: L.sett.anonymousDesc, disabled: !compete },
            ].map(({ state, setter, key, label, desc, disabled }) => (
              <div key={key} style={{display:"flex",alignItems:"flex-start",gap:12,opacity:disabled?0.4:1,transition:"opacity 0.2s"}}>
                <button onClick={async()=>{
                  if(disabled)return;
                  const next=!state;
                  setter(next);
                  await saveUserSettings({[key]:next});
                }} style={{flexShrink:0,marginTop:2,width:40,height:22,borderRadius:11,border:"none",cursor:disabled?"not-allowed":"pointer",background:state?"#4caf6e":"rgba(255,255,255,0.12)",transition:"background 0.2s",position:"relative"}}>
                  <span style={{position:"absolute",top:3,left:state?20:3,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"left 0.2s",display:"block"}}/>
                </button>
                <div>
                  <div style={{fontSize:13,color:C.white,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.03em"}}>{label}</div>
                  <div style={{fontSize:11,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",marginTop:2,lineHeight:1.5}}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Subscription ── */}
        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:"18px 16px"}}>
          <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:16}}>{L.prof.subSection}</div>
          {subLoading ? (
            <div style={{color:C.gray1,fontSize:12,fontFamily:"'Josefin Sans',sans-serif"}}>{L.prof.subLoading}</div>
          ) : subData?.error ? (
            <>
              <div style={{color:C.gray1,fontSize:12,fontFamily:"'Josefin Sans',sans-serif",marginBottom:14}}>{subData.error === "no subscription" ? L.prof.subNone : subData.error}</div>
              {(() => {
                const PLANS = [
                  { id:"fyltra",      label:"Fyltra",     price:"14.99", feat: lang==="fr"?"Journal · Stats · TradingView · 3 comptes":"Journal · Stats · TradingView · 3 accounts" },
                  { id:"fyltra_plus", label:"Fyltra+",    price:"24.99", feat: lang==="fr"?"+ IA Coach · EOD Review · Market Replay · 10 comptes":"+ AI Coach · EOD Review · Market Replay · 10 accounts" },
                  { id:"fyltra_pro",  label:"Fyltra Pro", price:"39.99", feat: lang==="fr"?"+ Sync MT5 (1/jour) · Comptes illimités":"+ MT5 sync (1/day) · Unlimited accounts" },
                ];
                const goNewPlan = async (planId) => {
                  setPlanChanging(planId);
                  try {
                    const r = await fetch("/api/create-checkout", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ plan: planId, email: user?.email, existingUser: true }) });
                    const d = await r.json();
                    if (d.url) window.location.href = d.url;
                  } catch {}
                  setPlanChanging(null);
                };
                return (
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {PLANS.map(plan => {
                      const isCurrent = userPlan === plan.id;
                      const isLoading = planChanging === plan.id;
                      return (
                        <div key={plan.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 12px",borderRadius:8,border:`1px solid ${isCurrent?"rgba(232,205,169,0.35)":C.border}`,background:isCurrent?"rgba(232,205,169,0.05)":C.bg3}}>
                          <div>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <span style={{fontSize:12,color:C.white,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>{plan.label}</span>
                              <span style={{fontSize:11,color:"rgba(232,205,169,0.7)",fontFamily:"'JetBrains Mono',monospace"}}>€{plan.price}</span>
                              {isCurrent && <span style={{fontSize:8,color:"rgba(232,205,169,0.6)",fontFamily:"'JetBrains Mono',monospace",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",background:"rgba(232,205,169,0.08)",border:"1px solid rgba(232,205,169,0.2)",borderRadius:4,padding:"1px 5px"}}>{L.prof.currentPlanBadge}</span>}
                            </div>
                            <div style={{fontSize:10,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",marginTop:2}}>{plan.feat}</div>
                          </div>
                          {!isCurrent && (
                            <button onClick={()=>goNewPlan(plan.id)} disabled={!!planChanging} style={{padding:"7px 14px",borderRadius:6,border:`1px solid ${C.border}`,background:"transparent",color:isLoading?C.gray2:C.dim,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,fontSize:10,letterSpacing:"0.1em",textTransform:"uppercase",cursor:planChanging?"not-allowed":"pointer",flexShrink:0,marginLeft:10}}>
                              {isLoading?"…":L.prof.selectPlan}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </>
          ) : subData ? (
            <>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
                <div style={{background:C.bg3,borderRadius:8,padding:"12px 14px"}}>
                  <div style={{fontSize:9,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:4}}>{L.prof.planCurrent}</div>
                  <div style={{fontSize:15,color:C.white,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>{subData.productName || "—"}</div>
                  <div style={{fontSize:11,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",marginTop:2}}>{subData.variantName || ""}</div>
                </div>
                <div style={{background:C.bg3,borderRadius:8,padding:"12px 14px"}}>
                  <div style={{fontSize:9,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:4}}>{subData.cancelled?L.prof.subExpires:L.prof.subNextBilling}</div>
                  <div style={{fontSize:15,color:subData.cancelled?"rgba(192,57,43,0.9)":C.white,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>{fmtDate(subData.cancelled ? subData.endsAt : subData.renewsAt)}</div>
                  <div style={{fontSize:11,fontFamily:"'Josefin Sans',sans-serif",marginTop:2,color:subData.cancelled?"rgba(192,57,43,0.7)":clrGreen}}>{subData.cancelled?L.prof.subCancelled:L.prof.subActive}</div>
                </div>
              </div>
              {/* ── Change plan ── */}
              {(() => {
                const PLANS = [
                  { id:"fyltra",      label:"Fyltra",     price:"14.99", feat: lang==="fr"?"Journal · Stats · TradingView · 3 comptes":"Journal · Stats · TradingView · 3 accounts" },
                  { id:"fyltra_plus", label:"Fyltra+",    price:"24.99", feat: lang==="fr"?"+ IA Coach · EOD Review · Market Replay · 10 comptes":"+ AI Coach · EOD Review · Market Replay · 10 accounts" },
                  { id:"fyltra_pro",  label:"Fyltra Pro", price:"39.99", feat: lang==="fr"?"+ Sync MT5 (1/jour) · Comptes illimités":"+ MT5 sync (1/day) · Unlimited accounts" },
                ];
                const PRICES_LABEL = { starter:"€14.99", trader:"€24.99", pro:"€39.99" };
                const goChangePlan = async (planId) => {
                  const msg = lang==="fr"
                    ? `Vous serez facturé ${PRICES_LABEL[planId]} immédiatement. Votre plan actuel sera annulé. Continuer ?`
                    : `You will be charged ${PRICES_LABEL[planId]} immediately. Your current plan will be cancelled. Continue?`;
                  if (!window.confirm(msg)) return;
                  setPlanChanging(planId);
                  try {
                    const r = await fetch("/api/change-plan", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ plan: planId, oldSubscriptionId: subData?.id || "", email: user.email || "" }) });
                    const d = await r.json();
                    if (d.url) window.location.href = d.url;
                    else alert(d.error || "Erreur");
                  } catch {}
                  setPlanChanging(null);
                };
                return (
                  <div style={{marginBottom:14}}>
                    <button onClick={()=>setShowPlanChange(v=>!v)} style={{background:"transparent",border:"none",cursor:"pointer",padding:0,display:"flex",alignItems:"center",gap:6,marginBottom: showPlanChange?12:0}}>
                      <span style={{fontSize:11,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.1em",textTransform:"uppercase"}}>{L.prof.changePlan}</span>
                      <span style={{fontSize:10,color:C.gray2,transition:"transform .2s",display:"inline-block",transform:showPlanChange?"rotate(180deg)":"none"}}>▾</span>
                    </button>
                    {(showPlanChange || subData.cancelled) && (
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        {PLANS.map(plan => {
                          const isCurrent = userPlan === plan.id || (!userPlan && plan.id==="fyltra");
                          const isLoading = planChanging === plan.id;
                          return (
                            <div key={plan.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 12px",borderRadius:8,border:`1px solid ${isCurrent?"rgba(232,205,169,0.35)":C.border}`,background:isCurrent?"rgba(232,205,169,0.05)":C.bg3,transition:"all .2s"}}>
                              <div>
                                <div style={{display:"flex",alignItems:"center",gap:8}}>
                                  <span style={{fontSize:12,color:C.white,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>{plan.label}</span>
                                  <span style={{fontSize:11,color:"rgba(232,205,169,0.7)",fontFamily:"'JetBrains Mono',monospace"}}>€{plan.price}</span>
                                  {isCurrent && <span style={{fontSize:8,color:"rgba(232,205,169,0.6)",fontFamily:"'JetBrains Mono',monospace",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",background:"rgba(232,205,169,0.08)",border:"1px solid rgba(232,205,169,0.2)",borderRadius:4,padding:"1px 5px"}}>{L.prof.currentPlanBadge}</span>}
                                </div>
                                <div style={{fontSize:10,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",marginTop:2}}>{plan.feat}</div>
                              </div>
                              {!isCurrent && (
                                <button onClick={()=>goChangePlan(plan.id)} disabled={!!planChanging} style={{padding:"7px 14px",borderRadius:6,border:`1px solid ${C.border}`,background:"transparent",color:isLoading?C.gray2:C.dim,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,fontSize:10,letterSpacing:"0.1em",textTransform:"uppercase",cursor:planChanging?"not-allowed":"pointer",flexShrink:0,marginLeft:10,transition:"all .2s"}}>
                                  {isLoading?"…":L.prof.selectPlan}
                                </button>
                              )}
                            </div>
                          );
                        })}
                        <div style={{fontSize:10,color:C.gray2,fontFamily:"'Josefin Sans',sans-serif",lineHeight:1.5,paddingTop:2}}>
                          {lang==="fr"?"Le changement prend effet au prochain cycle de facturation.":"Change takes effect at the next billing cycle."}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              {!subData.cancelled && !subData.paused && (
                <button onClick={() => { setChurnReason(""); setPauseDone(false); setCancelStep(1); }} style={{padding:"10px 20px",borderRadius:8,border:"1px solid rgba(192,57,43,0.3)",background:"rgba(192,57,43,0.06)",color:"rgba(192,57,43,0.8)",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,fontSize:12,cursor:"pointer",transition:"all 0.2s"}}>
                  {L.profile.cancelSub}
                </button>
              )}
              {subData.paused && (
                <div style={{padding:"10px 14px",borderRadius:8,background:"rgba(232,205,169,0.06)",border:"1px solid rgba(232,205,169,0.2)",fontSize:12,fontFamily:"'Josefin Sans',sans-serif",color:"rgba(232,205,169,0.7)"}}>
                  {lang==="fr" ? `Abonnement en pause — reprise le ${fmtDate(subData.pausedUntil)}` : `Subscription paused — resumes ${fmtDate(subData.pausedUntil)}`}
                </div>
              )}
              {subData.cancelled && <div style={{fontSize:12,color:"rgba(192,57,43,0.7)",fontFamily:"'Josefin Sans',sans-serif"}}>{L.profile.cancelled}</div>}
            </>
          ) : null}
        </div>

        {/* ── Danger zone ── */}
        <div style={{marginTop:16,padding:"14px 16px",borderRadius:10,border:"1px solid rgba(192,57,43,0.18)",background:"rgba(192,57,43,0.03)"}}>
          <div style={{fontSize:9,fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,letterSpacing:"0.18em",textTransform:"uppercase",color:"rgba(192,57,43,0.5)",marginBottom:10}}>
            {lang==="fr" ? "Zone de danger" : "Danger zone"}
          </div>
          {deleteStep === "confirm" ? (
            <div>
              <div style={{fontSize:12,fontFamily:"'Josefin Sans',sans-serif",color:C.dim,lineHeight:1.7,marginBottom:12}}>
                {lang==="fr"
                  ? "Toutes tes données seront supprimées définitivement (trades, stats, notes, compte). Cette action est irréversible."
                  : "All your data will be permanently deleted (trades, stats, notes, account). This action cannot be undone."}
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={deleteAccount} disabled={deleteLoading} style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:"rgba(192,57,43,0.85)",color:"#fff",fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,fontSize:11,letterSpacing:"0.08em",textTransform:"uppercase",cursor:deleteLoading?"not-allowed":"pointer",opacity:deleteLoading?0.7:1}}>
                  {deleteLoading ? "…" : lang==="fr" ? "Confirmer la suppression" : "Confirm deletion"}
                </button>
                <button onClick={()=>setDeleteStep(null)} style={{padding:"10px 14px",borderRadius:8,border:`1px solid ${C.border}`,background:"transparent",color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",fontSize:11,cursor:"pointer"}}>
                  {lang==="fr" ? "Annuler" : "Cancel"}
                </button>
              </div>
            </div>
          ) : deleteStep === "check" ? (
            <div>
              <div style={{fontSize:12,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,color:C.white,marginBottom:8}}>
                {lang==="fr" ? "As-tu résilié ton abonnement ?" : "Have you cancelled your subscription?"}
              </div>
              <div style={{fontSize:11,fontFamily:"'Josefin Sans',sans-serif",color:C.dim,lineHeight:1.7,marginBottom:12}}>
                {lang==="fr"
                  ? "Si ce n'est pas le cas, ta facturation continuera après la suppression du compte. Les paiements déjà effectués ne sont pas remboursables. Pour toute demande, contacte-nous à contact@fyltra.app."
                  : "If not, billing will continue after account deletion. Past payments are non-refundable. For any request, contact us at contact@fyltra.app."}
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setDeleteStep("confirm")} style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:"rgba(192,57,43,0.85)",color:"#fff",fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,fontSize:11,letterSpacing:"0.08em",textTransform:"uppercase",cursor:"pointer"}}>
                  {lang==="fr" ? "Oui, mon abonnement est résilié" : "Yes, my subscription is cancelled"}
                </button>
                <button onClick={()=>setDeleteStep(null)} style={{padding:"10px 14px",borderRadius:8,border:`1px solid ${C.border}`,background:"transparent",color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",fontSize:11,cursor:"pointer"}}>
                  {lang==="fr" ? "Non / Annuler" : "No / Cancel"}
                </button>
              </div>
            </div>
          ) : (
            <button onClick={()=>setDeleteStep("check")} style={{padding:"8px 16px",borderRadius:8,border:"1px solid rgba(192,57,43,0.25)",background:"transparent",color:"rgba(192,57,43,0.7)",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,fontSize:11,cursor:"pointer",letterSpacing:"0.06em",transition:"all 0.2s"}}>
              {lang==="fr" ? "Supprimer mon compte" : "Delete my account"}
            </button>
          )}
        </div>

        {/* ── Contact ── */}
        <div style={{textAlign:"center",padding:"12px 0 4px"}}>
          <a href="mailto:contact@fyltra.app" style={{fontSize:12,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",textDecoration:"none",letterSpacing:"0.05em",transition:"color .2s"}}
            onMouseEnter={e=>e.currentTarget.style.color=C.white}
            onMouseLeave={e=>e.currentTarget.style.color=C.gray1}>
            {L.profile.contact}
          </a>
        </div>
      </div>
    );
  };
  const profileContent = renderProfileContent();

  // ── Settings Content ──
  const renderSettingsContent = (inModal = false) => (
    <div style={{ maxWidth: inModal ? "none" : 680, margin: inModal ? 0 : "0 auto" }}>
      {!inModal && <PageTitle sub={L.pages.sett.sub} title={L.pages.sett.title} />}
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:"18px 16px",marginBottom:12}}>
        <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:10}}>{L.sett.currency}</div>
        <select value={currency} onChange={e=>setCurrency(e.target.value)} style={{width:"100%",background:C.bg3,border:`1px solid ${C.border}`,borderRadius:8,padding:"9px 12px",color:C.white,fontFamily:"'Josefin Sans',sans-serif",fontSize:13,outline:"none",cursor:"pointer",appearance:"none",WebkitAppearance:"none",backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23888' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,backgroundRepeat:"no-repeat",backgroundPosition:"right 12px center",paddingRight:32}}>
          {[
            {s:"€",l:"EUR — Euro"},
            {s:"$",l:"USD — Dollar américain"},
            {s:"£",l:"GBP — Livre sterling"},
            {s:"¥",l:"JPY — Yen japonais"},
            {s:"Fr",l:"CHF — Franc suisse"},
            {s:"A$",l:"AUD — Dollar australien"},
            {s:"C$",l:"CAD — Dollar canadien"},
            {s:"NZ$",l:"NZD — Dollar néo-zélandais"},
            {s:"HK$",l:"HKD — Dollar de Hong Kong"},
            {s:"S$",l:"SGD — Dollar de Singapour"},
            {s:"NOK",l:"NOK — Couronne norvégienne"},
            {s:"SEK",l:"SEK — Couronne suédoise"},
            {s:"DKK",l:"DKK — Couronne danoise"},
            {s:"₹",l:"INR — Roupie indienne"},
            {s:"R$",l:"BRL — Real brésilien"},
            {s:"R",l:"ZAR — Rand sud-africain"},
            {s:"₺",l:"TRY — Livre turque"},
            {s:"₩",l:"KRW — Won sud-coréen"},
            {s:"zł",l:"PLN — Zloty polonais"},
            {s:"Mex$",l:"MXN — Peso mexicain"},
            {s:"AED",l:"AED — Dirham émirati"},
          ].map(({s,l})=>(
            <option key={s} value={s} style={{background:darkMode?"#1a1a1a":"#f0ede8",color:C.white}}>{s} — {l.split(" — ")[1]}</option>
          ))}
        </select>
      </div>
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:"18px 16px",marginBottom:12}}>
        <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:14}}>{L.sett.language}</div>
        <div style={{display:"flex",gap:8}}>
          {[{k:"fr",l:"Français"},{k:"en",l:"English"}].map(lg=>(
            <button key={lg.k} onClick={()=>setLang(lg.k)} style={{flex:1,padding:"10px",borderRadius:8,border:`1px solid ${lang===lg.k?C.accent:C.border}`,background:lang===lg.k?"rgba(0,0,0,0.08)":"transparent",color:lang===lg.k?C.accent:C.gray1,fontSize:12,cursor:"pointer",fontFamily:"'Josefin Sans',sans-serif",fontWeight:lang===lg.k?600:300,letterSpacing:"0.06em",transition:"all 0.2s"}}>
              {lg.l}
            </button>
          ))}
        </div>
      </div>
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:"18px 16px",marginBottom:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div>
            <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:4}}>{L.sett.appearance}</div>
            <div style={{fontSize:13,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif"}}>{darkMode?L.sett.dark:L.sett.light}</div>
          </div>
          <button onClick={()=>setDarkMode(d=>!d)} style={{width:52,height:28,borderRadius:14,border:"none",background:darkMode?"#f0ede8":"#ccc",cursor:"pointer",position:"relative",transition:"background 0.3s",flexShrink:0}}>
            <div style={{position:"absolute",top:3,left:darkMode?26:3,width:22,height:22,borderRadius:11,background:darkMode?"#111":"#fff",transition:"left 0.25s",boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}/>
          </button>
        </div>
        <div style={{height:1,background:C.border,margin:"0 -16px 14px"}}/>
      </div>

      {/* ── COULEURS ── */}
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:"18px 16px",marginBottom:12}}>
        <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:16}}>{L.sett.colors}</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:12,marginBottom:14}}>
          {[[L.sett.bg,customBg,setCustomBg,darkMode?"#0f0f0f":"#f8f7f5"],[L.sett.cards,customBg2,setCustomBg2,darkMode?"#1a1a1a":"#f0ede8"]].map(([label,val,setter,def])=>(
            <div key={label} style={{minWidth:0}}>
              <div style={{fontSize:10,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8}}>{label}</div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <input type="color" value={val||def} onChange={e=>setter(e.target.value)}
                  style={{width:34,height:34,borderRadius:7,border:`1px solid ${C.border}`,cursor:"pointer",padding:2,background:"transparent",flexShrink:0}} />
                <input type="text" value={val} placeholder={def}
                  onChange={e=>{const v=e.target.value;if(/^#[0-9a-fA-F]{0,6}$/.test(v)){setter(v);}}}
                  onBlur={e=>{if(!/^#[0-9a-fA-F]{6}$/.test(e.target.value)&&e.target.value!=="")setter("");}}
                  style={{flex:1,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:7,padding:"7px 10px",color:C.white,fontFamily:"'Josefin Sans',sans-serif",fontSize:12,letterSpacing:"0.08em",outline:"none",minWidth:0}} />
              </div>
            </div>
          ))}
        </div>
        <div style={{marginBottom:14}}>
          <div style={{fontSize:10,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8}}>{L.sett.textColor}</div>
          <div style={{display:"flex",gap:6}}>
            {[{v:null,l:L.sett.auto},{v:true,l:L.sett.white},{v:false,l:L.sett.black}].map(opt=>(
              <button key={String(opt.v)} onClick={()=>setCustomTextWhite(opt.v)} style={{flex:1,padding:"9px",borderRadius:8,border:`1px solid ${customTextWhite===opt.v?C.accent:C.border}`,background:customTextWhite===opt.v?"rgba(128,128,128,0.12)":"transparent",color:customTextWhite===opt.v?C.white:C.gray1,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",cursor:"pointer",transition:"all 0.2s"}}>
                {opt.l}
              </button>
            ))}
          </div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <button onClick={async()=>{
            setColorSaving("saving");
            const { error } = await saveUserSettings({ custom_bg:customBg||null, custom_bg2:customBg2||null, custom_text_white:customTextWhite });
            setColorSaving(error ? `error:${error.message}` : "ok");
            setTimeout(()=>setColorSaving(null),3000);
          }} style={{padding:"9px 20px",borderRadius:8,border:colorSaving&&colorSaving!=="ok"&&colorSaving!=="saving"?"1px solid rgba(229,100,100,0.4)":"none",background:colorSaving==="ok"?"rgba(74,222,128,0.1)":colorSaving==="saving"?"rgba(255,255,255,0.06)":colorSaving?`rgba(229,100,100,0.1)`:"#111",color:colorSaving==="ok"?clrGreen:colorSaving==="saving"?C.gray1:colorSaving?"rgba(229,100,100,0.9)":"#fff",fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,fontSize:12,cursor:colorSaving?"not-allowed":"pointer",transition:"all 0.2s",letterSpacing:"0.1em"}}>
            {colorSaving==="ok"?L.btn.saved:colorSaving==="saving"?L.btn.saving:colorSaving?L.btn.error:L.btn.save}
          </button>
          {(customBg||customBg2||customTextWhite!==null)&&(
            <button onClick={()=>{setCustomBg("");setCustomBg2("");setCustomTextWhite(null);}} style={{fontSize:11,color:"rgba(229,100,100,0.7)",fontFamily:"'Josefin Sans',sans-serif",background:"none",border:"1px solid rgba(229,100,100,0.2)",borderRadius:8,padding:"7px 16px",cursor:"pointer",transition:"all 0.2s"}}>
              {L.btn.reset}
            </button>
          )}
        </div>
      </div>

      {/* ── TRADE SETTINGS ── */}
      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:"18px 16px",marginBottom:12}}>
        <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:8}}>{L.acct.tradeSettings}</div>
        <div style={{fontSize:11,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",marginBottom:16,letterSpacing:"0.04em",lineHeight:1.7}}>Activez les champs à appliquer en mode <strong style={{color:C.white}}>Fixe</strong> dans l'onglet Trade.</div>
        {[
          {k:"tpFixed", label:"TP fixe", placeholder:"ex: 150"},
          {k:"slFixed", label:"SL fixe", placeholder:"ex: 75"},
          {k:"rrFixed", label:"R/R fixe", placeholder:"ex: 2"},
        ].map(f => (
          <div key={f.k} style={{marginBottom:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:tradeSettings[f.k].enabled?8:0}}>
              <span style={{fontSize:11,color:C.white,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.06em"}}>{f.label}</span>
              <button onClick={()=>setTS(f.k,{enabled:!tradeSettings[f.k].enabled})} style={{width:40,height:22,borderRadius:11,border:"none",background:tradeSettings[f.k].enabled?C.accent:"rgba(255,255,255,0.1)",cursor:"pointer",position:"relative",transition:"background 0.22s",flexShrink:0}}>
                <div style={{position:"absolute",top:2,left:tradeSettings[f.k].enabled?20:2,width:18,height:18,borderRadius:9,background:tradeSettings[f.k].enabled?(darkMode?"#111":"#fff"):"#ccc",transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
              </button>
            </div>
            {tradeSettings[f.k].enabled && (
              <input type="text" inputMode="decimal" placeholder={f.placeholder} value={tradeSettings[f.k].value} onChange={e=>setTS(f.k,{value:e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"")})} style={{...iStyle,fontSize:13}}/>
            )}
          </div>
        ))}
        <div style={{marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:tradeSettings.sizeFixed.enabled?8:0}}>
            <span style={{fontSize:11,color:C.white,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.06em"}}>Taille fixe</span>
            <button onClick={()=>setTS("sizeFixed",{enabled:!tradeSettings.sizeFixed.enabled})} style={{width:40,height:22,borderRadius:11,border:"none",background:tradeSettings.sizeFixed.enabled?C.accent:"rgba(255,255,255,0.1)",cursor:"pointer",position:"relative",transition:"background 0.22s",flexShrink:0}}>
              <div style={{position:"absolute",top:2,left:tradeSettings.sizeFixed.enabled?20:2,width:18,height:18,borderRadius:9,background:tradeSettings.sizeFixed.enabled?(darkMode?"#111":"#fff"):"#ccc",transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
            </button>
          </div>
          {tradeSettings.sizeFixed.enabled && (
            <div>
              <div style={{display:"flex",borderRadius:6,border:`1px solid ${C.border}`,overflow:"hidden",marginBottom:8,alignSelf:"flex-start",width:"fit-content"}}>
                {["contrats","lots"].map(u=>(
                  <button key={u} onClick={()=>setTS("sizeFixed",{unit:u})} style={{padding:"4px 12px",border:"none",background:tradeSettings.sizeFixed.unit===u?C.accent:"transparent",color:tradeSettings.sizeFixed.unit===u?(darkMode?"#111":"#fff"):C.gray1,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",fontWeight:tradeSettings.sizeFixed.unit===u?600:300,cursor:"pointer",letterSpacing:"0.08em",transition:"all 0.18s"}}>{u}</button>
                ))}
              </div>
              <input type="text" inputMode="decimal" placeholder={`ex: 1 ${tradeSettings.sizeFixed.unit}`} value={tradeSettings.sizeFixed.value} onChange={e=>setTS("sizeFixed",{value:e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,"")})} style={{...iStyle,fontSize:13}}/>
            </div>
          )}
        </div>
        <button onClick={saveTS} style={{width:"100%",padding:"12px",borderRadius:6,border:`1px solid ${C.border}`,background:tsSaved?C.accent:"transparent",color:tsSaved?(darkMode?"#111":"#fff"):C.dim,fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.18em",textTransform:"uppercase",cursor:"pointer",transition:"all 0.3s"}}>
          {tsSaved?L.btn.saved:`${L.btn.save} →`}
        </button>
      </div>

      <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:cShadow,padding:"18px 16px"}}>
        <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,marginBottom:4}}>Version</div>
        <div style={{fontSize:13,color:C.gray1,fontFamily:"'Josefin Sans',sans-serif"}}><img src={darkMode?"/fyltra-logo-black.svg":"/fyltra-logo-white.svg"} style={{height:32,width:"auto",verticalAlign:"middle"}} alt="Fyltra"/> v1.0 · Trading Journal</div>
        <div style={{fontSize:10,color:C.gray2,fontFamily:"'Josefin Sans',sans-serif",marginTop:4,letterSpacing:"0.06em"}}>Créé par Smile</div>
      </div>
    </div>
  );
  const settingsContent = renderSettingsContent();

  /* ── Fyltra Metrics ──────────────────────────────────────────── */
  const calcMetrics = (period = "30d") => {
    if (!trades.length) return { score:0, win_rate:0, profit_factor:0, consistency:0, risk_adjusted:0, trade_count:0 };
    const now = new Date();
    let filtered = trades;
    if (period === "7d")  { const d = new Date(now); d.setDate(d.getDate()-7);  filtered = trades.filter(t => new Date(t.date) >= d); }
    if (period === "30d") { const d = new Date(now); d.setDate(d.getDate()-30); filtered = trades.filter(t => new Date(t.date) >= d); }
    const trade_count = filtered.length;

    const wins      = filtered.filter(t => t.result === "WIN").length;
    const win_rate  = trade_count > 0 ? wins / trade_count : 0;

    const gross_win  = filtered.filter(t => (t.pnl||0) > 0).reduce((s,t) => s+(t.pnl||0), 0);
    const gross_loss = Math.abs(filtered.filter(t => (t.pnl||0) < 0).reduce((s,t) => s+(t.pnl||0), 0));
    const profit_factor = gross_loss > 0 ? gross_win / gross_loss : (gross_win > 0 ? 99 : 0);

    const weekCount = period === "7d" ? 1 : period === "30d" ? 4 : 8;
    let posWeeks = 0, totalWeeks = 0;
    for (let i = 0; i < weekCount; i++) {
      const wS = new Date(now); wS.setDate(wS.getDate()-(i+1)*7);
      const wE = new Date(now); wE.setDate(wE.getDate()-i*7);
      const wT = filtered.filter(t => { const d = new Date(t.date); return d >= wS && d < wE; });
      if (wT.length > 0) { totalWeeks++; if (wT.reduce((s,t) => s+(t.pnl||0),0) > 0) posWeeks++; }
    }
    const consistency = totalWeeks > 0 ? posWeeks / totalWeeks : 0;

    const totalPnl = filtered.reduce((s,t) => s+(t.pnl||0), 0);
    let peak = 0, runPnl = 0, maxDD = 0;
    for (const t of filtered) { runPnl += (t.pnl||0); if (runPnl > peak) peak = runPnl; const dd = peak - runPnl; if (dd > maxDD) maxDD = dd; }
    const risk_adjusted = maxDD > 0 ? totalPnl / maxDD : (totalPnl > 0 ? 99 : 0);

    const wrScore = Math.min(1, win_rate / 0.65);
    const noted = filtered.filter(t => t.notes && t.notes.trim().length > 10).length;
    const journalScore = trade_count > 0 ? noted / trade_count : 0;
    const pnls = filtered.map(t => t.pnl||0);
    let sharpeRaw = 0;
    if (pnls.length >= 3) { const mean = pnls.reduce((a,b)=>a+b,0)/pnls.length; const std = Math.sqrt(pnls.reduce((s,p)=>s+Math.pow(p-mean,2),0)/pnls.length); sharpeRaw = std > 0 ? mean/std : (mean > 0 ? 1 : 0); }
    const sharpeScore = Math.min(1, Math.max(0, (sharpeRaw+0.5)/2));
    const negEmotions = Object.entries(EMOTION_POLARITY).filter(([,v])=>v==="negative").map(([k])=>k);
    const negTrades = filtered.filter(t=>negEmotions.includes(t.emotion));
    const negLoss = negTrades.filter(t=>t.result==="LOSS").length;
    const emoScore = negTrades.length > 0 ? 1-(negLoss/negTrades.length) : 1;
    const activeDays = new Set(filtered.map(t=>t.date)).size;
    const regularityScore = Math.min(1, activeDays / (period==="7d"?5:15));
    const raw = consistency*250 + wrScore*200 + journalScore*200 + sharpeScore*150 + emoScore*100 + regularityScore*100;
    const score = Math.round(Math.min(1000, Math.max(0, raw)));
    return { score, win_rate, profit_factor, consistency, risk_adjusted, trade_count };
  };
  const calcFyltraScore = () => calcMetrics("30d").score;

  const getTier = score => score >= 850 ? "Diamant" : score >= 650 ? "Or" : score >= 400 ? "Argent" : "Bronze";
  const tierColor = tier => TIER_COLORS[tier] || "#c4a46b";
  const TIER_COLORS = { Diamant:"#7dd3fc", Or:"#e8cda9", Argent:"#cbd5e1", Bronze:"#c4a46b" };

  /* ── Leaderboard Content ──────────────────────────────────────── */
  const classementContent = (() => {
    const ff  = "'Josefin Sans',sans-serif";
    const accent = "#E8CDA9";
    const lbl = { fontSize:9, fontWeight:800, letterSpacing:"3px", textTransform:"uppercase", color:darkMode?"rgba(232,205,169,0.4)":"rgba(0,0,0,0.35)", fontFamily:ff };
    const TC = { Diamant:"#7FC9E8", Or:"#E8CDA9", Argent:"#B8C4D9", Bronze:"#C9A876" };
    const TIERS_DEF = [
      { key:"Bronze",  label:"Bronze",  range:"< 400",  color:TC.Bronze  },
      { key:"Argent",  label:"Argent",  range:"≥ 400",  color:TC.Argent  },
      { key:"Or",      label:"Or",      range:"≥ 650",  color:TC.Or      },
      { key:"Diamant", label:"Diamant", range:"≥ 850",  color:TC.Diamant },
    ];
    const PERIODS = [
      { key:"7d",  label: lang==="fr"?"7 jours":"7 days"  },
      { key:"30d", label: lang==="fr"?"30 jours":"30 days" },
      { key:"all", label: lang==="fr"?"Tout":"All time"   },
    ];
    const CRITERIA = [
      { key:"score",         label: lang==="fr"?"Score Global":"Global Score", col:"score",         fmt: v => String(Math.round(v||0)),       minTrades:5  },
      { key:"win_rate",      label:"Win Rate",      col:"win_rate",      fmt: v => `${Math.round((v||0)*100)}%`,  minTrades:10 },
      { key:"profit_factor", label:"Profit Factor", col:"profit_factor", fmt: v => (v||0).toFixed(2),            minTrades:5  },
      { key:"consistency",   label:"Consistency",   col:"consistency",   fmt: v => `${Math.round((v||0)*100)}%`, minTrades:5  },
      { key:"risk_adjusted", label:"Risk Adj.",     col:"risk_adjusted", fmt: v => (v||0).toFixed(2),            minTrades:5  },
    ];
    const activeCrit = CRITERIA.find(c => c.key === lbCriteria) || CRITERIA[0];
    const myMetrics  = calcMetrics(lbPeriod);
    const myScore    = myMetrics.score;
    const myTier     = getTier(myScore);
    const myTierColor = TC[myTier] || TC.Bronze;
    const myEligible = myMetrics.trade_count >= activeCrit.minTrades;

    // Progress toward next tier
    const tierProgress = (() => {
      if (myScore >= 850) return { pct:100, pts:0, next:"Diamant" };
      if (myScore >= 650) return { pct:(myScore-650)/200*100, pts:850-myScore, next:"Diamant" };
      if (myScore >= 400) return { pct:(myScore-400)/250*100, pts:650-myScore, next:"Or" };
      return { pct:myScore/400*100, pts:400-myScore, next:"Argent" };
    })();

    const SCORE_COMPONENTS = [
      { label:"Consistency", value:Math.round(myMetrics.consistency*250), max:250 },
      { label:"Win Rate",    value:Math.round(myMetrics.win_rate*200),    max:200 },
      { label:"Journal",     value:null,                                   max:200 },
      { label:"Sharpe",      value:null,                                   max:150 },
      { label:L.rank.emotions, value:null,                                 max:100 },
    ];

    const loadLeaderboard = async () => {
      setLbLoading(true);
      const m30 = calcMetrics("30d");
      if (compete && user) {
        const displayName = anonymous
          ? (lang==="fr"?"Anonyme":"Anonymous")
          : (profileForm.nickname || profileForm.firstName || user.email?.split("@")[0] || "Trader");
        const row = { user_id:user.id, display_name:displayName, score:m30.score, tier:getTier(m30.score), updated_at:new Date().toISOString(), win_rate:m30.win_rate, profit_factor:m30.profit_factor, consistency:m30.consistency, risk_adjusted:m30.risk_adjusted, trade_count:m30.trade_count };
        const { error: uErr } = await supabase.from("leaderboard").upsert(row, { onConflict:"user_id" });
        if (uErr) {
          await supabase.from("leaderboard").upsert({ user_id:user.id, display_name:displayName, score:m30.score, tier:getTier(m30.score), updated_at:new Date().toISOString() }, { onConflict:"user_id" });
        }
      }
      const sortCol = activeCrit.col;
      let { data: top10, error: fErr } = await supabase.from("leaderboard").select("*").order(sortCol, { ascending:false }).limit(20);
      if (fErr) {
        const { data } = await supabase.from("leaderboard").select("*").order("score", { ascending:false }).limit(20);
        top10 = data;
      }
      const eligible = (top10||[]).filter(e => e.trade_count === 0 || (e.trade_count||0) >= activeCrit.minTrades || e.user_id === user?.id).slice(0, 10);
      setLbData(eligible);
      if (compete && user) {
        const myInTop = eligible.findIndex(r => r.user_id === user.id);
        if (myInTop === -1) {
          const { count } = await supabase.from("leaderboard").select("*", { count:"exact", head:true }).gt(sortCol, myMetrics[lbCriteria]||0);
          setLbUserRank((count||0)+1);
        } else {
          setLbUserRank(myInTop+1);
        }
      }
      setLbLoading(false);
    };

    if (view === "classement" && lbData === null && !lbLoading && tradesLoaded) loadLeaderboard();
    const resetLb = () => { setLbData(null); setLbUserRank(null); };

    return (
      <div style={{ padding:"0 24px 24px" }}>

        {/* ── HEADER ── */}
        <div style={{ marginBottom:20 }}>
          <div style={lbl}>{L.pages.rank.sub}</div>
          <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:28, fontWeight:700, color:C.white, lineHeight:1.1, marginTop:4 }}>{L.pages.rank.title}</div>
        </div>

        {/* ── PERIOD PILLS ── */}
        <div style={{ display:"flex", gap:6, marginBottom:20 }}>
          {PERIODS.map(p => {
            const active = lbPeriod === p.key;
            return (
              <button key={p.key} onClick={() => { setLbPeriod(p.key); resetLb(); }} style={{ padding:"7px 16px", borderRadius:20, border:"none", background:active?accent:C.bg3, color:active?"#000":C.gray1, fontSize:11, fontFamily:ff, fontWeight:active?700:400, letterSpacing:"0.08em", cursor:"pointer", transition:"all 0.15s" }}>
                {p.label}
              </button>
            );
          })}
        </div>

        {/* ── MAIN GRID ── */}
        <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"380px 1fr", gap:14, alignItems:"start" }}>

          {/* ══ COLONNE GAUCHE — MON SCORE ══ */}
          <div style={{ background:C.bg2, borderRadius:14, padding:22, boxShadow:cShadow, position:"sticky", top:16 }}>

            {/* Label */}
            <div style={{ ...lbl, marginBottom:16 }}>
              {lang==="fr"?"MON SCORE FYLTRA":"MY FYLTRA SCORE"} · {PERIODS.find(p=>p.key===lbPeriod)?.label.toUpperCase()}
            </div>

            {/* Score géant */}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontFamily:ff, fontSize:64, fontWeight:700, letterSpacing:"-2px", color:myTierColor, lineHeight:1 }}>{myScore}</div>
              <div style={{ fontFamily:ff, fontSize:11, fontWeight:800, letterSpacing:"2px", textTransform:"uppercase", color:myTierColor, opacity:0.7, marginTop:6 }}>{myTier}</div>
            </div>

            {/* Progress bar vers tier suivant */}
            {myScore < 850 && (
              <div style={{ marginBottom:20 }}>
                <div style={{ height:3, background:darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.08)", borderRadius:2, overflow:"hidden", marginBottom:6 }}>
                  <div style={{ height:"100%", width:`${Math.min(100,tierProgress.pct)}%`, background:myTierColor, borderRadius:2, transition:"width 0.6s" }} />
                </div>
                <div style={{ fontFamily:ff, fontSize:10, color:C.gray1 }}>
                  {tierProgress.pts} pts → {tierProgress.next}
                </div>
              </div>
            )}

            {/* Composantes du score */}
            <div style={{ marginBottom:16 }}>
              {SCORE_COMPONENTS.map(({ label, value, max }) => (
                <div key={label} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 0", borderBottom:`1px solid ${C.border}` }}>
                  <div style={{ fontFamily:ff, fontSize:11, fontWeight:700, color:C.white, minWidth:80 }}>{label}</div>
                  <div style={{ width:60, height:3, background:C.bg3, borderRadius:2, overflow:"hidden", flexShrink:0 }}>
                    <div style={{ height:"100%", width:`${value!=null?Math.min(100,value/max*100):0}%`, background:accent, borderRadius:2, transition:"width 0.6s" }} />
                  </div>
                  <div style={{ fontFamily:ff, fontSize:11, fontWeight:700, color:C.white, minWidth:48, textAlign:"right" }}>
                    {value!=null?value:"—"}/{max}
                  </div>
                </div>
              ))}
            </div>

            {/* Stats clés */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
              {[
                { label:"Win Rate",      val:`${Math.round(myMetrics.win_rate*100)}%` },
                { label:"Profit Factor", val:myMetrics.profit_factor>=99?"—":myMetrics.profit_factor.toFixed(2) },
                { label:"Risk Adj.",     val:myMetrics.risk_adjusted>=99?"—":myMetrics.risk_adjusted.toFixed(2) },
              ].map(({ label, val }) => (
                <div key={label} style={{ background:C.bg3, borderRadius:10, padding:10 }}>
                  <div style={{ fontSize:7, fontWeight:800, letterSpacing:"1.5px", textTransform:"uppercase", color:C.gray2, fontFamily:ff, marginBottom:4 }}>{label}</div>
                  <div style={{ fontFamily:ff, fontSize:16, fontWeight:700, color:C.white }}>{val}</div>
                </div>
              ))}
            </div>

            {/* Alerts */}
            {!myEligible && (
              <div style={{ marginTop:14, padding:"8px 12px", background:darkMode?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.03)", borderRadius:8, border:`1px solid ${C.border}` }}>
                <div style={{ fontSize:10, color:C.gray1, fontFamily:ff, lineHeight:1.5 }}>
                  {lang==="fr"
                    ? `Minimum ${activeCrit.minTrades} trades requis. (${myMetrics.trade_count} trades)`
                    : `Minimum ${activeCrit.minTrades} trades required. (${myMetrics.trade_count} trades)`}
                </div>
              </div>
            )}
            {!compete && (
              <div style={{ marginTop:14, padding:"10px 12px", background:darkMode?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.03)", borderRadius:8, border:`1px solid ${C.border}` }}>
                <div style={{ fontSize:11, color:C.gray1, fontFamily:ff, lineHeight:1.5 }}>
                  {lang==="fr"
                    ? <>Active <span style={{ color:C.white, fontWeight:700 }}>Participer au classement</span> dans ton <button onClick={() => setView("profil")} style={{ background:"none", border:"none", cursor:"pointer", color:accent, fontFamily:ff, fontSize:11, padding:0, textDecoration:"underline" }}>Profil</button>.</>
                    : <>Enable <span style={{ color:C.white, fontWeight:700 }}>Join the rankings</span> in your <button onClick={() => setView("profil")} style={{ background:"none", border:"none", cursor:"pointer", color:accent, fontFamily:ff, fontSize:11, padding:0, textDecoration:"underline" }}>Profile</button>.</>}
                </div>
              </div>
            )}
          </div>

          {/* ══ COLONNE DROITE ══ */}
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

            {/* TIERS */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
              {TIERS_DEF.map(tier => {
                const isActive = myTier === tier.key;
                return (
                  <div key={tier.key} style={{ padding:14, borderRadius:14, background:C.bg2, border:`2px solid ${isActive?tier.color:C.border}`, boxShadow:isActive?`0 0 16px ${tier.color}26`:"none", transition:"all 0.2s" }}>
                    <div style={{ fontFamily:ff, fontSize:13, fontWeight:700, color:tier.color, marginBottom:4 }}>{tier.label}</div>
                    <div style={{ fontFamily:ff, fontSize:10, color:C.gray1 }}>{tier.range}</div>
                  </div>
                );
              })}
            </div>

            {/* SORT BY */}
            <div>
              <div style={{ ...lbl, marginBottom:8 }}>{lang==="fr"?"Trier par":"Sort by"}</div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {CRITERIA.map(c => {
                  const active = lbCriteria === c.key;
                  return (
                    <button key={c.key} onClick={() => { setLbCriteria(c.key); resetLb(); }} style={{ padding:"7px 14px", borderRadius:20, border:"none", background:active?accent:C.bg3, color:active?"#000":C.gray1, fontSize:10, fontFamily:ff, fontWeight:active?700:400, letterSpacing:"0.08em", cursor:"pointer", transition:"all 0.15s", whiteSpace:"nowrap" }}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* LEADERBOARD */}
            <div style={{ background:C.bg2, borderRadius:14, overflow:"hidden", boxShadow:cShadow }}>
              <div style={{ padding:"16px 16px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div style={{ ...lbl }}>TOP 10 · {activeCrit.label.toUpperCase()}</div>
                <button onClick={resetLb} style={{ background:"none", border:"none", cursor:"pointer", color:C.gray1, fontSize:10, fontFamily:ff, fontWeight:300, letterSpacing:"0.05em" }}>↻ {lang==="fr"?"Actualiser":"Refresh"}</button>
              </div>

              {lbLoading ? (
                <div style={{ padding:"32px", textAlign:"center", color:C.gray1, fontSize:12, fontFamily:ff }}>
                  {lang==="fr"?"Chargement...":"Loading..."}
                </div>
              ) : !lbData || lbData.length === 0 ? (
                <div style={{ padding:"32px", textAlign:"center", color:C.gray1, fontSize:12, fontFamily:ff, lineHeight:1.6 }}>
                  {L.rank.noTraders.split("\n")[0]}<br/>{L.rank.noTraders.split("\n")[1]}
                </div>
              ) : (
                <>
                  {lbData.map((entry, idx) => {
                    const rank  = idx + 1;
                    const isMe  = entry.user_id === user?.id;
                    const tc    = TC[entry.tier] || TC.Bronze;
                    const val   = activeCrit.fmt(entry[activeCrit.col]);
                    const medals = ["🥇","🥈","🥉"];
                    return (
                      <div key={entry.user_id} style={{ display:"grid", gridTemplateColumns:"40px 1fr auto", gap:12, alignItems:"center", padding:"12px 16px", borderBottom:idx<lbData.length-1?`1px solid ${C.border}`:"none", background:isMe?"rgba(232,205,169,0.04)":"transparent", borderLeft:isMe?`2px solid ${accent}`:"2px solid transparent", transition:"background 0.15s" }}>
                        <div style={{ textAlign:"center" }}>
                          {rank <= 3
                            ? <span style={{ fontSize:18 }}>{medals[rank-1]}</span>
                            : <div style={{ width:26, height:26, borderRadius:"50%", background:C.bg3, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto" }}>
                                <span style={{ fontFamily:ff, fontSize:11, fontWeight:700, color:C.gray1 }}>{rank}</span>
                              </div>
                          }
                        </div>
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontFamily:ff, fontSize:13, fontWeight:700, color:isMe?C.white:C.dim, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                            {entry.display_name}{isMe?(lang==="fr"?" (moi)":" (me)"):""}
                          </div>
                          <div style={{ fontFamily:ff, fontSize:9, fontWeight:800, letterSpacing:"1px", textTransform:"uppercase", color:tc, opacity:0.6, marginTop:2 }}>{entry.tier}</div>
                        </div>
                        <div style={{ fontFamily:ff, fontSize:18, fontWeight:700, color:tc, textAlign:"right" }}>{val}</div>
                      </div>
                    );
                  })}

                  {/* Pinned own position when outside top 10 */}
                  {compete && lbUserRank && lbUserRank > lbData.length && (
                    <>
                      <div style={{ padding:"8px 16px", textAlign:"center", color:C.gray2, fontSize:11, fontFamily:ff }}>· · ·</div>
                      <div style={{ display:"grid", gridTemplateColumns:"40px 1fr auto", gap:12, alignItems:"center", padding:"12px 16px", background:"rgba(232,205,169,0.04)", borderTop:`1px solid ${C.border}`, borderLeft:`2px solid ${accent}` }}>
                        <div style={{ textAlign:"center" }}>
                          <div style={{ width:26, height:26, borderRadius:"50%", background:C.bg3, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto" }}>
                            <span style={{ fontFamily:ff, fontSize:11, fontWeight:700, color:C.gray1 }}>{lbUserRank}</span>
                          </div>
                        </div>
                        <div>
                          <div style={{ fontFamily:ff, fontSize:13, fontWeight:700, color:C.white }}>{profileForm.nickname||profileForm.firstName||(lang==="fr"?"Moi":"Me")}</div>
                          <div style={{ fontFamily:ff, fontSize:9, fontWeight:800, letterSpacing:"1px", textTransform:"uppercase", color:myTierColor, opacity:0.6, marginTop:2 }}>{myTier}</div>
                        </div>
                        <div style={{ fontFamily:ff, fontSize:18, fontWeight:700, color:myTierColor, textAlign:"right" }}>{activeCrit.fmt(myMetrics[lbCriteria]||0)}</div>
                      </div>
                    </>
                  )}

                  <div style={{ padding:16, textAlign:"center" }}>
                    <div style={{ fontFamily:ff, fontSize:9, fontWeight:300, color:C.gray2 }}>
                      {lang==="fr"?"Classement mis à jour toutes les 24h":"Ranking updated every 24h"}
                    </div>
                  </div>
                </>
              )}
            </div>

          </div>{/* fin colonne droite */}
        </div>{/* fin main grid */}
      </div>
    );
  })();

  // ── NOTES / JOURNAL ──────────────────────────────────────────────
  useEffect(() => {
    if (view !== "notes" || notesLoaded || !user) return;
    (async () => {
      const { data } = await supabase.from("session_notes").select("*").eq("user_id", user.id).order("date", { ascending: false });
      if (data) {
        setSessionNotes(data);
        if (data.length > 0) {
          setSelNoteId(data[0].id);
          setSelNoteDate(data[0].date);
          setNoteContent(data[0].content || "");
          setNoteImageUrl(data[0].image_url || null);
        }
      }
      setNotesLoaded(true);
    })();
  }, [view, notesLoaded, user]);

  const saveNote = async (id, content) => {
    if (!user || !id) return;
    setNoteSaving(true);
    const { data, error } = await supabase
      .from("session_notes")
      .update({ content })
      .eq("id", id)
      .select()
      .single();
    if (!error && data) {
      setSessionNotes(prev => prev.map(n => n.id === id ? { ...n, content } : n));
    }
    setNoteSaving(false);
    setNoteSaved(true);
    setTimeout(() => setNoteSaved(false), 2000);
  };

  const createNote = async () => {
    if (!user) return;
    const todayStr = new Date().toISOString().split("T")[0];
    const { data, error } = await supabase
      .from("session_notes")
      .insert({ user_id: user.id, date: todayStr, content: "", image_url: null })
      .select()
      .single();
    if (!error && data) {
      setSessionNotes(prev => [data, ...prev]);
      setSelNoteId(data.id);
      setSelNoteDate(todayStr);
      setNoteContent("");
      setNoteImageUrl(null);
    }
  };

  const deleteNote = async (id) => {
    const note = sessionNotes.find(n => n.id === id);
    if (!note) return;
    if (note.image_url) {
      const imgPath = note.image_url.split("/trade-screenshots/").pop();
      if (imgPath) await supabase.storage.from("trade-screenshots").remove([decodeURIComponent(imgPath)]);
    }
    await supabase.from("session_notes").delete().eq("id", id);
    const remaining = sessionNotes.filter(n => n.id !== id);
    setSessionNotes(remaining);
    if (remaining.length > 0) {
      setSelNoteId(remaining[0].id);
      setSelNoteDate(remaining[0].date);
      setNoteContent(remaining[0].content || "");
      setNoteImageUrl(remaining[0].image_url || null);
    } else {
      setSelNoteId(null);
      setSelNoteDate(new Date().toISOString().split("T")[0]);
      setNoteContent("");
      setNoteImageUrl(null);
    }
  };

  const uploadNoteImage = async (file) => {
    if (!user || !file || !selNoteId) return;
    setNoteImageUploading(true);
    try {
      if (noteImageUrl) {
        const oldPath = noteImageUrl.split("/trade-screenshots/").pop();
        if (oldPath) await supabase.storage.from("trade-screenshots").remove([decodeURIComponent(oldPath)]);
      }
      const ext = file.name.split(".").pop();
      const path = `${user.id}/notes/${selNoteId}/${Date.now()}.${ext}`;
      const { data: upData, error: upErr } = await supabase.storage.from("trade-screenshots").upload(path, file, { contentType: file.type, upsert: false });
      if (!upErr && upData) {
        const { data: { publicUrl } } = supabase.storage.from("trade-screenshots").getPublicUrl(upData.path);
        setNoteImageUrl(publicUrl);
        await supabase.from("session_notes").update({ image_url: publicUrl }).eq("id", selNoteId);
        setSessionNotes(prev => prev.map(n => n.id === selNoteId ? { ...n, image_url: publicUrl } : n));
      }
    } catch (e) { console.error("uploadNoteImage:", e.message); }
    setNoteImageUploading(false);
  };

  const deleteNoteImage = async () => {
    if (!noteImageUrl || !user || !selNoteId) return;
    const imgPath = noteImageUrl.split("/trade-screenshots/").pop();
    if (imgPath) await supabase.storage.from("trade-screenshots").remove([decodeURIComponent(imgPath)]);
    setNoteImageUrl(null);
    await supabase.from("session_notes").update({ image_url: null }).eq("id", selNoteId);
    setSessionNotes(prev => prev.map(n => n.id === selNoteId ? { ...n, image_url: null } : n));
  };

  const notesContent = (() => {
    const ff = "'Josefin Sans',sans-serif";
    const cardS = { background:C.bg2, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:cShadow };
    const todayStr = new Date().toISOString().split("T")[0];

    const handleNoteSelect = (note) => {
      setSelNoteId(note.id);
      setSelNoteDate(note.date);
      setNoteContent(note.content || "");
      setNoteImageUrl(note.image_url || null);
    };

    const handleDateChange = async (newDate) => {
      if (!newDate || !selNoteId) return;
      setSelNoteDate(newDate);
      await supabase.from("session_notes").update({ date: newDate }).eq("id", selNoteId);
      setSessionNotes(prev => prev.map(n => n.id === selNoteId ? { ...n, date: newDate } : n).sort((a,b) => b.date.localeCompare(a.date)));
    };

    const handleTextChange = (val) => {
      setNoteContent(val);
      if (noteTimerRef.current) clearTimeout(noteTimerRef.current);
      noteTimerRef.current = setTimeout(() => saveNote(selNoteId, val), 1200);
    };

    const fmtDate = (d) => {
      const dt = new Date(d + "T12:00:00");
      return dt.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { weekday:"short", day:"numeric", month:"short" });
    };

    const selectedNote = sessionNotes.find(n => n.id === selNoteId);

    return (
      <div>
        <PageTitle sub={lang==="fr"?"Notes quotidiennes de session":"Daily session notes"} title={lang==="fr"?"Journal":"Journal"} />

        <div style={{ display:"flex", gap:14, flexDirection:isMobile?"column":"row", alignItems:"flex-start" }}>

          {/* Sidebar — note list */}
          <div style={{ width:isMobile?"100%":180, flexShrink:0 }}>
            <button
              onClick={createNote}
              style={{ width:"100%", marginBottom:8, padding:"10px 14px", borderRadius:10, border:`1px solid ${C.accent}`, background:"rgba(232,205,169,0.08)", color:C.accent, fontSize:11, fontFamily:ff, fontWeight:600, letterSpacing:"0.1em", cursor:"pointer", textAlign:"left", transition:"all 0.15s" }}
            >
              {lang==="fr"?"+ Nouvelle note":"+ New note"}
            </button>

            <div style={{ display:"flex", flexDirection:"column", gap:4, maxHeight:isMobile?200:520, overflowY:"auto" }}>
              {sessionNotes.map(n => (
                <button
                  key={n.id}
                  onClick={() => handleNoteSelect(n)}
                  style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:`1px solid ${selNoteId===n.id?C.accent:"transparent"}`, background:selNoteId===n.id?"rgba(232,205,169,0.06)":C.bg3, color:selNoteId===n.id?C.accent:C.gray1, fontSize:10, fontFamily:ff, cursor:"pointer", textAlign:"left", transition:"all 0.15s" }}
                >
                  <div style={{ fontWeight:600, marginBottom:2, color:selNoteId===n.id?C.accent:C.white, fontSize:11 }}>{fmtDate(n.date)}</div>
                  <div style={{ color:C.gray2, fontSize:9, overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis", maxWidth:"100%" }}>{n.content ? n.content.slice(0,40) + (n.content.length>40?"…":"") : "—"}</div>
                </button>
              ))}
              {sessionNotes.length === 0 && (
                <div style={{ fontSize:10, color:C.gray2, fontFamily:ff, padding:"8px 4px" }}>{lang==="fr"?"Aucune note encore.":"No notes yet."}</div>
              )}
            </div>
          </div>

          {/* Editor */}
          <div style={{ flex:1, ...cardS, padding:isMobile?"16px":"20px 22px", minHeight:400 }}>
            {/* Header */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
              <div>
                <div style={{ fontSize:9, color:C.dim, fontFamily:ff, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:600, marginBottom:3 }}>{lang==="fr"?"Session du":"Session —"}</div>
                <div style={{ position:"relative", display:"inline-block" }}>
                  <div style={{ color:C.white, fontFamily:ff, fontSize:15, fontWeight:600, pointerEvents:"none" }}>
                    {new Date(selNoteDate + "T12:00:00").toLocaleDateString(lang==="fr" ? "fr-FR" : "en-US", { day:"2-digit", month:"2-digit", year:"numeric" })}
                  </div>
                  <input
                    type="date"
                    value={selNoteDate}
                    max={todayStr}
                    onChange={e => { if(e.target.value) handleDateChange(e.target.value); }}
                    style={{ position:"absolute", inset:0, opacity:0, cursor:"pointer", width:"100%", height:"100%" }}
                  />
                </div>
              </div>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                {noteSaved && <span style={{ fontSize:10, color:clrGreen, fontFamily:ff }}>✓ {lang==="fr"?"Sauvegardé":"Saved"}</span>}
                {noteSaving && <span style={{ fontSize:10, color:C.gray2, fontFamily:ff }}>···</span>}
                {selectedNote && (
                  <button onClick={() => deleteNote(selNoteId)} style={{ padding:"5px 10px", borderRadius:8, border:`1px solid rgba(229,100,100,0.3)`, background:"transparent", color:"rgba(229,100,100,0.7)", fontSize:10, fontFamily:ff, cursor:"pointer" }}>
                    {lang==="fr"?"Supprimer":"Delete"}
                  </button>
                )}
                <button onClick={() => saveNote(selNoteId, noteContent)} disabled={noteSaving} style={{ padding:"7px 16px", borderRadius:8, border:"none", background:"#111", color:"#fff", fontSize:11, fontFamily:ff, fontWeight:700, cursor:"pointer", letterSpacing:"0.08em", opacity:noteSaving?0.6:1 }}>
                  {lang==="fr"?"Sauvegarder":"Save"}
                </button>
              </div>
            </div>

            {/* Textarea */}
            <textarea
              value={noteContent}
              onChange={e => handleTextChange(e.target.value)}
              placeholder={lang==="fr"?"Écris tes observations de session ici — contexte de marché, erreurs, réussites, émotions, plan pour demain...":"Write your session notes here — market context, mistakes, wins, emotions, plan for tomorrow..."}
              style={{ width:"100%", minHeight:340, background:"transparent", border:"none", outline:"none", resize:"vertical", color:C.white, fontFamily:ff, fontSize:13, lineHeight:1.9, letterSpacing:"0.02em", boxSizing:"border-box", caretColor:C.accent }}
            />

            {/* Image zone */}
            <div style={{ marginTop:14, paddingTop:14, borderTop:`1px solid ${C.border}` }}>
              {canUseNoteImg ? (
                noteImageUrl ? (
                  <div style={{ position:"relative", display:"inline-block" }}>
                    <img
                      src={noteImageUrl}
                      alt="note"
                      onClick={() => setLightboxUrl(noteImageUrl)}
                      style={{ maxWidth:"100%", maxHeight:200, borderRadius:8, border:`1px solid ${C.border}`, display:"block", cursor:"zoom-in", objectFit:"cover" }}
                    />
                    <button
                      onClick={deleteNoteImage}
                      style={{ position:"absolute", top:6, right:6, width:24, height:24, borderRadius:"50%", background:"rgba(0,0,0,0.65)", border:"none", color:"#fff", fontSize:13, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}
                    >✕</button>
                  </div>
                ) : (
                  <label style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"8px 14px", borderRadius:8, border:`1px dashed ${C.border}`, cursor: noteImageUploading ? "default" : "pointer", color:C.gray1, fontSize:11, fontFamily:ff, transition:"border-color 0.15s" }}>
                    <input type="file" accept="image/*" style={{ display:"none" }} onChange={e => { if (e.target.files[0]) uploadNoteImage(e.target.files[0]); e.target.value = ""; }} disabled={noteImageUploading} />
                    {noteImageUploading ? "···" : (lang==="fr" ? "+ Ajouter une image" : "+ Add image")}
                  </label>
                )
              ) : (
                <div style={{ fontSize:10, color:C.dim, fontFamily:ff }}>
                  {lang==="fr" ? "Images disponibles avec Fyltra+ et Fyltra Pro" : "Images available with Fyltra+ and Fyltra Pro"}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  })();

  const homeContent = (() => {
    const ff = "'Josefin Sans',sans-serif";
    const cardS = { background:C.bg2, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:cShadow };
    const kpiCardBg = (color) => darkMode ? `linear-gradient(135deg, ${color}12 0%, ${C.bg2} 55%)` : C.bg2;
    const kpiBorder = (color) => darkMode ? {} : { borderTop:`2px solid ${color}` };
    const todayStr = new Date().toISOString().split("T")[0];
    const todayTrades = activeOnlyTrades.filter(t => t.date === todayStr);
    const todayPnl = todayTrades.reduce((s,t) => s+(t.pnl||0), 0);

    const now = new Date();
    const dow = (now.getDay()+6)%7;
    const weekStart = new Date(now); weekStart.setDate(now.getDate()-dow); weekStart.setHours(0,0,0,0);
    const weekTrades = activeOnlyTrades.filter(t => new Date(t.date+"T12:00:00") >= weekStart);
    const weekPnl = weekTrades.reduce((s,t) => s+(t.pnl||0), 0);

    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthTrades = activeOnlyTrades.filter(t => new Date(t.date+"T12:00:00") >= monthStart);
    const monthPnl = monthTrades.reduce((s,t) => s+(t.pnl||0), 0);

    const recentTrades = [...activeOnlyTrades].sort((a,b) => b.date.localeCompare(a.date)||((b.createdAt||"").localeCompare(a.createdAt||""))).slice(0,5);
    const activePfs = propfirms.filter(p=>!p.status||p.status==="active");

    const wTrades = weekTrades;
    const wPnl = weekPnl;
    const wWr = wTrades.length ? Math.round(wTrades.filter(t=>t.result==="WIN").length/wTrades.length*100) : 0;
    const wCnt = wTrades.length;
    const goals = weeklyGoals;

    // Equity curve — global, un point par trade
    const sortedForEq = [...activeOnlyTrades].sort((a,b)=>a.date.localeCompare(b.date)||((a.createdAt||"").localeCompare(b.createdAt||"")));
    const equityCurve = [{ label:"", v:0, pnl:0, date:"", instrument:"" }];
    sortedForEq.forEach((t,i)=>{
      const prev = equityCurve[equityCurve.length-1].v;
      equityCurve.push({ label:String(i+1), v:parseFloat((prev+(t.pnl||0)).toFixed(2)), pnl:t.pnl||0, date:t.date, instrument:t.instrument||"" });
    });
    const equityCurveEnd = equityCurve[equityCurve.length-1].v;
    const equityPositive = equityCurveEnd >= 0;
    const eqVals = equityCurve.map(d=>d.v);
    const eqMax = Math.max(...eqVals);
    const eqMin = Math.min(...eqVals);
    const zeroPct = eqMax === eqMin ? 50 : Math.min(100, Math.max(0, Math.round(eqMax / (eqMax - eqMin) * 100)));

    // KPIs globaux
    const totalPnl = activeOnlyTrades.reduce((s,t)=>s+(t.pnl||0),0);
    const totalWR = activeOnlyTrades.length ? Math.round(activeOnlyTrades.filter(t=>t.result==="WIN").length/activeOnlyTrades.length*100) : null;
    const grossWin = activeOnlyTrades.filter(t=>t.result==="WIN").reduce((s,t)=>s+(t.pnl||0),0);
    const grossLoss = Math.abs(activeOnlyTrades.filter(t=>t.result==="LOSS").reduce((s,t)=>s+(t.pnl||0),0));
    const profitFactor = grossLoss > 0 ? grossWin/grossLoss : (grossWin>0 ? null : 0);
    const rrTrades = activeOnlyTrades.filter(t=>parseFloat(t.rr)>0);
    const avgRRNum = rrTrades.length ? rrTrades.reduce((s,t)=>s+parseFloat(t.rr),0)/rrTrades.length : null;
    const bestTrade = activeOnlyTrades.reduce((best,t)=>(!best||t.pnl>best.pnl)?t:best, null);
    const streak = (() => { let s=0; for(const t of [...activeOnlyTrades].sort((a,b)=>b.date.localeCompare(a.date)||((b.createdAt||"").localeCompare(a.createdAt||"")))) { if(t.result==="WIN") s++; else break; } return s; })();

    // Trading Health 0-100
    const wrScore = Math.min((totalWR||0)/65, 1);
    const pfScore = profitFactor != null ? Math.min(profitFactor/2.5, 1) : 0;
    const rrScore = avgRRNum != null ? Math.min(avgRRNum/2.0, 1) : 0;
    const fyltaScore = activeOnlyTrades.length >= 5 ? Math.round(wrScore*35 + pfScore*40 + rrScore*25) : null;
    const scoreColor = fyltaScore==null?"#555":fyltaScore>=70?clrGreen:fyltaScore>=45?C.accent:clrRed;
    const scoreLabel = lang==="fr"
      ? (fyltaScore==null?"—":fyltaScore>=70?"Performance excellente":fyltaScore>=45?"En progression":fyltaScore>=25?"A améliorer":"Critique")
      : (fyltaScore==null?"—":fyltaScore>=70?"Excellent performance":fyltaScore>=45?"Progressing":fyltaScore>=25?"Needs work":"Critical");

    // Fyltra Score 0-1000
    const fyltaScore1000 = activeOnlyTrades.length >= 5 ? calcFyltraScore() : null;
    const fyltaTier = fyltaScore1000 != null ? getTier(fyltaScore1000) : null;
    const TC_HOME = { Diamant:"#7dd3fc", Or:"#e8cda9", Argent:"#cbd5e1", Bronze:"#c4a46b" };
    const fyltaTierLabel = fyltaTier == null ? "—" : lang === "fr"
      ? fyltaTier
      : ({ Diamant:"Diamond", Or:"Gold", Argent:"Silver", Bronze:"Bronze" }[fyltaTier] || fyltaTier);
    const fyltaTierColor = fyltaTier ? TC_HOME[fyltaTier] : "#555";
    const m30Home = fyltaScore1000 != null ? calcMetrics("30d") : null;
    const m30Wins = m30Home ? Math.round(m30Home.win_rate * m30Home.trade_count) : 0;
    const m30Losses = m30Home ? m30Home.trade_count - m30Wins : 0;

    // Fyltragram axes for home Fyltra Score card
    const fHomeCalc = (() => {
      const trd = activeOnlyTrades; const N = trd.length;
      if (N < 5) return null;
      const bd = {}; trd.forEach(t => { if (!t.date) return; (bd[t.date]=bd[t.date]||[]).push(t); });
      const days = Object.values(bd);
      const tpdArr = days.map(d=>d.length).sort((a,b)=>a-b);
      const thresh = Math.max(5,(tpdArr[Math.floor(tpdArr.length/2)]||1)*2);
      const disc = Math.min(100,Math.max(0,Math.round(100-(days.filter(d=>d.length>thresh).length/Math.max(days.length,1))*35-(1-trd.filter(t=>t.session).length/N)*25)));
      const eMap={...EMOTION_POLARITY}; extraEmotions.forEach(e=>{if(typeof e==='object'&&e.polarity)eMap[e.label]=e.polarity;});
      const wE=trd.filter(t=>t.emotion); const anx=wE.filter(t=>eMap[t.emotion]==='negative').length;
      const emot=wE.length>0?Math.round((1-anx/wE.length)*100):75;
      const wRR=trd.filter(t=>t.rr>0); const wN=trd.filter(t=>t.notes&&t.notes.trim().length>10);
      const pat=Math.min(100,Math.round(wRR.length>0?(wRR.filter(t=>t.rr>=1).length/wRR.length)*100*0.7+(wN.length/N)*100*0.3:50+(wN.length/N)*50));
      const srt=[...trd].sort((a,b)=>(a.date||'').localeCompare(b.date||'')||(a.entryTime||'').localeCompare(b.entryTime||''));
      let resT=0,resG=0; for(let i=0;i<srt.length;i++){if(srt[i].result!=='LOSS')continue;resT++;if(i+1<srt.length){const nx=srt[i+1];if(nx.date!==srt[i].date||nx.result==='WIN')resG++;}else resG++;}
      const res=resT>0?Math.round((resG/resT)*100):85;
      const dpnl=days.map(d=>d.reduce((s,t)=>s+(t.pnl||0),0));
      const mean=dpnl.reduce((s,v)=>s+v,0)/Math.max(dpnl.length,1);
      const std=Math.sqrt(dpnl.reduce((s,v)=>s+Math.pow(v-mean,2),0)/Math.max(dpnl.length,1));
      const mAbs=dpnl.reduce((s,v)=>s+Math.abs(v),0)/Math.max(dpnl.length,1);
      const cons=mAbs>0?Math.min(100,Math.max(0,Math.round(100-(std/mAbs)*35))):60;
      const wSL=trd.filter(t=>t.sl>0||t.rr>0).length;
      const avgRRv=wRR.length>0?wRR.reduce((s,t)=>s+t.rr,0)/wRR.length:0;
      const cap=parseFloat(capital)||10000;
      const maxDL=Math.max(...days.map(d=>d.filter(t=>t.result==='LOSS').reduce((s,t)=>s+Math.abs(t.pnl||0),0)),0);
      const risk=Math.min(100,Math.round((wSL/N)*100*0.4+Math.min(100,avgRRv*50)*0.3+(maxDL<cap*0.02?100:maxDL<cap*0.05?70:40)*0.3));
      return{discipline:disc,emotional:emot,patience:pat,resilience:res,consistency:cons,risk};
    })();
    const fHomeAxes = fHomeCalc ? (lang==='fr'
      ?[{key:'discipline',label:'DISCIPLINE'},{key:'emotional',label:'ÉMOTIONS'},{key:'patience',label:'PATIENCE'},{key:'resilience',label:'RÉSILIENCE'},{key:'consistency',label:'RÉGULARITÉ'},{key:'risk',label:'RISQUE'}]
      :[{key:'discipline',label:'DISCIPLINE'},{key:'emotional',label:'CONTROL'},{key:'patience',label:'PATIENCE'},{key:'resilience',label:'RESILIENCE'},{key:'consistency',label:'CONSISTENCY'},{key:'risk',label:'RISK'}]
    ).map(a=>({...a,value:fHomeCalc[a.key]})) : null;

    // Win Rate gauge (demi-cercle SVG)
    const gaugeR = 38;
    const gaugeCirc = Math.PI * gaugeR;
    const gaugeDash = gaugeCirc * (1 - (totalWR||0)/100);
    const gaugeColor = (totalWR||0)>=60?clrGreen:(totalWR||0)>=45?C.accent:clrRed;

    // Score donut SVG
    const scoreR = 46;
    const scoreCirc = 2 * Math.PI * scoreR;
    const scoreDash = fyltaScore != null ? scoreCirc * (1 - fyltaScore/100) : scoreCirc;

    // Meilleur instrument
    const instrMap = {};
    trades.forEach(t => {
      if (!t.instrument) return;
      if (!instrMap[t.instrument]) instrMap[t.instrument] = { pnl:0, wins:0, total:0 };
      instrMap[t.instrument].pnl += t.pnl||0;
      instrMap[t.instrument].total++;
      if (t.result==="WIN") instrMap[t.instrument].wins++;
    });
    const topInstrs = Object.entries(instrMap)
      .sort((a,b) => b[1].pnl - a[1].pnl)
      .slice(0, isMobile?3:4)
      .map(([name, v]) => ({ name, pnl:v.pnl, wr:Math.round(v.wins/v.total*100), total:v.total }));

    // Carousel data
    const last30dates = Array.from({length:30},(_,i)=>{ const d=new Date(now); d.setDate(d.getDate()-(29-i)); return d.toISOString().split("T")[0]; });
    const last30set = new Set(last30dates);
    const radarWR = Math.round(Math.min((totalWR||0)/80*100, 100));
    const radarPF = profitFactor != null ? Math.round(Math.min(profitFactor/2.5*100, 100)) : 0;
    const radarRR = avgRRNum != null ? Math.round(Math.min(avgRRNum/2.5*100, 100)) : 0;
    const tradingDays30 = [...new Set(trades.filter(t=>last30set.has(t.date)).map(t=>t.date))];
    const winDays30 = tradingDays30.filter(d => trades.filter(t=>t.date===d).reduce((s,t)=>s+(t.pnl||0),0) > 0).length;
    const radarConsistency = tradingDays30.length ? Math.round(winDays30/tradingDays30.length*100) : 0;
    const avgTradesPerDay = tradingDays30.length ? trades.filter(t=>last30set.has(t.date)).length/tradingDays30.length : 0;
    const radarDiscipline = Math.round(Math.max(0, Math.min(100, (1 - Math.max(0,avgTradesPerDay-3)/6)*100)));
    const radarData = [
      { axis: "Win Rate", v: radarWR },
      { axis: "Profit Factor", v: radarPF },
      { axis: "R/R", v: radarRR },
      { axis: lang==="fr"?"Régularité":"Consistency", v: radarConsistency },
      { axis: "Discipline", v: radarDiscipline },
    ];

    const sessionMap = {};
    trades.forEach(t => {
      const s = t.session || "—";
      if (!sessionMap[s]) sessionMap[s] = { pnl:0, wins:0, total:0 };
      sessionMap[s].pnl += t.pnl||0;
      sessionMap[s].total++;
      if (t.result==="WIN") sessionMap[s].wins++;
    });
    const sessionData = Object.entries(sessionMap)
      .sort((a,b) => b[1].pnl - a[1].pnl)
      .map(([s, v]) => ({ name:s, pnl:Number(v.pnl.toFixed(2)), wr: v.total ? Math.round(v.wins/v.total*100) : 0 }));

    const pnlVals = trades.map(t=>t.pnl||0);
    const pnlMin2 = Math.min(...pnlVals, -1);
    const pnlMax2 = Math.max(...pnlVals, 1);
    const bSize = (pnlMax2 - pnlMin2) / 8;
    const distData = Array.from({length:8}, (_,i) => {
      const lo = pnlMin2 + i*bSize;
      const hi = lo + bSize;
      const count = pnlVals.filter(v => i===7 ? v>=lo&&v<=hi : v>=lo&&v<hi).length;
      return { label:`${lo>=0?"+":""}${Math.round(lo)}`, count, pos: lo>=0 };
    });

    const now6m = new Date();
    const monthlyPnl = Array.from({length:6}, (_,i) => {
      const d = new Date(now6m.getFullYear(), now6m.getMonth()-5+i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
      const pnl = trades.filter(t=>t.date&&t.date.startsWith(key)).reduce((s,t)=>s+(t.pnl||0),0);
      return { name: d.toLocaleDateString(lang==="fr"?"fr-FR":"en-US",{month:"short"}), pnl: Number(pnl.toFixed(2)) };
    });

    const wrC2 = (totalWR||0)>=60?clrGreen:(totalWR||0)>=45?"#f59e0b":clrRed;
    const pfC = profitFactor==null?"#fcd34d":profitFactor>=1.5?clrGreen:profitFactor>=1?"#f59e0b":clrRed;
    const rrC = avgRRNum==null?C.gray2:avgRRNum>=2?clrGreen:avgRRNum>=1?"#f59e0b":clrRed;
    const Ring = (v, max, color, size, stroke, label) => {
      const r = (size - stroke) / 2;
      const circ = 2 * Math.PI * r;
      const dash = circ * (1 - Math.min(v, max) / max);
      return (
        <svg width={size} height={size} style={{ transform:"rotate(-90deg)", flexShrink:0 }}>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={darkMode?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.07)"} strokeWidth={stroke}/>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
            strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={dash}
            style={{ transition:"stroke-dashoffset 0.6s ease" }}/>
          <text x={size/2} y={size/2+1} textAnchor="middle" dominantBaseline="middle"
            style={{ transform:`rotate(90deg)`, transformOrigin:`${size/2}px ${size/2}px` }}
            fill={darkMode?"#fff":"#0f0f0f"} fontSize={size>=72?15:size>=54?12:10} fontFamily={ff} fontWeight="700">
            {label}
          </text>
        </svg>
      );
    };

    return (
      <div>

        {/* Prop firm alerts */}
        {activePfs.length > 0 && (() => {
          const alerts = activePfs.flatMap(pf => {
            const pfTrades = trades.filter(t => !t.accountIds||t.accountIds.length===0||t.accountIds.includes(pf.id));
            const allPnl = pfTrades.reduce((s,t)=>s+(t.pnl||0),0);
            const maxLoss = parseFloat(pf.maxLoss)||0;
            const target = parseFloat(pf.target)||0;
            const dTrades = pfTrades.filter(t=>t.date===todayStr);
            const dPnl = dTrades.reduce((s,t)=>s+(t.pnl||0),0);
            const dl = parseFloat(pf.dailyLoss)||0;
            const res = [];
            if (dl>0 && dPnl<0 && Math.abs(dPnl)/dl > 0.7) res.push({ type:"danger", msg:`${pf.firm||pf.name} — Daily loss à ${Math.round(Math.abs(dPnl)/dl*100)}%` });
            if (maxLoss>0 && allPnl<0 && Math.abs(allPnl)/maxLoss > 0.7) res.push({ type:"warning", msg:`${pf.firm||pf.name} — Max loss à ${Math.round(Math.abs(allPnl)/maxLoss*100)}%` });
            if (target>0 && parseFloat(pf.capital||0)>0 && allPnl>0 && allPnl/target > 0.9) res.push({ type:"insight", msg:`${pf.firm||pf.name} — Profit target à ${Math.round(allPnl/target*100)}%` });
            return res;
          });
          if (!alerts.length) return null;
          const cl = { danger:"rgba(248,113,113,0.1)", warning:"rgba(232,205,169,0.08)", insight:"rgba(74,222,128,0.08)" };
          const bc = { danger:"rgba(248,113,113,0.28)", warning:"rgba(232,205,169,0.22)", insight:"rgba(74,222,128,0.22)" };
          return (
            <div style={{ marginBottom:10 }}>
              {alerts.map((a,i) => (
                <div key={i} style={{ ...cardS, padding:"10px 14px", marginBottom:6, background:cl[a.type], border:`1px solid ${bc[a.type]}`, display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ width:5, height:5, borderRadius:"50%", background:a.type==="danger"?clrRed:a.type==="warning"?C.accent:clrGreen, flexShrink:0 }}/>
                  <span style={{ fontSize:11, color:C.white, fontFamily:ff }}>{a.msg}</span>
                </div>
              ))}
            </div>
          );
        })()}

        {trades.length === 0 ? (
          (() => {
            const ffT = "'Outfit', sans-serif";
            const ffM = "'JetBrains Mono', monospace";
            const displayName = profileForm.nickname || profileForm.firstName || user?.email?.split("@")[0] || "Trader";
            const strat0    = strategies[0] || {};
            const step1Done = !!(profileForm.firstName || profileForm.nickname);
            const step2Done = propfirms.length > 0;
            const step3Done = !!(strat0.description || (strat0.steps?.length > 0) || strat0.rules);
            const step4Done = false;
            const doneCount = [step1Done, step2Done, step3Done].filter(Boolean).length;
            const STEPS = [
              { num:"01", title:lang==="fr"?"Configure ton profil":"Set up your profile",   desc:lang==="fr"?"Prénom, pseudo, devise de référence":"First name, nickname, currency",   done:step1Done, locked:false,      action:()=>{ setShowUserModal(true); setUserModalTab("profil"); } },
              { num:"02", title:lang==="fr"?"Ajoute un compte":"Add an account",            desc:lang==="fr"?"Prop firm ou compte personnel":"Prop firm or personal account",          done:step2Done, locked:!step1Done, action:()=>setView("propfirm") },
              { num:"03", title:lang==="fr"?"Ajoute ta stratégie":"Add your strategy",      desc:lang==="fr"?"Décris ton approche de trading":"Describe your trading approach",        done:step3Done, locked:!step2Done, action:()=>setView("strategy") },
              { num:"04", title:lang==="fr"?"Log ton premier trade":"Log your first trade", desc:lang==="fr"?"Résultat, émotion, session…":"Result, emotion, session…",                done:step4Done, locked:!step3Done, action:()=>setView("add") },
            ];
            return (
              <div style={{ minHeight:"calc(100vh - 56px)", display:"flex", alignItems:"center", justifyContent:"center", padding:isMobile?"32px 20px":"48px 40px" }}>
              <div style={{ width:"100%", maxWidth:640 }}>

                {/* Header */}
                <div style={{ marginBottom:40 }}>
                  <div style={{ fontFamily:ff, fontWeight:800, fontSize:9, letterSpacing:"3px", textTransform:"uppercase", color:"rgba(232,205,169,0.4)", marginBottom:12 }}>
                    {lang==="fr" ? "PAR OÙ COMMENCER ?" : "WHERE TO START?"}
                  </div>
                  <div style={{ fontFamily:ffT, fontWeight:700, fontSize:isMobile?28:38, color:C.white, letterSpacing:"-0.5px", lineHeight:1.1, marginBottom:12 }}>
                    {lang==="fr" ? `Bienvenue, ${displayName}.` : `Welcome, ${displayName}.`}
                  </div>
                  <div style={{ fontFamily:ff, fontWeight:300, fontSize:13, color:C.gray1, lineHeight:1.6 }}>
                    {lang==="fr" ? "Ton journal est prêt. 4 étapes pour démarrer." : "Your journal is ready. 4 steps to get started."}
                  </div>
                </div>

                {/* Progress bar */}
                <div style={{ marginBottom:28 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                    <span style={{ fontFamily:ff, fontWeight:800, fontSize:8, letterSpacing:"3px", textTransform:"uppercase", color:"rgba(232,205,169,0.4)" }}>PROGRESSION</span>
                    <span style={{ fontFamily:ffM, fontSize:11, color:C.gray2 }}>{doneCount} / 4</span>
                  </div>
                  <div style={{ height:2, background:darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.07)", borderRadius:2, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${(doneCount/4)*100}%`, background:"#E8CDA9", borderRadius:2, transition:"width 0.4s ease" }}/>
                  </div>
                </div>

                {/* Steps */}
                <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                  {STEPS.map((step, i) => {
                    if (step.locked) return (
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:18, background:C.bg2, border:`1px solid ${C.border}`, borderRadius:16, padding:"20px 24px", opacity:0.3, pointerEvents:"none" }}>
                        <div style={{ width:40, height:40, borderRadius:12, background:"rgba(232,205,169,0.06)", border:"1px solid rgba(232,205,169,0.12)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <span style={{ fontFamily:ffM, fontWeight:700, fontSize:13, color:"rgba(232,205,169,0.5)" }}>{step.num}</span>
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontFamily:ff, fontWeight:700, fontSize:14, color:C.white, letterSpacing:"0.3px", marginBottom:4 }}>{step.title}</div>
                          <div style={{ fontFamily:ff, fontWeight:300, fontSize:12, color:C.gray2, letterSpacing:"0.2px" }}>{step.desc}</div>
                        </div>
                        <ArrowRight size={16} color="rgba(232,205,169,0.25)"/>
                      </div>
                    );
                    if (step.done) return (
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:18, background:C.bg2, border:"1px solid rgba(93,202,165,0.2)", borderRadius:16, padding:"20px 24px", boxShadow:cShadow }}>
                        <div style={{ width:40, height:40, borderRadius:12, background:"rgba(93,202,165,0.08)", border:"1px solid rgba(93,202,165,0.2)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <Check size={16} color="#5DCAA5"/>
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontFamily:ff, fontWeight:700, fontSize:14, color:"#5DCAA5", opacity:0.7, letterSpacing:"0.3px", marginBottom:4 }}>{step.title}</div>
                          <div style={{ fontFamily:ff, fontWeight:300, fontSize:12, color:C.gray2, letterSpacing:"0.2px" }}>{step.desc}</div>
                        </div>
                        <Check size={16} color="#5DCAA5" opacity={0.5}/>
                      </div>
                    );
                    return (
                      <div key={i} onClick={step.action}
                        onMouseEnter={e=>{ e.currentTarget.style.borderColor="rgba(232,205,169,0.2)"; e.currentTarget.style.background=darkMode?"#1f1f1f":"#ebe8e2"; }}
                        onMouseLeave={e=>{ e.currentTarget.style.borderColor=C.border; e.currentTarget.style.background=C.bg2; }}
                        style={{ display:"flex", alignItems:"center", gap:18, background:C.bg2, border:`1px solid ${C.border}`, borderRadius:16, padding:"20px 24px", boxShadow:cShadow, cursor:"pointer", transition:"border-color 0.15s, background 0.15s" }}>
                        <div style={{ width:40, height:40, borderRadius:12, background:"rgba(232,205,169,0.06)", border:"1px solid rgba(232,205,169,0.12)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <span style={{ fontFamily:ffM, fontWeight:700, fontSize:13, color:"rgba(232,205,169,0.5)" }}>{step.num}</span>
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontFamily:ff, fontWeight:700, fontSize:14, color:C.white, letterSpacing:"0.3px", marginBottom:4 }}>{step.title}</div>
                          <div style={{ fontFamily:ff, fontWeight:300, fontSize:12, color:C.gray2, letterSpacing:"0.2px" }}>{step.desc}</div>
                        </div>
                        <ArrowRight size={16} color="rgba(232,205,169,0.25)" style={{ transition:"transform 0.15s" }}/>
                      </div>
                    );
                  })}
                </div>

              </div>
              </div>
            );
          })()
        ) : (<>

          {/* ── MAGAZINE LAYOUT — 2 colonnes ── */}
          <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"58% 42%", gap:12, marginBottom:12 }}>

            {/* ── COLONNE GAUCHE ── */}
            <div style={{ display:"flex", flexDirection:"column", gap:10, paddingRight:isMobile?0:12, borderRight:isMobile?"none":`1px solid ${C.border}` }}>

              {/* Greeting + P&L */}
              {(() => { const hc = totalPnl>=0?clrGreen:clrRed; return (
              <div style={{ padding:"4px 4px 8px" }}>
                <div style={{ fontSize:9, color:C.dim, fontFamily:ff, letterSpacing:"0.2em", textTransform:"uppercase" }}>
                  {new Date().toLocaleDateString(lang==="fr"?"fr-FR":"en-US",{weekday:"long",day:"numeric",month:"long"})}
                </div>
                <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:12, marginTop:2, flexWrap:"wrap" }}>
                  <div style={{ fontSize:isMobile?18:22, fontFamily:ff, fontWeight:700, color:C.white, letterSpacing:"-0.01em" }}>
                    {lang==="fr"?"Bonjour,":"Hello,"} {profileForm.nickname||profileForm.firstName||"Trader"}
                  </div>
                  <div style={{ display:"flex", alignItems:"baseline", gap:10 }}>
                    <div style={{ fontSize:isMobile?32:48, fontFamily:ff, fontWeight:800, color:hc, lineHeight:1, letterSpacing:"-0.03em", textShadow:`0 0 40px ${hc}45, 0 0 14px ${hc}25` }}>
                      {fms(totalPnl)}{currency}
                    </div>
                    {streak>=3 && (
                      <div style={{ fontSize:9, color:"#fcd34d", background:"rgba(252,211,77,0.1)", border:"1px solid rgba(252,211,77,0.25)", borderRadius:20, padding:"3px 10px", fontFamily:ff, fontWeight:600, letterSpacing:"0.1em", textTransform:"uppercase" }}>
                        Streak {streak}W
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ fontSize:10, color:C.dim, fontFamily:ff, marginTop:6 }}>
                  {activeOnlyTrades.length} trades &nbsp;·&nbsp; {lang==="fr"?"Auj.":"Today"} {fms(todayPnl)}{currency}
                </div>
              </div>
              ); })()}

              {/* Equity curve */}
              <div className="ap-stat" style={{ "--sd":"0ms", ...cardS, padding:"16px 18px 12px", position:"relative", overflow:"hidden" }}>
                <div style={{ position:"absolute", inset:0, background:equityPositive?`linear-gradient(135deg,${clrGreen}06 0%,transparent 60%)`:`linear-gradient(135deg,${clrRed}06 0%,transparent 60%)`, pointerEvents:"none" }}/>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                  <div>
                    <div style={{ fontSize:9, color:C.dim, fontFamily:ff, fontWeight:600, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:3 }}>
                      {lang==="fr"?"Courbe d'équité — global":"Equity curve — all time"}
                    </div>
                    <div style={{ fontSize:22, fontFamily:ff, fontWeight:300, color:equityCurveEnd>=0?clrGreen:clrRed, letterSpacing:"-0.01em", lineHeight:1 }}>
                      {fms(equityCurveEnd)}{currency}
                    </div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    {bestTrade && <div style={{ fontSize:10, color:C.gray2, fontFamily:ff }}>Best <span style={{ color:C.white, fontWeight:600 }}>{fms(bestTrade.pnl)}{currency}</span></div>}
                    <div style={{ fontSize:10, color:C.gray2, fontFamily:ff, marginTop:2 }}>
                      {lang==="fr"?"Moy":"Avg"} <span style={{ color:C.white }}>{fms(trades.length?totalPnl/trades.length:0)}{currency}</span>
                    </div>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={isMobile?110:170}>
                  <AreaChart data={equityCurve} margin={{top:4,right:4,bottom:0,left:0}}>
                    <defs>
                      <linearGradient id="eqStroke2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset={`${zeroPct}%`} stopColor={clrGreen} stopOpacity={1}/>
                        <stop offset={`${zeroPct}%`} stopColor={clrRed}   stopOpacity={1}/>
                      </linearGradient>
                      <linearGradient id="eqFill2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"            stopColor={equityPositive?clrGreen:clrRed} stopOpacity={0.22}/>
                        <stop offset={`${zeroPct}%`} stopColor={equityPositive?clrGreen:clrRed} stopOpacity={0.08}/>
                        <stop offset={`${zeroPct}%`} stopColor={equityPositive?clrRed:clrGreen} stopOpacity={0.08}/>
                        <stop offset="100%"          stopColor={equityPositive?clrRed:clrGreen} stopOpacity={0.01}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="label" hide />
                    <YAxis tick={{ fontSize:8, fill:C.dim, fontFamily:ff }} tickLine={false} axisLine={false} tickFormatter={v=>fm(v)} width={44}/>
                    <ReferenceLine y={0} stroke={darkMode?"rgba(255,255,255,0.12)":"rgba(0,0,0,0.12)"} strokeWidth={1}/>
                    <Tooltip
                      content={({active,payload})=> active && payload?.length && payload[0].payload.date ? (
                        <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:6,padding:"7px 11px"}}>
                          <div style={{fontSize:9,color:C.dim,fontFamily:ff,marginBottom:2}}>{payload[0].payload.date}{payload[0].payload.instrument?" · "+payload[0].payload.instrument:""}</div>
                          <div style={{fontSize:12,color:payload[0].payload.pnl>=0?clrGreen:clrRed,fontFamily:ff,fontWeight:600}}>{fms(payload[0].payload.pnl)}{currency}</div>
                          <div style={{fontSize:10,color:C.dim,fontFamily:ff}}>{lang==="fr"?"Cumulé":"Total"} {fms(payload[0].value)}{currency}</div>
                        </div>
                      ) : null}
                    />
                    <Area type="monotone" dataKey="v" stroke="url(#eqStroke2)" strokeWidth={2} fill="url(#eqFill2)"
                      dot={{ r:2.5, fill:"url(#eqStroke2)", strokeWidth:0 }}
                      activeDot={{ r:5, fill:"url(#eqStroke2)", strokeWidth:0 }}
                      isAnimationActive={false}/>
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Instruments + Objectifs semaine côte à côte */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>

                {/* Instruments */}
                <div className="ap-stat" style={{ "--sd":"70ms", ...cardS, padding:"16px 18px" }}>
                  <div style={{ fontSize:9, color:C.dim, fontFamily:ff, fontWeight:600, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:12 }}>
                    {lang==="fr"?"Par instrument":"By instrument"}
                  </div>
                  {topInstrs.length > 0 ? (
                    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      {topInstrs.map(ins => {
                        const maxPnlInstr = Math.max(...topInstrs.map(x=>Math.abs(x.pnl)), 1);
                        const ic = ins.pnl>=0?clrGreen:clrRed;
                        return (
                          <div key={ins.name} style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <div style={{ minWidth:0, flex:1, fontSize:10, fontFamily:ff, fontWeight:600, color:C.white, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{ins.name}</div>
                            <div style={{ flex:2, height:4, background:darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)", borderRadius:3, overflow:"hidden" }}>
                              <div style={{ height:"100%", width:`${Math.abs(ins.pnl)/maxPnlInstr*100}%`, background:ic, borderRadius:3 }}/>
                            </div>
                            <div style={{ textAlign:"right", fontSize:10, color:ic, fontFamily:ff, fontWeight:600, flexShrink:0 }}>{fms(ins.pnl)}{currency}</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ fontSize:11, color:C.gray2, fontFamily:ff }}>{lang==="fr"?"Aucun trade":"No trades"}</div>
                  )}
                </div>

                {/* Objectifs semaine */}
                <div className="ap-stat" style={{ "--sd":"140ms", ...cardS, padding:"16px 18px" }}>
                {(goals.pnl||goals.wr||goals.trades) ? (<>
                    <div style={{ fontSize:9, color:C.dim, fontFamily:ff, fontWeight:600, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:10 }}>{lang==="fr"?"Objectifs semaine":"Weekly goals"}</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                      {[
                        goals.pnl    && { label:"PnL",         value:wPnl, target:parseFloat(goals.pnl),  fmt:v=>`${fms(v)}${currency}`, tFmt:v=>`${fm(v)}${currency}` },
                        goals.wr     && { label:"Win rate",     value:wWr,  target:parseFloat(goals.wr),   fmt:v=>`${v}%`, tFmt:v=>`${v}%` },
                        goals.trades && { label:lang==="fr"?"Trades max":"Max trades", value:wCnt, target:parseFloat(goals.trades), fmt:v=>`${v}`, tFmt:v=>`${v}`, invert:true },
                      ].filter(Boolean).map(g => {
                        const pct = g.target>0 ? Math.min(100,Math.max(0,(g.value/g.target)*100)) : 0;
                        const done = g.invert ? g.value<=g.target : g.value>=g.target;
                        return (
                          <div key={g.label}>
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                              <span style={{ fontSize:10, color:C.dim, fontFamily:ff, letterSpacing:"0.1em", textTransform:"uppercase" }}>{g.label}</span>
                              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                <span style={{ fontSize:12, color:done?clrGreen:C.white, fontFamily:ff }}>{g.fmt(g.value)}</span>
                                <span style={{ fontSize:9, color:C.gray2, fontFamily:ff }}>/ {g.tFmt(g.target)}</span>
                                {done && <span style={{ fontSize:9, color:clrGreen }}>✓</span>}
                              </div>
                            </div>
                            <div style={{ height:3, background:C.bg3, borderRadius:2 }}>
                              <div style={{ height:"100%", width:`${pct}%`, background:done?clrGreen:C.accent, borderRadius:2, transition:"width 0.4s" }}/>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>) : (
                    <button onClick={()=>setWGoalsEdit(true)} style={{ width:"100%", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:6, padding:"12px 0", background:"none", border:"none", cursor:"pointer" }}>
                      <div style={{ fontSize:9, color:C.dim, fontFamily:ff, fontWeight:600, letterSpacing:"0.15em", textTransform:"uppercase", width:"100%", marginBottom:4 }}>{lang==="fr"?"Objectifs semaine":"Weekly goals"}</div>
                      <div style={{ fontSize:11, color:C.gray1, fontFamily:ff }}>{lang==="fr"?"+ Définir mes objectifs":"+ Set my goals"}</div>
                    </button>
                  )}
                </div>

              </div>{/* fin grid instruments+objectifs */}

              {/* Comptes */}
              {propfirms.length > 0 && (
              <div className="ap-stat" style={{ "--sd":"200ms", ...cardS, padding:"16px 18px" }}>
                <div style={{ fontSize:9, color:C.dim, fontFamily:ff, fontWeight:600, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:12 }}>
                  {lang==="fr"?"Comptes":"Accounts"}
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {propfirms.filter(p=>p.step!==2).map(pf => {
                    const pfMatch = ALL_KNOWN_FIRMS.find(k=>k.name.toLowerCase()===(pf.firm||"").toLowerCase());
                    const pfTrades = trades.filter(t=>!t.accountIds||t.accountIds.length===0||t.accountIds.includes(pf.id));
                    const pfPnl = pfTrades.reduce((s,t)=>s+(t.pnl||0),0);
                    const capBase = parseFloat(pf.capital||0);
                    const cap = capBase + pfPnl;
                    const capColor = pfPnl >= 0 ? clrGreen : clrRed;
                    return (
                      <div key={pf.id} style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }} onClick={()=>{ setView("comptes"); setTimeout(()=>setSelectedAccount(pf),50); }}>
                        {pfMatch ? (
                          <img src={`https://www.google.com/s2/favicons?sz=128&domain=${pfMatch.domain}`} style={{ width:28, height:28, borderRadius:6, objectFit:"contain", flexShrink:0 }} alt="" onError={e=>e.target.style.display="none"}/>
                        ) : (
                          <div style={{ width:28, height:28, borderRadius:6, background:darkMode?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.06)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, color:C.dim, flexShrink:0 }}>◈</div>
                        )}
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:11, fontFamily:ff, fontWeight:600, color:C.white, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{pf.name||pf.firm||(lang==="fr"?"Fond propre":"Personal account")}</div>
                          {pf.firm && pf.name && <div style={{ fontSize:9, color:C.dim, fontFamily:ff }}>{pf.firm}</div>}
                        </div>
                        <div style={{ textAlign:"right", flexShrink:0 }}>
                          <div style={{ fontSize:12, fontFamily:ff, fontWeight:600, color:capColor }}>{cap.toLocaleString()}{currency}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              )}

              {/* Daily insight */}
              {(dailyPhraseLoading || dailyPhrase) && (
                <div className="ap-stat" style={{ "--sd":"290ms", ...cardS, padding:"16px 18px" }}>
                  <div style={{ fontSize:9, color:C.dim, fontFamily:ff, fontWeight:600, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:10 }}>
                    {lang==="fr"?"Analyse du jour":"Daily insight"}
                  </div>
                  {dailyPhraseLoading ? (
                    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                      <div style={{ height:7, background:darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)", borderRadius:4, width:"90%" }}/>
                      <div style={{ height:7, background:darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)", borderRadius:4, width:"65%" }}/>
                    </div>
                  ) : (
                    <div style={{ fontSize:12, color:C.gray1, fontFamily:ff, fontWeight:300, lineHeight:1.75, fontStyle:"italic" }}>
                      {dailyPhrase.split(/\n\n+/)[0]}
                    </div>
                  )}
                </div>
              )}

            </div>{/* fin colonne gauche */}

            {/* ── COLONNE DROITE ── */}
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>

              {/* Trading Health — triangle radar */}
              {(() => {
                const sz=170, cx=sz/2, cy=sz/2, R=56;
                const angDeg=[270,30,150];
                const angs=angDeg.map(d=>d*Math.PI/180);
                const pt=(a,r)=>[cx+r*Math.cos(a),cy+r*Math.sin(a)];
                const vals=[Math.round(wrScore*100),Math.round(pfScore*100),Math.round(rrScore*100)];
                const lbls=["WIN RATE","PROF. FACTOR","RISK/REWARD"];
                const dataPoly=angs.map((a,i)=>pt(a,(vals[i]/100)*R));
                const colV=v=>v>=70?'#4ade80':v>=40?'#fb923c':'#f87171';
                return (
                  <div className="ap-stat" style={{ "--sd":"30ms", ...cardS, padding:"12px 18px 14px", overflow:"visible", position:"relative" }}>
                    <div style={{ position:"absolute", top:0, left:0, right:0, height:1, background:`linear-gradient(90deg,transparent,${scoreColor}55,transparent)` }}/>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                      <div style={{ fontSize:8, color:C.dim, fontFamily:ff, fontWeight:700, letterSpacing:"0.25em", textTransform:"uppercase" }}>Trading Health</div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:22, fontWeight:800, color:scoreColor, fontFamily:ff, letterSpacing:"-0.02em", lineHeight:1 }}>{fyltaScore!=null?fyltaScore:"—"}</div>
                        <div style={{ fontSize:8, color:scoreColor, fontFamily:ff, fontWeight:600, marginTop:2, letterSpacing:"0.02em" }}>{scoreLabel}</div>
                      </div>
                    </div>
                    <div style={{ display:"flex", justifyContent:"center" }}>
                      <svg width={sz} height={sz} viewBox={`0 0 ${sz} ${sz}`} style={{ overflow:"visible" }}>
                        <defs>
                          <radialGradient id="th-rg" cx="50%" cy="50%" r="60%">
                            <stop offset="0%" stopColor={scoreColor} stopOpacity={0.45}/>
                            <stop offset="100%" stopColor={scoreColor} stopOpacity={0.1}/>
                          </radialGradient>
                        </defs>
                        {[0.33,0.66,1].map(l=>(
                          <polygon key={l} fill="none" stroke={darkMode?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.1)"} strokeWidth={0.75}
                            points={angs.map(a=>pt(a,R*l).join(',')).join(' ')}/>
                        ))}
                        {angs.map((a,i)=>{const[x2,y2]=pt(a,R);return<line key={i} x1={cx} y1={cy} x2={x2} y2={y2} stroke={darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.12)"} strokeWidth={0.75}/>;} )}
                        <polygon points={dataPoly.map(p=>p.join(',')).join(' ')}
                          fill="url(#th-rg)" stroke={scoreColor} strokeWidth={1.5} strokeOpacity={0.85}
                          style={{filter:`drop-shadow(0 0 6px ${scoreColor}44)`}}/>
                        {dataPoly.map((p,i)=>(
                          <circle key={i} cx={p[0]} cy={p[1]} r={3} fill={scoreColor} opacity={0.9}/>
                        ))}
                        {angs.map((a,i)=>{
                          const[lx,ly]=pt(a,R+26);
                          return(
                            <g key={i}>
                              <text x={lx} y={ly-6} textAnchor="middle" dominantBaseline="middle"
                                fill={darkMode?"rgba(255,255,255,0.35)":"rgba(0,0,0,0.45)"} fontSize={8}
                                fontFamily="'Josefin Sans',sans-serif" letterSpacing="0.6">{lbls[i]}</text>
                              <text x={lx} y={ly+9} textAnchor="middle" dominantBaseline="middle"
                                fill={colV(vals[i])} fontSize={13} fontWeight={700}
                                fontFamily="'Josefin Sans',sans-serif">{vals[i]}</text>
                            </g>
                          );
                        })}
                      </svg>
                    </div>
                  </div>
                );
              })()}

              {/* Fyltra Score — Fyltragram */}
              {(() => {
                if (!fHomeAxes) return (
                  <div className="ap-stat" style={{ "--sd":"60ms", ...cardS, padding:"18px 20px", overflow:"hidden", position:"relative" }}>
                    <div style={{ position:"absolute", top:0, left:0, right:0, height:1, background:`linear-gradient(90deg,transparent,${fyltaTierColor}55,transparent)` }}/>
                    <div style={{ fontSize:8, color:C.dim, fontFamily:ff, fontWeight:700, letterSpacing:"0.25em", textTransform:"uppercase", marginBottom:10 }}>Fyltra Score</div>
                    <div style={{ fontSize:9, color:C.dim, fontFamily:ff, textAlign:"center", padding:"20px 0" }}>Min. 5 trades</div>
                  </div>
                );
                return (
                  <div className="ap-stat" style={{ "--sd":"60ms", ...cardS, padding:"12px 18px 14px", overflow:"visible", position:"relative" }}>
                    <div style={{ position:"absolute", top:0, left:0, right:0, height:1, background:`linear-gradient(90deg,transparent,${fyltaTierColor}55,transparent)` }}/>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                      <div style={{ fontSize:8, color:C.dim, fontFamily:ff, fontWeight:700, letterSpacing:"0.25em", textTransform:"uppercase" }}>Fyltra Score</div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:11, color:fyltaTierColor, fontFamily:ff, fontWeight:700, letterSpacing:"0.08em", lineHeight:1 }}>{fyltaTierLabel?.toUpperCase()}</div>
                        <div style={{ display:"flex", alignItems:"baseline", gap:3, justifyContent:"flex-end", marginTop:3 }}>
                          <span style={{ fontSize:22, fontWeight:800, color:fyltaTierColor, fontFamily:ff, letterSpacing:"-0.03em", lineHeight:1 }}>{fyltaScore1000}</span>
                          <span style={{ fontSize:9, color:C.dim, fontFamily:ff }}>/1000</span>
                        </div>
                        <div style={{ fontSize:8, color:C.dim, fontFamily:ff, marginTop:2 }}>{m30Wins}W · {m30Losses}L · 30d</div>
                      </div>
                    </div>
                    <div style={{ display:"flex", justifyContent:"center" }}>
                      <Fyltragram axes={fHomeAxes} size={190} isDark={darkMode} labelFontSize={8.5} valueFontSize={13}/>
                    </div>
                  </div>
                );
              })()}

              {/* 3 KPI rings */}
              <div className="ap-stat" style={{ "--sd":"100ms", display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
                {[
                  { label:"Win Rate",     v:totalWR||0,                          max:100, color:wrC2, text:totalWR!=null?`${totalWR}%`:"—",             note:activeOnlyTrades.filter(t=>t.result==="WIN").length+"W / "+activeOnlyTrades.filter(t=>t.result==="LOSS").length+"L" },
                  { label:"Profit Factor",v:Math.min((profitFactor||0)*20, 100), max:100, color:pfC,  text:profitFactor==null?"∞":profitFactor.toFixed(1), note:`${fms(grossWin)} / -${fm(grossLoss)}${currency}` },
                  { label:"Avg R/R",      v:Math.min((avgRRNum||0)/3*100, 100),  max:100, color:rrC,  text:avgRRNum!=null?avgRRNum.toFixed(1):"—",       note:rrTrades.length+" trades" },
                ].map(k => (
                  <div key={k.label} style={{ ...cardS, padding:"12px 10px", display:"flex", flexDirection:"column", alignItems:"center", gap:6, minWidth:0, overflow:"hidden" }}>
                    <div style={{ fontSize:8, color:C.dim, fontFamily:ff, fontWeight:600, letterSpacing:"0.12em", textTransform:"uppercase", textAlign:"center", width:"100%", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{k.label}</div>
                    {Ring(k.v, k.max, k.color, 54, 5, k.text)}
                    <div style={{ fontSize:8, color:C.dim, fontFamily:ff, textAlign:"center", lineHeight:1.4, width:"100%", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{k.note}</div>
                  </div>
                ))}
              </div>

              {/* Aujourd'hui / Semaine / Mois */}
              <div className="ap-stat" style={{ "--sd":"170ms", display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
                {[
                  { l:lang==="fr"?"Auj.":"Today",   v:todayPnl },
                  { l:lang==="fr"?"Semaine":"Week",  v:weekPnl  },
                  { l:lang==="fr"?"Mois":"Month",    v:monthPnl },
                ].map(m => (
                  <div key={m.l} style={{ ...cardS, padding:"10px 10px", minWidth:0, overflow:"hidden" }}>
                    <div style={{ fontSize:8, color:C.dim, fontFamily:ff, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:5 }}>{m.l}</div>
                    <div style={{ fontSize:isMobile?"clamp(11px,3.5vw,16px)":18, fontWeight:700, color:m.v>=0?clrGreen:clrRed, fontFamily:ff, letterSpacing:"-0.01em", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                      {fms(m.v)}{currency}
                    </div>
                  </div>
                ))}
              </div>

              {/* Sessions + Drawdown — côte à côte */}
              <div className="ap-stat" style={{ "--sd":"230ms", display:"flex", gap:8 }}>

              {sessionData.length > 0 && (
              <div style={{ ...cardS, padding:"14px 16px", flex:1, minWidth:0 }}>
                <div style={{ fontSize:9, color:C.dim, fontFamily:ff, fontWeight:600, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:2 }}>Sessions</div>
                <ResponsiveContainer width="100%" height={130}>
                  <BarChart data={sessionData} margin={{top:10,right:4,bottom:4,left:4}}>
                    <XAxis dataKey="name" tick={{ fontSize:8, fill:C.dim, fontFamily:ff }} axisLine={false} tickLine={false} />
                    <YAxis hide />
                    <Tooltip cursor={false} contentStyle={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:8, fontSize:10, fontFamily:ff }} itemStyle={{ color:C.white }} labelStyle={{ color:C.dim }} formatter={v=>[`${fms(v)}${currency}`, "PnL"]} />
                    <ReferenceLine y={0} stroke={darkMode?"rgba(255,255,255,0.15)":"rgba(0,0,0,0.15)"} />
                    <Bar dataKey="pnl" radius={[4,4,0,0]}>
                      {sessionData.map((s,i)=><Cell key={i} fill={s.pnl>=0?clrGreen:clrRed}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              )}

              {/* Drawdown */}
              <div style={{ ...cardS, padding:"16px 18px", flex:1, minWidth:0 }}>
                <div style={{ fontSize:9, color:C.dim, fontFamily:ff, fontWeight:600, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:12 }}>Drawdown</div>
                {activePfs.length === 0 ? (
                  <div style={{ fontSize:10, color:C.gray2, fontFamily:ff, textAlign:"center", paddingTop:8 }}>{lang==="fr"?"Aucun compte actif":"No active account"}</div>
                ) : activePfs.slice(0,1).map(pf => {
                  const pfTrades = trades.filter(t=>!t.accountIds||t.accountIds.length===0||t.accountIds.includes(pf.id));
                  const allPnl = pfTrades.reduce((s,t)=>s+(t.pnl||0),0);
                  const dl = parseFloat(pf.dailyLoss)||0;
                  const ml = parseFloat(pf.maxLoss)||0;
                  const target = parseFloat(pf.target)||0;
                  const dPnl = pfTrades.filter(t=>t.date===todayStr).reduce((s,t)=>s+(t.pnl||0),0);
                  const rows = [
                    dl>0     && { label:lang==="fr"?"Journalier":"Daily",    limit:dl,     current:Math.abs(Math.min(dPnl,0)),   tag:"24H",   isTarget:false },
                    ml>0     && { label:lang==="fr"?"Max drawdown":"Max DD", limit:ml,     current:Math.abs(Math.min(allPnl,0)), tag:"Total", isTarget:false },
                    target>0 && { label:lang==="fr"?"Objectif":"Target",     limit:target, current:Math.max(allPnl,0),           tag:"",      isTarget:true },
                  ].filter(Boolean);
                  if (!rows.length) return <div key={pf.id} style={{ fontSize:10, color:C.gray2, fontFamily:ff, textAlign:"center" }}>{lang==="fr"?"Configurez vos limites":"Configure limits"}</div>;
                  return (
                    <div key={pf.id} style={{ display:"flex", flexDirection:"column", gap:12 }}>
                      <div style={{ fontSize:9, color:C.dim, fontFamily:ff }}>{pf.firm||pf.name}</div>
                      {rows.map(r => {
                        const pct = Math.min(r.current/r.limit*100, 100);
                        const barColor = r.isTarget ? clrGreen : pct>=80?clrRed:pct>=50?C.accent:clrGreen;
                        return (
                          <div key={r.label}>
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:6, marginBottom:5 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:4, minWidth:0, flex:1 }}>
                                {r.tag && <span style={{ fontSize:8, background:`${barColor}18`, color:barColor, padding:"1px 5px", borderRadius:4, fontFamily:ff, fontWeight:600, flexShrink:0 }}>{r.tag}</span>}
                                <span style={{ fontSize:10, color:C.gray1, fontFamily:ff, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.label}</span>
                              </div>
                              <div style={{ flexShrink:0, whiteSpace:"nowrap", textAlign:"right" }}>
                                <span style={{ fontSize:11, color:barColor, fontFamily:ff, fontWeight:600 }}>{pct.toFixed(0)}%</span>
                                <span style={{ fontSize:9, color:C.dim, fontFamily:ff, marginLeft:3 }}>/ {fm(r.limit)}{currency}</span>
                              </div>
                            </div>
                            <div style={{ height:4, background:darkMode?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.08)", borderRadius:2 }}>
                              <div style={{ height:"100%", width:`${pct}%`, background:barColor, borderRadius:2, transition:"width 0.4s" }}/>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              </div>{/* fin flex sessions+drawdown */}


            </div>{/* fin colonne droite */}
          </div>{/* fin magazine grid */}

          {/* ── PLEINE LARGEUR ── */}


          {/* Derniers trades */}
          {recentTrades.length > 0 && (
            <div className="ap-stat" style={{ "--sd":"320ms", ...cardS, padding:"16px", marginBottom:10 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                <div style={{ fontSize:9, color:C.dim, fontFamily:ff, fontWeight:600, letterSpacing:"0.15em", textTransform:"uppercase" }}>{lang==="fr"?"Derniers trades":"Recent trades"}</div>
                <button onClick={() => setView("trades")} style={{ background:"none", border:"none", color:C.gray2, fontSize:10, fontFamily:ff, cursor:"pointer", letterSpacing:"0.06em" }}>{lang==="fr"?"Voir tout →":"See all →"}</button>
              </div>
              <div style={{ display:"flex", flexDirection:"column" }}>
                {recentTrades.map((t,i) => {
                  const isW = t.result==="WIN"; const isL = t.result==="LOSS";
                  return (
                    <div key={t.id} onClick={() => setSelectedTradeDetail(t)} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 0", borderTop:i>0?`1px solid ${C.border}`:"none", cursor:"pointer" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:3, alignSelf:"stretch", borderRadius:2, background:isW?clrGreen:isL?clrRed:"#555", flexShrink:0, minHeight:28 }}/>
                        <div>
                          <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                            <span style={{ fontSize:12, color:C.white, fontFamily:ff, fontWeight:600, letterSpacing:"0.02em" }}>{t.instrument}</span>
                            <span style={{ fontSize:9, color:C.gray2, fontFamily:ff, letterSpacing:"0.06em", textTransform:"uppercase" }}>{t.direction}</span>
                            {(t.tags||[]).slice(0,2).map(tag => { const bc=badgeDisplayStyle(tag); const {name}=parseBadge(tag); return <span key={tag} style={{ fontSize:8, color:bc.color, background:bc.bg, padding:"1px 6px", borderRadius:10, border:`1px solid ${bc.border}` }}>{name}</span>; })}
                          </div>
                          <div style={{ fontSize:10, color:C.gray2, fontFamily:ff, marginTop:1 }}>
                            {t.date.slice(5)}{t.session?` · ${t.session}`:""}{t.rr?` · RR ${t.rr}`:""}
                          </div>
                        </div>
                      </div>
                      <span style={{ fontSize:14, fontFamily:ff, fontWeight:600, color:isW?clrGreen:isL?clrRed:C.dim, whiteSpace:"nowrap", marginLeft:8 }}>{fms(t.pnl)}{currency}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </>)}
      </div>
    );
  })();
  const pricingContent = (() => {
    const PLANS = [
      { id:"fyltra",      label:"Fyltra",     price:"14.99", feat:lang==="fr"?"Journal · Stats avancées · TradingView · 3 comptes":"Journal · Advanced stats · TradingView · 3 accounts", badge:null },
      { id:"fyltra_plus", label:"Fyltra+",    price:"24.99", feat:lang==="fr"?"+ Coach IA · EOD Review · Market Replay · 10 comptes":"+ AI Coach · EOD Review · Market Replay · 10 accounts", badge:lang==="fr"?"Plus populaire":"Most popular" },
      { id:"fyltra_pro",  label:"Fyltra Pro", price:"39.99", feat:lang==="fr"?"+ Sync MT5 automatique · Comptes illimités":"+ Auto MT5 sync · Unlimited accounts", badge:null },
    ];
    const ff = "'Josefin Sans',sans-serif";
    const goCheckoutPricing = async (planId) => {
      try {
        const r = await fetch("/api/create-checkout", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ plan:planId, email:user?.email, existingUser:true }) });
        const d = await r.json();
        if (d.url) window.location.href = d.url;
      } catch {}
    };
    return (
      <div style={{ minHeight:"100%", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"24px 20px", boxSizing:"border-box" }}>
        <img src={darkMode?"/fyltra-logo-black.svg":"/fyltra-logo-white.svg"} style={{ height:64, width:"auto", marginBottom:32 }} alt="Fyltra"/>
        <div style={{ fontSize:22, fontWeight:700, color:C.white, fontFamily:ff, letterSpacing:"-0.03em", marginBottom:8, textAlign:"center" }}>
          {lang==="fr"?"Choisissez votre plan":"Choose your plan"}
        </div>
        <div style={{ fontSize:13, color:C.dim, fontFamily:ff, fontWeight:300, marginBottom:36, textAlign:"center", lineHeight:1.6, maxWidth:400 }}>
          {lang==="fr"?"Accès complet à Fyltra pour suivre, analyser et améliorer vos performances.":"Full access to Fyltra to track, analyze and improve your trading."}
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:10, width:"100%", maxWidth:420 }}>
          {PLANS.map(plan => (
            <div key={plan.id} style={{ background:C.bg2, border:plan.badge?`1px solid rgba(232,205,169,0.3)`:`1px solid ${C.border}`, borderRadius:14, padding:"18px 20px", position:"relative" }}>
              {plan.badge && (
                <div style={{ position:"absolute", top:-1, right:16, transform:"translateY(-50%)", background:"linear-gradient(135deg,#e8cda9,#c9aa82)", borderRadius:20, padding:"3px 10px", fontSize:9, fontWeight:700, color:"#1a1208", fontFamily:ff, letterSpacing:"0.08em", textTransform:"uppercase" }}>
                  {plan.badge}
                </div>
              )}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", alignItems:"baseline", gap:6, marginBottom:4 }}>
                    <span style={{ fontSize:14, fontWeight:700, color:C.white, fontFamily:ff }}>{plan.label}</span>
                    <span style={{ fontSize:13, color:"rgba(232,205,169,0.8)", fontFamily:"'JetBrains Mono',monospace" }}>€{plan.price}</span>
                    <span style={{ fontSize:10, color:C.dim, fontFamily:ff }}>/mois</span>
                  </div>
                  <div style={{ fontSize:11, color:C.dim, fontFamily:ff, lineHeight:1.5 }}>{plan.feat}</div>
                </div>
                <button onClick={() => goCheckoutPricing(plan.id)} style={{ flexShrink:0, marginLeft:16, padding:"9px 18px", borderRadius:8, border:plan.badge?"none":`1px solid ${C.border}`, background:plan.badge?"linear-gradient(135deg,#e8cda9,#c9aa82)":"rgba(255,255,255,0.05)", color:plan.badge?"#1a1208":C.white, fontSize:11, fontWeight:700, fontFamily:ff, cursor:"pointer", letterSpacing:"0.04em", whiteSpace:"nowrap" }}>
                  {lang==="fr"?"Choisir":"Select"}
                </button>
              </div>
            </div>
          ))}
        </div>
        <button onClick={() => { setSubExpired(false); supabase.auth.signOut(); }} style={{ marginTop:24, background:"none", border:"none", color:C.dim, fontFamily:ff, fontSize:11, cursor:"pointer", textDecoration:"underline", textUnderlineOffset:3 }}>
          {lang==="fr"?"Se déconnecter":"Sign out"}
        </button>
      </div>
    );
  })();

  const getContent = (desktop) => {
    if (subExpired) return pricingContent;
    if (view === "home")        return homeContent;
    if (view === "propfirm")    return selectedPf ? accountDetailContent(selectedPf, desktop) : propfirmContent;
    if (view === "add") {
      if (selectedPf?.metaapi_id) {
        return (
          <div>
            <PageTitle sub={L.pages.add.sub} title={L.pages.add.title} />
            <div style={{ marginTop:24, padding:"32px 20px", borderRadius:12, border:"1px solid rgba(255,255,255,0.08)", background:"rgba(255,255,255,0.02)", textAlign:"center" }}>
              <div style={{ fontSize:32, marginBottom:16, color:C.gray1 }}>⟳</div>
              <div style={{ fontFamily:"'Josefin Sans',sans-serif", fontWeight:700, fontSize:13, color:C.white, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:10 }}>
                {lang==="fr" ? "Compte MT5 synchronisé" : "MT5 Synced Account"}
              </div>
              <div style={{ fontFamily:"'Josefin Sans',sans-serif", fontWeight:300, fontSize:12, color:C.gray1, lineHeight:1.8, maxWidth:280, margin:"0 auto" }}>
                {lang==="fr" ? "Ce compte est synchronisé avec MT5. Les trades sont importés automatiquement." : "This account is synced with MT5. Trades are imported automatically."}
              </div>
            </div>
          </div>
        );
      }
      return addTradeContent;
    }
    if (view === "history")     return historyContent;
    if (view === "trades")      return tradesContent;
    if (view === "notes")       return notesContent;
    if (view === "strategy")    return strategyContent;
    if (view === "ai")          return canUseAI ? aiContent(desktop) : (
      <div style={{ maxWidth:480, margin:"60px auto", textAlign:"center", padding:"0 24px" }}>
        <div style={{ width:56, height:56, borderRadius:16, background:"linear-gradient(135deg,rgba(var(--gold-rgb),0.15),rgba(var(--gold-rgb),0.05))", border:"1px solid rgba(var(--gold-rgb),0.2)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px", fontSize:24 }}>◆</div>
        <div style={{ fontSize:18, fontWeight:700, color:C.white, fontFamily:"'Josefin Sans',sans-serif", marginBottom:10 }}>IA Coach</div>
        <div style={{ fontSize:13, color:C.dim, lineHeight:1.6, marginBottom:28, fontFamily:"'Josefin Sans',sans-serif" }}>
          {lang==="fr" ? "L'analyse IA est disponible à partir de Fyltra+ (€24.99/mois)." : "AI analysis is available from Fyltra+ (€24.99/month)."}
        </div>
        <button onClick={()=>setShowUpgradeModal(true)} style={{ padding:"12px 28px", borderRadius:10, background:"#111", color:"#fff", fontFamily:"'Josefin Sans',sans-serif", fontWeight:700, fontSize:12, letterSpacing:"0.12em", textTransform:"uppercase", border:"none", cursor:"pointer" }}>
          {lang==="fr" ? "Voir les plans →" : "View plans →"}
        </button>
      </div>
    );
    if (view === "replay")      return canUseReplay ? null : (
      <div style={{ maxWidth:480, margin:"60px auto", textAlign:"center", padding:"0 24px" }}>
        <div style={{ width:56, height:56, borderRadius:16, background:"linear-gradient(135deg,rgba(var(--gold-rgb),0.15),rgba(var(--gold-rgb),0.05))", border:"1px solid rgba(var(--gold-rgb),0.2)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px", fontSize:24 }}>▶</div>
        <div style={{ fontSize:18, fontWeight:700, color:C.white, fontFamily:"'Josefin Sans',sans-serif", marginBottom:10 }}>Replay</div>
        <div style={{ fontSize:13, color:C.dim, lineHeight:1.6, marginBottom:28, fontFamily:"'Josefin Sans',sans-serif" }}>
          {lang==="fr" ? "Le replay de trades est disponible à partir de Fyltra+ (€24.99/mois)." : "Trade replay is available from Fyltra+ (€24.99/month)."}
        </div>
        <button onClick={()=>setShowUpgradeModal(true)} style={{ padding:"12px 28px", borderRadius:10, background:"#111", color:"#fff", fontFamily:"'Josefin Sans',sans-serif", fontWeight:700, fontSize:12, letterSpacing:"0.12em", textTransform:"uppercase", border:"none", cursor:"pointer" }}>
          {lang==="fr" ? "Voir les plans →" : "View plans →"}
        </button>
      </div>
    );
    if (view === "rewards") {
      const ffTitle = "'Outfit',sans-serif";
      const ff = "'Josefin Sans',sans-serif";
      const mt5PfIds = new Set(propfirms.filter(p => !!p.metaapi_id).map(p => p.id));
      const mt5Trades = trades.filter(t => (t.accountIds||[]).some(id => mt5PfIds.has(id)));
      const hasAnyMt5Account = mt5PfIds.size > 0;
      const mt5Propfirms = propfirms.filter(p => !!p.metaapi_id);
      const REWARDS = [
        { id:"first_trade",  title:"First Trade",  desc:lang==="fr"?"Enregistre ton premier trade":"Record your first trade",  Icon:Zap,         unlocked:mt5Trades.length>0 },
        { id:"ten_trades",   title:"10 Trades",    desc:lang==="fr"?"Atteins 10 trades enregistrés":"Reach 10 recorded trades", Icon:TrendingUp,  unlocked:mt5Trades.length>=10 },
        { id:"fifty_trades", title:"50 Trades",    desc:lang==="fr"?"Atteins 50 trades enregistrés":"Reach 50 recorded trades", Icon:BarChart2,   unlocked:mt5Trades.length>=50 },
        { id:"prop_trader",  title:"Prop Trader",  desc:lang==="fr"?"Crée un compte prop firm":"Create a prop firm account",    Icon:Shield,      unlocked:mt5Propfirms.some(p=>p.type==="propfirm") },
        { id:"step_passed",  title:"Step Passed",  desc:lang==="fr"?"Valide ton premier challenge":"Pass your first challenge", Icon:Award,       unlocked:mt5Propfirms.some(p=>p.status==="passed") },
        { id:"first_payout", title:"First Payout", desc:lang==="fr"?"Reçois ton premier payout":"Receive your first payout",   Icon:DollarSign,  unlocked:mt5Propfirms.some(p=>p.payout_received) },
      ];
      const unlockedCount = REWARDS.filter(r=>r.unlocked).length;
      const total = REWARDS.length;
      const GHOST = [
        { Icon:Sparkles, title:"???",         desc:"Stay tuned..." },
        { Icon:Clock,    title:"???",         desc:"Stay tuned..." },
        { Icon:Trophy,   title:"Coming Soon", desc:"Stay tuned..." },
      ];
      return (
        <div style={{padding:"0 0 48px"}}>
          {/* Header */}
          <div style={{marginBottom:20}}>
            <div style={{fontSize:9,fontFamily:ff,fontWeight:600,letterSpacing:"0.2em",color:"rgba(232,205,169,0.6)",textTransform:"uppercase",marginBottom:6}}>
              {lang==="fr"?"Tes accomplissements":"Your accomplishments"}
            </div>
            <div style={{fontSize:28,fontFamily:ffTitle,fontWeight:800,color:C.white,lineHeight:1,marginBottom:14,letterSpacing:"-0.02em"}}>
              {lang==="fr"?"Récompenses":"Rewards"}
            </div>
            {/* Progress bar */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <div/>
              <div style={{fontSize:10,fontFamily:ff,fontWeight:300,color:C.gray1,letterSpacing:"0.06em"}}>
                {unlockedCount} / {total} {lang==="fr"?"débloqués":"unlocked"}
              </div>
            </div>
            <div style={{height:2,borderRadius:4,background:"rgba(255,255,255,0.06)",overflow:"hidden"}}>
              <div style={{height:"100%",width:`${(unlockedCount/total)*100}%`,background:"#E8CDA9",borderRadius:4,transition:"width 0.6s ease"}}/>
            </div>
          </div>

          {/* Reward cards */}
          <div style={{display:"flex",flexDirection:"column"}}>
            {REWARDS.map(r => (
              <div key={r.id} style={{
                background: r.unlocked ? "#141414" : "#0F0F0F",
                borderRadius: 16,
                padding: 16,
                display: "flex",
                alignItems: "center",
                marginBottom: 10,
                opacity: r.unlocked ? 1 : 0.5,
                borderLeft: r.unlocked ? "3px solid #E8CDA9" : "none",
                border: r.unlocked
                  ? "1px solid rgba(232,205,169,0.10)"
                  : "1px solid rgba(255,255,255,0.04)",
                borderLeftWidth: r.unlocked ? 3 : 1,
                boxShadow: r.unlocked ? "0 2px 16px rgba(232,205,169,0.04)" : "none",
                transition: "opacity 0.3s",
              }}>
                {/* Icon */}
                <div style={{
                  width:44, height:44, borderRadius:12, flexShrink:0,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  background: r.unlocked ? "rgba(232,205,169,0.08)" : "rgba(255,255,255,0.04)",
                  border: r.unlocked ? "1px solid rgba(232,205,169,0.15)" : "1px solid rgba(255,255,255,0.06)",
                }}>
                  {r.unlocked
                    ? <r.Icon size={18} color="#E8CDA9" strokeWidth={1.5}/>
                    : <Lock size={16} color="rgba(255,255,255,0.20)" strokeWidth={1.5}/>
                  }
                </div>
                {/* Text */}
                <div style={{flex:1, padding:"0 12px", minWidth:0}}>
                  <div style={{fontSize:13, fontFamily:ff, fontWeight:700, color:C.white, marginBottom:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", letterSpacing:"0.04em"}}>{r.title}</div>
                  <div style={{fontSize:11, fontFamily:ff, fontWeight:300, color:C.dim, lineHeight:1.5}}>{r.desc}</div>
                </div>
                {/* Badge */}
                {r.unlocked ? (
                  <div style={{flexShrink:0, padding:"4px 8px", borderRadius:20, background:"rgba(232,205,169,0.10)", border:"1px solid rgba(232,205,169,0.25)"}}>
                    <span style={{fontSize:8, fontFamily:ff, fontWeight:700, color:"#E8CDA9", letterSpacing:"0.12em", textTransform:"uppercase", whiteSpace:"nowrap"}}>✓ {lang==="fr"?"DÉBLOQUÉ":"UNLOCKED"}</span>
                  </div>
                ) : hasAnyMt5Account ? (
                  <div style={{flexShrink:0, padding:"4px 8px", borderRadius:20, background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)"}}>
                    <span style={{fontSize:8, fontFamily:ff, fontWeight:700, color:"rgba(255,255,255,0.25)", letterSpacing:"0.12em", textTransform:"uppercase", whiteSpace:"nowrap"}}>{lang==="fr"?"VERROUILLÉ":"LOCKED"}</span>
                  </div>
                ) : (
                  <div style={{flexShrink:0, padding:"4px 8px", borderRadius:20, background:"rgba(232,205,169,0.06)", border:"1px solid rgba(232,205,169,0.15)"}}>
                    <span style={{fontSize:7, fontFamily:ff, fontWeight:700, color:"rgba(232,205,169,0.5)", letterSpacing:"0.08em", textTransform:"uppercase", whiteSpace:"nowrap"}}>MT5 Sync — Pro</span>
                  </div>
                )}
              </div>
            ))}

            {/* Coming soon separator */}
            <div style={{display:"flex",alignItems:"center",gap:12,margin:"6px 0 14px"}}>
              <div style={{flex:1,height:1,background:"rgba(255,255,255,0.05)"}}/>
              <span style={{fontSize:8,fontFamily:ff,fontWeight:600,letterSpacing:"0.2em",color:"rgba(255,255,255,0.20)",textTransform:"uppercase",whiteSpace:"nowrap"}}>{lang==="fr"?"BIENTÔT":"MORE REWARDS"}</span>
              <div style={{flex:1,height:1,background:"rgba(255,255,255,0.05)"}}/>
            </div>

            {/* Ghost cards */}
            {GHOST.map((g,i) => (
              <div key={i} style={{
                background:"#0F0F0F", borderRadius:16, padding:16,
                display:"flex", alignItems:"center",
                marginBottom:10, opacity:0.25,
                border:"1px solid rgba(255,255,255,0.04)",
              }}>
                <div style={{width:44,height:44,borderRadius:12,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.06)"}}>
                  <g.Icon size={16} color="rgba(255,255,255,0.20)" strokeWidth={1.5}/>
                </div>
                <div style={{flex:1,padding:"0 12px",minWidth:0}}>
                  <div style={{fontSize:13,fontFamily:ff,fontWeight:700,color:C.white,marginBottom:2,letterSpacing:"0.04em"}}>{g.title}</div>
                  <div style={{fontSize:11,fontFamily:ff,fontWeight:300,color:C.dim}}>{g.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    if (view === "profil")      return homeContent;
    if (view === "settings")    return homeContent;
    if (view === "classement")  return classementContent;

    if (view === "test") {
      // ── data ──────────────────────────────────────────────────────────────
      const fngVal    = testData.fng?.data?.[0];
      const rates     = testData.rates?.rates || {};
      const calEvents = testData.calendar?.events || [];
      const vixClose  = testData.vix?.candles?.slice(-1)[0]?.close;
      const vixPrev   = testData.vix?.candles?.slice(-2)[0]?.close;
      const PAIRS = ["EUR","GBP","JPY","CHF","CAD","AUD"];
      const FLAG  = { EUR:"🇪🇺", GBP:"🇬🇧", JPY:"🇯🇵", CHF:"🇨🇭", CAD:"🇨🇦", AUD:"🇦🇺" };

      // ── color helpers ──────────────────────────────────────────────────────
      const fngColor = (v) => {
        const n = parseInt(v);
        if (n <= 25) return "#E05252";
        if (n <= 45) return "#E08852";
        if (n <= 55) return "#E8CDA9";
        return "#5DCAA5";
      };
      const fngLabel = (v) => {
        const n = parseInt(v);
        if (n <= 25) return "EXTREME FEAR";
        if (n <= 45) return "FEAR";
        if (n <= 55) return "NEUTRAL";
        if (n <= 75) return "GREED";
        return "EXTREME GREED";
      };
      const colorToRgb = (hex) => {
        if (hex === "#E05252") return "224,82,82";
        if (hex === "#E08852") return "224,136,82";
        if (hex === "#E8CDA9") return "232,205,169";
        return "93,202,165";
      };
      const vixColor = (v) => v < 15 ? "#5DCAA5" : v < 20 ? "#E8CDA9" : v < 30 ? "#E08852" : "#E05252";
      const vixLabel = (v) => v < 15 ? "CALME" : v < 20 ? "MODÉRÉ" : v < 30 ? "ÉLEVÉ" : "EXTRÊME";

      // ── calendar helpers ───────────────────────────────────────────────────
      const COUNTRY_MAP = {
        us:{flag:"🇺🇸",currency:"USD"}, eu:{flag:"🇪🇺",currency:"EUR"},
        gb:{flag:"🇬🇧",currency:"GBP"}, jp:{flag:"🇯🇵",currency:"JPY"},
        cn:{flag:"🇨🇳",currency:"CNY"}, ca:{flag:"🇨🇦",currency:"CAD"},
        au:{flag:"🇦🇺",currency:"AUD"}, nz:{flag:"🇳🇿",currency:"NZD"},
        ch:{flag:"🇨🇭",currency:"CHF"}, de:{flag:"🇩🇪",currency:"EUR"},
        fr:{flag:"🇫🇷",currency:"EUR"},
      };
      const DAY_SHORT  = ["Dim","Lun","Mar","Mer","Jeu","Ven","Sam"];
      const DAY_LONG   = ["Dimanche","Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi"];
      const DAY_EN     = ["SUNDAY","MONDAY","TUESDAY","WEDNESDAY","THURSDAY","FRIDAY","SATURDAY"];
      const MONTH_FR   = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
      const MONTH_EN   = ["JANUARY","FEBRUARY","MARCH","APRIL","MAY","JUNE","JULY","AUGUST","SEPTEMBER","OCTOBER","NOVEMBER","DECEMBER"];

      const todayStr = new Date().toISOString().slice(0,10);
      const tmrwStr  = new Date(Date.now()+86400000).toISOString().slice(0,10);
      const now      = new Date();
      const dateLabel = `${DAY_EN[now.getDay()]}, ${MONTH_EN[now.getMonth()]} ${now.getDate()}`;

      const MOCK = [
        {event:"Core CPI m/m",           country:"us",impact:"high",  time:todayStr+"T08:30",date:todayStr,actual:null,estimate:"0.3%",previous:"0.2%"},
        {event:"CPI m/m",                country:"us",impact:"high",  time:todayStr+"T08:30",date:todayStr,actual:null,estimate:"0.2%",previous:"0.3%"},
        {event:"Fed Chair Powell Speaks", country:"us",impact:"high",  time:todayStr+"T10:00",date:todayStr,actual:null,estimate:null,  previous:null  },
        {event:"German WPI m/m",         country:"de",impact:"low",   time:todayStr+"T02:00",date:todayStr,actual:null,estimate:"0.8%",previous:"2%"  },
        {event:"PPI m/m",                country:"ch",impact:"medium",time:todayStr+"T02:30",date:todayStr,actual:null,estimate:"0.4%",previous:"0.8%"},
        {event:"Retail Sales m/m",       country:"gb",impact:"medium",time:tmrwStr+"T06:00", date:tmrwStr, actual:null,estimate:"0.5%",previous:"1.2%"},
        {event:"CPI y/y",                country:"eu",impact:"high",  time:tmrwStr+"T09:00", date:tmrwStr, actual:null,estimate:"2.1%",previous:"2.2%"},
        {event:"BOJ Rate Decision",      country:"jp",impact:"high",  time:tmrwStr+"T02:00", date:tmrwStr, actual:null,estimate:null,  previous:null  },
      ];
      const displayEvents = calEvents.length > 0 ? calEvents : MOCK;

      const getPillDays = () => {
        const t = new Date(); const dow = t.getDay();
        const monday = new Date(t);
        monday.setDate(t.getDate() - (dow===0?6:dow-1));
        return Array.from({length:5}, (_,i) => {
          const d = new Date(monday); d.setDate(monday.getDate()+i);
          return d.toISOString().slice(0,10);
        });
      };
      const pillDays    = getPillDays();
      const pillLabel   = (ds) => { const d=new Date(ds+"T12:00:00Z"); return `${DAY_SHORT[d.getUTCDay()]} ${d.getUTCDate()}`; };
      const selDayLabel = (ds) => { const d=new Date(ds+"T12:00:00Z"); return `${DAY_LONG[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH_FR[d.getUTCMonth()]}`; };

      const impactOrder = {high:0,medium:1,low:2};
      const KEEP = ["us","eu","gb","jp","cn","ca","au","nz","ch","de","fr"];
      const dayEvents = displayEvents
        .filter(ev => {
          const d = (ev.time||ev.date||"").slice(0,10);
          return d===calMarketDay && KEEP.includes((ev.country||"").toLowerCase());
        })
        .sort((a,b) => {
          const diff=(impactOrder[a.impact]??3)-(impactOrder[b.impact]??3);
          return diff!==0?diff:(a.time||"").localeCompare(b.time||"");
        });

      const actualColor = (actual, estimate) => {
        if (!actual||!estimate) return C.white;
        const a=parseFloat(actual), f=parseFloat(estimate);
        if (isNaN(a)||isNaN(f)) return C.white;
        return a>=f?"#5DCAA5":"#E05252";
      };

      const isDark     = C.bg === "#0f0f0f";
      const S          = "'Josefin Sans', sans-serif";
      const labelColor = isDark ? "rgba(232,205,169,0.4)" : "rgba(0,0,0,0.35)";
      const dimText    = isDark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.30)";
      const subtleText = isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)";
      const gaugeBg    = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)";
      const dotBg      = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.09)";
      const cardShadow = isDark
        ? "0 6px 32px rgba(0,0,0,0.65), 0 2px 8px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1), 0 -2px 28px rgba(255,255,255,0.07), inset 0 1px 0 rgba(255,255,255,0.36)"
        : "0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.06)";
      const mkCard = { background:C.bg2, border:`1px solid ${C.border}`, borderRadius:14, padding:16, boxShadow:cardShadow };
      const secLbl  = { fontFamily:S, fontWeight:800, fontSize:8, letterSpacing:"3px", textTransform:"uppercase", color:labelColor, marginBottom:10 };
      const cardLbl = { fontFamily:S, fontWeight:800, fontSize:8, letterSpacing:"2.5px", textTransform:"uppercase", color:labelColor, marginBottom:12 };

      return (
        <div style={{ padding:24, boxSizing:"border-box", maxWidth:"100%", overflowX:"hidden" }}>
          <style>{`.mkt-grid{display:grid;grid-template-columns:340px 1fr;gap:14px;align-items:start}@media(max-width:720px){.mkt-grid{grid-template-columns:1fr}}`}</style>

          {/* ── Header ── */}
          <div style={{ marginBottom:20 }}>
            <div style={{ fontFamily:S, fontWeight:800, fontSize:9, letterSpacing:"3px", textTransform:"uppercase", color:labelColor, marginBottom:6 }}>
              {dateLabel}
            </div>
            <div style={{ fontFamily:S, fontWeight:800, fontSize:26, letterSpacing:"-0.5px", color:C.white }}>
              Market
            </div>
          </div>

          {testLoading && !testLoaded && (
            <div style={{ display:"flex", alignItems:"center", gap:10, color:subtleText, fontFamily:S, fontSize:11, marginBottom:20 }}>
              <div style={{ width:6, height:6, borderRadius:"50%", background:"rgba(232,205,169,0.6)", animation:"mt5-pulse 1.1s ease-in-out infinite" }}/>
              Chargement...
            </div>
          )}

          <div className="mkt-grid">

            {/* ── Colonne gauche — Market Intelligence ── */}
            <div>
              <div style={secLbl}>MARKET INTELLIGENCE</div>
              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

                {/* Card 1 — Fear & Greed */}
                <div style={mkCard}>
                  <div style={cardLbl}>FEAR &amp; GREED · CRYPTO</div>
                  {fngVal ? (() => {
                    const col = fngColor(fngVal.value);
                    const rgb = colorToRgb(col);
                    return (
                      <>
                        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
                          <span style={{ fontFamily:S, fontWeight:800, fontSize:52, letterSpacing:"-3px", lineHeight:1, color:col }}>
                            {fngVal.value}
                          </span>
                          <div style={{ background:`rgba(${rgb},0.10)`, border:`1px solid rgba(${rgb},0.20)`, borderRadius:6, padding:"4px 8px" }}>
                            <span style={{ fontFamily:S, fontWeight:800, fontSize:8, letterSpacing:"1.5px", color:col }}>
                              {fngLabel(fngVal.value)}
                            </span>
                          </div>
                        </div>
                        <div style={{ width:"100%", height:2, background:gaugeBg, borderRadius:2, marginBottom:8, overflow:"hidden" }}>
                          <div style={{ width:`${fngVal.value}%`, height:"100%", background:col, borderRadius:2 }}/>
                        </div>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                          {["0","25","50","75","100"].map(v => (
                            <span key={v} style={{ fontFamily:S, fontWeight:800, fontSize:7, color:dimText }}>{v}</span>
                          ))}
                        </div>
                        <div style={{ fontFamily:S, fontWeight:400, fontSize:8, color:dimText }}>Alternative.me</div>
                      </>
                    );
                  })() : (
                    <div style={{ fontFamily:S, fontSize:11, color:subtleText }}>—</div>
                  )}
                </div>

                {/* Card 2 — Taux de change */}
                <div style={mkCard}>
                  <div style={cardLbl}>TAUX DE CHANGE · USD</div>
                  <div>
                    {PAIRS.map((p,i) => rates[p] ? (
                      <div key={p} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:i<PAIRS.length-1?`1px solid ${C.border}`:"none" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <span style={{ fontSize:13 }}>{FLAG[p]}</span>
                          <span style={{ fontFamily:S, fontWeight:800, fontSize:10, color:isDark?"rgba(255,255,255,0.50)":"rgba(0,0,0,0.50)" }}>{p}</span>
                        </div>
                        <span style={{ fontFamily:S, fontWeight:800, fontSize:12, color:C.white, fontVariantNumeric:"tabular-nums" }}>
                          {p==="JPY" ? rates[p].toFixed(2) : rates[p].toFixed(4)}
                        </span>
                      </div>
                    ) : null)}
                  </div>
                  {testData.rates?.time_last_update_utc && (
                    <div style={{ fontFamily:S, fontWeight:400, fontSize:8, color:dimText, textAlign:"right", marginTop:8 }}>
                      {testData.rates.time_last_update_utc.slice(0,10)}
                    </div>
                  )}
                </div>

                {/* Card 3 — VIX */}
                <div style={mkCard}>
                  <div style={cardLbl}>VIX · VOLATILITÉ</div>
                  {vixClose != null ? (() => {
                    const col  = vixColor(vixClose);
                    const diff = vixPrev != null ? vixClose - vixPrev : null;
                    return (
                      <>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:0 }}>
                          <span style={{ fontFamily:S, fontWeight:800, fontSize:52, letterSpacing:"-3px", lineHeight:1, color:col }}>
                            {vixClose.toFixed(1)}
                          </span>
                          <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6, paddingTop:4 }}>
                            <span style={{ fontFamily:S, fontWeight:800, fontSize:8, letterSpacing:"2px", color:col, opacity:0.5 }}>
                              {vixLabel(vixClose)}
                            </span>
                            {diff != null && (
                              <div style={{ display:"flex", alignItems:"baseline", gap:4 }}>
                                <span style={{ fontFamily:S, fontWeight:800, fontSize:11, color:col }}>
                                  {diff >= 0 ? "↑" : "↓"} {diff >= 0 ? "+" : "−"}{Math.abs(diff).toFixed(2)}
                                </span>
                                <span style={{ fontFamily:S, fontWeight:400, fontSize:9, color:dimText }}>vs veille</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ width:"100%", height:2, background:gaugeBg, borderRadius:2, overflow:"hidden", marginTop:12, marginBottom:6 }}>
                          <div style={{ width:`${Math.min(vixClose/50*100,100)}%`, height:"100%", background:col, borderRadius:2 }}/>
                        </div>
                        <div style={{ display:"flex", justifyContent:"space-between" }}>
                          {["0","15","20","30","50+"].map(v => (
                            <span key={v} style={{ fontFamily:S, fontWeight:800, fontSize:7, color:dimText }}>{v}</span>
                          ))}
                        </div>
                      </>
                    );
                  })() : (
                    <div style={{ fontFamily:S, fontSize:11, color:subtleText }}>—</div>
                  )}
                </div>

              </div>
            </div>

            {/* ── Colonne droite — Calendrier Économique ── */}
            <div>
              <div style={secLbl}>CALENDRIER ÉCONOMIQUE</div>
              <div style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:14, overflow:"hidden", boxShadow:cardShadow }}>

                {/* Pill selector */}
                <div style={{ padding:"14px 16px 0", display:"flex", gap:5, overflowX:"auto", scrollbarWidth:"none", WebkitOverflowScrolling:"touch", msOverflowStyle:"none" }}>
                  {pillDays.map(ds => {
                    const active = ds === calMarketDay;
                    return (
                      <button key={ds} onClick={() => setCalMarketDay(ds)} style={{
                        flexShrink:0, padding:"5px 12px", borderRadius:20, border:"none", cursor:"pointer",
                        background:active ? "#E8CDA9" : C.bg3,
                        color:active ? "#000000" : C.gray1,
                        fontFamily:S, fontWeight:800, fontSize:9, letterSpacing:"1px",
                        transition:"all 0.15s",
                      }}>
                        {pillLabel(ds)}
                      </button>
                    );
                  })}
                </div>

                {/* Date header */}
                <div style={{ display:"flex", alignItems:"center", gap:10, padding:"14px 16px 10px", borderBottom:`1px solid ${C.border}` }}>
                  <div style={{ width:2, height:14, background:"#E8CDA9", borderRadius:2, flexShrink:0 }}/>
                  <span style={{ fontFamily:S, fontWeight:800, fontSize:12, color:C.white }}>{selDayLabel(calMarketDay)}</span>
                </div>

                {/* Events list */}
                {dayEvents.length === 0 ? (
                  <div style={{ padding:"20px 16px", fontFamily:S, fontWeight:400, fontSize:11, color:subtleText }}>
                    Aucun événement majeur pour ce jour.
                  </div>
                ) : dayEvents.map((ev, i) => {
                  const cm      = COUNTRY_MAP[(ev.country||"").toLowerCase()] || {flag:"🌐", currency:(ev.country||"").toUpperCase()};
                  const time    = (ev.time||"").slice(11,16) || "—";
                  const impact  = (ev.impact||"low").toLowerCase();
                  const isHigh  = impact === "high";
                  const isMed   = impact === "medium";
                  const dotColor = isHigh ? "#E05252" : isMed ? "#E08852" : dimText;
                  const dotCount = isHigh ? 3 : isMed ? 2 : 1;
                  const hasData  = ev.actual != null || ev.estimate != null || ev.previous != null;
                  const aColor   = ev.actual ? actualColor(ev.actual, ev.estimate) : isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.25)";
                  return (
                    <div key={i} style={{
                      display:"grid", gridTemplateColumns:"44px 1fr", gap:"0 12px",
                      padding:"12px 16px",
                      borderBottom: i < dayEvents.length - 1 ? `1px solid ${C.border}` : "none",
                      ...(isHigh ? { background:"rgba(224,82,82,0.03)", borderLeft:"2px solid rgba(224,82,82,0.25)", paddingLeft:14 } : {}),
                    }}>
                      <div style={{ fontFamily:S, fontWeight:800, fontSize:11, color:"#E8CDA9", fontVariantNumeric:"tabular-nums", letterSpacing:"-0.3px", paddingTop:1 }}>
                        {time}
                      </div>
                      <div>
                        <div style={{ fontFamily:S, fontWeight:700, fontSize:11, color:C.white, marginBottom:5, lineHeight:1.3 }}>
                          {ev.event}
                        </div>
                        <div style={{ display:"flex", gap:5, alignItems:"center", marginBottom: hasData ? 8 : 0 }}>
                          <div style={{ display:"flex", gap:3 }}>
                            {[0,1,2].map(d => (
                              <div key={d} style={{ width:5, height:5, borderRadius:"50%", background: d < dotCount ? dotColor : dotBg }}/>
                            ))}
                          </div>
                          <span style={{ fontSize:12 }}>{cm.flag}</span>
                          <span style={{ fontFamily:S, fontWeight:800, fontSize:9, color:"#E8CDA9", letterSpacing:"1px" }}>{cm.currency}</span>
                        </div>
                        {hasData && (
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:4 }}>
                            {[
                              { label:"ACTUEL",    val:ev.actual,   color:aColor },
                              { label:"PRÉVISION", val:ev.estimate, color:C.white },
                              { label:"PRÉCÉDENT", val:ev.previous, color:C.white },
                            ].map(({label,val,color}) => (
                              <div key={label}>
                                <div style={{ fontFamily:S, fontWeight:800, fontSize:11, color: val ? color : isDark?"rgba(255,255,255,0.18)":"rgba(0,0,0,0.25)", fontVariantNumeric:"tabular-nums" }}>
                                  {val || "—"}
                                </div>
                                <div style={{ fontFamily:S, fontWeight:800, fontSize:7, letterSpacing:"1px", color:dimText, marginTop:2 }}>
                                  {label}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        </div>
      );
    }

    return null;
  };

  const TV_TZ = [
    {l:"UTC-8",v:"America/Los_Angeles"},{l:"UTC-7",v:"America/Denver"},{l:"UTC-6",v:"America/Chicago"},
    {l:"UTC-5",v:"America/New_York"},{l:"UTC-4",v:"America/Caracas"},{l:"UTC-3",v:"America/Sao_Paulo"},
    {l:"UTC-2",v:"Atlantic/South_Georgia"},{l:"UTC-1",v:"Atlantic/Azores"},{l:"UTC",v:"Etc/UTC"},
    {l:"UTC+1",v:"Europe/London"},{l:"UTC+2",v:"Europe/Paris"},{l:"UTC+3",v:"Europe/Moscow"},
    {l:"UTC+4",v:"Asia/Dubai"},{l:"UTC+5",v:"Asia/Karachi"},{l:"UTC+5:30",v:"Asia/Kolkata"},
    {l:"UTC+6",v:"Asia/Dhaka"},{l:"UTC+7",v:"Asia/Bangkok"},{l:"UTC+8",v:"Asia/Shanghai"},
    {l:"UTC+9",v:"Asia/Tokyo"},{l:"UTC+10",v:"Australia/Sydney"},{l:"UTC+12",v:"Pacific/Auckland"},
  ];
  const chartsPanel = (
    <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 90px)",gap:8}}>
      <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
        <span style={{fontSize:9,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",fontWeight:700,letterSpacing:"0.15em",textTransform:"uppercase"}}>Fuseau</span>
        <select value={tvTimezone} onChange={e=>setTvTimezone(e.target.value)} style={{padding:"4px 8px",borderRadius:20,border:`1px solid ${C.border}`,background:C.bg2,color:C.white,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",outline:"none",cursor:"pointer"}}>
          {TV_TZ.map(t=><option key={t.v} value={t.v}>{t.l}</option>)}
        </select>
      </div>
      <div style={{flex:1,borderRadius:12,overflow:"hidden",border:`1px solid ${C.border}`,minHeight:0}}>
        <TradingViewChart symbol={tvSymbol} interval={tvInterval} darkMode={darkMode} timezone={tvTimezone}/>
      </div>
    </div>
  );
  const replayPanel = (
    <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 90px)",padding:"12px",boxSizing:"border-box"}}>
      <div style={{flex:1,display:"flex",flexDirection:"column",borderRadius:16,border:"1px solid rgba(232,205,169,0.15)",background:darkMode?"#0e0f13":"#f0f0f3",overflow:"hidden",minHeight:0}}>
        <ReplayChart darkMode={darkMode} isMobile={isMobile} />
      </div>
    </div>
  );

  /* ── RENDER ── */
  if (authLoading) return (
    <div style={{ minHeight:"100vh", background:"#0f0f0f", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <style>{FONTS}</style>
      <div style={{ fontSize:11, color:"rgba(255,255,255,0.3)", fontFamily:"'Josefin Sans',sans-serif", letterSpacing:"0.2em", textTransform:"uppercase" }}>Chargement...</div>
    </div>
  );
  if (passwordRecovery) return <ResetPasswordScreen onDone={() => setPasswordRecovery(false)} />;

  if (!user) return <Navigate to="/" replace />;
  if (!user.email_confirmed_at) return <Navigate to="/register?unconfirmed=1" replace />;
  if (subExpired && !activating) return <Navigate to="/plans" replace />;

  return (
    <div style={{ minHeight:"100vh", background:C.bg, color:C.white, fontFamily:"'Josefin Sans',sans-serif" }}>
      <style>{FONTS}</style>

      {showLoadingScreen && (
        <div style={{
          position:"fixed", inset:0, zIndex:10000,
          background:darkMode?"#0f0f0f":"#ffffff",
          display:"flex", alignItems:"center", justifyContent:"center",
          opacity: loadingFading ? 0 : 1,
          transition:"opacity 0.5s ease",
          pointerEvents: loadingFading ? "none" : "auto",
        }}>
          <div style={{ position:"relative", width:isMobile?220:320, height:isMobile?80:116 }}>
            <img
              src={darkMode?"/fyltra-logo-black.svg":"/fyltra-logo-white.svg"}
              style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"contain", opacity:0.07 }}
              alt=""
            />
            <img
              src={darkMode?"/fyltra-logo-black.svg":"/fyltra-logo-white.svg"}
              style={{
                position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"contain",
                clipPath:`inset(${100 - loadingProgress}% 0 0 0)`,
                transition:"clip-path 0.06s linear",
              }}
              alt="Fyltra"
            />
          </div>
        </div>
      )}

      {showOnboarding && (() => {
        const ff = "'Josefin Sans',sans-serif";
        const STEPS = [
          { icon:"⌂", title:lang==="fr"?"Accueil":"Home",                                  desc:lang==="fr"?"Vue d'ensemble de tes performances — P&L, win rate, Fyltra Score et santé de trading en temps réel.":"Overview of your performance — P&L, win rate, Fyltra Score and trading health in real time." },
          { icon:"◉", title:lang==="fr"?"Compte":"Account",                                desc:lang==="fr"?"Gère tes prop firms et comptes perso. Suis le daily loss, la consistance et l'objectif de profit en temps réel.":"Manage your prop firms and personal accounts. Track daily loss, consistency and profit target in real time." },
          { icon:"＋", title:lang==="fr"?"Trade":"Trade",                                   desc:lang==="fr"?"Saisis un trade en quelques secondes — instrument, direction, P&L, émotion, session. Sans friction.":"Log a trade in seconds — instrument, direction, P&L, emotion, session. No friction." },
          { icon:"◎", title:lang==="fr"?"Journal":"Journal",                               desc:lang==="fr"?"Tes notes de session organisées par date. Documente ce qui s'est passé, ce que tu ressens, ce que tu retiens.":"Your session notes organised by date. Document what happened, how you felt, what you take away." },
          { icon:"◎", title:lang==="fr"?"Historique":"History",                            desc:lang==="fr"?"Tous tes trades filtrables par date, instrument, résultat ou émotion. Ton journal complet en un clic.":"All your trades, filterable by date, instrument, result or emotion. Your full log in one click." },
          { icon:"◈", title:lang==="fr"?"Statistiques":"Statistics",                       desc:lang==="fr"?"Win rate, profit factor, R/R, drawdown, Monte Carlo. Tes vrais chiffres, calculés automatiquement.":"Win rate, profit factor, R/R, drawdown, Monte Carlo. Your real numbers, calculated automatically." },
          { icon:"◆", title:lang==="fr"?"Plan":"Plan",                                     desc:lang==="fr"?"Documente ta stratégie, tes règles et tes setups. Ton plan toujours accessible depuis l'app.":"Document your strategy, rules and setups. Your plan always one click away." },
          { icon:"▲", title:lang==="fr"?"Classement":"Rankings",                           desc:lang==="fr"?"Ton Fyltra Score face aux autres traders. Progresse, monte de tier, domine le classement.":"Your Fyltra Score against other traders. Progress, tier up, top the leaderboard." },
          { icon:"★", title:lang==="fr"?"Récompenses":"Rewards",                           desc:lang==="fr"?"Débloque des badges en tradant avec discipline. Chaque jalon compte — cohérence, win streak, maîtrise émotionnelle.":"Unlock badges by trading with discipline. Every milestone counts — consistency, win streaks, emotional control." },
          { icon:"◈", title:lang==="fr"?"Marché":"Market",                                 desc:lang==="fr"?"Accès rapide aux données de marché en temps réel pour contextualiser tes trades.":"Quick access to real-time market data to put your trades in context." },
          { icon:"TV", title:"TradingView",                                                 desc:lang==="fr"?"Analyse tes charts directement dans Fyltra. TradingView intégré sans quitter l'app.":"Analyse your charts directly in Fyltra. TradingView embedded, no tab switching." },
          { icon:"▶", title:"Replay",                                                       desc:lang==="fr"?"Rejoue tes séances de trading bougie par bougie. Entraîne-toi et affine ta lecture du marché.":"Replay your trading sessions candle by candle. Train and sharpen your market reading." },
          { icon:"◉", title:lang==="fr"?"IA Coach":"AI Coach",                             desc:lang==="fr"?"L'IA analyse tes trades et te donne un diagnostic de session. Tes patterns, pas des généralités.":"The AI analyses your trades and gives you a session diagnosis. Your patterns, not generic advice." },
          { icon:"◎", title:lang==="fr"?"Personnalisation":"Customisation",                desc:lang==="fr"?"Clique sur ta carte en bas à gauche pour personnaliser l'app : mode sombre / clair, langue (FR/EN), devise (€/$), masquer les chiffres.":"Click your card at the bottom left to personalise the app: dark / light mode, language (FR/EN), currency (€/$), hide numbers." },
        ];
        const s = STEPS[onboardStep];
        const isLast = onboardStep === STEPS.length - 1;
        const dismiss = () => { try { if (user) localStorage.setItem(`fyltra_onboarded_${user.id}`, "1"); } catch {} setShowOnboarding(false); setView("home"); };
        return (
          <div style={{ position:"fixed", inset:0, zIndex:9999, display:"flex", alignItems:"flex-end", justifyContent:"center", padding:isMobile?"0 0 110px":"0 0 40px" }}>
            <div style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:20, padding:"24px 24px 20px", maxWidth:460, width:"calc(100% - 32px)", boxShadow:"0 -4px 60px rgba(0,0,0,0.5),0 0 0 1px rgba(255,255,255,0.06),inset 0 1px 0 rgba(255,255,255,0.12)" }}>
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
                <div style={{ width:40,height:40,borderRadius:10,background:"linear-gradient(135deg,rgba(232,205,169,0.14),rgba(232,205,169,0.04))",border:"1px solid rgba(232,205,169,0.18)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:C.accent,fontFamily:ff,flexShrink:0 }}>{s.icon}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:8, color:C.dim, fontFamily:ff, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:2 }}>{lang==="fr"?"Étape":"Step"} {onboardStep+1} / {STEPS.length}</div>
                  <div style={{ fontSize:isMobile?15:17, fontWeight:700, color:C.white, fontFamily:ff, letterSpacing:"-0.01em", lineHeight:1.2 }}>{s.title}</div>
                </div>
                <button onClick={dismiss} style={{ flexShrink:0, background:"none", border:"none", color:C.gray2, fontSize:16, cursor:"pointer", padding:"4px 6px", lineHeight:1 }}>✕</button>
              </div>
              <div style={{ fontSize:12, color:C.gray1, fontFamily:ff, lineHeight:1.7, marginBottom:16 }}>{s.desc}</div>
              <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:16 }}>
                {STEPS.map((_,i) => (
                  <div key={i} onClick={() => setOnboardStep(i)} style={{ height:2.5, flex:i===onboardStep?2.5:1, borderRadius:2, background:i===onboardStep?C.accent:i<onboardStep?"rgba(232,205,169,0.3)":"rgba(255,255,255,0.08)", transition:"all 0.3s ease", cursor:"pointer" }}/>
                ))}
              </div>
              <div style={{ display:"flex", gap:8 }}>
                {onboardStep > 0 && (
                  <button onClick={() => setOnboardStep(n => n-1)} style={{ padding:"10px 16px", borderRadius:10, border:`1px solid ${C.border}`, background:"transparent", color:C.gray2, fontSize:11, fontFamily:ff, cursor:"pointer", letterSpacing:"0.06em" }}>
                    ←
                  </button>
                )}
                <button onClick={() => isLast ? dismiss() : setOnboardStep(n => n+1)} style={{ flex:1, padding:"11px 20px", borderRadius:10, border:"none", background:isLast?"linear-gradient(135deg,#e8cda9,#c9aa82)":"rgba(255,255,255,0.92)", color:"#111", fontSize:12, fontFamily:ff, fontWeight:700, cursor:"pointer", letterSpacing:"0.1em" }}>
                  {isLast ? (lang==="fr"?"C'est parti !":"Let's go!") : (lang==="fr"?"Suivant →":"Next →")}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {isMobile ? (
        /* ── MOBILE ── */
        <div style={{ minHeight:"100vh", paddingBottom:100 }}>
          <div style={{ padding:"16px 20px", background:`linear-gradient(180deg,${C.bg2},${C.bg})`, borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:50, backdropFilter:"blur(16px)", flexWrap:"wrap" }}>
            <div style={{ display:"flex", alignItems:"center", gap:9 }}>
              <img src={darkMode?"/fyltra-logo-black.svg":"/fyltra-logo-white.svg"} style={{height:88,width:"auto",flexShrink:0}} alt="Fyltra"/>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <button onClick={()=>setHideNumbers(h=>{ localStorage.setItem("fyltra_hide_numbers", String(!h)); return !h; })} title={hideNumbers?(lang==="fr"?"Afficher les chiffres":"Show numbers"):(lang==="fr"?"Masquer les chiffres":"Hide numbers")} style={{background:hideNumbers?"rgba(232,205,169,0.12)":darkMode?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.06)",border:`1px solid ${hideNumbers?C.accent:C.border}`,borderRadius:8,width:34,height:34,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:hideNumbers?C.accent:C.gray1,flexShrink:0,transition:"all 0.2s"}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {hideNumbers ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></> : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>}
                </svg>
              </button>
              <button onClick={()=>setDarkMode(d=>!d)} title={darkMode?"Mode clair":"Mode sombre"} style={{background:darkMode?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.06)",border:`1px solid ${C.border}`,borderRadius:8,width:34,height:34,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:C.gray1,flexShrink:0,transition:"all 0.2s"}}>
                {darkMode ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                  </svg>
                )}
              </button>
              <button onClick={()=>showMenu?closeMenu():setShowMenu(true)} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 12px",color:C.gray1,cursor:"pointer",display:"flex",flexDirection:"column",gap:"4px",alignItems:"center",justifyContent:"center"}}>
                <div style={{width:16,height:1.5,background:C.gray1,borderRadius:1}}/>
                <div style={{width:16,height:1.5,background:C.gray1,borderRadius:1}}/>
                <div style={{width:16,height:1.5,background:C.gray1,borderRadius:1}}/>
              </button>
            </div>
          </div>
          <div style={{ padding:"22px 18px", maxWidth:560, margin:"0 auto", opacity:showLoadingScreen?0:1 }}>
            <div style={{display:view==="charts" && !subExpired?"flex":"none",flexDirection:"column"}}>{chartsPanel}</div>
            <div style={{display:view==="replay" && !subExpired && canUseReplay?"flex":"none",flexDirection:"column"}}>{replayPanel}</div>
            {(view!=="charts" && (view!=="replay" || !canUseReplay) || subExpired) && <div key={view+(selectedPf?.id||"")+(showLoadingScreen?"_L":"")} style={{animation:"tabFadeIn 0.42s cubic-bezier(0.34,1.1,0.64,1)", willChange:"transform, opacity"}}>{getContent(false)}</div>}
          </div>
          {showMenu && (
            <>
              <div onClick={closeMenu} style={{position:"fixed",inset:0,zIndex:298}}/>
              <div style={{position:"fixed",top:70,right:16,zIndex:299,animation:`${menuClosing?"slideToRight":"slideFromRight"} 0.24s cubic-bezier(.4,0,.2,1)`,display:"flex",flexDirection:"column",gap:4,background:"linear-gradient(180deg,rgba(60,60,60,0.97) 0%,rgba(18,18,18,0.99) 55%,rgba(8,8,8,1) 100%)",backdropFilter:"blur(24px)",WebkitBackdropFilter:"blur(24px)",borderRadius:24,padding:"10px",boxShadow:"0 6px 20px rgba(0,0,0,0.5),0 20px 50px rgba(0,0,0,0.4),0 0 60px rgba(255,255,255,0.11),0 0 0 1px rgba(255,255,255,0.13),inset 0 1px 0 rgba(255,255,255,0.38),inset 0 -2px 0 rgba(0,0,0,0.8)",border:"1px solid rgba(255,255,255,0.1)",minWidth:180}}>
                {L.quickNav.map(item=>{
                  const active = view === item.k;
                  return (
                    <button key={item.k} onClick={()=>{setView(item.k);closeMenu();}} style={{display:"flex",alignItems:"center",width:"100%",padding:active?"10px 18px":"10px 14px",borderRadius:16,border:"none",cursor:"pointer",background:active?"radial-gradient(ellipse 110% 100% at 50% 35%,rgba(252,252,252,0.93) 0%,rgba(225,225,225,0.85) 55%,rgba(200,200,200,0.75) 100%)":"transparent",transition:"all 0.25s cubic-bezier(.4,0,.2,1)",boxShadow:active?"0 0 26px 8px rgba(255,255,255,0.22),0 0 50px 16px rgba(255,255,255,0.09),0 6px 20px rgba(0,0,0,0.5),0 2px 6px rgba(0,0,0,0.3)":"none"}}>
                      <span style={{fontSize:11,fontFamily:"'Josefin Sans',sans-serif",fontWeight:active?700:300,letterSpacing:"0.1em",textTransform:"uppercase",color:active?"#222":"rgba(255,255,255,0.55)",transition:"color 0.25s",whiteSpace:"nowrap"}}>{item.l}</span>
                    </button>
                  );
                })}
                <div style={{height:1,background:"rgba(255,255,255,0.08)",margin:"4px 6px"}}/>
                {/* User card */}
                <button onClick={()=>{closeMenu();setShowUserModal(true);}} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"10px 14px",borderRadius:16,border:"1px solid rgba(255,255,255,0.1)",cursor:"pointer",background:"rgba(255,255,255,0.04)",transition:"all 0.2s"}}
                  onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.08)"}
                  onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.04)"}>
                  <div style={{width:28,height:28,borderRadius:"50%",background:"linear-gradient(135deg,rgba(232,205,169,0.5) 0%,rgba(180,140,100,0.4) 100%)",border:"1px solid rgba(232,205,169,0.25)",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",flexShrink:0}}>
                    {avatarUrl
                      ? <img src={avatarUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}} />
                      : <span style={{fontSize:11,fontWeight:700,color:"rgba(232,205,169,0.9)",fontFamily:"'Outfit',sans-serif"}}>{(profileForm.nickname||profileForm.firstName||"?")[0].toUpperCase()}</span>
                    }
                  </div>
                  <div style={{flex:1,minWidth:0,textAlign:"left"}}>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.85)",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,letterSpacing:"0.06em",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{profileForm.nickname||profileForm.firstName||"Trader"}</div>
                  </div>
                  <span style={{fontSize:10,color:"rgba(255,255,255,0.2)"}}>›</span>
                </button>
              </div>
            </>
          )}
          {selectedDay && (
            <div style={{position:"fixed",inset:0,zIndex:400,display:"flex",alignItems:"flex-end",justifyContent:"center",paddingBottom:90,overflowY:"auto"}} onClick={closeDay}>
              <div onClick={e=>e.stopPropagation()} style={{width:"calc(100% - 32px)",maxWidth:480,background:"rgba(14,14,14,0.97)",backdropFilter:"blur(24px)",WebkitBackdropFilter:"blur(24px)",borderRadius:24,padding:"20px",boxShadow:"0 -8px 48px rgba(0,0,0,0.4)",border:"1px solid rgba(255,255,255,0.08)",animation:`${dayClosing?"fadeOutDown":"fadeInUp"} 0.25s cubic-bezier(.4,0,.2,1)`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                  <div>
                    <div style={{fontSize:13,color:"rgba(255,255,255,0.9)",fontFamily:"'Barlow',sans-serif",fontWeight:600,letterSpacing:"0.08em"}}>{new Date(selectedDay.date+"T12:00:00").toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long"})}</div>
                    <div style={{fontSize:20,color:selectedDay.pnl>=0?"#4caf6e":"#e05a5a",fontFamily:"'Josefin Sans',sans-serif",fontWeight:300,marginTop:2}}>{fms(selectedDay.pnl)}{currency} · {selectedDay.trades.length} trade{selectedDay.trades.length!==1?"s":""}</div>
                  </div>
                  <button onClick={closeDay} style={{background:"rgba(255,255,255,0.08)",border:"none",borderRadius:50,width:32,height:32,color:"rgba(255,255,255,0.5)",cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
                </div>
                {/* Day summary — gauge + stats */}
                {(() => {
                  const dW=selectedDay.trades.filter(t=>t.result==="WIN").length;
                  const dL=selectedDay.trades.filter(t=>t.result==="LOSS").length;
                  const dT=selectedDay.trades.length;
                  const r=38,cx=55,cy=42,sw=10,PI=Math.PI;
                  const wFrac=dT>0?dW/dT:0; const lFrac=dT>0?dL/dT:0;
                  const wr=dT?Math.round(dW/dT*100):0;
                  const LEFT2={x:cx-r,y:cy}, RIGHT2={x:cx+r,y:cy};
                  const a2XY=(deg)=>({x:cx+r*Math.cos(deg*PI/180),y:cy-r*Math.sin(deg*PI/180)});
                  const bgA=`M${LEFT2.x},${LEFT2.y} A${r},${r} 0 0 1 ${RIGHT2.x},${RIGHT2.y}`;
                  const wD2=180-wFrac*180; const wP2=a2XY(wD2);
                  const lD2=lFrac*180; const lP2=a2XY(lD2);
                  const wA2=wFrac>0.01?`M${LEFT2.x},${LEFT2.y} A${r},${r} 0 ${wFrac>=1?1:0} 1 ${wP2.x.toFixed(1)},${wP2.y.toFixed(1)}`:"";
                  const lA2=lFrac>0.01?`M${RIGHT2.x},${RIGHT2.y} A${r},${r} 0 ${lFrac>=1?1:0} 0 ${lP2.x.toFixed(1)},${lP2.y.toFixed(1)}`:"";
                  return (
                    <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:14,background:"rgba(255,255,255,0.04)",borderRadius:10,padding:"12px 14px"}}>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"center",flexShrink:0,width:110}}>
                        <svg width={110} height={cy+28} viewBox={`0 0 110 ${cy+28}`}>
                          <path d={bgA} stroke="rgba(255,255,255,0.1)" strokeWidth={sw} fill="none" strokeLinecap="round"/>
                          {wFrac>0.01&&<path d={wA2} stroke="#4caf6e" strokeWidth={sw} fill="none" strokeLinecap="round"/>}
                          {lFrac>0.01&&<path d={lA2} stroke="#e05a5a" strokeWidth={sw} fill="none" strokeLinecap="round"/>}
                          <text x={cx} y={cy} textAnchor="middle" fontSize={14} fontWeight={300} fill={wr>=50?"#4caf6e":"#e05a5a"} fontFamily="'Josefin Sans',sans-serif">{wr}%</text>
                          <text x={cx} y={cy+12} textAnchor="middle" fontSize={6} fill="rgba(255,255,255,0.35)" fontFamily="'Josefin Sans',sans-serif" letterSpacing="1">WIN RATE</text>
                          <text x={4} y={cy+22} textAnchor="start" fontSize={8} fontWeight="600" fill="#4caf6e" fontFamily="'Josefin Sans',sans-serif">{dW}W</text>
                          <text x={cx} y={cy+22} textAnchor="middle" fontSize={8} fill="rgba(255,255,255,0.35)" fontFamily="'Josefin Sans',sans-serif">{dT-dW-dL}BE</text>
                          <text x={106} y={cy+22} textAnchor="end" fontSize={8} fontWeight="600" fill="#e05a5a" fontFamily="'Josefin Sans',sans-serif">{dL}L</text>
                        </svg>
                      </div>
                      <div style={{flex:1}}>
                        {(() => {
                          const dayEmotions=EMOTIONS.map(e=>{const et=selectedDay.trades.filter(t=>t.emotion===e);const wr=et.length?Math.round(et.filter(t=>t.result==="WIN").length/et.length*100):0;return{name:e,count:et.length,wr,pnl:et.reduce((a,t)=>a+(t.pnl||0),0)};}).filter(e=>e.count>0);
                          const daySess=SESSIONS.map(s=>{const st=selectedDay.trades.filter(t=>t.session===s);const wr=st.length?Math.round(st.filter(t=>t.result==="WIN").length/st.length*100):0;return{name:s,count:st.length,wr,pnl:st.reduce((a,t)=>a+(t.pnl||0),0)};}).filter(s=>s.count>0);
                          return (
                            <div style={{display:"flex",flexDirection:"column",gap:6}}>
                              {dayEmotions.length>0&&<div style={{fontSize:7,color:"rgba(255,255,255,0.3)",textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:"'Josefin Sans',sans-serif"}}>Émotions</div>}
                              {dayEmotions.map(e=>(
                                <div key={e.name} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                                  <span style={{fontSize:10,color:"rgba(255,255,255,0.55)",fontFamily:"'Josefin Sans',sans-serif"}}>{L.emotions[e.name]||e.name}</span>
                                  <span style={{fontSize:10,color:e.wr>=50?"#4caf6e":"#e05a5a",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>{e.wr}% · {fms(e.pnl)}{currency}</span>
                                </div>
                              ))}
                              {daySess.length>0&&<div style={{fontSize:7,color:"rgba(255,255,255,0.3)",textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:"'Josefin Sans',sans-serif",marginTop:2}}>Sessions</div>}
                              {daySess.map(s=>(
                                <div key={s.name} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                                  <span style={{fontSize:10,color:"rgba(255,255,255,0.45)",fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"0.04em"}}>{s.name}</span>
                                  <span style={{fontSize:10,color:s.pnl>=0?"#4caf6e":"#e05a5a",fontFamily:"'Josefin Sans',sans-serif"}}>{s.wr}% · {fms(s.pnl)}{currency}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })()}
                {selectedDay.trades.length>=1&&(()=>{
                  let c2=0;
                  const pd=[{label:"0",v:0},...[...selectedDay.trades].sort(cmpTrades).map((t,i)=>{c2+=t.pnl||0;return{label:String(i+1),v:parseFloat(c2.toFixed(2)),pnl:t.pnl||0,instrument:t.instrument||""};})];
                  const dayTotalPnl=pd[pd.length-1]?.v||0;
                  return(
                    <div style={{marginBottom:12,background:"rgba(255,255,255,0.04)",borderRadius:10,padding:"10px 12px"}}>
                      <div style={{fontSize:7,color:"rgba(255,255,255,0.3)",textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:"'Josefin Sans',sans-serif",marginBottom:6}}>Courbe du jour</div>
                      <ResponsiveContainer width="100%" height={70}>
                        <LineChart data={pd} margin={{top:4,right:4,left:0,bottom:0}}>
                          <ReferenceLine y={0} stroke={darkMode?"rgba(255,255,255,0.15)":"rgba(0,0,0,0.25)"} strokeWidth={1}/>
                          <Tooltip contentStyle={{background:"rgba(20,20,20,0.95)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:6,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",color:"#fff"}} formatter={v=>[`${fms(v)}${currency}`,L.stats.cumulated]} labelFormatter={l=>pd.find(d=>d.label===l)?.instrument||""}/>
                          <Line type="monotone" dataKey="v" stroke={dayTotalPnl>=0?"#4caf6e":"#e05a5a"} strokeWidth={2} dot={{r:2,fill:dayTotalPnl>=0?"#4caf6e":"#e05a5a",strokeWidth:0}} activeDot={{r:4,fill:dayTotalPnl>=0?"#4caf6e":"#e05a5a",strokeWidth:0}}/>
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
                {/* Day mini stats */}
                {(() => {
                  const dWins=selectedDay.trades.filter(t=>t.result==="WIN").length;
                  const dLoss=selectedDay.trades.filter(t=>t.result==="LOSS").length;
                  const dTotal=selectedDay.trades.length;
                  const dWR=dTotal?Math.round(dWins/dTotal*100):0;
                  const dAvgW=dWins?selectedDay.trades.filter(t=>t.result==="WIN").reduce((s,t)=>s+(t.pnl||0),0)/dWins:0;
                  const dAvgL=dLoss?Math.abs(selectedDay.trades.filter(t=>t.result==="LOSS").reduce((s,t)=>s+(t.pnl||0),0)/dLoss):0;
                  const dPF=dAvgL>0?(dAvgW*dWins/(dAvgL*dLoss)).toFixed(2):dWins>0?"∞":"—";
                  const dRR=dTotal?(selectedDay.trades.reduce((s,t)=>s+(parseFloat(t.rr)||0),0)/dTotal).toFixed(1):"—";
                  const dLong=selectedDay.trades.filter(t=>t.direction==="LONG");
                  const dShort=selectedDay.trades.filter(t=>t.direction==="SHORT");
                  const dLW=dLong.length?Math.round(dLong.filter(t=>t.result==="WIN").length/dLong.length*100):0;
                  const dSW=dShort.length?Math.round(dShort.filter(t=>t.result==="WIN").length/dShort.length*100):0;
                  return (
                    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6,marginBottom:12}}>
                      {[
                        {l:L.stats.profitFactor,v:dPF==="—"||dPF==="∞"?dPF:dPF+"x",c:parseFloat(dPF)>=1||dPF==="∞"?"#4caf6e":"#e05a5a"},
                        {l:L.stats.avgRR,v:dRR==="—"?"—":dRR+":1",c:"rgba(255,255,255,0.5)"},
                        {l:lang==="en"?"Trades":"Nb Trades",v:dTotal,c:"rgba(255,255,255,0.7)"},
                        {l:"Long",v:dLong.length?dLW+"%":"—",c:dLW>=50?"#4caf6e":"#e05a5a",sub:dLong.length+"T"},
                        {l:"Short",v:dShort.length?dSW+"%":"—",c:dSW>=50?"#4caf6e":"#e05a5a",sub:dShort.length+"T"},
                      ].map(s=>(
                        <div key={s.l} style={{background:"rgba(255,255,255,0.05)",borderRadius:7,padding:"8px 10px"}}>
                          <div style={{fontSize:7,color:"rgba(255,255,255,0.3)",textTransform:"uppercase",letterSpacing:"0.1em",fontFamily:"'Josefin Sans',sans-serif",marginBottom:3}}>{s.l}</div>
                          <div style={{fontSize:14,fontWeight:300,color:s.c,fontFamily:"'Josefin Sans',sans-serif"}}>{s.v}</div>
                          {s.sub&&<div style={{fontSize:8,color:"rgba(255,255,255,0.3)",fontFamily:"'Josefin Sans',sans-serif"}}>{s.sub}</div>}
                        </div>
                      ))}
                    </div>
                  );
                })()}

              </div>
            </div>
          )}
          <PillNav view={view} setView={setView} darkMode={darkMode} canUseAI={canUseAI} canUseReplay={subExpired ? false : canUseReplay} lang={lang} />
        </div>
      ) : (
        /* ── DESKTOP ── */
        <div style={{ display:"flex", minHeight:"100vh" }}>
          <Sidebar view={view} setView={setView} darkMode={darkMode} onSignOut={() => setShowSignOutConfirm(true)} nickname={profileForm.nickname} firstName={profileForm.firstName} avatarUrl={avatarUrl} canUseAI={canUseAI} canUseReplay={subExpired ? false : canUseReplay} onUserCardClick={() => setShowUserModal(true)} lang={lang} />
          <div style={{ marginLeft:220, flex:1, display:"flex", flexDirection:"column" }}>
            <div style={{ height:92, borderBottom:`1px solid ${C.border}`, background:C.bg, position:"sticky", top:0, zIndex:40, backdropFilter:"blur(12px)", display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 36px" }}>
              <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0,marginLeft:"auto"}}>
                <button onClick={()=>setHideNumbers(h=>{ localStorage.setItem("fyltra_hide_numbers", String(!h)); return !h; })} title={hideNumbers?(lang==="fr"?"Afficher les chiffres":"Show numbers"):(lang==="fr"?"Masquer les chiffres":"Hide numbers")} style={{background:hideNumbers?"rgba(232,205,169,0.12)":darkMode?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.06)",border:`1px solid ${hideNumbers?C.accent:C.border}`,borderRadius:10,width:36,height:36,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:hideNumbers?C.accent:C.gray1,flexShrink:0,transition:"all 0.2s"}}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {hideNumbers ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></> : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>}
                  </svg>
                </button>
                <button onClick={()=>setDarkMode(d=>!d)} title={darkMode?"Mode clair":"Mode sombre"} style={{background:darkMode?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.06)",border:`1px solid ${C.border}`,borderRadius:10,width:36,height:36,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:C.gray1,flexShrink:0,transition:"all 0.2s"}}>
                  {darkMode ? (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
                    </svg>
                  ) : (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div style={{ padding:"28px 36px", flex:1, opacity:showLoadingScreen?0:1 }}>
              <div style={{display:view==="charts"?"flex":"none",flexDirection:"column"}}>{chartsPanel}</div>
              <div style={{display:view==="replay" && canUseReplay?"flex":"none",flexDirection:"column"}}>{replayPanel}</div>
              {(view!=="charts" && (view!=="replay" || !canUseReplay)) && <div key={view+(selectedPf?.id||"")+(showLoadingScreen?"_L":"")} style={{animation:"tabFadeIn 0.42s cubic-bezier(0.34,1.1,0.64,1)", willChange:"transform, opacity"}}>{getContent(true)}</div>}
            </div>
          </div>
          {/* Day popup for desktop — same rich content as mobile */}
          {selectedDay && (
            <div style={{position:"fixed",inset:0,zIndex:400,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.45)",backdropFilter:"blur(6px)"}} onClick={closeDay}>
              <div onClick={e=>e.stopPropagation()} style={{width:560,maxHeight:"90vh",overflowY:"auto",background:"rgba(14,14,14,0.97)",backdropFilter:"blur(24px)",borderRadius:24,padding:"24px",boxShadow:"0 24px 80px rgba(0,0,0,0.6)",border:"1px solid rgba(255,255,255,0.08)",animation:`${dayClosing?"fadeOutDown":"fadeInUp"} 0.25s cubic-bezier(.4,0,.2,1)`}}>
                {/* Header */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
                  <div>
                    <div style={{fontSize:14,color:"rgba(255,255,255,0.9)",fontFamily:"'Barlow',sans-serif",fontWeight:600,letterSpacing:"0.08em"}}>{new Date(selectedDay.date+"T12:00:00").toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long"})}</div>
                    <div style={{fontSize:24,color:selectedDay.pnl>=0?"#4caf6e":"#e05a5a",fontFamily:"'Josefin Sans',sans-serif",fontWeight:300,marginTop:4}}>{fms(selectedDay.pnl)}{currency} · {selectedDay.trades.length} trade{selectedDay.trades.length!==1?"s":""}</div>
                  </div>
                  <button onClick={closeDay} style={{background:"rgba(255,255,255,0.08)",border:"none",borderRadius:50,width:36,height:36,color:"rgba(255,255,255,0.5)",cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>×</button>
                </div>
                {/* Gauge + stats */}
                {(() => {
                  const dW=selectedDay.trades.filter(t=>t.result==="WIN").length;
                  const dL=selectedDay.trades.filter(t=>t.result==="LOSS").length;
                  const dT=selectedDay.trades.length;
                  const r=38,cx=55,cy=42,sw=10,PI=Math.PI;
                  const wFrac=dT>0?dW/dT:0; const lFrac=dT>0?dL/dT:0;
                  const wr=dT?Math.round(dW/dT*100):0;
                  const LEFT2={x:cx-r,y:cy}, RIGHT2={x:cx+r,y:cy};
                  const a2XY=(deg)=>({x:cx+r*Math.cos(deg*PI/180),y:cy-r*Math.sin(deg*PI/180)});
                  const bgA=`M${LEFT2.x},${LEFT2.y} A${r},${r} 0 0 1 ${RIGHT2.x},${RIGHT2.y}`;
                  const wD2=180-wFrac*180; const wP2=a2XY(wD2);
                  const lD2=lFrac*180; const lP2=a2XY(lD2);
                  const wA2=wFrac>0.01?`M${LEFT2.x},${LEFT2.y} A${r},${r} 0 ${wFrac>=1?1:0} 1 ${wP2.x.toFixed(1)},${wP2.y.toFixed(1)}`:"";
                  const lA2=lFrac>0.01?`M${RIGHT2.x},${RIGHT2.y} A${r},${r} 0 ${lFrac>=1?1:0} 0 ${lP2.x.toFixed(1)},${lP2.y.toFixed(1)}`:"";
                  const dayEmotions=EMOTIONS.map(e=>{const et=selectedDay.trades.filter(t=>t.emotion===e);const wr2=et.length?Math.round(et.filter(t=>t.result==="WIN").length/et.length*100):0;return{name:e,count:et.length,wr:wr2,pnl:et.reduce((a,t)=>a+(t.pnl||0),0)};}).filter(e=>e.count>0);
                  const daySess=SESSIONS.map(s=>{const st=selectedDay.trades.filter(t=>t.session===s);const wr2=st.length?Math.round(st.filter(t=>t.result==="WIN").length/st.length*100):0;return{name:s,count:st.length,wr:wr2,pnl:st.reduce((a,t)=>a+(t.pnl||0),0)};}).filter(s=>s.count>0);
                  const dAvgW=dW?selectedDay.trades.filter(t=>t.result==="WIN").reduce((s,t)=>s+(t.pnl||0),0)/dW:0;
                  const dAvgL=dL?Math.abs(selectedDay.trades.filter(t=>t.result==="LOSS").reduce((s,t)=>s+(t.pnl||0),0)/dL):0;
                  const dPF=dAvgL>0?(dAvgW*dW/(dAvgL*dL)).toFixed(2):dW>0?"∞":"—";
                  const dRR=dT?(selectedDay.trades.reduce((s,t)=>s+(parseFloat(t.rr)||0),0)/dT).toFixed(1):"—";
                  return (
                    <div style={{marginBottom:14}}>
                      <div style={{display:"flex",gap:16,background:"rgba(255,255,255,0.04)",borderRadius:12,padding:"14px",marginBottom:10}}>
                        <svg width={110} height={cy+28} viewBox={`0 0 110 ${cy+28}`} style={{flexShrink:0}}>
                          <path d={bgA} stroke="rgba(255,255,255,0.1)" strokeWidth={sw} fill="none" strokeLinecap="round"/>
                          {wFrac>0.01&&<path d={wA2} stroke="#4caf6e" strokeWidth={sw} fill="none" strokeLinecap="round"/>}
                          {lFrac>0.01&&<path d={lA2} stroke="#e05a5a" strokeWidth={sw} fill="none" strokeLinecap="round"/>}
                          <text x={cx} y={cy} textAnchor="middle" fontSize={14} fontWeight={300} fill={wr>=50?"#4caf6e":"#e05a5a"} fontFamily="'Josefin Sans',sans-serif">{wr}%</text>
                          <text x={cx} y={cy+12} textAnchor="middle" fontSize={6} fill="rgba(255,255,255,0.35)" fontFamily="'Josefin Sans',sans-serif" letterSpacing="1">WIN RATE</text>
                          <text x={3} y={cy+22} textAnchor="start" fontSize={8} fontWeight="600" fill="#4caf6e" fontFamily="'Josefin Sans',sans-serif">{dW}W</text>
                          <text x={cx} y={cy+22} textAnchor="middle" fontSize={8} fill="rgba(255,255,255,0.35)" fontFamily="'Josefin Sans',sans-serif">{dT-dW-dL}BE</text>
                          <text x={107} y={cy+22} textAnchor="end" fontSize={8} fontWeight="600" fill="#e05a5a" fontFamily="'Josefin Sans',sans-serif">{dL}L</text>
                        </svg>
                        <div style={{flex:1,display:"flex",flexDirection:"column",gap:5}}>
                          {dayEmotions.length>0&&<div style={{fontSize:7,color:"rgba(255,255,255,0.3)",textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:"'Josefin Sans',sans-serif"}}>Émotions</div>}
                          {dayEmotions.map(e=>(
                            <div key={e.name} style={{display:"flex",justifyContent:"space-between"}}>
                              <span style={{fontSize:11,color:"rgba(255,255,255,0.6)",fontFamily:"'Josefin Sans',sans-serif"}}>{L.emotions[e.name]||e.name}</span>
                              <span style={{fontSize:11,color:e.wr>=50?"#4caf6e":"#e05a5a",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>{e.wr}% · {fms(e.pnl)}{currency}</span>
                            </div>
                          ))}
                          {daySess.length>0&&<div style={{fontSize:7,color:"rgba(255,255,255,0.3)",textTransform:"uppercase",letterSpacing:"0.12em",fontFamily:"'Josefin Sans',sans-serif",marginTop:4}}>Sessions</div>}
                          {daySess.map(s=>(
                            <div key={s.name} style={{display:"flex",justifyContent:"space-between"}}>
                              <span style={{fontSize:11,color:"rgba(255,255,255,0.5)",fontFamily:"'Josefin Sans',sans-serif"}}>{s.name}</span>
                              <span style={{fontSize:11,color:s.pnl>=0?"#4caf6e":"#e05a5a",fontFamily:"'Josefin Sans',sans-serif"}}>{fms(s.pnl)}{currency}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:12}}>
                        {[{l:L.stats.profitFactor,v:dPF==="—"||dPF==="∞"?dPF:dPF+"x",c:parseFloat(dPF)>=1||dPF==="∞"?"#4caf6e":"#e05a5a"},{l:L.stats.avgRR,v:dRR==="—"?"—":dRR+":1",c:"rgba(255,255,255,0.5)"},{l:lang==="en"?"Trades":"Nb Trades",v:dT,c:"rgba(255,255,255,0.7)"}].map(s=>(
                          <div key={s.l} style={{background:"rgba(255,255,255,0.05)",borderRadius:8,padding:"10px 12px"}}>
                            <div style={{fontSize:8,color:"rgba(255,255,255,0.3)",textTransform:"uppercase",letterSpacing:"0.1em",fontFamily:"'Josefin Sans',sans-serif",marginBottom:4}}>{s.l}</div>
                            <div style={{fontSize:16,fontWeight:300,color:s.c,fontFamily:"'Josefin Sans',sans-serif"}}>{s.v}</div>
                          </div>
                        ))}
                      </div>
                      {/* Equity curve */}
                      {(()=>{
                        let dc=0;
                        const dpd=[{label:"0",v:0},...[...selectedDay.trades].sort(cmpTrades).map((t,i)=>{dc+=t.pnl||0;return{label:String(i+1),v:parseFloat(dc.toFixed(2)),pnl:t.pnl||0,instrument:t.instrument||""};})];
                        const dtPnl=dpd[dpd.length-1]?.v||0;
                        return (
                          <div style={{background:"rgba(255,255,255,0.03)",borderRadius:10,padding:"10px 12px",marginBottom:4}}>
                            <div style={{fontSize:7,color:"rgba(255,255,255,0.3)",textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:"'Josefin Sans',sans-serif",marginBottom:6}}>Courbe du jour</div>
                            <ResponsiveContainer width="100%" height={80}>
                              <LineChart data={dpd} margin={{top:4,right:4,left:0,bottom:0}}>
                                <ReferenceLine y={0} stroke={darkMode?"rgba(255,255,255,0.12)":"rgba(0,0,0,0.25)"} strokeWidth={1}/>
                                <Tooltip contentStyle={{background:"rgba(20,20,20,0.95)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:6,fontSize:10,fontFamily:"'Josefin Sans',sans-serif",color:"#fff"}} formatter={v=>[`${fms(v)}${currency}`,L.stats.cumulated]} labelFormatter={l=>dpd.find(d=>d.label===l)?.instrument||""}/>
                                <Line type="monotone" dataKey="v" stroke={dtPnl>=0?"#4caf6e":"#e05a5a"} strokeWidth={2} dot={{r:3,fill:dtPnl>=0?"#4caf6e":"#e05a5a",strokeWidth:0}} activeDot={{r:5,strokeWidth:0}}/>
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── LIGHTBOX ── */}
      {lightboxUrl && (
        <div onClick={()=>setLightboxUrl(null)} style={{ position:"fixed", inset:0, zIndex:10500, background:"rgba(0,0,0,0.35)", backdropFilter:"blur(28px)", WebkitBackdropFilter:"blur(28px)", display:"flex", alignItems:"center", justifyContent:"center", padding:16, animation:"fadeIn 0.18s ease" }}>
          <img src={lightboxUrl} alt="screenshot" onClick={e=>e.stopPropagation()} style={{ maxWidth:"100%", maxHeight:"100%", borderRadius:10, boxShadow:"0 24px 80px rgba(0,0,0,0.8)", objectFit:"contain" }} />
          <div onClick={()=>setLightboxUrl(null)} style={{ position:"absolute", top:20, right:20, width:36, height:36, borderRadius:"50%", background:"rgba(255,255,255,0.12)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:18, color:"#fff" }}>✕</div>
        </div>
      )}

      {/* ── USER MODAL (Profile + Settings) ── */}
      {showUserModal && (
        <div onClick={() => { setShowUserModal(false); setSignOutAnim("idle"); }} style={{ position:"fixed", inset:0, zIndex:1800, background:"rgba(0,0,0,0.6)", backdropFilter:"blur(24px)", WebkitBackdropFilter:"blur(24px)", display:"flex", alignItems:"center", justifyContent:"center", padding:"20px" }}>
          <style>{`
            @keyframes userModalIn{from{opacity:0;transform:scale(0.96) translateY(8px)}to{opacity:1;transform:scale(1) translateY(0)}}
            @keyframes soProfileOut{from{opacity:1;transform:scale(1) translateY(0)}to{opacity:0;transform:scale(0.94) translateY(-18px)}}
            @keyframes soConfirmIn{from{opacity:0;transform:scale(0.86) translateY(28px)}to{opacity:1;transform:scale(1) translateY(0)}}
            @keyframes soConfirmOut{from{opacity:1;transform:scale(1) translateY(0)}to{opacity:0;transform:scale(0.92) translateY(16px)}}
            .user-modal-scroll::-webkit-scrollbar{display:none} .user-modal-scroll{scrollbar-width:none;-ms-overflow-style:none;}
          `}</style>
          <div ref={modalContainerRef} onClick={e => e.stopPropagation()} style={{
            width:"100%", maxWidth:480,
            maxHeight: containerMaxH,
            background:C.bg, borderRadius:20,
            display:"flex", flexDirection:"column",
            boxShadow:"0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.12)",
            animation:"userModalIn 0.22s cubic-bezier(.4,0,.2,1)",
            overflow:"hidden",
            transition:"max-height 0.36s cubic-bezier(.4,0,.2,1)",
          }}>

            {/* Header */}
            <div style={{ padding:"20px 20px 0", flexShrink:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:20 }}>
                {/* Avatar with upload */}
                <label style={{ cursor:"pointer", position:"relative", flexShrink:0 }}>
                  <input type="file" accept="image/*" style={{ display:"none" }} onChange={async e => {
                    const file = e.target.files?.[0];
                    if (!file || !user) return;
                    setAvatarUploading(true);
                    try {
                      const dataUrl = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onerror = reject;
                        reader.onload = ev => {
                          const img = new Image();
                          img.onerror = reject;
                          img.onload = () => {
                            const canvas = document.createElement("canvas");
                            canvas.width = 128; canvas.height = 128;
                            const ctx = canvas.getContext("2d");
                            const min = Math.min(img.width, img.height);
                            const sx = (img.width - min) / 2;
                            const sy = (img.height - min) / 2;
                            ctx.drawImage(img, sx, sy, min, min, 0, 0, 128, 128);
                            resolve(canvas.toDataURL("image/jpeg", 0.82));
                          };
                          img.src = ev.target.result;
                        };
                        reader.readAsDataURL(file);
                      });
                      setAvatarUrl(dataUrl);
                      const { error: saveErr } = await saveUserSettings({ avatar_url: dataUrl });
                      if (saveErr) console.error("avatar_url save error:", saveErr.message);
                    } catch(err) { console.error("Avatar upload error:", err); }
                    setAvatarUploading(false);
                  }} />
                  <div style={{
                    width:52, height:52, borderRadius:"50%",
                    background:"linear-gradient(135deg,rgba(232,205,169,0.4) 0%,rgba(180,140,100,0.4) 100%)",
                    border:"2px solid rgba(232,205,169,0.3)",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    overflow:"hidden", position:"relative",
                  }}>
                    {avatarUploading
                      ? <span style={{fontSize:10,color:"rgba(232,205,169,0.7)"}}>···</span>
                      : avatarUrl
                        ? <img src={avatarUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}} />
                        : <span style={{fontSize:20,fontWeight:700,color:"rgba(232,205,169,0.9)",fontFamily:"'Outfit',sans-serif"}}>
                            {(profileForm.nickname||profileForm.firstName||"?")[0].toUpperCase()}
                          </span>
                    }
                    <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.3)", display:"flex", alignItems:"center", justifyContent:"center", opacity:0, transition:"opacity 0.2s" }}
                      onMouseEnter={e => e.currentTarget.style.opacity=1} onMouseLeave={e => e.currentTarget.style.opacity=0}>
                      <span style={{fontSize:14}}>+</span>
                    </div>
                  </div>
                </label>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:18,fontWeight:700,color:C.white,fontFamily:"'Josefin Sans',sans-serif",letterSpacing:"-0.01em",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {profileForm.nickname || profileForm.firstName || "Trader"}
                  </div>
                  <div style={{fontSize:11,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",marginTop:2}}>{user?.email}</div>
                </div>
                <button onClick={() => setShowUserModal(false)} style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:8, padding:"6px 10px", color:C.gray1, cursor:"pointer", fontSize:13, flexShrink:0 }}>✕</button>
              </div>

              {/* Tab switcher */}
              <div style={{ display:(signOutAnim==="confirming"||signOutAnim==="confirm-out")?"none":"flex", gap:4, padding:4, background:C.bg2, borderRadius:12, border:`1px solid ${C.border}`, marginBottom:20 }}>
                {[{k:"profil",l:lang==="fr"?"Profil":"Profile"},{k:"settings",l:lang==="fr"?"Paramètres":"Settings"}].map(tab => (
                  <button key={tab.k} onClick={() => setUserModalTab(tab.k)} style={{
                    flex:1, padding:"8px", borderRadius:8, border:"none", cursor:"pointer",
                    background: userModalTab===tab.k ? (darkMode?"rgba(255,255,255,0.9)":"#111") : "transparent",
                    color: userModalTab===tab.k ? (darkMode?"#111":"#fff") : C.gray1,
                    fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.1em", textTransform:"uppercase",
                    transition:"all 0.18s",
                  }}>{tab.l}</button>
                ))}
              </div>
            </div>

            {/* Content + Sign out — conditional on signOutAnim */}
            {(signOutAnim === "idle" || signOutAnim === "profile-out") ? (
              <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:0, animation: signOutAnim === "profile-out" ? "soProfileOut 0.28s cubic-bezier(.4,0,.2,1) forwards" : "none" }}>
                <div className="user-modal-scroll" style={{ flex:1, overflowY:"auto", padding:"0 20px" }}>
                  {userModalTab === "profil" ? renderProfileContent(true) : renderSettingsContent(true)}
                </div>
                <div style={{ padding:"16px 20px", borderTop:`1px solid ${C.border}`, flexShrink:0 }}>
                  <button onClick={triggerInModalSignOut} style={{
                    width:"100%", padding:"12px", borderRadius:12, border:"1px solid rgba(229,100,100,0.25)",
                    background:"rgba(229,100,100,0.06)", color:"rgba(229,100,100,0.8)",
                    fontSize:12, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.1em", textTransform:"uppercase",
                    cursor:"pointer", transition:"all 0.2s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background="rgba(229,100,100,0.15)"}
                  onMouseLeave={e => e.currentTarget.style.background="rgba(229,100,100,0.06)"}
                  >
                    {lang==="fr" ? "Déconnexion" : "Sign out"}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", padding:"44px 32px 40px", textAlign:"center", animation: signOutAnim === "confirm-out" ? "soConfirmOut 0.21s cubic-bezier(.4,0,.2,1) forwards" : "soConfirmIn 0.28s cubic-bezier(.34,1.3,.64,1)" }}>
                <div>
                  <div style={{ width:60, height:60, borderRadius:18, background:"rgba(229,100,100,0.1)", border:"1px solid rgba(229,100,100,0.22)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 22px", boxShadow:"0 0 32px rgba(229,100,100,0.15)" }}>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="rgba(229,100,100,0.85)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                  </div>
                  <div style={{ fontFamily:"'Josefin Sans',sans-serif", fontSize:22, fontWeight:700, color:C.white, marginBottom:10, letterSpacing:"-0.025em" }}>{lang==="fr"?"Déconnexion":"Sign out"}</div>
                  <div style={{ fontFamily:"'Josefin Sans',sans-serif", fontSize:12, color:C.gray1, letterSpacing:"0.04em", lineHeight:1.65, marginBottom:32 }}>
                    {lang==="fr"?"Êtes-vous sûr de vouloir\nvous déconnecter ?":"Are you sure you want\nto sign out?"}
                  </div>
                  <div style={{ display:"flex", gap:10 }}>
                    <button onClick={cancelInModalSignOut} style={{ flex:1, padding:"12px", borderRadius:10, border:`1px solid ${C.border}`, background:"transparent", color:C.gray1, fontFamily:"'Josefin Sans',sans-serif", fontSize:11, fontWeight:600, letterSpacing:"0.12em", textTransform:"uppercase", cursor:"pointer" }}>
                      {lang==="fr"?"Annuler":"Cancel"}
                    </button>
                    <button onClick={confirmInModalSignOut} style={{ flex:1, padding:"12px", borderRadius:10, border:"1px solid rgba(229,100,100,0.4)", background:"rgba(229,100,100,0.1)", color:"rgba(229,100,100,0.9)", fontFamily:"'Josefin Sans',sans-serif", fontSize:11, fontWeight:600, letterSpacing:"0.12em", textTransform:"uppercase", cursor:"pointer" }}>
                      {lang==="fr"?"Se déconnecter":"Sign out"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── CONFIRMATION DÉCONNEXION ── */}
      {showSignOutConfirm && (
        <div style={{position:"fixed",inset:0,zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.55)",backdropFilter:"blur(10px)",WebkitBackdropFilter:"blur(10px)",animation:"fadeIn 0.2s ease"}} onClick={cancelSignOut}>
          <div onClick={e=>e.stopPropagation()} style={{width:320,background:C.bg2,border:`1px solid ${C.border}`,borderRadius:20,padding:"32px 28px",boxShadow:"0 32px 80px rgba(0,0,0,0.6),0 8px 24px rgba(0,0,0,0.3),0 0 0 1px rgba(255,255,255,0.08),inset 0 1px 0 rgba(255,255,255,0.2)",animation:`${signOutLeaving?"scaleOut":"scaleIn"} 0.25s cubic-bezier(.4,0,.2,1)`,textAlign:"center"}}>
            <div style={{width:52,height:52,borderRadius:16,background:"rgba(229,100,100,0.1)",border:"1px solid rgba(229,100,100,0.2)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px",boxShadow:"0 0 24px rgba(229,100,100,0.12)"}}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(229,100,100,0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </div>
            <div style={{fontFamily:"'Josefin Sans',sans-serif",fontSize:20,fontWeight:700,color:C.white,marginBottom:8,letterSpacing:"-0.025em"}}>Déconnexion</div>
            <div style={{fontFamily:"'Josefin Sans',sans-serif",fontSize:11,color:C.gray1,letterSpacing:"0.04em",lineHeight:1.6,marginBottom:28}}>Êtes-vous sûr de vouloir<br/>vous déconnecter ?</div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={cancelSignOut} style={{flex:1,padding:"11px",borderRadius:10,border:`1px solid ${C.border}`,background:"transparent",color:C.gray1,fontFamily:"'Josefin Sans',sans-serif",fontSize:11,fontWeight:600,letterSpacing:"0.12em",textTransform:"uppercase",cursor:"pointer"}}>Annuler</button>
              <button onClick={confirmSignOut} style={{flex:1,padding:"11px",borderRadius:10,border:"1px solid rgba(229,100,100,0.4)",background:"rgba(229,100,100,0.1)",color:"rgba(229,100,100,0.9)",fontFamily:"'Josefin Sans',sans-serif",fontSize:11,fontWeight:600,letterSpacing:"0.12em",textTransform:"uppercase",cursor:"pointer"}}>Se déconnecter</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Subscription expired overlay ── */}
      {/* ── Upgrade plan modal ── */}
      {showUpgradeModal && (
        <div onClick={()=>setShowUpgradeModal(false)} style={{position:"fixed",inset:0,zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.72)",backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)",padding:24}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:400,background:darkMode?"rgba(10,10,16,0.98)":"rgba(255,255,255,0.98)",border:`1px solid ${C.border}`,borderRadius:20,padding:"36px 28px",boxShadow:"0 40px 80px rgba(0,0,0,0.6)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
              <img src="/fyltra-logo-black.svg" style={{height:36,width:"auto"}} alt="Fyltra" />
              <button onClick={()=>setShowUpgradeModal(false)} style={{background:"none",border:"none",color:C.dim,cursor:"pointer",fontSize:20,lineHeight:1,padding:0}}>×</button>
            </div>
            <div style={{fontFamily:"'Josefin Sans',sans-serif",fontSize:15,fontWeight:700,color:C.white,marginBottom:4}}>
              {lang==="fr"?"Passe au plan supérieur":"Upgrade your plan"}
            </div>
            <div style={{fontFamily:"'Josefin Sans',sans-serif",fontSize:11,color:C.dim,marginBottom:20,lineHeight:1.6}}>
              {lang==="fr"?"Débloque toutes les fonctionnalités Fyltra.":"Unlock all Fyltra features."}
            </div>
            {[
              {id:"starter",label:"Fyltra",     price:"14.99", feat:lang==="fr"?"Journal · Stats · TradingView · 3 comptes":"Journal · Stats · TradingView · 3 accounts"},
              {id:"trader", label:"Fyltra+",    price:"24.99", feat:lang==="fr"?"+ IA Coach · EOD Review · Market Replay · 10 comptes":"+ AI Coach · EOD Review · Market Replay · 10 accounts"},
              {id:"pro",    label:"Fyltra Pro", price:"39.99", feat:lang==="fr"?"+ Sync MT5 (1/jour) · Comptes illimités":"+ MT5 sync (1/day) · Unlimited accounts"},
            ].map(plan => (
              <div key={plan.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 12px",borderRadius:8,border:`1px solid ${C.border}`,background:C.bg3,marginBottom:8}}>
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:12,color:C.white,fontFamily:"'Josefin Sans',sans-serif",fontWeight:600}}>{plan.label}</span>
                    <span style={{fontSize:11,color:"rgba(232,205,169,0.7)",fontFamily:"'JetBrains Mono',monospace"}}>€{plan.price}</span>
                  </div>
                  <div style={{fontSize:10,color:C.dim,fontFamily:"'Josefin Sans',sans-serif",marginTop:2}}>{plan.feat}</div>
                </div>
                <button onClick={async()=>{
                  try{
                    const r=await fetch("/api/create-checkout",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({plan:plan.id,email:user?.email,existingUser:true})});
                    const d=await r.json();
                    if(d.url) window.location.href=d.url;
                  }catch{}
                }} style={{padding:"7px 14px",borderRadius:6,border:"1px solid rgba(232,205,169,0.35)",background:"rgba(232,205,169,0.08)",color:"rgba(232,205,169,0.9)",fontFamily:"'Josefin Sans',sans-serif",fontWeight:600,fontSize:10,letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer",flexShrink:0,marginLeft:10}}>
                  {lang==="fr"?"Choisir":"Select"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── MT5 Enrichment Modal ── */}
      {pendingMt5Trades && (() => {
        const allEmotions = [...EMOTIONS, ...extraEmotions.map(e => typeof e === "string" ? e : e.label)];
        const setEnrichment = (idx, field, value) =>
          setMt5Enrichments(prev => ({ ...prev, [idx]: { ...prev[idx], [field]: value } }));
        const applyToAll = (field, value) =>
          setMt5Enrichments(prev => {
            const next = { ...prev };
            pendingMt5Trades.forEach((_, i) => { next[i] = { ...next[i], [field]: value }; });
            return next;
          });
        const cancel = () => { setPendingMt5Trades(null); setMt5Enrichments({}); setMt5GlobalUnit(null); };
        const confirm = async () => {
          setConfirmingMt5(true);
          const enriched = pendingMt5Trades.map((t, i) => {
            const e = mt5Enrichments[i] || {};
            return {
              ...t,
              ...(e.emotion    ? { emotion:     e.emotion }          : {}),
              ...(e.strategyId ? { strategyId:  e.strategyId }        : {}),
              ...(e.size       ? { size:         e.size }             : {}),
              ...(e.sizeUnit   ? { sizeUnit:     e.sizeUnit }         : {}),
              ...(e.rr         ? { rr:           e.rr }               : {}),
              ...(e.notes      ? { notes: [t.notes, e.notes].filter(Boolean).join("\n") } : {}),
            };
          });
          const { error } = await supabase.from("trades").insert(enriched.map(t => tradeToDb(t)));
          setConfirmingMt5(false);
          if (!error) { setTrades(p => [...enriched, ...p]); setPendingMt5Trades(null); setMt5Enrichments({}); }
        };
        const labelSty = { fontSize:9, color:"rgba(var(--gold-rgb),0.55)", fontFamily:"'Josefin Sans',sans-serif", fontWeight:700, letterSpacing:"0.14em", textTransform:"uppercase", marginBottom:4 };
        const selSty   = { ...iStyle, fontSize:11, padding:"8px 10px", borderRadius:8, background:C.bg3, color:C.white, colorScheme:darkMode?"dark":"light" };
        const inpSty   = { ...iStyle, fontSize:11, padding:"8px 10px", borderRadius:8, background:C.bg3, color:C.white, colorScheme:darkMode?"dark":"light" };
        return (
          <div style={{ position:"fixed", inset:0, zIndex:9000, background:"rgba(0,0,0,0.72)", backdropFilter:"blur(6px)", display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
            <div style={{ width:"100%", maxWidth:600, background:C.bg2, borderRadius:"16px 16px 0 0", border:`1px solid ${C.border}`, borderBottom:"none", display:"flex", flexDirection:"column", maxHeight:"90vh" }}>
              {/* loading bar */}
              {confirmingMt5 && (
                <div style={{ height:3, borderRadius:"16px 16px 0 0", overflow:"hidden", flexShrink:0, background:"rgba(var(--gold-rgb),0.12)", position:"relative" }}>
                  <div style={{ position:"absolute", top:0, left:0, height:"100%", background:"rgba(var(--gold-rgb),0.85)", boxShadow:"0 0 8px rgba(var(--gold-rgb),0.6)", borderRadius:99, animation:"mt5-progress-bar 3s cubic-bezier(0.4,0,0.2,1) forwards" }}/>
                </div>
              )}
              {/* header */}
              <div style={{ padding:"18px 20px 14px", borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
                <div style={{ fontSize:13, fontWeight:700, color:C.white, fontFamily:"'Josefin Sans',sans-serif", letterSpacing:"-0.01em" }}>
                  {confirmingMt5 ? "Enregistrement en cours…" : `${pendingMt5Trades.length} trade${pendingMt5Trades.length !== 1 ? "s" : ""} importé${pendingMt5Trades.length !== 1 ? "s" : ""} depuis MT5`}
                </div>
                <div style={{ fontSize:10, color:C.dim, fontFamily:"'Josefin Sans',sans-serif", marginTop:3 }}>
                  {confirmingMt5 ? `Insertion de ${pendingMt5Trades.length} trade${pendingMt5Trades.length !== 1 ? "s" : ""} dans la base de données…` : "Enrichis les données avant de confirmer. Tous les champs sont optionnels."}
                </div>
              </div>

              {/* apply-to-all bar */}
              <div style={{ padding:"12px 20px", borderBottom:`1px solid ${C.border}`, background:"rgba(var(--gold-rgb),0.04)", flexShrink:0 }}>
                <div style={{ ...labelSty, marginBottom:8 }}>Appliquer a tous</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  <div>
                    <div style={labelSty}>Emotion</div>
                    <select style={selSty} defaultValue="" onChange={e => { if (e.target.value) applyToAll("emotion", e.target.value); }}>
                      <option value="">--</option>
                      {allEmotions.map(em => <option key={em} value={em}>{em}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={labelSty}>Strategie</div>
                    <select style={selSty} defaultValue="" onChange={e => { if (e.target.value) applyToAll("strategyId", e.target.value); }}>
                      <option value="">--</option>
                      {strategies.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* trade list */}
              <div style={{ overflowY:"auto", flex:1, padding:"8px 0" }}>
                {pendingMt5Trades.map((t, i) => {
                  const enr = mt5Enrichments[i] || {};
                  const strat = strategies.find(s => s.id === enr.strategyId);
                  return (
                    <div key={i} style={{ padding:"12px 20px", borderBottom:`1px solid ${C.border}` }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10, flexWrap:"wrap" }}>
                        <div style={{ width:6, height:6, borderRadius:"50%", background: t.result === "WIN" ? "#4caf6e" : t.result === "LOSS" ? "#e05a5a" : C.dim, flexShrink:0 }}/>
                        <div style={{ fontSize:11, fontWeight:700, color:C.white, fontFamily:"'Josefin Sans',sans-serif" }}>{t.instrument}</div>
                        <div style={{ fontSize:10, color:C.dim, fontFamily:"'Josefin Sans',sans-serif" }}>{t.direction} · {t.result} · {t.date}</div>
                        {(t.entryTime || t.exitTime) && (
                          <div style={{ fontSize:10, color:"rgba(var(--gold-rgb),0.5)", fontFamily:"'Josefin Sans',sans-serif" }}>
                            {t.entryTime && `IN ${utcToLocal(t.entryTime)}`}{t.entryTime && t.exitTime && " → "}{t.exitTime && `OUT ${utcToLocal(t.exitTime)}`}
                          </div>
                        )}
                        {(t.entry || t.exit) && (
                          <div style={{ fontSize:10, color:C.dim, fontFamily:"'Josefin Sans',sans-serif" }}>
                            {t.entry && `@ ${t.entry}`}{t.entry && t.exit && " → "}{t.exit && `${t.exit}`}
                          </div>
                        )}
                        {t.pnl !== undefined && t.pnl !== null && (() => {
                          const swapM = t.notes?.match(/swap:([-\d.]+)/);
                          const commM = t.notes?.match(/commission:([-\d.]+)/);
                          const swapV = swapM ? parseFloat(swapM[1]) : 0;
                          const commV = commM ? parseFloat(commM[1]) : 0;
                          return (
                            <div style={{ marginLeft:"auto", textAlign:"right" }}>
                              <div style={{ fontSize:11, fontWeight:700, color: t.pnl >= 0 ? "#4caf6e" : "#e05a5a", fontFamily:"'Josefin Sans',sans-serif" }}>
                                {hideNumbers ? "****" : `${t.pnl >= 0 ? "+" : ""}${Number(t.pnl).toFixed(2)}`}
                              </div>
                              {(swapV !== 0 || commV !== 0) && (
                                <div style={{ display:"flex", gap:6, justifyContent:"flex-end", marginTop:2 }}>
                                  {swapV !== 0 && <span style={{ fontSize:8.5, color: swapV > 0 ? "#4caf6e" : "#e05a5a", fontFamily:"'Josefin Sans',sans-serif", letterSpacing:"0.05em" }}>swap {swapV > 0 ? "+" : ""}{swapV.toFixed(2)}</span>}
                                  {commV !== 0 && <span style={{ fontSize:8.5, color: commV > 0 ? "#4caf6e" : "#e05a5a", fontFamily:"'Josefin Sans',sans-serif", letterSpacing:"0.05em" }}>comm {commV > 0 ? "+" : ""}{commV.toFixed(2)}</span>}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:8, marginBottom:8 }}>
                        <div>
                          <div style={labelSty}>Emotion</div>
                          <select style={selSty} value={enr.emotion || ""} onChange={e => setEnrichment(i, "emotion", e.target.value)}>
                            <option value="">--</option>
                            {allEmotions.map(em => <option key={em} value={em}>{em}</option>)}
                          </select>
                        </div>
                        <div>
                          <div style={labelSty}>Strategie</div>
                          <select style={selSty} value={enr.strategyId || ""} onChange={e => setEnrichment(i, "strategyId", e.target.value)}>
                            <option value="">--</option>
                            {strategies.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
                            <div style={labelSty}>Taille</div>
                            <div style={{ display:"flex", gap:0, borderRadius:6, overflow:"hidden", border:`1px solid ${C.border}` }}>
                              {["contrats","lots"].map(u => (
                                <button key={u} onClick={() => setEnrichment(i, "sizeUnit", u)} style={{ padding:"1px 6px", border:"none", background: (enr.sizeUnit||"contrats") === u ? "rgba(var(--gold-rgb),0.18)" : "transparent", color: (enr.sizeUnit||"contrats") === u ? "rgba(var(--gold-rgb),0.9)" : C.dim, fontSize:8, fontFamily:"'Josefin Sans',sans-serif", fontWeight:700, letterSpacing:"0.06em", cursor:"pointer", textTransform:"uppercase", transition:"all 0.15s" }}>{u}</button>
                              ))}
                            </div>
                          </div>
                          <input type="number" min="0" step="0.01" placeholder="0.01" style={inpSty} value={enr.size || ""} onChange={e => setEnrichment(i, "size", e.target.value)}/>
                        </div>
                        <div>
                          <div style={labelSty}>R:R</div>
                          <input type="number" min="0" step="0.1" placeholder="2.0" style={inpSty} value={enr.rr || ""} onChange={e => setEnrichment(i, "rr", e.target.value)}/>
                        </div>
                      </div>
                      <div>
                        <div style={labelSty}>Notes</div>
                        <textarea placeholder="Observations, contexte, erreurs..." style={{ ...inpSty, resize:"none", lineHeight:1.5, minHeight:52 }} value={enr.notes || ""} onChange={e => setEnrichment(i, "notes", e.target.value)}/>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* footer */}
              <div style={{ padding:"14px 20px", borderTop:`1px solid ${C.border}`, display:"flex", gap:10, flexShrink:0 }}>
                <button onClick={cancel} disabled={confirmingMt5} style={{ flex:1, padding:"13px", borderRadius:10, border:`1px solid ${C.border}`, background:"transparent", color:confirmingMt5?C.bg3:C.dim, fontFamily:"'Josefin Sans',sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", cursor:confirmingMt5?"not-allowed":"pointer", transition:"all 0.2s" }}>Annuler</button>
                <button onClick={confirm} disabled={confirmingMt5} style={{ flex:2, padding:"13px", borderRadius:10, border:"1px solid rgba(var(--gold-rgb),0.4)", background:confirmingMt5?"rgba(var(--gold-rgb),0.04)":"rgba(var(--gold-rgb),0.08)", color:confirmingMt5?"rgba(var(--gold-rgb),0.4)":"rgba(var(--gold-rgb),0.95)", fontFamily:"'Josefin Sans',sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", cursor:confirmingMt5?"not-allowed":"pointer", transition:"all 0.2s" }}>
                  {confirmingMt5 ? "Enregistrement…" : "Confirmer l'import"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Voice trade modal ── */}
      {voiceOpen && (() => {
        const ff = "'Josefin Sans',sans-serif";
        const ovBg = darkMode ? "rgba(0,0,0,0.82)" : "rgba(0,0,0,0.55)";
        const mdBg = darkMode ? "#1a1a1a" : "#ffffff";
        const borderCol = darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";
        const dimC = darkMode ? "rgba(255,255,255,0.38)" : "rgba(0,0,0,0.38)";
        const textC = darkMode ? "#ffffff" : "#0f0f0f";
        const tokens = [
          { key:"direction", label:lang==="fr"?"LONG/SHORT":"LONG/SHORT" },
          { key:"instrument", label:"INSTRUMENT" },
          { key:"entry", label:lang==="fr"?"ENTRÉE":"ENTRY" },
          { key:"entryTime", label:lang==="fr"?"HEURE ENTRÉE":"ENTRY TIME" },
          { key:"exit", label:lang==="fr"?"SORTIE":"EXIT" },
          { key:"exitTime", label:lang==="fr"?"HEURE SORTIE":"EXIT TIME" },
          { key:"result", label:"WIN/LOSS" },
          { key:"pnl", label:"P&L" },
          { key:"size", label:lang==="fr"?"TAILLE":"SIZE" },
          { key:"session", label:"SESSION" },
          { key:"emotion", label:lang==="fr"?"ÉMOTION":"EMOTION" },
        ];
        const templateFR = [
          { text:"J'ai pris un " }, { token:"direction" },
          { text:" sur " }, { token:"instrument" },
          { text:", entrée à " }, { token:"entry" },
          { text:" à " }, { token:"entryTime" },
          { text:", sortie à " }, { token:"exit" },
          { text:" à " }, { token:"exitTime" },
          { text:", résultat " }, { token:"result" },
          { text:", P&L de " }, { token:"pnl" },
          { text:", taille " }, { token:"size" },
          { text:", session " }, { token:"session" },
          { text:", émotion " }, { token:"emotion" },
        ];
        const templateEN = [
          { text:"I took a " }, { token:"direction" },
          { text:" on " }, { token:"instrument" },
          { text:", entry at " }, { token:"entry" },
          { text:" at " }, { token:"entryTime" },
          { text:", exit at " }, { token:"exit" },
          { text:" at " }, { token:"exitTime" },
          { text:", result " }, { token:"result" },
          { text:", P&L of " }, { token:"pnl" },
          { text:", size " }, { token:"size" },
          { text:", session " }, { token:"session" },
          { text:", emotion " }, { token:"emotion" },
        ];
        const template = lang === "fr" ? templateFR : templateEN;
        return (
          <div style={{ position:"fixed", inset:0, zIndex:9999, background:ovBg, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
               onClick={e => { if (e.target === e.currentTarget) { stopVoice(); setVoiceOpen(false); } }}>
            <div style={{ background:mdBg, border:`1px solid ${borderCol}`, borderRadius:16, width:"100%", maxWidth:480, display:"flex", flexDirection:"column", overflow:"hidden",
              boxShadow:darkMode?"0 32px 80px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,255,255,0.06)":"0 16px 48px rgba(0,0,0,0.18)" }}>

              {/* header */}
              <div style={{ padding:"18px 20px 0", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ width:28, height:28, borderRadius:"50%", background:voiceListening?"rgba(239,68,68,0.15)":"rgba(255,255,255,0.06)", border:`1px solid ${voiceListening?"rgba(239,68,68,0.4)":borderCol}`, display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.3s" }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={voiceListening?"#ef4444":dimC} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2a3 3 0 0 1 3 3v7a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3z"/>
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                      <line x1="12" y1="19" x2="12" y2="23"/>
                      <line x1="8" y1="23" x2="16" y2="23"/>
                    </svg>
                  </div>
                  <span style={{ fontSize:12, fontFamily:ff, fontWeight:700, color:textC, letterSpacing:"0.15em", textTransform:"uppercase" }}>
                    {lang==="fr"?"Saisie vocale":"Voice input"}
                  </span>
                  {voiceListening && <span style={{ fontSize:9, fontFamily:ff, color:"#ef4444", fontWeight:600, letterSpacing:"0.1em", textTransform:"uppercase", animation:"pulse 1.5s ease-in-out infinite" }}>
                    {lang==="fr"?"Écoute...":"Listening..."}
                  </span>}
                </div>
                <button onClick={() => { stopVoice(); setVoiceOpen(false); }} style={{ width:28, height:28, borderRadius:"50%", border:`1px solid ${borderCol}`, background:"transparent", color:dimC, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>×</button>
              </div>

              {/* template */}
              <div style={{ padding:"16px 20px 0" }}>
                <div style={{ fontSize:9, fontFamily:ff, color:dimC, fontWeight:600, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:10 }}>
                  {lang==="fr"?"Lisez cette phrase à voix haute :":"Read this sentence out loud:"}
                </div>
                <div style={{ background:darkMode?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.04)", border:`1px solid ${borderCol}`, borderRadius:10, padding:"14px 16px", lineHeight:2, fontSize:12, fontFamily:ff }}>
                  {template.map((seg, i) =>
                    seg.text ? (
                      <span key={i} style={{ color:dimC, fontWeight:300 }}>{seg.text}</span>
                    ) : (
                      <span key={i} style={{ color:darkMode?"#f59e0b":"#d97706", fontWeight:700, background:darkMode?"rgba(245,158,11,0.12)":"rgba(217,119,6,0.1)", borderRadius:4, padding:"1px 5px", fontSize:10, letterSpacing:"0.08em" }}>
                        {tokens.find(t => t.key === seg.token)?.label}
                      </span>
                    )
                  )}
                </div>
              </div>

              {/* transcript */}
              <div style={{ padding:"14px 20px 0" }}>
                <div style={{ fontSize:9, fontFamily:ff, color:dimC, fontWeight:600, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:8 }}>
                  {lang==="fr"?"Transcription en direct :":"Live transcript:"}
                </div>
                <div style={{ minHeight:60, background:darkMode?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.03)", border:`1px solid ${voiceListening?"rgba(239,68,68,0.25)":borderCol}`, borderRadius:10, padding:"12px 14px", fontSize:12, fontFamily:ff, color:voiceTranscript?textC:dimC, lineHeight:1.6, transition:"border-color 0.3s", wordBreak:"break-word" }}>
                  {voiceTranscript || (lang==="fr"?"En attente...":"Waiting...")}
                </div>
              </div>

              {/* actions */}
              <div style={{ padding:"16px 20px 20px", display:"flex", gap:10 }}>
                <button onClick={voiceListening ? stopVoice : startVoice}
                  style={{ flex:1, padding:"13px", borderRadius:10, border:`1px solid ${voiceListening?"rgba(239,68,68,0.5)":borderCol}`, background:voiceListening?"rgba(239,68,68,0.08)":"transparent", color:voiceListening?"#ef4444":textC, fontFamily:ff, fontSize:11, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", cursor:"pointer", transition:"all 0.2s", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                  {voiceListening ? (
                    <><svg width="11" height="11" viewBox="0 0 24 24" fill="#ef4444"><rect x="3" y="3" width="18" height="18" rx="3"/></svg>
                      {lang==="fr"?"Arrêter":"Stop"}</>
                  ) : (
                    <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2a3 3 0 0 1 3 3v7a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3z"/>
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                        <line x1="12" y1="19" x2="12" y2="23"/>
                        <line x1="8" y1="23" x2="16" y2="23"/>
                      </svg>
                      {lang==="fr"?"Parler":"Speak"}</>
                  )}
                </button>
                <button onClick={applyVoice} disabled={!voiceTranscript.trim() || voiceParsing}
                  style={{ flex:2, padding:"13px", borderRadius:10, border:`1px solid ${voiceTranscript.trim()&&!voiceParsing?"rgba(245,158,11,0.4)":borderCol}`, background:voiceTranscript.trim()&&!voiceParsing?"rgba(245,158,11,0.1)":"transparent", color:voiceTranscript.trim()&&!voiceParsing?"#f59e0b":dimC, fontFamily:ff, fontSize:11, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", cursor:voiceTranscript.trim()&&!voiceParsing?"pointer":"not-allowed", transition:"all 0.2s" }}>
                  {voiceParsing ? (lang==="fr"?"Analyse...":"Parsing...") : (lang==="fr"?"Appliquer au formulaire":"Apply to form")}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── EDIT TRADE MODAL ── */}
      {editingTrade && (
        <div onClick={cancelEdit} style={{ position:"fixed", inset:0, zIndex:2100, background:"rgba(0,0,0,0.7)", backdropFilter:"blur(24px)", display:"flex", alignItems:"center", justifyContent:"center", padding:"20px" }}>
          <style>{`@keyframes modalCardIn{from{opacity:0;transform:scale(0.95) translateY(12px)}to{opacity:1;transform:scale(1) translateY(0)}} @keyframes modalCardOut{from{opacity:1;transform:scale(1) translateY(0)}to{opacity:0;transform:scale(0.95) translateY(12px)}} .edit-scroll::-webkit-scrollbar{display:none}.edit-scroll{scrollbar-width:none;}`}</style>
          <div onClick={e => e.stopPropagation()} className="edit-scroll" style={{ width:"100%", maxWidth:500, maxHeight:"90vh", overflowY:"auto", background:C.bg, borderRadius:20, boxShadow:"0 40px 100px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.1)", animation:`${closingEdit?"modalCardOut":"modalCardIn"} 0.22s cubic-bezier(.4,0,.2,1) forwards` }}>
            {/* top bar */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 20px", borderBottom:`1px solid ${C.border}` }}>
              <span style={{ fontSize:13, color:C.white, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.06em" }}>{L.form.editTrade}</span>
              <button onClick={cancelEdit} style={{ background:"rgba(255,255,255,0.06)", border:`1px solid ${C.border}`, borderRadius:8, width:30, height:30, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:C.gray1, fontSize:15 }}>×</button>
            </div>
            <div style={{ padding:"16px 20px 20px", display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                <div><Label>{L.form.date}</Label><input type="date" value={editingTrade.date} onChange={e => setEditingTrade(p => ({ ...p, date:e.target.value }))} style={{ ...iStyle, padding:"9px 10px", fontSize:13 }} /></div>
                <div><Label>{L.form.pnl}</Label><input type="text" inputMode="decimal" value={editPnlRaw} onChange={e => setEditPnlRaw(e.target.value.replace(/,/g,".").replace(/[^0-9.]/g,""))} style={{ ...iStyle, padding:"9px 10px", fontSize:13 }} /></div>
              </div>
              <div><Label>{L.form.result}</Label>
                <div style={{ display:"flex", gap:5 }}>
                  {["WIN","LOSS","BREAKEVEN"].map(r => <button key={r} onClick={() => setEditingTrade(p => ({ ...p, result:r }))} style={{ flex:1, padding:"7px", borderRadius:4, border:"none", background:editingTrade.result===r?"radial-gradient(ellipse 90% 90% at 50% 38%, rgba(245,245,245,0.9) 0%, rgba(215,215,215,0.83) 100%)":"transparent", color:editingTrade.result===r?"#111":C.gray1, fontSize:10, fontFamily:"'Josefin Sans',sans-serif", fontWeight:editingTrade.result===r?600:300, cursor:"pointer", textTransform:"uppercase", boxShadow:editingTrade.result===r?"0 3px 10px rgba(0,0,0,0.4)":"none", transition:"all 0.2s" }}>{r}</button>)}
                </div>
              </div>
              <div><Label>{L.form.direction}</Label>
                <div style={{ display:"flex", gap:5 }}>
                  {["LONG","SHORT"].map(d => <button key={d} onClick={() => setEditingTrade(p => ({ ...p, direction:d }))} style={{ flex:1, padding:"7px", borderRadius:4, border:"none", background:editingTrade.direction===d?"radial-gradient(ellipse 90% 90% at 50% 38%, rgba(245,245,245,0.9) 0%, rgba(215,215,215,0.83) 100%)":"transparent", color:editingTrade.direction===d?"#111":C.gray1, fontSize:10, fontFamily:"'Josefin Sans',sans-serif", fontWeight:editingTrade.direction===d?600:300, cursor:"pointer", textTransform:"uppercase", boxShadow:editingTrade.direction===d?"0 3px 10px rgba(0,0,0,0.4)":"none", transition:"all 0.2s" }}>{d}</button>)}
                </div>
              </div>
              <div><Label>Session</Label>
                <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                  {SESSIONS.map(s => <button key={s} onClick={() => setEditingTrade(p => ({ ...p, session:s }))} style={{ flex:"1 1 auto", padding:"7px", borderRadius:4, border:"none", background:editingTrade.session===s?"radial-gradient(ellipse 90% 90% at 50% 38%, rgba(245,245,245,0.9) 0%, rgba(215,215,215,0.83) 100%)":"transparent", color:editingTrade.session===s?"#111":C.gray1, fontSize:10, fontFamily:"'Josefin Sans',sans-serif", fontWeight:editingTrade.session===s?600:300, cursor:"pointer", textTransform:"uppercase", boxShadow:editingTrade.session===s?"0 3px 10px rgba(0,0,0,0.4)":"none", transition:"all 0.2s" }}>{s}</button>)}
                </div>
              </div>
              <div><Label>{lang==="fr"?"Émotion":"Emotion"}</Label>
                <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                  {[...EMOTIONS, ...extraEmotions.map(e => typeof e==="string"?e:e.label)].map(e => <button key={e} onClick={() => setEditingTrade(p => ({ ...p, emotion:e }))} style={{ flex:"1 1 auto", padding:"7px", borderRadius:4, border:"none", background:editingTrade.emotion===e?"radial-gradient(ellipse 90% 90% at 50% 38%, rgba(245,245,245,0.9) 0%, rgba(215,215,215,0.83) 100%)":"transparent", color:editingTrade.emotion===e?"#111":C.gray1, fontSize:10, fontFamily:"'Josefin Sans',sans-serif", fontWeight:editingTrade.emotion===e?600:300, cursor:"pointer", textTransform:"uppercase", boxShadow:editingTrade.emotion===e?"0 3px 10px rgba(0,0,0,0.4)":"none", transition:"all 0.2s" }}>{e}</button>)}
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                <div><Label>Instrument</Label><input type="text" value={editingTrade.instrument||""} onChange={e => setEditingTrade(p => ({ ...p, instrument:e.target.value }))} style={{ ...iStyle, padding:"9px 10px", fontSize:13 }} /></div>
                <div><Label>RR</Label><input type="text" inputMode="decimal" value={editingTrade.rr||""} onChange={e => setEditingTrade(p => ({ ...p, rr:e.target.value.replace(/,/g,".") }))} style={{ ...iStyle, padding:"9px 10px", fontSize:13 }} /></div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                <div><Label>{L.form.entry}</Label><input type="text" inputMode="decimal" placeholder="0.00" value={editingTrade.entry||""} onChange={e => setEditingTrade(p => ({ ...p, entry:e.target.value.replace(/,/g,".") }))} style={{ ...iStyle, padding:"9px 10px", fontSize:13 }} /></div>
                <div><Label>{L.form.exit}</Label><input type="text" inputMode="decimal" placeholder="0.00" value={editingTrade.exit||""} onChange={e => setEditingTrade(p => ({ ...p, exit:e.target.value.replace(/,/g,".") }))} style={{ ...iStyle, padding:"9px 10px", fontSize:13 }} /></div>
              </div>
              {strategies.length > 0 && (
                <div><Label>{L.form.stratUsed}</Label>
                  <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                    {strategies.map(s => {
                      const sel = editingTrade.strategyId === s.id;
                      return (
                        <button key={s.id} onClick={() => setEditingTrade(p => ({ ...p, strategyId: sel ? null : s.id }))} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", borderRadius:5, border:`1px solid ${sel?C.accent:C.gray3}`, background:sel?"rgba(0,0,0,0.06)":"transparent", cursor:"pointer" }}>
                          <span style={{ fontSize:12, fontFamily:"'Josefin Sans',sans-serif", fontWeight:sel?600:300, color:sel?C.accent:C.white }}>{s.name||"Stratégie"}</span>
                          <div style={{ width:16, height:16, borderRadius:3, border:`1px solid ${sel?C.accent:C.gray2}`, background:sel?C.accent:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{sel&&<span style={{ color:darkMode?"#111":"#fff", fontSize:10 }}>✓</span>}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div><Label>Notes</Label><textarea rows={3} value={editingTrade.notes||""} onChange={e => setEditingTrade(p => ({ ...p, notes:e.target.value }))} style={{ ...iStyle, resize:"vertical", lineHeight:1.5, padding:"9px 10px", fontSize:13, textAlign:"left" }} /></div>
              {propfirms.filter(p=>!p.status||p.status==="active").length>0 && (
                <div><Label>{lang==="fr"?"Compte(s)":"Account(s)"}</Label>
                  <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                    {propfirms.filter(p=>!p.status||p.status==="active").map(pf => {
                      const sel=(editingTrade.accountIds||[]).includes(pf.id);
                      return <button key={pf.id} onClick={() => setEditingTrade(p => ({ ...p, accountIds:sel?(p.accountIds||[]).filter(id=>id!==pf.id):[...(p.accountIds||[]),pf.id] }))} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", borderRadius:5, border:`1px solid ${sel?C.accent:C.gray3}`, background:sel?"rgba(0,0,0,0.06)":"transparent", cursor:"pointer" }}>
                        <span style={{ fontSize:12, fontFamily:"'Josefin Sans',sans-serif", fontWeight:sel?600:300, color:sel?C.accent:C.white }}>{pf.firm}{pf.name?" · "+pf.name:""}</span>
                        <div style={{ width:16, height:16, borderRadius:3, border:`1px solid ${sel?C.accent:C.gray2}`, background:sel?C.accent:"transparent", display:"flex", alignItems:"center", justifyContent:"center" }}>{sel&&<span style={{ color:darkMode?"#111":"#fff", fontSize:10 }}>✓</span>}</div>
                      </button>;
                    })}
                  </div>
                </div>
              )}
              <div><Label>Badges</Label>
                <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginBottom:(editingTrade.tags||[]).length?7:0 }}>
                  {(editingTrade.tags||[]).map(tag => { const bc=badgeDisplayStyle(tag); const {name}=parseBadge(tag); return <span key={tag} style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"3px 10px", borderRadius:20, background:bc.bg, border:`1px solid ${bc.border}`, fontSize:10, color:bc.color, fontFamily:"'Josefin Sans',sans-serif", fontWeight:500 }}>{name}<button onClick={() => setEditingTrade(p => ({ ...p, tags:(p.tags||[]).filter(t=>t!==tag) }))} style={{ background:"none", border:"none", color:bc.color, opacity:0.6, cursor:"pointer", fontSize:12, lineHeight:1, padding:0 }}>×</button></span>; })}
                </div>
                <div style={{ display:"flex", gap:5, marginBottom:6 }}>
                  {[{k:"neg",l:"− Négatif",c:clrRed,bg:"rgba(248,113,113,0.15)",b:"rgba(248,113,113,0.4)"},{k:"neutral",l:"Neutre",c:C.gray1,bg:"transparent",b:C.border},{k:"pos",l:"+ Positif",c:clrGreen,bg:"rgba(74,222,128,0.15)",b:"rgba(74,222,128,0.4)"}].map(p => <button key={p.k} onClick={() => setEditBadgePolarity(p.k)} style={{ flex:1, padding:"5px", borderRadius:5, border:`1px solid ${editBadgePolarity===p.k?p.b:C.border}`, background:editBadgePolarity===p.k?p.bg:"transparent", color:editBadgePolarity===p.k?p.c:C.gray2, fontSize:10, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, cursor:"pointer", transition:"all 0.15s" }}>{p.l}</button>)}
                </div>
                <input type="text" value={editTagInput} placeholder={lang==="fr"?"Nom du badge... (Entrée)":"Badge name... (Enter)"} onChange={e => setEditTagInput(e.target.value)}
                  onKeyDown={e => { if ((e.key==="Enter"||e.key===",")&&editTagInput.trim()) { e.preventDefault(); const name=editTagInput.trim().toLowerCase().replace(/\s+/g,"-"); const badge=editBadgePolarity!=="neutral"?`${name}|${editBadgePolarity}`:name; if (!(editingTrade.tags||[]).some(t=>parseBadge(t).name===name)) setEditingTrade(p=>({...p,tags:[...(p.tags||[]),badge]})); setEditTagInput(""); setEditBadgePolarity("neutral"); } }}
                  onBlur={() => { if (editTagInput.trim()) { const name=editTagInput.trim().toLowerCase().replace(/\s+/g,"-"); const badge=editBadgePolarity!=="neutral"?`${name}|${editBadgePolarity}`:name; if (!(editingTrade.tags||[]).some(t=>parseBadge(t).name===name)) setEditingTrade(p=>({...p,tags:[...(p.tags||[]),badge]})); setEditTagInput(""); setEditBadgePolarity("neutral"); } }}
                  style={{ ...iStyle, padding:"9px 10px", fontSize:13, textAlign:"left" }} />
              </div>
              <div style={{ display:"flex", gap:7, marginTop:4 }}>
                <button onClick={saveEdit} style={{ flex:2, padding:"11px", borderRadius:10, border:"none", background:"radial-gradient(ellipse 90% 90% at 50% 38%, rgba(245,245,245,0.95) 0%, rgba(215,215,215,0.88) 55%, rgba(230,230,230,0.92) 100%)", color:"#111", fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", cursor:"pointer" }}>✓ {lang==="fr"?"Sauvegarder":"Save"}</button>
                <button onClick={cancelEdit} style={{ flex:1, padding:"11px", borderRadius:10, border:`1px solid ${C.border}`, background:"transparent", color:C.gray1, fontSize:11, fontFamily:"'Josefin Sans',sans-serif", letterSpacing:"0.1em", textTransform:"uppercase", cursor:"pointer" }}>{lang==="fr"?"Annuler":"Cancel"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── TRADE DETAIL MODAL ── */}
      {selectedTradeDetail && (() => {
        const t = selectedTradeDetail;
        const pnl = t.pnl || 0;
        const isWin = t.result === "WIN";
        const isLoss = t.result === "LOSS";
        const pnlColor = isWin ? clrGreen : isLoss ? clrRed : C.dim;
        const initials = (t.instrument||"??").slice(0,2).toUpperCase();
        const isFr = lang === "fr";
        const StatCard = ({ label, value, cols=1 }) => (
          <div style={{ background:C.bg3, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px", gridColumn:`span ${cols}`, boxShadow:cShadow }}>
            <div style={{ fontSize:9, color:C.dim, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:5 }}>{label}</div>
            <div style={{ fontSize:15, color:C.white, fontFamily:"'Josefin Sans',sans-serif", fontWeight:400, letterSpacing:"0.01em" }}>{value||"—"}</div>
          </div>
        );
        const SectionTitle = ({ children }) => (
          <div style={{ fontSize:11, color:C.gray1, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:8, marginTop:4 }}>{children}</div>
        );
        return (
          <div onClick={closeDetail} style={{ position:"fixed", inset:0, zIndex:2000, background:"rgba(0,0,0,0.7)", backdropFilter:"blur(24px)", display:"flex", alignItems:"center", justifyContent:"center", padding:"20px" }}>
            <style>{`.td-scroll::-webkit-scrollbar{display:none} .td-scroll{scrollbar-width:none;}`}</style>
            <div onClick={e => e.stopPropagation()} className="td-scroll" style={{ width:"100%", maxWidth:600, maxHeight:"90vh", overflowY:"auto", background:C.bg, borderRadius:20, boxShadow:"0 40px 100px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.1)", animation:`${closingDetail?"modalCardOut":"modalCardIn"} 0.22s cubic-bezier(.4,0,.2,1) forwards` }}>

              {/* ── TOP BAR ── */}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 20px", borderBottom:`1px solid ${C.border}` }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <button onClick={closeDetail} style={{ background:"rgba(255,255,255,0.06)", border:`1px solid ${C.border}`, borderRadius:8, width:30, height:30, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:C.gray1, fontSize:15 }}>×</button>
                  <span style={{ fontSize:13, color:C.white, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.06em" }}>{isFr?"Détails du trade":"Trade details"}</span>
                </div>
                <button onClick={() => { closeDetail(); startEdit(t); }} style={{ display:"flex", alignItems:"center", gap:6, background:C.bg2, border:`1px solid ${C.border}`, borderRadius:8, padding:"6px 14px", color:C.gray1, fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.08em", cursor:"pointer" }}>
                  <span style={{ fontSize:13 }}>✎</span> {isFr?"Éditer":"Edit"}
                </button>
              </div>

              <div style={{ padding:"16px 20px 24px", display:"flex", flexDirection:"column", gap:14 }}>

                {/* ── HERO CARD ── */}
                <div style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:14, padding:"18px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:16, boxShadow:cShadow }}>
                  <div style={{ display:"flex", alignItems:"center", gap:14, minWidth:0, flex:1 }}>
                    <div style={{ width:52, height:52, borderRadius:14, background:`linear-gradient(135deg,${isWin?"rgba(74,222,128,0.15)":isLoss?"rgba(248,113,113,0.15)":"rgba(255,255,255,0.06)"} 0%,rgba(255,255,255,0.03) 100%)`, border:`1px solid ${isWin?"rgba(74,222,128,0.2)":isLoss?"rgba(248,113,113,0.2)":C.border}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      <span style={{ fontFamily:"'Josefin Sans',sans-serif", fontWeight:700, fontSize:15, color:isWin?clrGreen:isLoss?clrRed:C.gray1, letterSpacing:"0.04em" }}>{initials}</span>
                    </div>
                    <div style={{ minWidth:0, overflow:"hidden" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4, flexWrap:"wrap" }}>
                        <span style={{ fontFamily:"'Josefin Sans',sans-serif", fontWeight:700, fontSize:20, color:C.white, letterSpacing:"-0.02em" }}>{t.instrument}</span>
                        <span style={{ fontSize:9, fontWeight:700, letterSpacing:"0.14em", color:t.direction==="LONG"?clrGreen:clrRed, background:t.direction==="LONG"?"rgba(74,222,128,0.1)":"rgba(248,113,113,0.1)", border:`1px solid ${t.direction==="LONG"?"rgba(74,222,128,0.25)":"rgba(248,113,113,0.25)"}`, borderRadius:5, padding:"2px 7px", fontFamily:"'Josefin Sans',sans-serif", flexShrink:0 }}>{t.direction}</span>
                        <span style={{ fontSize:9, fontWeight:700, letterSpacing:"0.14em", color:isWin?clrGreen:isLoss?clrRed:C.dim, background:isWin?"rgba(74,222,128,0.1)":isLoss?"rgba(248,113,113,0.1)":"rgba(255,255,255,0.04)", border:`1px solid ${isWin?"rgba(74,222,128,0.25)":isLoss?"rgba(248,113,113,0.25)":C.border}`, borderRadius:5, padding:"2px 7px", fontFamily:"'Josefin Sans',sans-serif", flexShrink:0 }}>{t.result}</span>
                      </div>
                      <div style={{ fontSize:11, color:C.dim, fontFamily:"'Josefin Sans',sans-serif", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {t.date}{t.entryTime ? ` · ${utcToLocal(t.entryTime)}` : ""}
                        {t.accountIds&&t.accountIds.length>0 ? ` · ${t.accountIds.map(id=>propfirms.find(p=>p.id===id)?.name).filter(Boolean).join(", ")}` : ""}
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <div style={{ fontFamily:"'Josefin Sans',sans-serif", fontSize:"clamp(18px,5vw,26px)", fontWeight:400, color:pnlColor, letterSpacing:"-0.01em", lineHeight:1, whiteSpace:"nowrap" }}>{fms(pnl)}{currency}</div>
                    <div style={{ fontSize:9, color:C.dim, fontFamily:"'Josefin Sans',sans-serif", marginTop:4, letterSpacing:"0.06em", textTransform:"uppercase" }}>P&L</div>
                  </div>
                </div>

                {/* ── PRIX ── */}
                {(t.entry || t.exit) && (
                  <div>
                    <SectionTitle>{isFr?"Prix":"Prices"}</SectionTitle>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:8 }}>
                      {t.entry && <StatCard label={isFr?"Prix d'entrée":"Entry price"} value={t.entry} />}
                      {t.exit  && <StatCard label={isFr?"Prix de sortie":"Exit price"} value={t.exit} />}
                      {t.rr    && <StatCard label="R:R" value={`1 : ${t.rr}`} />}
                      {t.size  && <StatCard label={isFr?"Taille":"Size"} value={`${t.size} ${t.sizeUnit||""}`} />}
                    </div>
                  </div>
                )}

                {/* ── TIMING ── */}
                <div>
                  <SectionTitle>Timing</SectionTitle>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:8 }}>
                    <StatCard label={isFr?"Date":"Date"} value={t.date} />
                    {t.entryTime && <StatCard label={isFr?"Ouverture":"Open"} value={utcToLocal(t.entryTime)} />}
                    {t.exitTime  && <StatCard label={isFr?"Fermeture":"Close"} value={utcToLocal(t.exitTime)} />}
                    {t.session   && <StatCard label="Session" value={t.session} />}
                  </div>
                </div>

                {/* ── CONTEXTE ── */}
                <div>
                  <SectionTitle>{isFr?"Contexte":"Context"}</SectionTitle>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:8 }}>
                    {t.strategyId && strategies.find(s=>s.id===t.strategyId) && <StatCard label={isFr?"Stratégie":"Strategy"} value={strategies.find(s=>s.id===t.strategyId).name} />}
                    {t.emotion && <StatCard label={isFr?"Émotion":"Emotion"} value={L.emotions[t.emotion]||t.emotion} />}
                    {(t.tags||[]).length>0 && (
                      <div style={{ background:C.bg3, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px", boxShadow:cShadow }}>
                        <div style={{ fontSize:9, color:C.dim, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:7 }}>Tags</div>
                        <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                          {t.tags.map(tag => { const bc=badgeDisplayStyle(tag); const {name}=parseBadge(tag); return <span key={tag} style={{ fontSize:9, color:bc.color, background:bc.bg, padding:"2px 8px", borderRadius:20, border:`1px solid ${bc.border}`, fontFamily:"'Josefin Sans',sans-serif", fontWeight:500 }}>{name}</span>; })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── NOTES ── */}
                {t.notes && (
                  <div>
                    <SectionTitle>Notes</SectionTitle>
                    <div style={{ background:C.bg3, border:`1px solid ${C.border}`, borderRadius:10, padding:"13px 16px", fontSize:13, color:C.gray1, lineHeight:1.75, fontStyle:"italic", fontFamily:"'Josefin Sans',sans-serif", fontWeight:300, boxShadow:cShadow }}>{t.notes}</div>
                  </div>
                )}

                {/* ── MÉDIAS ── */}
                {t.screenshotUrl && (
                  <div>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                      <SectionTitle>{isFr?"Médias":"Media"}</SectionTitle>
                    </div>
                    <div onClick={()=>setLightboxUrl(t.screenshotUrl)} style={{ background:C.bg3, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden", boxShadow:cShadow, cursor:"zoom-in" }}>
                      <img src={t.screenshotUrl} alt="screenshot" style={{ width:"100%", display:"block" }} />
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        );
      })()}

      {/* ── TradingView Modal ── */}
      {/* ── Export PDF Modal ── */}
      {showExportModal && (() => {
        const ff = "'Josefin Sans',sans-serif";
        return (
          <div style={{ position:"fixed", inset:0, zIndex:9998, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(0,0,0,0.7)", backdropFilter:"blur(10px)", WebkitBackdropFilter:"blur(10px)" }} onClick={()=>setShowExportModal(false)}>
            <div style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:20, padding:"28px 28px 24px", maxWidth:360, width:"calc(100% - 40px)", boxShadow:"0 24px 80px rgba(0,0,0,0.8),inset 0 1px 0 rgba(255,255,255,0.18)" }} onClick={e=>e.stopPropagation()}>
              <div style={{ fontSize:15, fontWeight:700, color:C.white, fontFamily:ff, marginBottom:6, letterSpacing:"-0.01em" }}>Export PDF</div>
              <div style={{ fontSize:11, color:C.dim, fontFamily:ff, marginBottom:22 }}>{lang==="fr"?"Choisissez la période à exporter":"Choose the period to export"}</div>
              <div style={{ display:"flex", gap:10, marginBottom:20 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:9, color:C.dim, fontFamily:ff, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:6 }}>{lang==="fr"?"Du":"From"}</div>
                  <input type="date" value={exportFrom} onChange={e=>setExportFrom(e.target.value)} style={{ width:"100%", background:C.bg3, border:`1px solid ${C.border}`, borderRadius:8, padding:"9px 10px", color:C.white, fontFamily:ff, fontSize:12, outline:"none", boxSizing:"border-box" }} />
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:9, color:C.dim, fontFamily:ff, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:6 }}>{lang==="fr"?"Au":"To"}</div>
                  <input type="date" value={exportTo} onChange={e=>setExportTo(e.target.value)} style={{ width:"100%", background:C.bg3, border:`1px solid ${C.border}`, borderRadius:8, padding:"9px 10px", color:C.white, fontFamily:ff, fontSize:12, outline:"none", boxSizing:"border-box" }} />
                </div>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={()=>setShowExportModal(false)} style={{ padding:"10px 16px", borderRadius:10, border:`1px solid ${C.border}`, background:"transparent", color:C.gray2, fontSize:11, fontFamily:ff, cursor:"pointer", letterSpacing:"0.08em" }}>
                  {lang==="fr"?"Annuler":"Cancel"}
                </button>
                <button onClick={()=>{ if(!exportFrom||!exportTo) return; exportPDF(exportFrom,exportTo); setShowExportModal(false); showToast(lang==="fr"?"PDF généré !":"PDF generated!"); }} style={{ flex:1, padding:"11px 20px", borderRadius:10, border:"none", background:"rgba(255,255,255,0.92)", color:"#111", fontSize:12, fontFamily:ff, fontWeight:700, cursor:"pointer", letterSpacing:"0.1em" }}>
                  {lang==="fr"?"Générer →":"Generate →"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Cancel flow ── */}
      {cancelStep > 0 && (() => {
        const ff = "'Josefin Sans',sans-serif";
        const ffT = "'Outfit',sans-serif";
        const isFyltraPlus = userPlan === "fyltra_plus";
        const isFyltraPro  = userPlan === "fyltra_pro";
        const mt5PfIdsC = new Set(propfirms.filter(p => !!p.metaapi_id).map(p => p.id));
        const mt5TradesC = trades.filter(t => (t.accountIds||[]).some(id => mt5PfIdsC.has(id)));
        const rewardsUnlockedC = [
          mt5TradesC.length > 0, mt5TradesC.length >= 10, mt5TradesC.length >= 50,
          propfirms.some(p => p.type==="propfirm" && !!p.metaapi_id),
          propfirms.some(p => p.status==="passed" && !!p.metaapi_id),
          propfirms.some(p => p.payout_received && !!p.metaapi_id),
        ].filter(Boolean).length;
        const score = Math.round(calcFyltraScore());
        const CHURN = [
          { k:"too_expensive",   l: lang==="fr" ? "Trop cher" : "Too expensive" },
          { k:"not_trading",     l: lang==="fr" ? "Je ne trade pas en ce moment" : "Not trading right now" },
          { k:"missing_feature", l: lang==="fr" ? "Il me manque une fonctionnalité" : "Missing a feature" },
          { k:"found_another",   l: lang==="fr" ? "J'ai trouvé un autre outil" : "Found another tool" },
        ];
        return (
          <div style={{ position:"fixed", inset:0, zIndex:9500, display:"flex", alignItems:"center", justifyContent:"center", padding:24, background:"rgba(0,0,0,0.90)", backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)" }}>
            <div style={{ width:"100%", maxWidth:460, background:C.bg2, borderRadius:20, border:`1px solid ${C.border}`, boxShadow:"0 32px 80px rgba(0,0,0,0.7)", overflow:"hidden" }}>
              {/* Step progress */}
              <div style={{ padding:"18px 24px 0", display:"flex", gap:6 }}>
                {[1,2,3].map(s => (
                  <div key={s} style={{ height:2, flex:1, borderRadius:99, background: cancelStep >= s ? "#E8CDA9" : "rgba(255,255,255,0.07)", transition:"background 0.3s" }}/>
                ))}
              </div>

              {/* STEP 1 — Awareness */}
              {cancelStep === 1 && (
                <div style={{ padding:"20px 24px 24px" }}>
                  <div style={{ textAlign:"center", marginBottom:18 }}>
                    <div style={{ fontSize:9, fontFamily:ff, fontWeight:700, letterSpacing:"0.2em", color:"rgba(232,205,169,0.55)", textTransform:"uppercase", marginBottom:8 }}>
                      {lang==="fr" ? "Avant de partir" : "Before you leave"}
                    </div>
                    <div style={{ fontSize:22, fontFamily:ffT, fontWeight:800, color:C.white, lineHeight:1.1, letterSpacing:"-0.02em" }}>
                      {lang==="fr" ? "Voici ce que tu as construit." : "Here's what you've built."}
                    </div>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:14 }}>
                    {[
                      { label: lang==="fr" ? "Trades loggés" : "Trades logged",    value: String(trades.length) },
                      { label: "Fyltra Score",                                      value: String(score) },
                      lbUserRank && { label: lang==="fr" ? "Position classement" : "Ranking position", value: `#${lbUserRank}` },
                      (isFyltraPlus || isFyltraPro) && coachMemory && { label: lang==="fr" ? "Mémoire IA" : "AI memory", value: "Active" },
                      isFyltraPro && { label: lang==="fr" ? "Trades MT5 sync" : "MT5 trades synced", value: String(mt5TradesC.length) },
                      isFyltraPro && { label: lang==="fr" ? "Certificats" : "Certificates", value: `${rewardsUnlockedC} / 6` },
                    ].filter(Boolean).map((s, i) => (
                      <div key={i} style={{ padding:"11px 13px", borderRadius:10, background:C.bg3, border:`1px solid ${C.border}` }}>
                        <div style={{ fontSize:8, fontFamily:ff, fontWeight:600, letterSpacing:"0.14em", textTransform:"uppercase", color:C.gray1, marginBottom:4 }}>{s.label}</div>
                        <div style={{ fontSize:20, fontFamily:ffT, fontWeight:700, color:C.white }}>{s.value}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding:"11px 16px", borderRadius:10, background:"rgba(192,57,43,0.06)", border:"1px solid rgba(192,57,43,0.18)", marginBottom:18, textAlign:"center" }}>
                    <span style={{ fontSize:12, fontFamily:ff, fontWeight:600, color:"rgba(192,57,43,0.8)", letterSpacing:"0.04em" }}>
                      {lang==="fr" ? "Tu vas perdre l'accès à tout ça." : "You're about to lose access to all of this."}
                    </span>
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    <button onClick={() => setCancelStep(2)} style={{ width:"100%", padding:"13px", borderRadius:10, border:"1px solid rgba(232,205,169,0.3)", background:"rgba(232,205,169,0.07)", color:"#E8CDA9", fontFamily:ff, fontWeight:700, fontSize:11, letterSpacing:"0.1em", textTransform:"uppercase", cursor:"pointer", transition:"all 0.2s" }}>
                      {lang==="fr" ? "Mettre en pause 30 jours" : "Pause for 30 days"}
                    </button>
                    <button onClick={() => setCancelStep(3)} style={{ width:"100%", padding:"11px", borderRadius:10, border:`1px solid ${C.border}`, background:"transparent", color:C.gray1, fontFamily:ff, fontWeight:600, fontSize:11, letterSpacing:"0.06em", cursor:"pointer" }}>
                      {lang==="fr" ? "Résilier quand même →" : "Cancel anyway →"}
                    </button>
                    <button onClick={() => setCancelStep(0)} style={{ width:"100%", padding:"8px", border:"none", background:"transparent", color:C.gray2, fontFamily:ff, fontSize:11, cursor:"pointer" }}>
                      {lang==="fr" ? "Garder mon abonnement" : "Keep my subscription"}
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 2 — Pause */}
              {cancelStep === 2 && (() => {
                const resumeDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                const resumeStr = resumeDate.toLocaleDateString(lang==="fr" ? "fr-FR" : "en-GB", { day:"numeric", month:"long" });
                if (pauseDone) return (
                  <div style={{ padding:"32px 24px 28px", textAlign:"center" }}>
                    <div style={{ width:48, height:48, borderRadius:"50%", background:"rgba(232,205,169,0.1)", border:"1px solid rgba(232,205,169,0.25)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px", fontSize:20, color:"#E8CDA9" }}>◎</div>
                    <div style={{ fontSize:18, fontFamily:ffT, fontWeight:700, color:C.white, marginBottom:8 }}>
                      {lang==="fr" ? "Pause activée." : "Pause activated."}
                    </div>
                    <div style={{ fontSize:12, fontFamily:ff, fontWeight:300, color:C.gray1, lineHeight:1.8, marginBottom:24 }}>
                      {lang==="fr" ? `Ton abonnement reprend automatiquement le ${resumeStr}. Tes données sont sauvegardées.` : `Your subscription resumes automatically on ${resumeStr}. Your data is safe.`}
                    </div>
                    <button onClick={() => { setCancelStep(0); setPauseDone(false); }} style={{ padding:"10px 28px", borderRadius:10, border:`1px solid ${C.border}`, background:"transparent", color:C.dim, fontFamily:ff, fontWeight:700, fontSize:11, letterSpacing:"0.1em", textTransform:"uppercase", cursor:"pointer" }}>
                      {lang==="fr" ? "Fermer" : "Close"}
                    </button>
                  </div>
                );
                return (
                  <div style={{ padding:"20px 24px 24px" }}>
                    <div style={{ textAlign:"center", marginBottom:18 }}>
                      <div style={{ fontSize:9, fontFamily:ff, fontWeight:700, letterSpacing:"0.2em", color:"rgba(232,205,169,0.55)", textTransform:"uppercase", marginBottom:8 }}>
                        {lang==="fr" ? "Une alternative" : "An alternative"}
                      </div>
                      <div style={{ fontSize:22, fontFamily:ffT, fontWeight:800, color:C.white, lineHeight:1.1, letterSpacing:"-0.02em" }}>
                        {lang==="fr" ? "Prends une pause." : "Take a break."}
                      </div>
                    </div>
                    <div style={{ padding:"16px", borderRadius:12, background:C.bg3, border:"1px solid rgba(232,205,169,0.12)", marginBottom:16 }}>
                      <div style={{ fontSize:11, fontFamily:ff, fontWeight:700, color:"#E8CDA9", marginBottom:10, letterSpacing:"0.04em" }}>
                        {lang==="fr" ? "30 jours de pause, sans frais" : "30 days on hold, no charges"}
                      </div>
                      {[
                        lang==="fr" ? "Ton compte reste actif en lecture seule" : "Your account stays in read-only mode",
                        lang==="fr" ? "Aucune facturation pendant la pause" : "No billing during the pause",
                        lang==="fr" ? `Reprise automatique le ${resumeStr}` : `Auto-resumes on ${resumeStr}`,
                      ].map((l, i) => (
                        <div key={i} style={{ display:"flex", alignItems:"center", gap:8, marginBottom: i < 2 ? 6 : 0 }}>
                          <span style={{ color:"#E8CDA9", fontSize:11, flexShrink:0 }}>✓</span>
                          <span style={{ fontSize:12, fontFamily:ff, fontWeight:300, color:C.dim, lineHeight:1.5 }}>{l}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      <button onClick={pauseSub} disabled={pauseLoading} style={{ width:"100%", padding:"13px", borderRadius:10, border:"none", background:"#E8CDA9", color:"#111", fontFamily:ff, fontWeight:700, fontSize:11, letterSpacing:"0.1em", textTransform:"uppercase", cursor:pauseLoading ? "not-allowed" : "pointer", opacity:pauseLoading ? 0.6 : 1, transition:"all 0.2s" }}>
                        {pauseLoading ? "…" : lang==="fr" ? "Mettre en pause 30 jours" : "Pause for 30 days"}
                      </button>
                      <button onClick={() => setCancelStep(3)} style={{ width:"100%", padding:"11px", borderRadius:10, border:`1px solid ${C.border}`, background:"transparent", color:C.gray1, fontFamily:ff, fontWeight:600, fontSize:11, letterSpacing:"0.06em", cursor:"pointer" }}>
                        {lang==="fr" ? "Non, je veux résilier →" : "No, I want to cancel →"}
                      </button>
                      <button onClick={() => setCancelStep(0)} style={{ width:"100%", padding:"8px", border:"none", background:"transparent", color:C.gray2, fontFamily:ff, fontSize:11, cursor:"pointer" }}>
                        {lang==="fr" ? "Garder mon abonnement" : "Keep my subscription"}
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* STEP 3 — Churn reason */}
              {cancelStep === 3 && (
                <div style={{ padding:"20px 24px 24px" }}>
                  <div style={{ textAlign:"center", marginBottom:18 }}>
                    <div style={{ fontSize:9, fontFamily:ff, fontWeight:700, letterSpacing:"0.2em", color:C.gray2, textTransform:"uppercase", marginBottom:8 }}>
                      {lang==="fr" ? "Dernière étape" : "Last step"}
                    </div>
                    <div style={{ fontSize:22, fontFamily:ffT, fontWeight:800, color:C.white, lineHeight:1.1, letterSpacing:"-0.02em" }}>
                      {lang==="fr" ? "Pourquoi tu pars ?" : "Why are you leaving?"}
                    </div>
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:18 }}>
                    {CHURN.map(r => (
                      <button key={r.k} onClick={() => setChurnReason(r.k)} style={{ display:"flex", alignItems:"center", gap:12, width:"100%", padding:"12px 14px", borderRadius:10, border:`1px solid ${churnReason===r.k ? "rgba(232,205,169,0.4)" : C.border}`, background:churnReason===r.k ? "rgba(232,205,169,0.06)" : "transparent", cursor:"pointer", transition:"all 0.15s", textAlign:"left" }}>
                        <div style={{ width:16, height:16, borderRadius:"50%", border:`1.5px solid ${churnReason===r.k ? "#E8CDA9" : C.gray2}`, background:churnReason===r.k ? "#E8CDA9" : "transparent", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.15s" }}>
                          {churnReason===r.k && <div style={{ width:6, height:6, borderRadius:"50%", background:"#111" }}/>}
                        </div>
                        <span style={{ fontSize:13, fontFamily:ff, fontWeight:churnReason===r.k ? 600 : 300, color:churnReason===r.k ? "#E8CDA9" : C.white, letterSpacing:"0.04em" }}>{r.l}</span>
                      </button>
                    ))}
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    <button onClick={() => cancelSub(churnReason)} disabled={cancelLoading || !churnReason} style={{ width:"100%", padding:"13px", borderRadius:10, border:"none", background: churnReason ? "rgba(192,57,43,0.85)" : "rgba(192,57,43,0.25)", color:"#fff", fontFamily:ff, fontWeight:700, fontSize:11, letterSpacing:"0.1em", textTransform:"uppercase", cursor: (cancelLoading || !churnReason) ? "not-allowed" : "pointer", opacity:cancelLoading ? 0.7 : 1, transition:"all 0.2s" }}>
                      {cancelLoading ? "…" : lang==="fr" ? "Confirmer la résiliation" : "Confirm cancellation"}
                    </button>
                    <button onClick={() => setCancelStep(2)} style={{ width:"100%", padding:"8px", border:"none", background:"transparent", color:C.gray2, fontFamily:ff, fontSize:11, cursor:"pointer" }}>
                      ← {lang==="fr" ? "Retour" : "Back"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Toast ── */}
      {toast && (
        <div style={{ position:"fixed", bottom: isMobile?110:32, left:"50%", transform:"translateX(-50%)", zIndex:10001, background:darkMode?"rgba(30,30,30,0.97)":"rgba(18,18,18,0.95)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:50, padding:"10px 20px", color:"rgba(255,255,255,0.9)", fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight:600, letterSpacing:"0.12em", textTransform:"uppercase", boxShadow:"0 8px 32px rgba(0,0,0,0.5)", pointerEvents:"none", whiteSpace:"nowrap" }}>
          {toast}
        </div>
      )}

    </div>
  );
}
